import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  Tooltip, 
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  CircularProgress,
  LinearProgress,
  Button,
  Link,
  Drawer,
  Collapse,
  Slide,
  Slider,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  FormControlLabel,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  InputAdornment,
  Checkbox,
  Snackbar,
  Alert
} from '@mui/material';
import {
  MyLocation as LocationIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Satellite as SatelliteIcon,
  Terrain as TerrainIcon,
  Layers as LayersIcon,
  GridOn as GridIcon,
  Gradient as GradientIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Assessment as ResultsIcon,
  Science as ScienceIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as TreeArrowDownIcon,
  KeyboardArrowRight as TreeArrowRightIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  FactCheck as FactCheckIcon
} from '@mui/icons-material';

// OpenLayers imports
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import WebGLTile from 'ol/layer/WebGLTile';
import BaseTileLayer from 'ol/layer/BaseTile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import GeoTIFF from 'ol/source/GeoTIFF';
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import { fromLonLat, transform, toLonLat, transformExtent } from 'ol/proj';
import Feature, { FeatureLike } from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import Overlay from 'ol/Overlay';
import { defaults as defaultControls } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction';

// Mineral Discovery Components
import MineralDiscoveryPanel from './mineral-discovery/MineralDiscoveryPanel';
import DualAnalysisResultsPanel from './mineral-discovery/DualAnalysisResultsPanel';
import GridPotentialLayer from './mineral-discovery/GridPotentialLayer';
import ContinuousHeatMapLayer, { AnalysisPointDetails } from './mineral-discovery/ContinuousHeatMapLayer';
import HeatmapGradientLegend from './mineral-discovery/HeatmapGradientLegend';
import AlgorithmResultsViewer from './mineral-discovery/AlgorithmResultsViewer';
import RuleContributionPanel from './mineral-discovery/RuleContributionPanel';
import StatisticalValidationDashboard from './mineral-discovery/StatisticalValidationDashboard';
import InteractiveRuleTuningPanel from './mineral-discovery/InteractiveRuleTuningPanel';
import EnhancedAnalysisLogPanel from './EnhancedAnalysisLogPanel';

// Polygon Drawing Components
import PolygonDrawingTool from './polygon/PolygonDrawingTool';
import PolygonManager from './polygon/PolygonManager';
import { ModelRunSettings } from './ModelControlCenter';
import {
  DEFAULT_ANALYSIS_GRID_SETTINGS,
  GridSettings,
  sanitizeGridSettings,
  deserializeGridSettings,
  serializeGridSettings
} from '../utils/gridSettings';
import { analysisHasDetailedResults } from '../utils/analysisData';
import { apiFetch, apiRequest, buildApiUrl, getActiveApiBase } from '../config/apiClient';
import AgentEvaluationPanel, { AgentEvalRequest, MORPH_TASKS } from './AgentEvaluationPanel';
import { DEMO_TOUR_STOPS } from '../config/demoTour';
import { NORWAY_TARGET_LAYER_ORDER } from '../config/norwayTargets';
import {
  LAYER_GROUP_ACCENT,
  LAYER_GROUP_ORDER,
  PORTFOLIO_STAGE_DEFINITIONS,
  PORTFOLIO_STAGE_ORDER,
  PROJECT_GEOJSON_POLYGON_LAYERS,
  getProjectGeoJsonPolygonLayers,
  getProjectGeoJsonDefaultVisibility,
  hexToRgba,
  type LayerControlGroup,
  type PortfolioStage,
  type PortfolioStageDefinition,
  type ProjectGeoJsonPolygonLayer
} from '../config/mapLayerRegistry';

const DEFAULT_PROJECT_ZOOM = 14;
const MAX_AUTO_ZOOM = 15;

// Project-specific default zoom levels for country/region overview
const PROJECT_DEFAULT_ZOOMS: Record<string, number> = {
  'project-gtk-finland': 5,      // Show all of Finland
  'project-sgu-sweden': 5,       // Show all of Sweden
  'project-sweden-uranium': 6,   // Northern Sweden uranium focus
  'project-daura': 10,           // Single deposit focus
  'project-tassa': 10,           // Single deposit focus
  'project-amba': 10,            // Single deposit focus
  'project-el-pelon': 10,        // Single deposit focus
  'project-peru-wide': 6,        // Peru wide screen
  'project-png-wide': 6,         // Papua New Guinea wide screen
  'project-yukon-wide': 6,       // Yukon (GeoYukon) jurisdiction screen
  'project-caujul': 13,          // Caujul deposit focus
  'project-france-brgm': 5,     // Show all of France
  'project-greenland-geus': 5,   // Greenland national source-layer review
  'project-canada-ontario': 6,   // Ontario jurisdiction screen
  'project-canada-nl': 5,        // Newfoundland & Labrador jurisdiction screen
  'project-australia-wa': 6,     // Western Australia jurisdiction screen
  'project-bolivia': 6,          // Bolivia jurisdiction screen
  'project-zambia-mmmd': 6,      // Zambia national MMMD/GSD screen
};
const COUNTRY_OUTLINE_URL = '/country_boundaries_trace.geojson';
const QUEBEC_OUTLINE_URL = '/quebec_province_outline.geojson';
const NEWFOUNDLAND_OUTLINE_URL = '/newfoundland_province_outline.geojson';
const ZAMBIA_OUTLINE_URL = '/data/zambia/zambia_landfolio_national_boundary_display.geojson';
const PROJECT_COUNTRY_OUTLINE: Record<string, { iso?: string; url?: string }> = {
  'project-sgu-sweden': { iso: 'SWE' },
  'project-sweden-uranium': { iso: 'SWE' },
  'project-gtk-finland': { iso: 'FIN' },
  'project-peru-wide': { iso: 'PER' },
  'project-png-wide': { iso: 'PNG' },
  'project-caujul': { iso: 'PER' },
  'project-australia-wa': { iso: 'AUS' },
  'project-canada-ontario': { iso: 'CAN' },
  'project-canada-quebec': { url: QUEBEC_OUTLINE_URL },
  'project-canada-nl': { url: NEWFOUNDLAND_OUTLINE_URL },
  'project-ireland-gsi': { iso: 'IRL' },
  'project-greenland-geus': { iso: 'GRL' },
  'project-norway-ngu': { iso: 'NOR' },
  'project-chile-sernageomin': { iso: 'CHL' },
  'project-france-brgm': { iso: 'FRA' },
  'project-colombia-sgc': { iso: 'COL' },
  'project-drc-cami': { iso: 'COD' },
  'project-usa-usgs': { iso: 'USA' },
  'project-bolivia': { iso: 'BOL' },
  'project-zambia-mmmd': { url: ZAMBIA_OUTLINE_URL },
};
const RANKED_TARGET_COUNTRY_PROJECT_ID: Record<string, string> = {
  sweden: 'project-sgu-sweden',
  finland: 'project-gtk-finland',
  peru: 'project-peru-wide',
  png: 'project-png-wide',
  yukon: 'project-yukon-wide',
  norway: 'project-norway-ngu',
  france: 'project-france-brgm',
  greenland: 'project-greenland-geus',
  zambia: 'project-zambia-mmmd',
};
const RANKED_TARGET_TIER_COLOR: Record<string, string> = {
  tier_1: '#16a34a',
  tier_2: '#3b82f6',
  tier_3: '#ca8a04',
  monitor: '#f59e0b',
  blocked: '#dc2626',
  killed: '#64748b',
  jv_acquisition: '#8b5cf6',
  lithium: '#ec4899',
  pending: '#94a3b8',
};
const HEATMAP_PREF_PREFIX = 'mineral-ai-heatmap-enabled-';
const MAP_LAYER_PANEL_COLLAPSED_KEY = 'mineral-ai-map-layer-panel-collapsed';
const MAP_LAYER_LEGACY_LIST_KEY = 'mineral-ai-map-layer-legacy-list-open';
const MAP_LAYER_ACTIVE_PREFIX = 'mineral-ai-active-layers-';
const MAP_LAYER_VISIBILITY_PREFIX = 'mineral-ai-layer-visibility-';
const MAP_LAYER_GROUPS_PREFIX = 'mineral-ai-layer-groups-';
const MAP_LAYER_TARGET_AREAS_PREFIX = 'mineral-ai-layer-target-areas-';
const PROJECT_GEOJSON_VISIBILITY_PREFIX = 'mineral-ai-project-geojson-visibility-';
const PROJECT_GEOJSON_EXPANDED_PREFIX = 'mineral-ai-project-geojson-expanded-';
const PROJECT_GEOJSON_POLYGON_ONLY_PREFIX = 'mineral-ai-project-geojson-polygon-only-';
const MAP_UI_FONT = '"IBM Plex Sans", "Aptos", "Segoe UI", system-ui, sans-serif';

// Layers that carry a disabledReason (awaiting tile/vector staging) are hidden
// from the layer panel by default — grayed-out toggles read as broken in a
// live demo. Set REACT_APP_SHOW_DISABLED_LAYERS=1 to show them while developing.
const HIDE_DISABLED_LAYER_ENTRIES = process.env.REACT_APP_SHOW_DISABLED_LAYERS !== '1';
const MAP_UI_MONO_FONT = '"IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace';

// Map-layer legend panel design tokens. Single source of truth for the panel's
// typography / spacing rhythm — use these instead of scattered literals so the
// whole panel stays consistent. HARD RULE: nothing in the panel goes below 10px.
const LEGEND_UI = {
  labelText: { fontSize: '13px', fontWeight: 520, fontFamily: MAP_UI_FONT },
  labelColorChecked: '#e8edf4',
  labelColorUnchecked: '#d3dbe5',
  detailText: { fontSize: '11px', color: '#8f9dab', fontFamily: MAP_UI_FONT },
  groupHeaderText: {
    fontSize: '12.5px',
    fontWeight: 780,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.85,
    color: '#edf3f8',
    fontFamily: MAP_UI_FONT,
  },
  countText: {
    fontSize: '10.5px',
    fontFamily: MAP_UI_MONO_FONT,
    fontVariantNumeric: 'tabular-nums' as const,
  },
  chipFontSize: '10px',
  rowMinHeight: 44,
  groupHeaderMinHeight: 38,
  // Text column of the shared row grid starts at swatch(26) + columnGap(6) from
  // the row's left padding box — inline legends align to this.
  textColumnInset: '32px',
  hairline: '1px solid rgba(148, 163, 184, 0.16)',
  panelBg: '#0e141b',
} as const;
const API_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || getActiveApiBase();
const SHOULD_LOG_DEBUG = process.env.NODE_ENV === 'development';
const devLog = (...args: any[]) => {
  if (SHOULD_LOG_DEBUG) {
    console.log(...args);
  }
};
const devInfo = (...args: any[]) => {
  if (SHOULD_LOG_DEBUG) {
    console.info(...args);
  }
};
const EMPTY_VECTOR_STYLE = new Style({});
const fetchApiOrAsset = (url: string, options?: RequestInit) => {
  if (url.startsWith('/') && !url.startsWith('/api/')) {
    return fetch(url, options);
  }
  if (url.startsWith(API_BASE)) {
    return apiFetch(url.slice(API_BASE.length) || '/', options);
  }
  return apiFetch(url, options);
};

const storageProjectKey = (projectId?: string | null) => projectId || 'global';

const readBooleanRecord = (key: string): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.entries(parsed).reduce<Record<string, boolean>>((next, [entryKey, value]) => {
      if (typeof value === 'boolean') {
        next[entryKey] = value;
      }
      return next;
    }, {});
  } catch {
    return {};
  }
};

const writeJsonStorage = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures so private-mode browsers still render the map.
  }
};

const readStoredBoolean = (key: string, fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // Ignore storage failures and use the requested default.
  }
  return fallback;
};

const writeStoredBoolean = (key: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Ignore storage failures so the controls remain usable.
  }
};

const DEFAULT_BASE_LAYER_ID = 'satellite';
const BASE_LAYER_IDS = ['satellite', 'terrain', 'geological', 'street'] as const;
const BASE_LAYER_ID_SET = new Set<string>(BASE_LAYER_IDS);
const BASE_MAP_CONTROL_IDS = new Set(BASE_LAYER_IDS.map((layerId) => `base:${layerId}`));

const normalizeActiveLayers = (layers: string[]): string[] => {
  const normalized = new Set<string>([DEFAULT_BASE_LAYER_ID]);
  layers.forEach((layer) => {
    if (
      typeof layer === 'string' &&
      layer &&
      !BASE_LAYER_ID_SET.has(layer) &&
      !BASE_MAP_CONTROL_IDS.has(layer)
    ) {
      normalized.add(layer);
    }
  });
  return Array.from(normalized);
};

const readStoredActiveLayers = (projectId?: string | null): string[] => {
  const key = `${MAP_LAYER_ACTIVE_PREFIX}${storageProjectKey(projectId)}`;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return [DEFAULT_BASE_LAYER_ID];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return normalizeActiveLayers(parsed.filter((value): value is string => typeof value === 'string'));
    }
  } catch {
    // Ignore storage failures and fall back to the default basemap.
  }
  return [DEFAULT_BASE_LAYER_ID];
};

const PROJECT_LAYER_LABEL_FEATURE_KEY = '__geomorphicLayerLabelFeature';
const PROJECT_LAYER_LABEL_TEXT_KEY = '__geomorphicLayerLabelText';
const PROJECT_POINT_LABEL_OFFSET_INDEX_KEY = '__geomorphicPointLabelOffsetIndex';

const getProjectLayerLabelWeight = (feature: any): number => {
  const geometry = feature?.getGeometry?.();
  if (!geometry) {
    return -1;
  }
  const geometryType = geometry.getType?.() || '';
  const extent = geometry.getExtent?.();
  const extentArea = extent
    ? Math.max(0, extent[2] - extent[0]) * Math.max(0, extent[3] - extent[1])
    : 0;
  const geometryRank = geometryType.includes('Polygon')
    ? 3
    : geometryType.includes('Line')
      ? 2
      : geometryType.includes('Point')
        ? 1
        : 0;
  return geometryRank * 1_000_000_000_000_000 + extentArea;
};

const markProjectLayerLabelFeature = (features: any[], layerLabel: string) => {
  let bestFeature: any = null;
  let bestWeight = -Infinity;
  features.forEach((feature) => {
    feature?.set?.(PROJECT_LAYER_LABEL_FEATURE_KEY, false, true);
    feature?.set?.(PROJECT_LAYER_LABEL_TEXT_KEY, '', true);
    const weight = getProjectLayerLabelWeight(feature);
    if (weight > bestWeight) {
      bestWeight = weight;
      bestFeature = feature;
    }
  });
  if (bestFeature && layerLabel) {
    bestFeature.set(PROJECT_LAYER_LABEL_FEATURE_KEY, true, true);
    bestFeature.set(PROJECT_LAYER_LABEL_TEXT_KEY, layerLabel, true);
  }
};

const markProjectPointLabelFeatures = (features: any[], labelField: string) => {
  const coordinateCounts = new globalThis.Map<string, number>();
  features.forEach((feature) => {
    feature?.set?.(PROJECT_LAYER_LABEL_FEATURE_KEY, false, true);
    feature?.set?.(PROJECT_LAYER_LABEL_TEXT_KEY, '', true);
    feature?.set?.(PROJECT_POINT_LABEL_OFFSET_INDEX_KEY, 0, true);
    const labelValue = feature?.get?.(labelField);
    const label = labelValue === null || labelValue === undefined ? '' : String(labelValue).trim();
    const geometry = feature?.getGeometry?.();
    const geometryType = geometry?.getType?.() || '';
    if (!label || (geometryType !== 'Point' && geometryType !== 'MultiPoint')) {
      return;
    }
    const coordinates = geometryType === 'Point'
      ? geometry.getCoordinates?.()
      : geometry.getCoordinates?.()?.[0];
    const coordinateKey = Array.isArray(coordinates)
      ? coordinates.slice(0, 2).map((value: number) => Math.round(value * 10) / 10).join(':')
      : `feature-${feature?.getId?.() || coordinateCounts.size}`;
    const offsetIndex = coordinateCounts.get(coordinateKey) || 0;
    coordinateCounts.set(coordinateKey, offsetIndex + 1);
    feature.set(PROJECT_LAYER_LABEL_FEATURE_KEY, true, true);
    feature.set(PROJECT_LAYER_LABEL_TEXT_KEY, label, true);
    feature.set(PROJECT_POINT_LABEL_OFFSET_INDEX_KEY, offsetIndex, true);
  });
};

const getCompactProjectLayerLabel = (label: unknown, maxChars = 36): string => {
  const text = String(label || '');
  return text.length > maxChars ? `${text.slice(0, Math.max(4, maxChars - 3))}...` : text;
};

const getProjectLayerLabelPoint = (feature: any): Point | undefined => {
  const geometry = feature?.getGeometry?.();
  if (!geometry) {
    return undefined;
  }
  const geometryType = geometry.getType?.() || '';
  if (geometryType === 'Point') {
    return geometry.clone?.() || geometry;
  }
  if (geometryType === 'MultiPoint') {
    const coordinates = geometry.getCoordinates?.();
    return Array.isArray(coordinates) && coordinates[0] ? new Point(coordinates[0]) : undefined;
  }
  if (geometryType === 'Polygon' && geometry.getInteriorPoint) {
    return geometry.getInteriorPoint();
  }
  if (geometryType === 'MultiPolygon' && geometry.getInteriorPoints) {
    const interiorCoordinates = geometry.getInteriorPoints()?.getCoordinates?.() || [];
    const bestCoordinate = interiorCoordinates.reduce((best: number[] | null, coordinate: number[]) => {
      if (!best) {
        return coordinate;
      }
      return (coordinate[2] || 0) > (best[2] || 0) ? coordinate : best;
    }, null);
    if (bestCoordinate) {
      return new Point([bestCoordinate[0], bestCoordinate[1]]);
    }
  }
  const extent = geometry.getExtent?.();
  return extent
    ? new Point([(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2])
    : undefined;
};

const getProjectLayerLabelRules = (zoom: number, isGroupedTarget: boolean, minZoom?: number) => {
  if (zoom < (minZoom ?? 6.8)) {
    return null;
  }
  if (minZoom === undefined && !isGroupedTarget && zoom < 9.2) {
    return null;
  }
  const fontSize = zoom >= 12 ? 11 : zoom >= 9 ? 10 : 9;
  const offsetY = zoom >= 10 ? -13 : -9;
  const maxChars = zoom >= 12 ? 38 : zoom >= 9 ? 28 : 20;
  return { fontSize, offsetY, maxChars };
};

const createProjectLayerLabelStyle = (
  feature: any,
  label: string,
  color: string,
  zoom = 10,
  isGroupedTarget = false,
  minZoom?: number,
  showEveryPointLabel = false,
) => {
  const labelRules = getProjectLayerLabelRules(zoom, isGroupedTarget, minZoom);
  if (!labelRules) {
    return undefined;
  }
  const point = label ? getProjectLayerLabelPoint(feature) : undefined;
  if (!point) {
    return undefined;
  }
  return new Style({
    geometry: point,
    text: new Text({
      text: getCompactProjectLayerLabel(label, labelRules.maxChars),
      font: `700 ${labelRules.fontSize}px ${MAP_UI_FONT}`,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: '#020617', width: 3.4 }),
      offsetY: labelRules.offsetY - (Number(feature.get(PROJECT_POINT_LABEL_OFFSET_INDEX_KEY)) || 0) * 12,
      overflow: showEveryPointLabel,
    }),
    zIndex: 720 + (Number(feature.get(PROJECT_POINT_LABEL_OFFSET_INDEX_KEY)) || 0),
  });
};

type KiuriGrantBlockId = 'seitevare' | 'koberget' | 'njuorram';

type KiuriGrantBlock = {
  id: KiuriGrantBlockId;
  name: string;
  permitId: string;
  dnr: string;
  area: string;
  color: string;
  formerName: string;
};

const KIURI_GRANT_BLOCKS: KiuriGrantBlock[] = [
  {
    id: 'seitevare',
    name: 'Seitevare',
    permitId: '2026:80',
    dnr: 'BS 200-688-2026',
    area: '25,507 ha',
    color: '#00E5FF',
    formerName: 'Kiuri North'
  },
  {
    id: 'koberget',
    name: 'Koberget',
    permitId: '2026:82',
    dnr: 'BS 200-690-2026',
    area: '1,614 ha',
    color: '#22C55E',
    formerName: 'Tjaula'
  },
  {
    id: 'njuorram',
    name: 'Njuorram',
    permitId: '2026:81',
    dnr: 'BS 200-689-2026',
    area: '2,010 ha',
    color: '#3B82F6',
    formerName: 'Tallberget'
  }
];

const KIURI_GRANT_BLOCK_BY_ID = KIURI_GRANT_BLOCKS.reduce<Record<KiuriGrantBlockId, KiuriGrantBlock>>((blocks, block) => {
  blocks[block.id] = block;
  return blocks;
}, {} as Record<KiuriGrantBlockId, KiuriGrantBlock>);

const getDefaultKiuriGrantBlockVisibility = (visible = false): Record<KiuriGrantBlockId, boolean> =>
  KIURI_GRANT_BLOCKS.reduce<Record<KiuriGrantBlockId, boolean>>((visibility, block) => {
    visibility[block.id] = visible;
    return visibility;
  }, {} as Record<KiuriGrantBlockId, boolean>);

const isKiuriGrantBlockId = (value: string): value is KiuriGrantBlockId =>
  Object.prototype.hasOwnProperty.call(KIURI_GRANT_BLOCK_BY_ID, value);

const GRANUDDEN_DRAFT_LAYER = {
  id: 'granudden_draft',
  name: 'Granudden onshore draft',
  area: '203 ha / 2 blocks',
  color: '#14B8A6',
  status: 'DRAFT — NOT FILED',
} as const;

type LayerLegendSwatch = 'circle' | 'square' | 'line' | 'dash' | 'gradient';

type LayerLegendEntry = {
  label: string;
  color: string;
  strokeColor?: string;
  swatch?: LayerLegendSwatch;
};

type LayerControlItem = {
  id: string;
  label: string;
  group: LayerControlGroup;
  color: string;
  checked: boolean;
  onToggle: (visible: boolean) => void | Promise<void>;
  targetArea?: string;
  detail?: string;
  count?: string | number;
  disabled?: boolean;
  disabledReason?: string;
  presetControlled?: boolean;
  swatch?: LayerLegendSwatch;
  portfolioStage?: PortfolioStage;
  legend?: LayerLegendEntry[];
  legendGroup?: string;
};

type StaticGlobalTargetLayer = {
  id: string;
  controlId: string;
  projectId: string;
  label: string;
  targetArea?: string;
  detail?: string;
  color: string;
  portfolioStage?: PortfolioStage;
  lonLat?: [number, number];
  aliases?: string[];
};

const SWEDEN_NJUGG_STATIC_TARGET_LAYERS: StaticGlobalTargetLayer[] = [
  {
    id: 'njuggtraskliden_expanded',
    controlId: 'sweden:njuggtraskliden_expanded',
    projectId: 'project-sgu-sweden',
    label: 'Njuggtraskliden expanded',
    targetArea: 'Njuggtraskliden district',
    detail: '12,300 ha recommended design, NW magnetic anomaly',
    color: '#38bdf8',
    portfolioStage: 'Open',
    lonLat: [19.284, 64.83],
    aliases: ['NJUGGTRÄSKLIDEN', 'Njuggträskliden', 'Njugg'],
  },
  {
    id: 'njuggtraskliden',
    controlId: 'sweden:njuggtraskliden',
    projectId: 'project-sgu-sweden',
    label: 'Njuggtraskliden original',
    targetArea: 'Njuggtraskliden district',
    detail: '14,400 ha original target, 92% open',
    color: '#06b6d4',
    portfolioStage: 'Open',
    lonLat: [19.284, 64.83],
    aliases: ['NJUGGTRÄSKLIDEN', 'Njuggträskliden', 'Njugg'],
  },
  {
    id: 'risliden_vms',
    controlId: 'sweden:risliden_vms',
    projectId: 'project-sgu-sweden',
    label: 'Risliden (Cu-Zn anomaly, not VMS)',
    targetArea: 'Njuggtraskliden district',
    detail: 'Naming artefact — Cu-Zn anomaly INSIDE permit, not VMS',
    color: '#64748b',
    portfolioStage: 'Monitor',
    lonLat: [19.284, 64.83],
  },
  {
    id: 'njugg_gold_south',
    controlId: 'sweden:njugg_gold_south',
    projectId: 'project-sgu-sweden',
    label: 'Njugg Gold South (desktop only)',
    targetArea: 'Njuggtraskliden district',
    detail: 'Lapsed Au ground ~13 km SE, OUTSIDE polygon — desktop check',
    color: '#14b8a6',
    portfolioStage: 'Monitor',
    lonLat: [19.284, 64.83],
  },
  {
    id: 'mjovattnet_watch',
    controlId: 'sweden:mjovattnet_watch',
    projectId: 'project-sgu-sweden',
    label: 'Mjovattnet (CONTESTED)',
    targetArea: 'Njuggtraskliden district',
    detail: 'Deposit nr 103 held to Aug 2026; race Boliden for it ~Aug 2027',
    color: '#94a3b8',
    portfolioStage: 'Monitor',
    lonLat: [19.284, 64.83],
  },
  {
    id: 'mjovattnet_nr101_block',
    controlId: 'sweden:mjovattnet_nr101_block',
    projectId: 'project-sgu-sweden',
    label: 'Mjovattnet regional block (Boliden nr 1001)',
    targetArea: 'Njuggtraskliden district',
    detail: 'Boliden application on expired EV Metals nr 101 — karenstid race',
    color: '#ef4444',
    portfolioStage: 'Monitor',
    lonLat: [19.284, 64.83],
  },
  {
    id: 'njugg_boliden_district',
    controlId: 'sweden:njugg_boliden_district',
    projectId: 'project-sgu-sweden',
    label: 'Boliden Skellefte district (48 permits)',
    targetArea: 'Njuggtraskliden district',
    detail: '~100,500 ha active Boliden ground; nearest block ~12 km (VMS/Au)',
    color: '#f43f5e',
    portfolioStage: 'Monitor',
    lonLat: [19.284, 64.83],
    aliases: ['Njugg Boliden district', 'Skellefte district'],
  },
];

const STATIC_GLOBAL_TARGET_LAYERS: StaticGlobalTargetLayer[] = [
  ...SWEDEN_NJUGG_STATIC_TARGET_LAYERS,
];

type LayerPreset = {
  id: string;
  label: string;
  layerIds: string[];
};

type LayerQaResultStatus = 'pass' | 'warn' | 'fail' | 'skipped';

type LayerQaResult = {
  id: string;
  label: string;
  group: LayerControlGroup;
  status: LayerQaResultStatus;
  message: string;
  elapsedMs: number;
};

type LayerQaReport = {
  startedAt: string;
  finishedAt: string;
  total: number;
  passed: number;
  warned: number;
  failed: number;
  skipped: number;
  results: LayerQaResult[];
};

type InspectionRow = {
  label: string;
  value: string;
};

type InspectionRecord = {
  id: string;
  title: string;
  subtitle?: string;
  coordinates: string;
  layerLabel?: string;
  source?: string;
  portfolioStage?: PortfolioStage;
  portfolioStageDefinition?: PortfolioStageDefinition;
  confidence?: string;
  nextAction?: string;
  agentEval?: AgentEvalRequest;
  rows: InspectionRow[];
};

type ActionSearchResultKind = 'Feature' | 'Layer' | 'Target Area' | 'Status' | 'Borehole' | 'Project' | 'Ranked Target';

type ActionSearchResult = {
  id: string;
  kind: ActionSearchResultKind;
  label: string;
  subtitle?: string;
  color: string;
  layerIds: string[];
  layerControlId?: string;
  projectId?: string;
  projectName?: string;
  portfolioStage?: PortfolioStage;
  feature?: any;
  layer?: any;
  lonLat?: [number, number];
  extent?: [number, number, number, number];
  rows?: InspectionRow[];
  payload?: any;
};

type MapSearchProject = {
  id: string;
  name?: string;
  location?: string;
  coordinates?: { lat?: number | string | null; lng?: number | string | null };
};

type RankedTargetSearchRecord = {
  id?: number;
  target_slug?: string;
  target_name?: string;
  country?: string;
  commodity?: string;
  tier?: string;
  tier_label?: string;
  rank_in_tier?: number | null;
  key_grade?: string | null;
  conviction?: string | null;
  open_ground?: string | null;
  claim_size_ha?: number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  detail_page?: string | null;
  notes?: string | null;
  staking_action?: string | null;
  score?: number | null;
  blocker?: string | null;
};

type ActionSearchRestoreState = {
  projectId?: string | null;
  viewport: {
    lon: number | null;
    lat: number | null;
    zoom: number | null;
  };
  activeLayers: string[];
  projectGeoJsonVisibility: Record<string, boolean>;
  projectGeoJsonExpanded: boolean;
  projectGeoJsonPolygonOnly: boolean;
  inspectionRecord: InspectionRecord | null;
  actionSearchInput: string;
};

type MapQaBridgeState = {
  version: string;
  selectedProject: {
    id: string;
    name?: string;
    location?: string;
  } | null;
  viewport: {
    lon: number | null;
    lat: number | null;
    zoom: number | null;
  };
  layerPanelCollapsed: boolean;
  activeLayers: string[];
  inspection: InspectionRecord | null;
  projectGeoJsonExpanded: boolean;
  projectGeoJsonPolygonOnly: boolean;
  projectGeoJsonVisibility: Record<string, boolean>;
  layers: Array<Omit<LayerControlItem, 'onToggle'>>;
  presets: LayerPreset[];
  projectGeoJsonLayers: Array<ProjectGeoJsonPolygonLayer & {
    controlId: string;
    checked: boolean;
    visible: boolean;
    featureCount: number;
  }>;
};

type MapQaBridgeResponse =
  | { ok: true; state: MapQaBridgeState }
  | { ok: false; error: string; state: MapQaBridgeState };

type MapQaBridge = {
  version: string;
  getState: () => MapQaBridgeState;
  setViewport: (viewport: { lon: number; lat: number; zoom?: number }) => Promise<MapQaBridgeResponse>;
  toggleLayer: (layerId: string, visible?: boolean) => Promise<MapQaBridgeResponse>;
  applyPreset: (presetId: string) => Promise<MapQaBridgeResponse>;
  setProjectGeoJsonLayers: (
    layerIds: string[],
    options?: { visible?: boolean; only?: boolean; polygonOnly?: boolean }
  ) => Promise<MapQaBridgeResponse>;
  runLayerHealthCheck: (layerIds?: string[]) => Promise<LayerQaReport>;
  getShareUrl: () => string;
  runScenario: (scenarioId: string) => Promise<MapQaBridgeResponse>;
};

const QA_BRIDGE_VERSION = '2026-05-24';
const QA_BRIDGE_ALLOWED_HOSTS = new Set(['localhost', '127.0.0.1', 'app.geomorphic.ai']);
const TARGET_AREA_DISTINCT_LAYER_COLORS = [
  '#f59e0b',
  '#38bdf8',
  '#a855f7',
  '#22c55e',
  '#f43f5e',
  '#14b8a6',
  '#facc15',
  '#e879f9',
  '#60a5fa',
  '#fb7185',
  '#84cc16',
  '#f97316',
];
const FINLAND_STAKING_BOUNDARY_IDS = new Set([
  'koitelainen',
  'kaitaselka',
  'kuusivaara',
  'salla_gold',
  'tongosoivanaapa',
  'tervola_cu',
  'raahe_ladoga',
  'paljakka',
  'paljakka_karenssi',
  'paljakka_v4',
  'hammaslahti',
  'jarvi_pohjanmaa',
  'ritakallionmaa',
  'ritakallionmaa_option_f',
  'dragon_jokisivu',
  'dragon_uunimaki',
  'vammala_inferred_corridor',
  'cluster_317_ritakallionmaa',
  'cluster_196_palokallio',
  'cluster_422_punovuori',
  'cluster_753_vanttila',
  'perakorpi_lumikangas',
  'perakorpi_hyyppa',
  'perakorpi_kauhajarvi',
  'perakorpi_peramaa',
]);

type StakingBoundaryFeatureLike = {
  properties?: {
    id?: unknown;
  };
};

const getStakingBoundaryFeatureId = (feature: StakingBoundaryFeatureLike): string | null => {
  const id = feature?.properties?.id;
  return typeof id === 'string' && id ? id : null;
};

const isStakingBoundaryFeatureForProject = (
  feature: StakingBoundaryFeatureLike,
  projectId?: string | null
) => {
  const id = getStakingBoundaryFeatureId(feature);
  if (!id) {
    return false;
  }
  if (projectId === 'project-gtk-finland') {
    return FINLAND_STAKING_BOUNDARY_IDS.has(id);
  }
  if (projectId === 'project-sgu-sweden') {
    return !FINLAND_STAKING_BOUNDARY_IDS.has(id);
  }
  return false;
};

const getProjectStakingBoundaryIds = (
  features: ReadonlyArray<StakingBoundaryFeatureLike>,
  projectId?: string | null
) => Array.from(
  new Set(
    features
      .filter((feature) => isStakingBoundaryFeatureForProject(feature, projectId))
      .map(getStakingBoundaryFeatureId)
      .filter((id): id is string => Boolean(id))
  )
);

const isMapQaBridgeEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_ENABLE_QA_BRIDGE === 'true' ||
    QA_BRIDGE_ALLOWED_HOSTS.has(window.location.hostname)
  );
};

const waitForMapQaFrame = (delayMs = 350) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));

const hasSharedMapViewportForProject = (projectId?: string | null) => {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (!params.has('lat') || !params.has('lon')) {
    return false;
  }
  const sharedProject = params.get('project');
  return !sharedProject || sharedProject === projectId;
};

const getLayerSearchText = (item: LayerControlItem) =>
  `${item.label} ${item.group} ${item.targetArea || ''} ${item.detail || ''} ${item.count || ''} ${item.portfolioStage || ''}`.toLowerCase();

const LayerOperationsPanel: React.FC<{
  items: LayerControlItem[];
  presets: LayerPreset[];
  onPresetApply?: (presetId: string) => void;
  onRunLayerQa?: (layerIds?: string[]) => Promise<LayerQaReport>;
  onCopyShareUrl?: () => Promise<string>;
  storageKey?: string;
}> = ({ items, presets, onPresetApply, onRunLayerQa, onCopyShareUrl, storageKey = 'global' }) => {
  const [query, setQuery] = useState('');
  const [layerScope, setLayerScope] = useState<'all' | 'active'>('all');
  const [portfolioStageFilter, setPortfolioStageFilter] = useState<PortfolioStage | 'all'>('all');
  const [qaRunning, setQaRunning] = useState(false);
  const [qaReport, setQaReport] = useState<LayerQaReport | null>(null);
  const [shareStatus, setShareStatus] = useState('');
  const [activeLayersExpanded, setActiveLayersExpanded] = useState(false);
  const [expandedLayerLegends, setExpandedLayerLegends] = useState<Record<string, boolean>>({});
  const groupStorageKey = `${MAP_LAYER_GROUPS_PREFIX}${storageKey}`;
  const targetAreaStorageKey = `${MAP_LAYER_TARGET_AREAS_PREFIX}${storageKey}`;
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => readBooleanRecord(groupStorageKey));
  const [expandedTargetAreas, setExpandedTargetAreas] = useState<Record<string, boolean>>(() => readBooleanRecord(targetAreaStorageKey));
  const skipGroupPersistRef = useRef(true);
  const skipTargetAreaPersistRef = useRef(true);
  const normalizedQuery = query.trim().toLowerCase();
  const activeItems = items.filter((item) => item.checked);
  const availablePortfolioStages = PORTFOLIO_STAGE_ORDER.filter((stage) =>
    items.some((item) => item.portfolioStage === stage)
  );
  const availablePortfolioStageSignature = availablePortfolioStages.join('|');
  const portfolioStageCounts = availablePortfolioStages.reduce<Record<string, number>>((counts, stage) => {
    counts[stage] = items.filter((item) => item.portfolioStage === stage).length;
    return counts;
  }, {});
  const visibleItems = items.filter((item) => !HIDE_DISABLED_LAYER_ENTRIES || !item.disabledReason);
  const scopeFilteredItems = layerScope === 'active'
    ? visibleItems.filter((item) => item.checked)
    : visibleItems;
  const statusFilteredItems = portfolioStageFilter === 'all'
    ? scopeFilteredItems
    : scopeFilteredItems.filter((item) => item.portfolioStage === portfolioStageFilter);
  const filteredItems = normalizedQuery
    ? statusFilteredItems.filter((item) => getLayerSearchText(item).includes(normalizedQuery))
    : statusFilteredItems;
  const qaCandidateIds = filteredItems
    .filter((item) => item.group !== 'Base Map' && !item.disabled)
    .map((item) => item.id);

  useEffect(() => {
    if (portfolioStageFilter !== 'all' && !availablePortfolioStageSignature.split('|').includes(portfolioStageFilter)) {
      setPortfolioStageFilter('all');
    }
  }, [availablePortfolioStageSignature, portfolioStageFilter]);

  useEffect(() => {
    skipGroupPersistRef.current = true;
    skipTargetAreaPersistRef.current = true;
    setExpandedGroups(readBooleanRecord(groupStorageKey));
    setExpandedTargetAreas(readBooleanRecord(targetAreaStorageKey));
  }, [groupStorageKey, targetAreaStorageKey]);

  useEffect(() => {
    if (skipGroupPersistRef.current) {
      skipGroupPersistRef.current = false;
      return;
    }
    writeJsonStorage(groupStorageKey, expandedGroups);
  }, [groupStorageKey, expandedGroups]);

  useEffect(() => {
    if (skipTargetAreaPersistRef.current) {
      skipTargetAreaPersistRef.current = false;
      return;
    }
    writeJsonStorage(targetAreaStorageKey, expandedTargetAreas);
  }, [targetAreaStorageKey, expandedTargetAreas]);

  const groupedItems = LAYER_GROUP_ORDER
    .map((group) => ({
      group,
      items: filteredItems.filter((item) => item.group === group),
      // Group N/M badges reflect actual layer state, not the current chip/search
      // filter — otherwise the counts jump whenever a status chip is selected.
      activeCount: visibleItems.filter((item) => item.group === group && item.checked).length,
      totalCount: visibleItems.filter((item) => item.group === group).length,
      matchCount: statusFilteredItems.filter((item) => item.group === group).length
    }))
    .filter((entry) => entry.items.length > 0 || (!normalizedQuery && entry.matchCount > 0));

  const applyPreset = (preset: LayerPreset) => {
    onPresetApply?.(preset.id);
    const enabled = new Set(preset.layerIds);
    items.forEach((item) => {
      if (item.disabled || item.presetControlled === false) {
        return;
      }
      void item.onToggle(enabled.has(item.id));
    });
  };

  const clearOperationalLayers = () => {
    onPresetApply?.('clear');
    items.forEach((item) => {
      if (item.disabled || item.presetControlled === false || item.group === 'Base Map') {
        return;
      }
      void item.onToggle(false);
    });
  };

  const runVisibleLayerQa = async () => {
    if (!onRunLayerQa || qaCandidateIds.length === 0 || qaRunning) {
      return;
    }
    setQaRunning(true);
    try {
      const report = await onRunLayerQa(qaCandidateIds);
      setQaReport(report);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Layer QA failed before a report was produced';
      const now = new Date().toISOString();
      setQaReport({
        startedAt: now,
        finishedAt: now,
        total: 1,
        passed: 0,
        warned: 0,
        failed: 1,
        skipped: 0,
        results: [{
          id: 'layer-qa',
          label: 'Layer QA',
          group: 'Operations',
          status: 'fail',
          message,
          elapsedMs: 0,
        }],
      });
    } finally {
      setQaRunning(false);
    }
  };

  const copyShareUrl = async () => {
    if (!onCopyShareUrl) {
      return;
    }
    const url = await onCopyShareUrl();
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      setShareStatus('Copied');
    } else {
      setShareStatus('URL ready');
    }
    window.setTimeout(() => setShareStatus(''), 2200);
  };

  const qaStatusColor = (status: LayerQaResultStatus) => {
    if (status === 'pass') return '#22c55e';
    if (status === 'warn') return '#f59e0b';
    if (status === 'fail') return '#ef4444';
    return '#94a3b8';
  };
  const qaVisibleResults = qaReport
    ? [
        ...qaReport.results.filter((result) => result.status === 'fail'),
        ...qaReport.results.filter((result) => result.status === 'warn'),
        ...qaReport.results.filter((result) => result.status === 'pass'),
        ...qaReport.results.filter((result) => result.status === 'skipped'),
      ].slice(0, 6)
    : [];

  const renderSwatch = (item: LayerControlItem) => {
    const base = {
      width: item.swatch === 'line' || item.swatch === 'dash' ? 18 : 12,
      height: item.swatch === 'line' || item.swatch === 'dash' ? 5 : 12,
      flexShrink: 0,
      opacity: 1,
    };
    if (item.swatch === 'gradient') {
      return (
        <Box
          sx={{
            ...base,
            borderRadius: '2px',
            background: 'linear-gradient(90deg, #1d4ed8, #22c55e, #facc15, #ef4444)',
            border: '1px solid rgba(255,255,255,0.68)',
            filter: item.checked ? 'none' : 'saturate(0.85) brightness(0.82)'
          }}
        />
      );
    }
    if (item.swatch === 'line' || item.swatch === 'dash') {
      return (
        <Box
          sx={{
            ...base,
            borderRadius: 1,
            borderTop: `${item.swatch === 'dash' ? 2 : 3}px ${item.swatch === 'dash' ? 'dashed' : 'solid'} ${item.color}`,
            backgroundColor: hexToRgba(item.color, item.checked ? 0.22 : 0.14),
            boxShadow: `0 0 0 1px ${hexToRgba(item.color, item.checked ? 0.44 : 0.26)} inset`
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          ...base,
          borderRadius: item.swatch === 'square' ? '2px' : '50%',
          backgroundColor: hexToRgba(item.color, item.checked ? 0.82 : 0.24),
          border: `${item.checked ? 2 : 1.5}px solid ${item.color}`,
          boxShadow: item.checked ? `0 0 0 1px ${hexToRgba(item.color, 0.22)}` : 'none',
        }}
      />
    );
  };

  const renderPortfolioBadge = (stage?: PortfolioStage) => {
    if (!stage) {
      return null;
    }
    const style = PORTFOLIO_STAGE_DEFINITIONS[stage];
    return (
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          px: 0.55,
          py: '1px',
          borderRadius: '3px',
          border: `1px solid ${style.border}`,
          backgroundColor: style.background,
          color: style.color,
          fontSize: '10px',
          fontFamily: MAP_UI_MONO_FONT,
          fontWeight: 800,
          letterSpacing: 0,
          lineHeight: 1.2,
        }}
      >
          {style.shortLabel}
      </Box>
    );
  };

  const getSharedPortfolioStage = (areaItems: LayerControlItem[]) => {
    const stages = Array.from(
      new Set(areaItems.map((item) => item.portfolioStage).filter((stage): stage is PortfolioStage => Boolean(stage)))
    );
    return stages.length === 1 ? stages[0] : undefined;
  };

  const renderLayerLegend = (item: LayerControlItem) => {
    if (!item.checked || !item.legend?.length || !expandedLayerLegends[item.id]) {
      return null;
    }
    const renderLegendMarker = (entry: LayerLegendEntry) => {
      const swatch = entry.swatch || 'circle';
      if (swatch === 'line' || swatch === 'dash') {
        return (
          <Box
            component="span"
            sx={{
              width: 22,
              height: 7,
              borderTop: `2px ${swatch === 'dash' ? 'dashed' : 'solid'} ${entry.color}`,
              backgroundColor: entry.color.startsWith('#') ? hexToRgba(entry.color, 0.12) : entry.color,
              borderRadius: '2px',
              flexShrink: 0,
              boxShadow: `0 0 0 1px ${hexToRgba(entry.strokeColor || entry.color, 0.18)} inset`,
            }}
          />
        );
      }
      if (swatch === 'square') {
        return (
          <Box
            component="span"
            sx={{
              width: 11,
              height: 11,
              borderRadius: '2px',
              flexShrink: 0,
              backgroundColor: entry.color.startsWith('#') ? hexToRgba(entry.color, 0.14) : entry.color,
              border: `2px solid ${entry.strokeColor || entry.color}`,
              boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.28)',
            }}
          />
        );
      }
      return (
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: entry.color,
            border: `1px solid ${entry.strokeColor || 'rgba(0, 0, 0, 0.75)'}`,
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.30)'
          }}
        />
      );
    };
    return (
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: 'grid',
          // Two columns only when every entry is short; otherwise flow to one.
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '4px 8px',
          ml: LEGEND_UI.textColumnInset,
          mr: 0.4,
          mt: 0.35,
          mb: 0.5,
          px: 0.7,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${hexToRgba(item.color, 0.28)}`,
          backgroundColor: 'rgba(2, 6, 23, 0.42)'
        }}
      >
        {item.legend.map((entry) => (
          <Box key={`${item.id}:${entry.label}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
            {renderLegendMarker(entry)}
            <Typography sx={{ color: '#cbd5e1', fontSize: '10px', fontFamily: MAP_UI_FONT, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {entry.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderLayerRow = (item: LayerControlItem, showLegend = true) => {
    const detailText = item.disabledReason || item.detail;
    const labelColor = item.disabled
      ? '#94a3b8'
      : item.checked
        ? LEGEND_UI.labelColorChecked
        : LEGEND_UI.labelColorUnchecked;
    const titleText = detailText ? `${item.label} — ${detailText}` : item.label;
    const row = (
      <Box
        key={item.id}
        title={titleText}
        onClick={() => !item.disabled && void item.onToggle(!item.checked)}
        sx={{
          // Let labels use the row. Counts and legend controls only consume space
          // when present, instead of reserving empty columns beside every label.
          display: 'grid',
          gridTemplateColumns: '26px minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 0.75,
          minHeight: LEGEND_UI.rowMinHeight,
          ml: 1.25,
          mr: 0.25,
          px: 0.6,
          py: 0.45,
          borderRadius: '4px',
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          opacity: item.disabled ? 0.55 : 1,
          backgroundColor: item.checked ? 'rgba(255, 255, 255, 0.065)' : 'transparent',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: item.disabled ? 'transparent' : item.checked ? 'rgba(255, 255, 255, 0.09)' : 'rgba(255, 255, 255, 0.045)' },
          '&:focus-within': { boxShadow: 'inset 0 0 0 1px rgba(125, 211, 252, 0.32)' }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', opacity: item.checked ? 1 : 0.68 }}>
          {renderSwatch(item)}
        </Box>
        <Box sx={{ minWidth: 0, py: 0.15 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.55, minWidth: 0 }}>
            <Typography sx={{ ...LEGEND_UI.labelText, color: labelColor, lineHeight: 1.25, whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 0, flex: 1 }}>
              {item.label}
            </Typography>
            {renderPortfolioBadge(item.portfolioStage)}
          </Box>
          {detailText && (
            <Typography sx={{ ...LEGEND_UI.detailText, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {detailText}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.45, flexShrink: 0 }}>
          {item.count !== undefined && (
            <Typography sx={{ ...LEGEND_UI.countText, color: item.checked ? '#d8e1ea' : '#8795a3', minWidth: 34, textAlign: 'right' }}>
              {item.count}
            </Typography>
          )}
          {showLegend && item.checked && item.legend?.length ? (
            <Tooltip title={expandedLayerLegends[item.id] ? 'Hide legend' : 'Show legend'} placement="left">
              <IconButton
                size="small"
                aria-label={`${expandedLayerLegends[item.id] ? 'Hide' : 'Show'} ${item.label} legend`}
                onClick={(event) => {
                  event.stopPropagation();
                  setExpandedLayerLegends((current) => ({ ...current, [item.id]: !current[item.id] }));
                }}
                sx={{ width: 20, height: 20, p: 0, color: '#8292a3', '&:hover': { color: '#dbeafe', backgroundColor: 'rgba(125, 211, 252, 0.08)' } }}
              >
                {expandedLayerLegends[item.id]
                  ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                  : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          ) : null}
          <Switch
            size="small"
            disabled={item.disabled}
            checked={item.checked}
            inputProps={{ 'aria-label': `${item.checked ? 'Hide' : 'Show'} ${item.label}` }}
            onClick={(event) => event.stopPropagation()}
            onChange={() => {
              if (!item.disabled) void item.onToggle(!item.checked);
            }}
            sx={{
              width: 36,
              height: 22,
              p: '3px',
              '& .MuiSwitch-switchBase': {
                p: '5px',
                color: '#82909e',
                '&.Mui-checked': {
                  transform: 'translateX(14px)',
                  color: '#f8fafc',
                  '& + .MuiSwitch-track': { backgroundColor: item.color, opacity: 0.72 },
                },
              },
              '& .MuiSwitch-thumb': { width: 12, height: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.5)' },
              '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#526171', opacity: 0.48 },
              '& .Mui-disabled + .MuiSwitch-track': { opacity: 0.18 },
            }}
          />
        </Box>
      </Box>
    );
    const rowWithLegend = (
      <Box key={item.id}>
        {item.disabledReason ? (
          <Tooltip title={item.disabledReason} placement="left">
            {row}
          </Tooltip>
        ) : row}
        {showLegend ? renderLayerLegend(item) : null}
      </Box>
    );
    return rowWithLegend;
  };

  const renderLayerRows = (groupItems: LayerControlItem[]) => {
    const renderedLegendGroups = new Set<string>();
    return groupItems.map((item) => {
      let showLegend = true;
      if (item.checked && item.legendGroup) {
        showLegend = !renderedLegendGroups.has(item.legendGroup);
        renderedLegendGroups.add(item.legendGroup);
      }
      return renderLayerRow(item, showLegend);
    });
  };

  type TargetAreaEntry =
    | { type: 'item'; item: LayerControlItem }
    | { type: 'area'; area: string; items: LayerControlItem[] };

  const getTargetAreaEntries = (groupItems: LayerControlItem[]): TargetAreaEntry[] => {
    const areaCounts = groupItems.reduce<Record<string, number>>((counts, item) => {
      const area = item.targetArea?.trim();
      if (area) {
        counts[area] = (counts[area] || 0) + 1;
      }
      return counts;
    }, {});
    const emittedAreas = new Set<string>();
    return groupItems.flatMap<TargetAreaEntry>((item) => {
      const area = item.targetArea?.trim();
      if (!area || areaCounts[area] < 2) {
        return [{ type: 'item' as const, item }];
      }
      if (emittedAreas.has(area)) {
        return [];
      }
      emittedAreas.add(area);
      return [{
        type: 'area' as const,
        area,
        items: groupItems.filter((candidate) => candidate.targetArea?.trim() === area)
      }];
    });
  };

  const renderTargetArea = (area: string, areaItems: LayerControlItem[], accent: string) => {
    const areaKey = `targets:${area}`;
    const enabledItems = areaItems.filter((item) => !item.disabled);
    const activeCount = areaItems.filter((item) => item.checked).length;
    const totalCount = areaItems.length;
    const allEnabledActive = enabledItems.length > 0 && enabledItems.every((item) => item.checked);
    const isPartiallyActive = activeCount > 0 && !allEnabledActive;
    const isExpanded = normalizedQuery ? true : (expandedTargetAreas[areaKey] ?? activeCount > 0);
    const areaColor = areaItems.find((item) => item.checked)?.color || areaItems[0]?.color || accent;
    const portfolioStage = getSharedPortfolioStage(areaItems);
    const toggleArea = (visible: boolean) => {
      enabledItems.forEach((item) => {
        void item.onToggle(visible);
      });
    };

    return (
      <Box
        key={areaKey}
        sx={{
          my: 0.1,
          // An open project becomes a full-width workspace; its child layers
          // then use the same responsive two-column rhythm as the main list.
          gridColumn: isExpanded ? '1 / -1' : 'auto',
        }}
      >
        <Box
          onClick={() => setExpandedTargetAreas((prev) => ({ ...prev, [areaKey]: !isExpanded }))}
          title={`${area} — ${totalCount} target layers`}
          sx={{
            // Notion-style tree row: disclosure | project glyph | label | count | toggle.
            display: 'grid',
            gridTemplateColumns: '22px 26px minmax(0, 1fr) 58px 40px',
            alignItems: 'center',
            columnGap: 0.55,
            minHeight: 44,
            ml: 1.05,
            mr: 0.25,
            px: 0.45,
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: activeCount ? 'rgba(255, 255, 255, 0.055)' : 'transparent',
            '&:hover': { backgroundColor: activeCount ? 'rgba(255, 255, 255, 0.085)' : 'rgba(255, 255, 255, 0.045)' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8795a3' }}>
            {isExpanded ? (
              <TreeArrowDownIcon sx={{ fontSize: 17 }} />
            ) : (
              <TreeArrowRightIcon sx={{ fontSize: 17 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1px', minWidth: 0 }}>
            {areaItems.slice(0, 4).map((item) => (
              <Box
                key={`${areaKey}-${item.id}-swatch`}
                sx={{
                  width: 3,
                  height: 16,
                  borderRadius: '1.5px',
                  backgroundColor: hexToRgba(item.color, item.checked ? 0.76 : 0.24),
                  border: `1px ${item.swatch === 'dash' ? 'dashed' : 'solid'} ${item.color}`,
                  opacity: item.checked ? 1 : 0.55
                }}
              />
            ))}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
              <Typography sx={{ ...LEGEND_UI.labelText, fontSize: '13.5px', fontWeight: 720, color: LEGEND_UI.labelColorChecked, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {area}
              </Typography>
              {renderPortfolioBadge(portfolioStage)}
            </Box>
            <Typography sx={{ ...LEGEND_UI.detailText, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {totalCount} target layers
            </Typography>
          </Box>
          <Typography sx={{ ...LEGEND_UI.countText, color: activeCount ? '#d8e1ea' : '#8795a3', flexShrink: 0, textAlign: 'right' }}>
            {activeCount}/{totalCount}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Checkbox
              size="small"
              checked={allEnabledActive}
              indeterminate={isPartiallyActive}
              disabled={enabledItems.length === 0}
              inputProps={{ 'aria-label': `${allEnabledActive ? 'Hide' : 'Show'} all ${area} layers` }}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleArea(!allEnabledActive)}
              sx={{
                p: 0.35,
                color: '#667586',
                '&.Mui-checked': { color: areaColor },
                '&.MuiCheckbox-indeterminate': { color: areaColor },
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            />
          </Box>
        </Box>
        <Collapse in={isExpanded} timeout="auto">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              columnGap: 0.8,
              rowGap: 0.1,
              pl: 2.25,
              py: 0.15,
            }}
          >
            {areaItems.map((item) => renderLayerRow(item))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
      <TextField
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search layers"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#7dd3fc', fontSize: 15 }} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <Tooltip title="Clear layer search" placement="top">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label="Clear layer search"
                  onClick={() => setQuery('')}
                  sx={{
                    width: 22,
                    height: 22,
                    color: '#94a3b8',
                    '&:hover': {
                      color: '#e2e8f0',
                      backgroundColor: 'rgba(148, 163, 184, 0.16)',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: 34,
            backgroundColor: 'rgba(15, 23, 42, 0.82)',
            color: '#e2e8f0',
            fontFamily: MAP_UI_FONT,
            fontSize: '11px',
            borderRadius: 1,
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148, 163, 184, 0.28)' },
          '& .MuiInputBase-input::placeholder': { color: '#94a3b8', opacity: 1 }
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          {([
            { id: 'all' as const, label: 'All', count: visibleItems.length },
            { id: 'active' as const, label: 'Active', count: activeItems.length },
          ]).map((scope) => (
            <Chip
              key={scope.id}
              label={`${scope.label} ${scope.count}`}
              size="small"
              onClick={() => setLayerScope(scope.id)}
              sx={{
                height: 24,
                borderRadius: 1,
                border: `1px solid ${layerScope === scope.id ? 'rgba(125, 211, 252, 0.62)' : 'rgba(148, 163, 184, 0.28)'}`,
                backgroundColor: layerScope === scope.id ? 'rgba(125, 211, 252, 0.11)' : 'transparent',
                color: layerScope === scope.id ? '#dbeafe' : '#94a3b8',
                fontFamily: MAP_UI_FONT,
                fontSize: '10px',
                fontWeight: layerScope === scope.id ? 700 : 600,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          ))}
        </Box>
        <Typography sx={{ fontFamily: MAP_UI_MONO_FONT, fontSize: '10px', color: '#718096', fontVariantNumeric: 'tabular-nums' }}>
          {filteredItems.length} shown
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr)', gap: 0.8, alignItems: 'start' }}>
        <Typography sx={{ fontFamily: MAP_UI_MONO_FONT, fontSize: '9px', fontWeight: 800, color: '#718096', letterSpacing: 0.7, textTransform: 'uppercase', pt: 0.65 }}>
          Views
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {presets.map((preset) => (
            <Chip
              key={preset.id}
              label={preset.label}
              size="small"
              clickable
              onClick={() => applyPreset(preset)}
              sx={{
                height: 24,
                borderRadius: 1,
                border: '1px solid rgba(148, 163, 184, 0.32)',
                backgroundColor: 'transparent',
                color: '#cbd5e1',
                fontFamily: MAP_UI_FONT,
                fontSize: LEGEND_UI.chipFontSize,
                '& .MuiChip-label': { px: 0.85 },
                '&:hover': { borderColor: '#58a6ff', backgroundColor: 'rgba(88, 166, 255, 0.1)', color: '#dbeafe' }
              }}
            />
          ))}
          <Chip
            label="Clear"
            size="small"
            clickable
            onClick={clearOperationalLayers}
            sx={{
              height: 24,
              borderRadius: 1,
              border: '1px solid rgba(239, 68, 68, 0.32)',
              backgroundColor: 'transparent',
              color: '#f0a5a5',
              fontFamily: MAP_UI_FONT,
              fontSize: LEGEND_UI.chipFontSize,
              '& .MuiChip-label': { px: 0.85 },
              '&:hover': { borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#fecaca' }
            }}
          />
        </Box>
      </Box>

      {availablePortfolioStages.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr)', gap: 0.8, alignItems: 'start' }}>
          <Typography sx={{ fontFamily: MAP_UI_MONO_FONT, fontSize: '9px', fontWeight: 800, color: '#718096', letterSpacing: 0.7, textTransform: 'uppercase', pt: 0.65 }}>
            Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.45, alignItems: 'center' }}>
          <Chip
            label="All status"
            size="small"
            onClick={() => setPortfolioStageFilter('all')}
            sx={{
              height: 23,
              borderRadius: 1,
              border: '1px solid rgba(148, 163, 184, 0.38)',
              backgroundColor: portfolioStageFilter === 'all' ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.62)',
              color: '#cbd5e1',
              fontFamily: MAP_UI_FONT,
              fontSize: '10px',
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
          {availablePortfolioStages.map((stage) => {
            const style = PORTFOLIO_STAGE_DEFINITIONS[stage];
            const selected = portfolioStageFilter === stage;
            return (
              <Chip
                key={`portfolio-filter-${stage}`}
                label={`${style.shortLabel} ${portfolioStageCounts[stage] || 0}`}
                size="small"
                onClick={() => setPortfolioStageFilter(selected ? 'all' : stage)}
                sx={{
                  height: 23,
                  borderRadius: 1,
                  border: `1px solid ${style.border}`,
                  backgroundColor: selected ? style.background : 'rgba(15, 23, 42, 0.62)',
                  color: style.color,
                  fontFamily: MAP_UI_MONO_FONT,
                  fontSize: '10px',
                  fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 }
                }}
              />
            );
          })}
          </Box>
        </Box>
      )}

      {activeItems.length > 0 && (
        <Box>
          <Box
            onClick={() => setActiveLayersExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.5,
              py: 0.35,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' }
            }}
          >
            {activeLayersExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
            )}
            <Typography sx={{ ...LEGEND_UI.detailText, color: '#cbd5e1', fontWeight: 600, flex: 1 }}>
              {activeItems.length} active
            </Typography>
            <Typography
              component="button"
              onClick={(event) => { event.stopPropagation(); clearOperationalLayers(); }}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#f0a5a5',
                fontFamily: MAP_UI_FONT,
                fontSize: '10px',
                fontWeight: 600,
                '&:hover': { color: '#fecaca' }
              }}
            >
              Hide all
            </Typography>
          </Box>
          <Collapse in={activeLayersExpanded} timeout="auto">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {activeItems.map((item) => (
                <Chip
                  key={`active-${item.id}`}
                  label={item.label}
                  size="small"
                  onDelete={item.group === 'Base Map' ? undefined : () => void item.onToggle(false)}
                  sx={{
                    maxWidth: '100%',
                    height: 22,
                    borderRadius: 1,
                    backgroundColor: 'rgba(15, 23, 42, 0.94)',
                    border: `1px solid ${item.color}`,
                    color: '#e5e7eb',
                    fontFamily: MAP_UI_FONT,
                    fontSize: LEGEND_UI.chipFontSize,
                    '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                    '& .MuiChip-deleteIcon': { color: '#94a3b8', fontSize: 14 }
                  }}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {(onRunLayerQa || onCopyShareUrl) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: onRunLayerQa && onCopyShareUrl ? 'repeat(2, minmax(0, 1fr))' : '1fr',
            gap: 0.6,
          }}
        >
          {onCopyShareUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
              onClick={() => void copyShareUrl()}
              sx={{
                minWidth: 0,
                height: 26,
                px: 0.8,
                borderRadius: 1,
                borderColor: shareStatus ? 'rgba(34, 197, 94, 0.7)' : 'rgba(148, 163, 184, 0.42)',
                color: shareStatus ? '#bbf7d0' : '#cbd5e1',
                fontSize: '10px',
                fontFamily: MAP_UI_FONT,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: 0.5 },
                '&:hover': { borderColor: '#58a6ff', backgroundColor: 'rgba(88, 166, 255, 0.1)' }
              }}
            >
              {shareStatus || 'Share view'}
            </Button>
          )}
          {onRunLayerQa && (
            <Button
              size="small"
              variant="outlined"
              disabled={qaRunning || qaCandidateIds.length === 0}
              startIcon={qaRunning ? undefined : <FactCheckIcon sx={{ fontSize: 14 }} />}
              onClick={() => void runVisibleLayerQa()}
              sx={{
                minWidth: 0,
                height: 26,
                px: 0.8,
                borderRadius: 1,
                borderColor: qaReport?.failed ? 'rgba(239, 68, 68, 0.72)' : 'rgba(148, 163, 184, 0.42)',
                color: qaReport?.failed ? '#fecaca' : '#cbd5e1',
                fontSize: '10px',
                fontFamily: MAP_UI_FONT,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: 0.5 },
                '&:hover': { borderColor: '#58a6ff', backgroundColor: 'rgba(88, 166, 255, 0.1)' },
                '&.Mui-disabled': { color: '#64748b', borderColor: 'rgba(100, 116, 139, 0.28)' }
              }}
            >
              {qaRunning ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}>
                  <CircularProgress size={12} sx={{ color: '#bfdbfe' }} />
                  Running
                </Box>
              ) : 'Run QA'}
            </Button>
          )}
        </Box>
      )}

      {qaReport && (
        <Box
          sx={{
            borderRadius: 1,
            border: `1px solid ${qaReport.failed ? 'rgba(239, 68, 68, 0.45)' : qaReport.warned ? 'rgba(245, 158, 11, 0.42)' : 'rgba(34, 197, 94, 0.38)'}`,
            backgroundColor: 'rgba(2, 6, 23, 0.38)',
            px: 0.8,
            py: 0.65,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, mb: qaVisibleResults.length ? 0.45 : 0 }}>
            <Typography sx={{ color: '#e2e8f0', fontSize: '10.5px', fontFamily: MAP_UI_FONT, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
              Layer QA
            </Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '10px', fontFamily: MAP_UI_MONO_FONT, fontVariantNumeric: 'tabular-nums' }}>
              {qaReport.passed} ok / {qaReport.warned} warn / {qaReport.failed} fail
            </Typography>
          </Box>
          {qaVisibleResults.map((result) => (
            <Box key={`qa-${result.id}`} sx={{ display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr) auto', alignItems: 'center', gap: 0.55, minHeight: 20 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: qaStatusColor(result.status), boxShadow: `0 0 0 1px ${hexToRgba(qaStatusColor(result.status), 0.36)}` }} />
              <Typography sx={{ color: '#cbd5e1', fontSize: '10px', fontFamily: MAP_UI_FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {result.label}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '10px', fontFamily: MAP_UI_MONO_FONT, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(result.elapsedMs)}ms
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, mt: 0.25 }}>
        {groupedItems.map(({ group, items: groupItems, activeCount, totalCount }) => {
          const isExpanded = normalizedQuery ? true : (expandedGroups[group] ?? true);
          const accent = LAYER_GROUP_ACCENT[group];
          return (
            <Box key={group}>
              <Box
                onClick={() => setExpandedGroups((prev) => ({ ...prev, [group]: !isExpanded }))}
                sx={{
                  // Sticky so the current section header stays visible while the
                  // flex-filled scroll box (wrapping this panel) scrolls.
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#101820',
                  // Flat tree section: disclosure | label | count.
                  display: 'grid',
                  gridTemplateColumns: '24px minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: 0.6,
                  minHeight: 42,
                  cursor: 'pointer',
                  borderRadius: '4px',
                  px: 0.7,
                  boxShadow: 'inset 0 -1px 0 rgba(148, 163, 184, 0.11)',
                  '&:hover': { backgroundColor: '#141f29' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8795a3' }}>
                  {isExpanded ? (
                    <TreeArrowDownIcon sx={{ fontSize: 17 }} />
                  ) : (
                    <TreeArrowRightIcon sx={{ fontSize: 17 }} />
                  )}
                </Box>
                <Typography sx={{ ...LEGEND_UI.groupHeaderText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {group}
                </Typography>
                <Typography
                  title={`${activeCount} visible of ${totalCount}`}
                  sx={{
                    ...LEGEND_UI.countText,
                    minWidth: 42,
                    px: 0.65,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.055)',
                    color: activeCount ? '#e3ebf3' : '#91a0ae',
                    flexShrink: 0,
                    textAlign: 'center',
                  }}
                >
                  {activeCount} / {totalCount}
                </Typography>
              </Box>
              <Collapse in={isExpanded} timeout="auto">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    columnGap: 0.8,
                    rowGap: 0.05,
                    mt: 0.05,
                  }}
                >
                  {group === 'Targets'
                    ? getTargetAreaEntries(groupItems).map((entry) =>
                        entry.type === 'area'
                          ? renderTargetArea(entry.area, entry.items, accent)
                          : renderLayerRow(entry.item)
                      )
                    : renderLayerRows(groupItems)}
                </Box>
              </Collapse>
            </Box>
          );
        })}
        {groupedItems.length === 0 && (
          <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontFamily: MAP_UI_FONT, px: 0.5, py: 1 }}>
            No matching layers
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const InspectionDrawer: React.FC<{
  record: InspectionRecord | null;
  onClose: () => void;
  rightOffset: number;
  onEvaluate?: (request: AgentEvalRequest) => void;
  agentActivity?: React.ReactNode;
  docked?: boolean;
}> = ({ record, onClose, rightOffset, onEvaluate, agentActivity, docked = false }) => {
  const stage = record?.portfolioStageDefinition;
  const rows = record?.rows.slice(0, 18) || [];
  const [morphMenuAnchor, setMorphMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <Paper
      aria-hidden={!record}
      elevation={14}
      sx={{
        position: docked ? 'relative' : 'absolute',
        top: docked ? 'auto' : { xs: 76, md: 92 },
        right: docked ? 'auto' : { xs: 16, md: rightOffset },
        width: docked ? '100%' : 352,
        maxWidth: docked ? 'none' : 'calc(100vw - 32px)',
        height: docked ? '100%' : 'auto',
        minHeight: docked ? 0 : 'auto',
        maxHeight: docked ? 'none' : 'calc(100% - 116px)',
        display: record ? 'flex' : 'none',
        flexDirection: 'column',
        backgroundColor: docked ? '#0e141b' : 'rgba(5, 8, 13, 0.94)',
        color: '#e5e7eb',
        border: docked ? 'none' : `1px solid ${stage?.border || 'rgba(88, 166, 255, 0.38)'}`,
        borderRadius: docked ? 0 : 2,
        boxShadow: docked ? 'none' : '0 22px 48px rgba(0, 0, 0, 0.48)',
        backdropFilter: docked ? 'none' : 'blur(14px)',
        overflow: 'hidden',
        pointerEvents: 'auto',
        zIndex: 1390,
      }}
    >
      {record && (
        <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.35, py: 1.2, borderBottom: '1px solid rgba(148, 163, 184, 0.22)' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: '#f8fafc', fontFamily: MAP_UI_FONT, fontWeight: 800, fontSize: '13px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.title}>
              {record.title}
            </Typography>
            {record.subtitle && (
              <Typography sx={{ color: '#94a3b8', fontFamily: MAP_UI_FONT, fontSize: '9.5px', lineHeight: 1.25, mt: 0.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.subtitle}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8', mt: -0.4 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ p: docked ? 1.5 : 1.25, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, mb: 1 }}>
            {stage && (
              <Chip
                label={stage.shortLabel}
                size="small"
                sx={{
                  height: 22,
                  borderRadius: 1,
                  border: `1px solid ${stage.border}`,
                  backgroundColor: stage.background,
                  color: stage.color,
                  fontFamily: MAP_UI_MONO_FONT,
                  fontSize: '8px',
                  fontWeight: 900,
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '94px minmax(0, 1fr)', gap: '6px 10px', mb: 1.15 }}>
            <Typography sx={{ color: '#7dd3fc', fontFamily: MAP_UI_MONO_FONT, fontSize: '8.5px', textTransform: 'uppercase' }}>
              Coordinates
            </Typography>
            <Typography sx={{ color: '#e2e8f0', fontFamily: MAP_UI_MONO_FONT, fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {record.coordinates}
            </Typography>
            {record.source && (
              <>
                <Typography sx={{ color: '#7dd3fc', fontFamily: MAP_UI_MONO_FONT, fontSize: '8.5px', textTransform: 'uppercase' }}>
                  Source
                </Typography>
                <Typography sx={{ color: '#e2e8f0', fontFamily: MAP_UI_FONT, fontSize: '9.5px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={record.source}>
                  {record.source}
                </Typography>
              </>
            )}
          </Box>

          {record.agentEval && onEvaluate && (
            <>
              <Button
                fullWidth
                startIcon={
                  <Box component="img" src="/geomorphic-icon-black.png" alt="" sx={{ height: 22, width: 'auto', display: 'block' }} />
                }
                endIcon={<ExpandMoreIcon sx={{ fontSize: '18px !important', transform: morphMenuAnchor ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
                onClick={(event) => setMorphMenuAnchor(event.currentTarget)}
                sx={{
                  mb: 1.15,
                  py: 1.2,
                  borderRadius: 1,
                  backgroundColor: '#f8fafc',
                  color: '#0b0f14',
                  fontFamily: MAP_UI_MONO_FONT,
                  fontSize: '11.5px',
                  fontWeight: 900,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
                  '& .MuiButton-startIcon': { mr: 1.2 },
                  '&:hover': { backgroundColor: '#ffffff', boxShadow: '0 8px 22px rgba(0, 0, 0, 0.45)' },
                }}
              >
                Evaluate with Morph
              </Button>
              <Menu
                anchorEl={morphMenuAnchor}
                open={Boolean(morphMenuAnchor)}
                onClose={() => setMorphMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ zIndex: 1500 }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      minWidth: 300,
                      maxHeight: '62vh',
                      backgroundColor: 'rgba(13, 19, 27, 0.97)',
                      border: '1px solid rgba(226, 232, 240, 0.25)',
                      borderRadius: 1.5,
                      boxShadow: '0 18px 40px rgba(0, 0, 0, 0.55)',
                      backdropFilter: 'blur(12px)',
                    },
                  },
                }}
              >
                {MORPH_TASKS.map((task) => (
                  <MenuItem
                    key={task.id}
                    onClick={() => {
                      setMorphMenuAnchor(null);
                      onEvaluate({ ...(record.agentEval as AgentEvalRequest), task: task.id, taskLabel: task.label });
                    }}
                    sx={{ py: 0.9, px: 1.4, '&:hover': { backgroundColor: 'rgba(226, 232, 240, 0.08)' } }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#f1f5f9', fontFamily: MAP_UI_MONO_FONT, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        {task.label}
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontFamily: MAP_UI_FONT, fontSize: '9.5px', lineHeight: 1.3, mt: 0.2, whiteSpace: 'normal' }}>
                        {task.blurb}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {agentActivity && (
            <Box sx={{ mb: 1.15 }}>
              {agentActivity}
            </Box>
          )}

          {record.nextAction && (
            <Box sx={{ borderRadius: 1, border: `1px solid ${stage?.border || 'rgba(88, 166, 255, 0.30)'}`, backgroundColor: stage?.background || 'rgba(88, 166, 255, 0.10)', px: 0.9, py: 0.75, mb: 1.15 }}>
              <Typography sx={{ color: stage?.color || '#bfdbfe', fontFamily: MAP_UI_MONO_FONT, fontSize: '8px', fontWeight: 900, textTransform: 'uppercase', mb: 0.35 }}>
                Next Action
              </Typography>
              <Typography sx={{ color: '#e5e7eb', fontFamily: MAP_UI_FONT, fontSize: '10px', lineHeight: 1.35 }}>
                {record.nextAction}
              </Typography>
            </Box>
          )}

          {rows.length > 0 && (
            <Box sx={{ borderTop: '1px solid rgba(148, 163, 184, 0.24)', pt: 0.75 }}>
              {rows.map((row) => (
                <Box key={`${record.id}:${row.label}`} sx={{ display: 'grid', gridTemplateColumns: '112px minmax(0, 1fr)', gap: 0.8, minHeight: 21, alignItems: 'center' }}>
                  <Typography sx={{ color: '#94a3b8', fontFamily: MAP_UI_FONT, fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ color: '#e2e8f0', fontFamily: MAP_UI_FONT, fontSize: '9.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.value}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        </>
      )}
    </Paper>
  );
};

const MapActionSearch: React.FC<{
  inputValue: string;
  options: ActionSearchResult[];
  loading?: boolean;
  rightOffset: number;
  onInputChange: (value: string) => void;
  onSelect: (result: ActionSearchResult) => void | Promise<void>;
  restoreAvailable?: boolean;
  onRestore?: () => void | Promise<void>;
}> = ({ inputValue, options, loading, rightOffset, onInputChange, onSelect, restoreAvailable = false, onRestore }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 16,
      left: { xs: 16, md: 226 },
      right: { xs: 16, md: rightOffset },
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      pointerEvents: 'none',
      zIndex: 1405,
    }}
  >
  <Paper
    elevation={10}
    sx={{
      flex: '1 1 auto',
      minWidth: 0,
      maxWidth: { xs: 'calc(100vw - 32px)', md: 520 },
      backgroundColor: 'rgba(5, 8, 13, 0.88)',
      border: '1px solid rgba(125, 211, 252, 0.28)',
      borderRadius: 1.25,
      boxShadow: '0 14px 32px rgba(0, 0, 0, 0.44)',
      backdropFilter: 'blur(12px)',
      pointerEvents: 'auto',
      zIndex: 1405,
      overflow: 'hidden',
    }}
  >
    <Autocomplete
      size="small"
      blurOnSelect
      clearOnBlur={false}
      clearOnEscape
      filterOptions={(items) => items}
      open={inputValue.trim().length >= 2}
      options={options}
      loading={loading}
      inputValue={inputValue}
      value={null}
      noOptionsText="No map matches"
      getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') {
          onInputChange(value);
        }
      }}
      onChange={(_event, value) => {
        if (value && typeof value !== 'string') {
          onSelect(value as ActionSearchResult);
        }
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.id}
          sx={{
            display: 'grid !important',
            gridTemplateColumns: '9px minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 1,
            py: '7px !important',
            px: '10px !important',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: option.kind === 'Layer' ? '2px' : '50%', backgroundColor: option.color, boxShadow: `0 0 0 1px ${hexToRgba(option.color, 0.36)}` }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#f8fafc', fontFamily: MAP_UI_FONT, fontSize: '11px', fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {option.label}
            </Typography>
            {option.subtitle && (
              <Typography sx={{ color: '#94a3b8', fontFamily: MAP_UI_FONT, fontSize: '9px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.subtitle}
              </Typography>
            )}
          </Box>
          <Typography sx={{ color: option.color, fontFamily: MAP_UI_MONO_FONT, fontSize: '8px', fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>
            {option.kind}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search targets, layers, IDs, status"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#7dd3fc', fontSize: 16 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} sx={{ color: '#7dd3fc', mr: 0.5 }} /> : null}
                {inputValue ? (
                  <Tooltip title="Clear global search" placement="bottom">
                    <IconButton
                      size="small"
                      aria-label="Clear global search"
                      onClick={(event) => {
                        event.stopPropagation();
                        onInputChange('');
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 0.25,
                        color: '#94a3b8',
                        '&:hover': {
                          color: '#f8fafc',
                          backgroundColor: 'rgba(148, 163, 184, 0.16)',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: 38,
              backgroundColor: 'transparent',
              color: '#e5e7eb',
              fontFamily: MAP_UI_FONT,
              fontSize: '12px',
              borderRadius: 1.25,
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(125, 211, 252, 0.28)' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(125, 211, 252, 0.55)' },
            '& .MuiInputBase-input::placeholder': { color: '#94a3b8', opacity: 1 },
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: {
            mt: 0.7,
            backgroundColor: 'rgba(5, 8, 13, 0.96)',
            color: '#e5e7eb',
            border: '1px solid rgba(125, 211, 252, 0.24)',
            borderRadius: 1,
            boxShadow: '0 16px 36px rgba(0, 0, 0, 0.48)',
            '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: 'rgba(88, 166, 255, 0.18)' },
            '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'rgba(88, 166, 255, 0.14)' },
            '& .MuiAutocomplete-noOptions': {
              color: '#94a3b8',
              fontFamily: MAP_UI_FONT,
              fontSize: '11px',
            },
          }
        }
      }}
    />
  </Paper>
    {restoreAvailable && onRestore ? (
      <Button
        size="small"
        variant="outlined"
        onClick={() => void onRestore()}
        sx={{
          height: 40,
          flexShrink: 0,
          pointerEvents: 'auto',
          whiteSpace: 'nowrap',
          px: 1.25,
          borderRadius: 1.25,
          borderColor: 'rgba(148, 163, 184, 0.38)',
          backgroundColor: 'rgba(5, 8, 13, 0.88)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 14px 32px rgba(0, 0, 0, 0.44)',
          color: '#dbeafe',
          fontFamily: MAP_UI_FONT,
          fontSize: '9.5px',
          fontWeight: 800,
          letterSpacing: 0,
          textTransform: 'none',
          '&:hover': {
            borderColor: 'rgba(125, 211, 252, 0.62)',
            backgroundColor: 'rgba(88, 166, 255, 0.13)',
          },
        }}
      >
        Back to previous map
      </Button>
    ) : null}
  </Box>
);

const SGU_VECTOR_LAYERS: { id: string; label: string; color: string }[] = [
  { id: 'active_permits', label: 'SGU Active Permits', color: '#ef4444' },     // Red for active/granted
  { id: 'pending_permits', label: 'SGU Pending Permits', color: '#f59e0b' },   // Amber for pending
  { id: 'expired_permits', label: 'SGU Expired Permits', color: '#6b7280' },   // Gray for expired
  { id: 'rejected_permits', label: 'SGU Rejected Permits', color: '#8b5cf6' }, // Purple for rejected
  { id: 'mineral_occurrences', label: 'SGU Mineral Occurrences', color: '#f1c40f' },
  { id: 'boreholes', label: 'SGU Boreholes', color: '#00d2ff' },
  // Heritage layers from Riksantikvarieämbetet (RAÄ)
  { id: 'heritage_mining', label: 'RAÄ Mining Heritage', color: '#8b4513' },      // Brown - mine shafts, quarries
  { id: 'heritage_industrial', label: 'RAÄ Industrial Heritage', color: '#cd853f' }, // Peru - kilns, forges
  { id: 'heritage_hunting', label: 'RAÄ Hunting Heritage', color: '#2e8b57' },    // Sea green - hunting pits
  { id: 'heritage_settlement', label: 'RAÄ Settlement Heritage', color: '#4682b4' }, // Steel blue - habitation
  { id: 'heritage_all', label: 'RAÄ All Heritage', color: '#808080' },            // Gray - everything
  { id: 'brap_cu_results', label: 'BRAP Cu ≥0.5%', color: '#ff8c00' },  // Grade-colored markers
  { id: 'gravity_bouguer_contours', label: 'SGU Gravity Contours', color: '#9b59b6' },
  { id: 'quaternary_points', label: 'SGU Quaternary Points', color: '#8B4513' },  // Brown - moraine hills, outcrops, boulders
  { id: 'soil_depth_observations', label: 'SGU Soil Depth Obs', color: '#2F4F4F' },  // Dark slate - field observations
  { id: 'njugg_trench_overlay', label: 'Njugg Trenches + Drill Holes', color: '#ff6b35' }  // Orange - graphite trenches from PRAP 87526
];

const SGU_CLUSTERED_POINT_LAYER_IDS = new Set([
  'boreholes',
  'brap_cu_results',
  'mineral_occurrences',
  'quaternary_points',
  'soil_depth_observations',
]);

const getHeavyPointClusterDistance = (zoom: number): number => {
  if (zoom >= 10.9) return 0;
  if (zoom >= 10.1) return 6;
  if (zoom >= 9.2) return 14;
  if (zoom >= 8.0) return 24;
  return 40;
};

const getProjectGeoJsonPointClusterDistance = (zoom: number): number => {
  if (zoom >= 11.2) return 0;
  if (zoom >= 10.5) return 5;
  if (zoom >= 9.6) return 10;
  if (zoom >= 8.6) return 18;
  return 30;
};

const getDrillClusterReviewDistance = (zoom: number): number => {
  if (zoom >= 9.5) return 0;
  if (zoom >= 9.0) return 4;
  if (zoom >= 8.2) return 10;
  if (zoom >= 7.2) return 18;
  if (zoom >= 6.2) return 26;
  return 36;
};

const getDrillClusterDotRadius = (holeCount: number): number => {
  if (holeCount >= 1000) return 5.5;
  if (holeCount >= 100) return 5;
  if (holeCount > 50) return 4.6;
  if (holeCount > 20) return 4.2;
  if (holeCount >= 10) return 3.7;
  return 3.2;
};

const getFlatDrillClusterColor = (color: string, opacity = 0.9): string => {
  if (color.startsWith('#')) {
    return hexToRgba(color, opacity);
  }
  const rgbMatch = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  return rgbMatch
    ? `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`
    : color;
};

const updateClusterDistanceForZoom = (layer: any, zoom: number) => {
  const clusterSource = layer?.get?.('geomorphicClusterSource');
  if (clusterSource && typeof clusterSource.setDistance === 'function') {
    const distanceFn = layer?.get?.('geomorphicClusterDistanceFn');
    clusterSource.setDistance(
      typeof distanceFn === 'function'
        ? distanceFn(zoom)
        : getHeavyPointClusterDistance(zoom)
    );
  }
};

const getRawLayerSource = (layer: any): VectorSource | null => {
  return (layer?.get?.('geomorphicRawSource') || layer?.getSource?.() || null) as VectorSource | null;
};

const getClusterMemberFeatures = (feature: any): any[] => {
  const members = feature?.get?.('features');
  return Array.isArray(members) ? members : [];
};

const getRenderableMapFeature = (feature: any): any => {
  const members = getClusterMemberFeatures(feature);
  return members.length === 1 ? members[0] : feature;
};

const getDominantClusterColor = (members: any[], fallbackColor: string): string => {
  const counts = new globalThis.Map<string, number>();
  members.forEach((member) => {
    const color = member?.get?.('clusterReviewColor') || member?.get?.('color') || fallbackColor;
    counts.set(color, (counts.get(color) || 0) + 1);
  });
  let dominant = fallbackColor;
  let dominantCount = -1;
  counts.forEach((count, color) => {
    if (count > dominantCount) {
      dominant = color;
      dominantCount = count;
    }
  });
  return dominant;
};

const createHeavyPointClusterStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (members: any[], layerId: string, fallbackColor: string, zoom: number): Style[] => {
    const count = members.length;
    const color = getDominantClusterColor(members, fallbackColor);
    const label = count >= 1000 ? `${Math.round(count / 100) / 10}k` : String(count);
    const radius = Math.min(17, 8.5 + Math.log2(Math.max(2, count)) * 1.35);
    const key = `${layerId}:${color}:${label}:${Math.round(radius * 10)}:${zoom >= 10 ? 'detail' : 'overview'}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const fillColor = color.startsWith('#') ? hexToRgba(color, 0.58) : color;
    const styles = [
      new Style({
        image: new RegularShape({
          points: 6,
          radius: radius + 2.5,
          angle: Math.PI / 6,
          fill: new Fill({ color: 'rgba(2, 6, 23, 0.72)' }),
          stroke: new Stroke({ color: 'rgba(248, 250, 252, 0.72)', width: 0.8 }),
        }),
        zIndex: 779,
      }),
      new Style({
        image: new RegularShape({
          points: 6,
          radius,
          angle: Math.PI / 6,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color, width: 2 }),
        }),
        text: new Text({
          text: label,
          font: `800 ${radius > 13 ? 9.5 : 8.5}px ${MAP_UI_MONO_FONT}`,
          fill: new Fill({ color: '#f8fafc' }),
          stroke: new Stroke({ color: '#020617', width: 2.7 }),
        }),
        zIndex: 780,
      }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

const createProjectPointClusterStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (members: any[], layerId: string, fallbackColor: string, zoom: number): Style[] => {
    const count = members.length;
    const label = String(count);
    const radius = Math.min(16, 8 + Math.log2(Math.max(2, count)) * 1.25);
    const key = `${layerId}:${fallbackColor}:${label}:${Math.round(radius * 10)}:${zoom >= 11 ? 'detail' : 'overview'}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const fillColor = fallbackColor.startsWith('#') ? hexToRgba(fallbackColor, 0.52) : fallbackColor;
    const styles = [
      new Style({
        image: new RegularShape({
          points: 6,
          radius: radius + 2.5,
          angle: Math.PI / 6,
          fill: new Fill({ color: 'rgba(2, 6, 23, 0.70)' }),
          stroke: new Stroke({ color: 'rgba(226, 232, 240, 0.68)', width: 0.8 }),
        }),
        zIndex: 679,
      }),
      new Style({
        image: new RegularShape({
          points: 6,
          radius,
          angle: Math.PI / 6,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: fallbackColor, width: 2 }),
        }),
        text: new Text({
          text: label,
          font: `800 ${radius > 13 ? 9.5 : 8.5}px ${MAP_UI_MONO_FONT}`,
          fill: new Fill({ color: '#f8fafc' }),
          stroke: new Stroke({ color: '#020617', width: 2.6 }),
        }),
        zIndex: 680,
      }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

const createProjectPointFeatureStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (layerId: string, color: string, symbol: 'diamond' | 'circle' = 'diamond'): Style[] => {
    const key = `${layerId}:${color}:${symbol}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const casingImage = symbol === 'circle'
      ? new CircleStyle({ radius: 6.1, fill: new Fill({ color: 'rgba(2, 6, 23, 0.78)' }) })
      : new RegularShape({ points: 4, radius: 5.8, angle: Math.PI / 4, fill: new Fill({ color: 'rgba(2, 6, 23, 0.74)' }) });
    const markerImage = symbol === 'circle'
      ? new CircleStyle({ radius: 4.7, fill: new Fill({ color: color.startsWith('#') ? hexToRgba(color, 0.9) : color }), stroke: new Stroke({ color: '#f8fafc', width: 1 }) })
      : new RegularShape({ points: 4, radius: 4.4, angle: Math.PI / 4, fill: new Fill({ color: color.startsWith('#') ? hexToRgba(color, 0.84) : color }), stroke: new Stroke({ color: '#f8fafc', width: 0.8 }) });
    const styles = [
      new Style({ image: casingImage, zIndex: 659 }),
      new Style({ image: markerImage, zIndex: 660 }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

const createDrillClusterReviewStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (feature: FeatureLike): Style[] => {
    const holeCount = Number(feature.get('hole_count')) || 3;
    const color = getFlatDrillClusterColor(getDrillClusterReviewVisual(feature).color);
    const radius = getDrillClusterDotRadius(holeCount);
    const key = `${color}|${radius}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const styles = [
      new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color }),
        }),
        zIndex: 185,
      }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

const createLegacyDrillClusterReviewStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (feature: FeatureLike): Style[] => {
    const reviewStatus = feature.get('review_status') || 'pending';
    const verdict = feature.get('verdict');
    const foddClass = feature.get('fodd_classification') || '';
    if (reviewStatus === 'analyzed' || foddClass !== 'defined_untested') {
      return createDrillClusterReviewStyle(feature);
    }
    const holeCount = Number(feature.get('hole_count')) || 3;
    const radius = getDrillClusterDotRadius(holeCount);
    const color = getFlatDrillClusterColor('rgba(168, 85, 247, 0.76)');
    const key = `defined_untested|${verdict || ''}|${color}|${radius}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const styles = [
      new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color }),
        }),
        zIndex: 185,
      }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

type DrillClusterReviewVisual = {
  color: string;
  strokeColor: string;
  label: string;
};

const BOREHOLE_REVIEW_MATCH_RADIUS_M = 6000;
const BOREHOLE_DEFAULT_REVIEW_VISUAL: DrillClusterReviewVisual = {
  color: 'rgba(0, 210, 255, 0.86)',
  strokeColor: '#000000',
  label: 'Unlinked'
};

const DRILL_CLUSTER_REVIEW_LEGEND: LayerLegendEntry[] = [
  { label: 'Promote', color: 'rgba(34, 197, 94, 0.88)', strokeColor: '#14532d' },
  { label: 'Monitor', color: 'rgba(250, 204, 21, 0.86)', strokeColor: '#854d0e' },
  { label: 'Needs data', color: 'rgba(56, 189, 248, 0.82)', strokeColor: '#075985' },
  { label: 'Defined untested', color: 'rgba(168, 85, 247, 0.76)', strokeColor: '#581c87' },
  { label: 'Needs review', color: 'rgba(96, 165, 250, 0.70)', strokeColor: '#1d4ed8' },
  { label: 'Killed', color: 'rgba(239, 68, 68, 0.78)', strokeColor: '#7f1d1d' },
  { label: 'Pending', color: 'rgba(148, 163, 184, 0.68)', strokeColor: '#475569' },
];

const BOREHOLE_REVIEW_LEGEND: LayerLegendEntry[] = [
  ...DRILL_CLUSTER_REVIEW_LEGEND,
  { label: 'Unlinked', color: BOREHOLE_DEFAULT_REVIEW_VISUAL.color, strokeColor: BOREHOLE_DEFAULT_REVIEW_VISUAL.strokeColor },
];

const getDrillClusterReviewVisual = (feature: FeatureLike): DrillClusterReviewVisual => {
  const reviewStatus = String(feature.get('review_status') || feature.get('status') || 'pending');
  const verdict = String(feature.get('verdict') || '');
  const disposition = String(feature.get('disposition') || '');
  const foddClass = String(feature.get('fodd_classification') || '');
  const normalizedVerdict = verdict || (
    ['tier_1', 'tier_2', 'tier_3', 'stake_now', 'stake-now-cheap-optionality', 'stake_now_cheap_optionality', 'reopened_stake_now_cluster_a'].includes(disposition)
      ? 'promote'
      : disposition === 'killed'
        ? 'reject'
        : disposition
  );

  if (reviewStatus === 'analyzed' || reviewStatus === 'reviewed') {
    if (normalizedVerdict === 'promote') {
      return { color: 'rgba(34, 197, 94, 0.88)', strokeColor: '#14532d', label: 'Promote' };
    }
    if (normalizedVerdict === 'reject') {
      return { color: 'rgba(239, 68, 68, 0.78)', strokeColor: '#7f1d1d', label: 'Killed' };
    }
    if (normalizedVerdict === 'monitor') {
      return { color: 'rgba(250, 204, 21, 0.86)', strokeColor: '#854d0e', label: 'Monitor' };
    }
    if (normalizedVerdict === 'needs_data') {
      return { color: 'rgba(56, 189, 248, 0.82)', strokeColor: '#075985', label: 'Needs data' };
    }
    if (normalizedVerdict === 'known_deposit') {
      return { color: 'rgba(148, 163, 184, 0.82)', strokeColor: '#334155', label: 'Known deposit' };
    }
  }

  if (foddClass === 'defined_untested') {
    return { color: 'rgba(168, 85, 247, 0.76)', strokeColor: '#581c87', label: 'Defined untested' };
  }

  if (reviewStatus === 'needs_review') {
    return { color: 'rgba(96, 165, 250, 0.70)', strokeColor: '#1d4ed8', label: 'Needs review' };
  }

  return { color: 'rgba(148, 163, 184, 0.68)', strokeColor: '#475569', label: 'Pending' };
};

const createDrillClusterAggregateStyle = (() => {
  const cache = new globalThis.Map<string, Style[]>();
  return (members: any[]): Style[] => {
    const color = getDominantClusterColor(
      members,
      getFlatDrillClusterColor('rgba(148, 163, 184, 0.68)')
    );
    const radius = Math.min(9, 5.8 + Math.log2(Math.max(2, members.length)) * 0.75);
    const key = `${color}|${Math.round(radius * 10)}`;
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const styles = [
      new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color }),
        }),
        zIndex: 185,
      }),
    ];
    cache.set(key, styles);
    return styles;
  };
})();

const createDrillClusterVectorLayer = (
  features: any[],
  zoom: number,
  styleFactory: (feature: FeatureLike) => Style[]
): VectorLayer<any> => {
  features.forEach((feature) => {
    feature.set(
      'clusterReviewColor',
      getFlatDrillClusterColor(getDrillClusterReviewVisual(feature).color),
      true
    );
  });
  const rawSource = new VectorSource({ features });
  const clusterSource = new Cluster({
    distance: getDrillClusterReviewDistance(zoom),
    source: rawSource,
  });
  const layer = new VectorLayer<any>({
    source: clusterSource,
    properties: { geomorphicLayerLabel: 'Drill clusters' },
    style: (feature: FeatureLike) => {
      const members = getClusterMemberFeatures(feature);
      if (members.length > 1) {
        return createDrillClusterAggregateStyle(members);
      }
      return styleFactory(members.length === 1 ? members[0] : feature);
    },
    zIndex: 185,
  });
  layer.set('geomorphicRawSource', rawSource);
  layer.set('geomorphicClusterSource', clusterSource);
  layer.set('geomorphicClusterDistanceFn', getDrillClusterReviewDistance);
  return layer;
};

type FinlandExplorationPermitStatusKey = 'active' | 'application' | 'karenssi';

type FinlandExplorationPermitVisual = {
  label: string;
  detail: string;
  count: string;
  color: string;
  fillColor: string;
  lineDash?: number[];
  swatch: LayerLegendSwatch;
};

const FINLAND_EXPLORATION_PERMIT_VISUALS: Record<FinlandExplorationPermitStatusKey, FinlandExplorationPermitVisual> = {
  active: {
    label: 'Active exploration permits',
    detail: 'Granted TUKES exploration permit',
    count: '417',
    color: '#ef4444',
    fillColor: 'rgba(239, 68, 68, 0.10)',
    swatch: 'square',
  },
  application: {
    label: 'Permit applications',
    detail: 'Pending hakemus / application',
    count: '382',
    color: '#38bdf8',
    fillColor: 'rgba(56, 189, 248, 0.08)',
    lineDash: [6, 4],
    swatch: 'dash',
  },
  karenssi: {
    label: 'Karenssi cooling-off',
    detail: 'Post-expiry restricted period',
    count: '226',
    color: '#94a3b8',
    fillColor: 'rgba(148, 163, 184, 0.08)',
    lineDash: [10, 4, 2, 4],
    swatch: 'dash',
  },
};

const FINLAND_EXPLORATION_PERMIT_STATUS_ORDER: FinlandExplorationPermitStatusKey[] = ['active', 'application', 'karenssi'];

const FINLAND_EXPLORATION_PERMIT_LEGEND: LayerLegendEntry[] = FINLAND_EXPLORATION_PERMIT_STATUS_ORDER.map((statusKey) => {
  const visual = FINLAND_EXPLORATION_PERMIT_VISUALS[statusKey];
  return {
    label: visual.label.replace(' exploration permits', ''),
    color: visual.color,
    strokeColor: visual.color,
    swatch: visual.swatch,
  };
});

const getFinlandExplorationPermitStatusKey = (feature: FeatureLike): FinlandExplorationPermitStatusKey => {
  const status = String(feature.get('status') || feature.get('Aluestatus') || '').toLowerCase();
  if (status.includes('hakemus') || status.includes('application')) {
    return 'application';
  }
  if (status.includes('karenssi')) {
    return 'karenssi';
  }
  return 'active';
};

const getFeatureAnchorCoordinate = (feature: FeatureLike): [number, number] | null => {
  const geometry = feature.getGeometry();
  if (!geometry) {
    return null;
  }
  if (geometry instanceof Point) {
    const coords = geometry.getCoordinates();
    return [coords[0], coords[1]];
  }
  const extent = geometry.getExtent();
  if (!extent.every(Number.isFinite)) {
    return null;
  }
  return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
};

const getNearestDrillClusterReviewVisual = (
  feature: FeatureLike,
  clusterFeatures: FeatureLike[]
): DrillClusterReviewVisual | null => {
  const coord = getFeatureAnchorCoordinate(feature);
  if (!coord) {
    return null;
  }

  let nearest: FeatureLike | null = null;
  let nearestDistanceSquared = Number.POSITIVE_INFINITY;

  clusterFeatures.forEach((clusterFeature) => {
    const clusterCoord = getFeatureAnchorCoordinate(clusterFeature);
    if (!clusterCoord) {
      return;
    }
    const dx = coord[0] - clusterCoord[0];
    const dy = coord[1] - clusterCoord[1];
    const distanceSquared = dx * dx + dy * dy;
    if (distanceSquared < nearestDistanceSquared) {
      nearest = clusterFeature;
      nearestDistanceSquared = distanceSquared;
    }
  });

  if (!nearest || Math.sqrt(nearestDistanceSquared) > BOREHOLE_REVIEW_MATCH_RADIUS_M) {
    return null;
  }

  return getDrillClusterReviewVisual(nearest);
};

// Peru/INGEMMET vector layers
const PERU_VECTOR_LAYERS: { id: string; label: string; color: string; category: string }[] = [
  // Permits & Deposits
  { id: 'mining_cadastre', label: 'Mining Concessions', color: '#f59e0b', category: 'permits' },
  { id: 'expiring_permits', label: 'Expiring Permits (90d)', color: '#FF4444', category: 'permits' },
  { id: 'metallogenic_deposits', label: 'Mining Deposits', color: '#ef4444', category: 'deposits' },
  { id: 'mineral_occurrences_metallic', label: 'Metallic Occurrences', color: '#f1c40f', category: 'deposits' },
  // Geology
  { id: 'metallogenic_belts', label: 'Metallogenic Belts', color: '#8b5cf6', category: 'geology' },
  { id: 'metallogenic_structures', label: 'Major Structures', color: '#000000', category: 'geology' },
  // Geochemistry
  { id: 'geochem_au_anomalies', label: 'Gold Anomalies', color: '#ffd700', category: 'geochemistry' },
  { id: 'geochem_ag_anomalies', label: 'Silver Anomalies', color: '#c0c0c0', category: 'geochemistry' },
  { id: 'geochem_cu_anomalies', label: 'Copper Anomalies', color: '#b87333', category: 'geochemistry' },
  // Geophysics
  { id: 'gravity_stations', label: 'Gravity Stations', color: '#1abc9c', category: 'geophysics' },
  { id: 'magnetotelluric_stations', label: 'MT Stations', color: '#e67e22', category: 'geophysics' },
  // Playbook Analysis (loaded from static GeoJSON files)
  { id: 'playbook_targets', label: 'Playbook Targets (3,120)', color: '#E63946', category: 'analysis' },
  { id: 'independent_systems', label: 'Independent Systems (13)', color: '#22c55e', category: 'analysis' },
  { id: 'drill_collars', label: 'Drill Collars (153)', color: '#2196f3', category: 'analysis' },
  { id: 'open_ground', label: 'Open Ground (251)', color: '#4caf50', category: 'analysis' },
  { id: 'spectral_anomalies', label: 'Spectral Anomalies (293)', color: '#ff9800', category: 'analysis' },
  { id: 'mining_cadastre_static', label: 'Concession Boundaries (60k)', color: '#8b5cf6', category: 'analysis' },
  { id: 'stream_sediments_anomalous', label: 'Anomalous Sediments (7.9k)', color: '#00bcd4', category: 'geochemistry_analysis' },
  { id: 'crm_hotspots', label: 'CRM Hotspots', color: '#009688', category: 'geochemistry_analysis' },
  // Surface samples & projects
  { id: 'anap_geochemistry', label: 'ANAP Surface Geochemistry', color: '#ff6b35', category: 'surface_samples' },
  { id: 'litoteca_samples', label: 'LITOTECA Rock Samples', color: '#9b59b6', category: 'surface_samples' },
  { id: 'bdg_rock_geochemistry', label: 'BDG Rock Geochemistry (9.5k)', color: '#e74c3c', category: 'surface_samples' },
  { id: 'mining_projects', label: 'Mining Projects', color: '#f1c40f', category: 'surface_samples' },
  // Reference Databases (USGS, SAmMD)
  { id: 'usgs_mrds_deposits', label: 'USGS MRDS Deposits (3,018)', color: '#6366f1', category: 'reference_databases' },
  { id: 'usgs_andes_porphyry_cu_peru', label: 'USGS Porphyry Cu - Andes (52)', color: '#dc2626', category: 'reference_databases' },
  { id: 'usgs_global_porphyry_cu_peru', label: 'USGS Porphyry Cu - Global (75)', color: '#b91c1c', category: 'reference_databases' },
  { id: 'usgs_major_deposits_peru', label: 'USGS Major Deposits (31)', color: '#f59e0b', category: 'reference_databases' },
  { id: 'sammd_deposits', label: 'SAmMD Deposits (136)', color: '#10b981', category: 'reference_databases' },
  { id: 'usgs_mineral_facilities_peru', label: 'USGS Mine Facilities (188)', color: '#0ea5e9', category: 'reference_databases' },
  { id: 'usgs_exploration_sites_peru', label: 'USGS Exploration Sites (25)', color: '#22c55e', category: 'reference_databases' },
  { id: 'usgs_vms_peru', label: 'USGS VMS Deposits (7)', color: '#f97316', category: 'reference_databases' },
  { id: 'usgs_sedcu_peru', label: 'USGS Sed-Cu Deposits (14)', color: '#b45309', category: 'reference_databases' },
  { id: 'usgs_sedznpb_peru', label: 'USGS Sed-Zn-Pb Deposits (14)', color: '#737373', category: 'reference_databases' },
];

// Papua New Guinea (MRA) vector layers — served from /api/png/layers/{id}/features
const PNG_VECTOR_LAYERS: { id: string; label: string; color: string; type: string; category: string }[] = [
  // Cadastre
  { id: 'tenements_granted', label: 'Granted Tenements (held)', color: '#f59e0b', type: 'polygon', category: 'cadastre' },
  { id: 'tenements_applications', label: 'Applications (pending)', color: '#fb923c', type: 'polygon', category: 'cadastre' },
  { id: 'reserved_pending_gazette', label: 'Reserved / Pending Gazette', color: '#ef4444', type: 'polygon', category: 'cadastre' },
  // Open ground
  { id: 'open_blocks', label: 'Open Ground (stakable blocks)', color: '#22c55e', type: 'polygon', category: 'open_ground' },
  { id: 'staking_proposed', label: 'Proposed Staking Polygons', color: '#06b6d4', type: 'polygon', category: 'open_ground' },
  // Deposits
  { id: 'mines_projects', label: 'Mines & Advanced Projects', color: '#b91c1c', type: 'point', category: 'deposits' },
  { id: 'exploration_advanced', label: 'Advanced Exploration (drilled)', color: '#d946ef', type: 'polygon', category: 'deposits' },
  { id: 'mineral_occurrences', label: 'Mineral Occurrences (814)', color: '#facc15', type: 'point', category: 'deposits' },
  { id: 'open_ground_occurrences', label: 'Occurrences on OPEN ground', color: '#ec4899', type: 'point', category: 'open_ground' },
  // Drilling
  { id: 'drill_holes', label: 'Drill Holes', color: '#a855f7', type: 'point', category: 'drilling' },
  // Constraints
  { id: 'protected_areas_current', label: 'Protected Areas (current)', color: '#15803d', type: 'polygon', category: 'constraints' },
  { id: 'protected_areas_proposed', label: 'Protected Areas (proposed)', color: '#65a30d', type: 'polygon', category: 'constraints' },
  // Context
  { id: 'provinces', label: 'Provinces', color: '#64748b', type: 'polygon', category: 'context' },
];

// Yukon (GeoYukon) vector layers — served from /api/yukon/layers/{id}/features (live ArcGIS proxy
// for cadastre/geoscience; static curated files for our analysis products). kind='geophys' entries
// are ArcGIS dynamic raster overlays (TileArcGISRest) pointed straight at GeoYukon.
type YukonLayerDef = {
  id: string;
  label: string;
  color: string;
  type: string;
  category: string;
  kind?: 'vector' | 'geophys';
  arcgisService?: string;
  arcgisLayer?: number;
};
const YUKON_VECTOR_LAYERS: YukonLayerDef[] = [
  // Cadastre — the held / expired-grace / pending / lapsed split
  { id: 'quartz_claims_held', label: 'Quartz Claims — HELD (active)', color: '#f59e0b', type: 'polygon', category: 'cadastre' },
  { id: 'quartz_claims_expired_grace', label: 'Quartz Claims — EXPIRED (lapsing/renewal)', color: '#ef4444', type: 'polygon', category: 'cadastre' },
  { id: 'quartz_claims_pending', label: 'Quartz Claims — PENDING (30-day)', color: '#fb923c', type: 'polygon', category: 'cadastre' },
  { id: 'quartz_leases', label: 'Quartz Leases', color: '#d97706', type: 'polygon', category: 'cadastre' },
  { id: 'crown_grants', label: 'Crown Grants (freehold mineral)', color: '#92400e', type: 'polygon', category: 'cadastre' },
  { id: 'quartz_historical_lapsed', label: 'Historical Tenure (lapsed footprint)', color: '#64748b', type: 'polygon', category: 'cadastre' },
  // Constraints — the withdrawn / pending-gazette mask
  { id: 'areas_withdrawn_quartz', label: 'Areas Withdrawn from Staking (OIC)', color: '#dc2626', type: 'polygon', category: 'constraints' },
  // Open ground + targets
  { id: 'open_ground_stakable', label: 'Open Ground — walked-away (belt)', color: '#22c55e', type: 'polygon', category: 'open_ground' },
  { id: 'open_ground_expired', label: 'Open Ground — expired-ghost claims (>1yr)', color: '#16a34a', type: 'polygon', category: 'open_ground' },
  { id: 'target_105o_north_sn', label: 'Target — 105O-North Sn (core + district)', color: '#0ea5e9', type: 'polygon', category: 'targets' },
  { id: 'w_lane_screen', label: 'W-lane prospect screen (context)', color: '#c084fc', type: 'point', category: 'targets' },
  // Deposits & occurrences
  { id: 'mineral_occurrences', label: 'MINFILE Occurrences (3,223)', color: '#eab308', type: 'point', category: 'deposits' },
  { id: 'mineral_occurrences_critical', label: 'Critical-Commodity Occurrences', color: '#f97316', type: 'point', category: 'deposits' },
  { id: 'deposits_property', label: 'Mineral Inventory — Deposits (sized)', color: '#b91c1c', type: 'point', category: 'deposits' },
  // Drilling
  { id: 'drill_holes', label: 'Drill Collars — 250k (sparse, 1,900)', color: '#a855f7', type: 'point', category: 'drilling' },
  // Geochemistry
  { id: 'rgs_geochem', label: 'RGS Stream-Sediment Geochem (31k)', color: '#fdba74', type: 'point', category: 'geochemistry' },
  { id: 'rgs_untested_anomalies', label: 'RGS Untested W-Sn-Sb Anomalies', color: '#ea580c', type: 'point', category: 'geochemistry' },
  // Reports
  { id: 'assessment_reports', label: 'Assessment Report Footprints (10k)', color: '#60a5fa', type: 'polygon', category: 'reports' },
  // Geology
  { id: 'bedrock_geology', label: 'Bedrock Geology', color: '#a8a29e', type: 'polygon', category: 'geology' },
  { id: 'faults', label: 'Faults', color: '#f43f5e', type: 'line', category: 'geology' },
  // Geophysics (ArcGIS raster overlays straight from GeoYukon)
  { id: 'geophys_magnetic', label: 'Residual Magnetic Total Field (50 m)', color: '#14b8a6', type: 'raster', category: 'geophysics', kind: 'geophys', arcgisService: 'GY_Geological', arcgisLayer: 80 },
  { id: 'geophys_1vd', label: 'Magnetic 1st Vertical Derivative (50 m)', color: '#2dd4bf', type: 'raster', category: 'geophysics', kind: 'geophys', arcgisService: 'GY_Geological', arcgisLayer: 81 },
  { id: 'geophys_gravity', label: 'Bouguer Gravity Anomaly (2 km)', color: '#0d9488', type: 'raster', category: 'geophysics', kind: 'geophys', arcgisService: 'GY_Geological', arcgisLayer: 79 },
];
const GEOYUKON_ROOT = 'https://mapservices.gov.yk.ca/arcgis/rest/services/GeoYukon';

// Caujul Project vector layers
const CAUJUL_VECTOR_LAYERS: { id: string; label: string; color: string; category: string }[] = [
  // Alteration
  { id: 'alteration_argillic', label: 'Argillic Alteration', color: '#FFD700', category: 'alteration' },
  { id: 'alteration_phyllic', label: 'Phyllic Alteration', color: '#FF8C00', category: 'alteration' },
  { id: 'alteration_propylitic', label: 'Propylitic Alteration', color: '#32CD32', category: 'alteration' },
  { id: 'alteration_iron_oxides', label: 'Iron Oxides', color: '#CD5C5C', category: 'alteration' },
  { id: 'alteration_silica', label: 'Silica Alteration', color: '#E6E6FA', category: 'alteration' },
  // Geology
  { id: 'geology', label: 'Geology', color: '#D3D3D3', category: 'geology' },
  { id: 'faults', label: 'Faults', color: '#FF0000', category: 'geology' },
  // Geochemistry
  { id: 'samples', label: 'Sample Locations', color: '#0000FF', category: 'geochemistry' },
  // Prospectivity & Targets
  { id: 'prospectivity', label: 'Prospectivity Grid', color: '#FFA500', category: 'targeting' },
  { id: 'silver_corridors', label: 'Silver Corridors', color: '#4169E1', category: 'targeting' },
  { id: 'porphyry_targets', label: 'Porphyry Targets', color: '#FF8C00', category: 'targeting' },
];

// France/BRGM vector layers
const FRANCE_VECTOR_LAYERS: { id: string; label: string; color: string; category: string }[] = [
  // Permits
  { id: 'france_active_permits', label: 'Active Permits (Camino)', color: '#ef4444', category: 'permits' },
  { id: 'france_pending_permits', label: 'Pending Applications', color: '#f59e0b', category: 'permits' },
  { id: 'france_expired_permits', label: 'Expired Permits', color: '#6b7280', category: 'permits' },
  // Deposits & Occurrences
  { id: 'france_mineral_occurrences', label: 'BRGM Mineral Occurrences (4.2k)', color: '#f1c40f', category: 'deposits' },
  { id: 'france_historical_mines', label: 'Historical Mines (1.7k)', color: '#8b4513', category: 'deposits' },
  // Geophysics
  { id: 'france_aeromagnetic_campaigns', label: 'Aeromagnetic Survey Coverage', color: '#9b59b6', category: 'geophysics' },
];

type JurisdictionLayerDefinition = {
  id: string;
  label: string;
  color: string;
  category: string;
  path?: string;
  disabledReason?: string;
  zIndex?: number;
};

const JURISDICTION_LAYER_CONFIG: Record<string, { title: string; layers: JurisdictionLayerDefinition[] }> = {
  'project-sgu-sweden': {
    title: 'Sweden Alpha Lanes',
    layers: [
      { id: 'sweden-gapfinder-candidates', label: 'Gap-finder candidate systems', color: '#f97316', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/gapfinder_candidates.geojson', zIndex: 86 },
      { id: 'sweden-reassay-open-core-holes', label: 'Re-assay open core holes', color: '#38bdf8', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/reassay_open_core_holes.geojson', zIndex: 84 },
      { id: 'sweden-crossborder-clusters', label: 'Cross-border till clusters', color: '#a3e635', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/crossborder_clusters.geojson', zIndex: 85 },
      { id: 'sweden-smart-money-pending', label: 'Smart-money pending applications', color: '#f43f5e', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/smart_money_pending.geojson', zIndex: 88 },
      { id: 'sweden-kramsta-draft-polygon', label: 'Kramsta draft polygon', color: '#22d3ee', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/kramsta_draft_polygon.geojson', zIndex: 72 },
      { id: 'sweden-kramsta-jorns-application', label: 'Kramsta — Jörns application (pending)', color: '#eab308', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/kramsta_jorns_application.geojson', zIndex: 73 },
      { id: 'sweden-kramsta-occurrences', label: 'Kramsta occurrences', color: '#fb923c', category: 'Alpha Lanes (2026-07)', path: '/data/sweden/kramsta_occurrences.geojson', zIndex: 89 },
    ],
  },
  'project-gtk-finland': {
    title: 'Finland Alpha Lanes',
    layers: [
      { id: 'finland-reassay-open-core-holes', label: 'Re-assay open core holes', color: '#38bdf8', category: 'Alpha Lanes (2026-07)', path: '/data/finland/reassay_open_core_holes.geojson', zIndex: 84 },
      { id: 'finland-crossborder-clusters', label: 'Cross-border till clusters', color: '#a3e635', category: 'Alpha Lanes (2026-07)', path: '/data/finland/crossborder_clusters.geojson', zIndex: 85 },
      { id: 'finland-smart-money-pending', label: 'Smart-money pending applications', color: '#f43f5e', category: 'Alpha Lanes (2026-07)', path: '/data/finland/smart_money_pending.geojson', zIndex: 88 },
      { id: 'finland-korsnas-reassay-collars', label: 'Korsnäs re-assay collars', color: '#38bdf8', category: 'Alpha Lanes (2026-07)', path: '/data/finland/korsnas_reassay_collars.geojson', zIndex: 86 },
      { id: 'finland-korsnas-competitor-applications', label: 'Korsnäs competitor applications', color: '#eab308', category: 'Alpha Lanes (2026-07)', path: '/data/finland/korsnas_competitor_applications.geojson', zIndex: 73 },
      { id: 'finland-korsnas-varaus-main', label: 'Korsnäs draft varaus (772 ha)', color: '#22d3ee', category: 'Alpha Lanes (2026-07)', path: '/data/finland/korsnas_varaus_main.geojson', zIndex: 74 },
    ],
  },
  'project-canada-ontario': {
    title: 'Ontario Layers',
    layers: [
      { id: 'ontario-first-ten', label: 'Stratified First 10 Picks', color: '#22c55e', category: 'Curated Outputs', path: '/data/ontario/step3_first_ten.geojson', zIndex: 82 },
      { id: 'ontario-pip-screen', label: 'PIP Open-Ground Screen', color: '#16a34a', category: 'Curated Outputs', path: '/data/ontario/step3_open_ground_screen.geojson', zIndex: 74 },
      { id: 'ontario-indigenous-risk', label: 'Indigenous Risk Composite', color: '#f97316', category: 'Risk / Constraints', path: '/data/ontario/indigenous_risk_screen_composite.geojson', zIndex: 76 },
      { id: 'ontario-omi-deposits', label: 'OMI Deposits', color: '#facc15', category: 'Reference Data', path: '/data/ontario/omi_deposits_univmb.geojson', zIndex: 68 },
      { id: 'ontario-mlas-sample', label: 'MLAS Claims Sample', color: '#8b5cf6', category: 'Tenure', path: '/data/ontario/mining_claims_sample.geojson', zIndex: 64 },
      { id: 'ontario-afri-polygons', label: 'AFRI Polygons (99k)', color: '#38bdf8', category: 'Raw / Tile Required', disabledReason: 'Cloud/vector-tile staging required before browser loading' },
    ],
  },
  'project-australia-wa': {
    title: 'Western Australia Layers',
    layers: [
      { id: 'wa-first-ten', label: 'Stratified First 10 Picks', color: '#22c55e', category: 'Curated Outputs', path: '/data/wa/step3_first_ten.geojson', zIndex: 82 },
      { id: 'wa-live-pending-tenure', label: 'TENGRAPH Live + Pending', color: '#f59e0b', category: 'Tenure', path: '/data/wa/tengraph_live_pending.geojson', zIndex: 66 },
      { id: 'wa-minedex', label: 'MINEDEX Deposits', color: '#facc15', category: 'Reference Data', path: '/data/wa/minedex.geojson', zIndex: 68 },
      { id: 'wa-heritage-protected', label: 'Heritage Protected Areas', color: '#a855f7', category: 'Risk / Constraints', path: '/data/wa/heritage_protected_areas.geojson', zIndex: 70 },
      { id: 'wa-pastoral-stations', label: 'Pastoral Stations', color: '#14b8a6', category: 'Risk / Constraints', path: '/data/wa/pastoral_stations.geojson', zIndex: 62 },
      { id: 'wa-pip-screen', label: 'PIP Screen Partial (512 MB)', color: '#16a34a', category: 'Raw / Tile Required', disabledReason: 'PMTiles/vector-tile staging required before browser loading' },
      { id: 'wa-ach-register', label: 'ACH Register (225 MB)', color: '#ef4444', category: 'Raw / Tile Required', disabledReason: 'PMTiles/vector-tile staging required before browser loading' },
      { id: 'wa-mdhdb-drillholes', label: 'MDHDB Drillholes', color: '#38bdf8', category: 'Raw / Tile Required', disabledReason: 'MDHDB export is too large for direct GeoJSON loading' },
      { id: 'wa-dead-tenements', label: 'TENGRAPH Dead Tenements', color: '#6b7280', category: 'Raw / Tile Required', disabledReason: 'Dead-tenement inventory needs tile-backed delivery' },
    ],
  },
  'project-bolivia': {
    title: 'Bolivia Layers',
    layers: [
      { id: 'bolivia-pip-screen', label: 'PIP Screen', color: '#16a34a', category: 'Pending Outputs', disabledReason: 'Curated Bolivia screen not generated yet' },
      { id: 'bolivia-first-ten', label: 'Stratified First 10 Picks', color: '#22c55e', category: 'Pending Outputs', disabledReason: 'Curated Bolivia picks not generated yet' },
      { id: 'bolivia-indigenous-risk', label: 'Indigenous / Protected Composite', color: '#f97316', category: 'Pending Outputs', disabledReason: 'Composite layer not generated yet' },
      { id: 'bolivia-ajam-tenure', label: 'AJAM Tenure', color: '#f59e0b', category: 'Pending Outputs', disabledReason: 'AJAM browser layer not staged yet' },
    ],
  },
  'project-greenland-geus': {
    title: 'Greenland Layers',
    layers: [
      { id: 'greenland-priority-queue', label: 'Drill-cluster priority queue', color: '#facc15', category: 'Drilling Priority', path: '/data/greenland/greenland_drill_cluster_seed_v1.geojson', zIndex: 92 },
      { id: 'greenland-review-hulls', label: '2.5 km source-pull review hulls', color: '#f59e0b', category: 'Drilling Priority', path: '/data/greenland/greenland_drill_cluster_hulls_v1.geojson', zIndex: 82 },
      { id: 'greenland-totalcore', label: 'GEUS totalcore drillholes', color: '#a855f7', category: 'Drilling', path: '/data/greenland/greenland_totalcore_drillholes.geojson', zIndex: 76 },
      { id: 'greenland-greencore', label: 'GEUS greencore inventory', color: '#c084fc', category: 'Drilling', path: '/data/greenland/greenland_greencore_inventory.geojson', zIndex: 74 },
      { id: 'greenland-active-exploration', label: 'Active exploration licences', color: '#f59e0b', category: 'Tenure', path: '/data/greenland/greenland_active_exploration_licences.geojson', zIndex: 68 },
      { id: 'greenland-exploitation', label: 'Exploitation licences', color: '#ef4444', category: 'Tenure', path: '/data/greenland/greenland_exploitation_licences.geojson', zIndex: 70 },
      { id: 'greenland-applications', label: 'Licence applications', color: '#38bdf8', category: 'Tenure', path: '/data/greenland/greenland_licence_applications.geojson', zIndex: 69 },
      { id: 'greenland-historic-licences', label: 'Historic licences', color: '#64748b', category: 'Tenure', path: '/data/greenland/greenland_historic_licences.geojson', zIndex: 62 },
      { id: 'greenland-protected-areas', label: 'Protected areas', color: '#dc2626', category: 'Constraints', path: '/data/greenland/greenland_protected_areas.geojson', zIndex: 72 },
      { id: 'greenland-sensitive-areas', label: 'Sensitive areas', color: '#fb7185', category: 'Constraints', path: '/data/greenland/greenland_sensitive_areas.geojson', zIndex: 71 },
      { id: 'greenland-naturemap-important', label: 'NatureMap protected / important areas', color: '#e11d48', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_naturemap_protected_important_areas.geojson', zIndex: 73 },
      { id: 'greenland-protected-nature-types', label: 'Protected nature types', color: '#f97316', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_planportal_protected_nature_types.geojson', zIndex: 72 },
      { id: 'greenland-ramsar', label: 'Ramsar areas', color: '#0ea5e9', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_ramsar_areas.geojson', zIndex: 72 },
      { id: 'greenland-bird-protection', label: 'Bird colonies / protection areas', color: '#22c55e', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_bird_colonies_protection_areas.geojson', zIndex: 72 },
      { id: 'greenland-biological-important', label: 'Biologically important areas', color: '#84cc16', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_biologically_important_areas.geojson', zIndex: 72 },
      { id: 'greenland-national-park', label: 'National park', color: '#15803d', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_national_park.geojson', zIndex: 72 },
      { id: 'greenland-drinking-water', label: 'Drinking-water resources', color: '#06b6d4', category: 'Environmental / Wildlife', path: '/data/greenland/greenland_drinking_water_resources.geojson', zIndex: 72 },
      { id: 'greenland-nunniffiit-sites', label: 'Nunniffiit heritage sites', color: '#a855f7', category: 'Heritage / Community', path: '/data/greenland/greenland_nunniffiit_public_sites.geojson', zIndex: 74 },
      { id: 'greenland-heritage-acts', label: 'Heritage Act polygons', color: '#7e22ce', category: 'Heritage / Community', path: '/data/greenland/greenland_nunniffiit_heritage_acts.geojson', zIndex: 75 },
      { id: 'greenland-unesco', label: 'UNESCO heritage areas', color: '#c026d3', category: 'Heritage / Community', path: '/data/greenland/greenland_nunniffiit_unesco_areas.geojson', zIndex: 75 },
      { id: 'greenland-active-settlements', label: 'Active settlements', color: '#f43f5e', category: 'Heritage / Community', path: '/data/greenland/greenland_active_settlements.geojson', zIndex: 76 },
      { id: 'greenland-former-settlements', label: 'Former settlements', color: '#94a3b8', category: 'Heritage / Community', path: '/data/greenland/greenland_former_settlements.geojson', zIndex: 64 },
      { id: 'greenland-planning-zones', label: 'Municipal planning zones', color: '#f59e0b', category: 'Heritage / Community', path: '/data/greenland/greenland_municipality_planning_zones.geojson', zIndex: 63 },
      { id: 'greenland-mineral-occurrences', label: 'Mineral occurrences', color: '#fbbf24', category: 'Reference Data', path: '/data/greenland/greenland_mineral_occurrences.geojson', zIndex: 66 },
      { id: 'greenland-gmom-tracts', label: 'GMOM mineral-potential tracts', color: '#22c55e', category: 'Reference Data', path: '/data/greenland/greenland_gmom_tracts.geojson', zIndex: 60 },
      { id: 'greenland-dodex-company-reports', label: 'DODEX company report footprints', color: '#60a5fa', category: 'Reports', path: '/data/greenland/greenland_dodex_company_reports.geojson', zIndex: 58 },
      { id: 'greenland-survey-publications', label: 'GEUS publication footprints', color: '#93c5fd', category: 'Reports', path: '/data/greenland/greenland_dodex_survey_publications.geojson', zIndex: 56 },
      { id: 'greenland-geochem-anomalies', label: 'Geochem p98 anomalies', color: '#fb923c', category: 'Geochemistry', path: '/data/greenland/greenland_geochem_anomalies_p98.geojson', zIndex: 78 },
      { id: 'greenland-geophysics', label: 'Geophysical survey coverage', color: '#14b8a6', category: 'Geophysics', path: '/data/greenland/greenland_geophysical_surveys.geojson', zIndex: 57 },
      { id: 'greenland-intrusions', label: 'Intrusions', color: '#94a3b8', category: 'Geology', path: '/data/greenland/greenland_intrusions.geojson', zIndex: 54 },
    ],
  },
};

// Borehole cluster labels for Jokkmokk staking area (centroids from SGU borrhal.gpkg)
// UPDATED 2026-02-16: Redesigned per desk study. Hole counts verified against borrhal.geojson.
// Pälkasbäcken confirmed OPEN (27 km from Parkijaure nr 7). Rakten/Manak corrected to U (not Mn).
const BOREHOLE_CLUSTER_LABELS_VB: Array<{
  name: string;
  lon: number;
  lat: number;
  holes: number;
  commodity: string;
  highlight?: string;
  status?: 'BLOCKED' | 'KILLED' | 'INVESTIGATE';
}> = [
  // === SEITEVARE block (MUST STAKE) === (Kiuri district)
  { name: 'KIURI', lon: 18.5039, lat: 66.9512, holes: 69, commodity: 'Ag-Pb-Zn-Au', highlight: 'Best: 3m @ 110 g/t Ag, 4.92% Pb+Zn. 18.7 g/t Au blocks (source unfound). 360 kTon @ 4% Zn.' },
  { name: 'PÄLKASBÄCKEN', lon: 18.58, lat: 66.94, holes: 0, commodity: 'Cu', highlight: '4.77% Cu boulders, 3 IP anomalies NEVER DRILLED. OPEN.' },
  { name: 'Malmtjärn', lon: 18.45, lat: 66.95, holes: 0, commodity: 'Cu-W', highlight: 'Cu-W boulders + outcrop, NEVER DRILLED' },
  { name: 'Rassåive W', lon: 18.5494, lat: 66.9845, holes: 10, commodity: 'Zn', highlight: '360 kTon @ 4% Zn, NOT delimited (K 88-11)' },
  { name: 'Rassåive E', lon: 18.6041, lat: 67.0022, holes: 7, commodity: 'Base metals' },
  { name: 'Ravejaure', lon: 18.3873, lat: 66.9187, holes: 0, commodity: 'Cu-Mo', highlight: 'Cu-Mo porphyry occurrence' },
  { name: 'Iekelvare', lon: 18.57, lat: 66.905, holes: 6, commodity: 'Cu-Au-Mo', highlight: 'PRAP 83090: 7.8 ppm Au, 3% Cu, 0.44% Mo in blocks. Gold analysis INCOMPLETE. 1974-77 drill holes NOT in SGU DB. OPEN.' },
  { name: 'Pälkasbäcken', lon: 18.57, lat: 66.89, holes: 0, commodity: 'Cu', highlight: 'PRAP 83090: 4.77% Cu blocks. 3 coincident IP+slingram+soil Cu targets. NEVER DRILLED. OPEN.' },
  { name: 'Ritavare', lon: 18.54, lat: 66.94, holes: 0, commodity: 'Cu', highlight: 'PRAP 83090: 4.9% Cu blocks. 2 block trains. 2 holes recommended — NEVER DRILLED. OPEN.' },
  { name: 'Akatjvare', lon: 18.5095, lat: 66.9060, holes: 52, commodity: 'Wollastonite', highlight: '3.5 Mt wollastonite. NO metal assays — only major oxides tested. LKAB raw data LOST.' },
  { name: 'Latanjarka', lon: 18.5915, lat: 66.9030, holes: 31, commodity: 'Wollastonite', highlight: 'Wollastonite — stopped 1983 (too small). No metal assays. BRAP 81099 not digitized.' },
  { name: 'Kårnanjunnje', lon: 18.5466, lat: 66.8795, holes: 4, commodity: 'Wollastonite', highlight: 'S 86-22 (LKAB 1986). Wollastonite drilling — NO metal assays. Core may be at Malå.' },
  { name: 'Pallemvaratj', lon: 18.6663, lat: 66.9171, holes: 22, commodity: 'Pb-Zn-Ag', highlight: '0.5 Mt @ 7.0% Pb, 1.0% Zn, 30 ppm Ag. Parallel Cu zone: 0.80% Cu, 349 ppm Ag, 0.11% W. 4 km strike. OPEN.' },
  // === KOBERGET block (HIGH) === (Kiuri district)
  { name: 'KOBERGET', lon: 18.84, lat: 66.86, holes: 10, commodity: 'Cu-Co-Mo-Au', highlight: '>1% Cu, 726 ppm Co, 700m strike, trace electrum, Au NEVER assayed' },
  // === Outside polygons (reference only) ===
  { name: 'Lattakvare', lon: 18.3125, lat: 66.7279, holes: 14, commodity: 'W-Mo' },
  // === EVALUATED BUT NOT STAKED (status labels) ===
  // Iekelvare MOVED to main Kiuri list — confirmed OPEN at 18.57°E (25 km west of JIM permits)
  { name: 'Kallak/Jokkmokk Iron', lon: 19.10, lat: 66.83, holes: 500, commodity: 'Fe', status: 'BLOCKED', highlight: 'Iron deposit - Kallak nr 101/102' },
  { name: 'Rakten', lon: 18.55, lat: 67.08, holes: 33, commodity: 'U', status: 'KILLED', highlight: 'U too fragmented - early SGU (1945-1976)' },
  { name: 'Manak', lon: 18.57, lat: 67.06, holes: 11, commodity: 'U', status: 'KILLED', highlight: 'U too fragmented - early SGU (1947-1976)' },
  { name: 'Ultevis', lon: 18.65, lat: 67.13, holes: 16, commodity: 'U', status: 'INVESTIGATE', highlight: 'Early SGU U (1943) - not evaluated' },
  { name: 'Tallberget', lon: 19.2526, lat: 66.8186, holes: 14, commodity: 'Cu-Au', status: 'INVESTIGATE', highlight: 'ORED15490. 2.65% Cu, 8 g/t Au UNVERIFIED (SGU DB NULL). 36 km SE Kiuri. OPEN. Need K 88-15 from SGU Malå.' },
];

// Borehole cluster labels for LAISVALL district (17°E, 66°N) - renamed from "Njuggtraskliden"
// VERIFIED 2025-01-19: Grades cross-checked against SGU mineralresurser.gpkg and BRAP reports
const BOREHOLE_CLUSTER_LABELS_LV: Array<{
  name: string;
  lon: number;
  lat: number;
  holes: number;
  commodity: string;
  highlight?: string;
  status?: 'BLOCKED' | 'KILLED' | 'INVESTIGATE';
}> = [
  { name: 'LAISVALL', lon: 17.1235, lat: 66.1443, holes: 504, commodity: 'Pb-Zn', highlight: '64.49 Mt @ 3.8% Pb (closed mine - verify permits)' },
  { name: 'Pleutajokk', lon: 17.5759, lat: 66.2701, holes: 210, commodity: 'U', highlight: '4 Mt U resource' },
  { name: 'Björntjärn', lon: 17.6953, lat: 66.3284, holes: 256, commodity: 'Mo', highlight: '1.8 Mt @ 0.11% Mo' },
  { name: 'Allebuouda', lon: 17.7005, lat: 66.3277, holes: 90, commodity: 'Mo' },
  { name: 'Sundnäs', lon: 17.7570, lat: 66.3717, holes: 78, commodity: 'Cu-Ag', highlight: '1.3% Cu' },
  { name: 'Akkerselet', lon: 17.2118, lat: 65.9902, holes: 71, commodity: 'Pb', highlight: '7.7% Pb' },
  { name: 'Ardnajokk', lon: 17.8181, lat: 66.2856, holes: 66, commodity: 'W + Au', highlight: '23.5 g/t Au (BRAP verified)' },
  { name: 'Labbas', lon: 17.5614, lat: 66.4034, holes: 0, commodity: 'None', highlight: '⚠️ PHANTOM - 0 holes, no SGU data' },
  { name: 'Harrejokk', lon: 17.4306, lat: 66.3318, holes: 48, commodity: 'U', highlight: '❌ DEAD - U only, no base metals' },
  { name: 'Maldok', lon: 17.7175, lat: 66.3172, holes: 260, commodity: 'W-Pb', highlight: '❌ DRILLED OUT - 0.07 Mt only' },
  { name: 'Majva', lon: 17.2556, lat: 66.1751, holes: 93, commodity: 'Pb-Zn', highlight: '5.1% Pb' },
  { name: 'Ardnesjaure', lon: 17.4130, lat: 66.3920, holes: 28, commodity: 'Pb-Zn', highlight: '⚠️ TINY - only 0.05 Mt @ 9.1% Pb' },
  { name: 'AUTJAK', lon: 17.2868, lat: 66.3024, holes: 10, commodity: 'Ni-Cu', highlight: '4.3% Ni, 0.27% Cu (BRAP verified)' },
];

// Borehole cluster labels for ACTUAL NJUGGTRÄSKLIDEN (19.28°E, 64.82°N) - Ni-Cu-Co-PGM deposit
// Former Kendrick/EMX project - license lapsing Q1-Q2 2025
// VERIFIED 2025-01-19: All zones from SGU mineralresurser.gpkg
const BOREHOLE_CLUSTER_LABELS_NJ: Array<{
  name: string;
  lon: number;
  lat: number;
  holes: number;
  commodity: string;
  highlight?: string;
  status?: 'BLOCKED' | 'KILLED' | 'INVESTIGATE';
}> = [
  // VERIFIED 2025-12-23: Hole counts from SGU borrhal.gpkg (within 500m of each zone center)
  // Total unique holes in cluster: ~90. Zones overlap so same holes test multiple deposits.
  { name: 'NJUGGTRÄSKLIDEN', lon: 19.2822, lat: 64.8227, holes: 90, commodity: 'Ni-Cu-Co-PGM', highlight: 'Kendrick/EMX lapsed 2024 - OPEN' },
  { name: 'Zone A', lon: 19.2641, lat: 64.8292, holes: 14, commodity: 'Ni-Cu-Co', highlight: '0.11 Mt @ 0.69% Ni' },
  { name: 'Zone B', lon: 19.2662, lat: 64.8286, holes: 16, commodity: 'Ni-Cu-Co-Pt', highlight: '0.02 Mt, 0.3 g/t Pt' },
  { name: 'Zone C', lon: 19.2825, lat: 64.8212, holes: 54, commodity: 'Ni-Cu-Co', highlight: '0.19 Mt @ 0.7% Ni' },
  { name: 'Zone D', lon: 19.2830, lat: 64.8223, holes: 54, commodity: 'Ni-Cu-Co-Pt', highlight: '0.26 Mt @ 0.73% Ni, 4.9 g/t Pt' },
  { name: 'GRAPHITE ANOMALY', lon: 19.2721, lat: 64.8266, holes: 9, commodity: 'Graphite', highlight: 'Unexplored - high value target' },
  { name: 'MARATJÄRNLIDEN', lon: 19.3360, lat: 64.8008, holes: 9, commodity: 'Cu-Co', highlight: 'Cu-Co prospect - newly captured' },
  { name: 'Risliden', lon: 19.2781, lat: 64.8257, holes: 14, commodity: 'Cu-Zn', highlight: 'VMS Cu-Zn occurrence' },
  { name: 'ARNBERG', lon: 19.4012, lat: 64.8874, holes: 0, commodity: 'Cu', highlight: 'Cu occurrence - optional strike extension' },
];

// Combined cluster labels (Jokkmokk + Laisvall + Njuggträskliden)
const BOREHOLE_CLUSTER_LABELS = [...BOREHOLE_CLUSTER_LABELS_VB, ...BOREHOLE_CLUSTER_LABELS_LV, ...BOREHOLE_CLUSTER_LABELS_NJ];

// Serve from public/ so it is available at runtime
// Expanded to cover all staking regions: Jokkmokk, Laisvall, Björntjärn, Pleutajokk, Njuggträskliden
const NATURA_LOCAL_URL = '/data/natura2000_sweden.geojson';
const SGU_PREF_STORAGE_KEY = (projectId?: string | null) =>
  projectId ? `sgu-pref-${projectId}` : 'sgu-pref-default';

// Marker density/legibility defaults (matched to Daura heatmap granularity)
const MAX_MARKERS_PER_ANALYSIS = Number.MAX_SAFE_INTEGER; // show every grid point
const DEFAULT_VISIBLE_MARKER_COUNT = MAX_MARKERS_PER_ANALYSIS; // default to all points visible
const DEFAULT_LABELED_MARKER_COUNT = 40; // keep labels on the top cohort to avoid clutter
const MIN_MARKER_RADIUS_PX = 6;
const MAX_MARKER_RADIUS_PX = 11;

// Probability-driven gradient (borrowed from copper heatmap palette for consistency)
const PROBABILITY_COLOR_STOPS: Array<[number, string]> = [
  [0.0, '#e7f3f9'],
  [0.2, '#70cbc2'],
  [0.35, '#92c996'],
  [0.55, '#b2bd82'],
  [0.7, '#c89b76'],
  [0.85, '#da8458'],
  [1.0, '#cd5e46']
];

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  const bigint = parseInt(normalized, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const getProbabilityColor = (value: number): string => {
  const clamped = Math.max(0, Math.min(1, value));
  for (let i = 0; i < PROBABILITY_COLOR_STOPS.length - 1; i++) {
    const [startPos, startColor] = PROBABILITY_COLOR_STOPS[i];
    const [endPos, endColor] = PROBABILITY_COLOR_STOPS[i + 1];
    if (clamped >= startPos && clamped <= endPos) {
      const t = (clamped - startPos) / (endPos - startPos || 1);
      const startRgb = hexToRgb(startColor);
      const endRgb = hexToRgb(endColor);
      const r = Math.round(startRgb[0] + (endRgb[0] - startRgb[0]) * t);
      const g = Math.round(startRgb[1] + (endRgb[1] - startRgb[1]) * t);
      const b = Math.round(startRgb[2] + (endRgb[2] - startRgb[2]) * t);
      return `rgb(${r}, ${g}, ${b})`;
    }
  }
  const [, fallback] = PROBABILITY_COLOR_STOPS[PROBABILITY_COLOR_STOPS.length - 1];
  return fallback;
};

const normalizeProbabilityValue = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const raw = value > 1 ? value / 100 : value;
  return Math.max(0, Math.min(1, raw));
};

type ProbabilityStats = {
  min: number;
  max: number;
  p10: number;
  p90: number;
};

const computeProbabilityStats = (values: number[]): ProbabilityStats => {
  if (!values.length) {
    return { min: 0, max: 1, p10: 0, p90: 1 };
  }
  const sorted = [...values].filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!sorted.length) {
    return { min: 0, max: 1, p10: 0, p90: 1 };
  }
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const p10 = sorted[Math.max(0, Math.floor(sorted.length * 0.1) - 1)] ?? min;
  const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))] ?? max;
  return { min, max, p10, p90 };
};

const stretchProbability = (value: number, stats: ProbabilityStats): number => {
  const baseMin = Number.isFinite(stats.p10) ? stats.p10 : stats.min;
  const baseMax = Number.isFinite(stats.p90) && stats.p90 > baseMin ? stats.p90 : stats.max;
  const denom = Math.max(1e-5, baseMax - baseMin);
  return Math.max(0, Math.min(1, (value - baseMin) / denom));
};

const applyAlphaToColor = (color: string, alpha: number): string => {
  const match = color.match(/\d+/g);
  if (match && match.length >= 3) {
    const [r, g, b] = match.map((v) => Number(v));
    const clamped = Math.max(0, Math.min(1, alpha));
    return `rgba(${r}, ${g}, ${b}, ${clamped.toFixed(3)})`;
  }
  return color;
};

const getAnalysisId = (result: any): string | null => {
  if (!result || typeof result !== 'object') {
    return null;
  }
  const idCandidate =
    result.id ||
    result.analysis_id ||
    result.analysisId ||
    result.results?.analysis_id ||
    result.results?.id ||
    null;
  if (typeof idCandidate === 'string') {
    const trimmed = idCandidate.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const formatGeochemValue = (key: string, value: number): string => {
  const abs = Math.abs(value);
  if (!Number.isFinite(value)) {
    return '—';
  }
  if (abs >= 1000) {
    return value.toFixed(1);
  }
  if (abs >= 10) {
    return value.toFixed(2);
  }
  if (abs >= 1) {
    return value.toFixed(3);
  }
  return value.toPrecision(3);
};

const prettifyFieldLabel = (key: string): string => {
  if (!key) {
    return '';
  }
  return key
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
};

const INSPECTION_HIDDEN_PROPERTY_KEYS = new Set([
  'geometry',
  'boundedBy',
  PROJECT_LAYER_LABEL_FEATURE_KEY,
  PROJECT_LAYER_LABEL_TEXT_KEY,
  PROJECT_POINT_LABEL_OFFSET_INDEX_KEY,
  '__geomorphicLayerId',
  '__geomorphicLayerLabel',
  '__geomorphicLayerGroup',
  '__geomorphicLayerSource',
  '__geomorphicPortfolioStage',
  '__geomorphicConfidence',
  '__geomorphicNextAction',
]);

const INSPECTION_PRIORITY_KEYS = [
  'portfolio_status',
  'status',
  'classification',
  'priority_tier',
  'staking_action',
  'targetArea',
  'commodity',
  'deposit_model',
  'permit_id',
  'permitId',
  'holder',
  'owner',
  'operator',
  'feature_count',
  'area',
  'area_ha',
  'area_km2',
  'best_grade_value',
  'best_grade_unit',
  'open_ground_pct',
  'drill_holes_in_area',
  'source',
  'path',
];

const normalizePortfolioStage = (value: unknown): PortfolioStage | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (normalized.includes('claim') || normalized.includes('grant')) return 'Claimed';
  if (normalized.includes('file')) return 'Filed';
  if (normalized.includes('appl') || normalized.includes('pending')) return 'Applied';
  if (normalized.includes('open') || normalized.includes('stakeable')) return 'Open';
  if (normalized.includes('kill') || normalized.includes('reject') || normalized.includes('blocked')) return 'Killed';
  if (normalized.includes('monitor') || normalized.includes('watch')) return 'Monitor';
  if (normalized.includes('reviewed')) return 'Reviewed';
  if (normalized.includes('review')) return 'Needs review';
  return PORTFOLIO_STAGE_ORDER.find((stage) => stage.toLowerCase() === normalized);
};

const formatInspectionValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }
    return Math.abs(value) >= 1000 ? value.toLocaleString() : `${Number(value.toFixed(5))}`;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (Array.isArray(value)) {
    return value.length ? value.slice(0, 4).map((entry) => `${entry}`).join(', ') : null;
  }
  if (typeof value === 'object') {
    return null;
  }
  return `${value}`;
};

const buildInspectionRows = (properties: Record<string, unknown>): InspectionRow[] => {
  const emitted = new Set<string>();
  const rows: InspectionRow[] = [];
  const addRow = (key: string) => {
    if (emitted.has(key) || INSPECTION_HIDDEN_PROPERTY_KEYS.has(key)) {
      return;
    }
    const value = formatInspectionValue(properties[key]);
    if (!value) {
      return;
    }
    emitted.add(key);
    rows.push({ label: prettifyFieldLabel(key), value });
  };
  INSPECTION_PRIORITY_KEYS.forEach(addRow);
  Object.keys(properties)
    .filter((key) => !key.startsWith('__'))
    .slice(0, 40)
    .forEach(addRow);
  return rows.slice(0, 20);
};

const ACTION_SEARCH_FEATURE_KEYS = [
  'drillhole',
  'hole_id',
  'idcode',
  'permit_id',
  'permitId',
  'dnr',
  'diary_number',
  'target_id',
  'target',
  'name',
  'Name',
  'NAME',
  'label',
  'operator',
  'holder',
  'owner',
  'company',
  'applicant',
  'portfolio_status',
  'status',
  'classification',
  'targetArea',
  '__geomorphicLayerLabel',
  '__geomorphicLayerSource',
  '__geomorphicPortfolioStage',
];

const normalizeActionSearchText = (value: unknown) =>
  (formatInspectionValue(value) || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const buildActionSearchText = (values: unknown[]) =>
  values
    .map(normalizeActionSearchText)
    .filter(Boolean)
    .join(' ');

const matchesActionSearchQuery = (text: string, query: string) => {
  const normalizedText = normalizeActionSearchText(text);
  const tokens = normalizeActionSearchText(query).split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => normalizedText.includes(token));
};

const getFeatureSearchText = (feature: any, layerLabel?: string) => {
  const properties = feature?.getProperties?.() || {};
  const prioritized = ACTION_SEARCH_FEATURE_KEYS
    .map((key) => formatInspectionValue(properties[key]))
    .filter(Boolean)
    .join(' ');
  return `${layerLabel || ''} ${prioritized}`.toLowerCase();
};

const getFeatureActionTitle = (feature: any, fallback = 'Map feature') => {
  const properties = feature?.getProperties?.() || {};
  return (
    formatInspectionValue(properties.__geomorphicLayerLabel) ||
    formatInspectionValue(properties.drillhole) ||
    formatInspectionValue(properties.hole_id) ||
    formatInspectionValue(properties.idcode) ||
    formatInspectionValue(properties.permit_id) ||
    formatInspectionValue(properties.permitId) ||
    formatInspectionValue(properties.target_id) ||
    formatInspectionValue(properties.name) ||
    formatInspectionValue(properties.Name) ||
    formatInspectionValue(properties.NAME) ||
    formatInspectionValue(properties.label) ||
    fallback
  );
};

const getGeometryFocusCoordinate = (geometry: any): [number, number] | null => {
  if (!geometry) {
    return null;
  }
  const type = geometry.getType?.() || '';
  if (type === 'Point') {
    return geometry.getCoordinates?.() || null;
  }
  if (type === 'MultiPoint') {
    const coordinates = geometry.getCoordinates?.();
    return Array.isArray(coordinates) && coordinates[0] ? coordinates[0] : null;
  }
  if (type === 'Polygon' && geometry.getInteriorPoint) {
    return geometry.getInteriorPoint().getCoordinates();
  }
  if (type === 'MultiPolygon' && geometry.getInteriorPoints) {
    const coordinates = geometry.getInteriorPoints()?.getCoordinates?.() || [];
    const bestCoordinate = coordinates.reduce((best: number[] | null, coordinate: number[]) => {
      if (!best) {
        return coordinate;
      }
      return (coordinate[2] || 0) > (best[2] || 0) ? coordinate : best;
    }, null);
    if (bestCoordinate) {
      return [bestCoordinate[0], bestCoordinate[1]];
    }
  }
  const extent = geometry.getExtent?.();
  return extent && extent.length === 4
    ? [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]
    : null;
};

const mergeExtents = (
  current: [number, number, number, number] | null,
  next?: [number, number, number, number] | null
): [number, number, number, number] | null => {
  if (!next || next.length !== 4 || next.some((value) => !Number.isFinite(value))) {
    return current;
  }
  if (!current) {
    return [next[0], next[1], next[2], next[3]];
  }
  return [
    Math.min(current[0], next[0]),
    Math.min(current[1], next[1]),
    Math.max(current[2], next[2]),
    Math.max(current[3], next[3]),
  ];
};

type RgbTuple = [number, number, number];

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const interpolateTuple = (start: RgbTuple, end: RgbTuple, t: number): RgbTuple => {
  return [
    Math.round(start[0] + (end[0] - start[0]) * t),
    Math.round(start[1] + (end[1] - start[1]) * t),
    Math.round(start[2] + (end[2] - start[2]) * t)
  ];
};

const rgbaFromTuple = (rgb: RgbTuple, alpha: number): string => {
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${clampedAlpha.toFixed(3)})`;
};

const GEOCHEM_COLOR_LOW: RgbTuple = [56, 189, 248]; // light cyan
const GEOCHEM_COLOR_MID: RgbTuple = [255, 196, 0]; // amber
const GEOCHEM_COLOR_HIGH: RgbTuple = [220, 38, 38]; // red

const GEOCHEM_LEGEND_GRADIENT = `linear-gradient(90deg, ${rgbaFromTuple(GEOCHEM_COLOR_LOW, 1)} 0%, ${rgbaFromTuple(GEOCHEM_COLOR_MID, 1)} 50%, ${rgbaFromTuple(GEOCHEM_COLOR_HIGH, 1)} 100%)`;

const getGeochemColor = (normalized: number): RgbTuple => {
  const t = clamp01(normalized);
  if (t <= 0.5) {
    const localT = t * 2;
    return interpolateTuple(GEOCHEM_COLOR_LOW, GEOCHEM_COLOR_MID, localT);
  }
  const localT = (t - 0.5) * 2;
  return interpolateTuple(GEOCHEM_COLOR_MID, GEOCHEM_COLOR_HIGH, localT);
};

interface HighlightPoint {
  lat: number;
  lon: number;
  type: 'heatmap' | 'drill' | 'surface';
  payload?: Record<string, any>;
}

interface DrillCollarSummary {
  holeId: string;
  region?: string | null;
  latitude: number;
  longitude: number;
  elevationM?: number | null;
  totalDepthM?: number | null;
  status?: string | null;
  categoria?: string | null;
  intervalCount?: number | null;
  sampledLengthM?: number | null;
  avgGoldPpm?: number | null;
  maxGoldPpm?: number | null;
  avgSilverPpm?: number | null;
  maxSilverPpm?: number | null;
  gradeThicknessAuGm?: number | null;
  sourceFile?: string | null;
}

interface DrillIntervalSummary {
  holeId: string;
  region?: string | null;
  fromM?: number | null;
  toM?: number | null;
  lengthM?: number | null;
  goldPpm?: number | null;
  silverPpm?: number | null;
  copperPpm?: number | null;
  leadPpm?: number | null;
  zincPpm?: number | null;
  gradeThicknessAuGm?: number | null;
  lithology?: string | null;
  alteration?: string | null;
  alterationIntensity?: string | null;
  mineralization?: string | null;
  sampleCode?: string | null;
  sourceFile?: string | null;
}

interface DrillIntervalStats {
  intervalCount: number;
  totalSampledLengthM: number;
  gradeThicknessAuGm: number;
  maxGoldPpm: number | null;
  maxSilverPpm: number | null;
}

interface SurfaceAssaySummary {
  sampleCode?: string | null;
  region?: string | null;
  latitude: number;
  longitude: number;
  goldPpm?: number | null;
  silverPpm?: number | null;
  copperPpm?: number | null;
  leadPpm?: number | null;
  zincPpm?: number | null;
  values: Record<string, number | null>;
  metadata: Record<string, any>;
}

type InsightTabKey = 'heatmap' | 'drill' | 'surface';

const HIGHLIGHT_STYLE_MAP: Record<InsightTabKey, { fill: string; stroke: string; radius: number }> = {
  heatmap: {
    fill: 'rgba(56, 189, 248, 0.28)',
    stroke: 'rgba(56, 189, 248, 0.95)',
    radius: 10,
  },
  drill: {
    fill: 'rgba(249, 115, 22, 0.25)',
    stroke: 'rgba(249, 115, 22, 0.85)',
    radius: 9,
  },
  surface: {
    fill: 'rgba(168, 85, 247, 0.25)',
    stroke: 'rgba(168, 85, 247, 0.85)',
    radius: 8,
  },
};

const toNumberOrNull = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatPpm = (value: number | null | undefined, precision = 3): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const abs = Math.abs(value);
  if (abs >= 1000) {
    return value.toFixed(1);
  }
  if (abs >= 100) {
    return value.toFixed(1);
  }
  if (abs >= 10) {
    return value.toFixed(2);
  }
  if (abs >= 1) {
    return value.toFixed(3);
  }
  return value.toFixed(precision);
};

const formatLengthMeters = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)} km`;
  }
  return `${value.toFixed(1)} m`;
};

const formatGradeThickness = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  if (value >= 100) {
    return `${value.toFixed(1)} g·m`;
  }
  if (value >= 10) {
    return `${value.toFixed(2)} g·m`;
  }
  return `${value.toFixed(3)} g·m`;
};

const getTopIntervals = (intervals: DrillIntervalSummary[], limit = 12): DrillIntervalSummary[] =>
  [...intervals]
    .sort((a, b) => (b.gradeThicknessAuGm || 0) - (a.gradeThicknessAuGm || 0))
    .slice(0, limit);

export interface MapViewProps {
  analysisResults?: any[];
  selectedProject?: any;
  availableProjects?: MapSearchProject[];
  onProjectSelect?: (projectId: string) => void | Promise<void>;
  onLocationSelect?: (coordinates: { lat: number; lng: number }) => void;
  mineralMapData?: any;
  isNewMineralData?: boolean;
  // Commented out per strategic pivot to focus on document management
  // onGenerateMineralMap?: (coordinates: { lat: number; lng: number }) => void;
  // Removed test function props for clean UI
  onSaveMineralAnalysis?: (data: any) => void;
  onClearMineralAnalysis?: () => void;
  onMineralDataCentered?: () => void;
  // Mineral Discovery Integration
  onRunMineralDiscovery?: (config: any) => void;
  onExportResults?: (resultId: string, format: string) => void;
  // Polygon Drawing Integration
  polygonRegions?: PolygonRegion[];
  onPolygonCreate?: (polygon: CreatePolygonRequest) => void;
  onPolygonUpdate?: (polygonId: string, updates: Partial<PolygonRegion>) => void;
  onPolygonDelete?: (polygonId: string) => void;
  onPolygonDeleteAll?: () => void;
  onPolygonImport?: (file: File, options?: { color?: string; transparency?: number }) => void | Promise<void>;
  onPolygonAnalysis?: (polygonId: string, config: AnalysisConfig, options?: { mode?: 'full' | 'quick' }) => void | Promise<void>;
  onAnalysisComplete?: (result: any) => void;
  markerVisibility?: globalThis.Map<string, boolean>;
  onMarkerVisibilityChange?: (markerId: string, visible: boolean) => void;
  onBatchMarkerVisibilityChange?: (updates: Array<{markerId: string, visible: boolean}>) => void;
  gridSettings?: globalThis.Map<string, GridSettings>;
  onGridSettingsChange?: (analysisId: string, mineralKey: string, updates: Partial<GridSettings>) => void;
  renderLegendAnalysis?: boolean;
  onLegendEntriesChange?: (entries: AnalysisGridEntry[]) => void;
  gridResolutionResolver?: (context: { projectId?: string | null; polygonId?: string | null }) => number;
  legendPortalContainerId?: string;
  inspectionPortalContainerId?: string;
  onInspectionActiveChange?: (active: boolean) => void;
  mlLayers?: Record<string, boolean>;
  mlOpacity?: number;
}

interface HeatmapDataPoint {
  lat: number;
  lng: number;
  probability: number;
  mineral_type: string;
}

interface PolygonRegion {
  id: string;
  name: string;
  coordinates: number[][];
  metadata: PolygonMetadata;
  region_type: 'project_boundary' | 'analysis_region';
  is_valid: boolean;
  validation_errors: string[];
  visible: boolean;
  color?: string;
  transparency?: number;
  created_at: string;
  updated_at: string;
  used_in_analyses?: string[];
  last_analysis_date?: string;
}

interface PolygonMetadata {
  area_km2: number;
  perimeter_km: number;
  vertex_count: number;
  bounds: {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
  };
}

interface CreatePolygonRequest {
  name: string;
  coordinates: number[][];
  region_type: 'project_boundary' | 'analysis_region';
  metadata: PolygonMetadata;
}

interface AnalysisConfig {
  commodity: 'gold' | 'silver' | 'copper';
  analysis_name: string;
  confidence_threshold: number;
  output_formats: string[];
  run_settings?: ModelRunSettings;
}

interface RasterAssetDescriptor {
  layer: string;
  format: string;
  url: string;
  width: number;
  height: number;
  bounds: [number, number, number, number];
  value_range?: [number, number];
  statistics?: {
    min: number;
    max: number;
    mean: number;
    stdev: number;
  };
  nodata?: number | null;
  pixel_size?: [number, number];
  content_type?: string;
  filename?: string;
  relative_path?: string;
}

interface StaticRasterDescriptor {
  id: string;
  layer: string;
  displayName: string;
  category?: string;
  description?: string;
  source?: string;
  url: string;
  contentType?: string;
  format?: string;
  defaultOpacity?: number;
  valueRange?: [number, number];
  statistics?: {
    min?: number;
    max?: number;
    mean?: number;
  };
  nodata?: number | null;
  bounds?: {
    west?: number;
    south?: number;
    east?: number;
    north?: number;
  } | null;
  resolution?: {
    x?: number;
    y?: number;
  } | null;
}

interface AnalysisGridMetadata {
  analysisName?: string;
  analysisLabel?: string;
  discoveryProbability?: number;
  averageProbability?: number;
  maxProbability?: number;
  totalPoints?: number;
  highPotentialCount?: number;
  modelVersion?: string;
  completedAt?: string | null;
  bounds?: [number, number, number, number] | null;
  polygonBounds?: BoundsObject | null;
  projectId?: string | null;
  projectName?: string | null;
  grid_size?: [number, number];
  cell_dimensions?: {
    width_deg: number;
    height_deg: number;
    width_km: number;
    height_km: number;
  };
  polygon_bounds?: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
    center_lat: number;
    center_lon: number;
  };
  uncertainties?: Array<number | null>;
  commodity?: string;
  discovery_probability?: number;
  average_probability?: number;
  max_probability?: number;
  total_points?: number;
  high_potential_count?: number;
  [key: string]: unknown;
}

export interface AnalysisGridEntry {
  analysisId: string;
  polygonId?: string;
  polygonName?: string;
  projectId?: string | null;
  commodity: string;
  probabilities: number[];
  coordinates: [number, number][];
  confidences?: Array<number | null>;
  pointDetails?: AnalysisPointDetails[];
  metadata?: AnalysisGridMetadata;
  completedAt?: string | null;
  rasterAssets?: Record<string, RasterAssetDescriptor>;
}

interface HoveredGridPoint {
  analysisId: string;
  commodity: string;
  lat: number;
  lon: number;
  probability: number;
  detail?: AnalysisPointDetails | null;
  index?: number;
}


interface GeochemOverlayPoint {
  lat: number;
  lon: number;
  values: Record<string, number | null>;
  metadata?: Record<string, any>;
}

interface GeochemOverlayDescriptor {
  id: string;
  name: string;
  polygonHint?: string | null;
  elements: string[];
  pointCount: number;
  bounds?: BoundsObject;
  data: GeochemOverlayPoint[];
}

interface ControlFlyoutSummary {
  discoveryProbability: number | null;
  avgProbability: number | null;
  highPotential: number;
  gridPoints: number;
  completedAt: string;
  polygonName: string;
}

interface GridLayerData {
  probabilities: number[];
  coordinates: [number, number][];
  confidences?: number[];
  metadata?: {
    bounds: [number, number, number, number];
    grid_size: [number, number];
    cell_dimensions: {
      width_deg: number;
      height_deg: number;
      width_km: number;
      height_km: number;
    };
    polygon_bounds: {
      min_lat: number;
      max_lat: number;
      min_lon: number;
      max_lon: number;
      center_lat: number;
      center_lon: number;
    };
  };
}

export const HEATMAP_CONTROLS_ID = 'analysis-heatmap-controls';
const GEOCHEM_PREF_STORAGE_KEY = 'mineral-ai-geochem-preferences';

interface BoundsObject {
  min_lat: number;
  max_lat: number;
  min_lon: number;
  max_lon: number;
}

const formatTimestamp = (value?: string | null): string => {
  if (!value) {
    return 'Unknown run';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  });
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
};

const buildAnalysisRunLabel = (entry: AnalysisGridEntry): string => {
  const analysisName = entry.metadata?.analysisName || entry.metadata?.analysisLabel;
  if (analysisName && analysisName.trim().length > 0) {
    return analysisName.trim();
  }

  const commodityTag = (entry.commodity || 'Unknown').toUpperCase();
  const timestamp = entry.completedAt ? formatTimestamp(entry.completedAt) : undefined;
  const avgProbability = entry.metadata?.averageProbability ?? entry.metadata?.average_probability ?? null;
  const maxProbability = entry.metadata?.maxProbability ?? entry.metadata?.max_probability ?? null;
  const highCount = entry.metadata?.highPotentialCount ?? entry.metadata?.high_potential_count ?? 0;

  const parts: string[] = [commodityTag];
  if (timestamp && timestamp !== 'Unknown run') {
    parts.push(timestamp);
  }
  if (typeof avgProbability === 'number') {
    parts.push(`Avg ${formatPercentText(avgProbability)}`);
  }
  if (typeof maxProbability === 'number') {
    parts.push(`Max ${formatPercentText(maxProbability)}`);
  }
  if (highCount > 0) {
    parts.push(`${highCount} high cells`);
  }
  if (parts.length === 1) {
    const partsId = entry.analysisId ? entry.analysisId.split('_') : [];
    const suffix = partsId.length ? partsId[partsId.length - 1] : entry.analysisId || 'RUN';
    parts.push(`#${suffix}`);
  }
  return parts.join(' • ');
};

const formatPercentText = (value?: number | null, digits = 1): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${(value * 100).toFixed(digits)}%`;
  }
  return '—';
};

const toUnitInterval = (value: number): number => (value > 1 ? value / 100 : value);

const getDetailNumber = (detail: any, ...keys: string[]): number | null => {
  if (!detail) {
    return null;
  }
  for (const key of keys) {
    const value = detail?.[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

const getDetailString = (detail: any, ...keys: string[]): string | null => {
  if (!detail) {
    return null;
  }
  for (const key of keys) {
    const value = detail?.[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
};

const formatLatitude = (value: number): string => `${Math.abs(value).toFixed(4)}°${value >= 0 ? 'N' : 'S'}`;
const formatLongitude = (value: number): string => `${Math.abs(value).toFixed(4)}°${value >= 0 ? 'E' : 'W'}`;

const boundsIntersect = (a?: BoundsObject | null, b?: BoundsObject | null): boolean => {
  if (!a || !b) {
    return false;
  }
  if (a.max_lat < b.min_lat || a.min_lat > b.max_lat) {
    return false;
  }
  if (a.max_lon < b.min_lon || a.min_lon > b.max_lon) {
    return false;
  }
  return true;
};

const normalizeBoundsObject = (bounds: any): BoundsObject | null => {
  if (!bounds) {
    return null;
  }
  if (
    typeof bounds?.min_lat === 'number' &&
    typeof bounds?.max_lat === 'number' &&
    typeof bounds?.min_lon === 'number' &&
    typeof bounds?.max_lon === 'number'
  ) {
    return {
      min_lat: Number(bounds.min_lat),
      max_lat: Number(bounds.max_lat),
      min_lon: Number(bounds.min_lon),
      max_lon: Number(bounds.max_lon)
    };
  }
  if (Array.isArray(bounds) && bounds.length >= 4) {
    return {
      min_lat: Number(bounds[0]),
      min_lon: Number(bounds[1]),
      max_lat: Number(bounds[2]),
      max_lon: Number(bounds[3])
    };
  }
  return null;
};

const computeBoundsFromLatLon = (coordinates: [number, number][]): BoundsObject | null => {
  if (!coordinates.length) {
    return null;
  }

  let min_lat = Infinity;
  let max_lat = -Infinity;
  let min_lon = Infinity;
  let max_lon = -Infinity;

  coordinates.forEach(([lat, lon]) => {
    if (typeof lat === 'number' && Number.isFinite(lat)) {
      if (lat < min_lat) min_lat = lat;
      if (lat > max_lat) max_lat = lat;
    }
    if (typeof lon === 'number' && Number.isFinite(lon)) {
      if (lon < min_lon) min_lon = lon;
      if (lon > max_lon) max_lon = lon;
    }
  });

  if (!Number.isFinite(min_lat) || !Number.isFinite(max_lat) || !Number.isFinite(min_lon) || !Number.isFinite(max_lon)) {
    return null;
  }

  return {
    min_lat,
    max_lat,
    min_lon,
    max_lon
  };
};

const flattenPolygonCoordinates = (coordinates: any): Array<[number, number]> => {
  if (!Array.isArray(coordinates)) {
    return [];
  }

  const result: Array<[number, number]> = [];
  const stack = [...coordinates];

  while (stack.length) {
    const value = stack.pop();
    if (Array.isArray(value)) {
      if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
        result.push([value[0], value[1]]);
      } else {
        stack.push(...value);
      }
    }
  }

  return result;
};

const MapView: React.FC<MapViewProps> = ({
  analysisResults = [],
  selectedProject,
  availableProjects = [],
  onProjectSelect,
  onLocationSelect,
  mineralMapData,
  isNewMineralData = false,
  // Commented out per strategic pivot to focus on document management
  // onGenerateMineralMap,
  // Removed test function parameters
  onSaveMineralAnalysis,
  onClearMineralAnalysis,
  onMineralDataCentered,
  // Mineral Discovery Integration
  onRunMineralDiscovery,
  onExportResults,
  // Polygon Drawing Integration
  polygonRegions = [],
  onPolygonCreate,
  onPolygonUpdate,
  onPolygonDelete,
  onPolygonDeleteAll,
  onPolygonAnalysis,
  onPolygonImport,
  onAnalysisComplete,
  markerVisibility,
  onMarkerVisibilityChange,
  onBatchMarkerVisibilityChange,
  gridSettings,
  onGridSettingsChange,
  renderLegendAnalysis = true,
  onLegendEntriesChange,
  gridResolutionResolver,
  legendPortalContainerId,
  inspectionPortalContainerId,
  onInspectionActiveChange,
  mlLayers,
  mlOpacity = 0.7,
}) => {
  const resolveGridResolutionFor = useCallback(
    (projectId?: string | null, polygonId?: string | null) => {
      if (gridResolutionResolver) {
        return gridResolutionResolver({ projectId, polygonId });
      }
      return 32;
    },
    [gridResolutionResolver]
  );
  // Removed render-cycle debug logs for performance
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const rasterLayerCacheRef = useRef<globalThis.Map<string, BaseTileLayer<any, any>>>(new globalThis.Map());
  const staticRasterLayerCacheRef = useRef<globalThis.Map<string, BaseTileLayer<any, any>>>(new globalThis.Map());
  const sguVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<any>>>(new globalThis.Map());
  const sguFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  // Peru/INGEMMET layer refs
  const peruVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const peruFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  // Papua New Guinea (MRA) layer refs
  const pngVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const pngFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  // Yukon (GeoYukon) layer refs — vector layers + raster geophysics tile layers
  const yukonVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const yukonGeophysLayersRef = useRef<globalThis.Map<string, TileLayer<TileArcGISRest>>>(new globalThis.Map());
  const yukonFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  // Peru Playbook static analysis layers
  const peruAnalysisLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  // France/BRGM static layer refs
  const franceLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const jurisdictionLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const jurisdictionFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  // Caujul layer refs
  const caujulVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const caujulFetchInFlightRef = useRef<globalThis.Map<string, boolean>>(new globalThis.Map());
  const caujulConcessionsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const boreholeClusterLabelsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const swedenDrillClusterReviewFeaturesRef = useRef<Feature[]>([]);
  const swedenDrillClusterReviewFetchRef = useRef<Promise<Feature[]> | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const adjustOverlayPlacementRef = useRef<(() => void) | null>(null);
  const highlightLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const highlightSourceRef = useRef<VectorSource | null>(null);
  const clusterSpiderLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const clearClusterSpiderLayer = useCallback(() => {
    const layer = clusterSpiderLayerRef.current;
    if (layer && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(layer);
    }
    clusterSpiderLayerRef.current = null;
  }, []);
  useEffect(() => {
    clearClusterSpiderLayer();
  }, [clearClusterSpiderLayer, selectedProject?.id]);
  // Uranium targeting layers (Sweden Uranium project)
  const uraniumOccurrencesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const alumShaleLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const uraniumKnownDepositsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const radiometricUraniumLayerRef = useRef<TileLayer<TileWMS> | null>(null);
  const radiometricThoriumLayerRef = useRef<TileLayer<TileWMS> | null>(null);
  const uraniumRankedTargetsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const palangeVerificationLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const koliVmsTargetsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const ropenNearbyPermitsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const bergslagenTillAuLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const bergslagenTillTeLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const stakingBoundariesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const ropenVmsPointsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const geochemLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const stakingPolygonLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const dcStakingLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const kiuriDrillholesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const kiuriDistrictHolesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const kiuriGrabSamplesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const kiuriApplicationPolygonsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const granuddenDraftLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const granuddenEvidenceLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  // ML output layer refs
  const mlLayerRefsRef = useRef<globalThis.Map<string, TileLayer<any> | WebGLTile | VectorLayer<VectorSource>>>(new globalThis.Map());
  const mlLayersLoadedRef = useRef(false);

	  const BASE_LAYER_KEYS = useMemo<string[]>(() => [...BASE_LAYER_IDS], []);
	  const activeLayersProjectKey = storageProjectKey(selectedProject?.id);
	  const [activeLayers, setActiveLayers] = useState<string[]>(() => readStoredActiveLayers(selectedProject?.id));
	  const skipActiveLayerPersistRef = useRef(true);

	  useEffect(() => {
	    skipActiveLayerPersistRef.current = true;
	    setActiveLayers(readStoredActiveLayers(selectedProject?.id));
	  }, [selectedProject?.id]);

	  useEffect(() => {
	    if (skipActiveLayerPersistRef.current) {
	      skipActiveLayerPersistRef.current = false;
	      return;
	    }
	    writeJsonStorage(`${MAP_LAYER_ACTIVE_PREFIX}${activeLayersProjectKey}`, activeLayers);
	  }, [activeLayersProjectKey, activeLayers]);

  const loadGeochemPreferences = useCallback((projectId: string) => {
    if (!projectId) {
      return { overlayId: '', visible: false, colorField: '' };
    }
    try {
      const raw = localStorage.getItem(GEOCHEM_PREF_STORAGE_KEY);
      if (!raw) {
        return { overlayId: '', visible: false, colorField: '' };
      }
      const parsed = JSON.parse(raw);
      const entry = parsed?.[projectId];
      if (entry && typeof entry === 'object') {
        return {
          overlayId: typeof entry.overlayId === 'string' ? entry.overlayId : '',
          visible: Boolean(entry.visible),
          colorField: typeof entry.colorField === 'string' ? entry.colorField : ''
        };
      }
    } catch (error) {
      console.error('Failed to load geochemistry overlay preferences:', error);
    }
    return { overlayId: '', visible: false, colorField: '' };
  }, []);

  const persistGeochemPreferences = useCallback(
    (projectId: string, overlayId: string, visible: boolean, colorField: string) => {
      if (!projectId) {
        return;
      }
      try {
        const raw = localStorage.getItem(GEOCHEM_PREF_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        parsed[projectId] = { overlayId, visible, colorField };
        localStorage.setItem(GEOCHEM_PREF_STORAGE_KEY, JSON.stringify(parsed));
      } catch (error) {
        console.error('Failed to persist geochemistry overlay preferences:', error);
      }
    },
    []
  );

  const [geochemOverlays, setGeochemOverlays] = useState<GeochemOverlayDescriptor[]>([]);
  const [geochemOverlayId, setGeochemOverlayId] = useState<string>('');
  const [geochemOverlayVisible, setGeochemOverlayVisible] = useState<boolean>(false);
  const [geochemColorField, setGeochemColorField] = useState<string>('');
  const [projectStaticRasters, setProjectStaticRasters] = useState<StaticRasterDescriptor[]>([]);
  const [staticRasterVisibility, setStaticRasterVisibility] = useState<Record<string, boolean>>({});
const [sguLayerVisibility, setSguLayerVisibility] = useState<Record<string, boolean>>({});
// Peru/INGEMMET layer visibility
const [peruLayerVisibility, setPeruLayerVisibility] = useState<Record<string, boolean>>({});
// Papua New Guinea (MRA) layer visibility
const [pngLayerVisibility, setPngLayerVisibility] = useState<Record<string, boolean>>({});
// Yukon (GeoYukon) layer visibility
const [yukonLayerVisibility, setYukonLayerVisibility] = useState<Record<string, boolean>>({});
// Peru Playbook analysis layer visibility (static GeoJSON layers)
const [peruAnalysisVisibility, setPeruAnalysisVisibility] = useState<Record<string, boolean>>({});
// Caujul layer visibility
const [caujulLayerVisibility, setCaujulLayerVisibility] = useState<Record<string, boolean>>({});
// France/BRGM layer visibility
const [franceLayerVisibility, setFranceLayerVisibility] = useState<Record<string, boolean>>({});
const [jurisdictionLayerVisibility, setJurisdictionLayerVisibility] = useState<Record<string, boolean>>({});
const [clusterLabelsVisible, setClusterLabelsVisible] = useState<boolean>(false);
const [sguUseIntervals, setSguUseIntervals] = useState<boolean>(true);
const [naturaLayer, setNaturaLayer] = useState<VectorLayer<VectorSource> | null>(null);
const [naturaVisible, setNaturaVisible] = useState<boolean>(false);
const [koliVmsTargetsVisible, setKoliVmsTargetsVisible] = useState<boolean>(false);
const [ropenNearbyPermitsVisible, setRopenNearbyPermitsVisible] = useState<boolean>(false);
const [bergslagenTillAuVisible, setBergslagenTillAuVisible] = useState<boolean>(false);
const [bergslagenTillTeVisible, setBergslagenTillTeVisible] = useState<boolean>(false);
const [stakingPolygonsVisible, setStakingPolygonsVisible] = useState<boolean>(false);
const [stakingPolygonsGeojson, setStakingPolygonsGeojson] = useState<any>(null);
const [dcStakingVisible, setDcStakingVisible] = useState<boolean>(false);
const [dcStakingGeojson, setDcStakingGeojson] = useState<any>(null);
const [dcShowStaking, setDcShowStaking] = useState<boolean>(true);
const [dcShowCompetitor, setDcShowCompetitor] = useState<boolean>(false);
const dcCompetitorLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
const [kiuriDrillholesVisible, setKiuriDrillholesVisible] = useState<boolean>(false);
const [kiuriDistrictHolesVisible, setKiuriDistrictHolesVisible] = useState<boolean>(false);
const [kiuriGrabSamplesVisible, setKiuriGrabSamplesVisible] = useState<boolean>(false);
const [granuddenDraftVisible, setGranuddenDraftVisible] = useState<boolean>(false);
const [kiuriApplicationPolygonVisibility, setKiuriApplicationPolygonVisibility] = useState<Record<KiuriGrantBlockId, boolean>>(
  () => getDefaultKiuriGrantBlockVisibility(false)
);
const kiuriApplicationPolygonsVisible = useMemo(
  () => KIURI_GRANT_BLOCKS.some((block) => kiuriApplicationPolygonVisibility[block.id]),
  [kiuriApplicationPolygonVisibility]
);
const setKiuriApplicationPolygonsVisible = useCallback((visible: boolean) => {
  setKiuriApplicationPolygonVisibility(getDefaultKiuriGrantBlockVisibility(visible));
}, []);
const setKiuriApplicationPolygonBlockVisible = useCallback((blockId: KiuriGrantBlockId, visible: boolean) => {
  setKiuriApplicationPolygonVisibility((previous) => ({ ...previous, [blockId]: visible }));
}, []);

// Borehole search state
const [boreholeSearchResults, setBoreholeSearchResults] = useState<any[]>([]);
const [boreholeSearchLoading, setBoreholeSearchLoading] = useState(false);
const [boreholeSearchInput, setBoreholeSearchInput] = useState('');
const [actionSearchInput, setActionSearchInput] = useState('');
const [actionSearchRemoteBoreholes, setActionSearchRemoteBoreholes] = useState<any[]>([]);
const [actionSearchLoading, setActionSearchLoading] = useState(false);
const [actionSearchRankedTargets, setActionSearchRankedTargets] = useState<RankedTargetSearchRecord[]>([]);
const [actionSearchRankedTargetsLoading, setActionSearchRankedTargetsLoading] = useState(false);
const [boreholeIdLabelsVisible, setBoreholeIdLabelsVisible] = useState(false);
const boreholeIdLabelsVisibleRef = useRef(false);
const boreholeHighlightLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
const boreholeSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
const boreholeSearchIdRef = useRef(0);
const actionSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
const actionSearchIdRef = useRef(0);
const actionSearchRankedTargetsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
const actionSearchRankedTargetsIdRef = useRef(0);
const selectedProjectRef = useRef<any>(selectedProject);
const projectViewportUserInteractedRef = useRef<string | null>(null);
const actionSearchRestoreRef = useRef<ActionSearchRestoreState | null>(null);
const actionSearchFocusUntilRef = useRef<number>(0);
const [actionSearchRestoreAvailable, setActionSearchRestoreAvailable] = useState(false);

useEffect(() => {
  selectedProjectRef.current = selectedProject;
}, [selectedProject]);

// Finland (GTK) layer visibility states
const [finlandMineralDepositsVisible, setFinlandMineralDepositsVisible] = useState<boolean>(false);
const [finlandExplorationPermitStatusVisible, setFinlandExplorationPermitStatusVisible] = useState<Record<FinlandExplorationPermitStatusKey, boolean>>({
  active: false,
  application: false,
  karenssi: false,
});
const finlandExplorationPermitsVisible = useMemo(
  () => FINLAND_EXPLORATION_PERMIT_STATUS_ORDER.some((statusKey) => finlandExplorationPermitStatusVisible[statusKey]),
  [finlandExplorationPermitStatusVisible]
);
const setFinlandExplorationPermitsVisible = useCallback((visible: boolean) => {
  setFinlandExplorationPermitStatusVisible({
    active: visible,
    application: visible,
    karenssi: visible,
  });
}, []);
const setFinlandExplorationPermitStatusKeyVisible = useCallback((statusKey: FinlandExplorationPermitStatusKey, visible: boolean) => {
  setFinlandExplorationPermitStatusVisible((previous) => ({ ...previous, [statusKey]: visible }));
}, []);
const [finlandReservationsVisible, setFinlandReservationsVisible] = useState<boolean>(false);
const [finlandMiningAreasVisible, setFinlandMiningAreasVisible] = useState<boolean>(false);
const [finlandNaturaVisible, setFinlandNaturaVisible] = useState<boolean>(false);
const [finlandTillSamplesVisible, setFinlandTillSamplesVisible] = useState<boolean>(false);
const [finlandTillGoldVisible, setFinlandTillGoldVisible] = useState<boolean>(false);
const [finlandTillGoldLevels, setFinlandTillGoldLevels] = useState<{low: boolean, p90: boolean, p99: boolean}>({ low: true, p90: true, p99: true });
const [finlandTillBasemetalsVisible, setFinlandTillBasemetalsVisible] = useState<boolean>(false);
const [finlandGeochemElement, setFinlandGeochemElement] = useState<string>('Au');
// Finland Geophysics WMS layers
const [finlandMagneticVisible, setFinlandMagneticVisible] = useState<boolean>(false);
const [finlandEMResistivityVisible, setFinlandEMResistivityVisible] = useState<boolean>(false);
const [finlandRadiometricTernaryVisible, setFinlandRadiometricTernaryVisible] = useState<boolean>(false);
// Finland additional geochemistry layers
const [finlandTargetingTillVisible, setFinlandTargetingTillVisible] = useState<boolean>(false);
const [finlandStreamSedimentsVisible, setFinlandStreamSedimentsVisible] = useState<boolean>(false);
// Finland target layers (open ground anomalies)
const [finlandGoldTargetsVisible, setFinlandGoldTargetsVisible] = useState<boolean>(false);
const [finlandGoldTargetLevels, setFinlandGoldTargetLevels] = useState<{low: boolean, mid: boolean, high: boolean}>({ low: true, mid: true, high: true });
const [finlandCopperTargetsVisible, setFinlandCopperTargetsVisible] = useState<boolean>(false);
const [finlandCopperTargetLevels, setFinlandCopperTargetLevels] = useState<{low: boolean, mid: boolean, high: boolean}>({ low: true, mid: true, high: true });
const [finlandStakingPrioritiesVisible, setFinlandStakingPrioritiesVisible] = useState<boolean>(false);
const [finlandPGETargetsVisible, setFinlandPGETargetsVisible] = useState<boolean>(false);
const [finlandKoitelainenStakingVisible, setFinlandKoitelainenStakingVisible] = useState<boolean>(false);
const [finlandPerakorpiTiStakingVisible, setFinlandPerakorpiTiStakingVisible] = useState<boolean>(false);
const [finlandMakaraStakingVisible, setFinlandMakaraStakingVisible] = useState<boolean>(false);
const [finlandKoivuniemiGraphiteVisible, setFinlandKoivuniemiGraphiteVisible] = useState<boolean>(false);
const [finlandKoivuniemiConsultantOriginalVisible, setFinlandKoivuniemiConsultantOriginalVisible] = useState<boolean>(false);
const [finlandKoivuniemiConsultantModVisible, setFinlandKoivuniemiConsultantModVisible] = useState<boolean>(false);
const [finlandKoivuniemiRegisteredVisible, setFinlandKoivuniemiRegisteredVisible] = useState<boolean>(false);
const [finlandKoivuniemiDrillCollarsVisible, setFinlandKoivuniemiDrillCollarsVisible] = useState<boolean>(false);
const [finlandKokkoharjuReviewVisible, setFinlandKokkoharjuReviewVisible] = useState<boolean>(false);
const [finlandKultanummiGoldVisible, setFinlandKultanummiGoldVisible] = useState<boolean>(false);
// ============ NEWFOUNDLAND & LABRADOR — hand-coded point / line / raster layers (all ON by default) ============
const [nlModsOccurrencesVisible, setNlModsOccurrencesVisible] = useState<boolean>(true);
const [nlDrillCollarsVisible, setNlDrillCollarsVisible] = useState<boolean>(true);
const [nlOpenDrillCollarsVisible, setNlOpenDrillCollarsVisible] = useState<boolean>(true);
const [nlOpenOccurrencesVisible, setNlOpenOccurrencesVisible] = useState<boolean>(true);
const [nlOpenGeochemAnomaliesVisible, setNlOpenGeochemAnomaliesVisible] = useState<boolean>(true);
const [nlFaultsVisible, setNlFaultsVisible] = useState<boolean>(true);
const [nlMagneticsNflVisible, setNlMagneticsNflVisible] = useState<boolean>(true);
const [nlMagneticsLabradorVisible, setNlMagneticsLabradorVisible] = useState<boolean>(true);
	const projectGeoJsonStorageKey = storageProjectKey(selectedProject?.id);
	const [projectGeoJsonVisibility, setProjectGeoJsonVisibility] = useState<Record<string, boolean>>(() => ({
	  ...getProjectGeoJsonDefaultVisibility(selectedProject?.id),
	  ...readBooleanRecord(`${PROJECT_GEOJSON_VISIBILITY_PREFIX}${storageProjectKey(selectedProject?.id)}`)
	}));
	const [projectGeoJsonExpanded, setProjectGeoJsonExpanded] = useState<boolean>(() =>
	  readStoredBoolean(`${PROJECT_GEOJSON_EXPANDED_PREFIX}${storageProjectKey(selectedProject?.id)}`, false)
	);
	const [projectGeoJsonPolygonOnly, setProjectGeoJsonPolygonOnly] = useState<boolean>(() =>
	  readStoredBoolean(`${PROJECT_GEOJSON_POLYGON_ONLY_PREFIX}${storageProjectKey(selectedProject?.id)}`, false)
	);
	const skipProjectGeoJsonVisibilityPersistRef = useRef(true);
	const skipProjectGeoJsonUiPersistRef = useRef(true);
	const projectGeoJsonPolygonOnlyRef = useRef(false);
	const projectGeoJsonLayersForProject = useMemo(
	  () => getProjectGeoJsonPolygonLayers(selectedProject?.id),
	  [selectedProject?.id]
	);
	const projectGeoJsonDisplayColorById = useMemo(() => {
	  const layersByTargetArea = new globalThis.Map<string, string[]>();
	  projectGeoJsonLayersForProject.forEach((layerDef) => {
	    const targetArea = layerDef.targetArea?.trim();
	    if (!targetArea) {
	      return;
	    }
	    layersByTargetArea.set(targetArea, [...(layersByTargetArea.get(targetArea) || []), layerDef.id]);
	  });

	  return projectGeoJsonLayersForProject.reduce<Record<string, string>>((colors, layerDef) => {
	    const targetArea = layerDef.targetArea?.trim();
	    const siblingIds = targetArea ? layersByTargetArea.get(targetArea) || [] : [];
	    if (layerDef.lockColor) {
	      colors[layerDef.id] = layerDef.color;
	    } else if (siblingIds.length > 1) {
	      const siblingIndex = Math.max(0, siblingIds.indexOf(layerDef.id));
	      colors[layerDef.id] = TARGET_AREA_DISTINCT_LAYER_COLORS[siblingIndex % TARGET_AREA_DISTINCT_LAYER_COLORS.length];
	    } else {
	      colors[layerDef.id] = layerDef.color;
	    }
	    return colors;
	  }, {});
	}, [projectGeoJsonLayersForProject]);
	const availableProjectById = useMemo(() => {
	  const projectsById = new globalThis.Map<string, MapSearchProject>();
	  availableProjects.forEach((project) => {
	    if (project?.id) {
	      projectsById.set(project.id, project);
	    }
	  });
	  if (selectedProject?.id) {
	    projectsById.set(selectedProject.id, selectedProject);
	  }
	  return projectsById;
	}, [availableProjects, selectedProject]);
	useEffect(() => {
	  skipProjectGeoJsonVisibilityPersistRef.current = true;
	  skipProjectGeoJsonUiPersistRef.current = true;
	  setProjectGeoJsonVisibility({
	    ...getProjectGeoJsonDefaultVisibility(selectedProject?.id),
	    ...readBooleanRecord(`${PROJECT_GEOJSON_VISIBILITY_PREFIX}${projectGeoJsonStorageKey}`)
	  });
	  setProjectGeoJsonExpanded(readStoredBoolean(`${PROJECT_GEOJSON_EXPANDED_PREFIX}${projectGeoJsonStorageKey}`, false));
	  setProjectGeoJsonPolygonOnly(readStoredBoolean(`${PROJECT_GEOJSON_POLYGON_ONLY_PREFIX}${projectGeoJsonStorageKey}`, false));
	}, [projectGeoJsonStorageKey, selectedProject?.id]);

	useEffect(() => {
	  if (skipProjectGeoJsonVisibilityPersistRef.current) {
	    skipProjectGeoJsonVisibilityPersistRef.current = false;
	    return;
	  }
	  writeJsonStorage(`${PROJECT_GEOJSON_VISIBILITY_PREFIX}${projectGeoJsonStorageKey}`, projectGeoJsonVisibility);
	}, [projectGeoJsonStorageKey, projectGeoJsonVisibility]);

	useEffect(() => {
	  if (skipProjectGeoJsonUiPersistRef.current) {
	    skipProjectGeoJsonUiPersistRef.current = false;
	    return;
	  }
	  writeStoredBoolean(`${PROJECT_GEOJSON_EXPANDED_PREFIX}${projectGeoJsonStorageKey}`, projectGeoJsonExpanded);
	  writeStoredBoolean(`${PROJECT_GEOJSON_POLYGON_ONLY_PREFIX}${projectGeoJsonStorageKey}`, projectGeoJsonPolygonOnly);
	}, [projectGeoJsonStorageKey, projectGeoJsonExpanded, projectGeoJsonPolygonOnly]);
	const [finlandMakaraTillAuVisible, setFinlandMakaraTillAuVisible] = useState<boolean>(false);
const [finlandDrillholesVisible, setFinlandDrillholesVisible] = useState<boolean>(false);
const [finlandDrillClustersVisible, setFinlandDrillClustersVisible] = useState<boolean>(false);
const [swedenDrillClustersVisible, setSwedenDrillClustersVisible] = useState<boolean>(false);
const [swedenSingletonsVisible, setSwedenSingletonsVisible] = useState<boolean>(false);
const [norwayDrillClustersVisible, setNorwayDrillClustersVisible] = useState<boolean>(false);
const [irelandDrillClustersVisible, setIrelandDrillClustersVisible] = useState<boolean>(false);
const [peruDrillClustersVisible, setPeruDrillClustersVisible] = useState<boolean>(false);
const [ontarioDrillClustersVisible, setOntarioDrillClustersVisible] = useState<boolean>(false);
const [australiaDrillClustersVisible, setAustraliaDrillClustersVisible] = useState<boolean>(false);
const [quebecDrillClustersVisible, setQuebecDrillClustersVisible] = useState<boolean>(false);
const [franceDrillClustersVisible, setFranceDrillClustersVisible] = useState<boolean>(false);
const [colombiaDrillClustersVisible, setColombiaDrillClustersVisible] = useState<boolean>(false);
const [finlandRockSamplesVisible, setFinlandRockSamplesVisible] = useState<boolean>(false);
  // Finland cross-reference layers (Feb 2026)
  const [finlandFoddDepositsVisible, setFinlandFoddDepositsVisible] = useState<boolean>(false);
  const [finlandFoddSwedenVisible, setFinlandFoddSwedenVisible] = useState<boolean>(false);
  const [finlandFoddMetallogenicVisible, setFinlandFoddMetallogenicVisible] = useState<boolean>(false);
  const [finlandEgdiCrmVisible, setFinlandEgdiCrmVisible] = useState<boolean>(false);
  const [finlandAsterAlterationVisible, setFinlandAsterAlterationVisible] = useState<boolean>(false);
  const [finlandOreBoulderVisible, setFinlandOreBoulderVisible] = useState<boolean>(false);
  const [finlandExpiredClaimsVisible, setFinlandExpiredClaimsVisible] = useState<boolean>(false);

  // KoBold competitor claims (Finland + Sweden)
  const [koboldClaimsVisible, setKoboldClaimsVisible] = useState<boolean>(false);

  const sguPrefKey = useMemo(() => SGU_PREF_STORAGE_KEY(selectedProject?.id), [selectedProject?.id]);
  const [geochemSummaries, setGeochemSummaries] = useState<Record<string, any>>({});
  const [geochemBackground, setGeochemBackground] = useState<Record<string, any>>({});
  const [geochemTopSamples, setGeochemTopSamples] = useState<any[]>([]);
  const [showTopAnomalies, setShowTopAnomalies] = useState<boolean>(false);
  const [geochemNearSamples, setGeochemNearSamples] = useState<any[]>([]);
  const [showNearAnomalies, setShowNearAnomalies] = useState<boolean>(false);
  const [geochemLoading, setGeochemLoading] = useState<boolean>(false);
  const geochemMarkerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const geochemNearLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const scoreLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const [scores, setScores] = useState<any[]>([]);
  const [scoring, setScoring] = useState<boolean>(false);
  const SCORE_CACHE_KEY = useMemo(() => (selectedProject?.id ? `sgu-score-cache-${selectedProject.id}` : null), [selectedProject?.id]);
  const persistedScoresRef = useRef<any[]>([]);

  // SGU preference persistence (open ground, intervals, layer toggles)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(sguPrefKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.useIntervals === 'boolean') {
          setSguUseIntervals(parsed.useIntervals);
        }
        if (parsed?.layerVisibility && typeof parsed.layerVisibility === 'object') {
          setSguLayerVisibility(parsed.layerVisibility);
        }
        if (typeof parsed?.naturaVisible === 'boolean') {
          setNaturaVisible(parsed.naturaVisible);
        }
      }
    } catch (err) {
      console.warn('SGU: failed to load persisted prefs', err);
    }
  }, [sguPrefKey]);

  useEffect(() => {
    try {
        const payload = {
          useIntervals: sguUseIntervals,
          layerVisibility: sguLayerVisibility,
          naturaVisible,
        };
        localStorage.setItem(sguPrefKey, JSON.stringify(payload));
      } catch (err) {
        console.warn('SGU: failed to persist prefs', err);
      }
    }, [sguPrefKey, sguUseIntervals, sguLayerVisibility, naturaVisible]);

  const drawGeochemMarkers = useCallback(
    (
      samples: any[],
      layerRef: React.MutableRefObject<VectorLayer<VectorSource> | null>,
      opts?: { zIndex?: number; radius?: number; opacity?: number }
    ) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
      if (!samples || samples.length === 0) {
        layerRef.current = null;
        return;
      }
      const features = samples.map((s) => {
        const feat = new Feature({
          geometry: new Point(fromLonLat([s.lon, s.lat])),
          element: s.element,
          value: s.value,
        });
        return feat;
      });
      const colorMap: Record<string, string> = {
        Cu: '#ff9800',
        Au: '#fdd835',
        Ni: '#26a69a',
        Zn: '#7e57c2',
        Co: '#00bcd4',
      };
      const layer = new VectorLayer({
        source: new VectorSource({ features }),
        zIndex: opts?.zIndex ?? 90,
        style: (feature) => {
          const elem = feature.get('element') || 'X';
          const baseColor = colorMap[elem] || '#00d2ff';
          const radius = opts?.radius ?? 5;
          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color: baseColor }),
              stroke: new Stroke({ color: '#000', width: 1 })
            }),
            text: new Text({
              text: `${elem}`,
              font: 'bold 10px monospace',
              fill: new Fill({ color: '#000' }),
              backgroundFill: new Fill({ color: '#fff' }),
              padding: [1, 2, 1, 2],
              offsetY: -12,
            })
          });
        }
      });
      map.addLayer(layer);
      layerRef.current = layer;
    },
    []
  );

  const toggleNaturaLayer = useCallback(
    (visible: boolean) => {
      setNaturaVisible(visible);
      if (naturaLayer) {
        naturaLayer.setVisible(visible);
      }
    },
    [naturaLayer]
  );

  const toggleClusterLabels = useCallback(
    (visible: boolean) => {
      setClusterLabelsVisible(visible);
      if (boreholeClusterLabelsLayerRef.current) {
        boreholeClusterLabelsLayerRef.current.setVisible(visible);
      }
    },
    []
  );

  const loadSwedenDrillClusterReviewFeatures = useCallback(async (): Promise<Feature[]> => {
    if (swedenDrillClusterReviewFeaturesRef.current.length > 0) {
      return swedenDrillClusterReviewFeaturesRef.current;
    }

    if (!swedenDrillClusterReviewFetchRef.current) {
      swedenDrillClusterReviewFetchRef.current = apiFetch('/api/drill-clusters/geojson?country=sweden')
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Sweden drill clusters fetch failed (${response.status})`);
          }
          return response.json();
        })
        .then((data) => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          }) as Feature[];
          swedenDrillClusterReviewFeaturesRef.current = features;
          return features;
        })
        .catch((error) => {
          console.warn('Failed to load Sweden drill-cluster review colors', error);
          swedenDrillClusterReviewFetchRef.current = null;
          return [];
        });
    }

    return swedenDrillClusterReviewFetchRef.current;
  }, []);


  const drawScoreCells = useCallback((cells: any[]) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (scoreLayerRef.current) {
      map.removeLayer(scoreLayerRef.current);
    }
    if (!cells || cells.length === 0) {
      scoreLayerRef.current = null;
      return;
    }
    persistedScoresRef.current = cells;
    const features = cells.map((c) => {
      const [lon, lat] = c.center;
      return new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        score: c.score,
        occ: c.occ_count,
        bh: c.borehole_count,
        interval_bonus: c.interval_bonus,
        sgu_intervals: c.sgu_intervals || [],
        center_lon: lon,
        center_lat: lat,
      });
    });
    const layer = new VectorLayer({
      source: new VectorSource({ features }),
      properties: { name: 'sgu-score-cells' },
      zIndex: 87,
      style: (feature) => {
        const score = feature.get('score') || 0;
        const color = score > 0 ? '#66bb6a' : '#ef5350';
        return new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#000', width: 1 })
          }),
          text: new Text({
            text: `${score.toFixed(1)}`,
            font: 'bold 10px monospace',
            fill: new Fill({ color: '#fff' }),
            backgroundFill: new Fill({ color: 'rgba(0,0,0,0.6)' }),
            padding: [1, 2, 1, 2],
            offsetY: -12,
          })
        });
      }
    });
    map.addLayer(layer);
    scoreLayerRef.current = layer;
  }, []);
  const [insightsTab, setInsightsTab] = useState<InsightTabKey>('heatmap');
  const [drillCollars, setDrillCollars] = useState<DrillCollarSummary[]>([]);
  const [drillCollarsLoading, setDrillCollarsLoading] = useState<boolean>(false);
  const [drillCollarError, setDrillCollarError] = useState<string | null>(null);
  const drillIntervalCacheRef = useRef<
    globalThis.Map<string, { intervals: DrillIntervalSummary[]; stats: DrillIntervalStats }>
  >(new globalThis.Map());
  const [activeDrillHoleId, setActiveDrillHoleId] = useState<string | null>(null);
  const [expandedDrillHoleId, setExpandedDrillHoleId] = useState<string | null>(null);
  const [activeDrillIntervals, setActiveDrillIntervals] = useState<DrillIntervalSummary[]>([]);
  const [activeDrillIntervalStats, setActiveDrillIntervalStats] = useState<DrillIntervalStats | null>(null);
  const [drillIntervalsLoading, setDrillIntervalsLoading] = useState<boolean>(false);
  const [drillIntervalsError, setDrillIntervalsError] = useState<string | null>(null);
  const [surfaceAssays, setSurfaceAssays] = useState<SurfaceAssaySummary[]>([]);
  const [surfaceAssaysLoading, setSurfaceAssaysLoading] = useState<boolean>(false);
  const [surfaceAssaysError, setSurfaceAssaysError] = useState<string | null>(null);
  const [expandedSurfaceSampleId, setExpandedSurfaceSampleId] = useState<string | null>(null);
  const [highlightPoint, setHighlightPoint] = useState<HighlightPoint | null>(null);
  const [activeScoreCell, setActiveScoreCell] = useState<any | null>(null);
  const getCurrentMapBBox = useCallback((): [number, number, number, number] | null => {
    const map = mapInstanceRef.current;
    if (!map) {
      return null;
    }
    const size = map.getSize();
    if (!size) {
      return null;
    }
    const extent = map.getView().calculateExtent(size);
    const min = transform([extent[0], extent[1]], 'EPSG:3857', 'EPSG:4326');
    const max = transform([extent[2], extent[3]], 'EPSG:3857', 'EPSG:4326');
    return [min[0], min[1], max[0], max[1]];
  }, []);

  // Portal element detection for legend panel
  const [legendPortalElement, setLegendPortalElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!legendPortalContainerId) { setLegendPortalElement(null); return; }
    const update = () => {
      const el = document.getElementById(legendPortalContainerId);
      setLegendPortalElement(prev => el !== prev ? el : prev);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [legendPortalContainerId]);

  const [inspectionPortalElement, setInspectionPortalElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!inspectionPortalContainerId) { setInspectionPortalElement(null); return; }
    const update = () => {
      const el = document.getElementById(inspectionPortalContainerId);
      setInspectionPortalElement(prev => el !== prev ? el : prev);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [inspectionPortalContainerId]);

  const toggleSguLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setSguLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) {
        return;
      }
      const existing = sguVectorLayersRef.current.get(layerId);

      // Regular cluster labels are controlled by the Deposit labels toggle.
      // Borehole review status is carried by point color instead of boxed map callouts.
      if ((layerId === 'boreholes' || layerId === 'brap_cu_results') && visible) {
        if (!boreholeClusterLabelsLayerRef.current) {
          const regularClusters = BOREHOLE_CLUSTER_LABELS.filter((c) => !c.status);
          const labelFeatures = regularClusters.map((cluster) => {
            const feature = new Feature({
              geometry: new Point(fromLonLat([cluster.lon, cluster.lat])),
              name: cluster.name,
              holes: cluster.holes,
              commodity: cluster.commodity,
              highlight: cluster.highlight
            });
            return feature;
          });
          const labelLayer = new VectorLayer({
            source: new VectorSource({ features: labelFeatures }),
            properties: { name: 'borehole-cluster-labels' },
            visible: clusterLabelsVisible,
            zIndex: 80,
            style: (feature) => {
              const name = feature.get('name') || '';
              const holes = feature.get('holes') || 0;
              const commodity = feature.get('commodity') || '';
              const highlight = feature.get('highlight') || '';
              const isHighlight = name === 'KIURI' || name === 'LAISVALL' || name === 'AUTJAK' || name === 'NJUGGTRÄSKLIDEN' || highlight;

              let textColor = '#ffffff';
              let borderColor = '#00d2ff';
              let fontSize = '11px';
              let fontWeight = '';
              if (isHighlight) {
                textColor = '#ffeb3b';
                borderColor = '#ffeb3b';
                fontSize = 'bold 12px';
                fontWeight = 'bold ';
              }

              const labelText = `${name}\n${holes} holes\n${commodity}${highlight ? '\n' + highlight : ''}`;

              return new Style({
                text: new Text({
                  text: labelText,
                  font: `${fontWeight}${fontSize} sans-serif`,
                  textAlign: 'center',
                  offsetY: -25,
                  fill: new Fill({ color: textColor }),
                  stroke: new Stroke({ color: '#000000', width: 3 }),
                  backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
                  backgroundStroke: new Stroke({ color: borderColor, width: 2 }),
                  padding: [4, 6, 4, 6]
                })
              });
            }
          });
          map.addLayer(labelLayer);
          boreholeClusterLabelsLayerRef.current = labelLayer;
        }
      }

      if (!visible) {
        if (existing) {
          existing.setVisible(false);
        }
        return;
      }

      if (existing) {
        existing.setVisible(true);
        return;
      }

      if (sguFetchInFlightRef.current.get(layerId)) {
        return;
      }
      const bbox = getCurrentMapBBox();
      if (!bbox) {
        return;
      }

      sguFetchInFlightRef.current.set(layerId, true);
      try {
        // Use special endpoint for BRAP Cu results; static file for trench overlay
        const url = layerId === 'brap_cu_results'
          ? `${API_BASE}/api/sgu/brap-cu-results?min_cu_pct=0.5&bbox=${bbox.join(',')}`
          : layerId === 'njugg_trench_overlay'
          ? '/analysis/njuggtraskliden/trench_drillhole_overlay.geojson'
          : `${API_BASE}/api/sgu/layers/${layerId}/features?bbox=${bbox.join(',')}`;

        const resp = await fetchApiOrAsset(url);
        if (!resp.ok) {
          throw new Error(`SGU fetch failed (${resp.status})`);
        }
        const payload = await resp.json();
        const geojson = payload?.geojson || payload;
        const features = new GeoJSON().readFeatures(geojson, { featureProjection: 'EPSG:3857' });
        const layerDefinition = SGU_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDefinition?.color || '#00d2ff';

        if (layerId === 'boreholes') {
          const reviewFeatures = await loadSwedenDrillClusterReviewFeatures();
          features.forEach((feature) => {
            const visual = getNearestDrillClusterReviewVisual(feature, reviewFeatures) || BOREHOLE_DEFAULT_REVIEW_VISUAL;
            feature.set('clusterReviewColor', visual.color, true);
            feature.set('clusterReviewStrokeColor', visual.strokeColor, true);
            feature.set('clusterReviewLabel', visual.label, true);
          });
        }

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        // For gravity contours, compute local min/max for relative coloring
        let gravityMin = 0, gravityMax = 0;
        if (layerId === 'gravity_bouguer_contours') {
          const values = features
            .map(f => f.get('bougueranomali'))
            .filter((v): v is number => typeof v === 'number');
          if (values.length > 0) {
            gravityMin = Math.min(...values);
            gravityMax = Math.max(...values);
            devLog(`Gravity contours: local range ${gravityMin} to ${gravityMax} mGal`);
          }
        }

        const rawSource = new VectorSource({ features });
        const shouldClusterPoints =
          SGU_CLUSTERED_POINT_LAYER_IDS.has(layerId) &&
          features.length >= 300 &&
          features.every((feature) => {
            const type = feature.getGeometry()?.getType?.();
            return type === 'Point' || type === 'MultiPoint';
          });
        const clusterSource = shouldClusterPoints
          ? new Cluster({
              distance: getHeavyPointClusterDistance(currentZoomLevelRef.current),
              geometryFunction: (feature) => {
                const geometry = feature.getGeometry();
                if (geometry instanceof Point) {
                  return geometry;
                }
                if (geometry?.getType?.() === 'MultiPoint') {
                  const coordinates = (geometry as any).getCoordinates?.();
                  return Array.isArray(coordinates) && coordinates[0] ? new Point(coordinates[0]) : null;
                }
                return null;
              },
              minDistance: 8,
              source: rawSource,
            })
          : null;
        const layer = new VectorLayer({
          source: clusterSource || rawSource,
          properties: { name: `sgu-${layerId}`, geomorphicLayerLabel: layerDefinition?.label || layerId },
          visible: true,
          zIndex: layerId === 'brap_cu_results' ? 70 : 65  // BRAP Cu above other layers
        });
        layer.set('geomorphicRawSource', rawSource);
        if (clusterSource) {
          layer.set('geomorphicClusterSource', clusterSource);
        }

        layer.setStyle((feature) => {
          const zoom = mapInstanceRef.current?.getView()?.getZoom?.() || currentZoomLevelRef.current;
          const clusterMembers = getClusterMemberFeatures(feature);
          if (clusterMembers.length > 1) {
            return createHeavyPointClusterStyle(clusterMembers, layerId, defaultColor, zoom);
          }
          const renderFeature = clusterMembers.length === 1 ? clusterMembers[0] : feature;
          const geomType = renderFeature.getGeometry()?.getType() || '';

          // Njugg trench overlay: custom styling by feature type
          if (layerId === 'njugg_trench_overlay') {
            const ftype = renderFeature.get('type') || '';
            if (ftype === 'graphite_trench') {
              const cMean = renderFeature.get('c_tot_mean') || 0;
              const trenchId = renderFeature.get('trench_id') || '';
              return [
                new Style({
                  stroke: new Stroke({ color: '#ff6b35', width: 4 })
                }),
                new Style({
                  text: new Text({
                    text: `${trenchId} (${cMean}%C)`,
                    font: 'bold 11px sans-serif',
                    placement: 'line',
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 })
                  })
                })
              ];
            }
            // Drill holes colored by campaign
            const campaign = renderFeature.get('campaign') || '';
            const campaignColors: Record<string, string> = {
              'NJU79': '#4fc3f7', 'NJU80': '#4fc3f7', 'NJU82': '#29b6f6',
              'NJU90': '#66bb6a', 'NJU91': '#66bb6a', 'NJU98': '#66bb6a',
              'NJU07': '#ffa726', 'NJU08': '#ffa726',
            };
            const holeColor = campaignColors[campaign] || '#aaa';
            return [
              new Style({
                image: new CircleStyle({
                  radius: 3,
                  fill: new Fill({ color: holeColor }),
                  stroke: new Stroke({ color: '#000', width: 1 })
                })
              })
            ];
          }

          // For BRAP Cu results, use per-feature color and size from properties
          const featureColor = renderFeature.get('color') || defaultColor;
          const featureSize = renderFeature.get('size') || 4;
          const label = renderFeature.get('label') || '';

          if (geomType.includes('Point')) {
            const pointColor = layerId === 'boreholes'
              ? renderFeature.get('clusterReviewColor') || BOREHOLE_DEFAULT_REVIEW_VISUAL.color
              : featureColor;
            const pointStrokeColor = layerId === 'boreholes'
              ? renderFeature.get('clusterReviewStrokeColor') || BOREHOLE_DEFAULT_REVIEW_VISUAL.strokeColor
              : '#000';
            const styles: Style[] = [
              new Style({
                image: new CircleStyle({
                  radius: layerId === 'brap_cu_results' ? featureSize : 4,
                  fill: new Fill({ color: pointColor }),
                  stroke: new Stroke({ color: pointStrokeColor, width: layerId === 'brap_cu_results' ? 2 : 1.2 })
                })
              })
            ];
            // Add label for BRAP Cu results
            if (layerId === 'brap_cu_results' && label && zoom >= 8.5) {
              styles.push(
                new Style({
                  text: new Text({
                    text: label,
                    font: 'bold 10px sans-serif',
                    offsetY: -14,
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 })
                  })
                })
              );
            }
            // Add small ID labels for boreholes (controlled by separate toggle)
            if (layerId === 'boreholes' && boreholeIdLabelsVisibleRef.current && zoom >= 11.5) {
              const holeId = renderFeature.get('drillhole') || renderFeature.get('idcode') || '';
              if (holeId) {
                styles.push(
                  new Style({
                    text: new Text({
                      text: holeId,
                      font: '9px sans-serif',
                      offsetY: -10,
                      fill: new Fill({ color: '#00d2ff' }),
                      stroke: new Stroke({ color: '#000', width: 2 })
                    })
                  })
                );
              }
            }
            return styles;
          }
          if (geomType.includes('Line')) {
            // Special handling for gravity contours - color by Bouguer anomaly value (RELATIVE scale)
            if (layerId === 'gravity_bouguer_contours') {
              const bouguerValue = renderFeature.get('bougueranomali') ?? 0;
              // Use RELATIVE scale based on loaded data range
              // This makes local variations visible even when all values are negative
              const range = gravityMax - gravityMin;
              const normalized = range > 0
                ? Math.max(0, Math.min(1, (bouguerValue - gravityMin) / range))
                : 0.5;

              // Color scale: blue (local low) -> yellow/green (mid) -> red (local high)
              let r: number, g: number, b: number;
              if (normalized < 0.5) {
                // Blue to yellow (low to mid)
                const t = normalized * 2; // 0 to 1
                r = Math.round(30 + t * 225);   // 30 -> 255
                g = Math.round(80 + t * 175);   // 80 -> 255
                b = Math.round(220 - t * 220);  // 220 -> 0
              } else {
                // Yellow to red (mid to high)
                const t = (normalized - 0.5) * 2; // 0 to 1
                r = 255;                          // 255
                g = Math.round(255 - t * 175);    // 255 -> 80
                b = Math.round(0 + t * 30);       // 0 -> 30
              }

              const gravityColor = `rgb(${r}, ${g}, ${b})`;
              return new Style({
                stroke: new Stroke({ color: gravityColor, width: 1.5 })
              });
            }
            return new Style({
              stroke: new Stroke({ color: featureColor, width: 1.2 })
            });
          }
          return new Style({
            stroke: new Stroke({ color: featureColor, width: 1 }),
            fill: new Fill({ color: colorWithOpacity(featureColor, 0.18) })
          });
        });

        map.addLayer(layer);
        sguVectorLayersRef.current.set(layerId, layer);
      } catch (error) {
        console.error('Failed to load SGU layer', layerId, error);
        setSguLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      } finally {
        sguFetchInFlightRef.current.delete(layerId);
      }
    },
    [getCurrentMapBBox, loadSwedenDrillClusterReviewFeatures]
  );

  // Borehole search handler (debounced)
  const searchBoreholes = useCallback((query: string) => {
    if (boreholeSearchTimerRef.current) {
      clearTimeout(boreholeSearchTimerRef.current);
    }
    if (query.length < 2) {
      setBoreholeSearchResults([]);
      setBoreholeSearchLoading(false);
      return;
    }
    setBoreholeSearchLoading(true);
    const reqId = ++boreholeSearchIdRef.current;
    boreholeSearchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await apiFetch(`/api/sgu/boreholes/search?q=${encodeURIComponent(query)}`);
        if (resp.ok && reqId === boreholeSearchIdRef.current) {
          const data = await resp.json();
          setBoreholeSearchResults(data.results || []);
        }
      } catch (e) {
        console.error('Borehole search failed:', e);
      } finally {
        if (reqId === boreholeSearchIdRef.current) {
          setBoreholeSearchLoading(false);
        }
      }
    }, 300);
  }, []);

  useEffect(() => {
    if (actionSearchTimerRef.current) {
      clearTimeout(actionSearchTimerRef.current);
    }
    const query = actionSearchInput.trim();
    if (selectedProject?.id !== 'project-sgu-sweden' || query.length < 2) {
      setActionSearchRemoteBoreholes([]);
      setActionSearchLoading(false);
      return;
    }
    setActionSearchLoading(true);
    const reqId = ++actionSearchIdRef.current;
    actionSearchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await apiFetch(`/api/sgu/boreholes/search?q=${encodeURIComponent(query)}`);
        if (resp.ok && reqId === actionSearchIdRef.current) {
          const data = await resp.json();
          setActionSearchRemoteBoreholes(Array.isArray(data.results) ? data.results.slice(0, 8) : []);
        }
      } catch (error) {
        if (reqId === actionSearchIdRef.current) {
          setActionSearchRemoteBoreholes([]);
        }
      } finally {
        if (reqId === actionSearchIdRef.current) {
          setActionSearchLoading(false);
        }
      }
    }, 220);
    return () => {
      if (actionSearchTimerRef.current) {
        clearTimeout(actionSearchTimerRef.current);
      }
    };
  }, [actionSearchInput, selectedProject?.id]);

  useEffect(() => {
    if (actionSearchRankedTargetsTimerRef.current) {
      clearTimeout(actionSearchRankedTargetsTimerRef.current);
    }
    const query = actionSearchInput.trim();
    if (query.length < 2) {
      setActionSearchRankedTargets([]);
      setActionSearchRankedTargetsLoading(false);
      return;
    }

    const controller = new AbortController();
    const reqId = ++actionSearchRankedTargetsIdRef.current;
    setActionSearchRankedTargetsLoading(true);
    actionSearchRankedTargetsTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          search: query,
          limit: '20',
        });
        const resp = await apiFetch(`/api/ranked-targets?${params.toString()}`, {
          signal: controller.signal,
        });
        if (reqId !== actionSearchRankedTargetsIdRef.current || controller.signal.aborted) {
          return;
        }
        if (!resp.ok) {
          setActionSearchRankedTargets([]);
          return;
        }
        const data = await resp.json();
        setActionSearchRankedTargets(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (reqId === actionSearchRankedTargetsIdRef.current && error?.name !== 'AbortError') {
          setActionSearchRankedTargets([]);
        }
      } finally {
        if (reqId === actionSearchRankedTargetsIdRef.current) {
          setActionSearchRankedTargetsLoading(false);
        }
      }
    }, 220);

    return () => {
      controller.abort();
      if (actionSearchRankedTargetsTimerRef.current) {
        clearTimeout(actionSearchRankedTargetsTimerRef.current);
      }
    };
  }, [actionSearchInput]);

  // Borehole select handler — fly to location and highlight
  const handleBoreholeSelect = useCallback((result: any) => {
    if (!result || result.lon == null || result.lat == null) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    // Auto-enable boreholes layer if not visible
    if (!sguLayerVisibility['boreholes']) {
      toggleSguLayer('boreholes', true);
    }

    // Fly to location
    const coords = fromLonLat([result.lon, result.lat]);
    map.getView().animate({
      center: coords,
      zoom: 15,
      duration: 500,
    });

    // Add/update highlight marker
    if (!boreholeHighlightLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource(),
        properties: { name: 'borehole-highlight' },
        zIndex: 100,
        style: new Style({
          image: new CircleStyle({
            radius: 14,
            fill: new Fill({ color: 'rgba(0, 255, 255, 0.15)' }),
            stroke: new Stroke({ color: '#00ffff', width: 3 }),
          }),
        }),
      });
      map.addLayer(layer);
      boreholeHighlightLayerRef.current = layer;
    }
    const src = boreholeHighlightLayerRef.current.getSource()!;
    src.clear();
    src.addFeature(new Feature({ geometry: new Point(coords) }));
  }, [sguLayerVisibility, toggleSguLayer]);

  // Borehole ID labels toggle — updates ref and forces style refresh
  const toggleBoreholeIdLabels = useCallback((visible: boolean) => {
    setBoreholeIdLabelsVisible(visible);
    boreholeIdLabelsVisibleRef.current = visible;
    const layer = sguVectorLayersRef.current.get('boreholes');
    if (layer) {
      layer.changed(); // Force style re-evaluation
    }
  }, []);

  // Peru/INGEMMET layer toggle handler
  const togglePeruLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setPeruLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) {
        return;
      }
      const existing = peruVectorLayersRef.current.get(layerId);

      if (!visible) {
        if (existing) {
          existing.setVisible(false);
        }
        return;
      }

      if (existing) {
        existing.setVisible(true);
        return;
      }

      if (peruFetchInFlightRef.current.get(layerId)) {
        return;
      }
      const bbox = getCurrentMapBBox();
      if (!bbox) {
        return;
      }

      peruFetchInFlightRef.current.set(layerId, true);
      try {
        // Special handling for expiring permits — fetch from permit monitoring API
        let url: string;
        if (layerId === 'expiring_permits') {
          url = `${API_BASE}/api/permits/expiring?days=90`;
        } else {
          // For country-wide view (zoomed out), request all features as simplified centroids
          const zoom = mapInstanceRef.current?.getView().getZoom() || 6;
          const isZoomedOut = zoom < 9;
          const layerLimit = isZoomedOut ? 70000 : 5000;
          const simplifyParam = isZoomedOut ? '&simplify=true' : '';
          const bboxParam = isZoomedOut ? '' : `bbox=${bbox.join(',')}&`;
          url = `${API_BASE}/api/peru/layers/${layerId}/features?${bboxParam}limit=${layerLimit}${simplifyParam}`;
        }
        const resp = await fetchApiOrAsset(url);
        if (!resp.ok) {
          throw new Error(`Peru fetch failed (${resp.status})`);
        }
        const payload = await resp.json();
        const features = new GeoJSON().readFeatures(payload, { featureProjection: 'EPSG:3857' });
        const layerDef = PERU_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDef?.color || '#00d2ff';

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `peru-${layerId}` },
          visible: true,
          zIndex: 65
        });

        layer.setStyle((feature) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Point')) {
            return new Style({
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({ color: defaultColor }),
                stroke: new Stroke({ color: '#000', width: 1 })
              })
            });
          }
          if (geomType.includes('Line')) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 1.5 })
            });
          }
          return new Style({
            stroke: new Stroke({ color: defaultColor, width: 1 }),
            fill: new Fill({ color: colorWithOpacity(defaultColor, 0.18) })
          });
        });

        map.addLayer(layer);
        peruVectorLayersRef.current.set(layerId, layer);
        devLog(`Peru layer ${layerId} loaded with ${features.length} features`);
      } catch (error) {
        console.error('Failed to load Peru layer', layerId, error);
        setPeruLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      } finally {
        peruFetchInFlightRef.current.delete(layerId);
      }
    },
    [getCurrentMapBBox]
  );

  // Papua New Guinea (MRA) layer toggle handler — mirrors togglePeruLayer, hits /api/png/...
  const togglePngLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setPngLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) {
        return;
      }
      const existing = pngVectorLayersRef.current.get(layerId);

      if (!visible) {
        if (existing) {
          existing.setVisible(false);
        }
        return;
      }

      if (existing) {
        existing.setVisible(true);
        return;
      }

      if (pngFetchInFlightRef.current.get(layerId)) {
        return;
      }
      const bbox = getCurrentMapBBox();
      if (!bbox) {
        return;
      }

      pngFetchInFlightRef.current.set(layerId, true);
      try {
        // For country-wide view (zoomed out), request all features simplified (large open_blocks layer)
        const zoom = mapInstanceRef.current?.getView().getZoom() || 6;
        const isZoomedOut = zoom < 9;
        const layerLimit = isZoomedOut ? 70000 : 5000;
        const simplifyParam = isZoomedOut ? '&simplify=true' : '';
        const bboxParam = isZoomedOut ? '' : `bbox=${bbox.join(',')}&`;
        const url = `${API_BASE}/api/png/layers/${layerId}/features?${bboxParam}limit=${layerLimit}${simplifyParam}`;
        const resp = await fetchApiOrAsset(url);
        if (!resp.ok) {
          throw new Error(`PNG fetch failed (${resp.status})`);
        }
        const payload = await resp.json();
        const features = new GeoJSON().readFeatures(payload, { featureProjection: 'EPSG:3857' });
        const layerDef = PNG_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDef?.color || '#00d2ff';

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        // staking polygons + open-ground occurrences are the actionable targets — draw them ON TOP
        // (above the dense green open_blocks) and bold so they're visible even at country-wide zoom.
        const isStaking = layerId === 'staking_proposed';
        const isTargetPts = layerId === 'open_ground_occurrences';
        const zTop = isStaking ? 95 : isTargetPts ? 90 : 65;

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `png-${layerId}` },
          visible: true,
          zIndex: zTop
        });

        layer.setStyle((feature) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Point')) {
            return new Style({
              image: new CircleStyle({
                radius: isTargetPts ? 6 : 4,
                fill: new Fill({ color: defaultColor }),
                stroke: new Stroke({ color: isTargetPts ? '#ffffff' : '#000', width: isTargetPts ? 2 : 1 })
              })
            });
          }
          if (geomType.includes('Line')) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 1.5 })
            });
          }
          if (isStaking) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 3.5, lineDash: [10, 5] }),
              fill: new Fill({ color: colorWithOpacity(defaultColor, 0.15) })
            });
          }
          return new Style({
            stroke: new Stroke({ color: defaultColor, width: 1 }),
            fill: new Fill({ color: colorWithOpacity(defaultColor, 0.18) })
          });
        });

        map.addLayer(layer);
        pngVectorLayersRef.current.set(layerId, layer);
        devLog(`PNG layer ${layerId} loaded with ${features.length} features`);
      } catch (error) {
        console.error('Failed to load PNG layer', layerId, error);
        setPngLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      } finally {
        pngFetchInFlightRef.current.delete(layerId);
      }
    },
    [getCurrentMapBBox]
  );

  // Yukon (GeoYukon) layer toggle handler — vector layers proxy /api/yukon/...; kind='geophys'
  // entries are ArcGIS dynamic raster overlays pulled straight from GeoYukon (display-only).
  const toggleYukonLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setYukonLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) {
        return;
      }
      const layerDef = YUKON_VECTOR_LAYERS.find((l) => l.id === layerId);

      // --- raster geophysics overlay (TileArcGISRest direct to GeoYukon) ---
      if (layerDef?.kind === 'geophys') {
        const existingGeo = yukonGeophysLayersRef.current.get(layerId);
        if (!visible) {
          if (existingGeo) existingGeo.setVisible(false);
          return;
        }
        if (existingGeo) {
          existingGeo.setVisible(true);
          return;
        }
        const tileLayer = new TileLayer({
          source: new TileArcGISRest({
            url: `${GEOYUKON_ROOT}/${layerDef.arcgisService}/MapServer`,
            params: { LAYERS: `show:${layerDef.arcgisLayer}` },
            crossOrigin: undefined
          }),
          properties: { name: `yukon-geophys-${layerId}` },
          opacity: 0.6,
          visible: true,
          zIndex: 40
        });
        map.addLayer(tileLayer);
        yukonGeophysLayersRef.current.set(layerId, tileLayer);
        return;
      }

      // --- vector layer (live proxy / static curated) ---
      const existing = yukonVectorLayersRef.current.get(layerId);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }
      if (yukonFetchInFlightRef.current.get(layerId)) {
        return;
      }
      const bbox = getCurrentMapBBox();
      if (!bbox) {
        return;
      }

      yukonFetchInFlightRef.current.set(layerId, true);
      try {
        // Yukon's cadastre is huge; the proxy is always bbox-scoped + capped server-side. Pull the
        // current view's features on toggle (zoom into a district for a complete local picture).
        const url = `${API_BASE}/api/yukon/layers/${layerId}/features?bbox=${bbox.join(',')}&limit=6000`;
        const resp = await fetchApiOrAsset(url);
        if (!resp.ok) {
          throw new Error(`Yukon fetch failed (${resp.status})`);
        }
        const payload = await resp.json();
        const features = new GeoJSON().readFeatures(payload, { featureProjection: 'EPSG:3857' });
        const defaultColor = layerDef?.color || '#22c55e';

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `yukon-${layerId}` },
          visible: true,
          zIndex: layerDef?.category === 'constraints' ? 64 : 66
        });

        layer.setStyle((feature) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Point')) {
            return new Style({
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({ color: defaultColor }),
                stroke: new Stroke({ color: '#000', width: 1 })
              })
            });
          }
          if (geomType.includes('Line')) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 1.5 })
            });
          }
          return new Style({
            stroke: new Stroke({ color: defaultColor, width: 1.2 }),
            fill: new Fill({ color: colorWithOpacity(defaultColor, 0.18) })
          });
        });

        map.addLayer(layer);
        yukonVectorLayersRef.current.set(layerId, layer);
        devLog(`Yukon layer ${layerId} loaded with ${features.length} features`);
      } catch (error) {
        console.error('Failed to load Yukon layer', layerId, error);
        setYukonLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      } finally {
        yukonFetchInFlightRef.current.delete(layerId);
      }
    },
    [getCurrentMapBBox]
  );

  // Peru Playbook analysis layer toggle handler (static GeoJSON)
  const PERU_ANALYSIS_LAYER_IDS = useMemo(() => new Set([
    'playbook_targets', 'drill_collars', 'open_ground',
    'spectral_anomalies', 'mining_cadastre_static',
    'stream_sediments_anomalous', 'crm_hotspots',
    'anap_geochemistry', 'litoteca_samples', 'bdg_rock_geochemistry', 'mining_projects',
    'usgs_mrds_deposits', 'usgs_andes_porphyry_cu_peru', 'usgs_global_porphyry_cu_peru',
    'usgs_major_deposits_peru', 'sammd_deposits', 'usgs_mineral_facilities_peru',
    'usgs_exploration_sites_peru', 'usgs_vms_peru', 'usgs_sedcu_peru', 'usgs_sedznpb_peru'
  ]), []);

  const togglePeruAnalysisLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setPeruAnalysisVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) return;

      const existing = peruAnalysisLayersRef.current.get(layerId);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }

      try {
        const resp = await fetch(`/data/peru/${layerId}.geojson`);
        if (!resp.ok) throw new Error(`Peru analysis fetch failed (${resp.status})`);
        const data = await resp.json();
        const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
        const layerDef = PERU_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDef?.color || '#00d2ff';

        const classificationColors: Record<string, string> = {
          HIGH_PRIORITY: '#E63946',
          MEDIUM: '#FFB703',
          LOW: '#ADB5BD',
          CONCEDED: '#555555'
        };

        const elementColors: Record<string, string> = {
          Cu: '#B87333', Au: '#FFD700', Ag: '#C0C0C0', Mo: '#6A5ACD',
          W: '#4682B4', Sb: '#FF6347', As: '#DC143C', Bi: '#8B008B',
          Zn: '#7B8794', Pb: '#4A4A4A', Sn: '#CD853F', Co: '#2196f3',
          Ni: '#808080', Cr: '#228B22', Li: '#00BCD4', La: '#9C27B0',
          Ce: '#E91E63', Y: '#FF9800'
        };

        let style: any;

        if (layerId === 'playbook_targets') {
          style = (feature: any) => {
            const classification = feature.get('classification') || 'LOW';
            const score = feature.get('priority_score') || 0;
            const color = classificationColors[classification] || '#ADB5BD';
            const radius = classification === 'HIGH_PRIORITY' ? 8 : classification === 'MEDIUM' ? 6 : 4;
            const styles: Style[] = [
              new Style({
                image: new CircleStyle({
                  radius,
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: '#000', width: 1 }),
                }),
              }),
            ];
            // Label top 50 targets
            if (score >= (features.length > 50 ? features.sort((a: any, b: any) => (b.get('priority_score') || 0) - (a.get('priority_score') || 0))[49]?.get('priority_score') || 999 : 0)) {
              const tid = feature.get('target_id') || '';
              const dtype = feature.get('deposit_type') || '';
              styles.push(new Style({
                text: new Text({
                  text: `${tid}\n${dtype}`,
                  font: 'bold 10px monospace',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  offsetY: -(radius + 12),
                  textAlign: 'center',
                  overflow: true,
                }),
              }));
            }
            return styles;
          };
        } else if (layerId === 'drill_collars') {
          style = (feature: any) => {
            const bestAu = feature.get('best_au') || 0;
            const radius = bestAu > 1 ? 7 : bestAu > 0 ? 5 : 3;
            return new Style({
              image: new CircleStyle({
                radius,
                fill: new Fill({ color: '#2196f3' }),
                stroke: new Stroke({ color: '#fff', width: 1.5 }),
              }),
            });
          };
        } else if (layerId === 'independent_systems') {
          style = (feature: any) => {
            const featureType = feature.get('type') || '';
            const styles: Style[] = [];

            if (featureType === 'system_centroid') {
              // System centroid styling
              const stakingPriority = feature.get('staking_priority') || 'NOT_RECOMMENDED';
              const priorityColors: Record<string, string> = {
                IMMEDIATE: '#22c55e',
                AFTER_CC: '#eab308',
                NOT_RECOMMENDED: '#ef4444',
              };
              const color = priorityColors[stakingPriority] || '#ef4444';

              styles.push(new Style({
                image: new CircleStyle({
                  radius: 14,
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: '#fff', width: 2 }),
                }),
              }));

              // Add system_id label
              const systemId = feature.get('system_id') || '';
              if (systemId) {
                styles.push(new Style({
                  text: new Text({
                    text: systemId,
                    font: 'bold 11px monospace',
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: 0,
                    textAlign: 'center',
                    overflow: true,
                  }),
                }));
              }
            } else if (featureType === 'member_target') {
              // Member target styling
              const cadastreStatus = feature.get('cadastre_status') || '';
              const systemId = feature.get('system_id') || '';

              // Color based on cadastre status
              let targetColor = '#ef4444'; // default red for conceded
              if (cadastreStatus === 'OPEN') {
                targetColor = '#22c55e'; // green
              } else if (cadastreStatus === 'RECENTLY_FILED') {
                targetColor = '#f97316'; // orange
              }

              // Get system centroid color for border
              // Find the system centroid to get its staking priority color
              // Since we don't have access to all features here, use a default
              // The border will be updated dynamically if needed

              styles.push(new Style({
                image: new CircleStyle({
                  radius: 6,
                  fill: new Fill({ color: targetColor }),
                  stroke: new Stroke({ color: '#fff', width: 1.5 }),
                }),
              }));
            }

            return styles;
          };
        } else if (layerId === 'open_ground') {
          style = new Style({
            stroke: new Stroke({ color: '#4caf50', width: 2 }),
            fill: new Fill({ color: 'rgba(76, 175, 80, 0.15)' }),
          });
        } else if (layerId === 'spectral_anomalies') {
          style = new Style({
            stroke: new Stroke({ color: '#ff9800', width: 1.5 }),
            fill: new Fill({ color: 'rgba(255, 152, 0, 0.12)' }),
          });
        } else if (layerId === 'mining_cadastre_static') {
          style = (feature: any) => {
            const status = feature.get('s') || 'T';
            const isExtinguished = status === 'L' || status === 'X';

            if (isExtinguished) {
              // Extinguished concessions - dashed gray border
              return new Style({
                stroke: new Stroke({
                  color: 'rgba(107, 114, 128, 0.3)',  // light gray with low opacity
                  width: 1,
                  lineDash: [4, 4]  // dashed pattern
                }),
                fill: new Fill({ color: 'rgba(107, 114, 128, 0.05)' }),
              });
            } else {
              // Active concessions - solid blue/purple border
              return new Style({
                stroke: new Stroke({ color: '#8b5cf6', width: 2 }),
                fill: new Fill({ color: 'rgba(139, 92, 246, 0.1)' }),
              });
            }
          };
        } else if (layerId === 'stream_sediments_anomalous') {
          style = (feature: any) => {
            const elem = feature.get('dominant_element') || '';
            const color = elementColors[elem] || '#00bcd4';
            return new Style({
              image: new CircleStyle({
                radius: 2.5,
                fill: new Fill({ color }),
              }),
            });
          };
        } else if (layerId === 'crm_hotspots') {
          style = (feature: any) => {
            const elem = feature.get('crm_element') || '';
            const crmColors: Record<string, string> = {
              cobalt: '#2196f3', lithium: '#00BCD4', nickel: '#808080',
              tin: '#CD853F', tungsten: '#4682B4', hree: '#E91E63', lree: '#9C27B0'
            };
            const color = crmColors[elem] || '#009688';
            return new Style({
              image: new CircleStyle({
                radius: 3,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 0.5 }),
              }),
            });
          };
        } else if (layerId === 'anap_geochemistry') {
          style = (feature: any) => {
            const dominant = feature.get('dominant_element') || '';
            const color = elementColors[dominant] || '#ff6b35';
            return new Style({
              image: new CircleStyle({
                radius: 3.5,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 0.5 }),
              }),
            });
          };
        } else if (layerId === 'bdg_rock_geochemistry') {
          style = (feature: any) => {
            const clasif = feature.get('CLASIF_ROC') || '';
            const rockColors: Record<string, string> = {
              'Ignea': '#e74c3c',
              'Sedimentaria': '#f39c12',
              'Metamorfica': '#3498db',
              'Alterada': '#e67e22',
            };
            const color = rockColors[clasif] || '#e74c3c';
            return new Style({
              image: new CircleStyle({
                radius: 2.5,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 0.3 }),
              }),
            });
          };
        } else if (layerId === 'litoteca_samples') {
          style = (feature: any) => {
            const studyType = feature.get('study_type') || '';
            const studyColors: Record<string, string> = {
              'Petrographic Studies': '#9b59b6',
              'Microscopic Photo-Descriptions': '#8e44ad',
              'Petromineral Studies': '#7d3c98',
              'Spectral Mineral Analysis': '#6c3483',
              'Mineragraphic Studies': '#5b2c6f',
            };
            const color = studyColors[studyType] || '#9b59b6';
            return new Style({
              image: new RegularShape({
                points: 4,
                radius: 5,
                angle: Math.PI / 4,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#fff', width: 1 }),
              }),
            });
          };
        } else if (layerId === 'mining_projects') {
          style = (feature: any) => {
            const estado = feature.get('ESTADO') || '';
            const estadoColors: Record<string, string> = {
              'Operación': '#f1c40f',
              'Exploración': '#e67e22',
              'Proyecto': '#3498db',
            };
            const color = estadoColors[estado] || '#f1c40f';
            return new Style({
              image: new RegularShape({
                points: 5,
                radius: 7,
                radius2: 3,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 1 }),
              }),
            });
          };
        } else if (layerId === 'usgs_mrds_deposits') {
          const devColors: Record<string, string> = {
            Producer: '#22c55e', 'Past Producer': '#6366f1', Prospect: '#f59e0b', Occurrence: '#94a3b8', Plant: '#0ea5e9',
          };
          style = (feature: any) => {
            const devStat = feature.get('dev_stat') || 'Occurrence';
            const color = devColors[devStat] || '#6366f1';
            return new Style({
              image: new CircleStyle({
                radius: devStat === 'Producer' ? 5 : devStat === 'Past Producer' ? 4 : 3,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 0.5 }),
              }),
            });
          };
        } else if (layerId === 'usgs_andes_porphyry_cu_peru' || layerId === 'usgs_global_porphyry_cu_peru') {
          style = (feature: any) => {
            const tonnage = parseFloat(feature.get('Tonnage_Mt') || feature.get('tonnage') || '0');
            const radius = tonnage > 1000 ? 12 : tonnage > 100 ? 9 : tonnage > 10 ? 7 : 5;
            return new Style({
              image: new CircleStyle({
                radius,
                fill: new Fill({ color: layerId.includes('andes') ? '#dc2626' : '#b91c1c' }),
                stroke: new Stroke({ color: '#fff', width: 2 }),
              }),
              text: new Text({
                text: feature.get('Name') || feature.get('depname') || '',
                font: 'bold 9px monospace',
                fill: new Fill({ color: '#fff' }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                offsetY: -(radius + 8),
                overflow: true,
              }),
            });
          };
        } else if (layerId === 'usgs_major_deposits_peru') {
          style = (feature: any) => {
            const commod = (feature.get('commod1') || '').toLowerCase();
            const color = commod.includes('copper') ? '#b87333' : commod.includes('gold') ? '#ffd700' : commod.includes('iron') ? '#e74c3c' : '#f59e0b';
            return new Style({
              image: new RegularShape({
                points: 5,
                radius: 8,
                radius2: 4,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 1 }),
              }),
            });
          };
        } else if (layerId === 'sammd_deposits') {
          style = (feature: any) => {
            const size = parseInt(feature.get('DEP_SIZE') || '0');
            const radius = 4 + size * 2;
            return new Style({
              image: new RegularShape({
                points: 6,
                radius,
                fill: new Fill({ color: '#10b981' }),
                stroke: new Stroke({ color: '#fff', width: 1.5 }),
              }),
            });
          };
        } else if (layerId === 'usgs_mineral_facilities_peru') {
          style = new Style({
            image: new RegularShape({
              points: 4,
              radius: 5,
              fill: new Fill({ color: '#0ea5e9' }),
              stroke: new Stroke({ color: '#000', width: 0.5 }),
            }),
          });
        } else if (layerId === 'usgs_exploration_sites_peru') {
          style = new Style({
            image: new RegularShape({
              points: 3,
              radius: 7,
              fill: new Fill({ color: '#22c55e' }),
              stroke: new Stroke({ color: '#000', width: 1 }),
            }),
          });
        } else {
          style = new Style({
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({ color: defaultColor }),
              stroke: new Stroke({ color: '#000', width: 1 }),
            }),
          });
        }

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `peru-analysis-${layerId}` },
          visible: true,
          zIndex: layerId === 'playbook_targets' ? 80 : layerId === 'independent_systems' ? 79 : layerId === 'drill_collars' ? 75 : layerId === 'mining_cadastre_static' ? 50 : layerId === 'mining_projects' ? 78 : layerId.startsWith('usgs_andes') || layerId.startsWith('usgs_global') ? 77 : layerId === 'usgs_major_deposits_peru' ? 76 : layerId === 'sammd_deposits' ? 76 : 65,
          style,
        });

        map.addLayer(layer);
        peruAnalysisLayersRef.current.set(layerId, layer);
        devLog(`Peru analysis layer ${layerId} loaded with ${features.length} features`);
      } catch (error) {
        console.error('Failed to load Peru analysis layer', layerId, error);
        setPeruAnalysisVisibility((prev) => ({ ...prev, [layerId]: false }));
      }
    },
    []
  );

  // Caujul layer toggle handler
  const toggleCaujulLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setCaujulLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) {
        return;
      }
      const existing = caujulVectorLayersRef.current.get(layerId);

      if (!visible) {
        if (existing) {
          existing.setVisible(false);
        }
        return;
      }

      if (existing) {
        existing.setVisible(true);
        return;
      }

      if (caujulFetchInFlightRef.current.get(layerId)) {
        return;
      }
      const bbox = getCurrentMapBBox();
      if (!bbox) {
        return;
      }

      caujulFetchInFlightRef.current.set(layerId, true);
      try {
        const url = `${API_BASE}/api/caujul/layers/${layerId}/features?bbox=${bbox.join(',')}&limit=5000`;
        const resp = await fetchApiOrAsset(url);
        if (!resp.ok) {
          throw new Error(`Caujul fetch failed (${resp.status})`);
        }
        const payload = await resp.json();
        const features = new GeoJSON().readFeatures(payload, { featureProjection: 'EPSG:3857' });
        const layerDef = CAUJUL_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDef?.color || '#00d2ff';

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `caujul-${layerId}` },
          visible: true,
          zIndex: 65
        });

        layer.setStyle((feature) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Point')) {
            return new Style({
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({ color: defaultColor }),
                stroke: new Stroke({ color: '#000', width: 1 })
              })
            });
          }
          if (geomType.includes('Line')) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 1.5 })
            });
          }
          return new Style({
            stroke: new Stroke({ color: defaultColor, width: 1 }),
            fill: new Fill({ color: colorWithOpacity(defaultColor, 0.18) })
          });
        });

        map.addLayer(layer);
        caujulVectorLayersRef.current.set(layerId, layer);
        devLog(`Caujul layer ${layerId} loaded with ${features.length} features`);
      } catch (error) {
        console.error('Failed to load Caujul layer', layerId, error);
        setCaujulLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      } finally {
        caujulFetchInFlightRef.current.delete(layerId);
      }
    },
    [getCurrentMapBBox]
  );

  // France/BRGM static layer toggle handler
  const toggleFranceLayer = useCallback(
    async (layerId: string, visible: boolean) => {
      setFranceLayerVisibility((prev) => ({ ...prev, [layerId]: visible }));
      const map = mapInstanceRef.current;
      if (!map) return;

      const existing = franceLayersRef.current.get(layerId);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }

      try {
        const resp = await fetch(`/data/france/${layerId}.geojson`);
        if (!resp.ok) throw new Error(`France layer fetch failed (${resp.status})`);
        const data = await resp.json();
        const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
        const layerDef = FRANCE_VECTOR_LAYERS.find((l) => l.id === layerId);
        const defaultColor = layerDef?.color || '#f1c40f';

        const substanceColors: Record<string, string> = {
          Au: '#FFD700', Ag: '#C0C0C0', Cu: '#B87333', Pb: '#4A4A4A', Zn: '#7B8794',
          W: '#4682B4', Sb: '#FF6347', Sn: '#CD853F', U: '#32CD32', Li: '#00BCD4',
          Fe: '#8B4513', Ni: '#808080', Co: '#2196F3', Mo: '#6A5ACD', As: '#DC143C',
        };

        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `france-${layerId}` },
          visible: true,
          zIndex: 65,
        });

        layer.setStyle((feature: FeatureLike) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Polygon')) {
            return new Style({
              stroke: new Stroke({ color: defaultColor, width: 1.5 }),
              fill: new Fill({ color: defaultColor + '20' }),
            });
          }
          // Point features — color by substance if available
          const substance = feature.get('substance') || feature.get('c_substance') || '';
          const color = substanceColors[substance] || defaultColor;
          const radius = layerId.includes('mineral_occurrences') ? 5 : layerId.includes('mines') ? 6 : 4;
          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#000', width: 1 }),
            }),
          });
        });

        map.addLayer(layer);
        franceLayersRef.current.set(layerId, layer);
      } catch (error) {
        console.error('Failed to load France layer', layerId, error);
        setFranceLayerVisibility((prev) => ({ ...prev, [layerId]: false }));
      }
    },
    []
  );

  const toggleJurisdictionLayer = useCallback(
    async (layer: JurisdictionLayerDefinition, visible: boolean) => {
      if (layer.disabledReason || !layer.path) {
        return;
      }

      setJurisdictionLayerVisibility((prev) => ({ ...prev, [layer.id]: visible }));
      const map = mapInstanceRef.current;
      if (!map) return;

      const existing = jurisdictionLayersRef.current.get(layer.id);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }
      if (jurisdictionFetchInFlightRef.current.get(layer.id)) {
        return;
      }

      jurisdictionFetchInFlightRef.current.set(layer.id, true);
      try {
        const resp = await fetch(layer.path);
        if (!resp.ok) throw new Error(`${layer.label} fetch failed (${resp.status})`);
        const data = await resp.json();
        const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
        const color = layer.color;

        const colorWithOpacity = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const vectorLayer = new VectorLayer({
          source: new VectorSource({ features }),
          properties: { name: `jurisdiction-${layer.id}` },
          visible: true,
          zIndex: layer.zIndex ?? 67,
        });

        vectorLayer.setStyle((feature: FeatureLike) => {
          const geomType = feature.getGeometry()?.getType() || '';
          if (geomType.includes('Polygon')) {
            return new Style({
              stroke: new Stroke({ color, width: layer.category.includes('Tenure') ? 1 : 1.5 }),
              fill: new Fill({ color: colorWithOpacity(color, layer.category.includes('Tenure') ? 0.08 : 0.16) }),
            });
          }
          if (geomType.includes('Line')) {
            return new Style({
              stroke: new Stroke({ color, width: 1.5 }),
            });
          }

          const rank = Number(feature.get('stratified_rank') || 0);
          const radius = rank > 0 ? Math.max(5, 10 - rank * 0.4) : layer.category.includes('Reference') ? 4 : 5;
          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#0d1117', width: 1 }),
            }),
            text: rank > 0 ? new Text({
              text: String(rank),
              font: 'bold 10px monospace',
              fill: new Fill({ color: '#fff' }),
              stroke: new Stroke({ color: '#0d1117', width: 3 }),
              offsetY: -(radius + 8),
              overflow: true,
            }) : undefined,
          });
        });

        map.addLayer(vectorLayer);
        jurisdictionLayersRef.current.set(layer.id, vectorLayer);
      } catch (error) {
        console.error('Failed to load jurisdiction layer', layer.id, error);
        setJurisdictionLayerVisibility((prev) => ({ ...prev, [layer.id]: false }));
      } finally {
        jurisdictionFetchInFlightRef.current.delete(layer.id);
      }
    },
    []
  );

  useEffect(() => {
    const activeLayerIds = new Set(JURISDICTION_LAYER_CONFIG[selectedProject?.id || '']?.layers.map((layer) => layer.id) || []);
    jurisdictionLayersRef.current.forEach((layer, layerId) => {
      layer.setVisible(Boolean(jurisdictionLayerVisibility[layerId]) && activeLayerIds.has(layerId));
    });
  }, [selectedProject?.id, jurisdictionLayerVisibility]);

  // Caujul concession boundaries — load when Caujul project is selected
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (caujulConcessionsLayerRef.current) {
        mapInstance?.removeLayer(caujulConcessionsLayerRef.current);
        caujulConcessionsLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-caujul') {
      removeLayer();
      return;
    }
    if (!caujulConcessionsLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/caujul/concessions.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'caujul-concessions' },
        style: (feature) => {
          const name = feature.get('Concesion') || '';
          const estado = feature.get('Estado') || '';
          const isPetitorio = estado === 'P';
          return new Style({
            fill: new Fill({ color: isPetitorio ? 'rgba(255, 170, 0, 0.08)' : 'rgba(0, 200, 255, 0.06)' }),
            stroke: new Stroke({
              color: isPetitorio ? '#ffaa00' : '#00c8ff',
              width: 2,
              lineDash: isPetitorio ? [6, 4] : undefined
            }),
            text: new Text({
              text: name,
              font: '10px monospace',
              fill: new Fill({ color: '#e6edf3' }),
              stroke: new Stroke({ color: '#000', width: 3 }),
              overflow: true
            })
          });
        },
        zIndex: 50
      });
      caujulConcessionsLayerRef.current = layer;
      mapInstance.addLayer(layer);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id]);

  const fetchGeochemSummary = useCallback(
    async (elements: string[] = ['Cu', 'Au', 'Ni', 'Zn', 'Co']) => {
      const bbox = getCurrentMapBBox();
      if (!bbox) return;
      setGeochemLoading(true);
      try {
        const anomalyParam = '&anomaly_quantile=0.95';
        const resp = await apiRequest(
          `/api/sgu/geochem/summary?bbox=${bbox.join(',')}&elements=${elements.join(',')}&top_n=50${anomalyParam}`
        );
        const payload = resp && typeof (resp as any).json === 'function' ? await resp.json() : null;
        setGeochemSummaries((payload as any)?.summaries || {});
        setGeochemBackground((payload as any)?.background || {});
        const tops: any[] = [];
        const near: any[] = [];
        if (payload?.summaries) {
          Object.entries(payload.summaries).forEach(([elem, stats]: any) => {
            if (stats?.anomalies) {
              stats.anomalies.forEach((s: any) => {
                tops.push({ ...s, element: elem });
              });
            }
            if (stats?.near_threshold) {
              stats.near_threshold.forEach((s: any) => {
                near.push({ ...s, element: elem });
              });
            }
          });
        }
        setGeochemTopSamples(tops);
        setGeochemNearSamples(near);
        if (showTopAnomalies) {
          // Force re-render markers
          drawGeochemMarkers(tops, geochemMarkerLayerRef, { zIndex: 92, radius: 5 });
        }
      } catch (error) {
        console.error('Failed to load geochem summary', error);
        setGeochemSummaries({});
        setGeochemBackground({});
        setGeochemTopSamples([]);
        setGeochemNearSamples([]);
      } finally {
        setGeochemLoading(false);
      }
    },
    [getCurrentMapBBox, showTopAnomalies, showNearAnomalies, drawGeochemMarkers]
  );

  const fetchOpenGroundScores = useCallback(async () => {
    const bbox = getCurrentMapBBox();
    if (!bbox) return;
    setScoring(true);
    try {
      const intervalsParam = sguUseIntervals ? '&use_intervals=1' : '&use_intervals=0';
      // Use a coarser grid to improve responsiveness on large bboxes
      const resp = await apiRequest(
        `/api/sgu/score?bbox=${bbox.join(',')}&grid_deg=0.2&radius_km=10&top_n=30&use_structures=1&use_mag=0&use_radio=0${intervalsParam}`
      );
      const payload = resp && typeof (resp as any).json === 'function' ? await resp.json() : null;
      const cells = (payload as any)?.cells || [];
      setScores(cells);
      drawScoreCells(cells);
      // Persist scores locally for this project
      if (SCORE_CACHE_KEY) {
        try {
          localStorage.setItem(
            SCORE_CACHE_KEY,
            JSON.stringify({ bbox, cells, params: payload?.params || {} })
          );
        } catch (err) {
          console.error('Failed to cache SGU scores', err);
        }
      }
      // Push synthetic report entry to Reports tab using offline report if present
      if (onAnalysisComplete) {
        let offlineReport = '/analysis_outputs/sgu_score_reports/sgu_score_report_offline_latest.csv';
        try {
          const r = await apiRequest('/api/sgu/report/offline');
          const rp = r && typeof (r as any).json === 'function' ? await r.json() : null;
          if (rp?.report_path) {
            offlineReport = `${API_BASE}${rp.report_path.startsWith('/') ? '' : '/'}${rp.report_path}`;
          }
        } catch (err) {
          console.error('Failed to generate offline report; falling back to latest pointer', err);
        }
        const now = new Date();
        const nowId = `sgu-score-${now.getTime()}`;
        const runLabel = now.toISOString().replace('T', ' ').split('.')[0] + ' UTC';
        const result = {
          id: nowId,
          analysis_id: nowId,
          project_id: selectedProject?.id || null,
          project_name: selectedProject?.name || null,
          name: `SGU score ${runLabel}`,
          analysis_type: 'sgu_score',
          results: {
            report_path: offlineReport,
            params: payload?.params || { grid_deg: 0.2, radius_km: 10 },
            counts: payload?.counts || {},
          },
          created_at: new Date().toISOString(),
        };
        try {
          onAnalysisComplete(result);
        } catch (err) {
          console.error('Failed to push SGU score result to Reports tab', err);
        }
      }
    } catch (error) {
      console.error('Failed to score open ground', error);
      setScores([]);
      drawScoreCells([]);
    } finally {
      setScoring(false);
    }
  }, [getCurrentMapBBox, drawScoreCells, sguUseIntervals, onAnalysisComplete, selectedProject?.id, selectedProject?.name]);

  useEffect(() => {
    return () => {
      const map = mapInstanceRef.current;
      if (map) {
        sguVectorLayersRef.current.forEach((layer) => map.removeLayer(layer));
        if (boreholeClusterLabelsLayerRef.current) {
          map.removeLayer(boreholeClusterLabelsLayerRef.current);
        }
        if (geochemMarkerLayerRef.current) {
          map.removeLayer(geochemMarkerLayerRef.current);
        }
        if (geochemNearLayerRef.current) {
          map.removeLayer(geochemNearLayerRef.current);
        }
        if (scoreLayerRef.current) {
          map.removeLayer(scoreLayerRef.current);
        }
      }
      sguVectorLayersRef.current.clear();
      boreholeClusterLabelsLayerRef.current = null;
      geochemMarkerLayerRef.current = null;
      geochemNearLayerRef.current = null;
      scoreLayerRef.current = null;
    };
  }, []);

  const legacyAnalysisMarkersEnabled = true;
  useEffect(() => {
    // Restore cached scores when switching projects
    if (SCORE_CACHE_KEY) {
      try {
        const raw = localStorage.getItem(SCORE_CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached?.cells)) {
            setScores(cached.cells);
            drawScoreCells(cached.cells);
          }
        }
      } catch (err) {
        console.error('Failed to restore cached SGU scores', err);
      }
    } else {
      setScores([]);
      drawScoreCells([]);
    }

    if (!selectedProject?.id) {
      setGeochemOverlayId('');
      setGeochemOverlayVisible(false);
      setGeochemColorField('');
      return;
    }
    const prefs = loadGeochemPreferences(selectedProject.id);
    setGeochemOverlayId(prefs.overlayId || '');
    setGeochemOverlayVisible(Boolean(prefs.visible) && Boolean(prefs.overlayId));
    setGeochemColorField(prefs.colorField || '');
  }, [selectedProject?.id, loadGeochemPreferences]);

  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId) {
      setGeochemOverlays([]);
      return;
    }

    let cancelled = false;

    const loadOverlays = async () => {
      try {
        const response = await apiFetch(`/api/projects/${projectId}/geochem-overlays`);
        if (!response.ok) {
          throw new Error(`Failed to load geochemistry overlays (${response.status})`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        const overlays = Array.isArray(payload?.overlays) ? payload.overlays : [];
        const mapped = overlays
          .map((overlay: any): GeochemOverlayDescriptor | null => {
            if (!overlay) {
              return null;
            }
            const data = Array.isArray(overlay.data)
              ? overlay.data
                  .map((point: any) => {
                    const lat = typeof point?.lat === 'number' ? point.lat : Number(point?.lat);
                    const lon = typeof point?.lon === 'number' ? point.lon : Number(point?.lon);
                    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                      return null;
                    }
                    const valuesRaw = point?.values && typeof point.values === 'object' ? point.values : point;
                    const values: Record<string, number | null> = {};
                    if (valuesRaw && typeof valuesRaw === 'object') {
                      Object.entries(valuesRaw).forEach(([key, value]) => {
                        if (key === 'lat' || key === 'lon') {
                          return;
                        }
                        if (typeof value === 'number') {
                          values[key] = value;
                        } else if (value != null) {
                          const numeric = Number(value);
                          values[key] = Number.isFinite(numeric) ? numeric : null;
                        } else {
                          values[key] = null;
                        }
                      });
                    }
                    const metadataRaw = point?.metadata && typeof point.metadata === 'object' ? point.metadata : {};
                    const metadata: Record<string, any> = {};
                    if (metadataRaw && typeof metadataRaw === 'object') {
                      Object.entries(metadataRaw).forEach(([key, value]) => {
                        if (key === 'lat' || key === 'lon') {
                          return;
                        }
                        if (value === undefined || value === null) {
                          return;
                        }
                        metadata[key] = value;
                      });
                    }
                    return { lat, lon, values, metadata };
                  })
                  .filter((entry: GeochemOverlayPoint | null): entry is GeochemOverlayPoint => Boolean(entry))
              : [] as GeochemOverlayPoint[];

            const bounds = overlay.bounds && typeof overlay.bounds === 'object'
              ? {
                  min_lat: Number(overlay.bounds.min_lat),
                  max_lat: Number(overlay.bounds.max_lat),
                  min_lon: Number(overlay.bounds.min_lon),
                  max_lon: Number(overlay.bounds.max_lon)
                }
              : undefined;

            const pointCount = typeof overlay.point_count === 'number'
              ? overlay.point_count
              : data.length;

            const elements = Array.isArray(overlay.elements)
              ? overlay.elements.filter((item: any) => typeof item === 'string')
              : Object.keys(data[0]?.values || {}).filter((item) => item !== 'lat' && item !== 'lon');

            return {
              id: overlay.id || `${projectId}-geochem-${Math.random().toString(36).slice(2)}` ,
              name: overlay.name || 'Geochemistry Overlay',
              polygonHint: overlay.polygon_hint || overlay.polygonHint || null,
              elements,
              pointCount,
              bounds,
              data
            };
          })
          .filter((overlay: GeochemOverlayDescriptor | null): overlay is GeochemOverlayDescriptor => Boolean(overlay));

        setGeochemOverlays(mapped);
      } catch (error) {
        console.error('Failed to load geochemistry overlays:', error);
        if (!cancelled) {
          setGeochemOverlays([]);
        }
      }
    };

    loadOverlays();

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (map) {
      staticRasterLayerCacheRef.current.forEach((layer) => {
        map.removeLayer(layer);
      });
    }
    staticRasterLayerCacheRef.current.clear();
    setProjectStaticRasters([]);
    setStaticRasterVisibility({});

    if (!selectedProject?.id) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const loadStaticRasters = async () => {
      try {
        const response = await apiFetch(`/api/projects/${selectedProject.id}/rasters`, {
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`Failed to load project rasters: ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        const entries = Array.isArray(payload?.rasters) ? payload.rasters : [];

        const normalized: StaticRasterDescriptor[] = entries
          .map((item: any) => {
            if (!item) {
              return null;
            }
            const id =
              (typeof item.id === 'string' && item.id.trim().length > 0 && item.id.trim()) ||
              (typeof item.layer === 'string' && item.layer.trim().length > 0 && item.layer.trim());
            const rawUrl = typeof item.url === 'string' ? item.url : '';
            if (!id || !rawUrl) {
              return null;
            }
            const absoluteUrl = rawUrl.startsWith('http')
              ? rawUrl
              : buildApiUrl(rawUrl);

            const valueRange =
              Array.isArray(item.value_range) && item.value_range.length >= 2
                ? [Number(item.value_range[0]), Number(item.value_range[1])] as [number, number]
                : undefined;

            const statsSource = item.statistics || item.stats;
            const statistics =
              statsSource && typeof statsSource === 'object'
                ? {
                    min:
                      statsSource.min != null && Number.isFinite(Number(statsSource.min))
                        ? Number(statsSource.min)
                        : undefined,
                    max:
                      statsSource.max != null && Number.isFinite(Number(statsSource.max))
                        ? Number(statsSource.max)
                        : undefined,
                    mean:
                      statsSource.mean != null && Number.isFinite(Number(statsSource.mean))
                        ? Number(statsSource.mean)
                        : undefined
                  }
                : undefined;

            const boundsData = item.bounds;
            const bounds =
              boundsData && typeof boundsData === 'object'
                ? {
                    west:
                      boundsData.west != null && Number.isFinite(Number(boundsData.west))
                        ? Number(boundsData.west)
                        : undefined,
                    south:
                      boundsData.south != null && Number.isFinite(Number(boundsData.south))
                        ? Number(boundsData.south)
                        : undefined,
                    east:
                      boundsData.east != null && Number.isFinite(Number(boundsData.east))
                        ? Number(boundsData.east)
                        : undefined,
                    north:
                      boundsData.north != null && Number.isFinite(Number(boundsData.north))
                        ? Number(boundsData.north)
                        : undefined
                  }
                : null;

            const resolutionData = item.resolution;
            const resolution =
              resolutionData && typeof resolutionData === 'object'
                ? {
                    x:
                      resolutionData.x != null && Number.isFinite(Number(resolutionData.x))
                        ? Number(resolutionData.x)
                        : undefined,
                    y:
                      resolutionData.y != null && Number.isFinite(Number(resolutionData.y))
                        ? Number(resolutionData.y)
                        : undefined
                  }
                : null;

            return {
              id,
              layer:
                (typeof item.layer === 'string' && item.layer.trim().length > 0 && item.layer.trim()) || id,
              displayName:
                (typeof item.display_name === 'string' && item.display_name.trim().length > 0 && item.display_name.trim()) ||
                (typeof item.displayName === 'string' && item.displayName.trim().length > 0 && item.displayName.trim()) ||
                id,
              category: typeof item.category === 'string' ? item.category : undefined,
              description: typeof item.description === 'string' ? item.description : undefined,
              source: typeof item.source === 'string' ? item.source : undefined,
              url: absoluteUrl,
              contentType: typeof item.content_type === 'string' ? item.content_type : undefined,
              format: typeof item.format === 'string' ? item.format : undefined,
              defaultOpacity:
                typeof item.default_opacity === 'number'
                  ? item.default_opacity
                  : typeof item.defaultOpacity === 'number'
                    ? item.defaultOpacity
                    : undefined,
              valueRange,
              statistics,
              nodata:
                item.nodata != null && Number.isFinite(Number(item.nodata))
                  ? Number(item.nodata)
                  : undefined,
              bounds,
              resolution
            } as StaticRasterDescriptor;
          })
          .filter((entry: StaticRasterDescriptor | null): entry is StaticRasterDescriptor => Boolean(entry));

        setProjectStaticRasters(normalized);
        const initialVisibility = normalized.reduce<Record<string, boolean>>((acc, raster) => {
          acc[raster.id] = false;
          return acc;
        }, {});
        setStaticRasterVisibility(initialVisibility);
      } catch (error) {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error('Failed to load project rasters:', error);
      }
    };

    loadStaticRasters();

    return () => {
      cancelled = true;
      controller.abort();
      const cleanupMap = mapInstanceRef.current;
      if (cleanupMap) {
        staticRasterLayerCacheRef.current.forEach((layer) => {
          cleanupMap.removeLayer(layer);
        });
      }
      staticRasterLayerCacheRef.current.clear();
    };
  }, [selectedProject?.id]);

  useEffect(() => {
    const projectId = selectedProject?.id;
    drillIntervalCacheRef.current.clear();
    setActiveDrillHoleId(null);
    setExpandedDrillHoleId(null);
    setActiveDrillIntervals([]);
    setActiveDrillIntervalStats(null);
    setDrillIntervalsError(null);
    setExpandedSurfaceSampleId(null);
    setSurfaceAssaysError(null);
    setInsightsTab('heatmap');

    if (!projectId) {
      setDrillCollars([]);
      setSurfaceAssays([]);
      setDrillCollarError(null);
      setDrillCollarsLoading(false);
      setSurfaceAssaysLoading(false);
      return;
    }

    let cancelled = false;
    const drillController = new AbortController();
    const surfaceController = new AbortController();

    const mapDrillCollar = (raw: any): DrillCollarSummary | null => {
      const holeId = typeof raw?.hole_id === 'string' ? raw.hole_id.trim() : '';
      const lat = toNumberOrNull(raw?.latitude);
      const lon = toNumberOrNull(raw?.longitude);
      if (!holeId || lat === null || lon === null) {
        return null;
      }
      return {
        holeId,
        region: raw?.region ?? null,
        latitude: lat,
        longitude: lon,
        elevationM: toNumberOrNull(raw?.elevation_m),
        totalDepthM: toNumberOrNull(raw?.total_depth_m),
        status: raw?.status ?? null,
        categoria: raw?.categoria ?? null,
        intervalCount: toNumberOrNull(raw?.interval_count),
        sampledLengthM: toNumberOrNull(raw?.sampled_length_m),
        avgGoldPpm: toNumberOrNull(raw?.avg_gold_ppm),
        maxGoldPpm: toNumberOrNull(raw?.max_gold_ppm),
        avgSilverPpm: toNumberOrNull(raw?.avg_silver_ppm),
        maxSilverPpm: toNumberOrNull(raw?.max_silver_ppm),
        gradeThicknessAuGm: toNumberOrNull(raw?.grade_thickness_au_gm),
        sourceFile: raw?.source_file ?? null,
      };
    };

    const mapSurfaceSample = (raw: any): SurfaceAssaySummary | null => {
      const lat = toNumberOrNull(raw?.latitude);
      const lon = toNumberOrNull(raw?.longitude);
      if (lat === null || lon === null) {
        return null;
      }
      return {
        sampleCode: raw?.sample_code ?? null,
        region: raw?.region ?? null,
        latitude: lat,
        longitude: lon,
        goldPpm: toNumberOrNull(raw?.gold_ppm),
        silverPpm: toNumberOrNull(raw?.silver_ppm),
        copperPpm: toNumberOrNull(raw?.copper_ppm),
        leadPpm: toNumberOrNull(raw?.lead_ppm),
        zincPpm: toNumberOrNull(raw?.zinc_ppm),
        values: (raw?.values && typeof raw.values === 'object') ? raw.values : {},
        metadata: (raw?.metadata && typeof raw.metadata === 'object') ? raw.metadata : {},
      };
    };

    const fetchDrillData = async () => {
      setDrillCollarsLoading(true);
      setDrillCollarError(null);
      try {
        const response = await apiFetch(`/api/projects/${projectId}/drill/collars`, {
          signal: drillController.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load drill collars (${response.status})`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        const rawCollars = Array.isArray(payload?.collars) ? payload.collars : [];
        const mappedCollars = rawCollars
          .map(mapDrillCollar)
          .filter((entry: DrillCollarSummary | null): entry is DrillCollarSummary => Boolean(entry));
        setDrillCollars(mappedCollars);
        if (mappedCollars.length > 0) {
          setActiveDrillHoleId(mappedCollars[0].holeId);
        }
      } catch (error: any) {
        if (cancelled || error?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load drill collars:', error);
        setDrillCollarError(error?.message || 'Unable to load drill collar data.');
        setDrillCollars([]);
      } finally {
        if (!cancelled) {
          setDrillCollarsLoading(false);
        }
      }
    };

    const fetchSurfaceAssays = async () => {
      setSurfaceAssaysLoading(true);
      setSurfaceAssaysError(null);
      try {
        const response = await apiFetch(
          `/api/projects/${projectId}/surface-assays?limit=500`,
          { signal: surfaceController.signal }
        );
        if (!response.ok) {
          throw new Error(`Failed to load surface assays (${response.status})`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        const rawSamples = Array.isArray(payload?.samples) ? payload.samples : [];
        const mappedSamples = rawSamples
          .map(mapSurfaceSample)
          .filter((entry: SurfaceAssaySummary | null): entry is SurfaceAssaySummary => Boolean(entry));
        setSurfaceAssays(mappedSamples);
      } catch (error: any) {
        if (cancelled || error?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load surface assays:', error);
        setSurfaceAssaysError(error?.message || 'Unable to load surface assays.');
        setSurfaceAssays([]);
      } finally {
        if (!cancelled) {
          setSurfaceAssaysLoading(false);
        }
      }
    };

    fetchDrillData();
    fetchSurfaceAssays();

    return () => {
      cancelled = true;
      drillController.abort();
      surfaceController.abort();
    };
  }, [selectedProject?.id]);

  const loadDrillIntervals = useCallback(
    async (holeId: string | null, options: { force?: boolean } = {}) => {
      const projectId = selectedProject?.id;
      if (!projectId || !holeId) {
        setActiveDrillIntervals([]);
        setActiveDrillIntervalStats(null);
        return;
      }
      const normalizedHoleId = holeId.trim().toLowerCase();
      if (!normalizedHoleId) {
        setActiveDrillIntervals([]);
        setActiveDrillIntervalStats(null);
        return;
      }

      const cached = drillIntervalCacheRef.current.get(normalizedHoleId);
      if (cached && !options.force) {
        setActiveDrillIntervals(cached.intervals);
        setActiveDrillIntervalStats(cached.stats);
        return;
      }

      setDrillIntervalsLoading(true);
      setDrillIntervalsError(null);
      try {
        const response = await apiFetch(
          `/api/projects/${projectId}/drill/collars/${encodeURIComponent(holeId)}`,
        );
        if (!response.ok) {
          throw new Error(`Failed to load drill intervals (${response.status})`);
        }
        const payload = await response.json();
        const rawIntervals = Array.isArray(payload?.intervals) ? payload.intervals : [];

        const mapInterval = (raw: any): DrillIntervalSummary | null => {
          const fromM = toNumberOrNull(raw?.from_m);
          const toM = toNumberOrNull(raw?.to_m);
          const lengthM = toNumberOrNull(raw?.length_m);
          const gold = toNumberOrNull(raw?.gold_ppm);
          const silver = toNumberOrNull(raw?.silver_ppm);
          const copper = toNumberOrNull(raw?.copper_ppm);
          const lead = toNumberOrNull(raw?.lead_ppm);
          const zinc = toNumberOrNull(raw?.zinc_ppm);
          const gradeThickness = toNumberOrNull(raw?.grade_thickness_au_gm);
          return {
            holeId,
            region: raw?.region ?? null,
            fromM,
            toM,
            lengthM,
            goldPpm: gold,
            silverPpm: silver,
            copperPpm: copper,
            leadPpm: lead,
            zincPpm: zinc,
            gradeThicknessAuGm: gradeThickness,
            lithology: raw?.lithology ?? null,
            alteration: raw?.alteration ?? null,
            alterationIntensity: raw?.alteration_intensity ?? null,
            mineralization: raw?.mineralization ?? null,
            sampleCode: raw?.sample_code ?? null,
            sourceFile: raw?.source_file ?? null,
          };
        };

        const intervals = rawIntervals
          .map(mapInterval)
          .filter((entry: DrillIntervalSummary | null): entry is DrillIntervalSummary => Boolean(entry));

        const statsRaw = payload?.interval_stats || {};
        const stats: DrillIntervalStats = {
          intervalCount: toNumberOrNull(statsRaw?.interval_count) ?? intervals.length,
          totalSampledLengthM:
            toNumberOrNull(statsRaw?.total_sampled_length_m) ??
            intervals.reduce(
              (sum: number, interval: DrillIntervalSummary) => sum + (interval.lengthM || 0),
              0
            ),
          gradeThicknessAuGm:
            toNumberOrNull(statsRaw?.grade_thickness_au_gm) ??
            intervals.reduce(
              (sum: number, interval: DrillIntervalSummary) => sum + (interval.gradeThicknessAuGm || 0),
              0
            ),
          maxGoldPpm:
            toNumberOrNull(statsRaw?.max_gold_ppm) ??
            (intervals.length
              ? Math.max(...intervals.map((interval: DrillIntervalSummary) => interval.goldPpm || 0))
              : null),
          maxSilverPpm:
            toNumberOrNull(statsRaw?.max_silver_ppm) ??
            (intervals.length
              ? Math.max(...intervals.map((interval: DrillIntervalSummary) => interval.silverPpm || 0))
              : null),
        };

        drillIntervalCacheRef.current.set(normalizedHoleId, { intervals, stats });
        setActiveDrillIntervals(intervals);
        setActiveDrillIntervalStats(stats);
      } catch (error: any) {
        console.error('Failed to load drill intervals:', error);
        setDrillIntervalsError(error?.message || 'Unable to load drill intervals.');
        setActiveDrillIntervals([]);
        setActiveDrillIntervalStats(null);
      } finally {
        setDrillIntervalsLoading(false);
      }
    },
    [selectedProject?.id]
  );

  const handleDrillHover = useCallback(
    (collar: DrillCollarSummary | null) => {
      if (collar) {
        setHighlightPoint({
          lat: collar.latitude,
          lon: collar.longitude,
          type: 'drill',
          payload: { holeId: collar.holeId },
        });
      } else {
        setHighlightPoint((prev) => (prev && prev.type === 'drill' ? null : prev));
      }
    },
    []
  );

  const handleSurfaceHover = useCallback((sample: SurfaceAssaySummary | null) => {
    if (sample) {
      setHighlightPoint({
        lat: sample.latitude,
        lon: sample.longitude,
        type: 'surface',
        payload: { sampleCode: sample.sampleCode },
      });
    } else {
      setHighlightPoint((prev) => (prev && prev.type === 'surface' ? null : prev));
    }
  }, []);

  const drillOverlayEntry = useMemo(() => {
    return (
      geochemOverlays.find((overlay) => {
        const id = (overlay.id || '').toLowerCase();
        const name = (overlay.name || '').toLowerCase();
        return id.includes('drill') || name.includes('drill');
      }) || null
    );
  }, [geochemOverlays]);

  const surfaceOverlayEntry = useMemo(() => {
    return (
      geochemOverlays.find((overlay) => {
        const id = (overlay.id || '').toLowerCase();
        const name = (overlay.name || '').toLowerCase();
        return id.includes('surface') || name.includes('surface');
      }) || null
    );
  }, [geochemOverlays]);

  const drillCollarsForDisplay = useMemo(() => drillCollars.slice(0, 200), [drillCollars]);

  const surfaceAssaysForDisplay = useMemo(() => surfaceAssays.slice(0, 300), [surfaceAssays]);

  const activeDrillIntervalsTop = useMemo(() => {
    if (!activeDrillIntervals.length) {
      return [];
    }
    return [...activeDrillIntervals]
      .sort((a, b) => (b.gradeThicknessAuGm || 0) - (a.gradeThicknessAuGm || 0))
      .slice(0, 12);
  }, [activeDrillIntervals]);

  useEffect(() => {
    if (insightsTab === 'drill' && drillOverlayEntry) {
      if (geochemOverlayId !== drillOverlayEntry.id) {
        setGeochemOverlayId(drillOverlayEntry.id);
      }
      if (!geochemOverlayVisible) {
        setGeochemOverlayVisible(true);
      }
    } else if (insightsTab === 'surface' && surfaceOverlayEntry) {
      if (geochemOverlayId !== surfaceOverlayEntry.id) {
        setGeochemOverlayId(surfaceOverlayEntry.id);
      }
      if (!geochemOverlayVisible) {
        setGeochemOverlayVisible(true);
      }
    }
  }, [
    insightsTab,
    drillOverlayEntry,
    surfaceOverlayEntry,
    geochemOverlayId,
    geochemOverlayVisible,
    setGeochemOverlayId,
    setGeochemOverlayVisible
  ]);

  useEffect(() => {
    if (!geochemOverlayId) {
      return;
    }
    const exists = geochemOverlays.some((overlay) => overlay.id === geochemOverlayId);
    if (!exists) {
      setGeochemOverlayId('');
      setGeochemOverlayVisible(false);
    }
  }, [geochemOverlays, geochemOverlayId]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    persistGeochemPreferences(selectedProject.id, geochemOverlayId, geochemOverlayVisible, geochemColorField);
  }, [selectedProject?.id, geochemOverlayId, geochemOverlayVisible, geochemColorField, persistGeochemPreferences]);

  useEffect(() => {
    if (!projectStaticRasters.length) {
      return;
    }
    if (!markerVisibility || markerVisibility.size === 0) {
      return;
    }

    setStaticRasterVisibility((prev) => {
      let changed = false;
      const next: Record<string, boolean> = { ...prev };
      projectStaticRasters.forEach((raster) => {
        const key = `static_raster_${raster.id}`;
        if (!markerVisibility.has(key)) {
          return;
        }
        const visible = markerVisibility.get(key) === true;
        next[raster.id] = visible;
        if ((prev[raster.id] ?? false) !== visible) {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [markerVisibility, projectStaticRasters]);

  const [heatmapData, setHeatmapData] = useState<HeatmapDataPoint[]>([]);
  const [hoveredGridPoint, setHoveredGridPoint] = useState<HoveredGridPoint | null>(null);
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [popupType, setPopupType] = useState<'coordinate' | 'polygon' | 'analysis' | 'geochem' | 'exploration' | null>(null);
  const [inspectionRecord, setInspectionRecord] = useState<InspectionRecord | null>(null);
  useEffect(() => {
    onInspectionActiveChange?.(Boolean(inspectionRecord));
  }, [inspectionRecord, onInspectionActiveChange]);
  const [agentEvalRequest, setAgentEvalRequest] = useState<AgentEvalRequest | null>(null);
  const [tourToast, setTourToast] = useState<string | null>(null);
  const tourToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [layerLoadError, setLayerLoadError] = useState<string | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<PolygonRegion | null>(null);
  const [gridDataByMineral, setGridDataByMineral] = useState<globalThis.Map<string, AnalysisGridEntry>>(new globalThis.Map());
  const [insightsPolygonId, setInsightsPolygonId] = useState<string>('');
  const [insightsAnalysisId, setInsightsAnalysisId] = useState<string>('');
  const [insightsCommodity, setInsightsCommodity] = useState<string>('');
  const [expandedCellKey, setExpandedCellKey] = useState<string | null>(null);
  const [controlFlyoutId, setControlFlyoutId] = useState<string>('');
  const closeControlFlyout = useCallback(() => {
    setControlFlyoutId('');
  }, []);
	  const [isInsightsCollapsed, setIsInsightsCollapsed] = useState(true);
	  const [isLayersCollapsed, setIsLayersCollapsed] = useState(() =>
	    readStoredBoolean(MAP_LAYER_PANEL_COLLAPSED_KEY, false)
	  );
	  const [showLegacyLayerList, setShowLegacyLayerList] = useState(() =>
	    readStoredBoolean(MAP_LAYER_LEGACY_LIST_KEY, false)
	  );
	  // Collapse insights when project changes
	  useEffect(() => {
	    setIsInsightsCollapsed(true);
	  }, [selectedProject?.id]);

	  useEffect(() => {
	    writeStoredBoolean(MAP_LAYER_PANEL_COLLAPSED_KEY, isLayersCollapsed);
	  }, [isLayersCollapsed]);

	  useEffect(() => {
	    writeStoredBoolean(MAP_LAYER_LEGACY_LIST_KEY, showLegacyLayerList);
	  }, [showLegacyLayerList]);

  // Memoize visible polygons to prevent unnecessary re-renders of PolygonDrawingTool
  const memoizedVisiblePolygons = useMemo(() => {
    const visiblePolygons = polygonRegions.filter(p => p.visible);
    if (process.env.NODE_ENV === 'development') {
      devInfo('[MapView] polygonRegions', polygonRegions.length, 'visible', visiblePolygons.length);
    }
    return visiblePolygons;
  }, [polygonRegions]);

  const analysisPolygons = useMemo(
    () => polygonRegions.filter((polygon) => polygon.region_type === 'analysis_region'),
    [polygonRegions]
  );

  const norwayTargetPolygons = useMemo(() => {
    if (selectedProject?.id !== 'project-norway-ngu') {
      return [];
    }

    return NORWAY_TARGET_LAYER_ORDER.flatMap((target) => {
      const polygon = polygonRegions.find((candidate) => candidate.name === target.name);
      return polygon ? [{ ...target, polygon }] : [];
    });
  }, [polygonRegions, selectedProject?.id]);

  const projectAnalysisResults = useMemo(() => {
    if (!selectedProject?.id || !Array.isArray(analysisResults)) {
      return [];
    }
    return analysisResults.filter((result) => result?.project_id === selectedProject.id);
  }, [analysisResults, selectedProject?.id]);

  const hasDetailedAnalyses = useMemo(
    () => projectAnalysisResults.some((analysis) => analysisHasDetailedResults(analysis)),
    [projectAnalysisResults]
  );

  const polygonNameLookup = useMemo(() => {
    const lookup = new globalThis.Map<string, string>();
    analysisPolygons.forEach((polygon) => {
      lookup.set(polygon.name.toLowerCase(), polygon.id);
    });
    return lookup;
  }, [analysisPolygons]);

  const polygonBoundsLookup = useMemo(() => {
    const lookup = new globalThis.Map<string, BoundsObject>();
    analysisPolygons.forEach((polygon) => {
      const flattened = flattenPolygonCoordinates(polygon.coordinates);
      const bounds = computeBoundsFromLatLon(
        flattened.map(([lon, lat]) => [lat, lon])
      );
      if (bounds) {
        lookup.set(polygon.id, bounds);
      }
    });
    return lookup;
  }, [analysisPolygons]);

  const resolvePolygonForResult = useCallback(
    (result: any): { id?: string; name?: string } => {
      const polygonInfo = result?.polygon_info || result?.results?.polygon_info || {};

      const rawPolygonId = (result?.polygon_id || polygonInfo?.polygon_id)
        ?.toString()
        ?.trim();
      let polygonId = rawPolygonId && rawPolygonId.length > 0 ? rawPolygonId : undefined;

      const rawName = (result?.polygon_name || polygonInfo?.polygon_name)?.toString()?.trim();
      if (!polygonId && rawName) {
        const lookupId = polygonNameLookup.get(rawName.toLowerCase());
        if (lookupId) {
          polygonId = lookupId;
        }
      }

      if (!polygonId) {
        const boundsCandidate =
          result?.results?.polygon_bounds ||
          result?.polygon_bounds ||
          result?.results?.ml_results?.polygon_bounds;

        let normalizedBounds: BoundsObject | null = null;
        if (Array.isArray(boundsCandidate) && boundsCandidate.length >= 4) {
          normalizedBounds = {
            min_lat: Number(boundsCandidate[0]),
            min_lon: Number(boundsCandidate[1]),
            max_lat: Number(boundsCandidate[2]),
            max_lon: Number(boundsCandidate[3])
          };
        } else if (
          boundsCandidate &&
          typeof boundsCandidate === 'object' &&
          ['min_lat', 'max_lat', 'min_lon', 'max_lon'].every((key) => typeof boundsCandidate[key] === 'number')
        ) {
          normalizedBounds = {
            min_lat: Number(boundsCandidate.min_lat),
            max_lat: Number(boundsCandidate.max_lat),
            min_lon: Number(boundsCandidate.min_lon),
            max_lon: Number(boundsCandidate.max_lon)
          };
        }

        if (!normalizedBounds) {
          const flattened = result?.polygon || result?.results?.polygon_coordinates;
          const derived = Array.isArray(flattened) ? flattenPolygonCoordinates(flattened) : [];
          normalizedBounds = computeBoundsFromLatLon(derived.map(([lon, lat]) => [lat, lon]));
        }

        if (normalizedBounds) {
          const entries = Array.from(polygonBoundsLookup.entries());
          for (let i = 0; i < entries.length; i += 1) {
            const [candidateId, candidateBounds] = entries[i];
            const delta =
              Math.abs(candidateBounds.min_lat - normalizedBounds.min_lat) +
              Math.abs(candidateBounds.max_lat - normalizedBounds.max_lat) +
              Math.abs(candidateBounds.min_lon - normalizedBounds.min_lon) +
              Math.abs(candidateBounds.max_lon - normalizedBounds.max_lon);
            if (delta < 0.01) {
              polygonId = candidateId;
              break;
            }
          }
        }
      }

      if (!polygonId) {
        const gridPoints =
          (Array.isArray(result?.enhanced_results?.grid_points) && result.enhanced_results.grid_points) ||
          (Array.isArray(result?.grid_points) && result.grid_points) ||
          [];

        if (Array.isArray(gridPoints) && gridPoints.length > 0) {
          const derivedBounds = computeBoundsFromLatLon(
            gridPoints
              .map((point: any) => {
                const lat = Number(point?.latitude ?? point?.lat ?? (Array.isArray(point) ? point[1] : NaN));
                const lon = Number(point?.longitude ?? point?.lon ?? (Array.isArray(point) ? point[0] : NaN));
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                  return null;
                }
                return [lat, lon] as [number, number];
              })
              .filter((coords): coords is [number, number] => Array.isArray(coords))
          );

          if (derivedBounds) {
            const entries = Array.from(polygonBoundsLookup.entries());
            for (let i = 0; i < entries.length; i += 1) {
              const [candidateId, candidateBounds] = entries[i];
              const delta =
                Math.abs(candidateBounds.min_lat - derivedBounds.min_lat) +
                Math.abs(candidateBounds.max_lat - derivedBounds.max_lat) +
                Math.abs(candidateBounds.min_lon - derivedBounds.min_lon) +
                Math.abs(candidateBounds.max_lon - derivedBounds.max_lon);
              if (delta < 0.01) {
                polygonId = candidateId;
                break;
              }
            }
          }
        }
      }

      let polygonName = rawName;
      if (!polygonName && polygonId) {
        const match = analysisPolygons.find((polygon) => polygon.id === polygonId);
        if (match) {
          polygonName = match.name;
        }
      }

      return { id: polygonId, name: polygonName };
    },
    [analysisPolygons, polygonBoundsLookup, polygonNameLookup]
  );

  // Function to close popup
  const closePopup = useCallback(() => {
    overlayRef.current?.setPosition(undefined);
    setPopupInfo(null);
    setPopupType(null);
    setSelectedPolygon(null);
  }, []);
  const closeInspectionDrawer = useCallback(() => {
    setInspectionRecord(null);
    setAgentEvalRequest(null);
    clearClusterSpiderLayer();
  }, [clearClusterSpiderLayer]);
  const buildInspectionRecord = useCallback((feature: any, layer: any, lonLat: [number, number]): InspectionRecord => {
    const rawProperties = feature?.getProperties?.() || {};
    const properties = Object.entries(rawProperties).reduce<Record<string, unknown>>((next, [key, value]) => {
      if (key !== 'geometry') {
        next[key] = value;
      }
      return next;
    }, {});
    const layerLabel =
      formatInspectionValue(properties.__geomorphicLayerLabel) ||
      formatInspectionValue(layer?.get?.('geomorphicLayerLabel')) ||
      formatInspectionValue(layer?.get?.('name')) ||
      undefined;
    const title =
      formatInspectionValue(properties.__geomorphicLayerLabel) ||
      formatInspectionValue(properties.target_id) ||
      formatInspectionValue(properties.targetId) ||
      formatInspectionValue(properties.name) ||
      formatInspectionValue(properties.Name) ||
      formatInspectionValue(properties.NAME) ||
      formatInspectionValue(properties.label) ||
      layerLabel ||
      'Map feature';
    const targetArea = formatInspectionValue(properties.targetArea);
    const featureType = formatInspectionValue(properties.type);
    const portfolioStage = normalizePortfolioStage(
      properties.__geomorphicPortfolioStage ||
      properties.portfolioStage ||
      properties.portfolio_stage ||
      properties.portfolio_status ||
      properties.status ||
      properties.classification ||
      properties.staking_action
    );
    const source =
      formatInspectionValue(properties.__geomorphicLayerSource) ||
      formatInspectionValue(layer?.get?.('geomorphicLayerSource')) ||
      formatInspectionValue(properties.source) ||
      formatInspectionValue(properties.path) ||
      undefined;
    const confidence =
      formatInspectionValue(properties.__geomorphicConfidence) ||
      formatInspectionValue(properties.confidence) ||
      undefined;
    const nextAction =
      formatInspectionValue(properties.__geomorphicNextAction) ||
      formatInspectionValue(properties.next_action) ||
      formatInspectionValue(properties.staking_action) ||
      (portfolioStage ? PORTFOLIO_STAGE_DEFINITIONS[portfolioStage].description : undefined);

    // Drill-cluster features (from /api/drill-clusters/geojson) carry
    // cluster_id + country + review_status — these get the live agent
    // evaluation affordance in the inspection drawer.
    const clusterIdValue = Number(properties.cluster_id);
    const clusterCountry = typeof properties.country === 'string' ? properties.country.trim().toLowerCase() : '';
    const agentEval: AgentEvalRequest | undefined =
      Number.isFinite(clusterIdValue) && clusterCountry && properties.review_status !== undefined
        ? {
            country: clusterCountry,
            clusterId: clusterIdValue,
            clusterName: title,
            lon: lonLat[0],
            lat: lonLat[1],
          }
        : undefined;

    return {
      id: `${formatInspectionValue(properties.__geomorphicLayerId) || layerLabel || title}:${lonLat[0].toFixed(5)}:${lonLat[1].toFixed(5)}`,
      title,
      subtitle: [layerLabel, targetArea, featureType].filter(Boolean).join(' · '),
      coordinates: `${lonLat[1].toFixed(5)}, ${lonLat[0].toFixed(5)}`,
      layerLabel,
      source,
      portfolioStage,
      portfolioStageDefinition: portfolioStage ? PORTFOLIO_STAGE_DEFINITIONS[portfolioStage] : undefined,
      confidence,
      nextAction,
      agentEval,
      rows: buildInspectionRows(properties),
    };
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const [showProjectBoundary, setShowProjectBoundary] = useState(false);
  const boundaryLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const countryOutlineLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  
  // Mineral mapping state
  const [activeMineralLayers, setActiveMineralLayers] = useState<string[]>([]);
  const [mineralLayers, setMineralLayers] = useState<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const [showMineralLegend, setShowMineralLegend] = useState(false);
  const [isProcessingMineralLayers, setIsProcessingMineralLayers] = useState(false);

  // Analysis results toggle state
  const [showAnalysisResults, setShowAnalysisResults] = useState(false);

  // Uranium targeting layers visibility (Sweden Uranium project)
  const [uraniumOccurrencesVisible, setUraniumOccurrencesVisible] = useState(false);
  const [alumShaleVisible, setAlumShaleVisible] = useState(false);
  const [uraniumKnownDepositsVisible, setUraniumKnownDepositsVisible] = useState(false);
  const [radiometricUraniumVisible, setRadiometricUraniumVisible] = useState(false);
  const [radiometricThoriumVisible, setRadiometricThoriumVisible] = useState(false);
  const [uraniumRankedTargetsVisible, setUraniumRankedTargetsVisible] = useState(false);
  const [palangeVerificationVisible, setPalangeVerificationVisible] = useState(false);

  // Staking boundaries visibility state (per-tier toggles)
  // VERIFIED 2025-12-21: 15 targets including Köli VMS discoveries
  // UPDATED 2025-12-22: Added Remdalen & Unna Gaisartjåkko from BRAP 00057 (needs verification)
  const [stakingTierVisibility, setStakingTierVisibility] = useState<Record<string, boolean>>({
    // Köli Caledonides VMS (14.7-15.7°E, 65.4-66.3°N) - MUST STAKE highest priority
    'sodra_svalvatjakko': false,  // 1500 ha, 100% open - MUST STAKE (26.66% Zn - HIGHEST IN SWEDEN!)
    'dalovardo': false,           // 4000 ha, 100% open - MUST STAKE (8.36% Cu, 8.26% Zn, 214 g/t Ag)
    'norra_svalvatjakko': false,  // 1500 ha, 100% open - HIGH (16.8% Zn, 63 g/t Ag)
    'ropen_vms': false,           // 10120 ha concept polygon v3 — 7 VMS occurrences + 70-hole Usmeten drill cluster
    'ropen_pt_ropen': false,      // Ropen occurrence point (11.38% Zn, 253 g/t Ag, 1.2 g/t Au)
    'ropen_pt_rikker': false,     // Rikker (3.13% Zn, 70 g/t Ag)
    'ropen_pt_rikkervardo': false, // Rikkervardo (Zn-Pb)
    'ropen_pt_jemesjauretje': false, // Jemesjauretje (Zn-Pb, "Ropen S.")
    'ropen_pt_bijetjatje': false, // Bijetjatje (Cu, Ropen unit massive sulphide)
    'ropen_pt_kebnevardo': false, // Kebnevardo (Cu-Zn-Pb-Ag polymetallic)
    'ropen_pt_usmeten': false,    // Usmeten (153 kt @ 1.27% Cu, 70 holes, open at depth)
    'birdotjarre': false,         // 800 ha, 100% open - HIGH (380 g/t Ag - HIGHEST SILVER)
    'birdejaure': false,          // 800 ha, 100% open - HIGH (10.4% Zn, 9% Pb, 155 g/t Ag)
    // Stekenjokk VMS district (14.4-14.7°E, 65.1-65.4°N) - NEEDS VERIFICATION (from BRAP 00057)
    'remdalen': false,           // 0.7 Mt @ 1.43% Cu, 2.74% Zn - NEEDS VERIFICATION
    'unna_gaisartjakko': false,  // 1.0 Mt @ 0.8% Cu, 0.5% Zn - NEEDS VERIFICATION
    // Jokkmokk / Norrbotten (18.5°E, 66.9°N) - REDESIGNED Feb 2026 per desk study
    'seitevare': false,          // formerly kiuri_north — 25,275 ha, 100% open - MUST STAKE (Ag-Pb-Zn-Au district, 10+ prospects, 91 holes) — Kiuri district
    'koberget': false,           // formerly tjaula — ~1,800 ha, 100% open - HIGH (Cu-Co-Mo-Au, 10 holes, 700m strike, Au NEVER assayed) — Kiuri district
    'njuorram': false,           // formerly tallberget — ~2,000 ha, 100% open - HIGH (Cu-Au, 10 LKAB holes, 2.65% Cu, 8 g/t Au) — Kiuri district
    'kiuri_district_core': false, // formerly kiuri_tjaula_district_core — 7,827 ha, compact single-polygon comparison design — Kiuri district
    // Laisvall (17°E, 66°N) - Pb-Zn-U-Mo-Ni district
    'laisvall': false,     // 2978 ha, 47% open - HIGH (64 Mt produced, exploration upside)
    'bjorntjarn': false,   // 2500 ha, 100% open - HIGH (1.8 Mt @ 0.11% Mo)
    'pleutajokk': false,   // 2500 ha, 80% open - MEDIUM (4 Mt U - wait for ban lift)
    'autjak': false,       // 2827 ha, 100% open - MUST STAKE (4.3% Ni)
    // Skellefteå Ni-Cu-PGM district
    'njuggtraskliden': false, // 14400 ha, 92% open - MUST STAKE (Kendrick abandoned Dec 2024)
    'njuggtraskliden_expanded': false, // 12300 ha - RECOMMENDED (captures NW magnetic anomaly)
    'mjovattnet_watch': false, // CONTESTED - deposit permit nr 103 held to Aug 2026 (not refiled); Boliden nr 1001 is on the EXPIRED nr 101 block (0 ha over nr 103); race Boliden for nr 103 ground ~Aug 2027
    'mjovattnet_nr101_block': false, // Boliden nr 1001 application footprint (= expired EV Metals nr 101 regional block); live SGU geometry. The karenstid-race ground, NOT the held nr 103 deposit.
    'njugg_boliden_district': false, // dissolved union of 48 active Boliden permits in the Njugg/Skellefte view (~100,500 ha); shows district dominance, nearest block ~12 km (Amliden, VMS/Au)
    'risliden_vms': false, // naming artefact - sediment-hosted Cu-Zn anomaly INSIDE permit, NOT VMS (SGU alt_name for Njugg zones)
    'njugg_gold_south': false, // lapsed Boliden gold permit ~13km SE OUTSIDE polygon - desktop check only, not a stake target
    'arnberg': false,      // 900 ha, 100% open - OPTIONAL (Cu strike extension)
    'maratjarnliden_expansion': false, // ~3200 ha - POTENTIAL EXPANSION (Cu-Co open to south, matches old Kendrick SE extent)
    // Bergslagen / Örebro (15.5°E, 59.8°N) - Au-Te district
    'bergslagen_south_au': false, // ~10,800 ha, 100% open - STAKE OR LOSE (12,945 ppb Au, 5 competitor permits to north)
    // === CONFIRMED STAKING (added 2026-03-16 validation) ===
    // Sweden - Northern
    'adakfaltet': false,          // 6278 ha - BLOCKED (renewal filed)
    'aijajarvi': false,           // 740 ha open now (24%), full 2982 ha Dec 2027
    'klippen_au': false,          // 954 ha - LAND STATUS UNRESOLVED (SGU vs Bergsstaten conflict)
    // Sweden - Köli
    'ropen_vms_confirmed': false, // 10120 ha original district concept v3 (2026-04-10)
    'ropen_vms_east_extension': false, // 10547 ha irregular eastern-extended, lakes included (2026-04-15)
    'ropen_vms_updated': false,   // 3465 ha updated western-core redesign (2026-04-15)
    'ropen_vms_split': false,     // 1506 ha heritage-skirted 4-part split concept (2026-04-15)
    'ropen_vms_shoreline': false, // 1163 ha provisional shoreline-clipped split (2026-04-15)
    // Sweden - Central
    'w_belt': false,              // 4781 ha (3 claims) - STAKE NOW — Dyranfältet-Kvarngruvan
    'lurgarden_w': false,         // ~6500 ha - TIER 1 (W 50 ppm in core, 71 W-till anomalies, Sandvik W permit)
    'svardsjo_norra_w': false,    // ~1200 ha - TIER 1 (W 258.9 ppm in mine core, W 25.1 ppm till, gap between competitors)
    'fajsmyran_w': false,         // ~1500 ha - TIER 1 (W 32.4 ppm till @ 0.68 km, strongest W inner zone)
    'hofors_torsaker_w': false,   // 749 ha - TIER 1 STAKING CONFIRMED (3m @ 0.55% W, Halvarsgruvberget)
    // Sweden - Bergslagen
    'saxe_au_te': false,          // 26,636 ha - FILED 2026-05-19 (dnr BS 200-988-2026); see Saxe Deep Dive layers
    'pershytte_east': false,      // ~22167 ha - OPEN (designed polygon, Apr 2026)
    'bottengruvan': false,        // ~5000 ha - OPEN
    'ingagruvan_south': false,    // ~10000 ha - OPEN
    'ostanfalla': false,          // ~1000 ha - OPEN
    // Finland - Lapland
    'koitelainen': false,         // 9307 ha - OPEN (inside Natura 2000)
    'kaitaselka': false,          // 276 ha corridor - STAKE NOW (outside Natura 2000)
    'kuusivaara': false,          // ~3000 ha - COMPETITIVE
    'salla_gold': false,          // 5885 ha - OPEN
    'tongosoivanaapa': false,     // ~15000 ha - OPEN
    'tervola_cu': false,          // 3500 ha - 96% OPEN
    // Finland - Central/Eastern
    'raahe_ladoga': false,        // 3848 ha - STAKE NOW v3.1.5 (core + Pannari ext capturing 14 Pannari/Peuralampi + 4 Kusiaiskangas holes; Koivuneva excluded as 'essentially barren')
    'paljakka': false,            // v3 5720 ha - 100% OPEN, 0 ha overlap with active/karenssi permits
    'paljakka_karenssi': false,   // v3 + 240 ha south wedge recoverable post VA2024:0003 karenssi expiry
    'paljakka_v4': false,         // v4 6243 ha - preferred post-karenssi filing: captures Halla-Aho R314 drill collar
    'hammaslahti': false,         // Deprecated old centroid polygon; use Hammaslahti fringe concept layer
    'jarvi_pohjanmaa': false,     // ~9000 ha - OPEN (Li)
    // Finland - Western
    'ritakallionmaa': false,       // 3243 ha - STAKE NOW v3 filing-optimized Au polygon
    'ritakallionmaa_option_f': false,  // 14548 ha - FILING-READY single ring contiguous with v3, 2026-05-17
    'dragon_jokisivu': false,      // Dragon Jokisivu cluster (8 tenures union) - district context
    'dragon_uunimaki': false,      // Dragon Uunimäki 1 + 2 union - district context
    'vammala_inferred_corridor': false, // Speculative WNW-ESE shear corridor through Jok-Rita-Uun (geological hypothesis)
    'cluster_317_ritakallionmaa': false, // GTK+Dragon historic 30-hole cluster (our target)
    'cluster_196_palokallio': false,     // 23 GTK holes 2007-2008, intercepts 41.8 g/t Au — re-review pending
    'cluster_422_punovuori': false,      // GTK 3-hole 2001 Ni-Cu-PGE peridotite, no formal report
    'cluster_753_vanttila': false,       // Outokumpu 1991-98 Ni-Cu, REJECTED — wrong commodity (not Au)
    'perakorpi_lumikangas': false, // 2485 ha - PRIORITY 1 (230 Mt @ 4.4% TiO2, free ilmenite)
    'perakorpi_hyyppa': false,    // 1210 ha - EXPLORATION (uncharacterized)
    'perakorpi_kauhajarvi': false, // 3567 ha - PRIORITY 1 (9.6 Mt @ 5-6% TiO2, 84 DDH)
    'perakorpi_peramaa': false,   // 3506 ha - PRIORITY 2 (287 Mt @ 5.0% TiO2, intergrowth issue)
  });

  // Njuggtraskliden geophysics analysis toggles
  const [njuggAnalysisExpanded, setNjuggAnalysisExpanded] = useState(false);
  const [njuggDensityLayerVisible, setNjuggDensityLayerVisible] = useState(false);
  const [njuggDensityDepth, setNjuggDensityDepth] = useState<number>(50);
  const [njuggCrossSectionOpen, setNjuggCrossSectionOpen] = useState(false);
  const njuggDensityLayerRef = useRef<ImageLayer<Static> | null>(null);

  // Magnetic susceptibility layer (magnetic inversion)
  const [njuggMagneticLayerVisible, setNjuggMagneticLayerVisible] = useState(false);
  const [njuggMagneticDepth, setNjuggMagneticDepth] = useState<number>(50);
  const [njuggMagneticCrossSectionOpen, setNjuggMagneticCrossSectionOpen] = useState(false);
  const njuggMagneticLayerRef = useRef<ImageLayer<Static> | null>(null);

  // Aeromagnetic gradient layer (SGU 1973/1977 survey)
  const [njuggAeromagLayerVisible, setNjuggAeromagLayerVisible] = useState(false);
  const njuggAeromagLayerRef = useRef<ImageLayer<Static> | null>(null);

  // Njuggtraskliden density layer bounds (WGS84) - expanded with 50% padding
  const NJUGG_DENSITY_BOUNDS = [19.1119, 64.7835, 19.4436, 64.8782]; // [lon_min, lat_min, lon_max, lat_max]

  // Njuggtraskliden magnetic layer bounds (WGS84)
  const NJUGG_MAGNETIC_BOUNDS = [19.1221, 64.7885, 19.3448, 64.8958]; // [lon_min, lat_min, lon_max, lat_max]

  // Njuggtraskliden aeromagnetic gradient bounds (WGS84) - reprojected from SWEREF99 TM, nudged SE to align with vector boundaries
  const NJUGG_AEROMAGNETIC_BOUNDS = [18.9863, 64.7586, 19.5680, 64.9353]; // [lon_min, lat_min, lon_max, lat_max]

  // --- Saxe — Deep Dive (2026-05) layer group (Bergslagen, project-sgu-sweden) ---
  // Vector layers loaded from /data/sweden/saxe_*.geojson. Only the filed permit is visible by default.
  const SAXE_VECTOR_LAYERS = useMemo<Array<{ id: string; file: string; label: string }>>(() => [
    { id: 'saxe_filed', file: 'saxe_filed.geojson', label: 'Saxe — Filed permit (26,636 ha)' },
    { id: 'saxe_v6_design', file: 'saxe_v6_design.geojson', label: 'Saxe — v6 design (38,355 ha)' },
    { id: 'saxe_unfiled_blocks', file: 'saxe_unfiled_blocks.geojson', label: 'Saxe — Unfiled blocks' },
    { id: 'saxe_till_markers', file: 'saxe_till_markers.geojson', label: 'Saxe — Till geochem (Au ppb)' },
    { id: 'saxe_upice_corridors', file: 'saxe_upice_corridors.geojson', label: 'Saxe — Up-ice corridors' },
    { id: 'saxe_geophys_targets', file: 'saxe_geophys_targets.geojson', label: 'Saxe — Geophysics targets' },
  ], []);
  const [saxeLayerVisibility, setSaxeLayerVisibility] = useState<Record<string, boolean>>({
    saxe_filed: true,            // FILED permit Saxe nr 1 — default visible
    saxe_v6_design: false,
    saxe_unfiled_blocks: false,
    saxe_till_markers: false,
    saxe_upice_corridors: false,
    saxe_geophys_targets: false,
  });
  const saxeVectorLayersRef = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());

  // Saxe geophysics rasters (FECA 2016) — ImageStatic overlays, pixels in EPSG:3857.
  const SAXE_RASTER_EXTENT_3857 = useMemo<[number, number, number, number]>(
    () => [1709087.814, 8388154.885, 1758883.775, 8430851.422],
    []
  );
  const SAXE_RASTER_LAYERS = useMemo<Array<{ id: string; file: string; label: string }>>(() => [
    { id: 'saxe_mag_tmi', file: 'saxe_mag_tmi.png', label: 'Saxe — Mag TMI (nT)' },
    { id: 'saxe_mag_tilt', file: 'saxe_mag_tilt.png', label: 'Saxe — Mag tilt-derivative' },
    { id: 'saxe_vlf_resistivity', file: 'saxe_vlf_resistivity.png', label: 'Saxe — VLF resistivity (conductor=low)' },
    { id: 'saxe_gamma_ki', file: 'saxe_gamma_ki.png', label: 'Saxe — Gamma K-enrichment Ki' },
  ], []);
  const [saxeRasterVisibility, setSaxeRasterVisibility] = useState<Record<string, boolean>>({
    saxe_mag_tmi: false,
    saxe_mag_tilt: false,
    saxe_vlf_resistivity: false,
    saxe_gamma_ki: false,
  });
  const saxeRasterLayersRef = useRef<globalThis.Map<string, ImageLayer<Static>>>(new globalThis.Map());

  const toggleSaxeLayer = useCallback((id: string, visible: boolean) => {
    setSaxeLayerVisibility((prev) => ({ ...prev, [id]: visible }));
  }, []);
  const toggleSaxeRaster = useCallback((id: string, visible: boolean) => {
    setSaxeRasterVisibility((prev) => ({ ...prev, [id]: visible }));
  }, []);

  const prefetchedProjectRef = useRef<string | null>(null);

  // Hide copper project SGU layers when switching to uranium project
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;

    // When NOT on the Sweden copper project, hide all copper-specific layers
    if (selectedProject?.id !== 'project-sgu-sweden') {
      // Hide borehole cluster labels
      if (boreholeClusterLabelsLayerRef.current) {
        boreholeClusterLabelsLayerRef.current.setVisible(false);
      }
      // Hide all SGU vector layers (boreholes, mineral occurrences, permits, etc.)
      sguVectorLayersRef.current.forEach((layer) => {
        layer.setVisible(false);
      });
      // Hide SGU score layer (copper scoring)
      if (scoreLayerRef.current) {
        scoreLayerRef.current.setVisible(false);
      }
      // Hide geochem marker layers
      if (geochemMarkerLayerRef.current) {
        geochemMarkerLayerRef.current.setVisible(false);
      }
      if (geochemNearLayerRef.current) {
        geochemNearLayerRef.current.setVisible(false);
      }
      // Hide staking boundaries layer
      if (stakingBoundariesLayerRef.current) {
        stakingBoundariesLayerRef.current.setVisible(false);
      }
    }
  }, [selectedProject?.id]);

  // Njuggtraskliden density layer (gravity inversion)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (njuggDensityLayerRef.current) {
        mapInstance?.removeLayer(njuggDensityLayerRef.current);
        njuggDensityLayerRef.current = null;
      }
    };

    // Only show on Sweden project when toggle is enabled
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !njuggDensityLayerVisible) {
      removeLayer();
      return;
    }

    // Remove existing layer to update depth
    removeLayer();

    // Create image layer for density slice
    const extent = transformExtent(NJUGG_DENSITY_BOUNDS, 'EPSG:4326', 'EPSG:3857');
    const imageUrl = `/analysis/njuggtraskliden/density_slice_${njuggDensityDepth}m.png`;

    const layer = new ImageLayer({
      source: new Static({
        url: imageUrl,
        imageExtent: extent,
      }),
      opacity: 0.75,
      properties: { name: 'njugg-density-layer' },
      zIndex: 6, // Above aeromagnetic (5), below staking boundaries (160)
    });

    mapInstance.addLayer(layer);
    njuggDensityLayerRef.current = layer;

    return () => removeLayer();
  }, [selectedProject?.id, njuggDensityLayerVisible, njuggDensityDepth]);

  // Njuggtraskliden magnetic layer (magnetic inversion)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (njuggMagneticLayerRef.current) {
        mapInstance?.removeLayer(njuggMagneticLayerRef.current);
        njuggMagneticLayerRef.current = null;
      }
    };

    // Only show on Sweden project when toggle is enabled
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !njuggMagneticLayerVisible) {
      removeLayer();
      return;
    }

    // Remove existing layer to update depth
    removeLayer();

    // Create image layer for magnetic slice
    const extent = transformExtent(NJUGG_MAGNETIC_BOUNDS, 'EPSG:4326', 'EPSG:3857');
    const imageUrl = `/analysis/njuggtraskliden/magnetic_slice_${njuggMagneticDepth}m.png`;

    const layer = new ImageLayer({
      source: new Static({
        url: imageUrl,
        imageExtent: extent,
      }),
      opacity: 0.75,
      properties: { name: 'njugg-magnetic-layer' },
      zIndex: 7, // Above density (6), below staking boundaries (160)
    });

    mapInstance.addLayer(layer);
    njuggMagneticLayerRef.current = layer;

    return () => removeLayer();
  }, [selectedProject?.id, njuggMagneticLayerVisible, njuggMagneticDepth]);

  // Njuggtraskliden aeromagnetic gradient layer (SGU 1973/1977 survey)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (njuggAeromagLayerRef.current) {
        mapInstance?.removeLayer(njuggAeromagLayerRef.current);
        njuggAeromagLayerRef.current = null;
      }
    };

    // Only show on Sweden project when toggle is enabled
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !njuggAeromagLayerVisible) {
      removeLayer();
      return;
    }

    // Remove existing layer
    removeLayer();

    // Create image layer for aeromagnetic gradient
    const extent = transformExtent(NJUGG_AEROMAGNETIC_BOUNDS, 'EPSG:4326', 'EPSG:3857');
    const imageUrl = `/analysis/njuggtraskliden/aeromagnetic_gradient.png`;

    const layer = new ImageLayer({
      source: new Static({
        url: imageUrl,
        imageExtent: extent,
      }),
      opacity: 0.85,
      properties: { name: 'njugg-aeromagnetic-layer' },
      zIndex: 5, // Below inversion layers and staking boundaries
    });

    mapInstance.addLayer(layer);
    njuggAeromagLayerRef.current = layer;

    return () => removeLayer();
  }, [selectedProject?.id, njuggAeromagLayerVisible]);

  // Uranium targeting layers (Sweden Uranium project)
  // Known uranium deposits layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (uraniumKnownDepositsLayerRef.current) {
        mapInstance?.removeLayer(uraniumKnownDepositsLayerRef.current);
        uraniumKnownDepositsLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !uraniumKnownDepositsVisible) {
      removeLayer();
      return;
    }
    if (!uraniumKnownDepositsLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/uranium/known_uranium_deposits.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'uranium-known-deposits' },
        style: (feature) => {
          const depositType = feature.get('type') || '';
          const name = feature.get('name') || '';
          const gradePpm = feature.get('grade_ppm');
          const status = feature.get('status') || '';
          const geomType = feature.getGeometry()?.getType();

          // Color based on deposit type
          let color = '#ff0000';
          if (depositType.includes('Alum Shale')) color = '#ff8c00';
          else if (depositType.includes('Vein')) color = '#dc143c';
          else if (depositType.includes('Granite')) color = '#8b0000';
          else if (depositType.includes('REE')) color = '#9932cc';

          // Special styling for Stugberget (high-priority staking target)
          const isStugberget = name.toLowerCase().includes('stugberget');
          const isLosjon = name.toLowerCase().includes('losj');
          if (isStugberget) color = '#00ff00'; // Bright green for ready-to-stake
          if (isLosjon) color = '#ffff00'; // Yellow for untested high-potential

          const gradeText = gradePpm ? ` (${gradePpm} ppm)` : '';

          // Handle Polygon geometries (staking areas)
          if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
            const boundaryColor = isStugberget
              ? '#22c55e'
              : isLosjon
                ? '#67e8f9'
                : '#38bdf8';
            return new Style({
              fill: new Fill({ color: `${boundaryColor}33` }), // 20% opacity fill
              stroke: new Stroke({
                color: boundaryColor,
                width: 3,
                lineDash: isStugberget ? undefined : [8, 4] // Solid for Stugberget, dashed for others
              }),
              text: new Text({
                text: `${name}${status ? '\n' + status : ''}`,
                font: 'bold 12px sans-serif',
                textAlign: 'center',
                fill: new Fill({ color: '#ffffff' }),
                stroke: new Stroke({ color: '#000000', width: 3 }),
                backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
                padding: [4, 6, 4, 6],
                overflow: true
              })
            });
          }

          // Handle Point geometries (deposit markers)
          return new Style({
            image: new CircleStyle({
              radius: isLosjon ? 12 : 10, // Larger for Losjön to highlight
              fill: new Fill({ color }),
              stroke: new Stroke({ color: isLosjon ? '#fff' : '#000', width: isLosjon ? 3 : 2 })
            }),
            text: new Text({
              text: `${name}${gradeText}`,
              font: 'bold 11px sans-serif',
              textAlign: 'left',
              offsetX: 14,
              offsetY: 0,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 3 }),
              backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.6)' }),
              padding: [2, 4, 2, 4]
            })
          });
        },
        zIndex: 100
      });
      uraniumKnownDepositsLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      uraniumKnownDepositsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, uraniumKnownDepositsVisible]);

  // Uranium occurrences layer (254 SGU occurrences)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (uraniumOccurrencesLayerRef.current) {
        mapInstance?.removeLayer(uraniumOccurrencesLayerRef.current);
        uraniumOccurrencesLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !uraniumOccurrencesVisible) {
      removeLayer();
      return;
    }
    if (!uraniumOccurrencesLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/uranium/sgu_uranium_occurrences.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'uranium-occurrences' },
        style: new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: 'rgba(255, 255, 0, 0.7)' }),
            stroke: new Stroke({ color: '#ffa500', width: 1 })
          })
        }),
        zIndex: 90
      });
      uraniumOccurrencesLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      uraniumOccurrencesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, uraniumOccurrencesVisible]);

  // Alum Shale extent layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (alumShaleLayerRef.current) {
        mapInstance?.removeLayer(alumShaleLayerRef.current);
        alumShaleLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !alumShaleVisible) {
      removeLayer();
      return;
    }
    if (!alumShaleLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/uranium/alum_shale_extent.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'alum-shale-extent' },
        style: new Style({
          fill: new Fill({ color: 'rgba(139, 69, 19, 0.35)' }),
          stroke: new Stroke({ color: '#8b4513', width: 1.5 })
        }),
        zIndex: 70
      });
      alumShaleLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      alumShaleLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, alumShaleVisible]);

  // SGU Radiometric WMS - Uranium (eU)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (radiometricUraniumLayerRef.current) {
        mapInstance?.removeLayer(radiometricUraniumLayerRef.current);
        radiometricUraniumLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !radiometricUraniumVisible) {
      removeLayer();
      return;
    }
    if (!radiometricUraniumLayerRef.current) {
      const layer = new TileLayer({
        source: new TileWMS({
          url: 'https://maps3.sgu.se/geoserver/fysik/SE.GOV.SGU.URAN/ows',
          params: { 'LAYERS': 'SE.GOV.SGU.URAN', 'TILED': true, 'FORMAT': 'image/png', 'TRANSPARENT': true },
          serverType: 'geoserver',
          crossOrigin: 'anonymous'
        }),
        properties: { name: 'radiometric-uranium' },
        opacity: 0.7,
        zIndex: 60
      });
      radiometricUraniumLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      radiometricUraniumLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, radiometricUraniumVisible]);

  // SGU Radiometric WMS - Thorium (eTh)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (radiometricThoriumLayerRef.current) {
        mapInstance?.removeLayer(radiometricThoriumLayerRef.current);
        radiometricThoriumLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !radiometricThoriumVisible) {
      removeLayer();
      return;
    }
    if (!radiometricThoriumLayerRef.current) {
      const layer = new TileLayer({
        source: new TileWMS({
          url: 'https://maps3.sgu.se/geoserver/fysik/SE.GOV.SGU.THORIUM/ows',
          params: { 'LAYERS': 'SE.GOV.SGU.THORIUM', 'TILED': true, 'FORMAT': 'image/png', 'TRANSPARENT': true },
          serverType: 'geoserver',
          crossOrigin: 'anonymous'
        }),
        properties: { name: 'radiometric-thorium' },
        opacity: 0.7,
        zIndex: 60
      });
      radiometricThoriumLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      radiometricThoriumLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, radiometricThoriumVisible]);

  // Uranium ranked targets layer (top 50 scored targets)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (uraniumRankedTargetsLayerRef.current) {
        mapInstance?.removeLayer(uraniumRankedTargetsLayerRef.current);
        uraniumRankedTargetsLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !uraniumRankedTargetsVisible) {
      removeLayer();
      return;
    }
    if (!uraniumRankedTargetsLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/uranium/uranium_targets_ranked.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'uranium-ranked-targets' },
        style: (feature) => {
          const rank = feature.get('rank') || 99;
          const score = feature.get('score') || 0;
          const name = feature.get('name') || '';
          const gradePpm = feature.get('grade_ppm');

          // Color by score tier
          let color = '#4caf50'; // green for high scores
          if (score < 30) color = '#ff9800'; // orange for low
          else if (score < 45) color = '#ffeb3b'; // yellow for medium
          else if (score < 60) color = '#8bc34a'; // light green

          // Size by rank
          const radius = rank <= 5 ? 12 : rank <= 15 ? 10 : 8;

          const gradeText = gradePpm ? ` ${gradePpm}ppm` : '';

          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#000', width: 2 })
            }),
            text: new Text({
              text: `#${rank} ${name}${gradeText}`,
              font: rank <= 5 ? 'bold 12px sans-serif' : '11px sans-serif',
              textAlign: 'left',
              offsetX: radius + 4,
              offsetY: 0,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 3 }),
              backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
              padding: [2, 4, 2, 4]
            })
          });
        },
        zIndex: 110
      });
      uraniumRankedTargetsLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      uraniumRankedTargetsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, uraniumRankedTargetsVisible]);

  // Palange verification layer: filing/options polygons, rescued collars, and SGU occurrence
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (palangeVerificationLayerRef.current) {
        mapInstance?.removeLayer(palangeVerificationLayerRef.current);
        palangeVerificationLayerRef.current = null;
      }
    };

    if (!mapInstance || selectedProject?.id !== 'project-sweden-uranium' || !palangeVerificationVisible) {
      removeLayer();
      return;
    }

    if (!palangeVerificationLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/uranium/palange_verification.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'palange-verification' },
        declutter: true,
        style: (feature) => {
          const kind = feature.get('kind') || '';
          const name = feature.get('name') || feature.get('target_id') || '';

          if (kind === 'palange_polygon' || kind === 'assumed_bridge') {
            const version = feature.get('version') || '';
            const isFiling = String(version).includes('v5');
            const isBridge = kind === 'assumed_bridge';
            const color = isBridge
              ? '#94a3b8'
              : isFiling
                ? '#00d2ff'
                : String(version).includes('v3')
                  ? '#2dd4bf'
                  : String(version).includes('v2')
                    ? '#38bdf8'
                    : '#22c55e';
            const areaHa = Number(feature.get('area_ha')) || 0;
            const label = isBridge
              ? 'assumed bridge\n600 m corridor'
              : isFiling
                ? 'v5 filing geometry\n6,245 ha; Natura 0.0 ha'
                : String(version).includes('v1')
                  ? 'v1 core\n2,296 ha'
                  : String(version).includes('v2')
                    ? 'v2 north eU\n4,625 ha'
                    : String(version).includes('v3')
                      ? 'v3 Kamlunge option\n4,395 ha'
                      : `${name}${areaHa ? `\n${areaHa.toLocaleString()} ha` : ''}`;

            return new Style({
              fill: new Fill({ color: `${color}22` }),
              stroke: new Stroke({
                color,
                width: isFiling ? 3 : 2,
                lineDash: isFiling ? undefined : isBridge ? [12, 6] : [6, 4]
              }),
              text: new Text({
                text: label,
                font: 'bold 11px monospace',
                textAlign: 'center',
                fill: new Fill({ color: isFiling ? '#e0f2fe' : '#e6edf3' }),
                stroke: new Stroke({ color: '#001018', width: 4 }),
                backgroundFill: new Fill({ color: 'rgba(0, 16, 24, 0.75)' }),
                padding: [3, 5, 3, 5],
                overflow: true
              })
            });
          }

          if (kind === 'sgu_occurrence') {
            return new Style({
              image: new RegularShape({
                points: 3,
                radius: 10,
                rotation: Math.PI,
                fill: new Fill({ color: '#ffcc00' }),
                stroke: new Stroke({ color: '#0d1117', width: 2 })
              }),
              text: new Text({
                text: 'SGU U occurrence',
                font: 'bold 10px monospace',
                textAlign: 'left',
                offsetX: 13,
                offsetY: -1,
                fill: new Fill({ color: '#fff4b8' }),
                stroke: new Stroke({ color: '#0d1117', width: 3 }),
                backgroundFill: new Fill({ color: 'rgba(13, 17, 23, 0.72)' }),
                padding: [2, 4, 2, 4]
              })
            });
          }

          const hasChemical = Boolean(feature.get('chemical_analysis_flag'));
          const hasRadiometric = Boolean(feature.get('radiometric_measure_flag'));
          const color = hasChemical && hasRadiometric
            ? '#00d2ff'
            : hasRadiometric
              ? '#7ee787'
              : hasChemical
                ? '#ffb86c'
                : '#8b949e';
          const assayLabel = hasChemical && hasRadiometric
            ? 'C+R'
            : hasRadiometric
              ? 'R'
              : hasChemical
                ? 'C'
                : '';

          return new Style({
            image: new CircleStyle({
              radius: hasChemical || hasRadiometric ? 7 : 5,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#0d1117', width: 2 })
            }),
            text: new Text({
              text: assayLabel ? `${name} ${assayLabel}` : `${name}`,
              font: 'bold 10px monospace',
              textAlign: 'left',
              offsetX: 10,
              offsetY: 0,
              fill: new Fill({ color: '#e6edf3' }),
              stroke: new Stroke({ color: '#0d1117', width: 3 }),
              backgroundFill: new Fill({ color: 'rgba(13, 17, 23, 0.70)' }),
              padding: [2, 4, 2, 4]
            })
          });
        },
        zIndex: 125
      });
      palangeVerificationLayerRef.current = layer;
      mapInstance.addLayer(layer);
      mapInstance.getView().fit(
        transformExtent([22.78, 65.80, 23.01, 66.045], 'EPSG:4326', 'EPSG:3857'),
        {
          padding: [120, 120, 120, 120],
          duration: 600,
          maxZoom: 11
        }
      );
    } else {
      palangeVerificationLayerRef.current.setVisible(true);
    }

    return () => { removeLayer(); };
  }, [selectedProject?.id, palangeVerificationVisible]);

  // ============ FINLAND (GTK) LAYERS ============
  const finlandMineralDepositsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandExplorationPermitsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandExplorationPermitStatusVisibleRef = useRef<Record<FinlandExplorationPermitStatusKey, boolean>>({
    active: false,
    application: false,
    karenssi: false,
  });
  const finlandMiningAreasLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandReservationsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandNaturaLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandTillSamplesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandTillGoldLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandTillBasemetalsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandMagneticLayerRef = useRef<TileLayer<TileArcGISRest> | null>(null);
  const finlandEMResistivityLayerRef = useRef<TileLayer<TileArcGISRest> | null>(null);
  const finlandRadiometricTernaryLayerRef = useRef<TileLayer<TileArcGISRest> | null>(null);
  const finlandTargetingTillLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandTargetingTillAbortRef = useRef<AbortController | null>(null);
  const finlandTargetingTillLastBboxRef = useRef<string>('');
  const finlandStreamSedimentsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandGoldTargetsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandCopperTargetsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandStakingPrioritiesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandPGETargetsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoitelainenStakingLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandPerakorpiTiStakingLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandMakaraStakingLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoivuniemiGraphiteLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoivuniemiConsultantOriginalLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoivuniemiConsultantModLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoivuniemiRegisteredLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKoivuniemiDrillCollarsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKokkoharjuReviewLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandKultanummiGoldLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const projectGeoJsonLayerRefs = useRef<globalThis.Map<string, VectorLayer<VectorSource>>>(new globalThis.Map());
  const finlandMakaraTillAuLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

	  useEffect(() => {
	    projectGeoJsonPolygonOnlyRef.current = projectGeoJsonPolygonOnly;
	    projectGeoJsonLayerRefs.current.forEach((layer) => layer.changed());
    mapInstanceRef.current?.render();
  }, [projectGeoJsonPolygonOnly]);
  const finlandRockSamplesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandDrillholesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const swedenDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const swedenSingletonsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const norwayDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const irelandDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const peruDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const ontarioDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const australiaDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const quebecDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const franceDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const colombiaDrillClustersLayerRef = useRef<VectorLayer<any> | null>(null);
  const finlandFoddDepositsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandFoddSwedenLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandFoddMetallogenicLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandEgdiCrmLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandAsterAlterationLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandOreBoulderLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const finlandExpiredClaimsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const koboldClaimsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  // Newfoundland & Labrador hand-coded layer refs
  const nlModsOccurrencesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlDrillCollarsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlOpenDrillCollarsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlOpenOccurrencesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlOpenGeochemAnomaliesLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlFaultsLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const nlMagneticsNflLayerRef = useRef<TileLayer<TileArcGISRest> | null>(null);
  const nlMagneticsLabradorLayerRef = useRef<TileLayer<TileArcGISRest> | null>(null);

  // Finland Mineral Deposits Layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandMineralDepositsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMineralDepositsVisible) {
      finlandMineralDepositsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandMineralDepositsLayerRef.current) {
        finlandMineralDepositsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandMineralDepositsLayerRef.current) {
      fetch('/data/finland/mineral_deposits.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const commodity = feature.get('COMMODITY') || '';
              let color = '#ffd700'; // Default gold
              if (commodity.includes('gold')) color = '#ffd700';
              else if (commodity.includes('copper')) color = '#ff5722';
              else if (commodity.includes('nickel')) color = '#4caf50';
              else if (commodity.includes('zinc')) color = '#9c27b0';
              else if (commodity.includes('iron')) color = '#795548';
              else if (commodity.includes('cobalt')) color = '#2196f3';
              else if (commodity.includes('lithium')) color = '#00bcd4';
              return new Style({
                image: new CircleStyle({
                  radius: 4,
                  fill: new Fill({ color }),
                }),
              });
            },
            zIndex: 150,
          });
          finlandMineralDepositsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandMineralDepositsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandMineralDepositsVisible]);

  useEffect(() => {
    if (selectedProject?.id !== 'project-gtk-finland') {
      return;
    }
    const projectKey = storageProjectKey(selectedProject.id);
    const preferences = readBooleanRecord(`${MAP_LAYER_VISIBILITY_PREFIX}${projectKey}`);
    const hasStatusPreferences = FINLAND_EXPLORATION_PERMIT_STATUS_ORDER.some(
      (statusKey) => preferences[`finland:exploration-permits-${statusKey}`] !== undefined
    );
    if (!hasStatusPreferences && preferences['finland:exploration-permits'] === true) {
      setFinlandExplorationPermitsVisible(true);
    }
  }, [selectedProject?.id, setFinlandExplorationPermitsVisible]);

  useEffect(() => {
    finlandExplorationPermitStatusVisibleRef.current = finlandExplorationPermitStatusVisible;
    finlandExplorationPermitsLayerRef.current?.setVisible(finlandExplorationPermitsVisible);
    finlandExplorationPermitsLayerRef.current?.changed();
  }, [finlandExplorationPermitStatusVisible, finlandExplorationPermitsVisible]);

  // Finland Exploration Permits Layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandExplorationPermitsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandExplorationPermitsVisible) {
      finlandExplorationPermitsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandExplorationPermitsLayerRef.current) {
        finlandExplorationPermitsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandExplorationPermitsLayerRef.current) {
      fetch('/data/finland/exploration_permits_simplified.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const statusKey = getFinlandExplorationPermitStatusKey(feature);
              if (!finlandExplorationPermitStatusVisibleRef.current[statusKey]) {
                return undefined;
              }
              const visual = FINLAND_EXPLORATION_PERMIT_VISUALS[statusKey];
              return new Style({
                stroke: new Stroke({ color: visual.color, width: 2, lineDash: visual.lineDash }),
                fill: new Fill({ color: visual.fillColor }),
              });
            },
            zIndex: 50,
          });
          finlandExplorationPermitsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandExplorationPermitsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandExplorationPermitsVisible]);

  // KoBold Claims Layer (Finland TUKES + Sweden Bergsstaten, combined)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const activeOnThisProject =
      selectedProject?.id === 'project-gtk-finland' ||
      selectedProject?.id === 'project-sgu-sweden';
    if (!mapInstance || !activeOnThisProject || !koboldClaimsVisible) {
      koboldClaimsLayerRef.current?.setVisible(false);
      return;
    }
    if (!koboldClaimsLayerRef.current) {
      fetch('/data/kobold_claims.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }),
          });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const country = feature.get('country') || '';
              const ftype = String(feature.get('type') || '').toLowerCase();
              // Finland = red, Sweden = orange (distinguishable, KoBold-brand-ish)
              const stroke = country === 'Finland' ? '#e53935' : '#ff6f00';
              const fill = country === 'Finland'
                ? 'rgba(229, 57, 53, 0.18)'
                : 'rgba(255, 111, 0, 0.18)';
              // Dash pattern: application = dotted, reservation = dashed, permit = solid
              let lineDash: number[] | undefined;
              if (ftype.includes('application')) lineDash = [2, 4];
              else if (ftype.includes('reservation')) lineDash = [8, 4];
              return new Style({
                stroke: new Stroke({ color: stroke, width: 2.2, lineDash }),
                fill: new Fill({ color: fill }),
              });
            },
            zIndex: 220,
          });
          koboldClaimsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('KoBold claims load failed:', err));
    } else {
      koboldClaimsLayerRef.current.setVisible(true);
    }
  }, [selectedProject?.id, koboldClaimsVisible]);

  // Finland Mining Areas Layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandMiningAreasLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMiningAreasVisible) {
      finlandMiningAreasLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandMiningAreasLayerRef.current) {
        finlandMiningAreasLayerRef.current.setVisible(false);
      }
    };
    if (!finlandMiningAreasLayerRef.current) {
      fetch('/data/finland/mining_areas.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              stroke: new Stroke({ color: '#4caf50', width: 3 }),
              fill: new Fill({ color: 'rgba(76, 175, 80, 0.25)' }),
            }),
            zIndex: 60,
          });
          finlandMiningAreasLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandMiningAreasLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandMiningAreasVisible]);

  // Finland Reservations Layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandReservationsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandReservationsVisible) {
      finlandReservationsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandReservationsLayerRef.current) {
        finlandReservationsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandReservationsLayerRef.current) {
      fetch('/data/finland/reservations_simplified.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              stroke: new Stroke({ color: '#ff9800', width: 2, lineDash: [8, 4] }),
              fill: new Fill({ color: 'rgba(255, 152, 0, 0.08)' }),
            }),
            zIndex: 45,
          });
          finlandReservationsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandReservationsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandReservationsVisible]);

  // Finland Natura 2000 Layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandNaturaLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandNaturaVisible) {
      finlandNaturaLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandNaturaLayerRef.current) {
        finlandNaturaLayerRef.current.setVisible(false);
      }
    };
    if (!finlandNaturaLayerRef.current) {
      fetch('/data/finland/natura2000_finland.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              stroke: new Stroke({ color: '#8e44ad', width: 1.5 }),
              fill: new Fill({ color: 'rgba(142, 68, 173, 0.15)' }),
            }),
            zIndex: 30,
          });
          finlandNaturaLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandNaturaLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandNaturaVisible]);

  // Finland Till Geochemistry Samples Layer (753k locations)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandTillSamplesLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandTillSamplesVisible) {
      finlandTillSamplesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandTillSamplesLayerRef.current) {
        finlandTillSamplesLayerRef.current.setVisible(false);
      }
    };
    if (!finlandTillSamplesLayerRef.current) {
      // Load the grid summary for performance (2,056 cells instead of 753k points)
      fetch('/data/finland/till_samples_grid.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857',
          });
          const source = new VectorSource({ features });
          // Find max count for scaling
          const maxCount = Math.max(...features.map((f: any) => f.get('count') || 1));
          const layer = new VectorLayer({
            source,
            style: (feature: any) => {
              const count = feature.get('count') || 1;
              // Scale radius from 3 to 12 based on sample density
              const radius = 3 + (count / maxCount) * 9;
              // Color from light orange to dark orange based on density
              const intensity = Math.min(255, Math.floor(100 + (count / maxCount) * 155));
              return new Style({
                image: new CircleStyle({
                  radius,
                  fill: new Fill({ color: `rgba(255, ${255 - intensity}, 0, 0.7)` }),
                  stroke: new Stroke({ color: '#ff6600', width: 1 }),
                }),
              });
            },
            zIndex: 25,
          });
          finlandTillSamplesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandTillSamplesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandTillSamplesVisible]);

  // Finland Regional Till Gold Geochemistry (47k samples with Au assays - combined alym_519u + almr_519u for full coverage)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandTillGoldLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandTillGoldVisible) {
      finlandTillGoldLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandTillGoldLayerRef.current) {
        finlandTillGoldLayerRef.current.setVisible(false);
      }
    };
    if (!finlandTillGoldLayerRef.current) {
      fetch('/data/finland/regional_till_gold.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const source = new VectorSource({ features });
          // Find percentiles for color scaling
          const auValues = features.map((f: any) => f.get('Au') || 0).filter((v: number) => v > 0).sort((a: number, b: number) => a - b);
          const p90 = auValues[Math.floor(auValues.length * 0.90)] || 0.01;
          const p99 = auValues[Math.floor(auValues.length * 0.99)] || 0.1;
          // Store thresholds on the layer for filtering
          const layer = new VectorLayer({
            source,
            style: (feature: any) => {
              const au = feature.get('Au') || 0;
              // Determine level
              const isLow = au > 0 && au < p90;
              const isP90 = au >= p90 && au < p99;
              const isP99 = au >= p99;
              const zoom = currentZoomLevelRef.current;
              // Check if this level should be shown
              if (isLow && !finlandTillGoldLevels.low) return EMPTY_VECTOR_STYLE; // Hide
              if (isP90 && !finlandTillGoldLevels.p90) return EMPTY_VECTOR_STYLE;
              if (isP99 && !finlandTillGoldLevels.p99) return EMPTY_VECTOR_STYLE;
              if (au <= 0) return EMPTY_VECTOR_STYLE; // Hide below detection
              if (zoom < 6.3 && !isP99) return EMPTY_VECTOR_STYLE;
              if (zoom < 8.2 && isLow) return EMPTY_VECTOR_STYLE;
              let color = 'rgba(255, 255, 150, 0.6)'; // Low - pale yellow
              if (isP90) color = 'rgba(255, 165, 0, 0.8)'; // Elevated - orange
              else if (isP99) color = 'rgba(255, 0, 0, 0.9)'; // Anomalous - red
              return new Style({
                image: new CircleStyle({
                  radius: isP99 ? 6 : isP90 ? 5 : 3,
                  fill: new Fill({ color }),
                }),
              });
            },
            zIndex: 120,
          });
          finlandTillGoldLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      // Calculate thresholds ONCE outside the style function (O(n) instead of O(n²))
      const source = finlandTillGoldLayerRef.current.getSource();
      if (source) {
        const allFeatures = source.getFeatures();
        const auValues = allFeatures.map((f: any) => f.get('Au') || 0).filter((v: number) => v > 0).sort((a: number, b: number) => a - b);
        const p90 = auValues[Math.floor(auValues.length * 0.90)] || 0.01;
        const p99 = auValues[Math.floor(auValues.length * 0.99)] || 0.1;
        // Update style function with new level visibility
        finlandTillGoldLayerRef.current.setStyle((feature: any) => {
          const au = feature.get('Au') || 0;
          const isLow = au > 0 && au < p90;
          const isP90 = au >= p90 && au < p99;
          const isP99 = au >= p99;
          const zoom = currentZoomLevelRef.current;
          if (isLow && !finlandTillGoldLevels.low) return EMPTY_VECTOR_STYLE;
          if (isP90 && !finlandTillGoldLevels.p90) return EMPTY_VECTOR_STYLE;
          if (isP99 && !finlandTillGoldLevels.p99) return EMPTY_VECTOR_STYLE;
          if (au <= 0) return EMPTY_VECTOR_STYLE;
          if (zoom < 6.3 && !isP99) return EMPTY_VECTOR_STYLE;
          if (zoom < 8.2 && isLow) return EMPTY_VECTOR_STYLE;
          let color = 'rgba(255, 255, 150, 0.6)';
          if (isP90) color = 'rgba(255, 165, 0, 0.8)';
          else if (isP99) color = 'rgba(255, 0, 0, 0.9)';
          return new Style({
            image: new CircleStyle({
              radius: isP99 ? 6 : isP90 ? 5 : 3,
              fill: new Fill({ color }),
            }),
          });
        });
      }
      finlandTillGoldLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandTillGoldVisible, finlandTillGoldLevels]);

  // Finland Regional Till Basemetals Geochemistry (82k samples)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandTillBasemetalsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandTillBasemetalsVisible) {
      finlandTillBasemetalsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandTillBasemetalsLayerRef.current) {
        finlandTillBasemetalsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandTillBasemetalsLayerRef.current) {
      fetch('/data/finland/regional_till_basemetals.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const source = new VectorSource({ features });
          // Color by selected element (default Cu)
          const layer = new VectorLayer({
            source,
            style: (feature: any) => {
              const cu = feature.get('Cu') || 0;
              const cuValues = [10, 30, 100]; // Low, medium, high thresholds for Cu ppm
              const zoom = currentZoomLevelRef.current;
              if (cu <= cuValues[0]) return EMPTY_VECTOR_STYLE;
              if (zoom < 6.3 && cu <= cuValues[2]) return EMPTY_VECTOR_STYLE;
              if (zoom < 8.2 && cu <= cuValues[1]) return EMPTY_VECTOR_STYLE;
              let color = 'rgba(100, 100, 100, 0.2)';
              if (cu > cuValues[2]) color = 'rgba(139, 69, 19, 0.9)'; // High - brown
              else if (cu > cuValues[1]) color = 'rgba(255, 140, 0, 0.7)'; // Medium - dark orange
              else if (cu > cuValues[0]) color = 'rgba(144, 238, 144, 0.5)'; // Low - light green
              return new Style({
                image: new CircleStyle({
                  radius: cu > cuValues[2] ? 4 : 3,
                  fill: new Fill({ color }),
                }),
              });
            },
            zIndex: 115,
          });
          finlandTillBasemetalsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandTillBasemetalsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandTillBasemetalsVisible]);

  // Finland Geophysics - Aeromagnetic Anomaly Map (ArcGIS REST from GTK)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandMagneticLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMagneticVisible) {
      finlandMagneticLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMagneticLayerRef.current) {
      const layer = new TileLayer({
        source: new TileArcGISRest({
          url: 'https://gtkdata.gtk.fi/arcgis/rest/services/MDaE/Geophysics/MapServer',
          params: { 'LAYERS': 'show:64' },
          crossOrigin: 'anonymous'
        }),
        opacity: 0.7,
        zIndex: 5,
      });
      finlandMagneticLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      finlandMagneticLayerRef.current.setVisible(true);
    }
    return () => { finlandMagneticLayerRef.current?.setVisible(false); };
  }, [selectedProject?.id, finlandMagneticVisible]);

  // Finland Geophysics - EM Apparent Resistivity (ArcGIS REST from GTK)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandEMResistivityLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandEMResistivityVisible) {
      finlandEMResistivityLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandEMResistivityLayerRef.current) {
      const layer = new TileLayer({
        source: new TileArcGISRest({
          url: 'https://gtkdata.gtk.fi/arcgis/rest/services/MDaE/Geophysics/MapServer',
          params: { 'LAYERS': 'show:71' },
          crossOrigin: 'anonymous'
        }),
        opacity: 0.7,
        zIndex: 5,
      });
      finlandEMResistivityLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      finlandEMResistivityLayerRef.current.setVisible(true);
    }
    return () => { finlandEMResistivityLayerRef.current?.setVisible(false); };
  }, [selectedProject?.id, finlandEMResistivityVisible]);

  // Finland Geophysics - Radiometric Ternary K-Th-U (ArcGIS REST from GTK)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandRadiometricTernaryLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandRadiometricTernaryVisible) {
      finlandRadiometricTernaryLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandRadiometricTernaryLayerRef.current) {
      const layer = new TileLayer({
        source: new TileArcGISRest({
          url: 'https://gtkdata.gtk.fi/arcgis/rest/services/MDaE/Geophysics/MapServer',
          params: { 'LAYERS': 'show:75' },
          crossOrigin: 'anonymous'
        }),
        opacity: 0.7,
        zIndex: 5,
      });
      finlandRadiometricTernaryLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      finlandRadiometricTernaryLayerRef.current.setVisible(true);
    }
    return () => { finlandRadiometricTernaryLayerRef.current?.setVisible(false); };
  }, [selectedProject?.id, finlandRadiometricTernaryVisible]);

  // ============ NEWFOUNDLAND & LABRADOR LAYERS (hand-coded point / line / raster) ============

  // NL Mineral Occurrences (MODS) — Evidence, small grey dots
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlModsOccurrencesLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlModsOccurrencesVisible) {
      nlModsOccurrencesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlModsOccurrencesLayerRef.current?.setVisible(false); };
    if (!nlModsOccurrencesLayerRef.current) {
      fetch('/data/newfoundland/mods_occurrences.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 2.6,
                fill: new Fill({ color: 'rgba(156, 163, 175, 0.85)' }),
                stroke: new Stroke({ color: 'rgba(51, 65, 85, 0.6)', width: 0.4 }),
              }),
            }),
            zIndex: 150,
          });
          nlModsOccurrencesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL mods_occurrences:', err));
    } else {
      nlModsOccurrencesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlModsOccurrencesVisible]);

  // NL Drill Collars (all) — Drilling, small dark-blue dots
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlDrillCollarsVisible) {
      nlDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlDrillCollarsLayerRef.current?.setVisible(false); };
    if (!nlDrillCollarsLayerRef.current) {
      fetch('/data/newfoundland/drill_collars.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 2.6,
                fill: new Fill({ color: '#1e3a8a' }),
                stroke: new Stroke({ color: 'rgba(226, 232, 240, 0.5)', width: 0.4 }),
              }),
            }),
            zIndex: 152,
          });
          nlDrillCollarsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL drill_collars:', err));
    } else {
      nlDrillCollarsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlDrillCollarsVisible]);

  // NL Open Geochem Anomalies — Geochemistry, magenta dots
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlOpenGeochemAnomaliesLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlOpenGeochemAnomaliesVisible) {
      nlOpenGeochemAnomaliesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlOpenGeochemAnomaliesLayerRef.current?.setVisible(false); };
    if (!nlOpenGeochemAnomaliesLayerRef.current) {
      fetch('/data/newfoundland/open_geochem_anomalies.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 3.6,
                fill: new Fill({ color: 'rgba(217, 70, 239, 0.9)' }),
                stroke: new Stroke({ color: 'rgba(88, 28, 135, 0.6)', width: 0.5 }),
              }),
            }),
            zIndex: 156,
          });
          nlOpenGeochemAnomaliesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL open_geochem_anomalies:', err));
    } else {
      nlOpenGeochemAnomaliesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlOpenGeochemAnomaliesVisible]);

  // NL Faults — Geology, thin dark line
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlFaultsLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlFaultsVisible) {
      nlFaultsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlFaultsLayerRef.current?.setVisible(false); };
    if (!nlFaultsLayerRef.current) {
      fetch('/data/newfoundland/faults.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              stroke: new Stroke({ color: '#334155', width: 1 }),
            }),
            zIndex: 140,
          });
          nlFaultsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL faults:', err));
    } else {
      nlFaultsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlFaultsVisible]);

  // NL Open Occurrences — Targeting (decision layer), prominent green rings
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlOpenOccurrencesLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlOpenOccurrencesVisible) {
      nlOpenOccurrencesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlOpenOccurrencesLayerRef.current?.setVisible(false); };
    if (!nlOpenOccurrencesLayerRef.current) {
      fetch('/data/newfoundland/open_occurrences.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({ color: 'rgba(34, 197, 94, 0.18)' }),
                stroke: new Stroke({ color: '#16a34a', width: 2.4 }),
              }),
            }),
            zIndex: 198,
          });
          nlOpenOccurrencesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL open_occurrences:', err));
    } else {
      nlOpenOccurrencesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlOpenOccurrencesVisible]);

  // NL Open Drill Collars — Targeting (decision layer), prominent bright-green dots
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlOpenDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlOpenDrillCollarsVisible) {
      nlOpenDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => { nlOpenDrillCollarsLayerRef.current?.setVisible(false); };
    if (!nlOpenDrillCollarsLayerRef.current) {
      fetch('/data/newfoundland/open_drill_collars.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const source = new VectorSource({ features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' }) });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: '#22c55e' }),
                stroke: new Stroke({ color: '#ffffff', width: 1.4 }),
              }),
            }),
            zIndex: 200,
          });
          nlOpenDrillCollarsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load NL open_drill_collars:', err));
    } else {
      nlOpenDrillCollarsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, nlOpenDrillCollarsVisible]);

  // NL Geophysics — Magnetics Newfoundland (Residual), ArcGIS REST (layer id 177)
  // CORS: the NL GeoAtlas service reflects the request Origin rather than sending a
  // wildcard ACAO, so crossOrigin is omitted (tiles render; no canvas readback needed).
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlMagneticsNflLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlMagneticsNflVisible) {
      nlMagneticsNflLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlMagneticsNflLayerRef.current) {
      const layer = new TileLayer({
        source: new TileArcGISRest({
          url: 'https://dnrmaps.gov.nl.ca/arcgis/rest/services/GeoAtlas/Geophysics_Newfoundland/MapServer',
          params: { 'LAYERS': 'show:177' },
        }),
        opacity: 0.55,
        zIndex: 5,
      });
      nlMagneticsNflLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      nlMagneticsNflLayerRef.current.setVisible(true);
    }
    return () => { nlMagneticsNflLayerRef.current?.setVisible(false); };
  }, [selectedProject?.id, nlMagneticsNflVisible]);

  // NL Geophysics — Magnetics Labrador (Residual), ArcGIS REST (layer id 65)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-nl') {
      nlMagneticsLabradorLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlMagneticsLabradorVisible) {
      nlMagneticsLabradorLayerRef.current?.setVisible(false);
      return;
    }
    if (!nlMagneticsLabradorLayerRef.current) {
      const layer = new TileLayer({
        source: new TileArcGISRest({
          url: 'https://dnrmaps.gov.nl.ca/arcgis/rest/services/GeoAtlas/Geophysics_Labrador/MapServer',
          params: { 'LAYERS': 'show:65' },
        }),
        opacity: 0.55,
        zIndex: 5,
      });
      nlMagneticsLabradorLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      nlMagneticsLabradorLayerRef.current.setVisible(true);
    }
    return () => { nlMagneticsLabradorLayerRef.current?.setVisible(false); };
  }, [selectedProject?.id, nlMagneticsLabradorVisible]);

  // Finland Targeting Till Geochemistry (385k samples)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandTargetingTillLayerRef.current?.setVisible(false);
      finlandTargetingTillAbortRef.current?.abort();
      finlandTargetingTillAbortRef.current = null;
      return;
    }
    if (!finlandTargetingTillVisible) {
      finlandTargetingTillLayerRef.current?.setVisible(false);
      finlandTargetingTillAbortRef.current?.abort();
      finlandTargetingTillAbortRef.current = null;
      return;
    }
    const removeLayer = () => {
      if (finlandTargetingTillLayerRef.current) {
        finlandTargetingTillLayerRef.current.setVisible(false);
      }
    };
    if (!finlandTargetingTillLayerRef.current) {
      const source = new VectorSource();
      const styleCache: Record<string, Style> = {};
      const p90 = 50;
      const p99 = 200;

      const getStyle = (isTopTier: boolean, color: string): Style => {
        const key = `${isTopTier ? 'top' : 'base'}-${color}`;
        if (styleCache[key]) {
          return styleCache[key];
        }
        const style = new Style({
          image: new CircleStyle({
            radius: isTopTier ? 4 : 2,
            fill: new Fill({ color }),
          }),
        });
        styleCache[key] = style;
        return style;
      };

      const layer = new VectorLayer({
        source,
        style: (feature: any) => {
          const cu = Number(feature.get('Cu')) || 0;
          let color = 'rgba(100, 100, 100, 0.15)';
          if (cu > 0 && cu < p90) color = 'rgba(173, 216, 230, 0.4)'; // Light blue
          else if (cu >= p90 && cu < p99) color = 'rgba(65, 105, 225, 0.6)'; // Royal blue
          else if (cu >= p99) color = 'rgba(0, 0, 139, 0.8)'; // Dark blue
          return getStyle(cu >= p99, color);
        },
        zIndex: 110,
      });
      finlandTargetingTillLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      finlandTargetingTillLayerRef.current.setVisible(true);
    }

    const layerSource = finlandTargetingTillLayerRef.current.getSource();
    if (!layerSource) {
      return () => { removeLayer(); };
    }

    const loadViewportSlice = async () => {
      const size = mapInstance.getSize();
      if (!size) return;

      const extent3857 = mapInstance.getView().calculateExtent(size);
      const [minLon0, minLat0, maxLon0, maxLat0] = transformExtent(extent3857, 'EPSG:3857', 'EPSG:4326');

      // Pad bbox so quick pans don't constantly re-request tiny adjacent windows.
      const lonPad = (maxLon0 - minLon0) * 0.2;
      const latPad = (maxLat0 - minLat0) * 0.2;
      const minLon = Math.max(-180, minLon0 - lonPad);
      const minLat = Math.max(-90, minLat0 - latPad);
      const maxLon = Math.min(180, maxLon0 + lonPad);
      const maxLat = Math.min(90, maxLat0 + latPad);
      const bbox = `${minLon.toFixed(5)},${minLat.toFixed(5)},${maxLon.toFixed(5)},${maxLat.toFixed(5)}`;

      if (bbox === finlandTargetingTillLastBboxRef.current) {
        return;
      }
      finlandTargetingTillLastBboxRef.current = bbox;

      finlandTargetingTillAbortRef.current?.abort();
      const controller = new AbortController();
      finlandTargetingTillAbortRef.current = controller;

      try {
        const response = await apiFetch(
          `/api/finland/layers/targeting_till?bbox=${encodeURIComponent(bbox)}&limit=120000`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          throw new Error(`Failed targeting till fetch: ${response.status}`);
        }

        const payload = await response.json();
        const features = new GeoJSON().readFeatures(payload.geojson, {
          featureProjection: 'EPSG:3857',
        });
        layerSource.clear(true);
        layerSource.addFeatures(features);
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load targeting till viewport slice:', error);
      }
    };

    const handleMoveEnd = () => {
      void loadViewportSlice();
    };

    mapInstance.on('moveend', handleMoveEnd);
    void loadViewportSlice();

    return () => {
      mapInstance.un('moveend', handleMoveEnd);
      finlandTargetingTillAbortRef.current?.abort();
      finlandTargetingTillAbortRef.current = null;
      removeLayer();
    };
  }, [selectedProject?.id, finlandTargetingTillVisible]);

  // Finland Stream Sediments Geochemistry (70k samples)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandStreamSedimentsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandStreamSedimentsVisible) {
      finlandStreamSedimentsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandStreamSedimentsLayerRef.current) {
        finlandStreamSedimentsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandStreamSedimentsLayerRef.current) {
      fetch('/data/finland/stream_sediments.geojson')
        .then(res => res.json())
        .then((data: any) => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const source = new VectorSource({ features });
          const cuValues = features.map((f: any) => f.get('Cu') || 0).filter((v: number) => v > 0).sort((a: number, b: number) => a - b);
          const p90 = cuValues[Math.floor(cuValues.length * 0.90)] || 10;
          const p99 = cuValues[Math.floor(cuValues.length * 0.99)] || 50;
          const layer = new VectorLayer({
            source,
            style: (feature: any) => {
              const cu = feature.get('Cu') || 0;
              let color = 'rgba(100, 100, 100, 0.3)';
              if (cu > 0 && cu < p90) color = 'rgba(144, 238, 144, 0.5)'; // Light green
              else if (cu >= p90 && cu < p99) color = 'rgba(0, 128, 0, 0.7)'; // Green
              else if (cu >= p99) color = 'rgba(0, 100, 0, 0.9)'; // Dark green
              return new Style({
                image: new CircleStyle({
                  radius: cu >= p99 ? 5 : 3,
                  fill: new Fill({ color }),
                }),
              });
            },
            zIndex: 118,
          });
          finlandStreamSedimentsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandStreamSedimentsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandStreamSedimentsVisible]);

  // Finland Gold Targets (open ground anomalies)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandGoldTargetsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandGoldTargetsVisible) {
      finlandGoldTargetsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandGoldTargetsLayerRef.current) {
        finlandGoldTargetsLayerRef.current.setVisible(false);
      }
    };
    const createGoldTargetStyle = (feature: any) => {
      const rank = feature.get('rank') || 999;
      const maxAu = feature.get('max_Au_ppm') || 0;
      // Determine level and check visibility
      const isHigh = maxAu > 0.1;
      const isMid = maxAu > 0.05 && maxAu <= 0.1;
      const isLow = maxAu <= 0.05;
      if (isHigh && !finlandGoldTargetLevels.high) return new Style({});
      if (isMid && !finlandGoldTargetLevels.mid) return new Style({});
      if (isLow && !finlandGoldTargetLevels.low) return new Style({});
      // Top 20 get larger markers with labels
      const isTop20 = rank <= 20;
      const radius = isTop20 ? 10 : 6;
      // Color intensity based on Au value
      const color = isHigh ? 'rgba(255,0,0,0.9)' :
                   isMid ? 'rgba(255,165,0,0.9)' : 'rgba(255,215,0,0.8)';
      const styles = [
        new Style({
          image: new RegularShape({
            points: 5,
            radius: radius,
            radius2: radius * 0.4,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#000', width: isTop20 ? 2 : 1 }),
            angle: 0
          }),
          zIndex: 1000 - rank
        })
      ];
      if (isTop20) {
        styles.push(new Style({
          text: new Text({
            text: `#${rank}`,
            font: 'bold 10px sans-serif',
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#000', width: 3 }),
            offsetY: -16
          }),
          zIndex: 1000 - rank
        }));
      }
      return styles;
    };
    if (!finlandGoldTargetsLayerRef.current) {
      fetch('/data/finland/targets_gold_open.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: createGoldTargetStyle,
            zIndex: 200,
          });
          finlandGoldTargetsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandGoldTargetsLayerRef.current.setStyle(createGoldTargetStyle);
      finlandGoldTargetsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandGoldTargetsVisible, finlandGoldTargetLevels]);

  // Finland Copper Targets (open ground anomalies)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandCopperTargetsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandCopperTargetsVisible) {
      finlandCopperTargetsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandCopperTargetsLayerRef.current) {
        finlandCopperTargetsLayerRef.current.setVisible(false);
      }
    };
    const createCopperTargetStyle = (feature: any) => {
      const rank = feature.get('rank') || 999;
      const maxCu = feature.get('max_Cu_ppm') || 0;
      // Determine level and check visibility
      const isHigh = maxCu > 500;
      const isMid = maxCu > 200 && maxCu <= 500;
      const isLow = maxCu <= 200;
      if (isHigh && !finlandCopperTargetLevels.high) return new Style({});
      if (isMid && !finlandCopperTargetLevels.mid) return new Style({});
      if (isLow && !finlandCopperTargetLevels.low) return new Style({});
      // Top 20 get larger markers with labels
      const isTop20 = rank <= 20;
      const radius = isTop20 ? 10 : 5;
      // Color intensity based on Cu value
      const color = isHigh ? 'rgba(139,0,0,0.9)' :
                   isMid ? 'rgba(255,69,0,0.9)' : 'rgba(255,140,0,0.8)';
      const styles = [
        new Style({
          image: new RegularShape({
            points: 4,
            radius: radius,
            angle: Math.PI / 4,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#000', width: isTop20 ? 2 : 1 }),
          }),
          zIndex: 900 - rank
        })
      ];
      if (isTop20) {
        styles.push(new Style({
          text: new Text({
            text: `#${rank}`,
            font: 'bold 10px sans-serif',
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#000', width: 3 }),
            offsetY: -16
          }),
          zIndex: 900 - rank
        }));
      }
      return styles;
    };
    if (!finlandCopperTargetsLayerRef.current) {
      fetch('/data/finland/targets_copper_open.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: createCopperTargetStyle,
            zIndex: 190,
          });
          finlandCopperTargetsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandCopperTargetsLayerRef.current.setStyle(createCopperTargetStyle);
      finlandCopperTargetsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandCopperTargetsVisible, finlandCopperTargetLevels]);

  // Finland Staking Priorities (combined Au-Cu greenfield targets)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandStakingPrioritiesLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandStakingPrioritiesVisible) {
      finlandStakingPrioritiesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandStakingPrioritiesLayerRef.current) {
        finlandStakingPrioritiesLayerRef.current.setVisible(false);
      }
    };
    if (!finlandStakingPrioritiesLayerRef.current) {
      fetch('/data/finland/staking_priorities.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const rank = feature.get('priority_rank') || 999;
              const commodity = feature.get('commodity') || 'Au';
              const greenfield = feature.get('greenfield');
              const region = feature.get('region') || '';
              // Short region abbreviation for label
              const regionAbbrev: { [key: string]: string } = {
                'North Karelia': 'NK',
                'Pirkanmaa': 'Pirk',
                'Central Lapland': 'CLap',
                'Kainuu': 'Kai',
                'Central Finland': 'CF',
                'Satakunta': 'Sat',
                'Southwest Finland': 'SW',
                'Northern Ostrobothnia': 'NOst',
                'Lapland': 'Lap',
                'Sodankylä': 'Sdk',
              };
              const shortRegion = regionAbbrev[region] || region.substring(0, 4);
              // Color by commodity type
              let color = '#ffd700'; // Gold
              if (commodity === 'Cu') color = '#ff4500';
              else if (commodity === 'Cu-Au') color = '#ff00ff'; // Magenta for overlap
              // Size by priority rank
              const radius = rank <= 5 ? 14 : rank <= 10 ? 12 : rank <= 20 ? 10 : 8;
              // Label: F{rank} {Region} {Commodity}
              const label = `F${rank} ${shortRegion}`;
              return new Style({
                image: new RegularShape({
                  points: 5,
                  radius: radius,
                  radius2: radius * 0.5,
                  angle: 0,
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: greenfield ? '#00ff00' : '#fff', width: 2 }),
                }),
                text: new Text({
                  text: label,
                  font: 'bold 10px sans-serif',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  offsetY: -20,
                }),
                zIndex: 1000 - rank
              });
            },
            zIndex: 950,
          });
          finlandStakingPrioritiesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load staking priorities:', err));
    } else {
      finlandStakingPrioritiesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandStakingPrioritiesVisible]);

  // Finland Koitelainen Staking Polygon (9,000 ha PGM-Cr-V)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoitelainenStakingLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoitelainenStakingVisible) {
      finlandKoitelainenStakingLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoitelainenStakingLayerRef.current) {
        finlandKoitelainenStakingLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoitelainenStakingLayerRef.current) {
      fetch('/data/finland/koitelainen_staking.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const name = feature.get('name') || 'KOITELAINEN';
              const commodity = feature.get('commodity') || '';
              const featureType = feature.get('featureType');
              const featureId = String(feature.get('id') || '');
              const featureColor = feature.get('color') || '#00e5ff';
              if (featureId === 'koitelainen_pgm') {
                return new Style({
                  stroke: new Stroke({
                    color: '#0ea5e9',
                    width: 3,
                  }),
                  fill: new Fill({ color: 'rgba(14, 165, 233, 0.2)' }),
                  text: new Text({
                    text: `${name}\n${commodity} | MUST STAKE`,
                    font: 'bold 12px sans-serif',
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 500
                });
              }
              if (featureType === 'stakingArea') {
                const boundaryColor = featureId === 'koitelainen_east_lobe' ? '#22c55e' : '#00e5ff';
                // Parse hex color to rgba for fill — derive RGB components from the hex string
                const hex = boundaryColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                return new Style({
                  stroke: new Stroke({
                    color: boundaryColor,
                    width: 3,
                    lineDash: [10, 6],
                  }),
                  fill: new Fill({ color: `rgba(${r}, ${g}, ${b}, 0.12)` }),
                  text: new Text({
                    text: `${name}\nSEPARATE STAKING`,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: boundaryColor }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 550
                });
              }
              if (featureType === 'marker') {
                const markerColor = featureColor;
                return new Style({
                  image: new RegularShape({
                    points: 4,
                    radius: 10,
                    angle: Math.PI / 4,
                    fill: new Fill({ color: markerColor }),
                    stroke: new Stroke({ color: '#fff', width: 2 }),
                  }),
                  text: new Text({
                    text: `${name}\n${commodity}`,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: markerColor }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -18,
                    overflow: true,
                  }),
                  zIndex: 600
                });
              }
              if (featureType === 'contextZone') {
                return new Style({
                  stroke: new Stroke({
                    color: '#9c27b0',
                    width: 3,
                  }),
                  fill: new Fill({ color: 'rgba(156, 39, 176, 0.25)' }),
                  text: new Text({
                    text: `${name}\n${commodity}`,
                    font: 'bold 12px sans-serif',
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 500
                });
              }
              return new Style({
                stroke: new Stroke({
                  color: '#0ea5e9',
                  width: 3,
                }),
                fill: new Fill({ color: 'rgba(14, 165, 233, 0.2)' }),
                text: new Text({
                  text: `${name}\n${commodity} | MUST STAKE`,
                  font: 'bold 12px sans-serif',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 500
              });
            },
            zIndex: 160,
          });
          finlandKoitelainenStakingLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koitelainen staking:', err));
    } else {
      finlandKoitelainenStakingLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoitelainenStakingVisible]);

  // Finland Peräkorpi Ti Province Staking Polygon (10,768 ha Ti-P-Fe)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandPerakorpiTiStakingLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandPerakorpiTiStakingVisible) {
      finlandPerakorpiTiStakingLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandPerakorpiTiStakingLayerRef.current) {
        finlandPerakorpiTiStakingLayerRef.current.setVisible(false);
      }
    };
    if (!finlandPerakorpiTiStakingLayerRef.current) {
      fetch('/data/finland/perakorpi_ti_staking.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const name = feature.get('name') || 'PERÄKORPI TI';
              const commodity = feature.get('commodity') || '';
              const featureType = feature.get('featureType');
              const featureColor = feature.get('color') || '#00bfa5';
              if (featureType === 'stakingArea') {
                const hex = featureColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const tier = feature.get('tier') || '';
                return new Style({
                  stroke: new Stroke({
                    color: featureColor,
                    width: 3,
                    lineDash: [10, 6],
                  }),
                  fill: new Fill({ color: `rgba(${r}, ${g}, ${b}, 0.12)` }),
                  text: new Text({
                    text: `${name}\n${tier}`,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: featureColor }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 550
                });
              }
              if (featureType === 'marker') {
                return new Style({
                  image: new RegularShape({
                    points: 4,
                    radius: 10,
                    angle: Math.PI / 4,
                    fill: new Fill({ color: featureColor }),
                    stroke: new Stroke({ color: '#fff', width: 2 }),
                  }),
                  text: new Text({
                    text: `${name}\n${commodity}`,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: featureColor }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -18,
                    overflow: true,
                  }),
                  zIndex: 600
                });
              }
              return new Style({
                stroke: new Stroke({ color: '#00bfa5', width: 3 }),
                fill: new Fill({ color: 'rgba(0, 191, 165, 0.25)' }),
                text: new Text({
                  text: `${name}\n${commodity}`,
                  font: 'bold 12px sans-serif',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 500
              });
            },
            zIndex: 160,
          });
          finlandPerakorpiTiStakingLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Peräkorpi Ti staking:', err));
    } else {
      finlandPerakorpiTiStakingLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandPerakorpiTiStakingVisible]);

  // Finland Mäkärä Proposed Staking Polygon (4,022 ha Au — Lapland)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandMakaraStakingLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMakaraStakingVisible) {
      finlandMakaraStakingLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandMakaraStakingLayerRef.current) {
        finlandMakaraStakingLayerRef.current.setVisible(false);
      }
    };
    if (!finlandMakaraStakingLayerRef.current) {
      fetch('/data/finland/makara_staking_polygon.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const name = feature.get('name') || 'MÄKÄRÄ';
              const areaHa = feature.get('area_ha');
              const drillCollars = feature.get('drill_collars_inside');
              const labelBits: string[] = [];
              if (areaHa) labelBits.push(`${Number(areaHa).toLocaleString()} ha`);
              if (drillCollars != null) labelBits.push(`${drillCollars} collars`);
              const subLabel = labelBits.join(' | ');
              return new Style({
                stroke: new Stroke({
                  color: '#22c55e',
                  width: 3,
                  lineDash: [10, 6],
                }),
                fill: new Fill({ color: 'rgba(34, 197, 94, 0.16)' }),
                text: new Text({
                  text: subLabel ? `${name}\n${subLabel}` : String(name),
                  font: 'bold 12px sans-serif',
                  fill: new Fill({ color: '#22c55e' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 500
              });
            },
            zIndex: 160,
          });
          finlandMakaraStakingLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Mäkärä staking polygon:', err));
    } else {
      finlandMakaraStakingLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandMakaraStakingVisible]);

  // Finland Koivuniemi Graphite AOI (title-gated Phase 0 target)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoivuniemiGraphiteLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoivuniemiGraphiteVisible) {
      finlandKoivuniemiGraphiteLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoivuniemiGraphiteLayerRef.current) {
        finlandKoivuniemiGraphiteLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoivuniemiGraphiteLayerRef.current) {
      fetch('/data/finland/koivuniemi_graphite_source_aoi.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const featureType = feature.get('featureType');
              const name = feature.get('name') || 'KOIVUNIEMI';
              if (featureType === 'postKarenssiFilingV3') {
                const areaHa = feature.get('area_ha');
                return new Style({
                  stroke: new Stroke({
                    color: 'rgba(148, 163, 184, 0.72)',
                    width: 2.4,
                    lineDash: [8, 5],
                  }),
                  fill: new Fill({ color: 'rgba(148, 163, 184, 0.035)' }),
                  text: new Text({
                    text: `${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}conservative sub-option`,
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: '#cbd5e1' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 555
                });
              }
              if (featureType === 'postKarenssiFiling' || featureType === 'postKarenssiFilingV2') {
                const areaHa = feature.get('area_ha');
                const superseded = featureType === 'postKarenssiFilingV2';
                return new Style({
                  stroke: new Stroke({
                    color: superseded ? 'rgba(56, 189, 248, 0.5)' : '#22d3ee',
                    width: superseded ? 1.8 : 3.4,
                    lineDash: superseded ? [5, 5] : [12, 5, 3, 5],
                  }),
                  fill: new Fill({ color: superseded ? 'rgba(56, 189, 248, 0.025)' : 'rgba(34, 211, 238, 0.09)' }),
                  text: superseded ? undefined : new Text({
                    text: `${name}\n${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}file on karenssi expiry`,
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: '#67e8f9' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: superseded ? 552 : 575
                });
              }
              if (featureType === 'postKarenssiSendReadyExpanded') {
                const areaHa = feature.get('area_ha');
                return new Style({
                  stroke: new Stroke({
                    color: '#22c55e',
                    width: 4,
                    lineDash: [14, 5, 4, 5],
                  }),
                  fill: new Fill({ color: 'rgba(34, 197, 94, 0.105)' }),
                  text: new Text({
                    text: `${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}SEND-READY expanded filing`,
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color: '#86efac' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 582
                });
              }
              if (featureType === 'postKarenssiExpandedOption') {
                const areaHa = feature.get('area_ha');
                return new Style({
                  stroke: new Stroke({
                    color: 'rgba(20, 184, 166, 0.5)',
                    width: 1.6,
                    lineDash: [6, 6],
                  }),
                  fill: new Fill({ color: 'rgba(20, 184, 166, 0.03)' }),
                  text: new Text({
                    text: `${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}superseded expanded envelope`,
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color: '#5eead4' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 552
                });
              }
              if (featureType === 'sourceAoi') {
                const areaHa = feature.get('area_ha');
                const overlapPct = feature.get('karenssi_overlap_pct');
                const labelBits: string[] = [];
                if (areaHa) labelBits.push(`${Number(areaHa).toFixed(0)} ha`);
                if (overlapPct != null) labelBits.push(`${Number(overlapPct).toFixed(0)}% karenssi`);
                return new Style({
                  stroke: new Stroke({
                    color: '#2dd4bf',
                    width: 3,
                    lineDash: [8, 5],
                  }),
                  fill: new Fill({ color: 'rgba(45, 212, 191, 0.14)' }),
                  text: new Text({
                    text: `${name}\n${labelBits.join(' | ')}`,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: '#2dd4bf' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 560
                });
              }
              if (featureType === 'openFallback') {
                const areaHa = feature.get('area_ha');
                return new Style({
                  stroke: new Stroke({
                    color: '#22c55e',
                    width: 3,
                  }),
                  fill: new Fill({ color: 'rgba(34, 197, 94, 0.18)' }),
                  text: new Text({
                    text: `${name}\n${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}open fallback`,
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: '#22c55e' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 570
                });
              }
              if (featureType === 'geophysicsMagnetic' || featureType === 'geophysicsMaxmin') {
                const isMaxmin = featureType === 'geophysicsMaxmin';
                const color = isMaxmin ? '#a78bfa' : '#fb7185';
                const fillColor = isMaxmin ? 'rgba(167, 139, 250, 0.14)' : 'rgba(251, 113, 133, 0.12)';
                const methodLabel = isMaxmin ? '2017 MaxMin/slingram' : '2017 ground mag';
                return new Style({
                  stroke: new Stroke({
                    color,
                    width: isMaxmin ? 2.5 : 2,
                    lineDash: isMaxmin ? [6, 4] : [3, 4],
                  }),
                  fill: new Fill({ color: fillColor }),
                  text: new Text({
                    text: methodLabel,
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: isMaxmin ? 565 : 564
                });
              }
              if (featureType === 'aeroFlightLine') {
                return new Style({
                  stroke: new Stroke({
                    color: 'rgba(96, 165, 250, 0.85)',
                    width: 1.3,
                    lineDash: [5, 5],
                  }),
                  zIndex: 563
                });
              }
              if (featureType === 'highGradeIntercept') {
                const label = feature.get('label') || name;
                const offsetX = Number(feature.get('label_offset_x') || 0);
                const offsetY = Number(feature.get('label_offset_y') || -24);
                return new Style({
                  image: new RegularShape({
                    points: 5,
                    radius: 9,
                    radius2: 4,
                    angle: Math.PI,
                    fill: new Fill({ color: '#ff3d00' }),
                    stroke: new Stroke({ color: '#fff7ed', width: 1.7 }),
                  }),
                  text: new Text({
                    text: String(label),
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color: '#ffb703' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetX,
                    offsetY,
                    overflow: true,
                  }),
                  zIndex: 650
                });
              }
              if (featureType === 'koivuniemiCollar' || featureType === 'districtCollar') {
                const priority = Boolean(feature.get('priority'));
                const isDistrict = featureType === 'districtCollar';
                const fillColor = priority ? '#facc15' : isDistrict ? 'rgba(148, 163, 184, 0.85)' : '#2dd4bf';
                const strokeColor = priority ? '#7c5c00' : isDistrict ? '#475569' : '#0f766e';
                return new Style({
                  image: new CircleStyle({
                    radius: priority ? 6 : 4,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: strokeColor, width: priority ? 1.5 : 1 }),
                  }),
                  text: priority ? new Text({
                    text: String(name),
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color: '#facc15' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -14,
                    overflow: true,
                  }) : undefined,
                  zIndex: priority ? 620 : isDistrict ? 600 : 610
                });
              }
              return new Style({
                stroke: new Stroke({ color: '#2dd4bf', width: 2 }),
                fill: new Fill({ color: 'rgba(45, 212, 191, 0.12)' }),
                zIndex: 550
              });
            },
            zIndex: 166,
          });
          finlandKoivuniemiGraphiteLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koivuniemi graphite AOI:', err));
    } else {
      finlandKoivuniemiGraphiteLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoivuniemiGraphiteVisible]);

  // Finland Koivuniemi 5/20/26 consultant original filing polygon
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoivuniemiConsultantOriginalLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoivuniemiConsultantOriginalVisible) {
      finlandKoivuniemiConsultantOriginalLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoivuniemiConsultantOriginalLayerRef.current) {
        finlandKoivuniemiConsultantOriginalLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoivuniemiConsultantOriginalLayerRef.current) {
      fetch('/data/finland/koivuniemi_consultant_original_2026_05_20.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const name = feature.get('name') || '5/20/26 Consultant Original';
              const areaHa = feature.get('area_ha');
              return new Style({
                stroke: new Stroke({
                  color: '#94a3b8',
                  width: 3.2,
                  lineDash: [9, 5],
                }),
                fill: new Fill({ color: 'rgba(148, 163, 184, 0.08)' }),
                text: new Text({
                  text: `${name}\n${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}consultant original`,
                  font: 'bold 9px sans-serif',
                  fill: new Fill({ color: '#cbd5e1' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 584
              });
            },
            zIndex: 167,
          });
          finlandKoivuniemiConsultantOriginalLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koivuniemi consultant original polygon:', err));
    } else {
      finlandKoivuniemiConsultantOriginalLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoivuniemiConsultantOriginalVisible]);

  // Finland Koivuniemi 5/22/26 Geomorphic consultant-modified filing polygon
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoivuniemiConsultantModLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoivuniemiConsultantModVisible) {
      finlandKoivuniemiConsultantModLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoivuniemiConsultantModLayerRef.current) {
        finlandKoivuniemiConsultantModLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoivuniemiConsultantModLayerRef.current) {
      fetch('/data/finland/koivuniemi_consultant_mod_2026_05_22.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const name = feature.get('name') || '5/22/26 Geomorphic Consultant Mod';
              const areaHa = feature.get('area_ha');
              const offsetHoles = feature.get('offset_holes');
              return new Style({
                stroke: new Stroke({
                  color: '#38bdf8',
                  width: 3.8,
                  lineDash: [10, 4, 2, 4],
                }),
                fill: new Fill({ color: 'rgba(56, 189, 248, 0.11)' }),
                text: new Text({
                  text: `${name}\n${areaHa ? `${Number(areaHa).toFixed(0)} ha | ` : ''}${offsetHoles || 'PH-7/PH-17'} offset`,
                  font: 'bold 9px sans-serif',
                  fill: new Fill({ color: '#7dd3fc' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 586
              });
            },
            zIndex: 168,
          });
          finlandKoivuniemiConsultantModLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koivuniemi consultant mod polygon:', err));
    } else {
      finlandKoivuniemiConsultantModLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoivuniemiConsultantModVisible]);

  // Finland Koivuniemi VA2026:0039-01 registered (granted) reservation geometry
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoivuniemiRegisteredLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoivuniemiRegisteredVisible) {
      finlandKoivuniemiRegisteredLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoivuniemiRegisteredLayerRef.current) {
        finlandKoivuniemiRegisteredLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoivuniemiRegisteredLayerRef.current) {
      fetch('/data/finland/koivuniemi_va2026_0039_registered.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const areaHa = feature.get('area_ha');
              return new Style({
                stroke: new Stroke({
                  color: '#22c55e',
                  width: 4,
                }),
                fill: new Fill({ color: 'rgba(34, 197, 94, 0.08)' }),
                text: new Text({
                  text: `VA2026:0039-01 GRANTED 2026-06-17\n${areaHa ? `${Number(areaHa).toFixed(2)} ha | ` : ''}valid to 2027-05-31 | 40/44 collars`,
                  font: 'bold 9px sans-serif',
                  fill: new Fill({ color: '#86efac' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 587
              });
            },
            zIndex: 169,
          });
          finlandKoivuniemiRegisteredLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koivuniemi registered reservation polygon:', err));
    } else {
      finlandKoivuniemiRegisteredLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoivuniemiRegisteredVisible]);

  // Finland Koivuniemi camp drill collars vs registered geometry
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKoivuniemiDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKoivuniemiDrillCollarsVisible) {
      finlandKoivuniemiDrillCollarsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKoivuniemiDrillCollarsLayerRef.current) {
        finlandKoivuniemiDrillCollarsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKoivuniemiDrillCollarsLayerRef.current) {
      fetch('/data/finland/koivuniemi_drill_collars.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const holeId = feature.get('hole_id') || '';
              const camp = feature.get('camp_44_hole');
              const outside = feature.get('registered_relation') !== 'INSIDE';
              const fillColor = !camp ? '#64748b' : outside ? '#ef4444' : '#22c55e';
              return new Style({
                image: new CircleStyle({
                  radius: camp ? 5 : 3.5,
                  fill: new Fill({ color: fillColor }),
                  stroke: new Stroke({ color: outside && camp ? '#fecaca' : '#000', width: outside && camp ? 2 : 1.2 }),
                }),
                text: camp ? new Text({
                  text: holeId.replace(/^N444201[78]/, ''),
                  font: `bold 8px monospace`,
                  fill: new Fill({ color: outside ? '#fca5a5' : '#bbf7d0' }),
                  stroke: new Stroke({ color: '#000', width: 2.5 }),
                  offsetY: -11,
                }) : undefined,
                zIndex: outside && camp ? 592 : 590
              });
            },
            zIndex: 172,
          });
          finlandKoivuniemiDrillCollarsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Koivuniemi drill collars:', err));
    } else {
      finlandKoivuniemiDrillCollarsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKoivuniemiDrillCollarsVisible]);

  // Finland Kokkoharju-Antinoja Au-Cu review polygon (title-watch Phase 0)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKokkoharjuReviewLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKokkoharjuReviewVisible) {
      finlandKokkoharjuReviewLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKokkoharjuReviewLayerRef.current) {
        finlandKokkoharjuReviewLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKokkoharjuReviewLayerRef.current) {
      fetch('/data/finland/kokkoharju_antinoja_review.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const featureType = feature.get('featureType');
              const name = feature.get('name') || '';
              if (featureType === 'reviewPolygon') {
                const areaHa = Number(feature.get('area_ha') || 0);
                return new Style({
                  stroke: new Stroke({ color: 'rgba(229, 231, 235, 0.45)', width: 1.5, lineDash: [9, 5] }),
                  fill: new Fill({ color: 'rgba(148, 163, 184, 0.02)' }),
                  text: new Text({
                    text: `Kokkoharju-Antinoja\n${areaHa.toFixed(0)} ha | title-watch`,
                    font: 'bold 10px sans-serif',
                    fill: new Fill({ color: 'rgba(229, 231, 235, 0.75)' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 570
                });
              }
              if (featureType === 'openFallback') {
                const areaHa = Number(feature.get('area_ha') || 0);
                const exteriorOnly = (renderFeature: FeatureLike) => {
                  const geometry = renderFeature.getGeometry();
                  if (!geometry) return geometry;
                  if (geometry.getType() === 'Polygon') {
                    return new LineString((geometry as any).getCoordinates()[0]);
                  }
                  if (geometry.getType() === 'MultiPolygon') {
                    return new MultiLineString((geometry as any).getCoordinates().map((coords: any[]) => coords[0]));
                  }
                  return geometry;
                };
                return [
                  new Style({
                    fill: new Fill({ color: 'rgba(34, 197, 94, 0.04)' }),
                    text: new Text({
                      text: `${areaHa.toFixed(0)} ha fallback`,
                      font: 'bold 9px sans-serif',
                      fill: new Fill({ color: 'rgba(134, 239, 172, 0.75)' }),
                      stroke: new Stroke({ color: '#000', width: 3 }),
                      overflow: true,
                      placement: 'point'
                    }),
                    zIndex: 574
                  }),
                  new Style({
                    geometry: exteriorOnly,
                    stroke: new Stroke({ color: 'rgba(34, 197, 94, 0.55)', width: 1.5, lineDash: [5, 3] }),
                    zIndex: 575
                  })
                ];
              }
              if (featureType === 'consultantOverridePolygon') {
                const areaHa = Number(feature.get('area_ha') || 0);
                const interceptsInside = feature.get('source_au_intercepts_inside');
                const southExtensionInside = feature.get('south_extension_collars_inside');
                return [
                  new Style({
                    fill: new Fill({ color: 'rgba(14, 165, 233, 0.055)' }),
                    zIndex: 578
                  }),
                  new Style({
                    stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.95)', width: 7 }),
                    zIndex: 879
                  }),
	                  new Style({
	                    stroke: new Stroke({ color: '#0ea5e9', width: 4.5 }),
	                    text: new Text({
	                      text: `BROAD CONSULTANT POLYGON\n${areaHa.toFixed(0)} ha | title gates | ${interceptsInside}/20 Au + ${southExtensionInside}/4 south holes`,
	                      font: 'bold 10px sans-serif',
	                      fill: new Fill({ color: '#bae6fd' }),
	                      stroke: new Stroke({ color: '#000', width: 4 }),
	                      overflow: true,
                      placement: 'point'
                    }),
                    zIndex: 880
	                  })
	                ];
	              }
	              if (featureType === 'extensionCollar') {
	                return new Style({
	                  image: new RegularShape({
	                    points: 4,
	                    radius: 6.5,
	                    angle: Math.PI / 4,
	                    fill: new Fill({ color: '#ec4899' }),
	                    stroke: new Stroke({ color: '#111827', width: 1.5 }),
	                  }),
	                  text: new Text({
	                    text: String(name),
	                    font: 'bold 8px sans-serif',
	                    fill: new Fill({ color: '#f9a8d4' }),
	                    stroke: new Stroke({ color: '#000', width: 3 }),
	                    offsetY: -13,
	                    overflow: true,
	                  }),
	                  zIndex: 650
	                });
	              }
	              if (featureType === 'geophysicsFootprint') {
                const method = String(feature.get('method') || '').toLowerCase();
                const color = method === 'ip' ? '#a855f7' : method === 'slingram' ? '#38bdf8' : '#f43f5e';
                return new Style({
                  stroke: new Stroke({ color, width: 1.8, lineDash: [6, 4] }),
                  fill: new Fill({ color: 'rgba(15, 23, 42, 0.03)' }),
                  text: new Text({
                    text: String(feature.get('method') || ''),
                    font: 'bold 8px sans-serif',
                    fill: new Fill({ color }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 565
                });
              }
              if (featureType === 'lakeudenSoilAnomaly') {
                const au = Number(feature.get('Au_ppb') || 0);
                const cu = Number(feature.get('Cu_ppb') || 0);
                const radius = au >= 5 || cu >= 5000 ? 6 : 4;
                return new Style({
                  image: new RegularShape({
                    points: 3,
                    radius,
                    rotation: Math.PI,
                    fill: new Fill({ color: au >= 5 ? '#facc15' : '#fb923c' }),
                    stroke: new Stroke({ color: '#7c2d12', width: 1.2 }),
                  }),
                  zIndex: 625
                });
              }
              if (featureType === 'kokkoharjuCollar' || featureType === 'antinojaCollar') {
                const grade = Number(feature.get('best_table3_au_gpt') || 0);
                const isKokkoharju = featureType === 'kokkoharjuCollar';
                const fillColor = grade >= 3 ? '#facc15' : grade > 0 ? '#fb923c' : isKokkoharju ? '#fde68a' : '#67e8f9';
                return new Style({
                  image: new CircleStyle({
                    radius: grade >= 3 ? 6 : grade > 0 ? 5 : 3.5,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: isKokkoharju ? '#78350f' : '#155e75', width: 1 }),
                  }),
                  text: grade >= 3 ? new Text({
                    text: String(name),
                    font: 'bold 8px sans-serif',
                    fill: new Fill({ color: '#facc15' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -13,
                    overflow: true,
                  }) : undefined,
                  zIndex: grade >= 3 ? 640 : 610
                });
              }
              return new Style({
                stroke: new Stroke({ color: '#e5e7eb', width: 2 }),
                fill: new Fill({ color: 'rgba(148, 163, 184, 0.08)' }),
                zIndex: 560
              });
            },
            zIndex: 230,
          });
          finlandKokkoharjuReviewLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Kokkoharju-Antinoja review layer:', err));
    } else {
      finlandKokkoharjuReviewLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKokkoharjuReviewVisible]);

  // Finland Kultanummi Au source polygon (screening-open Phase 0 filing test)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandKultanummiGoldLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandKultanummiGoldVisible) {
      finlandKultanummiGoldLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandKultanummiGoldLayerRef.current) {
        finlandKultanummiGoldLayerRef.current.setVisible(false);
      }
    };
    if (!finlandKultanummiGoldLayerRef.current) {
      fetch('/data/finland/kultanummi_gold_source_aoi.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const featureType = String(feature.get('featureType') || '');
              const name = String(feature.get('name') || 'Kultanummi');
              if (featureType === 'kultanummiPolygon') {
                const areaHa = Number(feature.get('area_ha') || 0);
                return [
                  new Style({
                    fill: new Fill({ color: 'rgba(14, 165, 233, 0.055)' }),
                    text: new Text({
                      text: `KULTANUMMI AU POLYGON\n${areaHa.toLocaleString(undefined, { maximumFractionDigits: 0 })} ha | screening-open | 31 collars`,
                      font: 'bold 10px sans-serif',
                      fill: new Fill({ color: '#e0f2fe' }),
                      stroke: new Stroke({ color: '#000', width: 4 }),
                      overflow: true,
                      placement: 'point'
                    }),
                    zIndex: 586
                  }),
                  new Style({
                    stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.96)', width: 7.5 }),
                    zIndex: 886
                  }),
                  new Style({
                    stroke: new Stroke({ color: '#38bdf8', width: 4.2, lineDash: [14, 5, 4, 5] }),
                    zIndex: 887
                  })
                ];
              }
              if (featureType === 'kultanummiGeophysicsFootprint') {
                const method = String(feature.get('method') || '').toLowerCase();
                const color = method === 'ip' ? '#a855f7' : method === 'slingram' ? '#22c55e' : '#60a5fa';
                const label = method === 'ip' ? 'IP' : method === 'slingram' ? 'slingram' : 'mag';
                return new Style({
                  stroke: new Stroke({ color, width: 1.8, lineDash: [7, 5] }),
                  fill: new Fill({ color: hexToRgba(color, 0.055) }),
                  text: new Text({
                    text: label,
                    font: 'bold 8px sans-serif',
                    fill: new Fill({ color }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    overflow: true,
                    placement: 'point'
                  }),
                  zIndex: 566
                });
              }
              if (featureType === 'kultanummiFoddOccurrence') {
                return new Style({
                  image: new RegularShape({
                    points: 4,
                    radius: 8,
                    angle: Math.PI / 4,
                    fill: new Fill({ color: '#fef08a' }),
                    stroke: new Stroke({ color: '#111827', width: 1.8 }),
                  }),
                  text: new Text({
                    text: name,
                    font: 'bold 9px sans-serif',
                    fill: new Fill({ color: '#fef08a' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -15,
                    overflow: true,
                  }),
                  zIndex: 645
                });
              }
              if (featureType === 'kultanummiCollar') {
                const best300 = Number(feature.get('best_300ppb_au_gpt') || 0);
                const best100 = Number(feature.get('best_100ppb_au_gpt') || 0);
                const topSample = Number(feature.get('top_sample_au_gpt') || 0);
                const displayGrade = best300 || best100;
                const label = String(feature.get('label') || feature.get('rnum') || name);
                const shouldLabel = topSample >= 1 || best300 >= 0.8 || ['R400', 'R399', 'R404', 'R388'].some(id => label.includes(id));
                const highGrade = topSample >= 2 || best300 >= 1;
                const fillColor = highGrade ? '#fef08a' : best300 >= 0.3 ? '#38bdf8' : best100 >= 0.1 ? '#93c5fd' : 'rgba(148, 163, 184, 0.65)';
                const strokeColor = highGrade ? '#713f12' : best300 >= 0.3 ? '#075985' : '#334155';
                const radius = highGrade ? 7 : displayGrade >= 0.3 ? 5.5 : 3.8;
                return new Style({
                  image: highGrade ? new RegularShape({
                    points: 5,
                    radius,
                    radius2: 3.6,
                    angle: Math.PI,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: strokeColor, width: 1.4 }),
                  }) : new CircleStyle({
                    radius,
                    fill: new Fill({ color: fillColor }),
                    stroke: new Stroke({ color: strokeColor, width: 1.2 }),
                  }),
                  text: shouldLabel ? new Text({
                    text: label,
                    font: 'bold 8px sans-serif',
                    fill: new Fill({ color: highGrade ? '#fef08a' : '#bfdbfe' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -14,
                    overflow: true,
                  }) : undefined,
                  zIndex: highGrade ? 655 : displayGrade >= 0.3 ? 630 : 610
                });
              }
              return new Style({
                stroke: new Stroke({ color: '#38bdf8', width: 2 }),
                fill: new Fill({ color: 'rgba(14, 165, 233, 0.08)' }),
                zIndex: 560
              });
            },
            zIndex: 232,
          });
          finlandKultanummiGoldLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Kultanummi Au source layer:', err));
    } else {
      finlandKultanummiGoldLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandKultanummiGoldVisible]);

  // Source GeoJSON project polygon overlays recovered from public/data.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const activeIds = new Set(projectGeoJsonLayersForProject.map(layer => layer.id));

    PROJECT_GEOJSON_POLYGON_LAYERS.forEach((layerDef) => {
      const layer = projectGeoJsonLayerRefs.current.get(layerDef.id);
      if (layer && (!mapInstance || !activeIds.has(layerDef.id) || !projectGeoJsonVisibility[layerDef.id])) {
        layer.setVisible(false);
      }
    });

    if (!mapInstance) {
      return;
    }

    const labelOwnerIds = new Set<string>();
    const labelGroups = new Set<string>();
    const activeTargetLayerIdsByGroup = new globalThis.Map<string, string[]>();
    projectGeoJsonLayersForProject.forEach((layerDef) => {
      if (!projectGeoJsonVisibility[layerDef.id]) {
        return;
      }
      const labelGroup = layerDef.targetArea || layerDef.id;
      const targetLayerIds = activeTargetLayerIdsByGroup.get(labelGroup) || [];
      targetLayerIds.push(layerDef.id);
      activeTargetLayerIdsByGroup.set(labelGroup, targetLayerIds);
      if (labelGroups.has(labelGroup)) {
        return;
      }
      labelGroups.add(labelGroup);
      labelOwnerIds.add(layerDef.id);
    });

    projectGeoJsonLayersForProject.forEach((layerDef) => {
      const visible = Boolean(projectGeoJsonVisibility[layerDef.id]);
      const existing = projectGeoJsonLayerRefs.current.get(layerDef.id);
      if (!visible) {
        existing?.setVisible(false);
        return;
      }
      const displayColor = projectGeoJsonDisplayColorById[layerDef.id] || layerDef.color;
      const layerLabel = labelOwnerIds.has(layerDef.id) ? (layerDef.targetArea || layerDef.label) : '';
      const labelGroup = layerDef.targetArea || layerDef.id;
      const activeSiblingIds = activeTargetLayerIdsByGroup.get(labelGroup) || [layerDef.id];
      const activeSiblingCount = activeSiblingIds.length;
      const activeSiblingIndex = Math.max(0, activeSiblingIds.indexOf(layerDef.id));
      const isPointOnlyProjectLayer = (features: any[]) =>
        features.length >= 12 &&
        features.every((feature: any) => {
          const type = feature.getGeometry?.()?.getType?.();
          return type === 'Point' || type === 'MultiPoint';
        });
      const applyProjectLayerInspectionMetadata = (features: any[]) => {
        features.forEach((feature: any) => {
          feature.set('__geomorphicLayerId', layerDef.id, true);
          feature.set('__geomorphicLayerLabel', layerDef.label, true);
          feature.set('__geomorphicLayerGroup', layerDef.group || 'Targets', true);
          feature.set('__geomorphicLayerSource', layerDef.sourceLabel || layerDef.path, true);
          feature.set('__geomorphicPortfolioStage', layerDef.portfolioStage || 'Needs review', true);
          feature.set('__geomorphicConfidence', layerDef.confidence || 'Medium', true);
          feature.set('__geomorphicNextAction', layerDef.nextAction || '', true);
          feature.set('targetArea', layerDef.targetArea || '', true);
          feature.set('portfolio_status', layerDef.portfolioStage || 'Needs review', true);
        });
      };
      const makeProjectGeoJsonStyle = () => (feature: any) => {
        const zoom = mapInstanceRef.current?.getView()?.getZoom?.() || currentZoomLevelRef.current;
        const clusterMembers = getClusterMemberFeatures(feature);
        if (clusterMembers.length > 1) {
          return createProjectPointClusterStyle(clusterMembers, layerDef.id, displayColor, zoom);
        }
        const renderFeature = clusterMembers.length === 1 ? clusterMembers[0] : feature;
        const geometryType = renderFeature.getGeometry()?.getType?.() || '';
        const shouldShowLabel = Boolean(renderFeature.get(PROJECT_LAYER_LABEL_FEATURE_KEY));
        const label = shouldShowLabel
          ? getCompactProjectLayerLabel(renderFeature.get(PROJECT_LAYER_LABEL_TEXT_KEY) || layerDef.label)
          : '';
        const isPolygonGeometry = geometryType.includes('Polygon');
        if (projectGeoJsonPolygonOnlyRef.current && !isPolygonGeometry) {
          return [];
        }
        if (geometryType.includes('Point')) {
          const markerStyles = createProjectPointFeatureStyle(layerDef.id, displayColor, layerDef.pointSymbol);
          const labelStyle = shouldShowLabel
            ? createProjectLayerLabelStyle(renderFeature, label, displayColor, zoom, Boolean(layerDef.targetArea), layerDef.pointLabelMinZoom, Boolean(layerDef.pointLabelField))
            : undefined;
          return labelStyle ? [...markerStyles, labelStyle] : markerStyles;
        }

        const multiLayerDashPatterns: Array<number[] | undefined> = [
          [14, 4],
          undefined,
          [3, 4],
          [10, 3, 2, 3],
          [2, 3, 2, 7],
        ];
        const lineDash = activeSiblingCount > 1
          ? multiLayerDashPatterns[activeSiblingIndex % multiLayerDashPatterns.length]
          : layerDef.dashed ? [8, 5] : undefined;
        const strokeWidth = activeSiblingCount > 1 ? 2.7 + (activeSiblingIndex % 3) * 0.35 : 2.4;
        const zIndex = 580 + activeSiblingIndex * 4;
        const polygonCasingStyle = new Style({
          stroke: new Stroke({
            color: 'rgba(2, 6, 23, 0.78)',
            width: strokeWidth + 2,
            lineDash,
          }),
          zIndex: zIndex - 1
        });
        const polygonStyle = new Style({
          stroke: new Stroke({
            color: displayColor,
            width: strokeWidth,
            lineDash,
          }),
          fill: new Fill({ color: hexToRgba(displayColor, activeSiblingCount > 1 ? 0.075 : 0.13) }),
          zIndex
        });
        const labelStyle = shouldShowLabel ? createProjectLayerLabelStyle(feature, label, displayColor, zoom, Boolean(layerDef.targetArea)) : undefined;
        return labelStyle ? [polygonCasingStyle, polygonStyle, labelStyle] : [polygonCasingStyle, polygonStyle];
      };
      if (existing) {
        const existingFeatures = getRawLayerSource(existing)?.getFeatures() || [];
        if (layerDef.pointLabelField) {
          markProjectPointLabelFeatures(existingFeatures, layerDef.pointLabelField);
        } else {
          markProjectLayerLabelFeature(existingFeatures, layerLabel);
        }
        applyProjectLayerInspectionMetadata(existingFeatures);
        existing.set('geomorphicLayerColor', displayColor);
        existing.setStyle(makeProjectGeoJsonStyle());
        existing.setVisible(true);
        existing.changed();
          return;
      }

      fetch(layerDef.path)
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          if (layerDef.pointLabelField) {
            markProjectPointLabelFeatures(features, layerDef.pointLabelField);
          } else {
            markProjectLayerLabelFeature(features, layerLabel);
          }
          applyProjectLayerInspectionMetadata(features);
          const source = new VectorSource({ features });
          const shouldClusterPoints = isPointOnlyProjectLayer(features) && !layerDef.pointLabelField;
          const clusterSource = shouldClusterPoints
            ? new Cluster({
                distance: getProjectGeoJsonPointClusterDistance(currentZoomLevelRef.current),
                geometryFunction: (feature) => {
                  const geometry = feature.getGeometry();
                  if (geometry instanceof Point) {
                    return geometry;
                  }
                  if (geometry?.getType?.() === 'MultiPoint') {
                    const coordinates = (geometry as any).getCoordinates?.();
                    return Array.isArray(coordinates) && coordinates[0] ? new Point(coordinates[0]) : null;
                  }
                  return null;
                },
                minDistance: 4,
                source,
              })
            : null;
          const layer = new VectorLayer({
            source: clusterSource || source,
            declutter: layerDef.pointLabelField ? false : true,
            style: makeProjectGeoJsonStyle(),
            zIndex: 168,
          });
          layer.set('geomorphicRawSource', source);
          if (clusterSource) {
            layer.set('geomorphicClusterSource', clusterSource);
            layer.set('geomorphicClusterDistanceFn', getProjectGeoJsonPointClusterDistance);
          }
          layer.set('geomorphicLayerId', layerDef.id);
          layer.set('geomorphicLayerLabel', layerDef.label);
          layer.set('geomorphicLayerSource', layerDef.sourceLabel || layerDef.path);
          layer.set('geomorphicLayerColor', displayColor);
          projectGeoJsonLayerRefs.current.set(layerDef.id, layer);
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error(`Failed to load project GeoJSON polygon ${layerDef.path}:`, err));
    });
  }, [selectedProject?.id, projectGeoJsonDisplayColorById, projectGeoJsonLayersForProject, projectGeoJsonVisibility]);

  // Finland Mäkärä Till Au Samples (178 samples, peak 10.9 g/t)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandMakaraTillAuLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandMakaraTillAuVisible) {
      finlandMakaraTillAuLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandMakaraTillAuLayerRef.current) {
        finlandMakaraTillAuLayerRef.current.setVisible(false);
      }
    };
    if (!finlandMakaraTillAuLayerRef.current) {
      fetch('/data/finland/makara_till_au_samples.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const auRaw = feature.get('au_ppb');
              const au = typeof auRaw === 'number' ? auRaw : parseFloat(auRaw);
              if (!isFinite(au) || au <= 0) {
                return new Style({
                  image: new CircleStyle({
                    radius: 3,
                    fill: new Fill({ color: 'rgba(160, 160, 160, 0.55)' }),
                    stroke: new Stroke({ color: '#555', width: 0.5 }),
                  }),
                  zIndex: 610,
                });
              }
              // Graduated radius: log-scaled so 10.9 g/t doesn't dwarf background samples.
              // log10(0.5)≈-0.3 → r≈3; log10(10)=1 → r≈6; log10(100)=2 → r≈9; log10(10900)≈4.04 → r≈15.
              const radius = Math.max(3, Math.min(16, 3 + 3 * Math.log10(au)));
              let fillColor: string;
              let strokeColor: string;
              let zIndex: number;
              if (au > 100) {
                fillColor = 'rgba(220, 30, 30, 0.80)';
                strokeColor = '#7a0000';
                zIndex = 640;
              } else if (au >= 10) {
                fillColor = 'rgba(255, 140, 0, 0.75)';
                strokeColor = '#8a4a00';
                zIndex = 630;
              } else {
                fillColor = 'rgba(160, 160, 160, 0.55)';
                strokeColor = '#555';
                zIndex = 620;
              }
              return new Style({
                image: new CircleStyle({
                  radius,
                  fill: new Fill({ color: fillColor }),
                  stroke: new Stroke({ color: strokeColor, width: 0.8 }),
                }),
                zIndex,
              });
            },
            zIndex: 165,
          });
          finlandMakaraTillAuLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Mäkärä till Au samples:', err));
    } else {
      finlandMakaraTillAuLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandMakaraTillAuVisible]);

  // Finland PGE Targets Bounding Boxes (Permit verification areas)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandPGETargetsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandPGETargetsVisible) {
      finlandPGETargetsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandPGETargetsLayerRef.current) {
        finlandPGETargetsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandPGETargetsLayerRef.current) {
      fetch('/data/finland/pge_targets.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const permitRisk = feature.get('permit_risk') || 'MEDIUM';
              const priority = feature.get('priority') || '';
              const name = feature.get('name') || '';
              const rank = feature.get('priority_rank') || 99;
              const pgeType = feature.get('pge_type') || '';

              // Color by permit risk (LOW risk = GREEN = likely open ground)
              let strokeColor = '#22d3ee'; // Default cyan
              let fillColor = 'rgba(34, 211, 238, 0.15)';
              let lineDash: number[] | undefined = undefined;

              if (permitRisk === 'LOW') {
                strokeColor = '#22c55e'; // Green for likely open
                fillColor = 'rgba(34, 197, 94, 0.18)';
              } else if (permitRisk === 'MEDIUM') {
                strokeColor = '#22d3ee'; // Cyan for possible gaps
                fillColor = 'rgba(34, 211, 238, 0.15)';
              } else if (permitRisk === 'HIGH') {
                strokeColor = '#3b82f6'; // Blue for mostly locked
                fillColor = 'rgba(59, 130, 246, 0.12)';
              } else if (permitRisk === 'BLOCKED') {
                strokeColor = '#94a3b8'; // Gray for fully locked
                fillColor = 'rgba(148, 163, 184, 0.08)';
                lineDash = [8, 4];
              }

              return new Style({
                stroke: new Stroke({
                  color: strokeColor,
                  width: 3,
                  lineDash: lineDash
                }),
                fill: new Fill({ color: fillColor }),
                text: new Text({
                  text: `${name}\n${pgeType} | ${priority}`,
                  font: 'bold 11px sans-serif',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  overflow: true,
                  placement: 'point'
                }),
                zIndex: 500 - rank
              });
            },
            zIndex: 400,
          });
          finlandPGETargetsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load PGE targets:', err));
    } else {
      finlandPGETargetsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandPGETargetsVisible]);

  // Finland Rock Samples (key high-grade samples)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandRockSamplesLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandRockSamplesVisible) {
      finlandRockSamplesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandRockSamplesLayerRef.current) {
        finlandRockSamplesLayerRef.current.setVisible(false);
      }
    };
    if (!finlandRockSamplesLayerRef.current) {
      fetch('/data/finland/rock_samples_key.geojson')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const cuPct = feature.get('cu_pct') || 0;
              const auGt = feature.get('au_gt') || 0;
              const name = feature.get('name') || '';
              // Diamond shape for rock samples
              // Color: red for Cu, gold for Au, magenta for both
              let color = '#00ffff'; // Cyan default
              if (cuPct > 1 && auGt > 1) color = '#ff00ff'; // Magenta for Cu-Au
              else if (cuPct > 1) color = '#ff0000'; // Red for high Cu
              else if (auGt > 1) color = '#ffd700'; // Gold for high Au

              const radius = cuPct > 5 ? 16 : cuPct > 1 ? 14 : 12;

              return new Style({
                image: new RegularShape({
                  points: 4,
                  radius: radius,
                  angle: Math.PI / 4, // Diamond orientation
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: '#000', width: 2 }),
                }),
                text: new Text({
                  text: cuPct > 0 ? `${cuPct.toFixed(1)}% Cu` : `${auGt.toFixed(1)} g/t Au`,
                  font: 'bold 10px sans-serif',
                  fill: new Fill({ color: '#fff' }),
                  stroke: new Stroke({ color: '#000', width: 3 }),
                  offsetY: -22,
                }),
                zIndex: 2000
              });
            },
            zIndex: 960,
          });
          finlandRockSamplesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load rock samples:', err));
    } else {
      finlandRockSamplesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandRockSamplesVisible]);

  // Finland Drillholes (33k historic holes)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandDrillholesLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandDrillholesVisible) {
      finlandDrillholesLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandDrillholesLayerRef.current) {
        finlandDrillholesLayerRef.current.setVisible(false);
      }
    };
    if (!finlandDrillholesLayerRef.current) {
      fetch('/data/finland/drillholes.geojson')
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const source = new VectorSource({ features });
          const styleCache: Record<string, Style> = {};

          const getDrillholeStyle = (hasAnalysis: boolean, radius: number): Style => {
            const key = `${hasAnalysis ? 'analysis' : 'no-analysis'}-${radius}`;
            const cached = styleCache[key];
            if (cached) return cached;

            const color = hasAnalysis ? 'rgba(56,189,248,0.96)' : 'rgba(148,163,184,0.92)';
            const style = new Style({
              image: new CircleStyle({
                radius,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#f8fafc', width: 1.15 }),
              }),
            });
            styleCache[key] = style;
            return style;
          };

          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const hasAnalysis = feature.get('ANALYSIS') === 'x';
              const length = Number(feature.get('HOLE_LENGTH')) || 0;
              const radius = length > 500 ? 6 : length > 100 ? 5 : 4;
              const markerStyle = getDrillholeStyle(hasAnalysis, radius);
              const zoom = mapInstanceRef.current?.getView()?.getZoom?.() || currentZoomLevelRef.current;
              const holeId = String(feature.get('HOLE_ID') || feature.get('DRILLHOLE') || '').trim();
              if (zoom < 11.5 || !holeId) {
                return markerStyle;
              }
              return [
                markerStyle,
                new Style({
                  text: new Text({
                    text: holeId,
                    font: `700 9px ${MAP_UI_MONO_FONT}`,
                    fill: new Fill({ color: '#e2e8f0' }),
                    stroke: new Stroke({ color: '#020617', width: 3 }),
                    offsetY: -12,
                    overflow: false,
                  }),
                  zIndex: 246,
                }),
              ];
            },
            declutter: true,
            zIndex: 240,
          });
          finlandDrillholesLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => {
          console.error('Failed to load drillholes:', err);
          setLayerLoadError(`Finland drillholes failed to load: ${err instanceof Error ? err.message : String(err)}`);
        });
    } else {
      finlandDrillholesLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandDrillholesVisible]);

  // Finland Drill Clusters (systematic open ground scan)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandDrillClustersVisible) {
      finlandDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandDrillClustersLayerRef.current) {
        finlandDrillClustersLayerRef.current.setVisible(false);
      }
    };
    if (!finlandDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=finland')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createDrillClusterReviewStyle
          );
          finlandDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => { console.error('Failed to load drill clusters:', err); setLayerLoadError('Drill clusters layer failed to load — check the backend'); });
    } else {
      finlandDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandDrillClustersVisible]);

  // Sweden Drill Clusters (parallel to Finland — backend reads _status.json since 2026-05-03)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      swedenDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!swedenDrillClustersVisible) {
      swedenDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (swedenDrillClustersLayerRef.current) {
        swedenDrillClustersLayerRef.current.setVisible(false);
      }
    };
    if (!swedenDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=sweden')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857'
          });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createDrillClusterReviewStyle
          );
          swedenDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => { console.error('Failed to load Sweden drill clusters:', err); setLayerLoadError('Sweden drill clusters layer failed to load — check the backend'); });
    } else {
      swedenDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, swedenDrillClustersVisible]);

  // Sweden Singletons — Tier 4 noise-tail screen (boreholes outside any cluster
  // buffer, has assays, mineral operator, not in active mine 5km buffer).
  // Distinct visual: orange diamond (RegularShape), distinct from cluster circles.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      swedenSingletonsLayerRef.current?.setVisible(false);
      return;
    }
    if (!swedenSingletonsVisible) {
      swedenSingletonsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (swedenSingletonsLayerRef.current) {
        swedenSingletonsLayerRef.current.setVisible(false);
      }
    };
    if (!swedenSingletonsLayerRef.current) {
      apiFetch('/api/drill-clusters/singletons-geojson?country=sweden')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, {
            featureProjection: 'EPSG:3857',
          });
          const source = new VectorSource({ features });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const score = Number(feature.get('score')) || 0;
              // Brighter orange = higher score
              const fillAlpha = 0.5 + Math.min(score / 12, 0.4);
              const radius = score >= 7 ? 8 : score >= 5 ? 7 : 6;
              return new Style({
                image: new RegularShape({
                  points: 4,
                  radius,
                  angle: Math.PI / 4,
                  fill: new Fill({ color: `rgba(255, 152, 0, ${fillAlpha})` }),
                  stroke: new Stroke({ color: '#e65100', width: 1.5 }),
                }),
              });
            },
            zIndex: 186,
          });
          swedenSingletonsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Sweden singletons:', err));
    } else {
      swedenSingletonsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, swedenSingletonsVisible]);

  // ──────────────────────────────────────────────────────────────────────────
  // Drill cluster layers for new countries (seeded from _status.json)
  // Pattern mirrors Sweden/Finland — same color/size logic, just different
  // country query and project-id gate. Each block:
  //   apiFetch('/api/drill-clusters/geojson?country=X')
  //   color by review_status + verdict (same legend)
  //   flat review dots with zoom-dependent clustering
  //   zIndex 185
  // ──────────────────────────────────────────────────────────────────────────

  // Norway Drill Clusters
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-norway-ngu') {
      norwayDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!norwayDrillClustersVisible) {
      norwayDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => norwayDrillClustersLayerRef.current?.setVisible(false);
    if (!norwayDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=norway')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          norwayDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Norway drill clusters:', err));
    } else {
      norwayDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, norwayDrillClustersVisible]);

  // Ireland Drill Clusters
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-ireland-gsi') {
      irelandDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!irelandDrillClustersVisible) {
      irelandDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => irelandDrillClustersLayerRef.current?.setVisible(false);
    if (!irelandDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=ireland')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          irelandDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Ireland drill clusters:', err));
    } else {
      irelandDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, irelandDrillClustersVisible]);

  // Peru Drill Clusters
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-peru-wide') {
      peruDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!peruDrillClustersVisible) {
      peruDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => peruDrillClustersLayerRef.current?.setVisible(false);
    if (!peruDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=peru')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          peruDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Peru drill clusters:', err));
    } else {
      peruDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, peruDrillClustersVisible]);

  // Ontario Drill Clusters
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-ontario') {
      ontarioDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!ontarioDrillClustersVisible) {
      ontarioDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => ontarioDrillClustersLayerRef.current?.setVisible(false);
    if (!ontarioDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=canada')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          ontarioDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Ontario drill clusters:', err));
    } else {
      ontarioDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, ontarioDrillClustersVisible]);

  // Australia (WA) Drill Clusters — 4,281 ≥50-hole clusters from MDHDB
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-australia-wa') {
      australiaDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!australiaDrillClustersVisible) {
      australiaDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => australiaDrillClustersLayerRef.current?.setVisible(false);
    if (!australiaDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=australia')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          australiaDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Australia drill clusters:', err));
    } else {
      australiaDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, australiaDrillClustersVisible]);

  // Québec Drill Clusters — 451 ≥50-hole clusters from SIGÉOM Diamond drilling SHP
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-canada-quebec') {
      quebecDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!quebecDrillClustersVisible) {
      quebecDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => quebecDrillClustersLayerRef.current?.setVisible(false);
    if (!quebecDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=quebec')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          quebecDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Québec drill clusters:', err));
    } else {
      quebecDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, quebecDrillClustersVisible]);

  // France Drill Clusters — 200 (120 reviewed Tier-1/2/monitor + 80 BSS-ranked pending)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-france-brgm') {
      franceDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!franceDrillClustersVisible) {
      franceDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => franceDrillClustersLayerRef.current?.setVisible(false);
    if (!franceDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=france')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          franceDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load France drill clusters:', err));
    } else {
      franceDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, franceDrillClustersVisible]);

  // Colombia Drill Clusters — 71 (16 reviewed + 55 open-fringe pending)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-colombia-sgc') {
      colombiaDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    if (!colombiaDrillClustersVisible) {
      colombiaDrillClustersLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => colombiaDrillClustersLayerRef.current?.setVisible(false);
    if (!colombiaDrillClustersLayerRef.current) {
      apiFetch('/api/drill-clusters/geojson?country=colombia')
        .then(res => res.json())
        .then(data => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          const layer = createDrillClusterVectorLayer(
            features,
            mapInstance.getView().getZoom() || currentZoomLevelRef.current,
            createLegacyDrillClusterReviewStyle
          );
          colombiaDrillClustersLayerRef.current = layer;
          mapInstance.addLayer(layer);
        })
        .catch(err => console.error('Failed to load Colombia drill clusters:', err));
    } else {
      colombiaDrillClustersLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, colombiaDrillClustersVisible]);

  // Finland FODD Deposits (470 Fennoscandian deposits)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandFoddDepositsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandFoddDepositsVisible) {
      finlandFoddDepositsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandFoddDepositsLayerRef.current) {
        finlandFoddDepositsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandFoddDepositsLayerRef.current) {
      fetch('/data/finland/fodd_finland_deposits.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: '#e91e63' }),
                stroke: new Stroke({ color: '#fff', width: 1 })
              })
            }),
            zIndex: 140,
          });
          finlandFoddDepositsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandFoddDepositsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandFoddDepositsVisible]);

  // Finland FODD Sweden Deposits (1,078 Swedish deposits)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandFoddSwedenLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandFoddSwedenVisible) {
      finlandFoddSwedenLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandFoddSwedenLayerRef.current) {
        finlandFoddSwedenLayerRef.current.setVisible(false);
      }
    };
    if (!finlandFoddSwedenLayerRef.current) {
      fetch('/data/finland/fodd_sweden_deposits.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({ color: '#2196f3' }),
                stroke: new Stroke({ color: '#fff', width: 1 })
              })
            }),
            zIndex: 139,
          });
          finlandFoddSwedenLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandFoddSwedenLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandFoddSwedenVisible]);

  // Finland FODD Metallogenic Areas (200 polygons)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandFoddMetallogenicLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandFoddMetallogenicVisible) {
      finlandFoddMetallogenicLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandFoddMetallogenicLayerRef.current) {
        finlandFoddMetallogenicLayerRef.current.setVisible(false);
      }
    };
    if (!finlandFoddMetallogenicLayerRef.current) {
      fetch('/data/finland/fodd_metallogenic_areas.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: new Style({
              stroke: new Stroke({ color: '#e91e63', width: 2, lineDash: [8, 4] }),
              fill: new Fill({ color: 'rgba(233,30,99,0.08)' })
            }),
            zIndex: 20,
          });
          finlandFoddMetallogenicLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandFoddMetallogenicLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandFoddMetallogenicVisible]);

  // Finland EGDI Critical Raw Materials (1,517 occurrences)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandEgdiCrmLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandEgdiCrmVisible) {
      finlandEgdiCrmLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandEgdiCrmLayerRef.current) {
        finlandEgdiCrmLayerRef.current.setVisible(false);
      }
    };
    if (!finlandEgdiCrmLayerRef.current) {
      fetch('/data/finland/egdi_finland_crm.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 3,
                fill: new Fill({ color: '#00e676' }),
                stroke: new Stroke({ color: '#fff', width: 0.5 })
              })
            }),
            zIndex: 135,
          });
          finlandEgdiCrmLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandEgdiCrmLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandEgdiCrmVisible]);

  // Finland ASTER Alteration Anomalies (5,736 anomalies)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandAsterAlterationLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandAsterAlterationVisible) {
      finlandAsterAlterationLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandAsterAlterationLayerRef.current) {
        finlandAsterAlterationLayerRef.current.setVisible(false);
      }
    };
    if (!finlandAsterAlterationLayerRef.current) {
      fetch('/data/finland/aster_alteration_targets.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const ratioType = feature.get('ratio_type');
              const colorMap: Record<string, string> = {
                'iron_oxide': '#ff5722',
                'silica': '#e0e0e0',
                'illite_muscovite': '#fff176',
                'propylitic': '#66bb6a',
                'alunite_kaolinite': '#ab47bc',
                'ferric_iron': '#d32f2f',
                'carbonate': '#90caf9'
              };
              const color = colorMap[ratioType] || '#9e9e9e';
              return new Style({
                image: new CircleStyle({
                  radius: 3,
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: '#000', width: 0.5 })
                })
              });
            },
            zIndex: 130,
          });
          finlandAsterAlterationLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandAsterAlterationLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandAsterAlterationVisible]);

  // Finland Ore Boulders (31,570 boulders)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandOreBoulderLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandOreBoulderVisible) {
      finlandOreBoulderLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandOreBoulderLayerRef.current) {
        finlandOreBoulderLayerRef.current.setVisible(false);
      }
    };
    if (!finlandOreBoulderLayerRef.current) {
      fetch('/data/finland/ore_boulders.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: (feature) => {
              const metal = feature.get('METAL');
              const colorMap: Record<string, string> = {
                'Cu': '#ff5722',
                'Au': '#ffd700',
                'Ni': '#4caf50',
                'Fe': '#795548'
              };
              const color = colorMap[metal] || '#9e9e9e';
              return new Style({
                image: new RegularShape({
                  points: 4,
                  radius: 4,
                  angle: Math.PI / 4,
                  fill: new Fill({ color }),
                  stroke: new Stroke({ color: '#000', width: 0.5 })
                })
              });
            },
            zIndex: 125,
          });
          finlandOreBoulderLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandOreBoulderLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandOreBoulderVisible]);

  // Finland Expired Claims (12,690 centroids)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-gtk-finland') {
      finlandExpiredClaimsLayerRef.current?.setVisible(false);
      return;
    }
    if (!finlandExpiredClaimsVisible) {
      finlandExpiredClaimsLayerRef.current?.setVisible(false);
      return;
    }
    const removeLayer = () => {
      if (finlandExpiredClaimsLayerRef.current) {
        finlandExpiredClaimsLayerRef.current.setVisible(false);
      }
    };
    if (!finlandExpiredClaimsLayerRef.current) {
      fetch('/data/finland/expired_claims.geojson')
        .then(res => res.json())
        .then(data => {
          const source = new VectorSource({
            features: new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' })
          });
          const layer = new VectorLayer({
            source,
            style: new Style({
              image: new CircleStyle({
                radius: 2,
                fill: new Fill({ color: 'rgba(96,125,139,0.4)' }),
                stroke: new Stroke({ color: '#fff', width: 0.5 })
              })
            }),
            zIndex: 15,
          });
          finlandExpiredClaimsLayerRef.current = layer;
          mapInstance.addLayer(layer);
        });
    } else {
      finlandExpiredClaimsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, finlandExpiredClaimsVisible]);

  const ROPEN_VMS_REVIEW_AREA_HA = 10120;
  const ROPEN_VMS_REVIEW_CENTER = useMemo<[number, number]>(() => [14.832, 65.566], []);
  const ROPEN_VMS_REVIEW_DESCRIPTION =
    "ROPEN original district concept v3 (2026-04-10): 10,120 ha capturing 7 VMS occurrences plus the main Abelvattnet/Usmeten drill area. Zn-Pb-Ag-Au zone: Ropen (11.38% Zn, 253 g/t Ag, 1.2 g/t Au — UNDRILLED), Rikker, Rikkervardo, Jemesjauretje. Cu-proximal zone: Bijetjatje, Kebnevardo. SE arm: Abelvattnet/Usmeten (153 kt @ 1.27% Cu, 70 holes 1965-1977, open at depth — PRAP 84044 recommended 9 more holes never drilled). On the current stored outline, 42/70 SGU Abelvattnet collars fall inside the polygon; use the drill-extension layer for full collar coverage. Zero active permits, but this is still the broadest and highest-friction social/access footprint. CONCEPT — requires shoreline clipping and Vapsten-sensitive redesign.";
  const ROPEN_VMS_REVIEW_GEOMETRY = useMemo(() => ({
    "type": "Polygon",
    "coordinates": [[[14.684, 65.595], [14.755, 65.607], [14.820, 65.605], [14.960, 65.605], [14.980, 65.542], [14.838, 65.530], [14.822, 65.528], [14.778, 65.525], [14.715, 65.540], [14.684, 65.562], [14.684, 65.582], [14.684, 65.595]]]
  } as const), []);
  const ROPEN_VMS_EXTENSION_AREA_HA = 10547;
  const ROPEN_VMS_EXTENSION_CENTER = useMemo<[number, number]>(() => [14.86877, 65.56422], []);
  const ROPEN_VMS_EXTENSION_DESCRIPTION =
    "ROPEN filed polygon v4 (2026-04-15): 10,547 ha. Tightened W/NW, extended E for drill upside. All 7 VMS occurrences + 70/70 Abelvattnet/Usmeten collars. Lakes included per GeoVista.";
  const ROPEN_VMS_EXTENSION_GEOMETRY = useMemo(() => ({
    "type": "Polygon",
    "coordinates": [[
      [14.7, 65.595],
      [14.755, 65.6],
      [14.8, 65.6],
      [14.85, 65.602],
      [14.895, 65.6],
      [14.96, 65.605],
      [14.995, 65.61],
      [15.005, 65.606],
      [15.002, 65.575],
      [14.992, 65.548],
      [14.96, 65.53],
      [14.895, 65.51],
      [14.87, 65.509],
      [14.84, 65.512],
      [14.79, 65.53],
      [14.77, 65.545],
      [14.76, 65.565],
      [14.71, 65.57],
      [14.7, 65.58]
    ]]
  } as const), []);
  const ROPEN_VMS_UPDATED_AREA_HA = 3465;
  const ROPEN_VMS_UPDATED_CENTER = useMemo<[number, number]>(() => [14.789, 65.587], []);
  const ROPEN_VMS_UPDATED_DESCRIPTION =
    "ROPEN updated western-core redesign (2026-04-15): 3,465 ha compact comparison polygon built to preserve the highest-conviction western Zn-Pb-Ag-Au thesis while reducing southern/eastern sprawl. Retains Ropen (UNDRILLED hero showing), Rikker, and Rikkervardo. Drops Jemesjauretje plus the eastern Cu/Usmeten district arm. Nearest active permit to the south (Njeretjakke nr 1001, Greenhills AS) moves from 8.9 km on the original district concept to ~12.3 km here. Still a concept layer, not final filing geometry.";
  const ROPEN_VMS_UPDATED_GEOMETRY = useMemo(() => ({
    "type": "Polygon",
    "coordinates": [[
      [14.864893, 65.587262],
      [14.842799, 65.565036],
      [14.789256, 65.555773],
      [14.735566, 65.564887],
      [14.713113, 65.587051],
      [14.735114, 65.609296],
      [14.788747, 65.618577],
      [14.84253, 65.609445],
      [14.864893, 65.587262]
    ]]
  } as const), []);
  const ROPEN_VMS_SPLIT_AREA_HA = 1506;
  const ROPEN_VMS_SPLIT_CENTER = useMemo<[number, number]>(() => [14.808007, 65.571789], []);
  const ROPEN_VMS_SPLIT_DESCRIPTION =
    "ROPEN heritage-skirted split concept (2026-04-15): ~1,506 ha across 4 separate blocks. Retains Ropen, Rikker, Rikkervardo, Jemesjauretje, Bijetjatje, and the Abelvattnet/Usmeten drill cluster while stepping around the main eastern and southern RAÄ clusters. Drops Kebnevardo, which sits inside the densest eastern heritage concentration. Local comparison cut heritage intersections from 95 on the original district concept to 1 here. Still concept geometry only — shoreline at Ropen remains unresolved.";
  const ROPEN_VMS_SPLIT_GEOMETRY = useMemo(() => ({
    "type": "MultiPolygon",
    "coordinates": [
      [[
        [14.820651, 65.542734],
        [14.814695, 65.539015],
        [14.804141, 65.538131],
        [14.79517, 65.540599],
        [14.793034, 65.544974],
        [14.798989, 65.548693],
        [14.809547, 65.549578],
        [14.81852, 65.547109],
        [14.820651, 65.542734]
      ]],
      [[
        [14.980676, 65.551675],
        [14.959165, 65.549179],
        [14.945956, 65.550803],
        [14.94285, 65.555556],
        [14.945249, 65.558179],
        [14.955845, 65.560087],
        [14.980413, 65.556165],
        [14.982826, 65.553943],
        [14.980676, 65.551675]
      ]],
      [[
        [14.804722, 65.559252],
        [14.799035, 65.555817],
        [14.788982, 65.555799],
        [14.716774, 65.575154],
        [14.712923, 65.577729],
        [14.713903, 65.58073],
        [14.720269, 65.582943],
        [14.785758, 65.591456],
        [14.794163, 65.591121],
        [14.799679, 65.587956],
        [14.804722, 65.559252]
      ]],
      [[
        [14.932871, 65.591303],
        [14.926427, 65.587304],
        [14.915036, 65.58636],
        [14.905368, 65.589025],
        [14.903084, 65.593738],
        [14.909526, 65.597739],
        [14.920921, 65.598683],
        [14.930591, 65.596017],
        [14.932871, 65.591303]
      ]]
    ]
  } as const), []);
  const ROPEN_VMS_SHORELINE_AREA_HA = 1163;
  const ROPEN_VMS_SHORELINE_CENTER = useMemo<[number, number]>(() => [14.813822, 65.569401], []);
  const ROPEN_VMS_SHORELINE_DESCRIPTION =
    "ROPEN provisional shoreline-clipped split (2026-04-15): ~1,163 ha, clipped from the heritage-skirted split against the local merged-water shoreline layer. Retains Ropen, Rikker, Rikkervardo, Jemesjauretje, Bijetjatje, and the Abelvattnet/Usmeten drill cluster while keeping heritage intersections at 0. Uses a very small 30 m uncertainty pocket at the exact SGU Ropen point because that point plots 11.9 m inside the local shoreline layer and 29.2 m inside HydroLAKES. Strict water clip drops the exact Ropen point; this provisional version keeps it but is still not filing-ready.";
  const ROPEN_VMS_SHORELINE_GEOMETRY = useMemo(() => ({
    "type": "MultiPolygon",
    "coordinates": [
      [[
        [14.82032, 65.542178],
        [14.813513, 65.53872],
        [14.802801, 65.538269],
        [14.796926, 65.539718],
        [14.793164, 65.542517],
        [14.798943, 65.543122],
        [14.798412, 65.542394],
        [14.807167, 65.540404],
        [14.808133, 65.542278],
        [14.797989, 65.54328],
        [14.796867, 65.544135],
        [14.792798, 65.543638],
        [14.793366, 65.54553],
        [14.800171, 65.548989],
        [14.810888, 65.54944],
        [14.819234, 65.546619],
        [14.82032, 65.542178]
      ]],
      [[
        [14.980676, 65.551675],
        [14.959165, 65.549179],
        [14.945956, 65.550803],
        [14.94285, 65.555556],
        [14.945249, 65.558179],
        [14.955845, 65.560087],
        [14.980413, 65.556165],
        [14.982826, 65.553943],
        [14.980676, 65.551675]
      ]],
      [[
        [14.804722, 65.559252],
        [14.799035, 65.555817],
        [14.788982, 65.555799],
        [14.716774, 65.575154],
        [14.712923, 65.577729],
        [14.713903, 65.58073],
        [14.720269, 65.582943],
        [14.785758, 65.591456],
        [14.794163, 65.591121],
        [14.799679, 65.587956],
        [14.804722, 65.559252]
      ], [
        [14.718917, 65.576933],
        [14.71971, 65.57784],
        [14.71799, 65.577454],
        [14.718917, 65.576933]
      ], [
        [14.716254, 65.580553],
        [14.718368, 65.58065],
        [14.717734, 65.581595],
        [14.716254, 65.580553]
      ], [
        [14.764379, 65.572142],
        [14.769453, 65.570561],
        [14.766379, 65.571812],
        [14.764379, 65.572142]
      ]],
      [[
        [14.775958, 65.590184],
        [14.791115, 65.591575],
        [14.795954, 65.590618],
        [14.799091, 65.588818],
        [14.800034, 65.586295],
        [14.793391, 65.587556],
        [14.788755, 65.586926],
        [14.782877, 65.590039],
        [14.775958, 65.590184]
      ]],
      [[
        [14.93251, 65.590705],
        [14.92515, 65.586986],
        [14.913591, 65.586509],
        [14.908364, 65.587661],
        [14.9046, 65.589553],
        [14.903444, 65.594336],
        [14.910803, 65.598056],
        [14.922367, 65.598534],
        [14.931359, 65.595489],
        [14.933084, 65.593144],
        [14.93251, 65.590705]
      ]]
    ]
  } as const), []);

  // VERIFIED STAKING BOUNDARIES (2025-12-21) - HYDROLAKES WATER CLIPPED
  // 15 verified targets with water bodies removed using HydroLAKES global dataset
  const STAKING_BOUNDARIES_DATA = useMemo(() => ({
      "type": "FeatureCollection",
      "features": [
          {
              "type": "Feature",
              "properties": {
                  "id": "sodra_svalvatjakko",
                  "name": "S\u00d6DRA SV\u00c4LVATJ\u00c5KKO",
                  "target": "K\u00f6li Caledonides",
                  "tier": "MUST STAKE",
                  "priority": "MUST STAKE",
                  "commodity": "Zn-Cu-Ag-Au",
                  "grades": "26.66% Zn, 1.01% Cu, 0.78% Pb, 107 g/t Ag, 1.1 g/t Au",
                  "area_ha": 3598,
                  "openPct": 100,
                  "color": "#00bcd4",
                  "description": "S\u00d6DRA SV\u00c4LVATJ\u00c5KKO: Zn-Cu-Ag-Au. 3598 ha (HydroLAKES clipped).",
                  "center": [
                      14.777,
                      65.426
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              14.7,
                              65.41131996211
                          ],
                          [
                              14.701409501000057,
                              65.4114375000002
                          ],
                          [
                              14.70275949900008,
                              65.41133100000013
                          ],
                          [
                              14.703982,
                              65.41100350000016
                          ],
                          [
                              14.704668499000093,
                              65.41055550000016
                          ],
                          [
                              14.706155,
                              65.41034200000018
                          ],
                          [
                              14.708437501000105,
                              65.410466
                          ],
                          [
                              14.711006999000062,
                              65.41009
                          ],
                          [
                              14.713017500000035,
                              65.41027050000014
                          ],
                          [
                              14.715273999000146,
                              65.41123300000021
                          ],
                          [
                              14.721184,
                              65.41143700000015
                          ],
                          [
                              14.723085,
                              65.41088550000023
                          ],
                          [
                              14.728189999000165,
                              65.41108350000019
                          ],
                          [
                              14.728605000000243,
                              65.41075000000012
                          ],
                          [
                              14.731594001000133,
                              65.40986550000008
                          ],
                          [
                              14.733614000000216,
                              65.40982150000013
                          ],
                          [
                              14.735894501000075,
                              65.41000350000013
                          ],
                          [
                              14.742285499000104,
                              65.411946
                          ],
                          [
                              14.743369,
                              65.41178300000018
                          ],
                          [
                              14.744334499000104,
                              65.4110590000002
                          ],
                          [
                              14.745947000000228,
                              65.411124
                          ],
                          [
                              14.746749500000192,
                              65.411242
                          ],
                          [
                              14.747409,
                              65.4116365000001
                          ],
                          [
                              14.750891500000023,
                              65.412046
                          ],
                          [
                              14.75409500000012,
                              65.41290750000024
                          ],
                          [
                              14.760417999000026,
                              65.41282600000011
                          ],
                          [
                              14.775151999,
                              65.414869
                          ],
                          [
                              14.783910500000104,
                              65.41446650000006
                          ],
                          [
                              14.784578500000123,
                              65.41482100000013
                          ],
                          [
                              14.785086499000045,
                              65.41592750000024
                          ],
                          [
                              14.784788000000162,
                              65.4165445000001
                          ],
                          [
                              14.783282499000109,
                              65.41732350000007
                          ],
                          [
                              14.777088499000229,
                              65.41745650000018
                          ],
                          [
                              14.774809500000117,
                              65.417221
                          ],
                          [
                              14.7734615010001,
                              65.41727
                          ],
                          [
                              14.771022501000118,
                              65.417877
                          ],
                          [
                              14.767127,
                              65.41768450000018
                          ],
                          [
                              14.765099499000087,
                              65.41795750000023
                          ],
                          [
                              14.761608999000032,
                              65.41765550000014
                          ],
                          [
                              14.750089001000106,
                              65.41613550000011
                          ],
                          [
                              14.742558501000133,
                              65.41603800000024
                          ],
                          [
                              14.740793499000063,
                              65.41647850000021
                          ],
                          [
                              14.73918750100006,
                              65.41624750000017
                          ],
                          [
                              14.736218500000177,
                              65.41650950000025
                          ],
                          [
                              14.731253500000093,
                              65.41614650000008
                          ],
                          [
                              14.725063500000033,
                              65.41611150000017
                          ],
                          [
                              14.724267499000064,
                              65.41582750000003
                          ],
                          [
                              14.71832450100004,
                              65.41646650000007
                          ],
                          [
                              14.712638500000025,
                              65.41750200000018
                          ],
                          [
                              14.707516501000043,
                              65.4178055000001
                          ],
                          [
                              14.705771500000196,
                              65.41768550000006
                          ],
                          [
                              14.703639001000113,
                              65.4170555
                          ],
                          [
                              14.7,
                              65.41693310997924
                          ],
                          [
                              14.7,
                              65.45
                          ],
                          [
                              14.85,
                              65.45
                          ],
                          [
                              14.85,
                              65.4
                          ],
                          [
                              14.7,
                              65.4
                          ],
                          [
                              14.7,
                              65.41131996211
                          ]
                      ],
                      [
                          [
                              14.806426000000215,
                              65.412787
                          ],
                          [
                              14.80999450000013,
                              65.41493700000018
                          ],
                          [
                              14.81051700100005,
                              65.41549600000025
                          ],
                          [
                              14.814904501000058,
                              65.41720200000015
                          ],
                          [
                              14.815959000000134,
                              65.41793550000006
                          ],
                          [
                              14.815945000000113,
                              65.41838400000012
                          ],
                          [
                              14.814443,
                              65.41916350000008
                          ],
                          [
                              14.814282000000162,
                              65.42000100000013
                          ],
                          [
                              14.812661,
                              65.420219
                          ],
                          [
                              14.809299001000056,
                              65.42020150000019
                          ],
                          [
                              14.807689500000038,
                              65.4199710000002
                          ],
                          [
                              14.80542650000018,
                              65.41917150000006
                          ],
                          [
                              14.799001999000211,
                              65.41807500000021
                          ],
                          [
                              14.79539749900016,
                              65.41715850000003
                          ],
                          [
                              14.792966999000043,
                              65.41742900000014
                          ],
                          [
                              14.790427000000136,
                              65.41691
                          ],
                          [
                              14.78909050100009,
                              65.416232
                          ],
                          [
                              14.788339001000054,
                              65.415045
                          ],
                          [
                              14.78928400100017,
                              65.41465950000008
                          ],
                          [
                              14.789725001000193,
                              65.41348750000009
                          ],
                          [
                              14.791488001000118,
                              65.412993
                          ],
                          [
                              14.796599000000128,
                              65.41307150000029
                          ],
                          [
                              14.799587,
                              65.412078
                          ],
                          [
                              14.803490501000113,
                              65.41198750000018
                          ],
                          [
                              14.806426000000215,
                              65.412787
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "dalovardo",
                  "name": "DALOVARDO",
                  "target": "K\u00f6li Caledonides",
                  "tier": "MUST STAKE",
                  "priority": "MUST STAKE",
                  "commodity": "Cu-Zn-Ag-Au",
                  "grades": "8.36% Cu, 8.26% Zn, 214 g/t Ag, 2.2 g/t Au",
                  "area_ha": 14988,
                  "openPct": 100,
                  "color": "#00bcd4",
                  "description": "DALOVARDO: Cu-Zn-Ag-Au. 14988 ha (HydroLAKES clipped).",
                  "center": [
                      15.6,
                      66.225
                  ]
              },
              "geometry": {
                  "type": "MultiPolygon",
                  "coordinates": [
                      [
                          [
                              [
                                  15.5,
                                  66.23906679908553
                              ],
                              [
                                  15.501346501000057,
                                  66.23914200000013
                              ],
                              [
                                  15.501754499000128,
                                  66.23964500000011
                              ],
                              [
                                  15.5,
                                  66.24027639335779
                              ],
                              [
                                  15.5,
                                  66.3
                              ],
                              [
                                  15.7,
                                  66.3
                              ],
                              [
                                  15.7,
                                  66.15
                              ],
                              [
                                  15.5,
                                  66.15
                              ],
                              [
                                  15.5,
                                  66.23906679908553
                              ]
                          ],
                          [
                              [
                                  15.621931000000131,
                                  66.16057250000006
                              ],
                              [
                                  15.621782,
                                  66.16203000000024
                              ],
                              [
                                  15.618582999000068,
                                  66.16376550000007
                              ],
                              [
                                  15.616643000000124,
                                  66.164212
                              ],
                              [
                                  15.61137749900007,
                                  66.16476550000004
                              ],
                              [
                                  15.609704001000125,
                                  66.16610900000012
                              ],
                              [
                                  15.608038998999973,
                                  66.16638400000011
                              ],
                              [
                                  15.606382499000176,
                                  66.16599250000013
                              ],
                              [
                                  15.606943001000047,
                                  66.16509650000012
                              ],
                              [
                                  15.606946500000106,
                                  66.164702
                              ],
                              [
                                  15.606532500000185,
                                  66.16453550000006
                              ],
                              [
                                  15.60500850000011,
                                  66.16453350000012
                              ],
                              [
                                  15.600710999000114,
                                  66.165142
                              ],
                              [
                                  15.596420501000068,
                                  66.1649705000001
                              ],
                              [
                                  15.596565,
                                  66.16457600000012
                              ],
                              [
                                  15.601565501000096,
                                  66.1626770000002
                              ],
                              [
                                  15.60267200100003,
                                  66.1625125000001
                              ],
                              [
                                  15.605580499000041,
                                  66.16262850000015
                              ],
                              [
                                  15.606548999000097,
                                  66.16240550000009
                              ],
                              [
                                  15.608775499000103,
                                  66.1611170000001
                              ],
                              [
                                  15.613903999000172,
                                  66.1604505000002
                              ],
                              [
                                  15.613629500000059,
                                  66.1600605000001
                              ],
                              [
                                  15.612384501000122,
                                  66.1598345000001
                              ],
                              [
                                  15.609064500000159,
                                  66.159606
                              ],
                              [
                                  15.609067500000094,
                                  66.15921150000008
                              ],
                              [
                                  15.611148001000174,
                                  66.15882450000015
                              ],
                              [
                                  15.611156,
                                  66.157811
                              ],
                              [
                                  15.61198849900012,
                                  66.1575880000002
                              ],
                              [
                                  15.614615000000128,
                                  66.15792750000008
                              ],
                              [
                                  15.616969499000163,
                                  66.15793
                              ],
                              [
                                  15.622095000000115,
                                  66.157488
                              ],
                              [
                                  15.62430799900011,
                                  66.157549
                              ],
                              [
                                  15.625413500000093,
                                  66.15777450000013
                              ],
                              [
                                  15.626239000000112,
                                  66.158282
                              ],
                              [
                                  15.626096000000132,
                                  66.15889600000014
                              ],
                              [
                                  15.625260499000092,
                                  66.15951400000023
                              ],
                              [
                                  15.621931000000131,
                                  66.16057250000006
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  15.6139435,
                                  66.16353150000009
                              ],
                              [
                                  15.614344500000186,
                                  66.16328100000015
                              ],
                              [
                                  15.612442001000034,
                                  66.16280300000011
                              ],
                              [
                                  15.611778000000186,
                                  66.16274850000008
                              ],
                              [
                                  15.61137749900007,
                                  66.1629630000001
                              ],
                              [
                                  15.608492499000022,
                                  66.163327
                              ],
                              [
                                  15.608179001000224,
                                  66.16373950000008
                              ],
                              [
                                  15.608862499999987,
                                  66.16421550000007
                              ],
                              [
                                  15.610613999000236,
                                  66.16414600000024
                              ],
                              [
                                  15.6139435,
                                  66.16353150000009
                              ]
                          ]
                      ]
                  ]
              }
          },
          {
            "type": "Feature",
            "properties": {
              "id": "seitevare",
              "name": "SEITEVARE",
              "former_name": "KIURI NORTH",
              "former_id": "kiuri_north",
              "dnr": "BS 200-688-2026",
              "permit_id": "2026:80",
              "status": "GRANTED / ACTIVE",
              "source_crs": "EPSG:3006",
              "target": "Jokkmokk",
              "tier": "DISTRICT",
              "priority": "MUST STAKE",
              "commodity": "Ag-Pb-Zn-Cu-Au",
              "grades": "91 holes, ~8,830m. Best: 3m @ 110 g/t Ag, 4.92% Pb+Zn (KIU-7). 360 kTon @ 4% Zn (Rassåive W). 18.7 g/t Au blocks (source unfound). Pälkasbäcken: 4.77% Cu boulders. Pallemvaratj: 0.1Mt @ 4.15% Pb, 192 ppm Ag.",
              "area_ha": 25507.33,
              "official_area_ha": 25507.33,
              "filed_coordinate_area_ha": 25501.33,
              "openPct": 100.0,
              "cost_3yr": 218901,
              "color": "#00bcd4",
              "overlapping_permits": [],
              "overlapping_natura": [
                "Kvikkjokk-Kabla fjällurskog",
                "Ultevis fjällurskog"
              ],
              "description": "SEITEVARE (Kiuri district): principal on-title prospects include Kiuri, Rassåive, Ritavare and Pallemvaratj. Best public Kiuri intercept: 3 m @ 110 g/t Ag, 1.98% Pb and 2.94% Zn (KIU-7). Rassåive W has a historical 360 kTon @ 4% Zn estimate. Granudden is outside SEITEVARE, KOBERGET and NJUORRAM and is not included as permit evidence. Natura and protected-area overlap requires site-specific review before work.",
              "center": [
                18.5,
                66.955
              ]
            },
            "geometry": {"type": "Polygon", "coordinates": [[[18.359886, 67.020082], [18.720041, 67.02006], [18.720328, 66.874788], [18.359818, 66.874947], [18.359886, 67.020082]]]}
          },
          {
            "type": "Feature",
            "properties": {
              "id": "koberget",
              "name": "KOBERGET",
              "former_name": "TJAULA",
              "former_id": "tjaula",
              "dnr": "BS 200-690-2026",
              "permit_id": "2026:82",
              "status": "GRANTED / ACTIVE",
              "source_crs": "EPSG:3006",
              "target": "Jokkmokk",
              "tier": "OPEN - NEVER FULLY TESTED",
              "priority": "HIGH",
              "commodity": "Cu-Co-Mo-Au",
              "grades": "10 holes, ~794m. >1% Cu, 726 ppm Co, 0.4% Mo. 700m strike, trace electrum. Au NEVER assayed.",
              "area_ha": 1613.72,
              "official_area_ha": 1613.72,
              "filed_coordinate_area_ha": 1613.72,
              "openPct": 100.0,
              "cost_3yr": 40874,
              "color": "#22c55e",
              "overlapping_permits": [],
              "overlapping_natura": [],
              "description": "KOBERGET (Kiuri district): 10 holes, ~794m. >1% Cu, 726 ppm Co, 0.4% Mo. 700m strike, trace electrum. Au NEVER assayed. 100% open.",
              "center": [
                18.84,
                66.86
              ]
            },
            "geometry": {
              "type": "Polygon",
              "coordinates": [
                [
                  [18.792123, 66.882427],
                  [18.889274, 66.882406],
                  [18.889443, 66.843227],
                  [18.792022, 66.853770],
                  [18.792123, 66.882427]
                ]
              ]
            }
          },
          {
            "type": "Feature",
            "properties": {
              "id": "njuorram",
              "name": "NJUORRAM",
              "former_name": "TALLBERGET",
              "former_id": "tallberget",
              "dnr": "BS 200-689-2026",
              "permit_id": "2026:81",
              "status": "GRANTED / ACTIVE",
              "source_crs": "EPSG:3006",
              "target": "Jokkmokk",
              "tier": "OPEN - WALKED AWAY",
              "priority": "HIGH",
              "commodity": "Cu-Au",
              "grades": "10 LKAB holes (1984), ~1,196m. 2.65% Cu, 8.0 g/t Au. Tallberget prospect.",
              "area_ha": 2009.71,
              "official_area_ha": 2009.71,
              "filed_coordinate_area_ha": 2009.71,
              "openPct": 100.0,
              "color": "#3b82f6",
              "comparison": false,
              "overlapping_permits": [],
              "overlapping_natura": [],
              "description": "NJUORRAM (Kiuri district): 10 LKAB holes (TLB84001-010, 1984). 2.65% Cu, 8.0 g/t Au at Tallberget prospect. Zero digital assays in SGU. 100% open.",
              "center": [
                19.254,
                66.816
              ]
            },
            "geometry": {
              "type": "Polygon",
              "coordinates": [
                [
                  [19.205493, 66.837289],
                  [19.302729, 66.837277],
                  [19.302857, 66.795119],
                  [19.205561, 66.795136],
                  [19.205493, 66.837289]
                ]
              ]
            }
          },
          {
            "type": "Feature",
            "properties": {
              "id": "kiuri_district_core",
              "name": "KIURI DISTRICT CORE",
              "target": "Jokkmokk",
              "tier": "COMPARISON",
              "priority": "HIGH",
              "commodity": "Ag-Pb-Zn-Cu-Au",
              "grades": "Compact single-polygon comparison option retaining 44/60 Kiuri holes and 10/10 Tjaula holes.",
              "area_ha": 7827.2,
              "openPct": 100.0,
              "color": "#94a3b8",
              "comparison": true,
              "savings_ha": 15108.9,
              "savings_pct": 65.9,
              "water_ha": 17.1,
              "water_pct": 0.2,
              "kiuri_holes_retained": 44,
              "kiuri_holes_total": 60,
              "koberget_holes_retained": 10,
              "koberget_holes_total": 10,
              "permit_overlap_count": 0,
              "natura_2000_status": "Pending dedicated validation",
              "description": "KIURI DISTRICT CORE: compact single-polygon comparison layout linking the supported Kiuri core to Koberget while minimizing hectares.",
              "center": [
                18.65,
                66.91
              ]
            },
            "geometry": {
              "type": "Polygon",
              "coordinates": [
                [
                  [
                    18.506745151713954,
                    66.92932270766678
                  ],
                  [
                    18.457710398985586,
                    66.9328097110449
                  ],
                  [
                    18.428629634472514,
                    66.9455491626139
                  ],
                  [
                    18.430976414430095,
                    66.96268286660053
                  ],
                  [
                    18.463579833304884,
                    66.97414423332285
                  ],
                  [
                    18.502314787562,
                    66.97404005290485
                  ],
                  [
                    18.537142422685214,
                    66.96278006128918
                  ],
                  [
                    18.550151524251227,
                    66.94970807856643
                  ],
                  [
                    18.54491313943793,
                    66.93754572133484
                  ],
                  [
                    18.58162114256398,
                    66.9211985528564
                  ],
                  [
                    18.610868879680968,
                    66.91773431836155
                  ],
                  [
                    18.63421320014665,
                    66.92010303564471
                  ],
                  [
                    18.67016262927232,
                    66.93205066308123
                  ],
                  [
                    18.706524776082126,
                    66.92674992339853
                  ],
                  [
                    18.716310708639035,
                    66.91349331229827
                  ],
                  [
                    18.68975237584305,
                    66.9004436947267
                  ],
                  [
                    18.68102661289346,
                    66.87621064968604
                  ],
                  [
                    18.810659013961924,
                    66.86987236674906
                  ],
                  [
                    18.84886217869472,
                    66.87420551068365
                  ],
                  [
                    18.872674939574537,
                    66.86627979423089
                  ],
                  [
                    18.872195476587805,
                    66.85401355834125
                  ],
                  [
                    18.837142679391476,
                    66.8460255124559
                  ],
                  [
                    18.80563513897565,
                    66.8538391410103
                  ],
                  [
                    18.643044069495247,
                    66.8594901419592
                  ],
                  [
                    18.627729818391682,
                    66.86975404072749
                  ],
                  [
                    18.64732705146027,
                    66.90434212792469
                  ],
                  [
                    18.621405898467348,
                    66.90201103475555
                  ],
                  [
                    18.60148691624162,
                    66.89372718725626
                  ],
                  [
                    18.577136735623693,
                    66.89286747302528
                  ],
                  [
                    18.55665708479443,
                    66.89833854572875
                  ],
                  [
                    18.541979063745867,
                    66.91342579512326
                  ],
                  [
                    18.539986726851872,
                    66.90103115493683
                  ],
                  [
                    18.524272685403137,
                    66.89359140188826
                  ],
                  [
                    18.499949679818855,
                    66.89176312498199
                  ],
                  [
                    18.476205982648416,
                    66.89744174855001
                  ],
                  [
                    18.469163527269153,
                    66.90670457001302
                  ],
                  [
                    18.478800781102763,
                    66.9156678137411
                  ],
                  [
                    18.500464038269193,
                    66.92013643631847
                  ],
                  [
                    18.529042961691577,
                    66.91897401724006
                  ],
                  [
                    18.506745151713954,
                    66.92932270766678
                  ]
                ]
              ]
            }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "njuggtraskliden",
                  "name": "NJUGGTR\u00c4SKLIDEN",
                  "target": "Skellefte\u00e5",
                  "tier": "MUST STAKE",
                  "priority": "MUST STAKE",
                  "commodity": "Ni-Cu-PGM-Co-Graphite",
                  "grades": "0.71% Ni, 0.26% Cu, 0.04% Co, up to 5.34 g/t Pd",
                  "area_ha": 14400,
                  "openPct": 92,
                  "color": "#00bcd4",
                  "description": "NJUGGTR\u00c4SKLIDEN: Ni-Cu-PGM-Co-Graphite. 14400 ha (extended W+E, HydroLAKES clipped).",
                  "center": [
                      19.284,
                      64.83
                  ]
              },
              "geometry": {
                  "type": "MultiPolygon",
                  "coordinates": [
                      [
                          [
                              [
                                  19.195,
                                  64.78739970567268
                              ],
                              [
                                  19.20096599900006,
                                  64.78613650000011
                              ],
                              [
                                  19.204340512412106,
                                  64.785
                              ],
                              [
                                  19.195,
                                  64.785
                              ],
                              [
                                  19.195,
                                  64.78739970567268
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  19.195,
                                  64.79746962466363
                              ],
                              [
                                  19.195044500000222,
                                  64.79782350000005
                              ],
                              [
                                  19.19532150000009,
                                  64.79792950000018
                              ],
                              [
                                  19.19551349900013,
                                  64.79843250000016
                              ],
                              [
                                  19.195,
                                  64.79867492822277
                              ],
                              [
                                  19.195,
                                  64.84101842398623
                              ],
                              [
                                  19.19692850000007,
                                  64.84075150000007
                              ],
                              [
                                  19.19767549900007,
                                  64.84039800000016
                              ],
                              [
                                  19.199516,
                                  64.84035600000021
                              ],
                              [
                                  19.200483000000133,
                                  64.84072900000012
                              ],
                              [
                                  19.200323001000072,
                                  64.841572
                              ],
                              [
                                  19.198008500000128,
                                  64.84201550000017
                              ],
                              [
                                  19.19641649900018,
                                  64.84300800000011
                              ],
                              [
                                  19.195,
                                  64.84334965436588
                              ],
                              [
                                  19.195,
                                  64.875
                              ],
                              [
                                  19.296509173310742,
                                  64.875
                              ],
                              [
                                  19.297273001000065,
                                  64.874715
                              ],
                              [
                                  19.300540500000068,
                                  64.872452
                              ],
                              [
                                  19.302191500000106,
                                  64.87095400000015
                              ],
                              [
                                  19.304655500000024,
                                  64.86960250000016
                              ],
                              [
                                  19.308365,
                                  64.86771950000019
                              ],
                              [
                                  19.313085001000047,
                                  64.86648550000024
                              ],
                              [
                                  19.314712499000166,
                                  64.86582750000014
                              ],
                              [
                                  19.315801501000237,
                                  64.8650745000001
                              ],
                              [
                                  19.31610850000004,
                                  64.86439350000018
                              ],
                              [
                                  19.31602550000008,
                                  64.863776
                              ],
                              [
                                  19.31548,
                                  64.86362250000019
                              ],
                              [
                                  19.313915999000073,
                                  64.86377200000021
                              ],
                              [
                                  19.311293999000043,
                                  64.864898
                              ],
                              [
                                  19.309627,
                                  64.86527400000017
                              ],
                              [
                                  19.307767,
                                  64.86515150000014
                              ],
                              [
                                  19.306341000000202,
                                  64.86434050000008
                              ],
                              [
                                  19.305980499000043,
                                  64.86374350000006
                              ],
                              [
                                  19.305818499000054,
                                  64.8633475000002
                              ],
                              [
                                  19.306321500000024,
                                  64.86163850000014
                              ],
                              [
                                  19.311665,
                                  64.85910150000012
                              ],
                              [
                                  19.314332999000044,
                                  64.858311
                              ],
                              [
                                  19.31963400000018,
                                  64.855438
                              ],
                              [
                                  19.319962999000097,
                                  64.85492750000003
                              ],
                              [
                                  19.32153600100014,
                                  64.85382150000015
                              ],
                              [
                                  19.324653500000068,
                                  64.85346950000013
                              ],
                              [
                                  19.326889000000108,
                                  64.8534165000001
                              ],
                              [
                                  19.32731049900019,
                                  64.853631
                              ],
                              [
                                  19.327447,
                                  64.8546965000001
                              ],
                              [
                                  19.328316500000028,
                                  64.855291
                              ],
                              [
                                  19.328497,
                                  64.85568150000006
                              ],
                              [
                                  19.3278249990002,
                                  64.85659550000014
                              ],
                              [
                                  19.324527001000035,
                                  64.85863600000022
                              ],
                              [
                                  19.32464650100013,
                                  64.8605955000001
                              ],
                              [
                                  19.32434650000016,
                                  64.86132950000012
                              ],
                              [
                                  19.32342250100021,
                                  64.86235750000009
                              ],
                              [
                                  19.32176050000004,
                                  64.86325000000011
                              ],
                              [
                                  19.321431500000074,
                                  64.86376050000013
                              ],
                              [
                                  19.31938750000012,
                                  64.865268
                              ],
                              [
                                  19.315672500000062,
                                  64.86911850000013
                              ],
                              [
                                  19.314575501000036,
                                  64.86981800000012
                              ],
                              [
                                  19.314122,
                                  64.87039
                              ],
                              [
                                  19.314129000000207,
                                  64.87145350000014
                              ],
                              [
                                  19.311179999000046,
                                  64.87309500000015
                              ],
                              [
                                  19.308650500000056,
                                  64.873945
                              ],
                              [
                                  19.305684,
                                  64.87446350000016
                              ],
                              [
                                  19.301767999000106,
                                  64.8747800000001
                              ],
                              [
                                  19.300905072565595,
                                  64.875
                              ],
                              [
                                  19.375,
                                  64.875
                              ],
                              [
                                  19.375,
                                  64.82422652849903
                              ],
                              [
                                  19.374041501000022,
                                  64.82481600000011
                              ],
                              [
                                  19.37136450000014,
                                  64.82549950000003
                              ],
                              [
                                  19.369249000000195,
                                  64.826444
                              ],
                              [
                                  19.36590299900007,
                                  64.8270320000002
                              ],
                              [
                                  19.363400499000136,
                                  64.82804350000004
                              ],
                              [
                                  19.36128350100006,
                                  64.82798200000013
                              ],
                              [
                                  19.36089050000004,
                                  64.8269815000001
                              ],
                              [
                                  19.360279,
                                  64.82632200000012
                              ],
                              [
                                  19.357827,
                                  64.8267180000002
                              ],
                              [
                                  19.356755999000086,
                                  64.82657300000017
                              ],
                              [
                                  19.356049499000108,
                                  64.82518900000014
                              ],
                              [
                                  19.35857450000003,
                                  64.8233325000001
                              ],
                              [
                                  19.36085050000014,
                                  64.82260450000018
                              ],
                              [
                                  19.362024,
                                  64.821512
                              ],
                              [
                                  19.36252749900018,
                                  64.821334
                              ],
                              [
                                  19.368319,
                                  64.8213070000001
                              ],
                              [
                                  19.372111501000063,
                                  64.82009350000027
                              ],
                              [
                                  19.375,
                                  64.8201054182189
                              ],
                              [
                                  19.375,
                                  64.785
                              ],
                              [
                                  19.22337159035349,
                                  64.785
                              ],
                              [
                                  19.21540250000004,
                                  64.78698600000018
                              ],
                              [
                                  19.210678,
                                  64.789169
                              ],
                              [
                                  19.209393999000213,
                                  64.78942250000011
                              ],
                              [
                                  19.205314501000203,
                                  64.78940400000016
                              ],
                              [
                                  19.204548500000044,
                                  64.78958750000004
                              ],
                              [
                                  19.20356900000013,
                                  64.79017150000004
                              ],
                              [
                                  19.203360500000088,
                                  64.790625
                              ],
                              [
                                  19.20454150000006,
                                  64.79166650000013
                              ],
                              [
                                  19.207206000000156,
                                  64.79194250000012
                              ],
                              [
                                  19.207815500000095,
                                  64.79260150000005
                              ],
                              [
                                  19.203480500000126,
                                  64.79477550000018
                              ],
                              [
                                  19.200560001000042,
                                  64.79669200000012
                              ],
                              [
                                  19.198139,
                                  64.79730850000016
                              ],
                              [
                                  19.196667000000105,
                                  64.79711750000013
                              ],
                              [
                                  19.195,
                                  64.79732024980562
                              ],
                              [
                                  19.195,
                                  64.79746962466363
                              ]
                          ],
                          [
                              [
                                  19.25880650000005,
                                  64.79334550000004
                              ],
                              [
                                  19.258694500000104,
                                  64.79245
                              ],
                              [
                                  19.260429,
                                  64.79055950000014
                              ],
                              [
                                  19.263220500000102,
                                  64.789768
                              ],
                              [
                                  19.26534850000013,
                                  64.78994200000011
                              ],
                              [
                                  19.26593499900008,
                                  64.79043150000007
                              ],
                              [
                                  19.265916000000118,
                                  64.79133000000019
                              ],
                              [
                                  19.265214499000194,
                                  64.79201950000004
                              ],
                              [
                                  19.263127500000223,
                                  64.793191
                              ],
                              [
                                  19.262230001000148,
                                  64.79337800000013
                              ],
                              [
                                  19.25880650000005,
                                  64.79334550000004
                              ]
                          ],
                          [
                              [
                                  19.301529500000072,
                                  64.7942080000002
                              ],
                              [
                                  19.299385501000188,
                                  64.794932
                              ],
                              [
                                  19.295936501000085,
                                  64.79574050000008
                              ],
                              [
                                  19.291512499000135,
                                  64.79612300000014
                              ],
                              [
                                  19.287679500000024,
                                  64.796999
                              ],
                              [
                                  19.282754001000114,
                                  64.79655250000025
                              ],
                              [
                                  19.279277500000035,
                                  64.79713750000013
                              ],
                              [
                                  19.277428001000033,
                                  64.79706850000014
                              ],
                              [
                                  19.275919500000043,
                                  64.79659700000013
                              ],
                              [
                                  19.275602,
                                  64.79615450000011
                              ],
                              [
                                  19.278462499000113,
                                  64.79485750000003
                              ],
                              [
                                  19.279170501000067,
                                  64.79422150000005
                              ],
                              [
                                  19.281169000000148,
                                  64.793389
                              ],
                              [
                                  19.283969999000135,
                                  64.792654
                              ],
                              [
                                  19.284928500000035,
                                  64.7919
                              ],
                              [
                                  19.285961,
                                  64.79176350000017
                              ],
                              [
                                  19.288524001000155,
                                  64.792265
                              ],
                              [
                                  19.28923099900004,
                                  64.79264300000028
                              ],
                              [
                                  19.289926500000206,
                                  64.79397350000016
                              ],
                              [
                                  19.290332,
                                  64.79407650000024
                              ],
                              [
                                  19.293452499000182,
                                  64.79377850000009
                              ],
                              [
                                  19.295480000000225,
                                  64.79316950000009
                              ],
                              [
                                  19.29872949900016,
                                  64.79287300000016
                              ],
                              [
                                  19.301759499000184,
                                  64.792914
                              ],
                              [
                                  19.302121001000046,
                                  64.79369100000025
                              ],
                              [
                                  19.301529500000072,
                                  64.7942080000002
                              ]
                          ],
                          [
                              [
                                  19.237351000000103,
                                  64.81425400000012
                              ],
                              [
                                  19.238359,
                                  64.81389400000012
                              ],
                              [
                                  19.242874999000037,
                                  64.81317450000012
                              ],
                              [
                                  19.244393501000047,
                                  64.81269
                              ],
                              [
                                  19.244960500000047,
                                  64.81194950000008
                              ],
                              [
                                  19.246186499000032,
                                  64.81202
                              ],
                              [
                                  19.248558001000106,
                                  64.81231450000013
                              ],
                              [
                                  19.249471999000036,
                                  64.81330400000013
                              ],
                              [
                                  19.245380999000133,
                                  64.81530750000013
                              ],
                              [
                                  19.2426655000001,
                                  64.815707
                              ],
                              [
                                  19.24229999900018,
                                  64.81594
                              ],
                              [
                                  19.241797000000247,
                                  64.81718150000006
                              ],
                              [
                                  19.241212,
                                  64.81775650000009
                              ],
                              [
                                  19.238844499000095,
                                  64.81882150000018
                              ],
                              [
                                  19.23803649900009,
                                  64.81973800000014
                              ],
                              [
                                  19.233410500000105,
                                  64.821695
                              ],
                              [
                                  19.231512000000123,
                                  64.8223
                              ],
                              [
                                  19.22739450100005,
                                  64.8230635000001
                              ],
                              [
                                  19.225928500000066,
                                  64.8229315000001
                              ],
                              [
                                  19.22585250100019,
                                  64.82231350000023
                              ],
                              [
                                  19.22657000000015,
                                  64.82173550000005
                              ],
                              [
                                  19.235557,
                                  64.8157
                              ],
                              [
                                  19.23637150000019,
                                  64.81483800000012
                              ],
                              [
                                  19.237351000000103,
                                  64.81425400000012
                              ]
                          ],
                          [
                              [
                                  19.216558001000124,
                                  64.82909150000017
                              ],
                              [
                                  19.219555500000126,
                                  64.828852
                              ],
                              [
                                  19.220772501000113,
                                  64.82803800000016
                              ],
                              [
                                  19.22108200100007,
                                  64.82735750000006
                              ],
                              [
                                  19.224947499000052,
                                  64.82772200000011
                              ],
                              [
                                  19.22527150000019,
                                  64.82821750000005
                              ],
                              [
                                  19.225239500000043,
                                  64.829004
                              ],
                              [
                                  19.22487850100009,
                                  64.82929500000012
                              ],
                              [
                                  19.221673501000055,
                                  64.8299840000002
                              ],
                              [
                                  19.220339,
                                  64.83091250000001
                              ],
                              [
                                  19.21832050100005,
                                  64.831628
                              ],
                              [
                                  19.21485950000016,
                                  64.83131650000007
                              ],
                              [
                                  19.21401449900003,
                                  64.83088700000013
                              ],
                              [
                                  19.213786499000037,
                                  64.83010700000017
                              ],
                              [
                                  19.214379500000064,
                                  64.829586
                              ],
                              [
                                  19.216558001000124,
                                  64.82909150000017
                              ]
                          ],
                          [
                              [
                                  19.289762499000176,
                                  64.85069050000021
                              ],
                              [
                                  19.292496499000038,
                                  64.84939600000018
                              ],
                              [
                                  19.299861501000066,
                                  64.84512750000022
                              ],
                              [
                                  19.303463500000134,
                                  64.843418
                              ],
                              [
                                  19.304632001000186,
                                  64.843279
                              ],
                              [
                                  19.305132499000138,
                                  64.84410600000012
                              ],
                              [
                                  19.306371999000078,
                                  64.84453050000013
                              ],
                              [
                                  19.306178501000034,
                                  64.846102
                              ],
                              [
                                  19.307052,
                                  64.846755
                              ],
                              [
                                  19.309408501000064,
                                  64.84765550000014
                              ],
                              [
                                  19.30985400100019,
                                  64.848036
                              ],
                              [
                                  19.30894500000022,
                                  64.8491800000001
                              ],
                              [
                                  19.303103500000077,
                                  64.851953
                              ],
                              [
                                  19.297922001000074,
                                  64.85370050000006
                              ],
                              [
                                  19.28944950100015,
                                  64.857545
                              ],
                              [
                                  19.284894999000244,
                                  64.85905250000025
                              ],
                              [
                                  19.281141501000036,
                                  64.858575
                              ],
                              [
                                  19.2807205000002,
                                  64.85836000000023
                              ],
                              [
                                  19.280126501000098,
                                  64.85680700000012
                              ],
                              [
                                  19.278552000000218,
                                  64.85583350000013
                              ],
                              [
                                  19.278431501000227,
                                  64.85488
                              ],
                              [
                                  19.27977749900009,
                                  64.85406250000005
                              ],
                              [
                                  19.289762499000176,
                                  64.85069050000021
                              ]
                          ],
                          [
                              [
                                  19.35678450100005,
                                  64.79021950000003
                              ],
                              [
                                  19.361401999000236,
                                  64.789323
                              ],
                              [
                                  19.364135,
                                  64.7890915000001
                              ],
                              [
                                  19.364672001000088,
                                  64.78919100000024
                              ],
                              [
                                  19.364979000000176,
                                  64.78952049999998
                              ],
                              [
                                  19.36419750100015,
                                  64.790603
                              ],
                              [
                                  19.36258050000015,
                                  64.79131550000017
                              ],
                              [
                                  19.356403000000114,
                                  64.79236150000025
                              ],
                              [
                                  19.355288001000133,
                                  64.79289050000006
                              ],
                              [
                                  19.353735501000187,
                                  64.793095
                              ],
                              [
                                  19.352163501000177,
                                  64.79313200000018
                              ],
                              [
                                  19.349977999000032,
                                  64.79251050000022
                              ],
                              [
                                  19.348126999000044,
                                  64.7924425000001
                              ],
                              [
                                  19.34381599900007,
                                  64.79164750000012
                              ],
                              [
                                  19.343650999000033,
                                  64.79137300000014
                              ],
                              [
                                  19.344437001000017,
                                  64.79034400000012
                              ],
                              [
                                  19.34637600000019,
                                  64.79007300000018
                              ],
                              [
                                  19.349003999000047,
                                  64.79006450000014
                              ],
                              [
                                  19.35089,
                                  64.79041450000011
                              ],
                              [
                                  19.35678450100005,
                                  64.79021950000003
                              ]
                          ],
                          [
                              [
                                  19.36304999900011,
                                  64.81419550000015
                              ],
                              [
                                  19.364528499000073,
                                  64.81443850000022
                              ],
                              [
                                  19.36460249900017,
                                  64.81499800000023
                              ],
                              [
                                  19.36270500100005,
                                  64.81762500000019
                              ],
                              [
                                  19.359283501000107,
                                  64.81865900000014
                              ],
                              [
                                  19.35360999900007,
                                  64.82159650000023
                              ],
                              [
                                  19.35184450100013,
                                  64.8222
                              ],
                              [
                                  19.348538,
                                  64.82412900000014
                              ],
                              [
                                  19.346757500000024,
                                  64.82462050000004
                              ],
                              [
                                  19.34443850000008,
                                  64.82602250000014
                              ],
                              [
                                  19.338050500000065,
                                  64.828586
                              ],
                              [
                                  19.331921499000146,
                                  64.830074
                              ],
                              [
                                  19.329715000000192,
                                  64.83136150000007
                              ],
                              [
                                  19.325848500000234,
                                  64.83201450000013
                              ],
                              [
                                  19.323461001000055,
                                  64.83291050000014
                              ],
                              [
                                  19.317624,
                                  64.83568450000024
                              ],
                              [
                                  19.315290000000118,
                                  64.83595950000023
                              ],
                              [
                                  19.314950000000124,
                                  64.8343420000001
                              ],
                              [
                                  19.320575000000133,
                                  64.83095850000012
                              ],
                              [
                                  19.320511500000123,
                                  64.83045249999998
                              ],
                              [
                                  19.319500001000108,
                                  64.8297490000001
                              ],
                              [
                                  19.319507500000213,
                                  64.82879700000012
                              ],
                              [
                                  19.319947999000135,
                                  64.828113
                              ],
                              [
                                  19.324262,
                                  64.82582400000013
                              ],
                              [
                                  19.330684000000247,
                                  64.82353900000015
                              ],
                              [
                                  19.33503350100011,
                                  64.822542
                              ],
                              [
                                  19.342453499000158,
                                  64.819842
                              ],
                              [
                                  19.34405099900016,
                                  64.81896450000005
                              ],
                              [
                                  19.347370001000115,
                                  64.81815350000005
                              ],
                              [
                                  19.351848500000187,
                                  64.81614250000007
                              ],
                              [
                                  19.354182999000045,
                                  64.81586250000015
                              ],
                              [
                                  19.35467150100021,
                                  64.8155725000002
                              ],
                              [
                                  19.354717000000107,
                                  64.81489750000009
                              ],
                              [
                                  19.355336500000135,
                                  64.8146
                              ],
                              [
                                  19.35752950000017,
                                  64.8142690000002
                              ],
                              [
                                  19.35901700000022,
                                  64.81355950000017
                              ],
                              [
                                  19.360593,
                                  64.81352250000009
                              ],
                              [
                                  19.3623119990001,
                                  64.81359350000014
                              ],
                              [
                                  19.36304999900011,
                                  64.81419550000015
                              ]
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "njuggtraskliden_expanded",
                  "name": "NJUGGTRÄSKLIDEN EXPANDED",
                  "target": "Skellefteå",
                  "tier": "RECOMMENDED",
                  "priority": "RECOMMENDED",
                  "commodity": "Ni-Cu-PGM-Co-Graphite",
                  "grades": "0.71% Ni, 0.26% Cu, 0.04% Co, up to 5.34 g/t Pd",
                  "area_ha": 12300,
                  "openPct": 92,
                  "color": "#38bdf8",
                  "description": "EXPANDED: Captures 1425 nT magnetic anomaly NW of known deposit. 160 high anomalies outside original polygon.",
                  "center": [
                      19.2675,
                      64.8395
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              19.160,
                              64.785
                          ],
                          [
                              19.375,
                              64.785
                          ],
                          [
                              19.375,
                              64.894
                          ],
                          [
                              19.160,
                              64.894
                          ],
                          [
                              19.160,
                              64.785
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "mjovattnet_watch",
                  "name": "MJÖVATTNET (CONTESTED)",
                  "target": "Skellefteå",
                  "tier": "CONTESTED",
                  "priority": "CONTESTED",
                  "commodity": "Ni-Cu-PGM-Co",
                  "grades": "1.29% Ni, 0.19% Cu, 0.02% Co",
                  "area_ha": 1914,
                  "openPct": 0,
                  "color": "#78909c",
                  "description": "MJÖVATTNET deposit permit nr 103 (EMX/EV Metals, ~85 km E) HELD to 2026-08-31, not refiled → karenstid to ~Aug 2027 then open. Boliden's nr 1001 (5,767 ha, filed 2026-05-18) is on the EXPIRED nr 101 block — 0.0 ha over nr 103 — but signals Boliden will race the karenstid clock for the deposit ground. Contested, not blocked. Verified SGU OGC + EPSG:3006 geometry 2026-05-30.",
                  "center": [
                      20.815,
                      64.500
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              20.747,
                              64.476
                          ],
                          [
                              20.883,
                              64.476
                          ],
                          [
                              20.883,
                              64.526
                          ],
                          [
                              20.747,
                              64.526
                          ],
                          [
                              20.747,
                              64.476
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "mjovattnet_nr101_block",
                  "name": "MJÖVATTNET REGIONAL BLOCK (Boliden nr 1001 / exp EV Metals nr 101)",
                  "target": "Skellefteå",
                  "tier": "COMPETITOR",
                  "priority": "COMPETITOR",
                  "commodity": "Au-Co-Cu-Ni-Pd-Pt-Ag",
                  "grades": "Regional Ni-Cu-PGE corridor block",
                  "area_ha": 5767,
                  "openPct": 0,
                  "color": "#ef4444",
                  "description": "Boliden Mjövattnet nr 1001 (5,767 ha application, filed 2026-05-18, dnr BS 200-970-2026) sitting 99.9% on the EXPIRED EV Metals 'nr 101' regional block (lapsed 2025-05-17). This is the corridor ground Boliden is racing the karenstid clock for — filed one day after nr 101's karenstid cleared. It does NOT cover the held nr 103 deposit permit (~10 km E). Live SGU OGC geometry, simplified ~80 m, verified 2026-05-30.",
                  "center": [
                      21.0066,
                      64.5338
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [20.85193642, 64.52607839],
                          [21.12981284, 64.57480307],
                          [21.16100731, 64.54145281],
                          [20.88344013, 64.4928885],
                          [20.85193642, 64.52607839]
                      ]
                  ]
              }
          },
          {"type":"Feature","properties":{"id":"njugg_boliden_district","name":"BOLIDEN SKELLEFTE DISTRICT POSITION (48 active permits, ~100,500 ha)","target":"Skellefteå","tier":"COMPETITOR","priority":"COMPETITOR","commodity":"VMS Cu-Zn-Au-Ag + Au (+ Ni-Co-Mo at Älgliden)","grades":"Boliden's active Skellefte district landholding","area_ha":100494,"openPct":0,"color":"#f43f5e","description":"Dissolved union of all 48 active Boliden Mineral AB permits + mining concessions in the Njugg/Skellefte view (~100,500 ha; live SGU OGC export 2026-05-29, simplified ~200 m). Njugg sits on the SW edge of this district; nearest Boliden block is Amliden nr 1002 (~12 km, VMS Cu-Au). Mostly VMS/orogenic-Au ground (guld-koppar-silver-zink); only the Algliden 1006/1007/1008 group carries the Ni-Co-Mo magmatic suite. Context for why the Njugg dispensation pre-empted a likely Boliden day-one filing.","center":[19.6,64.95]},"geometry":{"type":"MultiPolygon","coordinates":[[[[19.49362859,65.01496527],[19.49534254,65.01897053],[19.50065229,65.01707083],[19.5004055,65.01483183],[19.49362859,65.01496527]]],[[[18.91562026,65.16027817],[18.79540692,65.15779924],[18.79142057,65.16159302],[18.81395893,65.17449958],[18.77890326,65.19023964],[18.78522514,65.19391699],[18.68668026,65.23723236],[18.67041169,65.25877111],[18.67348018,65.28775196],[18.68341534,65.30754459],[18.93749239,65.3002778],[18.92938121,65.26029448],[18.89848758,65.26109879],[18.89669998,65.24071098],[18.92542746,65.23162764],[18.92861212,65.22441757],[18.95132881,65.2203678],[18.9892908,65.20228928],[18.99684554,65.20384821],[19.01464289,65.16782279],[19.09483436,65.17788372],[19.09191246,65.18136855],[19.1503267,65.19313293],[19.20796041,65.12412639],[19.17959121,65.10812923],[19.19044925,65.09700516],[19.14718726,65.09060322],[19.08987751,65.09519227],[19.1208743,65.06938433],[19.13146482,65.06736926],[19.15085455,64.92370032],[18.67744811,64.98356589],[18.56776298,65.02192635],[18.67554746,65.01473093],[18.68638818,65.04204898],[18.66101079,65.04425154],[18.67355932,65.09611433],[18.65867972,65.10316795],[18.73331886,65.11704829],[18.85769474,65.11281653],[18.96038205,65.13847498],[18.99666783,65.1390999],[18.95898439,65.13850843],[18.91562026,65.16027817]],[[18.99549678,65.16543133],[18.91511483,65.18636926],[18.90368518,65.18921021],[18.99549678,65.16543133]],[[19.15927877,65.1364855],[19.14632266581996,65.14613267566644],[19.1463226,65.14613266],[19.15927877,65.1364855]],[[19.165695939253236,65.13801210338916],[19.16973748,65.13897356],[19.16973746108412,65.1389735740891],[19.165695939253236,65.13801210338916]],[[19.156783029373674,65.1486224255699],[19.15678301,65.14862244],[19.148738673033204,65.14670773213548],[19.156783029373674,65.1486224255699]],[[18.88559553,65.21860085],[18.90480219,65.21820592],[18.90574653,65.22626613],[18.89117699,65.23271166],[18.87732183,65.23299975],[18.87661896,65.22704454],[18.88611942,65.22310237],[18.88559553,65.21860085]]],[[[19.35033636,65.04108881],[19.35859026,65.04088561],[19.36880219,65.03498781],[19.36365628,65.03339712],[19.35033636,65.04108881]]],[[[19.55760189,65.04901566],[19.55510516,65.04638388],[19.54284205,65.04839755],[19.54533778,65.05102953],[19.55760189,65.04901566]]],[[[19.40107784,65.05387605],[19.39541587,65.05464158],[19.40289112,65.05626685],[19.40107784,65.05387605]]],[[[19.40108973,65.06025927],[19.38993233,65.06138714],[19.38401344,65.06153647],[19.39292596,65.06357931],[19.40108973,65.06025927]]],[[[19.36824255,65.06766754],[19.36057671,65.07315378],[19.34234988,65.07487587],[19.35310382,65.08012093],[19.37907646,65.07385271],[19.36824255,65.06766754]]],[[[19.6114541,65.10594597],[19.66689046,65.09049479],[19.67751942,65.0966826],[19.70939061,65.08719147],[19.62521927,65.04016055],[19.64528451,65.03683987],[19.67192041,65.02611719],[19.64838681,65.01192583],[19.60748847,65.02826416],[19.58567962,65.03106316],[19.57080634,65.02807089],[19.55555312,65.03731973],[19.57400444,65.04203929],[19.57918831,65.04913387],[19.57432102,65.05478803],[19.54765223,65.06088547],[19.53207946,65.07161737],[19.51529258,65.07439128],[19.48225037,65.09318886],[19.45457737,65.10246985],[19.56568564,65.14096289],[19.63290938,65.11878202],[19.6114541,65.10594597]],[[19.58011325,65.11165579],[19.580113238132224,65.11165579493368],[19.578489323211112,65.11096255200613],[19.58011325,65.11165579]],[[19.579212288979228,65.112030338608],[19.578007615865776,65.11253114677541],[19.5780076,65.11253114],[19.579212288979228,65.112030338608]],[[19.57929292,65.11308003],[19.578950757302884,65.11293391099242],[19.57618875824661,65.11437031032348],[19.57929292,65.11308003]],[[19.57558150614064,65.11462271835167],[19.57558143,65.11462275],[19.571739771755784,65.11298192093756],[19.57558150614064,65.11462271835167]],[[19.57504121,65.10872757],[19.58052192,65.10895894],[19.56899136,65.11124345],[19.57504121,65.10872757]]],[[[19.59416212,65.22671883],[19.58585683,65.20507871],[19.51505352,65.21497279],[19.55862596,65.27026552],[19.61917576,65.25361171],[19.59416212,65.22671883]]],[[[19.85145929,64.94710036],[19.90488296,64.93328666],[19.90331213,64.93104798],[19.91243855,64.92952077],[19.91071477,64.92711567],[19.89252755,64.93004175],[19.84455972,64.94336179],[19.85145929,64.94710036]]],[[[19.84036545,64.95392411],[19.81966162,64.96014907],[19.82455787,64.96307414],[19.84526255,64.95684842],[19.84036545,64.95392411]]],[[[19.81249813,64.98137132],[19.835939,64.97530204],[19.83705601,64.97743928],[19.85749456,64.97544387],[19.83730782,64.97555133],[19.84206745,64.97412873],[19.83584333,64.97039301],[19.82655623,64.97316843],[19.81290751,64.96663471],[19.8071991,64.96833967],[19.815666009234693,64.97342599719711],[19.80471529,64.97669643],[19.81249813,64.98137132]]],[[[19.69182742,64.9960748],[19.68443285,64.99393271],[19.68105778,64.996019],[19.68845268,64.99816126],[19.69182742,64.9960748]]],[[[19.79327973,65.06010055],[19.86283049,65.09161286],[19.9382626,65.08303458],[19.97083106,65.07301832],[20.00370745,65.0557345],[19.994605384764604,65.05271387862243],[20.05352766,65.02659865],[19.98715649,64.98983438],[19.93750746,65.00510632],[19.79758275,65.05556781],[19.79327973,65.06010055]],[[19.97083106,65.07301832],[19.94443398,65.06248889],[19.93297418,65.05791365],[19.97083106,65.07301832]],[[19.956973922074795,65.02085916998959],[19.95697392,65.02085919],[19.94884922314096,65.02085694215612],[19.956973922074795,65.02085916998959]],[[19.9571226468904,65.01942475999886],[19.95712265,65.01942476],[19.95711137936806,65.01953345987609],[19.9571226468904,65.01942475999886]],[[19.896799625059227,65.048005774885],[19.88833786,65.04853845],[19.888337889310574,65.04853841374872],[19.896799625059227,65.048005774885]],[[19.912457119669458,65.03672692901448],[19.90853369,65.04160411],[19.903695553867088,65.04757167045821],[19.912457119669458,65.03672692901448]],[[19.91246275,65.03671993],[19.89379329114763,65.03485775587956],[19.89439478,65.03411358],[19.91246275,65.03671993]],[[19.9049154,65.0670614],[19.93582003717577,65.05034312234707],[19.935820037183692,65.05034312234972],[19.92626598,65.05551442],[19.9049154,65.0670614]],[[19.928775093807523,65.0479888272531],[19.91584585,65.04366814],[19.91584589223345,65.0436681174555],[19.928775093807523,65.0479888272531]],[[19.94108708,65.04753049],[19.94108697,65.04753045],[19.944562357690618,65.04567407260075],[19.94108708,65.04753049]],[[19.95864908,65.05037739],[19.959141636181187,65.0501142555922],[19.954259012899715,65.0489116211346],[19.95864908,65.05037739]],[[19.98242452,65.05739505],[19.980399754681784,65.0584773922722],[19.98242447,65.05739505],[19.968205683707335,65.0526515873608],[19.98242452,65.05739505]],[[19.97716013,65.06020914],[19.963831116141673,65.05576323206162],[19.977160160738293,65.06020912356878],[19.97716013,65.06020914]],[[19.947718938135175,65.05317349634461],[19.955168027602276,65.0547460462156],[19.95122466,65.05434412],[19.947718938135175,65.05317349634461]],[[19.94941323,65.01427629],[19.93511528,65.00779253],[19.958471294645975,65.01763415680776],[19.94941323,65.01427629]],[[19.9589768,65.01782155],[19.97147864,65.01351998],[19.948743647474224,65.00721585751477],[19.97147874,65.01351995],[19.9589768,65.01782155]]],[[[19.7257551,65.2120494],[19.78154746,65.2338631],[19.87570777,65.22938698],[19.90812081,65.21494595],[19.93275648,65.19316838],[19.83056122,65.16724019],[19.7257551,65.2120494]]],[[[20.02798985,64.90163552],[20.01380809,64.90975934],[20.01859166,64.91126503],[20.03277271,64.90314067],[20.02798985,64.90163552]]],[[[20.24014369,64.92399921],[20.17699714,64.92155126],[20.11709867,64.89424353],[19.88536993,64.97150693],[19.93750746,65.00510632],[20.01417962,64.98150793],[20.10895731,64.98009805],[20.18174188,64.95277921],[20.11524614,64.95628085],[20.20987812,64.95128837],[20.24014369,64.92399921]],[[20.04476121,64.93145023],[20.04601105460004,64.93190822248616],[20.03633251,64.93558817],[20.04476121,64.93145023]],[[20.05046772,64.93054538],[20.05046771,64.9305454],[20.046490097302346,64.93208375717576],[20.05046772,64.93054538]],[[20.05515586,64.93037557],[20.05467441697471,64.93181382826546],[20.05348569691695,64.9304360652909],[20.05515586,64.93037557]],[[20.04939938,64.9331498],[20.050551713098145,64.93295545936084],[20.049399349349123,64.93314981505003],[20.04939938,64.9331498]],[[20.05681634,64.93775779],[20.056833064808952,64.93784692000595],[20.060752172244012,64.93762468598338],[20.05681634,64.93775779]],[[20.06193872,64.9390235],[20.06193132,64.93902375],[20.061662701114642,64.93759811865428],[20.06193872,64.9390235]],[[20.09317738,64.92083191],[20.090768274555597,64.93043998030451],[20.09328575,64.91966593],[20.09796561,64.9193606],[20.098768196672737,64.91645120131334],[20.09799297,64.91943602],[20.09317738,64.92083191]],[[20.090767598851844,64.93044287211671],[20.09959758,64.93082547],[20.10062333,64.92654845],[20.09959763,64.93082549],[20.090767598851844,64.93044287211671]],[[20.11570764576495,64.92638525886079],[20.09775743,64.92553945],[20.097535798524063,64.92638367417365],[20.09775582,64.92553048],[20.11570764576495,64.92638525886079]],[[20.12383696,64.91839351],[20.12250942,64.92374256],[20.116454,64.92347149],[20.115711450474578,64.92638543813757],[20.11645398,64.92347148],[20.12250933,64.92374253],[20.1238369,64.91839352],[20.112765087481353,64.91789762257449],[20.12383696,64.91839351]],[[20.112968907307554,64.91715043986741],[20.110677350505423,64.91703759594027],[20.11296891,64.91715043],[20.112968907307554,64.91715043986741]]],[[[20.25040248,65.06072441],[20.24461743,65.03138112],[20.19410059,64.99798572],[20.06615531,65.0261991],[20.14497582,65.07545053],[20.25040248,65.06072441]]]]}},
          {
              "type": "Feature",
              "properties": {
                  "id": "risliden_vms",
                  "name": "RISLIDEN (Cu-Zn anomaly, NOT VMS)",
                  "target": "Skellefteå",
                  "tier": "NOT A TARGET",
                  "priority": "NOT A TARGET",
                  "commodity": "Cu-Zn",
                  "grades": "Sediment-hosted Cu-Zn anomaly (no tonnage/grade)",
                  "area_ha": 9700,
                  "openPct": 0,
                  "color": "#64748b",
                  "description": "RISLIDEN: Sediment-hosted Cu-Zn anomaly (SGU econ_stat=anomaly, no tonnage/grade) — NOT a VMS deposit. 'Risliden' is SGU's alt_name for the Njugg Ni-Cu-PGE zones; ground is INSIDE our permit. Naming artefact, not a stake target. Verified SGU Mineralresurser 2026-05-30.",
                  "center": [
                      19.4775,
                      64.83
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              19.375,
                              64.785
                          ],
                          [
                              19.58,
                              64.785
                          ],
                          [
                              19.58,
                              64.875
                          ],
                          [
                              19.375,
                              64.875
                          ],
                          [
                              19.375,
                              64.785
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "njugg_gold_south",
                  "name": "NJUGG GOLD SOUTH (desktop only)",
                  "target": "Skellefteå",
                  "tier": "DESKTOP CHECK",
                  "priority": "DESKTOP CHECK",
                  "commodity": "Au",
                  "grades": "Lapsed historic Au permits — no confirmed anomaly on our ground",
                  "area_ha": 10500,
                  "openPct": 0,
                  "color": "#14b8a6",
                  "description": "GOLD SOUTH: Basis is lapsed Boliden gold permit 'Risliden nr 101' (1984-1990), ~13 km SE, OUTSIDE polygon. No confirmed Au till anomaly on our ground in SGU data. Desktop check only, not a stake target. Verified SGU 2026-05-30.",
                  "center": [
                      19.18,
                      64.73
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              19.08,
                              64.68
                          ],
                          [
                              19.28,
                              64.68
                          ],
                          [
                              19.28,
                              64.78
                          ],
                          [
                              19.08,
                              64.78
                          ],
                          [
                              19.08,
                              64.68
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "autjak",
                  "name": "AUTJAK",
                  "target": "Laisvall",
                  "tier": "MUST STAKE",
                  "priority": "MUST STAKE",
                  "commodity": "Ni-Cu",
                  "grades": "4.3% Ni, 0.27% Cu",
                  "area_ha": 2865,
                  "openPct": 100,
                  "color": "#00bcd4",
                  "description": "AUTJAK: Ni-Cu. 2865 ha (HydroLAKES clipped).",
                  "center": [
                      17.278,
                      66.301
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              17.219553,
                              66.31088246135054
                          ],
                          [
                              17.22253849900011,
                              66.31085150000013
                          ],
                          [
                              17.22282950000016,
                              66.31101900000016
                          ],
                          [
                              17.222844999000074,
                              66.311297
                          ],
                          [
                              17.22244249900018,
                              66.311525
                          ],
                          [
                              17.2226099990001,
                              66.31197200000014
                          ],
                          [
                              17.224003499000048,
                              66.3120175000002
                          ],
                          [
                              17.225650500000086,
                              66.31160800000015
                          ],
                          [
                              17.228167,
                              66.31175100000024
                          ],
                          [
                              17.22874350000012,
                              66.312082
                          ],
                          [
                              17.228657,
                              66.31292150000019
                          ],
                          [
                              17.227342500000134,
                              66.31422500000019
                          ],
                          [
                              17.22379450000011,
                              66.31538300000028
                          ],
                          [
                              17.219553,
                              66.31553406038307
                          ],
                          [
                              17.219553,
                              66.329427
                          ],
                          [
                              17.321873466370484,
                              66.329427
                          ],
                          [
                              17.32403900000014,
                              66.32741650000014
                          ],
                          [
                              17.324051499000063,
                              66.32545650000009
                          ],
                          [
                              17.323165000000245,
                              66.32255000000015
                          ],
                          [
                              17.322577500000023,
                              66.322107
                          ],
                          [
                              17.319495001000064,
                              66.321859
                          ],
                          [
                              17.31834050000009,
                              66.321256
                          ],
                          [
                              17.315655499000172,
                              66.32066750000013
                          ],
                          [
                              17.313499001000025,
                              66.31962550000009
                          ],
                          [
                              17.31346949900012,
                              66.31917700000014
                          ],
                          [
                              17.314279000000113,
                              66.31877450000019
                          ],
                          [
                              17.314219500000036,
                              66.31787850000018
                          ],
                          [
                              17.314478001000225,
                              66.31753950000024
                          ],
                          [
                              17.31595799900009,
                              66.31674050000004
                          ],
                          [
                              17.317338500000062,
                              66.31655650000022
                          ],
                          [
                              17.319286499000043,
                              66.3165375000001
                          ],
                          [
                              17.323217499000123,
                              66.31699700000019
                          ],
                          [
                              17.32362200000017,
                              66.31676900000019
                          ],
                          [
                              17.324654001000056,
                              66.3155255000002
                          ],
                          [
                              17.324687,
                              66.31390150000004
                          ],
                          [
                              17.328043,
                              66.31195800000012
                          ],
                          [
                              17.329100999000104,
                              66.31110400000017
                          ],
                          [
                              17.330649501000153,
                              66.30923750000011
                          ],
                          [
                              17.330702501000076,
                              66.30794950000006
                          ],
                          [
                              17.332755,
                              66.30736900000016
                          ],
                          [
                              17.33342100100009,
                              66.30690950000007
                          ],
                          [
                              17.33377200100017,
                              66.305897
                          ],
                          [
                              17.33629,
                              66.30609650000014
                          ],
                          [
                              17.337649000000113,
                              66.30557600000014
                          ],
                          [
                              17.340432000000135,
                              66.3055485000001
                          ],
                          [
                              17.34471200000013,
                              66.30499500000013
                          ],
                          [
                              17.345927999000082,
                              66.3044225000001
                          ],
                          [
                              17.34777800100011,
                              66.30294650000008
                          ],
                          [
                              17.349268999000117,
                              66.302313
                          ],
                          [
                              17.351600000000133,
                              66.30178300000011
                          ],
                          [
                              17.353743000000122,
                              66.300528
                          ],
                          [
                              17.354047,
                              66.30037665810231
                          ],
                          [
                              17.354047,
                              66.275373
                          ],
                          [
                              17.338947618211808,
                              66.275373
                          ],
                          [
                              17.34002050000015,
                              66.27611850000011
                          ],
                          [
                              17.34346650000009,
                              66.27776150000011
                          ],
                          [
                              17.344488000000183,
                              66.27848250000011
                          ],
                          [
                              17.344664,
                              66.27904200000012
                          ],
                          [
                              17.34147050000007,
                              66.27913150000023
                          ],
                          [
                              17.338956499000062,
                              66.27899000000019
                          ],
                          [
                              17.33696749900008,
                              66.27833700000025
                          ],
                          [
                              17.33391700100009,
                              66.2784835000001
                          ],
                          [
                              17.32994749900024,
                              66.27734750000019
                          ],
                          [
                              17.329083,
                              66.27690750000005
                          ],
                          [
                              17.32891349900018,
                              66.276461
                          ],
                          [
                              17.33029500100008,
                              66.27627700000022
                          ],
                          [
                              17.332140500000094,
                              66.27687300000014
                          ],
                          [
                              17.334526,
                              66.277186
                          ],
                          [
                              17.33555850100015,
                              66.27594250000004
                          ],
                          [
                              17.335620852368713,
                              66.275373
                          ],
                          [
                              17.219553,
                              66.275373
                          ],
                          [
                              17.219553,
                              66.31088246135054
                          ]
                      ],
                      [
                          [
                              17.26981199900007,
                              66.29199
                          ],
                          [
                              17.271439500000042,
                              66.29129750000004
                          ],
                          [
                              17.27222699900011,
                              66.29056350000002
                          ],
                          [
                              17.273456999000246,
                              66.29021100000011
                          ],
                          [
                              17.275551500000063,
                              66.2903035000001
                          ],
                          [
                              17.276684000000103,
                              66.290629
                          ],
                          [
                              17.2765740010002,
                              66.2910785000002
                          ],
                          [
                              17.27275349900009,
                              66.29229450000014
                          ],
                          [
                              17.271820500000047,
                              66.292922
                          ],
                          [
                              17.271487001000082,
                              66.29427050000015
                          ],
                          [
                              17.270428,
                              66.29512350000022
                          ],
                          [
                              17.2681159990002,
                              66.29598850000019
                          ],
                          [
                              17.26799249900006,
                              66.296214
                          ],
                          [
                              17.262588000000108,
                              66.29660550000006
                          ],
                          [
                              17.262698001000103,
                              66.29615150000006
                          ],
                          [
                              17.2650269990001,
                              66.29556900000011
                          ],
                          [
                              17.265190000000132,
                              66.29372000000012
                          ],
                          [
                              17.26558199900012,
                              66.29332150000022
                          ],
                          [
                              17.26981199900007,
                              66.29199
                          ]
                      ],
                      [
                          [
                              17.240026500000226,
                              66.32105100000012
                          ],
                          [
                              17.240840500000047,
                              66.320707
                          ],
                          [
                              17.241531,
                              66.32064250000008
                          ],
                          [
                              17.241965000000107,
                              66.32086250000009
                          ],
                          [
                              17.242151500000148,
                              66.32164550000022
                          ],
                          [
                              17.240022501000112,
                              66.32323500000012
                          ],
                          [
                              17.237022501000126,
                              66.32433000000015
                          ],
                          [
                              17.2367625010001,
                              66.32461500000011
                          ],
                          [
                              17.236847499000135,
                              66.32595950000007
                          ],
                          [
                              17.236464000000183,
                              66.32652350000006
                          ],
                          [
                              17.235498001000053,
                              66.32670300000024
                          ],
                          [
                              17.233647499000085,
                              66.32604750000007
                          ],
                          [
                              17.23163349900011,
                              66.32505650000007
                          ],
                          [
                              17.231332501000225,
                              66.32466950000003
                          ],
                          [
                              17.23156449900023,
                              66.323936
                          ],
                          [
                              17.232502500000066,
                              66.32331300000016
                          ],
                          [
                              17.23373450100013,
                              66.32296100000013
                          ],
                          [
                              17.23652600000014,
                              66.32304750000009
                          ],
                          [
                              17.238968,
                              66.32195750000005
                          ],
                          [
                              17.240026500000226,
                              66.32105100000012
                          ]
                      ],
                      [
                          [
                              17.323212999000248,
                              66.30416150000013
                          ],
                          [
                              17.322977,
                              66.30477750000011
                          ],
                          [
                              17.3210605000001,
                              66.30530349999998
                          ],
                          [
                              17.3181560000001,
                              66.30561450000022
                          ],
                          [
                              17.31696450000004,
                              66.306523
                          ],
                          [
                              17.31449750000013,
                              66.30716600000011
                          ],
                          [
                              17.313545499000043,
                              66.3075115000002
                          ],
                          [
                              17.311326499000188,
                              66.30764950000014
                          ],
                          [
                              17.310789,
                              66.307933
                          ],
                          [
                              17.31040749900012,
                              66.3085015000002
                          ],
                          [
                              17.310470001000056,
                              66.30945150000014
                          ],
                          [
                              17.307989,
                              66.30987050000016
                          ],
                          [
                              17.305040500000132,
                              66.30950850000016
                          ],
                          [
                              17.298342499000228,
                              66.30711100000013
                          ],
                          [
                              17.29347050100023,
                              66.307162
                          ],
                          [
                              17.29245650100006,
                              66.30655750000005
                          ],
                          [
                              17.292239,
                              66.305326
                          ],
                          [
                              17.291506500000196,
                              66.30477250000001
                          ],
                          [
                              17.29037599900016,
                              66.30450500000012
                          ],
                          [
                              17.28676800000011,
                              66.30471000000017
                          ],
                          [
                              17.284672000000114,
                              66.30456400000014
                          ],
                          [
                              17.284092499000053,
                              66.30423300000012
                          ],
                          [
                              17.284448500000053,
                              66.30327450000004
                          ],
                          [
                              17.28552699900007,
                              66.30270350000012
                          ],
                          [
                              17.28564350000005,
                              66.30236600000018
                          ],
                          [
                              17.285352,
                              66.30214450000005
                          ],
                          [
                              17.278794,
                              66.30193350000019
                          ],
                          [
                              17.278757999000163,
                              66.30137300000013
                          ],
                          [
                              17.280101500000228,
                              66.3005710000001
                          ],
                          [
                              17.280075999000076,
                              66.30018100000024
                          ],
                          [
                              17.278910501000155,
                              66.29935350000022
                          ],
                          [
                              17.282485501000053,
                              66.29869600000012
                          ],
                          [
                              17.286805501000117,
                              66.298767
                          ],
                          [
                              17.292213000000174,
                              66.29842850000006
                          ],
                          [
                              17.29410200000018,
                              66.299644
                          ],
                          [
                              17.29564350100003,
                              66.29979500000019
                          ],
                          [
                              17.296054500000025,
                              66.29967900000014
                          ],
                          [
                              17.296028000000206,
                              66.299289
                          ],
                          [
                              17.295007999000177,
                              66.29856800000022
                          ],
                          [
                              17.295228000000122,
                              66.297669
                          ],
                          [
                              17.295626,
                              66.29738700000024
                          ],
                          [
                              17.299096999000085,
                              66.29723700000011
                          ],
                          [
                              17.301985000000116,
                              66.2967025000001
                          ],
                          [
                              17.302214499000172,
                              66.29591550000009
                          ],
                          [
                              17.30288400000012,
                              66.29551450000008
                          ],
                          [
                              17.303985,
                              66.29533750000019
                          ],
                          [
                              17.30546349900004,
                              66.29453450000011
                          ],
                          [
                              17.30779450100016,
                              66.29395100000016
                          ],
                          [
                              17.30882950000006,
                              66.2927615000001
                          ],
                          [
                              17.30978749900021,
                              66.29252800000023
                          ],
                          [
                              17.319088499000088,
                              66.29215050000022
                          ],
                          [
                              17.326499501000114,
                              66.29055800000015
                          ],
                          [
                              17.330025,
                              66.28911950000008
                          ],
                          [
                              17.331129,
                              66.288997
                          ],
                          [
                              17.332808499000066,
                              66.28914600000013
                          ],
                          [
                              17.339847999000085,
                              66.28828800000025
                          ],
                          [
                              17.340040500000043,
                              66.2890705000001
                          ],
                          [
                              17.34354700100016,
                              66.28948000000025
                          ],
                          [
                              17.34373300100009,
                              66.29020450000007
                          ],
                          [
                              17.34153000100008,
                              66.29056750000012
                          ],
                          [
                              17.34101,
                              66.291133
                          ],
                          [
                              17.340066,
                              66.29159100000021
                          ],
                          [
                              17.336645501000078,
                              66.29246750000004
                          ],
                          [
                              17.336950500000057,
                              66.29286
                          ],
                          [
                              17.338509,
                              66.293288
                          ],
                          [
                              17.338542501000063,
                              66.29379450000016
                          ],
                          [
                              17.337883,
                              66.2943615000001
                          ],
                          [
                              17.335729,
                              66.29545
                          ],
                          [
                              17.33408550000007,
                              66.29586100000023
                          ],
                          [
                              17.331267999000147,
                              66.29745850000023
                          ],
                          [
                              17.33130800100008,
                              66.298077
                          ],
                          [
                              17.332723999000223,
                              66.29845300000011
                          ],
                          [
                              17.33275050000009,
                              66.2988430000002
                          ],
                          [
                              17.3308510010001,
                              66.29959300000019
                          ],
                          [
                              17.330334500000106,
                              66.300217
                          ],
                          [
                              17.33079550100007,
                              66.30088500000022
                          ],
                          [
                              17.331369000000223,
                              66.30110350000007
                          ],
                          [
                              17.333037001000037,
                              66.301083
                          ],
                          [
                              17.333607501000188,
                              66.30130150000014
                          ],
                          [
                              17.333657499000083,
                              66.302032
                          ],
                          [
                              17.3334024990001,
                              66.30242450000014
                          ],
                          [
                              17.33177449900012,
                              66.30306000000013
                          ],
                          [
                              17.330530999000132,
                              66.30318400000016
                          ],
                          [
                              17.325656500000036,
                              66.3031820000001
                          ],
                          [
                              17.32524899900011,
                              66.303352
                          ],
                          [
                              17.32529850100019,
                              66.3040825000001
                          ],
                          [
                              17.323212999000248,
                              66.30416150000013
                          ]
                      ],
                      [
                          [
                              17.266795001000048,
                              66.32134000000013
                          ],
                          [
                              17.274085499000194,
                              66.31986350000011
                          ],
                          [
                              17.2763925000001,
                              66.318886
                          ],
                          [
                              17.281905999000116,
                              66.31799100000012
                          ],
                          [
                              17.283312500000022,
                              66.318202
                          ],
                          [
                              17.283339000000183,
                              66.31859150000014
                          ],
                          [
                              17.28252450100007,
                              66.31893600000012
                          ],
                          [
                              17.277041500000223,
                              66.32033750000005
                          ],
                          [
                              17.277070499000104,
                              66.32078600000011
                          ],
                          [
                              17.277782999000237,
                              66.32106150000004
                          ],
                          [
                              17.27780500000017,
                              66.321398
                          ],
                          [
                              17.276447500000074,
                              66.321971
                          ],
                          [
                              17.274113500000112,
                              66.3225000000001
                          ],
                          [
                              17.27046300000012,
                              66.324275
                          ],
                          [
                              17.26923449900005,
                              66.32468100000023
                          ],
                          [
                              17.267016999000077,
                              66.324872
                          ],
                          [
                              17.26504250100004,
                              66.324496
                          ],
                          [
                              17.26491450000009,
                              66.32247900000021
                          ],
                          [
                              17.266795001000048,
                              66.32134000000013
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "norra_svalvatjakko",
                  "name": "NORRA SV\u00c4LVATJ\u00c5KKO",
                  "target": "K\u00f6li Caledonides",
                  "tier": "HIGH",
                  "priority": "HIGH",
                  "commodity": "Zn-Cu-Ag-Au",
                  "grades": "16.8% Zn, 0.78% Cu, 0.17% Pb, 63 g/t Ag, 0.9 g/t Au",
                  "area_ha": 3787,
                  "openPct": 100,
                  "color": "#3b82f6",
                  "description": "NORRA SV\u00c4LVATJ\u00c5KKO: Zn-Cu-Ag-Au. 3787 ha (HydroLAKES clipped).",
                  "center": [
                      14.774,
                      65.475
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              14.7,
                              65.5
                          ],
                          [
                              14.794340484219909,
                              65.5
                          ],
                          [
                              14.794365,
                              65.49919100000017
                          ],
                          [
                              14.79465349900022,
                              65.49857350000013
                          ],
                          [
                              14.795473500000128,
                              65.49824350000006
                          ],
                          [
                              14.801568001000078,
                              65.497544
                          ],
                          [
                              14.803181500000221,
                              65.49772050000018
                          ],
                          [
                              14.803285999000025,
                              65.49873
                          ],
                          [
                              14.801645499000188,
                              65.49939700000022
                          ],
                          [
                              14.798532043533578,
                              65.5
                          ],
                          [
                              14.85,
                              65.5
                          ],
                          [
                              14.85,
                              65.4776618290009
                          ],
                          [
                              14.84991,
                              65.47770650000007
                          ],
                          [
                              14.849350501000117,
                              65.47837600000014
                          ],
                          [
                              14.84904100000017,
                              65.47977750000007
                          ],
                          [
                              14.84793500100011,
                              65.480726
                          ],
                          [
                              14.844807500000115,
                              65.48166600000013
                          ],
                          [
                              14.843321999000182,
                              65.48171450000024
                          ],
                          [
                              14.841578,
                              65.481367
                          ],
                          [
                              14.841169500000035,
                              65.481535
                          ],
                          [
                              14.841286499000148,
                              65.48209650000013
                          ],
                          [
                              14.839666499000145,
                              65.482198
                          ],
                          [
                              14.836467500000083,
                              65.480837
                          ],
                          [
                              14.83488199900006,
                              65.47971000000013
                          ],
                          [
                              14.833150501000205,
                              65.4789725
                          ],
                          [
                              14.832898500000056,
                              65.47841000000011
                          ],
                          [
                              14.83345150100007,
                              65.47790700000024
                          ],
                          [
                              14.834681499000112,
                              65.477408
                          ],
                          [
                              14.836616001000152,
                              65.47573850000009
                          ],
                          [
                              14.838265999000214,
                              65.474682
                          ],
                          [
                              14.839626500000122,
                              65.474296
                          ],
                          [
                              14.842088499000056,
                              65.47307350000005
                          ],
                          [
                              14.842515999000113,
                              65.472233
                          ],
                          [
                              14.841190999000105,
                              65.4714395000002
                          ],
                          [
                              14.839983500000073,
                              65.47126650000018
                          ],
                          [
                              14.839874001000226,
                              65.47036900000012
                          ],
                          [
                              14.841360500000235,
                              65.47020800000024
                          ],
                          [
                              14.84349350100024,
                              65.47111400000011
                          ],
                          [
                              14.844144001000132,
                              65.47190300000011
                          ],
                          [
                              14.845475501000237,
                              65.47252550000007
                          ],
                          [
                              14.848152500000253,
                              65.47315700000019
                          ],
                          [
                              14.849347000000193,
                              65.47383750000006
                          ],
                          [
                              14.85,
                              65.47466518287395
                          ],
                          [
                              14.85,
                              65.45
                          ],
                          [
                              14.7,
                              65.45
                          ],
                          [
                              14.7,
                              65.5
                          ]
                      ],
                      [
                          [
                              14.764695499000084,
                              65.468812
                          ],
                          [
                              14.76418150000012,
                              65.46797
                          ],
                          [
                              14.764738501000068,
                              65.467467
                          ],
                          [
                              14.768819000000121,
                              65.46631100000025
                          ],
                          [
                              14.77030250100006,
                              65.46631700000012
                          ],
                          [
                              14.770543999000097,
                              65.46716150000009
                          ],
                          [
                              14.769844499999977,
                              65.46800000000019
                          ],
                          [
                              14.769294500000058,
                              65.46827400000012
                          ],
                          [
                              14.769277499000054,
                              65.46883450000007
                          ],
                          [
                              14.769915500000081,
                              65.46996000000013
                          ],
                          [
                              14.769599000000198,
                              65.47147400000023
                          ],
                          [
                              14.765537001000098,
                              65.47201100000012
                          ],
                          [
                              14.763133000000153,
                              65.471272
                          ],
                          [
                              14.76301749900017,
                              65.470653
                          ],
                          [
                              14.764539500000069,
                              65.46948400000025
                          ],
                          [
                              14.764695499000084,
                              65.468812
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "birdotjarre",
                  "name": "BIRDOTJ\u00c5RRE",
                  "target": "K\u00f6li Caledonides",
                  "tier": "HIGH",
                  "priority": "HIGH",
                  "commodity": "Zn-Ag-Au-Pb-Cu",
                  "grades": "16.4% Zn, 1.5% Pb, 0.27% Cu, 380 g/t Ag, 1.9 g/t Au",
                  "area_ha": 4511,
                  "openPct": 100,
                  "color": "#3b82f6",
                  "description": "BIRDOTJ\u00c5RRE: Zn-Ag-Au-Pb-Cu. 4511 ha (HydroLAKES clipped).",
                  "center": [
                      15.575,
                      66.23
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              15.5,
                              66.23906679908553
                          ],
                          [
                              15.501346501000057,
                              66.23914200000013
                          ],
                          [
                              15.501754499000128,
                              66.23964500000011
                          ],
                          [
                              15.5,
                              66.24027639335779
                          ],
                          [
                              15.5,
                              66.26
                          ],
                          [
                              15.65,
                              66.26
                          ],
                          [
                              15.65,
                              66.2
                          ],
                          [
                              15.5,
                              66.2
                          ],
                          [
                              15.5,
                              66.23906679908553
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "birdejaure",
                  "name": "BIRDEJAURE",
                  "target": "K\u00f6li Caledonides",
                  "tier": "HIGH",
                  "priority": "HIGH",
                  "commodity": "Zn-Pb-Cu-Ag-Au",
                  "grades": "10.4% Zn, 9% Pb, 1.92% Cu, 155 g/t Ag, 1 g/t Au",
                  "area_ha": 5594,
                  "openPct": 100,
                  "color": "#3b82f6",
                  "description": "BIRDEJAURE: Zn-Pb-Cu-Ag-Au. 5594 ha (HydroLAKES clipped).",
                  "center": [
                      15.55,
                      66.22
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              15.48,
                              66.26
                          ],
                          [
                              15.62,
                              66.26
                          ],
                          [
                              15.62,
                              66.18
                          ],
                          [
                              15.48,
                              66.18
                          ],
                          [
                              15.48,
                              66.26
                          ]
                      ],
                      [
                          [
                              15.498966,
                              66.24064850000008
                          ],
                          [
                              15.498254499000097,
                              66.24199200000015
                          ],
                          [
                              15.498107000000232,
                              66.24272300000024
                          ],
                          [
                              15.499622500000044,
                              66.24351050000013
                          ],
                          [
                              15.499613000000124,
                              66.244349
                          ],
                          [
                              15.498630500000047,
                              66.24513150000013
                          ],
                          [
                              15.496122500000183,
                              66.24585700000011
                          ],
                          [
                              15.4948720000001,
                              66.245855
                          ],
                          [
                              15.494318001000067,
                              66.24568350000004
                          ],
                          [
                              15.494045001000245,
                              66.245293
                          ],
                          [
                              15.49558750000017,
                              66.244175
                          ],
                          [
                              15.495733999000208,
                              66.24355650000024
                          ],
                          [
                              15.493793001000085,
                              66.243387
                          ],
                          [
                              15.489902500000198,
                              66.243545
                          ],
                          [
                              15.489212500000178,
                              66.24315350000006
                          ],
                          [
                              15.48908500000016,
                              66.24225600000011
                          ],
                          [
                              15.490353999000149,
                              66.24080150000009
                          ],
                          [
                              15.491891499000133,
                              66.24013200000024
                          ],
                          [
                              15.494533500000045,
                              66.239689
                          ],
                          [
                              15.497183500000062,
                              66.23890950000003
                          ],
                          [
                              15.501346501000057,
                              66.23914200000013
                          ],
                          [
                              15.501754499000128,
                              66.23964500000011
                          ],
                          [
                              15.498966,
                              66.24064850000008
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "laisvall",
                  "name": "LAISVALL",
                  "target": "Laisvall",
                  "tier": "HIGH",
                  "priority": "HIGH",
                  "commodity": "Pb-Zn-Ag",
                  "grades": "3.8% Pb, 0.3% Zn, 11 g/t Ag (SGU avg); 4.6% Pb, 0.8% Zn (final reserves)",
                  "area_ha": 3778,
                  "openPct": 47,
                  "color": "#3b82f6",
                  "description": "LAISVALL: Pb-Zn-Ag. 3778 ha (HydroLAKES clipped).",
                  "center": [
                      17.153,
                      66.144
                  ]
              },
              "geometry": {
                  "type": "MultiPolygon",
                  "coordinates": [
                      [
                          [
                              [
                                  17.089768,
                                  66.180336
                              ],
                              [
                                  17.113667069268015,
                                  66.180336
                              ],
                              [
                                  17.115085501000124,
                                  66.178904
                              ],
                              [
                                  17.1242400000001,
                                  66.17455250000017
                              ],
                              [
                                  17.125293500000225,
                                  66.17364650000013
                              ],
                              [
                                  17.126398500000107,
                                  66.17133650000022
                              ],
                              [
                                  17.12705649900022,
                                  66.17077050000017
                              ],
                              [
                                  17.127656,
                                  66.16924950000009
                              ],
                              [
                                  17.12882150000013,
                                  66.167894
                              ],
                              [
                                  17.130054001000133,
                                  66.16765950000013
                              ],
                              [
                                  17.130902501000094,
                                  66.16798850000009
                              ],
                              [
                                  17.132010500000206,
                                  66.16797450000013
                              ],
                              [
                                  17.133152500000108,
                                  66.16628300000013
                              ],
                              [
                                  17.133630999000047,
                                  66.16504550000008
                              ],
                              [
                                  17.135032,
                                  66.16301550000009
                              ],
                              [
                                  17.135139001000084,
                                  66.16250800000012
                              ],
                              [
                                  17.13454099900008,
                                  66.16178650000023
                              ],
                              [
                                  17.13517250000018,
                                  66.16077200000024
                              ],
                              [
                                  17.134866000000102,
                                  66.16032600000011
                              ],
                              [
                                  17.132147501000077,
                                  66.15883800000023
                              ],
                              [
                                  17.131939499000055,
                                  66.1577185000001
                              ],
                              [
                                  17.13163700000024,
                                  66.15727750000008
                              ],
                              [
                                  17.129761000000144,
                                  66.15600600000022
                              ],
                              [
                                  17.129437499000062,
                                  66.155224
                              ],
                              [
                                  17.129910000000223,
                                  66.15392850000006
                              ],
                              [
                                  17.1305620010001,
                                  66.15325
                              ],
                              [
                                  17.130497999000056,
                                  66.15218800000014
                              ],
                              [
                                  17.12992100100007,
                                  66.15179800000016
                              ],
                              [
                                  17.130172,
                                  66.15140600000012
                              ],
                              [
                                  17.132449500000234,
                                  66.15014850000006
                              ],
                              [
                                  17.133878500000094,
                                  66.14862050000016
                              ],
                              [
                                  17.133703,
                                  66.14800750000023
                              ],
                              [
                                  17.132852001000117,
                                  66.14767850000004
                              ],
                              [
                                  17.132808501000113,
                                  66.14695250000017
                              ],
                              [
                                  17.134399500000086,
                                  66.14581300000015
                              ],
                              [
                                  17.136427499000035,
                                  66.14276850000016
                              ],
                              [
                                  17.137364,
                                  66.14225350000004
                              ],
                              [
                                  17.137606,
                                  66.14169100000021
                              ],
                              [
                                  17.1394145000001,
                                  66.13959900000015
                              ],
                              [
                                  17.13936249900013,
                                  66.138761
                              ],
                              [
                                  17.1409060000002,
                                  66.13684150000006
                              ],
                              [
                                  17.141127,
                                  66.13594250000011
                              ],
                              [
                                  17.14163100100012,
                                  66.13515350000029
                              ],
                              [
                                  17.14146050000005,
                                  66.13459450000005
                              ],
                              [
                                  17.140886500000022,
                                  66.13426350000009
                              ],
                              [
                                  17.140528000000245,
                                  66.13292050000024
                              ],
                              [
                                  17.139812999000128,
                                  66.13253650000019
                              ],
                              [
                                  17.139860999000234,
                                  66.13107850000006
                              ],
                              [
                                  17.13883750100007,
                                  66.13013650000022
                              ],
                              [
                                  17.136157001000072,
                                  66.12926750000008
                              ],
                              [
                                  17.1358630000002,
                                  66.12898750000016
                              ],
                              [
                                  17.135325501000125,
                                  66.12697400000019
                              ],
                              [
                                  17.1354030010001,
                                  66.12596850000017
                              ],
                              [
                                  17.135668001000056,
                                  66.12579650000004
                              ],
                              [
                                  17.136516500000198,
                                  66.12382450000007
                              ],
                              [
                                  17.13756150000006,
                                  66.12280600000025
                              ],
                              [
                                  17.13856899900003,
                                  66.121228
                              ],
                              [
                                  17.13838650100024,
                                  66.12049850000017
                              ],
                              [
                                  17.137531000000195,
                                  66.12006200000013
                              ],
                              [
                                  17.137634500000217,
                                  66.11950050000007
                              ],
                              [
                                  17.142531501000235,
                                  66.11367750000011
                              ],
                              [
                                  17.144032499000048,
                                  66.11113950000004
                              ],
                              [
                                  17.1447154990002,
                                  66.108725
                              ],
                              [
                                  17.14496938546877,
                                  66.108264
                              ],
                              [
                                  17.089768,
                                  66.108264
                              ],
                              [
                                  17.089768,
                                  66.180336
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  17.212602,
                                  66.180336
                              ],
                              [
                                  17.212602,
                                  66.108264
                              ],
                              [
                                  17.165474345598607,
                                  66.108264
                              ],
                              [
                                  17.165428001000237,
                                  66.10862950000012
                              ],
                              [
                                  17.164575999000135,
                                  66.110485
                              ],
                              [
                                  17.164721499000223,
                                  66.11283800000012
                              ],
                              [
                                  17.164204000000154,
                                  66.113403
                              ],
                              [
                                  17.163845499000047,
                                  66.11430300000023
                              ],
                              [
                                  17.16186099900017,
                                  66.11572450000006
                              ],
                              [
                                  17.161223000000177,
                                  66.11657300000013
                              ],
                              [
                                  17.161358501000166,
                                  66.11875550000019
                              ],
                              [
                                  17.159912000000134,
                                  66.11994800000014
                              ],
                              [
                                  17.157883,
                                  66.12288100000012
                              ],
                              [
                                  17.157018001000097,
                                  66.12457500000022
                              ],
                              [
                                  17.156835501000103,
                                  66.126088
                              ],
                              [
                                  17.156218001000184,
                                  66.12727250000006
                              ],
                              [
                                  17.155994500000077,
                                  66.13040950000004
                              ],
                              [
                                  17.15575700100004,
                                  66.13103100000012
                              ],
                              [
                                  17.156133500000237,
                                  66.134893
                              ],
                              [
                                  17.156621999000095,
                                  66.13606350000009
                              ],
                              [
                                  17.156907499000113,
                                  66.13623150000018
                              ],
                              [
                                  17.156999000000155,
                                  66.13768850000014
                              ],
                              [
                                  17.15558049900011,
                                  66.13938250000012
                              ],
                              [
                                  17.155878,
                                  66.13971650000025
                              ],
                              [
                                  17.156074999000055,
                                  66.14066550000018
                              ],
                              [
                                  17.15196200000014,
                                  66.14350850000022
                              ],
                              [
                                  17.150419001000046,
                                  66.14537450000006
                              ],
                              [
                                  17.14979149900006,
                                  66.146447
                              ],
                              [
                                  17.14972349900006,
                                  66.147569
                              ],
                              [
                                  17.148722000000134,
                                  66.14931300000018
                              ],
                              [
                                  17.148062501000084,
                                  66.14982550000008
                              ],
                              [
                                  17.147964999000067,
                                  66.15049900000014
                              ],
                              [
                                  17.14867750000019,
                                  66.15082949999999
                              ],
                              [
                                  17.14894250000009,
                                  66.15514150000018
                              ],
                              [
                                  17.148741500000085,
                                  66.15637650000025
                              ],
                              [
                                  17.148912000999985,
                                  66.15911950000014
                              ],
                              [
                                  17.148296001000176,
                                  66.160358
                              ],
                              [
                                  17.148487001000035,
                                  66.163496
                              ],
                              [
                                  17.147192499000198,
                                  66.16720750000019
                              ],
                              [
                                  17.146995000000118,
                                  66.16850050000005
                              ],
                              [
                                  17.147079501000064,
                                  66.17214100000012
                              ],
                              [
                                  17.146321500000056,
                                  66.173323
                              ],
                              [
                                  17.14501200000018,
                                  66.17451350000016
                              ],
                              [
                                  17.144944,
                                  66.17569350000025
                              ],
                              [
                                  17.14229850000015,
                                  66.177735
                              ],
                              [
                                  17.14063343540685,
                                  66.180336
                              ],
                              [
                                  17.212602,
                                  66.180336
                              ]
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "bjorntjarn",
                  "name": "BJ\u00d6RNTJ\u00c4RN",
                  "target": "Laisvall",
                  "tier": "HIGH",
                  "priority": "HIGH",
                  "commodity": "Mo",
                  "grades": "0.11% Mo",
                  "area_ha": 2948,
                  "openPct": 100,
                  "color": "#3b82f6",
                  "description": "BJ\u00d6RNTJ\u00c4RN: Mo. 2948 ha (HydroLAKES clipped).",
                  "center": [
                      17.699,
                      66.33
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              17.65,
                              66.36
                          ],
                          [
                              17.75,
                              66.36
                          ],
                          [
                              17.75,
                              66.34117373980564
                          ],
                          [
                              17.748323,
                              66.34114300000016
                          ],
                          [
                              17.748396000000184,
                              66.34035750000015
                          ],
                          [
                              17.749432001000173,
                              66.3393885000001
                          ],
                          [
                              17.749373501000093,
                              66.33866300000011
                          ],
                          [
                              17.74892149900012,
                              66.33827400000013
                          ],
                          [
                              17.749285501000088,
                              66.33759650000019
                          ],
                          [
                              17.75,
                              66.33729167407915
                          ],
                          [
                              17.75,
                              66.31131538744827
                          ],
                          [
                              17.746717001000206,
                              66.31150050000014
                          ],
                          [
                              17.745090499000128,
                              66.31202750000006
                          ],
                          [
                              17.736686000000134,
                              66.31146800000016
                          ],
                          [
                              17.736661499000093,
                              66.311186
                          ],
                          [
                              17.737877501000185,
                              66.31072250000005
                          ],
                          [
                              17.738647501000173,
                              66.30992750000013
                          ],
                          [
                              17.740402499000027,
                              66.30922850000013
                          ],
                          [
                              17.741016501000104,
                              66.30821150000014
                          ],
                          [
                              17.742356001000132,
                              66.3075810000002
                          ],
                          [
                              17.745088001000113,
                              66.30692750000003
                          ],
                          [
                              17.746910999000193,
                              66.30707050000018
                          ],
                          [
                              17.75,
                              66.30819585047318
                          ],
                          [
                              17.75,
                              66.3
                          ],
                          [
                              17.65,
                              66.3
                          ],
                          [
                              17.65,
                              66.36
                          ]
                      ],
                      [
                          [
                              17.722646500000053,
                              66.31698000000023
                          ],
                          [
                              17.725264500000094,
                              66.31492450000007
                          ],
                          [
                              17.72686199900022,
                              66.31400750000017
                          ],
                          [
                              17.728385501000105,
                              66.31387650000005
                          ],
                          [
                              17.731333499000073,
                              66.314172
                          ],
                          [
                              17.73164900100005,
                              66.31461650000006
                          ],
                          [
                              17.731568500000037,
                              66.31534850000003
                          ],
                          [
                              17.729457999000147,
                              66.316779
                          ],
                          [
                              17.726626500000123,
                              66.31793550000009
                          ],
                          [
                              17.72284850000017,
                              66.317762
                          ],
                          [
                              17.722548501000176,
                              66.317483
                          ],
                          [
                              17.722646500000053,
                              66.31698000000023
                          ]
                      ],
                      [
                          [
                              17.730901501000062,
                              66.34810150000021
                          ],
                          [
                              17.730712999000104,
                              66.34748900000011
                          ],
                          [
                              17.731348000000196,
                              66.34675
                          ],
                          [
                              17.733433000000105,
                              66.3466660000002
                          ],
                          [
                              17.73445100100008,
                              66.34716050000014
                          ],
                          [
                              17.735507,
                              66.3481520000002
                          ],
                          [
                              17.735462,
                              66.349274
                          ],
                          [
                              17.73495450000007,
                              66.34989900000011
                          ],
                          [
                              17.732734001000154,
                              66.35003900000021
                          ],
                          [
                              17.731722500000103,
                              66.351343
                          ],
                          [
                              17.730779500000097,
                              66.3517495000001
                          ],
                          [
                              17.72923900100011,
                              66.351656
                          ],
                          [
                              17.728652000000125,
                              66.351327
                          ],
                          [
                              17.72867500100017,
                              66.349869
                          ],
                          [
                              17.727103500000226,
                              66.34944
                          ],
                          [
                              17.727049501000067,
                              66.348768
                          ],
                          [
                              17.727709501000106,
                              66.34831100000014
                          ],
                          [
                              17.730901501000062,
                              66.34810150000021
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "pleutajokk",
                  "name": "PLEUTAJOKK",
                  "target": "Laisvall",
                  "tier": "MEDIUM",
                  "priority": "MEDIUM",
                  "commodity": "U",
                  "grades": "1000 ppm U",
                  "area_ha": 1077,
                  "openPct": 80,
                  "color": "#26a69a",
                  "description": "PLEUTAJOKK: U. 1077 ha (HydroLAKES clipped).",
                  "center": [
                      17.585,
                      66.261
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              17.519933,
                              66.271832
                          ],
                          [
                              17.631867,
                              66.271832
                          ],
                          [
                              17.631867,
                              66.247577
                          ],
                          [
                              17.576345595768313,
                              66.247577
                          ],
                          [
                              17.57361450100018,
                              66.24922750000025
                          ],
                          [
                              17.573845499000072,
                              66.250458
                          ],
                          [
                              17.569243001000075,
                              66.25393300000013
                          ],
                          [
                              17.566373500000168,
                              66.25458450000025
                          ],
                          [
                              17.563609,
                              66.254786
                          ],
                          [
                              17.558988500000055,
                              66.25618800000024
                          ],
                          [
                              17.550749001000213,
                              66.25751900000017
                          ],
                          [
                              17.54508749900009,
                              66.25803550000012
                          ],
                          [
                              17.544283499000073,
                              66.25843950000007
                          ],
                          [
                              17.544178,
                              66.25888900000018
                          ],
                          [
                              17.543648,
                              66.25923200000017
                          ],
                          [
                              17.537158501000135,
                              66.25981500000017
                          ],
                          [
                              17.53268250100018,
                              66.26132700000011
                          ],
                          [
                              17.528849,
                              66.26215450000007
                          ],
                          [
                              17.526921501000118,
                              66.26240500000017
                          ],
                          [
                              17.5271585000001,
                              66.26184150000006
                          ],
                          [
                              17.52808650000003,
                              66.26121250000006
                          ],
                          [
                              17.527778000000126,
                              66.26082100000025
                          ],
                          [
                              17.524837500000046,
                              66.26052150000004
                          ],
                          [
                              17.523615500000233,
                              66.26093
                          ],
                          [
                              17.521136999000134,
                              66.26314600000012
                          ],
                          [
                              17.520812,
                              66.264383
                          ],
                          [
                              17.519933,
                              66.26478152882234
                          ],
                          [
                              17.519933,
                              66.271832
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "arnberg",
                  "name": "ARNBERG",
                  "target": "Skellefte\u00e5",
                  "tier": "OPTIONAL",
                  "priority": "OPTIONAL",
                  "commodity": "Cu",
                  "grades": "Cu + Fe-sulphides",
                  "area_ha": 463,
                  "openPct": 100,
                  "color": "#14b8a6",
                  "description": "ARNBERG: Cu. 463 ha (HydroLAKES clipped).",
                  "center": [
                      19.396,
                      64.885
                  ]
              },
              "geometry": {
                  "type": "MultiPolygon",
                  "coordinates": [
                      [
                          [
                              [
                                  19.369358,
                                  64.88659792119924
                              ],
                              [
                                  19.370067500000232,
                                  64.88634650000023
                              ],
                              [
                                  19.373172501000226,
                                  64.885877
                              ],
                              [
                                  19.375888000000145,
                                  64.88446450000004
                              ],
                              [
                                  19.379197500000032,
                                  64.88354550000025
                              ],
                              [
                                  19.379416001000095,
                                  64.88320350000004
                              ],
                              [
                                  19.38766649900009,
                                  64.88075550000013
                              ],
                              [
                                  19.390137500000094,
                                  64.88047150000006
                              ],
                              [
                                  19.39060000000012,
                                  64.88096750000005
                              ],
                              [
                                  19.393063500000153,
                                  64.88163550000007
                              ],
                              [
                                  19.395734000000118,
                                  64.88185400000015
                              ],
                              [
                                  19.399379500000066,
                                  64.88148300000012
                              ],
                              [
                                  19.401743500000123,
                                  64.8804155000002
                              ],
                              [
                                  19.406861499000172,
                                  64.87922750000024
                              ],
                              [
                                  19.417808,
                                  64.879182
                              ],
                              [
                                  19.420655,
                                  64.8797325000001
                              ],
                              [
                                  19.425274501,
                                  64.8797325000001
                              ],
                              [
                                  19.42559800000015,
                                  64.88017350000013
                              ],
                              [
                                  19.42293150000023,
                                  64.88096600000023
                              ],
                              [
                                  19.420716,
                                  64.882147
                              ],
                              [
                                  19.420828499000038,
                                  64.88298400000019
                              ],
                              [
                                  19.421385,
                                  64.88319500000011
                              ],
                              [
                                  19.42785549900009,
                                  64.88320350000004
                              ],
                              [
                                  19.429970001000186,
                                  64.8822540000001
                              ],
                              [
                                  19.430892500000027,
                                  64.88127950000018
                              ],
                              [
                                  19.43163699900009,
                                  64.8809245000001
                              ],
                              [
                                  19.432550501000094,
                                  64.88084400000014
                              ],
                              [
                                  19.432829,
                                  64.88094950000021
                              ],
                              [
                                  19.433042,
                                  64.8812893413925
                              ],
                              [
                                  19.433042,
                                  64.87624884471077
                              ],
                              [
                                  19.43298149900005,
                                  64.87623100000025
                              ],
                              [
                                  19.43249950000012,
                                  64.87461700000011
                              ],
                              [
                                  19.433042,
                                  64.87442136194908
                              ],
                              [
                                  19.433042,
                                  64.873886
                              ],
                              [
                                  19.369358,
                                  64.873886
                              ],
                              [
                                  19.369358,
                                  64.88659792119924
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  19.369358,
                                  64.900914
                              ],
                              [
                                  19.41040947998608,
                                  64.900914
                              ],
                              [
                                  19.410435501000222,
                                  64.90086500000024
                              ],
                              [
                                  19.41013250100025,
                                  64.89862750000009
                              ],
                              [
                                  19.409624499000188,
                                  64.89779550000003
                              ],
                              [
                                  19.409052500000087,
                                  64.89747250000005
                              ],
                              [
                                  19.40492700100009,
                                  64.89723550000008
                              ],
                              [
                                  19.404867001000127,
                                  64.89678800000013
                              ],
                              [
                                  19.405247,
                                  64.8966665000001
                              ],
                              [
                                  19.40836049900014,
                                  64.89625450000005
                              ],
                              [
                                  19.408424999000033,
                                  64.89575000000013
                              ],
                              [
                                  19.40563150100013,
                                  64.895593
                              ],
                              [
                                  19.40557199900013,
                                  64.89514550000013
                              ],
                              [
                                  19.405921500000034,
                                  64.89480050000014
                              ],
                              [
                                  19.405876499000044,
                                  64.89446450000008
                              ],
                              [
                                  19.404367999000044,
                                  64.89405250000004
                              ],
                              [
                                  19.403051001000108,
                                  64.894084
                              ],
                              [
                                  19.397828001000107,
                                  64.895499
                              ],
                              [
                                  19.39658299900009,
                                  64.89508000000012
                              ],
                              [
                                  19.39688850100009,
                                  64.89439900000019
                              ],
                              [
                                  19.397502000000145,
                                  64.89404750000011
                              ],
                              [
                                  19.399918,
                                  64.891407
                              ],
                              [
                                  19.40148700100019,
                                  64.89030050000008
                              ],
                              [
                                  19.404632,
                                  64.88719350000008
                              ],
                              [
                                  19.40470400000021,
                                  64.88674300000014
                              ],
                              [
                                  19.403729999000063,
                                  64.8863755000001
                              ],
                              [
                                  19.40365550000007,
                                  64.885816
                              ],
                              [
                                  19.406591499000058,
                                  64.88310500000017
                              ],
                              [
                                  19.40662600100012,
                                  64.88237650000008
                              ],
                              [
                                  19.406200999000077,
                                  64.8821625000001
                              ],
                              [
                                  19.40502299900004,
                                  64.88224450000007
                              ],
                              [
                                  19.40112000000022,
                                  64.88363250000015
                              ],
                              [
                                  19.399777,
                                  64.88445050000024
                              ],
                              [
                                  19.39896700100013,
                                  64.88531000000012
                              ],
                              [
                                  19.397370499000203,
                                  64.8861925000001
                              ],
                              [
                                  19.397313999000062,
                                  64.88675500000016
                              ],
                              [
                                  19.39774600000021,
                                  64.88702300000017
                              ],
                              [
                                  19.397651999000118,
                                  64.88730850000013
                              ],
                              [
                                  19.397031000000197,
                                  64.88760200000013
                              ],
                              [
                                  19.393454500000132,
                                  64.88847850000008
                              ],
                              [
                                  19.384794001000103,
                                  64.8898635000001
                              ],
                              [
                                  19.379879500000072,
                                  64.89161150000007
                              ],
                              [
                                  19.377892,
                                  64.89255750000007
                              ],
                              [
                                  19.376579499000115,
                                  64.893599
                              ],
                              [
                                  19.376223500000094,
                                  64.894897
                              ],
                              [
                                  19.376365500000134,
                                  64.89596150000011
                              ],
                              [
                                  19.37665100000015,
                                  64.89612100000016
                              ],
                              [
                                  19.379074001000106,
                                  64.896458
                              ],
                              [
                                  19.38303800000017,
                                  64.89647500000018
                              ],
                              [
                                  19.38479949900011,
                                  64.89682300000021
                              ],
                              [
                                  19.385115500000154,
                                  64.89721050000014
                              ],
                              [
                                  19.383852501000206,
                                  64.89763150000005
                              ],
                              [
                                  19.379165999000122,
                                  64.89813950000007
                              ],
                              [
                                  19.374426501000073,
                                  64.89825350000018
                              ],
                              [
                                  19.37010249900004,
                                  64.897517
                              ],
                              [
                                  19.369358,
                                  64.89753484247453
                              ],
                              [
                                  19.369358,
                                  64.900914
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  19.413219500000025,
                                  64.89345600000019
                              ],
                              [
                                  19.411197501000117,
                                  64.89350950000005
                              ],
                              [
                                  19.41048249900018,
                                  64.893832
                              ],
                              [
                                  19.4103485010001,
                                  64.89439250000015
                              ],
                              [
                                  19.41071349900011,
                                  64.89488650000004
                              ],
                              [
                                  19.411332501000118,
                                  64.8950870000001
                              ],
                              [
                                  19.412725001000126,
                                  64.8950665000001
                              ],
                              [
                                  19.414542,
                                  64.89444800000018
                              ],
                              [
                                  19.414469501000042,
                                  64.89392800000024
                              ],
                              [
                                  19.414077,
                                  64.893525
                              ],
                              [
                                  19.413219500000025,
                                  64.89345600000019
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  19.41160650000012,
                                  64.889454
                              ],
                              [
                                  19.410812499000144,
                                  64.88861550000013
                              ],
                              [
                                  19.410116499000026,
                                  64.8884705000001
                              ],
                              [
                                  19.409195999000133,
                                  64.888834
                              ],
                              [
                                  19.407164499000146,
                                  64.89035650000022
                              ],
                              [
                                  19.408016,
                                  64.89069500000022
                              ],
                              [
                                  19.408641000000102,
                                  64.890626
                              ],
                              [
                                  19.41131549900024,
                                  64.88978850000012
                              ],
                              [
                                  19.41160650000012,
                                  64.889454
                              ]
                          ]
                      ],
                      [
                          [
                              [
                                  19.418366999000057,
                                  64.88624550000003
                              ],
                              [
                                  19.41830250000004,
                                  64.8867495
                              ],
                              [
                                  19.417953001000058,
                                  64.8870950000001
                              ],
                              [
                                  19.41844600000013,
                                  64.887815
                              ],
                              [
                                  19.420256500000164,
                                  64.8875465000001
                              ],
                              [
                                  19.420869499000048,
                                  64.88719500000019
                              ],
                              [
                                  19.421268501000213,
                                  64.88622900000013
                              ],
                              [
                                  19.420820501000208,
                                  64.885849
                              ],
                              [
                                  19.42000050100006,
                                  64.88564450000007
                              ],
                              [
                                  19.41965,
                                  64.884023
                              ],
                              [
                                  19.419101500000238,
                                  64.88387
                              ],
                              [
                                  19.417189500000177,
                                  64.88436550000011
                              ],
                              [
                                  19.416613499000107,
                                  64.88499450000012
                              ],
                              [
                                  19.412839500000246,
                                  64.88637950000003
                              ],
                              [
                                  19.411747499000057,
                                  64.88707950000008
                              ],
                              [
                                  19.412232501000176,
                                  64.88774150000006
                              ],
                              [
                                  19.413415000000214,
                                  64.88771250000013
                              ],
                              [
                                  19.415917500000035,
                                  64.88669550000009
                              ],
                              [
                                  19.418366999000057,
                                  64.88624550000003
                              ]
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "maratjarnliden_expansion",
                  "name": "MARATJ\u00c4RNLIDEN EXPANSION",
                  "target": "Skellefte\u00e5",
                  "tier": "POTENTIAL",
                  "priority": "POTENTIAL",
                  "commodity": "Cu-Co-Ni",
                  "grades": "Cu-Co prospect open to south (BRAP 82119)",
                  "area_ha": 3200,
                  "openPct": 100,
                  "color": "#2dd4bf",
                  "description": "POTENTIAL EXPANSION: Maratj\u00e4rnliden Cu-Co prospect (discovered 1982) is open to the south. Matches old Kendrick nr 101 SE extent. Would capture strike continuation + soil geochem anomalies (GEOK 81002).",
                  "center": [
                      19.37,
                      64.77
                  ]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [
                      [
                          [
                              19.28,
                              64.785
                          ],
                          [
                              19.46,
                              64.785
                          ],
                          [
                              19.46,
                              64.75
                          ],
                          [
                              19.28,
                              64.75
                          ],
                          [
                              19.28,
                              64.785
                          ]
                      ]
                  ]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "remdalen",
                  "name": "REMDALEN [NEEDS VERIFICATION]",
                  "target": "Stekenjokk VMS District",
                  "tier": "VERIFY",
                  "priority": "VERIFY",
                  "commodity": "Cu-Zn",
                  "grades": "0.7 Mt @ 1.43% Cu, 2.74% Zn (SGU OREC02904)",
                  "area_ha": 2000,
                  "openPct": 100,
                  "color": "#78909c",
                  "description": "REMDALEN: From BRAP 00057. Adjacent to Stekenjokk mine (7 Mt produced). NEEDS PERMIT VERIFICATION.",
                  "center": [14.576, 65.204]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [[
                      [14.54, 65.19],
                      [14.61, 65.19],
                      [14.61, 65.22],
                      [14.54, 65.22],
                      [14.54, 65.19]
                  ]]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "unna_gaisartjakko",
                  "name": "UNNA GAISARTJÅKKO [NEEDS VERIFICATION]",
                  "target": "Stekenjokk VMS District",
                  "tier": "VERIFY",
                  "priority": "VERIFY",
                  "commodity": "Cu-Zn",
                  "grades": "1.0 Mt @ 0.8% Cu, 0.5% Zn (SGU OREC01554)",
                  "area_ha": 2000,
                  "openPct": 100,
                  "color": "#78909c",
                  "description": "UNNA GAISARTJÅKKO: From BRAP 00057. VMS prospect north of Stekenjokk. NEEDS PERMIT VERIFICATION.",
                  "center": [14.667, 65.363]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [[
                      [14.63, 65.35],
                      [14.70, 65.35],
                      [14.70, 65.38],
                      [14.63, 65.38],
                      [14.63, 65.35]
                  ]]
              }
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "ropen_vms",
                  "name": "ROPEN VMS",
                  "target": "Köli VMS Belt",
                  "tier": "MUST STAKE",
                  "priority": "MUST STAKE",
                  "commodity": "Zn-Pb-Ag-Au",
                  "grades": "11.38% Zn, 2.55% Pb, 253 g/t Ag, 0.3 g/t Au (BRAP 81566)",
                  "area_ha": ROPEN_VMS_REVIEW_AREA_HA,
                  "openPct": 100,
                  "color": "#00bcd4",
                  "overlapping_permits": [],
                  "overlapping_natura": [],
                  "description": ROPEN_VMS_REVIEW_DESCRIPTION,
                  "center": ROPEN_VMS_REVIEW_CENTER
              },
              "geometry": ROPEN_VMS_REVIEW_GEOMETRY
          },
          {
              "type": "Feature",
              "properties": {
                  "id": "bergslagen_south_au",
                  "name": "BERGSLAGEN SOUTH AU",
                  "target": "Bergslagen",
                  "tier": "STAKE OR LOSE",
                  "priority": "STAKE OR LOSE",
                  "commodity": "Au-Te",
                  "grades": "12,945 ppb Au (till), 37,212 ppb Te, 6 g/t Au bedrock (Morbergsf\u00e4ltet 1.28 Mt)",
                  "area_ha": 10800,
                  "openPct": 100,
                  "color": "#22c55e",
                  "description": "BERGSLAGEN SOUTH AU: Southern anomaly core below 5 competitor permits. 27 samples >1,000 ppb Au within 15 km, 13 in this polygon. Peak 12,945 ppb with Au-Te correlation (R\u00b2>0.8). 43 historic mineral occurrences. Zero active permits in south zone. Competitors (Explora Mineral, J\u00f6rns, McKnight) blocking north \u2014 this polygon captures the open southern dispersal train + potential bedrock sources. NOT YET CLIPPED for HydroLAKES (~9% lake coverage).",
                  "center": [15.535, 59.75]
              },
              "geometry": {
                  "type": "Polygon",
                  "coordinates": [[
                      [15.44, 59.80],
                      [15.63, 59.80],
                      [15.63, 59.70],
                      [15.44, 59.70],
                      [15.44, 59.80]
                  ]]
              }
          },
          {
          "type": "Feature",
          "properties": {
                    "id": "adakfaltet",
                    "name": "ADAKFALTET",
                    "target": "Skellefte VMS",
                    "tier": "BLOCKED",
                    "commodity": "Cu-Au-Ag",
                    "area_ha": 6278,
                    "openPct": 0,
                    "color": "#64748b",
                    "description": "BLOCKED - Transition Mineral Nordics renewal filed",
                    "center": [
                              18.64,
                              65.37
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  18.55436033180005,
                                                  65.3343090850639
                                        ],
                                        [
                                                  18.72563966819995,
                                                  65.3343090850639
                                        ],
                                        [
                                                  18.72563966819995,
                                                  65.40569091493612
                                        ],
                                        [
                                                  18.55436033180005,
                                                  65.40569091493612
                                        ],
                                        [
                                                  18.55436033180005,
                                                  65.3343090850639
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "aijajarvi",
                    "name": "AIJAJARVI",
                    "target": "Norrbotten Cu-Co",
                    "tier": "PHASE-GATED",
                    "commodity": "Cu-Co-Au",
                    "area_ha": 740,
                    "openPct": 24,
                    "color": "#14b8a6",
                    "description": "Phase-gated: 740 ha now, full 2982 ha Dec 2027",
                    "center": [
                              21.4146,
                              67.6649
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  21.5,
                                                  67.69500000000001
                                        ],
                                        [
                                                  21.500000000000004,
                                                  67.64500000000001
                                        ],
                                        [
                                                  21.37,
                                                  67.64500000000001
                                        ],
                                        [
                                                  21.369999999999997,
                                                  67.69500000000001
                                        ],
                                        [
                                                  21.422376673115334,
                                                  67.69501249297467
                                        ],
                                        [
                                                  21.422372148624756,
                                                  67.68931305331267
                                        ],
                                        [
                                                  21.416672524480596,
                                                  67.6854695982933
                                        ],
                                        [
                                                  21.437979180889315,
                                                  67.68063143899742
                                        ],
                                        [
                                                  21.44938762136761,
                                                  67.68481498036066
                                        ],
                                        [
                                                  21.470638662203424,
                                                  67.68890666342281
                                        ],
                                        [
                                                  21.476466484142637,
                                                  67.69500769905997
                                        ],
                                        [
                                                  21.5,
                                                  67.69500000000001
                                        ]
                              ],
                              [
                                        [
                                                  21.42237223152936,
                                                  67.67501935906758
                                        ],
                                        [
                                                  21.432881146001865,
                                                  67.66699792063487
                                        ],
                                        [
                                                  21.46302908136928,
                                                  67.66382665075231
                                        ],
                                        [
                                                  21.47305036599475,
                                                  67.6596314101874
                                        ],
                                        [
                                                  21.483207722666418,
                                                  67.67776974045638
                                        ],
                                        [
                                                  21.49018092087576,
                                                  67.68551960034652
                                        ],
                                        [
                                                  21.45712540995369,
                                                  67.6789834680293
                                        ],
                                        [
                                                  21.437979180889315,
                                                  67.68063143899742
                                        ],
                                        [
                                                  21.42237223152936,
                                                  67.67501935906758
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "klippen_au",
                    "name": "KLIPPEN AU",
                    "target": "Gold Line",
                    "tier": "FILE APR 10",
                    "commodity": "Au",
                    "area_ha": 954,
                    "openPct": 100,
                    "color": "#22d3ee",
                    "description": "Moratorium lifts Apr 10, 2026. File immediately.",
                    "center": [
                              17.975,
                              64.081
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  17.996762,
                                                  64.069072
                                        ],
                                        [
                                                  17.998738,
                                                  64.087443
                                        ],
                                        [
                                                  17.982065,
                                                  64.099462
                                        ],
                                        [
                                                  17.937458,
                                                  64.10486
                                        ],
                                        [
                                                  17.921062,
                                                  64.10519
                                        ],
                                        [
                                                  17.91984,
                                                  64.093539
                                        ],
                                        [
                                                  17.944424,
                                                  64.093043
                                        ],
                                        [
                                                  17.94395,
                                                  64.088562
                                        ],
                                        [
                                                  17.951528,
                                                  64.088409
                                        ],
                                        [
                                                  17.949582,
                                                  64.070037
                                        ],
                                        [
                                                  17.996762,
                                                  64.069072
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "w_belt",
                    "name": "DYRANF\u00c4LTET (3 CLAIMS)",
                    "target": "Central Sweden",
                    "tier": "STAKE NOW",
                    "commodity": "W-Cu-Co",
                    "area_ha": 4781,
                    "openPct": 100,
                    "color": "#26a69a",
                    "description": "3 claims: Eastern-N 1316 + Eastern-S 948 + Western 2518 ha",
                    "center": [
                              17.2,
                              62.06
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  17.3505,
                                                  62.0738
                                        ],
                                        [
                                                  17.3505,
                                                  62.1015
                                        ],
                                        [
                                                  17.4331,
                                                  62.1015
                                        ],
                                        [
                                                  17.4331,
                                                  62.0738
                                        ],
                                        [
                                                  17.3505,
                                                  62.0738
                                        ]
                              ],
                              [
                                        [
                                                  17.386942,
                                                  62.08386450000012
                                        ],
                                        [
                                                  17.38050900000019,
                                                  62.08443600000015
                                        ],
                                        [
                                                  17.376444000000106,
                                                  62.084482
                                        ],
                                        [
                                                  17.374092500000188,
                                                  62.082821500000136
                                        ],
                                        [
                                                  17.374634499000024,
                                                  62.08163950000005
                                        ],
                                        [
                                                  17.37568949900009,
                                                  62.08117900000019
                                        ],
                                        [
                                                  17.37831100000011,
                                                  62.08098300000023
                                        ],
                                        [
                                                  17.381563000000142,
                                                  62.081453000000124
                                        ],
                                        [
                                                  17.38184950100009,
                                                  62.08245950000003
                                        ],
                                        [
                                                  17.382477000000222,
                                                  62.083071500000074
                                        ],
                                        [
                                                  17.38546600000018,
                                                  62.0830375000001
                                        ],
                                        [
                                                  17.38691250100021,
                                                  62.08324550000009
                                        ],
                                        [
                                                  17.386942,
                                                  62.08386450000012
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "w_belt",
                    "name": "DYRANF\u00c4LTET CLAIM 2",
                    "target": "Central Sweden",
                    "tier": "STAKE NOW",
                    "commodity": "W-Cu-Co",
                    "area_ha": 0,
                    "openPct": 100,
                    "color": "#26a69a",
                    "description": "Dyranf\u00e4ltet sub-claim 2",
                    "center": [
                              17.2,
                              62.06
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  17.4266,
                                                  62.0017
                                        ],
                                        [
                                                  17.4266,
                                                  62.0279
                                        ],
                                        [
                                                  17.3646,
                                                  62.0279
                                        ],
                                        [
                                                  17.3646,
                                                  62.0017
                                        ],
                                        [
                                                  17.4266,
                                                  62.0017
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "w_belt",
                    "name": "DYRANF\u00c4LTET CLAIM 3",
                    "target": "Central Sweden",
                    "tier": "STAKE NOW",
                    "commodity": "W-Cu-Co",
                    "area_ha": 0,
                    "openPct": 100,
                    "color": "#26a69a",
                    "description": "Dyranf\u00e4ltet sub-claim 3",
                    "center": [
                              17.2,
                              62.06
                    ]
          },
          "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [
                              [
                                        [
                                                  [
                                                            17.1037,
                                                            62.0663
                                                  ],
                                                  [
                                                            17.1037,
                                                            62.0257
                                                  ],
                                                  [
                                                            16.9868,
                                                            62.0257
                                                  ],
                                                  [
                                                            16.9868,
                                                            62.0663
                                                  ],
                                                  [
                                                            17.001514467812267,
                                                            62.0663
                                                  ],
                                                  [
                                                            17.001521999000033,
                                                            62.06629450000014
                                                  ],
                                                  [
                                                            17.002949500000057,
                                                            62.06605800000011
                                                  ],
                                                  [
                                                            17.003166000000192,
                                                            62.06544100000025
                                                  ],
                                                  [
                                                            17.003516500000103,
                                                            62.06521400000014
                                                  ],
                                                  [
                                                            17.003476999000043,
                                                            62.06409250000024
                                                  ],
                                                  [
                                                            17.00394649900022,
                                                            62.06380550000023
                                                  ],
                                                  [
                                                            17.006321000000128,
                                                            62.06339450000024
                                                  ],
                                                  [
                                                            17.007841500000097,
                                                            62.06243050000006
                                                  ],
                                                  [
                                                            17.009262499000215,
                                                            62.062023
                                                  ],
                                                  [
                                                            17.010072501000025,
                                                            62.061289
                                                  ],
                                                  [
                                                            17.01149800000013,
                                                            62.06099850000004
                                                  ],
                                                  [
                                                            17.012553,
                                                            62.060482
                                                  ],
                                                  [
                                                            17.017188999000098,
                                                            62.05971950000003
                                                  ],
                                                  [
                                                            17.01716,
                                                            62.05893
                                                  ],
                                                  [
                                                            17.018570000000125,
                                                            62.058249
                                                  ],
                                                  [
                                                            17.01902750000005,
                                                            62.05768400000028
                                                  ],
                                                  [
                                                            17.019733499000154,
                                                            62.057341500000064
                                                  ],
                                                  [
                                                            17.021755499000108,
                                                            62.05709900000011
                                                  ],
                                                  [
                                                            17.021848499000157,
                                                            62.056313
                                                  ],
                                                  [
                                                            17.023266501000194,
                                                            62.05591
                                                  ],
                                                  [
                                                            17.02322000000015,
                                                            62.05456450000014
                                                  ],
                                                  [
                                                            17.024604499000077,
                                                            62.0532065000001
                                                  ],
                                                  [
                                                            17.03123600100014,
                                                            62.05152350000003
                                                  ],
                                                  [
                                                            17.034688999000025,
                                                            62.05116050000004
                                                  ],
                                                  [
                                                            17.03549800000019,
                                                            62.05042200000014
                                                  ],
                                                  [
                                                            17.03649450000006,
                                                            62.04822800000011
                                                  ],
                                                  [
                                                            17.038755,
                                                            62.0479835000001
                                                  ],
                                                  [
                                                            17.040765,
                                                            62.047351000000106
                                                  ],
                                                  [
                                                            17.04445650000008,
                                                            62.047039
                                                  ],
                                                  [
                                                            17.04777800000022,
                                                            62.046395
                                                  ],
                                                  [
                                                            17.050295000000233,
                                                            62.0466550000001
                                                  ],
                                                  [
                                                            17.05160650100015,
                                                            62.04653100000024
                                                  ],
                                                  [
                                                            17.052647000000206,
                                                            62.04562450000003
                                                  ],
                                                  [
                                                            17.0534785010002,
                                                            62.045451
                                                  ],
                                                  [
                                                            17.06194450000021,
                                                            62.045042
                                                  ],
                                                  [
                                                            17.061614001000123,
                                                            62.04577650000016
                                                  ],
                                                  [
                                                            17.059625,
                                                            62.046916500000236
                                                  ],
                                                  [
                                                            17.05954699900019,
                                                            62.048039000000244
                                                  ],
                                                  [
                                                            17.05346,
                                                            62.04825500000021
                                                  ],
                                                  [
                                                            17.052640000000224,
                                                            62.04865750000005
                                                  ],
                                                  [
                                                            17.052777501000037,
                                                            62.0491590000002
                                                  ],
                                                  [
                                                            17.053514501000052,
                                                            62.04971300000011
                                                  ],
                                                  [
                                                            17.053411501000085,
                                                            62.050162500000056
                                                  ],
                                                  [
                                                            17.04969950000003,
                                                            62.04991350000017
                                                  ],
                                                  [
                                                            17.048511500000018,
                                                            62.05014850000009
                                                  ],
                                                  [
                                                            17.047432001000118,
                                                            62.050046
                                                  ],
                                                  [
                                                            17.0461,
                                                            62.04955150000018
                                                  ],
                                                  [
                                                            17.04419799900012,
                                                            62.04979250000008
                                                  ],
                                                  [
                                                            17.043977000000154,
                                                            62.050297000000114
                                                  ],
                                                  [
                                                            17.045435499000064,
                                                            62.05096150000003
                                                  ],
                                                  [
                                                            17.045453500000065,
                                                            62.05146400000018
                                                  ],
                                                  [
                                                            17.044527501000175,
                                                            62.052315500000134
                                                  ],
                                                  [
                                                            17.044196501000215,
                                                            62.05304550000022
                                                  ],
                                                  [
                                                            17.044350001000055,
                                                            62.054
                                                  ],
                                                  [
                                                            17.044847999000126,
                                                            62.05455650000005
                                                  ],
                                                  [
                                                            17.0460655010001,
                                                            62.055164000000104
                                                  ],
                                                  [
                                                            17.049310001000208,
                                                            62.05564250000003
                                                  ],
                                                  [
                                                            17.050164,
                                                            62.056137500000034
                                                  ],
                                                  [
                                                            17.051852500000223,
                                                            62.05657550000018
                                                  ],
                                                  [
                                                            17.05309050000011,
                                                            62.05774000000025
                                                  ],
                                                  [
                                                            17.052982499000052,
                                                            62.05807750000025
                                                  ],
                                                  [
                                                            17.05131000000017,
                                                            62.05809250000004
                                                  ],
                                                  [
                                                            17.05025500000022,
                                                            62.058663
                                                  ],
                                                  [
                                                            17.044342500000084,
                                                            62.06034000000011
                                                  ],
                                                  [
                                                            17.042933499000128,
                                                            62.06107950000012
                                                  ],
                                                  [
                                                            17.036226000000113,
                                                            62.06068650000009
                                                  ],
                                                  [
                                                            17.03443550100019,
                                                            62.06075550000014
                                                  ],
                                                  [
                                                            17.031586000000118,
                                                            62.061284000000114
                                                  ],
                                                  [
                                                            17.025737501000208,
                                                            62.06150150000013
                                                  ],
                                                  [
                                                            17.023017999000103,
                                                            62.062306
                                                  ],
                                                  [
                                                            17.02078550000016,
                                                            62.06338950000003
                                                  ],
                                                  [
                                                            17.019973,
                                                            62.06412800000015
                                                  ],
                                                  [
                                                            17.019754000000148,
                                                            62.06463250000007
                                                  ],
                                                  [
                                                            17.018694,
                                                            62.06509050000011
                                                  ],
                                                  [
                                                            17.017384000000106,
                                                            62.065272500000106
                                                  ],
                                                  [
                                                            17.016578501000083,
                                                            62.06611850000007
                                                  ],
                                                  [
                                                            17.014912001000084,
                                                            62.06629900000013
                                                  ],
                                                  [
                                                            17.014909942249105,
                                                            62.0663
                                                  ],
                                                  [
                                                            17.1037,
                                                            62.0663
                                                  ]
                                        ],
                                        [
                                                  [
                                                            17.00283,
                                                            62.03189050000003
                                                  ],
                                                  [
                                                            17.004158500000074,
                                                            62.032327500000235
                                                  ],
                                                  [
                                                            17.00406550000008,
                                                            62.03306
                                                  ],
                                                  [
                                                            17.00383300100009,
                                                            62.03322800000012
                                                  ],
                                                  [
                                                            17.00387050000012,
                                                            62.03429550000004
                                                  ],
                                                  [
                                                            17.001407,
                                                            62.03555050000017
                                                  ],
                                                  [
                                                            17.000447,
                                                            62.035442500000215
                                                  ],
                                                  [
                                                            16.99959400000023,
                                                            62.03494700000016
                                                  ],
                                                  [
                                                            16.99835200000018,
                                                            62.03360750000019
                                                  ],
                                                  [
                                                            16.998450000000105,
                                                            62.032991500000094
                                                  ],
                                                  [
                                                            16.999357,
                                                            62.03157950000019
                                                  ],
                                                  [
                                                            17.00126299900012,
                                                            62.03145550000005
                                                  ],
                                                  [
                                                            17.00283,
                                                            62.03189050000003
                                                  ]
                                        ]
                              ],
                              [
                                        [
                                                  [
                                                            17.038360499000134,
                                                            62.050289000000134
                                                  ],
                                                  [
                                                            17.03717300000011,
                                                            62.05046550000014
                                                  ],
                                                  [
                                                            17.036589000000106,
                                                            62.05086550000016
                                                  ],
                                                  [
                                                            17.036740001000226,
                                                            62.05170350000003
                                                  ],
                                                  [
                                                            17.037229,
                                                            62.0520355000001
                                                  ],
                                                  [
                                                            17.03997,
                                                            62.051845
                                                  ],
                                                  [
                                                            17.040152500000033,
                                                            62.050273000000175
                                                  ],
                                                  [
                                                            17.038360499000134,
                                                            62.050289000000134
                                                  ]
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ropen_vms_confirmed",
                    "name": "ROPEN ORIGINAL",
                    "target": "Koli VMS Belt",
                    "tier": "APPLICATION REVIEW",
                    "commodity": "Zn-Pb-Ag-Au",
                    "area_ha": ROPEN_VMS_REVIEW_AREA_HA,
                    "openPct": 100,
                    "color": "#00bcd4",
                    "comparison": true,
                    "description": ROPEN_VMS_REVIEW_DESCRIPTION,
                    "center": ROPEN_VMS_REVIEW_CENTER
          },
          "geometry": ROPEN_VMS_REVIEW_GEOMETRY
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ropen_vms_east_extension",
                    "name": "ROPEN FILED POLYGON",
                    "target": "Koli VMS Belt",
                    "tier": "APPLICATION REVIEW",
                    "commodity": "Zn-Pb-Ag-Au-Cu",
                    "area_ha": ROPEN_VMS_EXTENSION_AREA_HA,
                    "openPct": 100,
                    "color": "#cddc39",
                    "comparison": true,
                    "description": ROPEN_VMS_EXTENSION_DESCRIPTION,
                    "center": ROPEN_VMS_EXTENSION_CENTER
          },
          "geometry": ROPEN_VMS_EXTENSION_GEOMETRY
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ropen_vms_updated",
                    "name": "ROPEN UPDATED",
                    "target": "Koli VMS Belt",
                    "tier": "APPLICATION REVIEW",
                    "commodity": "Zn-Pb-Ag-Au",
                    "area_ha": ROPEN_VMS_UPDATED_AREA_HA,
                    "openPct": 100,
                    "color": "#4caf50",
                    "description": ROPEN_VMS_UPDATED_DESCRIPTION,
                    "center": ROPEN_VMS_UPDATED_CENTER
          },
          "geometry": ROPEN_VMS_UPDATED_GEOMETRY
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ropen_vms_split",
                    "name": "ROPEN SPLIT",
                    "target": "Koli VMS Belt",
                    "tier": "APPLICATION REVIEW",
                    "commodity": "Zn-Pb-Ag-Au-Cu",
                    "area_ha": ROPEN_VMS_SPLIT_AREA_HA,
                    "openPct": 100,
                    "color": "#38bdf8",
                    "comparison": true,
                    "description": ROPEN_VMS_SPLIT_DESCRIPTION,
                    "center": ROPEN_VMS_SPLIT_CENTER
          },
          "geometry": ROPEN_VMS_SPLIT_GEOMETRY
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ropen_vms_shoreline",
                    "name": "ROPEN SHORELINE",
                    "target": "Koli VMS Belt",
                    "tier": "APPLICATION REVIEW",
                    "commodity": "Zn-Pb-Ag-Au-Cu",
                    "area_ha": ROPEN_VMS_SHORELINE_AREA_HA,
                    "openPct": 100,
                    "color": "#2dd4bf",
                    "comparison": true,
                    "description": ROPEN_VMS_SHORELINE_DESCRIPTION,
                    "center": ROPEN_VMS_SHORELINE_CENTER
          },
          "geometry": ROPEN_VMS_SHORELINE_GEOMETRY
},
          {
          "type": "Feature",
          "properties": {
                    "id": "svardsjo_norra_w",
                    "name": "SV\u00c4RDSJ\u00d6 NORRA W",
                    "target": "Bergslagen W Corridor",
                    "tier": "STAKE NOW",
                    "priority": "STAKE NOW",
                    "commodity": "W-Zn-Pb-Cu-Ag",
                    "grades": "W 258.9 ppm in mine core, W 25.1 ppm till, Zn 6% at Sv\u00e4rdsj\u00f6 mine",
                    "area_ha": 1200,
                    "openPct": 100,
                    "color": "#26a69a",
                    "description": "Sv\u00e4rdsj\u00f6 Norra W: Gap between Growthrock/District Metals (W, exp 2027-05) and Rolling Road/Flint Hill (E, exp 2028-04). SVE261 intersected skarn + limestone at cluster. W 258.9 ppm in mine drill core. W NEVER assayed in exploration holes.",
                    "center": [15.893, 60.745]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [[
                              [15.870, 60.724], [15.905, 60.724], [15.905, 60.770], [15.870, 60.770], [15.870, 60.724]
                    ]]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "lurgarden_w",
                    "name": "LURG\u00c5RDEN W-SKARN",
                    "target": "Bergslagen W Corridor",
                    "tier": "TIER 1",
                    "priority": "TIER 1",
                    "commodity": "W-Sn-Bi-Cu",
                    "grades": "W 50 ppm in ROA14 drill core, W 22.7 ppm till peak, Cu 728 ppm in skarn",
                    "area_ha": 6535,
                    "openPct": 100,
                    "color": "#80cbc4",
                    "description": "Lurg\u00e5rden W-Skarn: 71 W-anomalous till samples (largest in portfolio). Sandvik Tooling AB held W permit (2006-09). W 50 ppm + Cu 728 ppm in magnetite skarn at ROA14. 5/8 holes unassayed. 27 km NW of Sv\u00e4rdsj\u00f6.",
                    "center": [15.621, 60.75]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [[
                              [15.5125, 60.7322], [15.6900, 60.7322], [15.6900, 60.8010], [15.5125, 60.8010], [15.5125, 60.7322]
                    ]]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "fajsmyran_w",
                    "name": "FAJSMYRAN W-SKARN",
                    "target": "Bergslagen W Corridor",
                    "tier": "TIER 1",
                    "priority": "TIER 1",
                    "commodity": "W-Sn-Bi-Mo",
                    "grades": "W 32.4 ppm till @ 0.68 km, area max 77.3 ppm, 36 samples >p95",
                    "area_ha": 1500,
                    "openPct": 100,
                    "color": "#80cbc4",
                    "description": "Fajsmyran W-Skarn: strongest W-till inner zone in the Bergslagen portfolio. 9 Boliden Cu holes (1985); W never assayed. 100% open. Centroid polygon (no final design yet).",
                    "center": [16.9338, 60.6954]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [[
                              [16.898156352866753, 60.67795412907114], [16.96944364713325, 60.67795412907114], [16.96944364713325, 60.71284587092886], [16.898156352866753, 60.71284587092886], [16.898156352866753, 60.67795412907114]
                    ]]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "hofors_torsaker_w",
                    "name": "HOFORS-TORS\u00c5KER W-Mo",
                    "target": "Bergslagen W Corridor",
                    "tier": "STAKING CONFIRMED",
                    "priority": "STAKING CONFIRMED",
                    "commodity": "W-Mo-Cu",
                    "grades": "3m @ 0.55% W + 0.42% Cu (Halvarsgruvberget Bh 79001). Scheelite at 7 mine levels.",
                    "area_ha": 749,
                    "openPct": 100,
                    "color": "#26a69a",
                    "description": "Hofors-Tors\u00e5ker W-Mo: 749 ha N of Boliden Torsaker nr 1001. 8 W occurrences in 7 km district. Vingesbackegruvan 2,035 m clear. Staking confirmed.",
                    "center": [16.397, 60.527]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [[
                              [16.3698, 60.5189], [16.4239, 60.5061], [16.4251, 60.5346], [16.3705, 60.5352], [16.3698, 60.5189]
                    ]]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "saxe_au_te",
                    "name": "SAXE NR 1 (FILED)",
                    "target": "Bergslagen Au-Te",
                    "tier": "FILED",
                    "commodity": "Pb,Au,Cu,Ag,Zn",
                    "area_ha": 26636,
                    "openPct": 0,
                    "status": "FILED 2026-05-19, dnr BS 200-988-2026",
                    "color": "#22c55e",
                    "description": "SAXE NR 1 (FILED): undersökningstillstånd filed 2026-05-19, dnr BS 200-988-2026, holder Adelheid Holdings LLC. See Saxe — Deep Dive (2026-05) layers for filed/design/till/geophysics detail.",
                    "center": [
                              15.6135,
                              60.0256
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [[15.352936748292807, 60.07727853043803], [15.352828943776249, 60.08401322219542], [15.375350020624188, 60.08871027044543], [15.396903411347541, 60.10211619330704], [15.398557612818765, 60.10507439415207], [15.412765722638603, 60.105030854128486], [15.41006153042844, 60.09004391956032], [15.401317023343584, 60.08055276679471], [15.414083334211243, 60.07386868157769], [15.449992781181045, 60.078420680747136], [15.433310313617962, 60.09445945439935], [15.440464803700783, 60.105121225282325], [15.469959884619698, 60.105019798245515], [15.469876935728564, 60.13707595083482], [15.623696628037392, 60.1370696015573], [15.620508311614612, 60.12041124857774], [15.563951636765562, 60.09159466618576], [15.5950477747219, 60.08080931596765], [15.603321114482277, 60.06557764564413], [15.603306292645028, 60.06417343770434], [15.60408465353287, 60.064171411198735], [15.609163931719548, 60.05481239037197], [15.640388938162046, 60.0588927028289], [15.637664876181768, 60.0614097269209], [15.640860842402725, 60.06479249172792], [15.797156869603924, 60.12152626296851], [15.799996363034767, 60.12260792011187], [15.799973315933897, 60.06527347876438], [15.729573119595486, 60.027966423397196], [15.74126748458748, 60.01891270861172], [15.7360408283285, 60.01378760050853], [15.735917272153053, 60.013878156392984], [15.728600725129045, 60.01557986373761], [15.727887250881173, 60.015763441368634], [15.722991721626792, 60.01336578196666], [15.719324882294005, 60.00970419096528], [15.715921785639335, 60.009902231309184], [15.712472147497476, 60.00794541100623], [15.70877565934735, 60.011197918357276], [15.705250334745918, 60.01409029438205], [15.702939730753801, 60.01509033975387], [15.699890821705262, 60.01510652493769], [15.697007924224755, 60.014493232059074], [15.697568349973185, 60.00703721334018], [15.700405043141181, 60.00549562579251], [15.69700373250819, 59.997252365782195], [15.702530222874303, 59.99578637302875], [15.702620041537086, 59.99156553025914], [15.698780525976334, 59.98799407013592], [15.699293636532554, 59.98682397249835], [15.701562337791891, 59.98393841755192], [15.705992154372, 59.98158013111765], [15.716913785304477, 59.98116208147907], [15.735334186762335, 59.987615941061854], [15.740522966028774, 59.9872277433126], [15.73886808942983, 59.985351310310016], [15.738626999371336, 59.982568993306614], [15.733795609242547, 59.9747836015116], [15.728486712242793, 59.9777763145472], [15.720222418491772, 59.97674404710433], [15.71856337981699, 59.974597961077464], [15.715007934951618, 59.97587442848259], [15.716468220927768, 59.9771236757086], [15.714867462059704, 59.977671111376246], [15.7059348097086, 59.978886558400205], [15.696167987923053, 59.98306900903674], [15.691164164539025, 59.983723919881626], [15.682969813751535, 59.98610122700639], [15.682484012197095, 59.98861805300171], [15.6783695304804, 59.98899845288243], [15.675198620811097, 59.99170856964748], [15.672881370703667, 59.992348928902054], [15.668129164831997, 59.99668327188249], [15.668156319873601, 59.998030065388065], [15.666743894271063, 59.999114711064465], [15.662785683334794, 59.99841632298318], [15.665058353836866, 59.99553139390771], [15.664667515733582, 59.993917098079976], [15.665741081728347, 59.9938218237219], [15.669821833764237, 59.99173594902759], [15.673876515945016, 59.98839283335599], [15.684497358581167, 59.981962732523634], [15.684460290959725, 59.98016700943225], [15.683187300066873, 59.97056539491144], [15.67749742216118, 59.96386002726321], [15.676855332294702, 59.949944848847544], [15.586812042907527, 59.95010656713128], [15.533859575513736, 59.957964072227405], [15.51299264702412, 59.974928242051924], [15.509321645409319, 59.97503223132376], [15.514703568660554, 59.98704382356168], [15.492271449952543, 59.997096561912706], [15.476402420598735, 60.00308112758937], [15.440338737601012, 60.02825873299264], [15.417446356188796, 60.06272381622244], [15.405233495861653, 60.06285157316322], [15.39394193912494, 60.06495095006308], [15.396531225472865, 60.071138910110875], [15.395526241814999, 60.07724783539981], [15.391436788195008, 60.08094146323032], [15.37870369583217, 60.083313188766304], [15.375540970746934, 60.073893932377665], [15.352936748292807, 60.07727853043803]]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "pershytte_east",
                    "name": "PERSHYTTE EAST",
                    "target": "Bergslagen Au-Te",
                    "tier": "STAKE NOW",
                    "commodity": "Au-Te-Cu",
                    "area_ha": 22167,
                    "openPct": 99.9,
                    "color": "#3b82f6",
                    "description": "PERSHYTTE EAST: Au-Te-Cu. ~22,167 ha designed polygon. W edge buffered 208m from Gyttorp nr 100 (exp Jun 2026). NW corner trimmed to avoid Striberg nr 100. Au median 263.5 ppb (n=50), Au-Te r=0.917, Te 81.8% >5,000 ppb. 7 operators over 33 yrs — none tested Au-Te. Undrilled.",
                    "center": [
                              15.1029,
                              59.4647
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  14.985,
                                                  59.39
                                        ],
                                        [
                                                  15.22,
                                                  59.39
                                        ],
                                        [
                                                  15.22,
                                                  59.54
                                        ],
                                        [
                                                  15.01,
                                                  59.54
                                        ],
                                        [
                                                  14.985,
                                                  59.53
                                        ],
                                        [
                                                  14.985,
                                                  59.39
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "bottengruvan",
                    "name": "BOTTENGRUVAN",
                    "target": "Bergslagen Au-Te",
                    "tier": "STAKE NOW",
                    "commodity": "Au-Te-Sb-Cu",
                    "area_ha": 5000,
                    "openPct": 100,
                    "color": "#3b82f6",
                    "description": "BOTTENGRUVAN: Au-Te-Sb-Cu. Centroid polygon (no final design yet).",
                    "center": [
                              15.1266,
                              59.6689
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  15.0635269374387,
                                                  59.6370483431898
                                        ],
                                        [
                                                  15.1896730625613,
                                                  59.6370483431898
                                        ],
                                        [
                                                  15.1896730625613,
                                                  59.700751656810205
                                        ],
                                        [
                                                  15.0635269374387,
                                                  59.700751656810205
                                        ],
                                        [
                                                  15.0635269374387,
                                                  59.6370483431898
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ingagruvan_south",
                    "name": "INGAGRUVAN-SOUTH",
                    "target": "Bergslagen Au-Te",
                    "tier": "STAKE NOW",
                    "commodity": "Au-Te-Cu",
                    "area_ha": 10000,
                    "openPct": 100,
                    "color": "#3b82f6",
                    "description": "INGAGRUVAN-SOUTH: Au-Te-Cu. Centroid polygon (no final design yet).",
                    "center": [
                              15.239,
                              60.114
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  15.148598186746147,
                                                  60.068954954954954
                                        ],
                                        [
                                                  15.329401813253854,
                                                  60.068954954954954
                                        ],
                                        [
                                                  15.329401813253854,
                                                  60.15904504504504
                                        ],
                                        [
                                                  15.148598186746147,
                                                  60.15904504504504
                                        ],
                                        [
                                                  15.148598186746147,
                                                  60.068954954954954
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ostanfalla",
                    "name": "OSTANFALLA",
                    "target": "Bergslagen Au-Te",
                    "tier": "STAKE NOW",
                    "commodity": "Au-Te-Sb-Mo",
                    "area_ha": 1000,
                    "openPct": 100,
                    "color": "#3b82f6",
                    "description": "OSTANFALLA: Au-Te-Sb-Mo. Centroid polygon (no final design yet).",
                    "center": [
                              14.9767,
                              59.0507
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  14.949002031781134,
                                                  59.036455506035274
                                        ],
                                        [
                                                  15.004397968218864,
                                                  59.036455506035274
                                        ],
                                        [
                                                  15.004397968218864,
                                                  59.064944493964724
                                        ],
                                        [
                                                  14.949002031781134,
                                                  59.064944493964724
                                        ],
                                        [
                                                  14.949002031781134,
                                                  59.036455506035274
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "koitelainen",
                    "name": "KOITELAINEN",
                    "target": "Lapland",
                    "tier": "STAKE NOW",
                    "commodity": "Cr-V-PGE",
                    "area_ha": 9307,
                    "openPct": 100,
                    "color": "#2196f3",
                    "description": "Koitelainen layered intrusion. EU CRM trifecta.",
                    "center": [
                              27.24,
                              67.82
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  27.08,
                                                  67.822827
                                        ],
                                        [
                                                  27.083171,
                                                  67.826829
                                        ],
                                        [
                                                  27.086778,
                                                  67.829789
                                        ],
                                        [
                                                  27.090892,
                                                  67.831988
                                        ],
                                        [
                                                  27.095357,
                                                  67.833342
                                        ],
                                        [
                                                  27.1,
                                                  67.833799
                                        ],
                                        [
                                                  27.104643,
                                                  67.833342
                                        ],
                                        [
                                                  27.109005,
                                                  67.832019
                                        ],
                                        [
                                                  27.136385,
                                                  67.841864
                                        ],
                                        [
                                                  27.170581,
                                                  67.849162
                                        ],
                                        [
                                                  27.172426,
                                                  67.855
                                        ],
                                        [
                                                  27.385884,
                                                  67.855
                                        ],
                                        [
                                                  27.389243,
                                                  67.852243
                                        ],
                                        [
                                                  27.393683,
                                                  67.846833
                                        ],
                                        [
                                                  27.395714,
                                                  67.843034
                                        ],
                                        [
                                                  27.4,
                                                  67.841896
                                        ],
                                        [
                                                  27.4,
                                                  67.788
                                        ],
                                        [
                                                  27.08,
                                                  67.788
                                        ],
                                        [
                                                  27.08,
                                                  67.822827
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "kaitaselka",
                    "name": "KAITASELKA",
                    "target": "Lapland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu-Au-PGE",
                    "area_ha": 276,
                    "openPct": 100,
                    "color": "#4caf50",
                    "description": "Expanded 276 ha corridor outside Natura 2000. Captures the open strip between FI1301716 and the Magnus permit stack. FILE NOW.",
                    "center": [
                              27.3,
                              67.874
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [27.243757485716, 67.88069188814],
                                        [27.30072798319, 67.882734551032],
                                        [27.351532537104, 67.876951879075],
                                        [27.356539756635, 67.875969910164],
                                        [27.356586553102, 67.875547781393],
                                        [27.356573123022, 67.874668335582],
                                        [27.307512641748, 67.881284625711],
                                        [27.28622544355, 67.880968821319],
                                        [27.286428725911, 67.878956025146],
                                        [27.289734541245, 67.878298000983],
                                        [27.292168890166, 67.879755863308],
                                        [27.300450668673, 67.879984207241],
                                        [27.302895931154, 67.878330274559],
                                        [27.299717651826, 67.875553164756],
                                        [27.322896030426, 67.87375436994],
                                        [27.330739263078, 67.87587217554],
                                        [27.334870847974, 67.875320256589],
                                        [27.335203415293, 67.873829946507],
                                        [27.348891795711, 67.873606094102],
                                        [27.350227983278, 67.872690322588],
                                        [27.348986702397, 67.87140779669],
                                        [27.356204344929, 67.870331638235],
                                        [27.356044053482, 67.869939636162],
                                        [27.355464107682, 67.86908720926],
                                        [27.354665474936, 67.868260312057],
                                        [27.353655862955, 67.867466901485],
                                        [27.352445006805, 67.866714611459],
                                        [27.351044574575, 67.866010679532],
                                        [27.349468054597, 67.86536187739],
                                        [27.349299602728, 67.865304925266],
                                        [27.344, 67.8683],
                                        [27.3386, 67.8711],
                                        [27.3278, 67.8721],
                                        [27.3011, 67.8747],
                                        [27.2972, 67.8746],
                                        [27.2919, 67.8743],
                                        [27.2618, 67.8738],
                                        [27.2499, 67.8734],
                                        [27.2471, 67.8724],
                                        [27.243773029624, 67.872870892233],
                                        [27.243693056882, 67.87308642388],
                                        [27.243594048276, 67.873939967574],
                                        [27.243620516488, 67.876473107805],
                                        [27.249558337484, 67.876885157255],
                                        [27.248658516647, 67.87838134792],
                                        [27.251307315823, 67.879374077348],
                                        [27.277354472988, 67.880094366693],
                                        [27.267003461456, 67.881453646122],
                                        [27.243691003139, 67.880103774328],
                                        [27.243765242893, 67.880624199351],
                                        [27.243757485716, 67.88069188814]
                              ],
                              [
                                        [27.279272820292, 67.880158465257],
                                        [27.279418039109, 67.880206465166],
                                        [27.279039055279, 67.880182443845],
                                        [27.279272820292, 67.880158465257]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "salla_gold",
                    "name": "SALLA CORRIDOR",
                    "target": "Lapland",
                    "tier": "STAKE NOW",
                    "commodity": "Au",
                    "area_ha": 5885,
                    "openPct": 100,
                    "color": "#2196f3",
                    "description": "Virgin ground. 564 ppb Au till.",
                    "center": [
                              26.8,
                              67.33
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  26.59379144596203,
                                                  67.15
                                        ],
                                        [
                                                  26.59379144596203,
                                                  67.3118854426592
                                        ],
                                        [
                                                  26.601687937469805,
                                                  67.30928845566798
                                        ],
                                        [
                                                  26.601725793758394,
                                                  67.30927683097808
                                        ],
                                        [
                                                  26.63102987336684,
                                                  67.30090953662952
                                        ],
                                        [
                                                  26.646208554037973,
                                                  67.29450713665403
                                        ],
                                        [
                                                  26.646208554037973,
                                                  67.15
                                        ],
                                        [
                                                  26.59379144596203,
                                                  67.15
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "paljakka",
                    "name": "PALJAKKA",
                    "target": "Central Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu-Au",
                    "area_ha": 5720,
                    "openPct": 100,
                    "color": "#38bdf8",
                    "description": "11.94% Cu bedrock (OKU-type). v3 5,720 ha — 100% OPEN, 0 ha overlap with any active/karenssi permit.",
                    "center": [
                              28.2935,
                              63.4336
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  28.226218082438873,
                                                  63.42710291194926
                                        ],
                                        [
                                                  28.233017901216456,
                                                  63.47684578449101
                                        ],
                                        [
                                                  28.29761329189931,
                                                  63.482825011406824
                                        ],
                                        [
                                                  28.325472911712648,
                                                  63.47214351049066
                                        ],
                                        [
                                                  28.34282824730423,
                                                  63.46744311196053
                                        ],
                                        [
                                                  28.342521881392788,
                                                  63.46610484698906
                                        ],
                                        [
                                                  28.345834358798466,
                                                  63.462460771542574
                                        ],
                                        [
                                                  28.351708072485394,
                                                  63.46232853365006
                                        ],
                                        [
                                                  28.353921027814625,
                                                  63.46125153804589
                                        ],
                                        [
                                                  28.350848104196814,
                                                  63.45995378366027
                                        ],
                                        [
                                                  28.346820639335068,
                                                  63.4607520320774
                                        ],
                                        [
                                                  28.345551392341545,
                                                  63.46048223549478
                                        ],
                                        [
                                                  28.345380211172337,
                                                  63.45913013990557
                                        ],
                                        [
                                                  28.346756936540572,
                                                  63.45790676804867
                                        ],
                                        [
                                                  28.345248524682454,
                                                  63.4566328632456
                                        ],
                                        [
                                                  28.34507938581336,
                                                  63.452752401246684
                                        ],
                                        [
                                                  28.360558447616594,
                                                  63.42619915535239
                                        ],
                                        [
                                                  28.372078624555765,
                                                  63.40827794663721
                                        ],
                                        [
                                                  28.304264086681115,
                                                  63.39681448982691
                                        ],
                                        [
                                                  28.30267758645275,
                                                  63.39704594367442
                                        ],
                                        [
                                                  28.289696383879846,
                                                  63.38744196977765
                                        ],
                                        [
                                                  28.20406471219772,
                                                  63.41061793998345
                                        ],
                                        [
                                                  28.226218082438873,
                                                  63.42710291194926
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "paljakka_karenssi",
                    "name": "PALJAKKA +KARENSSI",
                    "target": "Central Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu-Au",
                    "area_ha": 5960,
                    "openPct": 100,
                    "color": "#2dd4bf",
                    "description": "v3 + 240 ha south wedge recoverable when VA2024:0003 karenssi clears (on/after 2026-01-10).",
                    "center": [
                              28.3,
                              63.42
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  28.226218082438873,
                                                  63.42710291194926
                                        ],
                                        [
                                                  28.233036015415646,
                                                  63.476852697713895
                                        ],
                                        [
                                                  28.29761329189931,
                                                  63.482825011406824
                                        ],
                                        [
                                                  28.325472911712648,
                                                  63.47214351049066
                                        ],
                                        [
                                                  28.342828247304226,
                                                  63.46744311196053
                                        ],
                                        [
                                                  28.34252188139278,
                                                  63.466104846989076
                                        ],
                                        [
                                                  28.345834358798466,
                                                  63.462460771542574
                                        ],
                                        [
                                                  28.349384980872426,
                                                  63.4625888956794
                                        ],
                                        [
                                                  28.353660456245485,
                                                  63.46169716821337
                                        ],
                                        [
                                                  28.351068414653994,
                                                  63.45982816039479
                                        ],
                                        [
                                                  28.35139689496637,
                                                  63.45926439822307
                                        ],
                                        [
                                                  28.36895902044634,
                                                  63.45679262065072
                                        ],
                                        [
                                                  28.374265223784377,
                                                  63.45000894217975
                                        ],
                                        [
                                                  28.355345565861647,
                                                  63.4360330307172
                                        ],
                                        [
                                                  28.37216978950336,
                                                  63.4082905138354
                                        ],
                                        [
                                                  28.304264086681115,
                                                  63.39681448982691
                                        ],
                                        [
                                                  28.30267758645275,
                                                  63.39704594367442
                                        ],
                                        [
                                                  28.289723224367307,
                                                  63.387443109844504
                                        ],
                                        [
                                                  28.20406471219772,
                                                  63.41061793998345
                                        ],
                                        [
                                                  28.226218082438873,
                                                  63.42710291194926
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "paljakka_v4",
                    "name": "PALJAKKA V4 (R314)",
                    "target": "Central Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu-Au",
                    "area_ha": 6243,
                    "openPct": 100,
                    "color": "#22c55e",
                    "description": "v4: karenssi cleared + eastern tongue capturing Halla-Aho R314 drill collar (Phase 0 re-assay target). Preferred filing once VA2024:0003-01 karenssi is verified expired on TUKES live.",
                    "center": [
                              28.33,
                              63.43
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  28.226218037363257,
                                                  63.427102918232336
                                        ],
                                        [
                                                  28.233040260775656,
                                                  63.476847491312554
                                        ],
                                        [
                                                  28.297606148026308,
                                                  63.48282351205811
                                        ],
                                        [
                                                  28.325472931672454,
                                                  63.47214352281969
                                        ],
                                        [
                                                  28.342813087768615,
                                                  63.467446779290626
                                        ],
                                        [
                                                  28.342521881392795,
                                                  63.46610484698905
                                        ],
                                        [
                                                  28.345834358798427,
                                                  63.462460771542595
                                        ],
                                        [
                                                  28.349386766924326,
                                                  63.46258810182683
                                        ],
                                        [
                                                  28.353644479815006,
                                                  63.46169623262717
                                        ],
                                        [
                                                  28.351068414653994,
                                                  63.45982816039479
                                        ],
                                        [
                                                  28.35139689496637,
                                                  63.45926439822307
                                        ],
                                        [
                                                  28.368962141412037,
                                                  63.456787477930895
                                        ],
                                        [
                                                  28.374259967103725,
                                                  63.450006545809714
                                        ],
                                        [
                                                  28.35534564369958,
                                                  63.43603304934037
                                        ],
                                        [
                                                  28.385683142257186,
                                                  63.43541705441305
                                        ],
                                        [
                                                  28.390271282537892,
                                                  63.429549362658314
                                        ],
                                        [
                                                  28.3896850141716,
                                                  63.41744688222105
                                        ],
                                        [
                                                  28.36649502957631,
                                                  63.41765422321283
                                        ],
                                        [
                                                  28.372149001535902,
                                                  63.40828847346462
                                        ],
                                        [
                                                  28.304277239820127,
                                                  63.39681671672187
                                        ],
                                        [
                                                  28.302677606150304,
                                                  63.39704591992319
                                        ],
                                        [
                                                  28.289703668980113,
                                                  63.38745000616565
                                        ],
                                        [
                                                  28.20408744831189,
                                                  63.41061339471555
                                        ],
                                        [
                                                  28.226218037363257,
                                                  63.427102918232336
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "tervola_cu",
                    "name": "TERVOLA",
                    "target": "Lapland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu",
                    "area_ha": 3500,
                    "openPct": 96,
                    "color": "#2196f3",
                    "description": "13,900 ppm Cu till. IOCG corridor.",
                    "center": [
                              25.72,
                              66.12
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  25.660137769325466,
                                                  66.14757325600665
                                        ],
                                        [
                                                  25.71689303410202,
                                                  66.14944914526961
                                        ],
                                        [
                                                  25.75529361860193,
                                                  66.13930183383418
                                        ],
                                        [
                                                  25.805455687635035,
                                                  66.1216783861694
                                        ],
                                        [
                                                  25.80597492961578,
                                                  66.11063733522356
                                        ],
                                        [
                                                  25.791424216956354,
                                                  66.09308932992361
                                        ],
                                        [
                                                  25.745201400189238,
                                                  66.08923370687438
                                        ],
                                        [
                                                  25.702001323201852,
                                                  66.09375855055768
                                        ],
                                        [
                                                  25.635284051061067,
                                                  66.12922306763669
                                        ],
                                        [
                                                  25.648455669392565,
                                                  66.1404974638965
                                        ],
                                        [
                                                  25.660137769325466,
                                                  66.14757325600665
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "kuusivaara",
                    "name": "KUUSIVAARA",
                    "target": "Finland",
                    "tier": "COMPETITIVE",
                    "commodity": "Au-Co",
                    "area_ha": 3000,
                    "openPct": 100,
                    "color": "#14b8a6",
                    "description": "KUUSIVAARA: Au-Co. Centroid polygon (no final design yet).",
                    "center": [
                              26.3,
                              67.43
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  26.2357180205402,
                                                  67.405327812725
                                        ],
                                        [
                                                  26.3642819794598,
                                                  67.405327812725
                                        ],
                                        [
                                                  26.3642819794598,
                                                  67.45467218727501
                                        ],
                                        [
                                                  26.2357180205402,
                                                  67.45467218727501
                                        ],
                                        [
                                                  26.2357180205402,
                                                  67.405327812725
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "tongosoivanaapa",
                    "name": "TONGOSOIVANAAPA",
                    "target": "Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Ni-Cu-PGE",
                    "area_ha": 15000,
                    "openPct": 100,
                    "color": "#2196f3",
                    "description": "TONGOSOIVANAAPA: Ni-Cu-PGE. Centroid polygon (no final design yet).",
                    "center": [
                              27.85,
                              67.75
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  27.704301097713575,
                                                  67.69483131209948
                                        ],
                                        [
                                                  27.995698902286428,
                                                  67.69483131209948
                                        ],
                                        [
                                                  27.995698902286428,
                                                  67.80516868790052
                                        ],
                                        [
                                                  27.704301097713575,
                                                  67.80516868790052
                                        ],
                                        [
                                                  27.704301097713575,
                                                  67.69483131209948
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ritakallionmaa",
                    "name": "RITAKALLIONMAA",
                    "target": "Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Au",
                    "area_ha": 3242.7,
                    "openPct": 100,
                    "color": "#2196f3",
                    "description": "RITAKALLIONMAA v3 (2026-04-24): 3,242.7 ha filing-optimized Au polygon. Retains 30/30 collars, all digitized geochem points, v1 drilled core, all mapping footprints, and key corrected-IP/resistivity/overview geophysics; trims v2 NW Natura-near corner and non-core raster frame. Zero TUKES/Natura overlap; nearest active permit Dragon Mining Jokisivu 10 ~1.97 km.",
                    "center": [
                              22.749416,
                              61.11249
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  22.70387807,
                                                  61.13592192
                                        ],
                                        [
                                                  22.80207927,
                                                  61.13860507
                                        ],
                                        [
                                                  22.80668319,
                                                  61.08992905
                                        ],
                                        [
                                                  22.70633216,
                                                  61.08641328
                                        ],
                                        [
                                                  22.6963169,
                                                  61.0890613
                                        ],
                                        [
                                                  22.69143221,
                                                  61.12900675
                                        ],
                                        [
                                                  22.70387807,
                                                  61.13592192
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "raahe_ladoga",
                    "name": "RAAHE-LADOGA V3.1.5",
                    "target": "Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Au",
                    "area_ha": 3848,
                    "openPct": 100,
                    "color": "#22d3ee",
                    "description": "RAAHE-LADOGA V3.1.5 (2026-04-22): 38.48 km² (3,848 ha) MultiPolygon. Core 21.0 km² (Suo 7.97 g/t Au, Peltokangas Z3+Z5 visible gold, Till 83-70984 1,434 ppb Au #1 in GTK national DB) + Pannari extension 17.5 km² capturing 18 of 25 GTK Pannari-campaign drill holes (1,383 m: 14 Pannari/Peuralampi + 4 Kusiaiskangas). Kusiaiskangas is the origin of the Mo-Cu-W boulder that triggered the entire 1981 Pannari investigation — classic orogenic-Au pathfinder, never Au-assayed. Koivuneva (7 holes, GTK-classified 'essentially barren') deliberately excluded as poor cost/benefit. Zero active TUKES overlap. Natura 2000 overlap 0.30 km² (0.77%) — 400 m ring around R314 only.",
                    "center": [
                              24.22,
                              63.14
                    ]
          },
          "geometry": {
                    "type": "MultiPolygon",
                    "coordinates": [
                              [
                                        [
                                                  [
                                                            24.217914,
                                                            63.111008
                                                  ],
                                                  [
                                                            24.215645,
                                                            63.110169
                                                  ],
                                                  [
                                                            24.213205,
                                                            63.109435
                                                  ],
                                                  [
                                                            24.210617,
                                                            63.108813
                                                  ],
                                                  [
                                                            24.207908,
                                                            63.10831
                                                  ],
                                                  [
                                                            24.205101,
                                                            63.10793
                                                  ],
                                                  [
                                                            24.202226,
                                                            63.107677
                                                  ],
                                                  [
                                                            24.199311,
                                                            63.107554
                                                  ],
                                                  [
                                                            24.196382,
                                                            63.107561
                                                  ],
                                                  [
                                                            24.193469,
                                                            63.107698
                                                  ],
                                                  [
                                                            24.1906,
                                                            63.107964
                                                  ],
                                                  [
                                                            24.187803,
                                                            63.108357
                                                  ],
                                                  [
                                                            24.163612,
                                                            63.112356
                                                  ],
                                                  [
                                                            24.160827,
                                                            63.11289
                                                  ],
                                                  [
                                                            24.158177,
                                                            63.113549
                                                  ],
                                                  [
                                                            24.155688,
                                                            63.114327
                                                  ],
                                                  [
                                                            24.153386,
                                                            63.115216
                                                  ],
                                                  [
                                                            24.151296,
                                                            63.116205
                                                  ],
                                                  [
                                                            24.149438,
                                                            63.117286
                                                  ],
                                                  [
                                                            24.147832,
                                                            63.118447
                                                  ],
                                                  [
                                                            24.146495,
                                                            63.119675
                                                  ],
                                                  [
                                                            24.122676,
                                                            63.14467
                                                  ],
                                                  [
                                                            24.121664,
                                                            63.14589
                                                  ],
                                                  [
                                                            24.120918,
                                                            63.147149
                                                  ],
                                                  [
                                                            24.120444,
                                                            63.148434
                                                  ],
                                                  [
                                                            24.120247,
                                                            63.149734
                                                  ],
                                                  [
                                                            24.120329,
                                                            63.151036
                                                  ],
                                                  [
                                                            24.120689,
                                                            63.152329
                                                  ],
                                                  [
                                                            24.121324,
                                                            63.1536
                                                  ],
                                                  [
                                                            24.122227,
                                                            63.154837
                                                  ],
                                                  [
                                                            24.123391,
                                                            63.156029
                                                  ],
                                                  [
                                                            24.124804,
                                                            63.157164
                                                  ],
                                                  [
                                                            24.126454,
                                                            63.158233
                                                  ],
                                                  [
                                                            24.128325,
                                                            63.159224
                                                  ],
                                                  [
                                                            24.130398,
                                                            63.160128
                                                  ],
                                                  [
                                                            24.132656,
                                                            63.160938
                                                  ],
                                                  [
                                                            24.135077,
                                                            63.161645
                                                  ],
                                                  [
                                                            24.137637,
                                                            63.162243
                                                  ],
                                                  [
                                                            24.140314,
                                                            63.162727
                                                  ],
                                                  [
                                                            24.143081,
                                                            63.163091
                                                  ],
                                                  [
                                                            24.145913,
                                                            63.163332
                                                  ],
                                                  [
                                                            24.148784,
                                                            63.163448
                                                  ],
                                                  [
                                                            24.151666,
                                                            63.163438
                                                  ],
                                                  [
                                                            24.154532,
                                                            63.163302
                                                  ],
                                                  [
                                                            24.157356,
                                                            63.163042
                                                  ],
                                                  [
                                                            24.160111,
                                                            63.162659
                                                  ],
                                                  [
                                                            24.162771,
                                                            63.162157
                                                  ],
                                                  [
                                                            24.165311,
                                                            63.161542
                                                  ],
                                                  [
                                                            24.167708,
                                                            63.160818
                                                  ],
                                                  [
                                                            24.169938,
                                                            63.159993
                                                  ],
                                                  [
                                                            24.171982,
                                                            63.159074
                                                  ],
                                                  [
                                                            24.173819,
                                                            63.158071
                                                  ],
                                                  [
                                                            24.221809,
                                                            63.129062
                                                  ],
                                                  [
                                                            24.223446,
                                                            63.127962
                                                  ],
                                                  [
                                                            24.224836,
                                                            63.126794
                                                  ],
                                                  [
                                                            24.225965,
                                                            63.12557
                                                  ],
                                                  [
                                                            24.226823,
                                                            63.124302
                                                  ],
                                                  [
                                                            24.2274,
                                                            63.123001
                                                  ],
                                                  [
                                                            24.227593,
                                                            63.122127
                                                  ],
                                                  [
                                                            24.2242,
                                                            63.123
                                                  ],
                                                  [
                                                            24.2233,
                                                            63.1235
                                                  ],
                                                  [
                                                            24.2231,
                                                            63.1236
                                                  ],
                                                  [
                                                            24.2218,
                                                            63.1204
                                                  ],
                                                  [
                                                            24.2258,
                                                            63.1204
                                                  ],
                                                  [
                                                            24.227313,
                                                            63.118814
                                                  ],
                                                  [
                                                            24.226839,
                                                            63.117734
                                                  ],
                                                  [
                                                            24.225988,
                                                            63.116464
                                                  ],
                                                  [
                                                            24.224866,
                                                            63.115239
                                                  ],
                                                  [
                                                            24.223482,
                                                            63.11407
                                                  ],
                                                  [
                                                            24.221852,
                                                            63.112968
                                                  ],
                                                  [
                                                            24.219989,
                                                            63.111944
                                                  ],
                                                  [
                                                            24.217914,
                                                            63.111008
                                                  ]
                                        ]
                              ],
                              [
                                        [
                                                  [
                                                            24.419122,
                                                            63.150129
                                                  ],
                                                  [
                                                            24.419298,
                                                            63.149521
                                                  ],
                                                  [
                                                            24.419343,
                                                            63.148908
                                                  ],
                                                  [
                                                            24.419256,
                                                            63.148297
                                                  ],
                                                  [
                                                            24.419037,
                                                            63.147691
                                                  ],
                                                  [
                                                            24.418689,
                                                            63.147099
                                                  ],
                                                  [
                                                            24.418214,
                                                            63.146525
                                                  ],
                                                  [
                                                            24.417618,
                                                            63.145974
                                                  ],
                                                  [
                                                            24.416907,
                                                            63.145452
                                                  ],
                                                  [
                                                            24.416086,
                                                            63.144964
                                                  ],
                                                  [
                                                            24.415164,
                                                            63.144515
                                                  ],
                                                  [
                                                            24.414149,
                                                            63.144108
                                                  ],
                                                  [
                                                            24.413052,
                                                            63.143748
                                                  ],
                                                  [
                                                            24.411883,
                                                            63.143438
                                                  ],
                                                  [
                                                            24.410652,
                                                            63.143181
                                                  ],
                                                  [
                                                            24.409372,
                                                            63.14298
                                                  ],
                                                  [
                                                            24.408054,
                                                            63.142836
                                                  ],
                                                  [
                                                            24.406712,
                                                            63.142751
                                                  ],
                                                  [
                                                            24.405358,
                                                            63.142726
                                                  ],
                                                  [
                                                            24.40119,
                                                            63.142739
                                                  ],
                                                  [
                                                            24.399905,
                                                            63.14277
                                                  ],
                                                  [
                                                            24.398633,
                                                            63.142855
                                                  ],
                                                  [
                                                            24.397383,
                                                            63.142992
                                                  ],
                                                  [
                                                            24.396167,
                                                            63.143182
                                                  ],
                                                  [
                                                            24.394995,
                                                            63.143421
                                                  ],
                                                  [
                                                            24.393877,
                                                            63.143708
                                                  ],
                                                  [
                                                            24.362718,
                                                            63.152597
                                                  ],
                                                  [
                                                            24.3626,
                                                            63.1528
                                                  ],
                                                  [
                                                            24.3629,
                                                            63.1533
                                                  ],
                                                  [
                                                            24.3657,
                                                            63.1545
                                                  ],
                                                  [
                                                            24.368,
                                                            63.1548
                                                  ],
                                                  [
                                                            24.3631,
                                                            63.1596
                                                  ],
                                                  [
                                                            24.3612,
                                                            63.1595
                                                  ],
                                                  [
                                                            24.3588,
                                                            63.1667
                                                  ],
                                                  [
                                                            24.3433,
                                                            63.1653
                                                  ],
                                                  [
                                                            24.3426,
                                                            63.1657
                                                  ],
                                                  [
                                                            24.3389,
                                                            63.1663
                                                  ],
                                                  [
                                                            24.3381,
                                                            63.168
                                                  ],
                                                  [
                                                            24.334421,
                                                            63.168992
                                                  ],
                                                  [
                                                            24.334408,
                                                            63.168973
                                                  ],
                                                  [
                                                            24.334104,
                                                            63.168649
                                                  ],
                                                  [
                                                            24.333731,
                                                            63.168339
                                                  ],
                                                  [
                                                            24.333293,
                                                            63.168048
                                                  ],
                                                  [
                                                            24.332795,
                                                            63.167777
                                                  ],
                                                  [
                                                            24.332239,
                                                            63.16753
                                                  ],
                                                  [
                                                            24.331633,
                                                            63.167309
                                                  ],
                                                  [
                                                            24.330982,
                                                            63.167115
                                                  ],
                                                  [
                                                            24.330292,
                                                            63.166952
                                                  ],
                                                  [
                                                            24.32957,
                                                            63.166819
                                                  ],
                                                  [
                                                            24.328823,
                                                            63.16672
                                                  ],
                                                  [
                                                            24.328057,
                                                            63.166653
                                                  ],
                                                  [
                                                            24.327281,
                                                            63.166621
                                                  ],
                                                  [
                                                            24.326502,
                                                            63.166624
                                                  ],
                                                  [
                                                            24.325727,
                                                            63.166661
                                                  ],
                                                  [
                                                            24.324964,
                                                            63.166733
                                                  ],
                                                  [
                                                            24.32422,
                                                            63.166837
                                                  ],
                                                  [
                                                            24.323502,
                                                            63.166974
                                                  ],
                                                  [
                                                            24.322818,
                                                            63.167143
                                                  ],
                                                  [
                                                            24.322173,
                                                            63.167341
                                                  ],
                                                  [
                                                            24.321574,
                                                            63.167566
                                                  ],
                                                  [
                                                            24.321027,
                                                            63.167817
                                                  ],
                                                  [
                                                            24.320537,
                                                            63.168091
                                                  ],
                                                  [
                                                            24.320109,
                                                            63.168385
                                                  ],
                                                  [
                                                            24.319747,
                                                            63.168697
                                                  ],
                                                  [
                                                            24.319454,
                                                            63.169023
                                                  ],
                                                  [
                                                            24.319233,
                                                            63.169361
                                                  ],
                                                  [
                                                            24.319087,
                                                            63.169707
                                                  ],
                                                  [
                                                            24.319016,
                                                            63.170058
                                                  ],
                                                  [
                                                            24.319022,
                                                            63.17041
                                                  ],
                                                  [
                                                            24.319104,
                                                            63.17076
                                                  ],
                                                  [
                                                            24.319262,
                                                            63.171105
                                                  ],
                                                  [
                                                            24.319493,
                                                            63.171442
                                                  ],
                                                  [
                                                            24.319797,
                                                            63.171766
                                                  ],
                                                  [
                                                            24.320169,
                                                            63.172076
                                                  ],
                                                  [
                                                            24.320595,
                                                            63.172359
                                                  ],
                                                  [
                                                            24.3173,
                                                            63.1733
                                                  ],
                                                  [
                                                            24.3149,
                                                            63.1722
                                                  ],
                                                  [
                                                            24.3145,
                                                            63.1718
                                                  ],
                                                  [
                                                            24.3155,
                                                            63.1712
                                                  ],
                                                  [
                                                            24.312864,
                                                            63.169473
                                                  ],
                                                  [
                                                            24.291998,
                                                            63.198362
                                                  ],
                                                  [
                                                            24.291635,
                                                            63.198963
                                                  ],
                                                  [
                                                            24.291406,
                                                            63.199577
                                                  ],
                                                  [
                                                            24.291313,
                                                            63.200198
                                                  ],
                                                  [
                                                            24.291357,
                                                            63.20082
                                                  ],
                                                  [
                                                            24.291536,
                                                            63.201437
                                                  ],
                                                  [
                                                            24.291851,
                                                            63.202044
                                                  ],
                                                  [
                                                            24.292296,
                                                            63.202633
                                                  ],
                                                  [
                                                            24.292869,
                                                            63.203199
                                                  ],
                                                  [
                                                            24.293563,
                                                            63.203737
                                                  ],
                                                  [
                                                            24.294372,
                                                            63.204242
                                                  ],
                                                  [
                                                            24.295287,
                                                            63.204707
                                                  ],
                                                  [
                                                            24.2963,
                                                            63.20513
                                                  ],
                                                  [
                                                            24.297401,
                                                            63.205505
                                                  ],
                                                  [
                                                            24.300301,
                                                            63.206395
                                                  ],
                                                  [
                                                            24.301442,
                                                            63.20671
                                                  ],
                                                  [
                                                            24.302645,
                                                            63.206974
                                                  ],
                                                  [
                                                            24.303898,
                                                            63.207185
                                                  ],
                                                  [
                                                            24.305191,
                                                            63.207341
                                                  ],
                                                  [
                                                            24.306511,
                                                            63.207439
                                                  ],
                                                  [
                                                            24.307845,
                                                            63.20748
                                                  ],
                                                  [
                                                            24.309183,
                                                            63.207463
                                                  ],
                                                  [
                                                            24.31051,
                                                            63.207388
                                                  ],
                                                  [
                                                            24.311815,
                                                            63.207256
                                                  ],
                                                  [
                                                            24.313086,
                                                            63.207068
                                                  ],
                                                  [
                                                            24.314312,
                                                            63.206826
                                                  ],
                                                  [
                                                            24.31548,
                                                            63.206532
                                                  ],
                                                  [
                                                            24.31658,
                                                            63.206188
                                                  ],
                                                  [
                                                            24.317602,
                                                            63.205798
                                                  ],
                                                  [
                                                            24.318536,
                                                            63.205366
                                                  ],
                                                  [
                                                            24.411951,
                                                            63.157663
                                                  ],
                                                  [
                                                            24.412712,
                                                            63.157238
                                                  ],
                                                  [
                                                            24.413388,
                                                            63.156784
                                                  ],
                                                  [
                                                            24.413975,
                                                            63.156305
                                                  ],
                                                  [
                                                            24.414466,
                                                            63.155805
                                                  ],
                                                  [
                                                            24.418381,
                                                            63.151307
                                                  ],
                                                  [
                                                            24.418815,
                                                            63.150726
                                                  ],
                                                  [
                                                            24.419122,
                                                            63.150129
                                                  ]
                                        ]
                              ]
                    ]
          }
}
,
          {
          "type": "Feature",
          "properties": {
                    "id": "hammaslahti",
                    "name": "HAMMASLAHTI",
                    "target": "Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Cu-Zn-Au-Ag",
                    "area_ha": 3000,
                    "openPct": 100,
                    "color": "#2196f3",
                    "description": "HAMMASLAHTI: Cu-Zn-Au-Ag. Centroid polygon (no final design yet).",
                    "center": [
                              29.93,
                              62.43
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  29.876692990993362,
                                                  62.40532781272499
                                        ],
                                        [
                                                  29.983307009006637,
                                                  62.40532781272499
                                        ],
                                        [
                                                  29.983307009006637,
                                                  62.45467218727501
                                        ],
                                        [
                                                  29.876692990993362,
                                                  62.45467218727501
                                        ],
                                        [
                                                  29.876692990993362,
                                                  62.40532781272499
                                        ]
                              ]
                    ]
          }
},
          {
          "type": "Feature",
          "properties": {
                    "id": "ritakallionmaa_option_f",
                    "name": "RITAKALLIONMAA OPTION F — DISTRICT UMBRELLA VARAUS (FILING-READY)",
                    "target": "Finland",
                    "tier": "FILING-READY",
                    "commodity": "Au",
                    "area_ha": 14548,
                    "openPct": 100,
                    "color": "#0ea5e9",
                    "description": "OPTION F UMBRELLA VARAUS — FILING-READY (2026-05-17): 14,548 ha single contiguous district position designed as a ring around v3 (VA2026:0029-01 Ritakallionmaa, 3,243 ha), tightly contiguous on all four sides with zero internal gap. Outer envelope: west boundary = Jokisivu 10 (ML2018:0082-01) east edge, east boundary = v3 east + 7 km (WNW strike extension), south boundary = Dragon Uunimäki 2 (VA2026:0002-01) north edge, north boundary = v3 north edge. Inner boundary tracks v3 exactly so combined Option F + v3 covers the full district. Clipped against 19 TUKES tenures (Dragon Mining 13 + Nordkalk limestone 3 + other) and 13 Natura SAC/SPA features in district bbox — 28 ha total clipped, Vanhakoski SAC does not intersect. Filing estimate: ~EUR 21,900 varaus fee at EUR 1.50/ha + EUR 80 base. Timing: file before Dragon's end-2026 Uunimäki Resource catalyst triggers regional consolidation. NOTE: existing v3 varaus VA2026:0029-01 remains separate — Option F is a NEW second reservation for additional ground beyond v3. EAST-LOBE EVIDENCE UPGRADE 2026-05-18: source-record pull of 5 GTK Hakku assay batches (1,742 primary one-metre assays, 23 holes, 24 columns Au + full pathfinder suite) converts Palokallio inside the east lobe from \"5 narrative-summary intercepts\" to a documented multi-hole Au + full pathfinder dataset. 13 of 17 Palokallio holes ≥0.5 g/t Au (76%), 9 of 17 ≥1.0 g/t Au (53%); best ≥0.5 g/t composite R329 4.75 m @ 9.10 g/t Au (vs the GTK 2011 narrative headline 0.9 m @ 41.8 g/t single sample); widest ≥0.5 g/t composites R342 7.00 m @ 1.04 g/t + R327 6.00 m @ 0.76 g/t + R341 5.00 m @ 1.17 g/t. Pathfinder Spearman correlations on n=633 full-suite samples: Au-Bi ρ=0.86 (block-bootstrap 95% CI [+0.81, +0.89]) ≫ Au-Ag 0.70 ≈ Au-Te 0.66 ≈ Au-As 0.66 > Au-Cu 0.59 — diagnostic orogenic-Au signature matching the Jokisivu mine paragenesis (arsenopyrite + native Au + bismuthinite + petzite). Gabbro re-sectioning: drilled extent ~0.085 km² is the SE corner of a ~0.35 km² inferred Valjus 2010 geophysical envelope; W (~230 m), S (~215 m), E (~110 m) margins untested + R329 bonanza open below 120 m depth. Full per-meter data + intercept tables + Phase 2 fence targets in geomorphic-docs/finland/targets/ritakallionmaa-au.md § Palokallio satellite drilling.",
                    "center": [22.79, 61.085]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.767249295142,61.03095485939694],[22.668914200940602,61.027829983765876],[22.654311698733256,61.13433611482595],[22.70387807,61.13592192],[22.931687996364516,61.14297492170642],[22.94536490844398,61.03643110671086],[22.767249295142,61.03095485939694]],[[22.70387807,61.13592192],[22.691432200155056,61.12900677360322],[22.6963169,61.0890613],[22.706332160000002,61.086413279999995],[22.80668319,61.08992905],[22.802079270000004,61.13860506999998],[22.70387807,61.13592192]],[[22.706612304228745,61.062702680781655],[22.70636127515594,61.06338382342811],[22.70834935646957,61.065731699326875],[22.711262757910784,61.06572900726418],[22.71125857436495,61.0671296275229],[22.705233276192978,61.067136904810596],[22.70284910918891,61.06396329453759],[22.70287116256393,61.06333265602903],[22.705024869973503,61.0620922588465],[22.70663543369128,61.06214692083996],[22.70683595079515,61.0618818589163],[22.706650826575864,61.06115371425772],[22.707018571425714,61.06112949392657],[22.70721533372342,61.06190147216791],[22.706612304228745,61.062702680781655]],[[22.8752323135119,61.05222008273062],[22.877953879236923,61.05175349870089],[22.879019988142595,61.051316115401384],[22.879585748002253,61.051289848809326],[22.880176113290105,61.054627467831004],[22.8800850154227,61.054637576377786],[22.880109875231955,61.054762175989026],[22.87796567166023,61.054922973384755],[22.87738055611828,61.05505642652186],[22.87020958626762,61.05480174636253],[22.86977988408084,61.054228770075625],[22.870753923441104,61.05368342380716],[22.872871840996275,61.0528754129569],[22.8752323135119,61.05222008273062]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "dragon_jokisivu",
                    "name": "DRAGON JOKISIVU (4 mining areas + 4 expl permits + karenssi)",
                    "target": "Finland",
                    "tier": "COMPETITOR",
                    "commodity": "Au",
                    "area_ha": 600,
                    "openPct": 0,
                    "color": "#ff3333",
                    "description": "Dragon Mining Oy active Jokisivu cluster (union of KL2015:0005-01 Jokisivu 2 + KL2018:0010-01 Jokisivu 3 + KL2024:0005-01 Jokisivu 4 mining areas + ML2017:0131-02 Jokisivu 7-8 + ML2018:0082-01 Jokisivu 10 expl permits + ML2026:0009-01 Jokisivu 4-5 expl permit application filed 12 Feb 2026 + KAIVNRO 7244 mining district + karenssi-shadow ML2012:0112-03). Producing Au mine since 2009; UG dev to 645m level by 31 Dec 2025; ~3.7 Mt @ 2.8 g/t Au mined. Closest active permit (Jokisivu 10) sits 1.88 km W of Ritakallio v2 western boundary.",
                    "center": [22.6229, 61.1203]
          },
          "geometry": {"type":"MultiPolygon","coordinates":[[[[22.5811891667577,61.12283961549909],[22.584286499293004,61.12378883994641],[22.58792352706048,61.124031205164364],[22.589070051334385,61.1245816423669],[22.58950230153786,61.12532054377164],[22.59140892683707,61.12517544057006],[22.59318283406326,61.12572971624613],[22.59314485964349,61.12477122043118],[22.59434885813389,61.12397830888283],[22.596893194913147,61.12372714335696],[22.598562886442938,61.12402533926453],[22.597786121614348,61.1227834968988],[22.599024403476776,61.12174852434584],[22.601047122973565,61.121426805538974],[22.60372355405678,61.1219987391963],[22.605884325677355,61.12120241992377],[22.61522786777265,61.11796613790768],[22.61532483605453,61.1170415300472],[22.616828455233996,61.11602097363825],[22.618268317151927,61.115788864821646],[22.619697112197603,61.11592981991257],[22.621339773696853,61.11525565063341],[22.622857042440057,61.11419122749302],[22.625665384914264,61.1134194867077],[22.627275319033508,61.11335756181082],[22.628591698446726,61.11369637366949],[22.622274782338714,61.11502630301468],[22.62307276200243,61.1154853037531],[22.624839847860766,61.114903840972715],[22.625542296074233,61.114991670730134],[22.631086672136167,61.11361988691995],[22.634756453014862,61.11518916073676],[22.627670967668003,61.119485522463165],[22.626754431848806,61.12051159156214],[22.631870876493423,61.121356982368596],[22.62674930698584,61.1205175938849],[22.62629024037783,61.1210434588805],[22.62423405191438,61.12081618379524],[22.62111175005331,61.12232783919619],[22.623234337747274,61.123843233287445],[22.628852911649687,61.120865209520296],[22.634484556081805,61.121799376297496],[22.628844799001243,61.12087011709706],[22.623234487636587,61.12384326512168],[22.623234301933657,61.123843277085285],[22.62111168153688,61.12232789092454],[22.620785945757934,61.12248115586184],[22.620121323420527,61.12225847732319],[22.620540558917753,61.12206806270201],[22.619760253126586,61.12193686148874],[22.61772523935339,61.12231957546914],[22.616969677192035,61.12295258302296],[22.61582817066412,61.12330310306774],[22.613008154888252,61.12319115240087],[22.61185157923052,61.121904951308075],[22.610926436017607,61.1214380366807],[22.610583384277188,61.12076137032121],[22.61083145705781,61.120233701801496],[22.612353647339983,61.11926036124218],[22.614587875658664,61.119140289769994],[22.61517396274564,61.11861962956649],[22.614303934463273,61.11941445196268],[22.61570538078098,61.11983583935209],[22.617243439737504,61.1208129156769],[22.61754296548783,61.121761823326],[22.61832796199962,61.122204647613934],[22.61850443693843,61.12004177952221],[22.617919695746696,61.11991240831851],[22.617801235898877,61.11974539800241],[22.617801248316056,61.119745308487495],[22.618311514214312,61.1195805423451],[22.61758390831484,61.11944256710191],[22.61752143782183,61.1190034478076],[22.615227094262583,61.11862472722808],[22.61656253723442,61.11750407972431],[22.603724266950515,61.12199890628066],[22.60476774023628,61.12245988751721],[22.605210425042518,61.12321123670739],[22.604814015663006,61.12391164021204],[22.603868344079665,61.1244127486161],[22.601550747788863,61.12467334778454],[22.59980261945826,61.12435557634294],[22.59868562732849,61.12388724130439],[22.59988388403322,61.12521610694418],[22.59947425192462,61.126089242237285],[22.598784673596434,61.126460773553745],[22.59631315000437,61.12691505411214],[22.59478687715152,61.12678414097496],[22.593742364315055,61.12638416694896],[22.593765699610675,61.12653757579716],[22.605621990443364,61.13008702902055],[22.596306659029704,61.127301225715776],[22.596208776774628,61.12734220165924],[22.60006288014861,61.12852692483417],[22.605018768601216,61.13140816882188],[22.606938201566148,61.1312593628671],[22.609489204975137,61.13188528418243],[22.610686731301673,61.13316539342413],[22.61048784137842,61.134069747745066],[22.60945544871163,61.13479094385802],[22.61115297041837,61.13573339096482],[22.612719303882464,61.135634930628015],[22.614203184087692,61.13592095303797],[22.616363092137306,61.13716879799847],[22.616759377575473,61.137758528737905],[22.634172429386577,61.132054742760154],[22.640132024669867,61.13524087074635],[22.644776425967713,61.13332445270349],[22.643701783025165,61.132559608467226],[22.64280024685902,61.1309912641318],[22.643472352260265,61.130280796651256],[22.645093635489523,61.12978560186838],[22.64616837877808,61.129160381909784],[22.64769945100032,61.12883320662466],[22.649441932900743,61.128999594845304],[22.650560441384542,61.129562946965144],[22.651666275815302,61.13078839792366],[22.654945936457256,61.129727364596214],[22.654413878018634,61.127281417082266],[22.653162071785445,61.126108187479076],[22.65274744686396,61.125063685705626],[22.64947038767021,61.124485870235915],[22.647164347040228,61.12552339467962],[22.645045599545245,61.12590407076463],[22.64372218281689,61.125769620203585],[22.64255190088749,61.12527849985408],[22.64218963619902,61.12540776118509],[22.643231747056827,61.125909297549754],[22.64367551398114,61.12648152821331],[22.643121908427094,61.12747612368005],[22.64034790317305,61.12835517706124],[22.63899259915547,61.129063492394266],[22.636134014195054,61.12916867021198],[22.635587680276018,61.13002300801658],[22.6340560851269,61.130546186699576],[22.63228796756799,61.130519050198814],[22.630965840915863,61.13007003447772],[22.63041618228438,61.12947253563192],[22.630219637927137,61.12837869977737],[22.629671617655102,61.12830141165824],[22.627526899833477,61.1288018028353],[22.626026687288583,61.12885054202823],[22.62681300162358,61.12963374383872],[22.62653544688058,61.130434721138336],[22.625650107324226,61.130929210617346],[22.623205171687914,61.131463503974395],[22.621771423430136,61.13135039721014],[22.620560559974088,61.13098676346126],[22.61967457409623,61.1297986625384],[22.620029244418014,61.12916746198162],[22.620957383857164,61.12869167866422],[22.62420557341042,61.128453539260356],[22.623465492802897,61.12778559131825],[22.622429382556014,61.12785280222369],[22.62078298938712,61.12863308831318],[22.618913475485403,61.128701799639096],[22.61506165347885,61.1277033145074],[22.614272814304435,61.12702154396756],[22.61431307100194,61.12636788769542],[22.615730952275243,61.1254122206969],[22.617977955621782,61.12504492736055],[22.61983253593931,61.12536967928961],[22.621812328881575,61.124454480906486],[22.623614108175897,61.12411379730209],[22.62586815827374,61.12516622009476],[22.627979424083563,61.12552211129394],[22.629241671091872,61.12509988582646],[22.630902395575653,61.12501077508791],[22.63233713345864,61.1252289715863],[22.633385143943112,61.12567918416922],[22.634139918296107,61.12500080110805],[22.6352461088932,61.124656100132874],[22.633788100489276,61.124314795991836],[22.63292467870034,61.12365444976137],[22.633240486508495,61.122553569298915],[22.634844218163327,61.121850703673644],[22.633763334872242,61.12166962558673],[22.63561078897146,61.1219760222261],[22.637040858431707,61.12182567296725],[22.639226561158885,61.1225844675298],[22.63959188657908,61.123169556597134],[22.639331907136846,61.12385925436169],[22.638516684595043,61.12441855840681],[22.63746949079072,61.12469609046535],[22.639619065382902,61.125292950358464],[22.640994588188484,61.12520860410516],[22.642044765902362,61.12475518394299],[22.642348528539067,61.123686090422034],[22.644487317823643,61.1229726154464],[22.64290175004795,61.12175873023484],[22.642851944883656,61.12082414182059],[22.643988719691716,61.120092921262035],[22.64583938011604,61.119794978926045],[22.644377050111604,61.118909069382504],[22.64483539135876,61.117757049015964],[22.645730558003155,61.11734675598863],[22.646949575140695,61.117158743005774],[22.651158756667982,61.11736904247069],[22.652089642115065,61.11694799108659],[22.653574894152698,61.116773547141015],[22.654090057965657,61.11628725221015],[22.654094167379085,61.115553870476624],[22.654728619388315,61.11495339969915],[22.655331299185278,61.11143617456915],[22.653570369821924,61.111461298178455],[22.652203199961978,61.11115457909342],[22.65112106069614,61.11066172289518],[22.650695505124958,61.110040899012596],[22.6511933462645,61.109166601656334],[22.653256448676252,61.10838882973587],[22.65253603542422,61.10793017385361],[22.65010032611533,61.107475596166495],[22.649187866738355,61.10680574968342],[22.649022625787637,61.10756223652193],[22.648239639914618,61.1080954261742],[22.647029635380896,61.108386984294576],[22.64551690595594,61.108426666356856],[22.644010937123955,61.10917034299207],[22.642143612264764,61.10924607370634],[22.640436000807103,61.1086568521768],[22.639937150638094,61.10804904219126],[22.639916750337594,61.10701233689793],[22.641647109196207,61.10592234663018],[22.646662387051492,61.1056293778929],[22.648168760794015,61.10597074333986],[22.649119263910578,61.106658467647264],[22.649319000787578,61.10588182766913],[22.646446454844785,61.10405257440821],[22.64588602848809,61.10397691240556],[22.646007436316612,61.10377298786841],[22.641505926522587,61.100905767350326],[22.63430047871157,61.104878742457096],[22.615751484571607,61.10884655323578],[22.583805624820172,61.12108075809185],[22.5811891667577,61.12283961549909]],[[22.6321646765064,61.11294657939658],[22.633455532539724,61.11317799372413],[22.632164664314544,61.11294658204689],[22.6321646765064,61.11294657939658]],[[22.63618709216485,61.11307491283444],[22.636335130439747,61.11175252627539],[22.635598000635355,61.11086075360385],[22.633181735099914,61.1109025350726],[22.631572717610638,61.11005509915906],[22.631525066749624,61.10860361595202],[22.632321460441275,61.10807529361293],[22.633607895580504,61.107714376716395],[22.63596297109622,61.10787418641476],[22.63759090009369,61.108546726576286],[22.638147171332005,61.10928366985116],[22.640010888645623,61.10918301572983],[22.641446564801136,61.10954072904397],[22.64232636052605,61.110516855644114],[22.641633244033233,61.111517537822024],[22.64360504095596,61.11181605196475],[22.644931519045848,61.112439508476896],[22.645454564668775,61.11304245687061],[22.64532852241842,61.113693502519304],[22.642789763492974,61.11507362813848],[22.641178697850865,61.1152598057006],[22.639238421929253,61.11605555692066],[22.63710950513564,61.1160527495762],[22.636381611387353,61.115894496362124],[22.636795451770247,61.115848298074816],[22.635419826238415,61.11510638822034],[22.635128876601833,61.11406247798766],[22.634491646557574,61.11394287309434],[22.634992133608566,61.1140368089107],[22.63618709216485,61.11307491283444]],[[22.639826709357045,61.11797517089367],[22.641079248198356,61.11812171163135],[22.642074358644347,61.11851452907837],[22.643021328321677,61.11974700641622],[22.64203827598125,61.120868320189324],[22.63981313911333,61.121350991470926],[22.63791299183847,61.121036955295196],[22.638254842449708,61.12072778864164],[22.636943044771698,61.119747821598565],[22.63759743330004,61.11869226089391],[22.63716852740442,61.118698793757716],[22.636630573401355,61.118655493632225],[22.636120425926727,61.11856225121241],[22.636046396297953,61.11854386199603],[22.635923989413815,61.11851232707947],[22.63729791655997,61.11869681586272],[22.638457808167217,61.1181442068709],[22.639826709357045,61.11797517089367]],[[22.61517413731798,61.11861961725303],[22.61517419008514,61.11861962236436],[22.615174214072393,61.11861962797439],[22.61517413731798,61.11861961725303]],[[22.613653815033228,61.12742102195963],[22.613653826138712,61.127421022320405],[22.613653784175312,61.12742102994904],[22.613653815033228,61.12742102195963]],[[22.636239571682076,61.11591042779103],[22.63623953713209,61.11591046264089],[22.63622770199144,61.115913020975555],[22.636239571682076,61.11591042779103]],[[22.635776604548173,61.11576843869234],[22.63580276862065,61.11596493157764],[22.634982888989025,61.116345364250904],[22.635776604548173,61.11576843869234]],[[22.63440511876074,61.11824385219804],[22.634330766807405,61.11834601815101],[22.634330723348604,61.11834601021406],[22.63440511876074,61.11824385219804]],[[22.634633754030574,61.116563114732024],[22.634234970657502,61.117241051240235],[22.63469372497141,61.117847602615036],[22.63463748296615,61.1179278957868],[22.634234810892085,61.117241091031886],[22.634633754030574,61.116563114732024]],[[22.634723531357146,61.116482509538315],[22.63468979480561,61.11651238938718],[22.634678993554743,61.11652188321494],[22.634723531357146,61.116482509538315]],[[22.634797686253343,61.11803700680765],[22.635458783542095,61.118371368842006],[22.635799521978274,61.118474162549234],[22.635828810618506,61.1184854587822],[22.634797686253343,61.11803700680765]]],[[[22.618504488752286,61.120041781203795],[22.61832863122007,61.12220476824288],[22.619760349380844,61.121936837636284],[22.620563555049145,61.12206155228791],[22.62137476967516,61.1216868759154],[22.619471016259244,61.12137658643507],[22.618705301710254,61.11919064335001],[22.617519454196483,61.11898834912497],[22.617584020584054,61.11944256175465],[22.618311629575953,61.11958052810577],[22.617919726596153,61.11991240032808],[22.618504488752286,61.120041781203795]]],[[[22.65294978255767,61.124442192930395],[22.6530154389784,61.123517716586036],[22.654027253475334,61.122883546454815],[22.65405224213353,61.12099358684859],[22.65120144960174,61.122003861406604],[22.65160320625382,61.12300528171052],[22.65026924040729,61.1240088703967],[22.65294978255767,61.124442192930395]]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "dragon_uunimaki",
                    "name": "DRAGON UUNIMÄKI 1 + 2",
                    "target": "Finland",
                    "tier": "COMPETITOR",
                    "commodity": "Au",
                    "area_ha": 1358,
                    "openPct": 0,
                    "color": "#ff3333",
                    "description": "Dragon Mining Oy Uunimäki holdings (union of ML2020:0020-01 Uunimäki 1 Exploration Licence 89.22 ha + VA2026:0002-01 Uunimäki 2 Reservation 1,269.09 ha). 21-hole maiden diamond core campaign reported 6 Mar 2026 (HKEX a331944) returned multiple >10 g/t Au intercepts including 2.50 m @ 39.68 g/t. Bench-scale metallurgy + further 2026 drilling + initial JORC Resource by end-2026 committed by Dragon. Located 14.86 km SSE of Jokisivu and 11.28 km SSE of Ritakallio v2 centroid.",
                    "center": [22.8132, 61.0191]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.82397683566059,61.00959296786866],[22.8227373652323,61.00978649230344],[22.821133235441625,61.009656701722314],[22.81891403440201,61.008487071016184],[22.816339674770422,61.00884695652491],[22.81441485886404,61.00841348935954],[22.813715849622675,61.0077096106396],[22.813729417185776,61.00666435846444],[22.814297225990533,61.00607130756022],[22.81553759226688,61.005649088218654],[22.81435025933687,61.004410839354556],[22.813583843499327,61.00446710723108],[22.81250902675448,61.005156847362514],[22.811037081198574,61.00540078078139],[22.808607892511727,61.005201881515234],[22.80759695657531,61.00490632522754],[22.80167054256658,61.00534078211631],[22.803688081416006,61.00601732603951],[22.804137122974634,61.007164179190994],[22.80319759778577,61.008091509226986],[22.801000671667644,61.008750054471555],[22.79965784789904,61.00869730733133],[22.798440444074796,61.0083249546524],[22.797392182901124,61.00754572369506],[22.79715930282298,61.00682662454549],[22.794259836699947,61.006762721061804],[22.79293181682801,61.00729195250219],[22.791299231685745,61.007387021100186],[22.790236205612718,61.00809736432815],[22.78820033057125,61.008614226023354],[22.78550372562736,61.00848933700778],[22.784371258735,61.008077442942685],[22.783803022067985,61.00751285305813],[22.782749215083616,61.007731100872775],[22.777154095320377,61.01122628603422],[22.770144674631833,61.01245431247492],[22.767826498016476,61.01364357029344],[22.76800255164375,61.01478415016155],[22.769454564622162,61.015305368551026],[22.770163886249417,61.01598672059247],[22.77005916804537,61.01676638686759],[22.76929271975869,61.0173042231137],[22.76769276816736,61.017656409763354],[22.76532438432177,61.01750593751159],[22.763644451273947,61.01694963625092],[22.763009278898767,61.016114442802724],[22.75993933761695,61.01768883873237],[22.760001814247737,61.01850413627221],[22.759235159452686,61.01913405271933],[22.757913823027785,61.019566186964234],[22.756236671472042,61.019587434161394],[22.753745777207776,61.020864420499],[22.752652320741248,61.02466195335729],[22.7549580992801,61.025090321298954],[22.755756214338135,61.02567530583556],[22.755874304583543,61.02630512147446],[22.762417326204236,61.02898000420022],[22.76424825662397,61.02855054103787],[22.766323014382415,61.02888334952568],[22.767244003445857,61.02967222123687],[22.766948604696612,61.03083196693793],[22.767249295142,61.03095485939694],[22.772660595799778,61.031104222554326],[22.774763413652487,61.03070112747787],[22.776881879781833,61.03122066432268],[22.820181727924933,61.03240675644341],[22.82183068263884,61.03155443569768],[22.82594107051484,61.03139857132941],[22.827334399900405,61.03163471069412],[22.828410827802507,61.032284749764415],[22.830229431481964,61.032680081131936],[22.85911464878465,61.03346137880417],[22.86019087732419,61.02417236724614],[22.857185039914288,61.024664781313554],[22.856988620008725,61.02572550253915],[22.85561979182453,61.02638181772174],[22.85055534197998,61.02640843143381],[22.849359647341426,61.02610644378409],[22.848495617022124,61.02551273864103],[22.848624466619817,61.024438793888166],[22.846954569967945,61.02313478108079],[22.84758620367869,61.02209332446762],[22.84608267260569,61.02128798083708],[22.8456442425992,61.02071672386851],[22.843261128858824,61.02149785453359],[22.840980980557646,61.02133796362156],[22.839662744933502,61.02258875568969],[22.836962248448355,61.02338353597756],[22.835512287641915,61.02334700070168],[22.834345961362704,61.02301917893535],[22.833294758895796,61.021702282828414],[22.83362121066516,61.020946000275345],[22.835278159460447,61.02025458594716],[22.835820802606854,61.01936008219425],[22.83807880073469,61.018612609089644],[22.843994778541777,61.01854938748365],[22.8453288278817,61.01898702895833],[22.845989770583174,61.019751663804286],[22.84767007871183,61.019061534482084],[22.849992974606458,61.019118909314635],[22.850342311438165,61.01800069904292],[22.852308464108848,61.01735716927428],[22.854154063023774,61.01749855715434],[22.85548101192932,61.01813694725486],[22.85572423312131,61.018909267043426],[22.854988587839493,61.019715468604254],[22.85587573109375,61.02029069451779],[22.856059759616866,61.02096582257087],[22.858395172256635,61.02115071055356],[22.8608685069792,61.02027500548732],[22.862312382593895,61.00940964485901],[22.856582343437232,61.00291815950489],[22.854991422674377,61.00202068490362],[22.853311617592947,61.0015454379669],[22.83736957124867,61.002719328559884],[22.837003875562374,61.004035112505456],[22.836176903589138,61.00447157067837],[22.83500051680318,61.0046963176454],[22.832492815542015,61.00449883999622],[22.83123596242611,61.003531320863786],[22.830186406305103,61.00402727607241],[22.828770269216708,61.0042443664623],[22.825527448776917,61.00399036355375],[22.824393561164545,61.00367333267744],[22.821107925357932,61.00391470499028],[22.821022093814527,61.00500857945731],[22.81933238917999,61.00585169506213],[22.82038614270533,61.00641167958041],[22.821892820862125,61.0061820189724],[22.823453934045673,61.00634791611465],[22.824938708599564,61.0070836601538],[22.825319902992867,61.00865384604419],[22.824099902995332,61.00955436570223],[22.826210763692956,61.01352901820802],[22.82397683566059,61.00959296786866]],[[22.826210763692956,61.01352901820802],[22.810644746224025,61.01851999075],[22.810078418516387,61.01869437448034],[22.809667367048338,61.018746745449704],[22.826210763692956,61.01352901820802]],[[22.801433711089025,61.021471286857],[22.80029652162402,61.02183577816396],[22.798064439822493,61.02255101126968],[22.80029645321665,61.021835749059534],[22.801433711089025,61.021471286857]],[[22.80167988518968,61.02124795095117],[22.801355531595938,61.02127701595586],[22.802305314984242,61.021191898502295],[22.80167988518968,61.02124795095117]],[[22.805951800916937,61.020023065258314],[22.80595180210057,61.020023056304495],[22.8060814529964,61.01998149369924],[22.805951800916937,61.020023065258314]],[[22.817267995657943,61.011103525788826],[22.81893659399868,61.01056390815899],[22.819799449936784,61.010284890799845],[22.821106174687067,61.00986224116487],[22.82141051166643,61.00976385336351],[22.817267995657943,61.011103525788826]],[[22.810636062110078,61.01324775346313],[22.816523726812687,61.011344179730884],[22.816523767394823,61.01134418099125],[22.810636062110078,61.01324775346313]],[[22.809172895581575,61.01372073658473],[22.80883532723049,61.01467283750558],[22.80731321201877,61.01577692599632],[22.80593866557925,61.01624058449381],[22.805861842513497,61.01659863805188],[22.805287430243737,61.016296333579014],[22.802655846054087,61.01583398001968],[22.802101118325115,61.01487773322679],[22.802297440701032,61.014230721401674],[22.803200979744656,61.01345212034462],[22.805158569906478,61.012760996495494],[22.80674412995044,61.01262572991345],[22.808053522972383,61.012873862180825],[22.8091811389358,61.013718026118006],[22.809584319963047,61.013587742892966],[22.809172895581575,61.01372073658473]],[[22.80249346358729,61.015884788748245],[22.80237307122897,61.01592245990934],[22.802655846054087,61.01583398001968],[22.80249346358729,61.015884788748245]],[[22.800666904570495,61.01645629639585],[22.80185455637247,61.016084702622706],[22.79962102816332,61.01678352615116],[22.800666904570495,61.01645629639585]],[[22.79793199880565,61.017311941664445],[22.795862947067,61.01795919533335],[22.79962102816332,61.01678352615116],[22.79793199880565,61.017311941664445]],[[22.79462939475371,61.01834504884889],[22.795824586011264,61.01797119512316],[22.792837157404346,61.0189056328668],[22.79462939475371,61.01834504884889]],[[22.798064416326596,61.022550994886515],[22.79806437892926,61.02255100937061],[22.792837157404346,61.0189056328668],[22.798064416326596,61.022550994886515]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "vammala_inferred_corridor",
                    "name": "VAMMALA MIGMATITE BELT — inferred resource corridor",
                    "target": "Finland",
                    "tier": "SPECULATIVE",
                    "priority": "POTENTIAL",
                    "commodity": "Au",
                    "area_ha": 10897,
                    "openPct": 0,
                    "color": "#00bcd4",
                    "description": "INFERRED RESOURCE CORRIDOR (2026-05-15 geological hypothesis, NOT a defined resource): WNW-ESE Vammala Migmatite Belt mineralized fabric inferred from Jokisivu (NW), Ritakallionmaa (centre), Uunimäki (S) deposit positions and WNW-striking shear orientation reported in Dragon Mining 2025-2026 HKEX disclosures. Comprises a principal corridor (3 km wide × 20 km long along Jokisivu-Rita ESE strike, ~097°/277° azimuth), a parallel southern corridor through Uunimäki, and a NNW-SSE relay zone linking the two parallel shears via the Rita-Uunimäki structural trend. Dashed cyan outline indicates SPECULATIVE — for directing satellite-shoot exploration within and adjacent to the Option F umbrella varaus, not for filing or resource estimation.",
                    "center": [22.79, 61.03]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.922562018726158,60.99887959609239],[22.701269759641573,61.00576768821561],[22.704725381105362,61.03263125839758],[22.78961137053133,61.03003312953858],[22.747027133177568,61.09975817463399],[22.544453237615986,61.10578725301466],[22.54778278641305,61.132653275550986],[22.9180793245441,61.121403750734096],[22.914435778405416,61.09454424720158],[22.778546598751,61.0987925138217],[22.821060559433285,61.02905686110182],[22.92620464457737,61.02573912917271],[22.922562018726158,60.99887959609239]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "cluster_317_ritakallionmaa",
                    "name": "CLUSTER 317 — RITAKALLIONMAA",
                    "target": "Finland",
                    "tier": "PROMOTE",
                    "commodity": "Au",
                    "area_ha": 6.3,
                    "openPct": 100,
                    "color": "#22c55e",
                    "description": "GTK 2004 (R311-R318), 8 holes / 610 m. Best: R314 41.00-42.00 m @ 3.9 g/t Au + continuous 4.7 m @ 1.7 g/t. Plus Dragon 22 holes 2007+2010 (HU/RK-1 to -22, 1,989 m): HU/RK-12 1.10 m @ 49.80 g/t bonanza; HU/RK-21 stacked 42-98 m; HU/RK-17 1.60 m @ 5.65 g/t. INSIDE v3 polygon — our target.",
                    "center": [22.746, 61.1125]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.743669999999998,61.11138],[22.74833,61.11138],[22.74833,61.11362],[22.743669999999998,61.11362],[22.743669999999998,61.11138]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "cluster_196_palokallio",
                    "name": "CLUSTER 196 — PALOKALLIO (intercepts found)",
                    "target": "Finland",
                    "tier": "RE-REVIEW",
                    "commodity": "Au",
                    "area_ha": 6.3,
                    "openPct": 100,
                    "color": "#facc15",
                    "description": "GTK 2006-2009 valtaus 8524/1+2 (Grönholm/Vuori/Alanen/Karttunen 2011, Hakku 19545): 17 Palokallio holes + 6 Kellarisuo holes = 23 holes / 1,999.7 m. PER-METER ASSAY UPGRADE 2026-05-18: source-record pull of 5 Hakku assay batches yielded 1,742 primary one-metre assays with full pathfinder suite (Au + Ag + Cu + As + Bi + Te + Sb + Co + Mo + S + Fe), reframing this cluster from \"5 narrative-summary intercepts\" to a documented multi-hole Au + pathfinder dataset. 13 of 17 Palokallio holes ≥0.5 g/t Au (76%), 9 of 17 ≥1.0 g/t Au (53%). Composites (deduplicated lab runs, 1 g/t cut-off, 2 m max dilution): 18 intercepts in 9 holes, best R329 2.65 m @ 16.05 g/t (max 39 g/t at 42.90-43.75 m — the GTK narrative cited 41.8 g/t × 0.9 m using one of two lab runs). At 0.5 g/t cut-off the R329 shoot reframes to 4.75 m @ 9.10 g/t and three new bulk-tonnage composites emerge: R342 7.00 m @ 1.04 g/t, R327 6.00 m @ 0.76 g/t, R341 5.00 m @ 1.17 g/t. Pathfinder Spearman correlations on n=633 full-suite samples: Au-Bi ρ=0.86 (95% block-bootstrap CI [+0.81, +0.89]) ≫ Au-Ag 0.70 ≈ Au-Te 0.66 ≈ Au-As 0.66 > Au-Cu 0.59 — diagnostic orogenic-Au signature matching Jokisivu mine paragenesis (arsenopyrite + native Au + bismuthinite + petzite). All 6 Kellarisuo holes barren of significant Au at the 1.7-km-west separate cluster. Mineralogy per 2011 GTK report: arsenopyrite + löllingite + chalcopyrite + pyrrhotite + scheelite + free gold in shear zones inside medium-grained gabbro intrusion — IDENTICAL setting to Jokisivu (15 km W) + Ritakallio (8 km W). Gabbro re-section: drilled SE corner is ~0.085 km² inside the inferred ~0.35 km² Valjus 2010 mag-low + IP-high + resistivity-high envelope; W (~230 m), S (~215 m), E (~110 m) margins UNDRILLED + R329 shoot open below 120 m vertical. KKJ Zone 2 (EPSG:2392) collars in original drill logs; GTK 2011 final report Taulukko 1 publishes the same collars in YKJ / Zone 3 / EPSG:2393 — both correct, identical WGS84 points. INSIDE Option F east lobe — recommend upgrade to Stake-Now satellite for Option F filing case. Full data + Phase 2 fence targets: geomorphic-docs/finland/drill-clusters/196-palokallio-*.{csv,geojson,png} + target page § Palokallio satellite drilling.",
                    "center": [22.8957, 61.1213]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.89337,61.12018],[22.898030000000002,61.12018],[22.898030000000002,61.12242],[22.89337,61.12242],[22.89337,61.12018]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "cluster_422_punovuori",
                    "name": "CLUSTER 422 — PUNOVUORI (Ni-Cu-PGE, no formal report)",
                    "target": "Finland",
                    "tier": "KILLED",
                    "commodity": "Au",
                    "area_ha": 6.3,
                    "openPct": 100,
                    "color": "#ef4444",
                    "description": "GTK 2001 (M211201R308-R310), 3 holes / 347 m / azimuth 228° / dip 60°. Surface peridotite + pyroxenite. NO formal report found in Hakku (only the unrelated 1992 quarry study at Punovuori). Cores at Loppi archive 21829, 14 boxes, status Julkinen (public). Pd-in-rock anomaly + Co/Cr/Ni/Cu/Zn till panel suggests magmatic Ni-Cu-PGE signature. OUTSIDE Option F to NE — would need extension to capture. Re-assay candidate (~EUR 2k) if magmatic Ni-Cu-PGE thesis pursued.",
                    "center": [22.8415, 61.1721]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.83917,61.17098],[22.84383,61.17098],[22.84383,61.17322],[22.83917,61.17322],[22.83917,61.17098]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "cluster_753_vanttila",
                    "name": "CLUSTER 753 — VANTTILA (Outokumpu Ni-Cu, REJECTED)",
                    "target": "Finland",
                    "tier": "KILLED",
                    "commodity": "Ni-Cu",
                    "area_ha": 6.3,
                    "openPct": 100,
                    "color": "#ef4444",
                    "description": "Outokumpu Finnmines/Mining Oy 1991-1998 (Grundström lead), kaiv.rek. 5056/1, abandoned 9.11.1998. 11 holes / 1,887.6 m. Ni-Cu MAGMATIC SULPHIDE play (NOT orogenic Au) — 400 × 200 m noritic-gabbro pipe intrusion in mica-gneiss/graphite-gneiss country rock. Mineralogy = pyrrhotite + chalcopyrite + pentlandite (DIFFERENT from Jokisivu/Palokallio arsenopyrite + löllingite + scheelite + Au). Best: VAN-8 1.40 m @ 0.60% Ni + 0.61% Cu (incl 41 m broad zone), VAN-1 1.13 m @ 0.28% Ni + 0.13% Cu, VAN-10 1.06 m peridotite differentiate 0.37% Ni + 0.16% Cu. Surface outcrop P189 = 0.36% Ni / 0.14% Cu / 1.9% S. NO Au/As/Bi/Te/W/PGE tested anywhere in 1991-1998 data. Outokumpu closed 1998: 'chance of finding a several-million-tonne body of sufficient grade near surface is unlikely'. NO read-through to Vammala-belt orogenic Au thesis. STAYS REJECT for primary thesis. Country-rock Au is a residual untested hypothesis only.",
                    "center": [22.9426, 61.1509]
          },
          "geometry": {"type":"Polygon","coordinates":[[[22.940269999999998,61.14978],[22.94493,61.14978],[22.94493,61.15202],[22.940269999999998,61.15202],[22.940269999999998,61.14978]]]}
},
          {
          "type": "Feature",
          "properties": {
                    "id": "jarvi_pohjanmaa",
                    "name": "JARVI-POHJANMAA",
                    "target": "Finland",
                    "tier": "STAKE NOW",
                    "commodity": "Li",
                    "area_ha": 9000,
                    "openPct": 100,
                    "color": "#22c55e",
                    "description": "JARVI-POHJANMAA: Li. Centroid polygon (no final design yet).",
                    "center": [
                              23.85,
                              63.13
                    ]
          },
          "geometry": {
                    "type": "Polygon",
                    "coordinates": [
                              [
                                        [
                                                  23.755450142588202,
                                                  63.087266518105835
                                        ],
                                        [
                                                  23.9445498574118,
                                                  63.087266518105835
                                        ],
                                        [
                                                  23.9445498574118,
                                                  63.17273348189417
                                        ],
                                        [
                                                  23.755450142588202,
                                                  63.17273348189417
                                        ],
                                        [
                                                  23.755450142588202,
                                                  63.087266518105835
                                        ]
                              ]
                    ]
          }
},
          {
              "type": "Feature",
              "properties": {
                    "id": "perakorpi_lumikangas",
                    "name": "LUMIKANGAS",
                    "target": "Finland",
                    "tier": "PRIORITY 1",
                    "commodity": "Ti-P-Fe",
                    "area_ha": 2485,
                    "openPct": 100,
                    "color": "#00bfa5",
                    "description": "LUMIKANGAS: 230 Mt @ 4.4% TiO\u2082, 2.1% P\u2082O\u2085. Free ilmenite (pigment-grade). 5 km anomaly, only 1.5 km drilled.",
                    "center": [22.136, 62.299]
              },
              "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[22.102,62.268],[22.170,62.268],[22.170,62.331],[22.102,62.331],[22.102,62.268]]]
              }
          },
          {
              "type": "Feature",
              "properties": {
                    "id": "perakorpi_hyyppa",
                    "name": "HYYPP\u00c4",
                    "target": "Finland",
                    "tier": "EXPLORATION",
                    "commodity": "Ti-Fe",
                    "area_ha": 1210,
                    "openPct": 100,
                    "color": "#80cbc4",
                    "description": "HYYPP\u00c4: Uncharacterized. 5 holes / 252 m. 1.5 km \u00d7 0.4 km anomaly.",
                    "center": [22.202, 62.335]
              },
              "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[22.173,62.317],[22.231,62.317],[22.231,62.353],[22.173,62.353],[22.173,62.317]]]
              }
          },
          {
              "type": "Feature",
              "properties": {
                    "id": "perakorpi_kauhajarvi",
                    "name": "KAUHAJARVI",
                    "target": "Finland",
                    "tier": "PRIORITY 1",
                    "commodity": "Ti-P-Fe-V",
                    "area_ha": 3567,
                    "openPct": 100,
                    "color": "#00bfa5",
                    "description": "KAUHAJARVI: 9.6 Mt @ 5\u20136% TiO\u2082. 84 DDH. Free ilmenite (basal zone). 3 km Kirveskyl\u00e4 gap untested. AVANTIS EU case study.",
                    "center": [22.270, 62.240]
              },
              "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[22.232,62.200],[22.308,62.200],[22.308,62.281],[22.232,62.281],[22.232,62.200]]]
              }
          },
          {
              "type": "Feature",
              "properties": {
                    "id": "perakorpi_peramaa",
                    "name": "PER\u00c4MAA",
                    "target": "Finland",
                    "tier": "PRIORITY 2",
                    "commodity": "Ti-P-Fe",
                    "area_ha": 3506,
                    "openPct": 100,
                    "color": "#00897b",
                    "description": "PER\u00c4MAA: 287 Mt @ 5.0% TiO\u2082, 2.6% P\u2082O\u2085. Ilmenite intergrowth issue (Kemira 1984). Subvertical dip, open at depth.",
                    "center": [22.370, 62.135]
              },
              "geometry": {
                    "type": "Polygon",
                    "coordinates": [[[22.322,62.104],[22.418,62.104],[22.418,62.167],[22.322,62.167],[22.322,62.104]]]
              }
          }
      ]
  }), [
    ROPEN_VMS_REVIEW_AREA_HA,
    ROPEN_VMS_REVIEW_CENTER,
    ROPEN_VMS_REVIEW_DESCRIPTION,
    ROPEN_VMS_REVIEW_GEOMETRY,
    ROPEN_VMS_EXTENSION_AREA_HA,
    ROPEN_VMS_EXTENSION_CENTER,
    ROPEN_VMS_EXTENSION_DESCRIPTION,
    ROPEN_VMS_EXTENSION_GEOMETRY,
    ROPEN_VMS_UPDATED_AREA_HA,
    ROPEN_VMS_UPDATED_CENTER,
    ROPEN_VMS_UPDATED_DESCRIPTION,
    ROPEN_VMS_UPDATED_GEOMETRY,
    ROPEN_VMS_SPLIT_AREA_HA,
    ROPEN_VMS_SPLIT_CENTER,
    ROPEN_VMS_SPLIT_DESCRIPTION,
    ROPEN_VMS_SPLIT_GEOMETRY,
    ROPEN_VMS_SHORELINE_AREA_HA,
    ROPEN_VMS_SHORELINE_CENTER,
    ROPEN_VMS_SHORELINE_DESCRIPTION,
    ROPEN_VMS_SHORELINE_GEOMETRY
  ]);

  const selectedProjectStakingBoundaryIds = useMemo(
    () => getProjectStakingBoundaryIds(STAKING_BOUNDARIES_DATA.features, selectedProject?.id),
    [STAKING_BOUNDARIES_DATA, selectedProject?.id]
  );
  const selectedProjectStakingBoundaryIdSet = useMemo(
    () => new Set(selectedProjectStakingBoundaryIds),
    [selectedProjectStakingBoundaryIds]
  );
  const setSelectedProjectStakingBoundariesVisible = useCallback(
    (visible: boolean) => {
      setStakingTierVisibility((prev) => {
        const next = { ...prev };
        selectedProjectStakingBoundaryIds.forEach((tierId) => {
          next[tierId] = visible;
        });
        return next;
      });
    },
    [selectedProjectStakingBoundaryIds]
  );


  // Köli VMS targets layer (for Sweden copper project)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (koliVmsTargetsLayerRef.current) {
        mapInstance?.removeLayer(koliVmsTargetsLayerRef.current);
        koliVmsTargetsLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !koliVmsTargetsVisible) {
      removeLayer();
      return;
    }
    if (!koliVmsTargetsLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/koli_vms_targets.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'koli-vms-targets' },
        style: (feature) => {
          const tier = feature.get('tier');
          const name = feature.get('name') || '';
          const cuPct = feature.get('cu_pct') || 0;
          const znPct = feature.get('zn_pct') || 0;
          const permit = feature.get('permit') || '';
          const isPermitted = permit === 'PERMITTED';

          // Color by tier - gray out if permitted
          let color = '#ff0000'; // red for tier 1
          if (isPermitted) {
            color = '#888888'; // gray for permitted areas
          } else if (tier === 2) {
            color = '#ff8c00'; // orange for tier 2
          } else if (tier === 'reference') {
            color = '#2196f3'; // blue for reference
          }

          const radius = tier === 1 ? 12 : tier === 2 ? 10 : 8;
          const permitLabel = isPermitted ? ' [PERMITTED]' : ' [OPEN]';
          const bgColor = isPermitted
            ? 'rgba(100, 100, 100, 0.85)'
            : tier === 'reference'
              ? 'rgba(33, 150, 243, 0.8)'
              : 'rgba(0, 100, 0, 0.8)';

          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({
                color: isPermitted ? '#555' : '#fff',
                width: 2,
                lineDash: isPermitted ? [4, 4] : undefined
              })
            }),
            text: new Text({
              text: `${name}${permitLabel}\n${cuPct}% Cu, ${znPct}% Zn`,
              font: tier === 1 ? 'bold 11px sans-serif' : '10px sans-serif',
              textAlign: 'left',
              offsetX: radius + 4,
              offsetY: 0,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 3 }),
              backgroundFill: new Fill({ color: bgColor }),
              padding: [2, 4, 2, 4]
            })
          });
        },
        zIndex: 105
      });
      koliVmsTargetsLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      koliVmsTargetsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, koliVmsTargetsVisible]);

  // Ropen-area active mineral rights (Undersökningstillstånd + Bearbetningskoncession)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (ropenNearbyPermitsLayerRef.current) {
        mapInstance?.removeLayer(ropenNearbyPermitsLayerRef.current);
        ropenNearbyPermitsLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !ropenNearbyPermitsVisible) {
      removeLayer();
      return;
    }
    if (!ropenNearbyPermitsLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/ropen_nearby_permits.geojson',
          format: new GeoJSON()
        }),
        properties: { name: 'ropen-nearby-permits' },
        style: (feature) => {
          const isConcession = feature.get('is_concession');
          const isPending = feature.get('is_pending');
          const name = feature.get('name') || '';
          const owner = (feature.get('owner') || '').replace(/\s*\(100%\)\s*$/, '');
          const validto = feature.get('validto') || '';
          const distKm = feature.get('dist_km');

          // Concessions = darker red; exploration = orange-red; pending = dashed
          const fillColor = isConcession ? 'rgba(198, 40, 40, 0.35)' : 'rgba(239, 83, 80, 0.22)';
          const strokeColor = isConcession ? '#b71c1c' : '#e53935';

          return new Style({
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({
              color: strokeColor,
              width: isConcession ? 2.5 : 2,
              lineDash: isPending ? [6, 4] : undefined
            }),
            text: new Text({
              text: `${name}\n${owner}\nto ${validto} · ${distKm} km`,
              font: 'bold 10px sans-serif',
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 3 }),
              backgroundFill: new Fill({ color: 'rgba(0,0,0,0.75)' }),
              padding: [2, 4, 2, 4],
              overflow: true
            })
          });
        },
        zIndex: 104
      });
      ropenNearbyPermitsLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      ropenNearbyPermitsLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, ropenNearbyPermitsVisible]);

  // Bergslagen till samples — Au (AR-ICPMS, 2022 SGU)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (bergslagenTillAuLayerRef.current) {
        mapInstance?.removeLayer(bergslagenTillAuLayerRef.current);
        bergslagenTillAuLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !bergslagenTillAuVisible) {
      removeLayer();
      return;
    }
    if (!bergslagenTillAuLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/bergslagen_till_ar_icpms.geojson',
          format: new GeoJSON(),
        }),
        properties: { name: 'bergslagen-till-au' },
        style: (feature) => {
          const au = feature.get('au_ppb');
          // Graduated color ramp for Au (ppb): <100 / 100-500 / 500-1000 / 1000-3000 / >3000
          let color = '#2c3e50'; // null / no data
          let radius = 2;
          if (au === null || au === undefined) {
            color = '#555';
            radius = 2;
          } else if (au < 100) {
            color = '#1a4d8f'; // dark blue — background
            radius = 3;
          } else if (au < 500) {
            color = '#3498db'; // light blue
            radius = 4;
          } else if (au < 1000) {
            color = '#f1c40f'; // yellow
            radius = 5;
          } else if (au < 3000) {
            color = '#e67e22'; // orange
            radius = 6;
          } else {
            color = '#e74c3c'; // red — extreme
            radius = 8;
          }
          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#ffffff', width: 1 }),
            }),
          });
        },
        zIndex: 102,
      });
      bergslagenTillAuLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      bergslagenTillAuLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, bergslagenTillAuVisible]);

  // Bergslagen till samples — Te (AR-ICPMS, 2022 SGU)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    const removeLayer = () => {
      if (bergslagenTillTeLayerRef.current) {
        mapInstance?.removeLayer(bergslagenTillTeLayerRef.current);
        bergslagenTillTeLayerRef.current = null;
      }
    };
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden' || !bergslagenTillTeVisible) {
      removeLayer();
      return;
    }
    if (!bergslagenTillTeLayerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: '/data/bergslagen_till_ar_icpms.geojson',
          format: new GeoJSON(),
        }),
        properties: { name: 'bergslagen-till-te' },
        style: (feature) => {
          const te = feature.get('te_ppb');
          // Graduated color ramp for Te (ppb): <1k / 1k-5k / 5k-15k / 15k-30k / >30k
          let color = '#555';
          let radius = 2;
          if (te === null || te === undefined) {
            color = '#555';
            radius = 2;
          } else if (te < 1000) {
            color = '#1a4d8f';
            radius = 3;
          } else if (te < 5000) {
            color = '#3498db';
            radius = 4;
          } else if (te < 15000) {
            color = '#f1c40f';
            radius = 5;
          } else if (te < 30000) {
            color = '#e67e22';
            radius = 6;
          } else {
            color = '#9b59b6'; // purple — extreme Te
            radius = 8;
          }
          return new Style({
            image: new CircleStyle({
              radius,
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#ffffff', width: 1 }),
            }),
          });
        },
        zIndex: 103,
      });
      bergslagenTillTeLayerRef.current = layer;
      mapInstance.addLayer(layer);
    } else {
      bergslagenTillTeLayerRef.current.setVisible(true);
    }
    return () => { removeLayer(); };
  }, [selectedProject?.id, bergslagenTillTeVisible]);

  // ML output layers (prospectivity rasters + cluster vectors)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || !mlLayers) return;

    const ML_BASE_URL = '/ml_outputs/og_fi_clgb';

    const RASTER_LAYERS: Record<string, { file: string; color: [number, number, number] }> = {
      prospectivity_raw: { file: 'prospectivity_raw.tif', color: [240, 136, 62] },
      wofe_baseline: { file: 'wofe_baseline.tif', color: [163, 113, 247] },
    };

    const VECTOR_LAYERS: Record<string, { file: string; color: string }> = {
      top_clusters: { file: 'top_clusters.geojson', color: '#3fb950' },
    };

    // Handle raster layers
    Object.entries(RASTER_LAYERS).forEach(([key, { file, color }]) => {
      const existing = mlLayerRefsRef.current.get(key);
      const visible = mlLayers[key] ?? false;

      if (existing) {
        existing.setVisible(visible);
        existing.setOpacity(mlOpacity);
        return;
      }

      if (!visible) return;

      const source = new GeoTIFF({
        sources: [{ url: `${ML_BASE_URL}/${file}`, nodata: -9999 }],
        convertToRGB: false,
        normalize: false,
      });

      const layer = new WebGLTile({
        source,
        visible: true,
        opacity: mlOpacity,
        zIndex: 90,
        style: {
          color: [
            'case',
            ['==', ['band', 1], -9999],
            [0, 0, 0, 0],
            [
              'array',
              color[0] / 255,
              color[1] / 255,
              color[2] / 255,
              ['clamp', ['band', 1], 0, 1],
            ],
          ],
        },
      });

      mlLayerRefsRef.current.set(key, layer);
      mapInstance.addLayer(layer);
    });

    // Handle vector layers
    Object.entries(VECTOR_LAYERS).forEach(([key, { file, color }]) => {
      const existing = mlLayerRefsRef.current.get(key);
      const visible = mlLayers[key] ?? false;

      if (existing) {
        existing.setVisible(visible);
        existing.setOpacity(mlOpacity);
        return;
      }

      if (!visible) return;

      const layer = new VectorLayer({
        source: new VectorSource({
          url: `${ML_BASE_URL}/${file}`,
          format: new GeoJSON(),
        }),
        visible: true,
        opacity: mlOpacity,
        zIndex: 91,
        style: (feature) => {
          const score = feature.get('mean_score') ?? 0;
          const alpha = 0.2 + score * 0.6;
          return new Style({
            stroke: new Stroke({ color, width: 1.5 }),
            fill: new Fill({ color: `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}` }),
          });
        },
      });

      mlLayerRefsRef.current.set(key, layer);
      mapInstance.addLayer(layer);
    });

    // Staking Polygon Scores — same as top_clusters but with polygon_scores key
    const psExisting = mlLayerRefsRef.current.get('polygon_scores');
    const psVisible = mlLayers.polygon_scores ?? false;
    if (psExisting) {
      psExisting.setVisible(psVisible);
      psExisting.setOpacity(mlOpacity);
    } else if (psVisible) {
      const layer = new VectorLayer({
        source: new VectorSource({
          url: `${ML_BASE_URL}/top_clusters.geojson`,
          format: new GeoJSON(),
        }),
        visible: true,
        opacity: mlOpacity,
        zIndex: 92,
        style: (feature) => {
          const score = feature.get('max_score') ?? 0;
          return new Style({
            stroke: new Stroke({ color: '#58a6ff', width: 2 }),
            fill: new Fill({
              color: `rgba(88, 166, 255, ${(0.15 + score * 0.5).toFixed(2)})`,
            }),
            text: new Text({
              text: score > 0.8 ? `${(score * 100).toFixed(0)}%` : '',
              font: '10px monospace',
              fill: new Fill({ color: '#fff' }),
              stroke: new Stroke({ color: '#000', width: 2 }),
            }),
          });
        },
      });
      mlLayerRefsRef.current.set('polygon_scores', layer);
      mapInstance.addLayer(layer);
    }
  }, [mlLayers, mlOpacity]);

  // Render staking boundaries layer based on tier visibility
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || (selectedProject?.id !== 'project-sgu-sweden' && selectedProject?.id !== 'project-gtk-finland')) {
      if (stakingBoundariesLayerRef.current) {
        mapInstance?.removeLayer(stakingBoundariesLayerRef.current);
        stakingBoundariesLayerRef.current = null;
      }
      return;
    }

    // Remove existing layer
    if (stakingBoundariesLayerRef.current) {
      mapInstance.removeLayer(stakingBoundariesLayerRef.current);
      stakingBoundariesLayerRef.current = null;
    }

    // Filter features based on visibility
    const visibleFeatures = STAKING_BOUNDARIES_DATA.features.filter((feature) => {
      const featureId = getStakingBoundaryFeatureId(feature);
      return Boolean(
        featureId &&
        selectedProjectStakingBoundaryIdSet.has(featureId) &&
        stakingTierVisibility[featureId] === true
      );
    });

    if (visibleFeatures.length === 0) return;

    const filteredGeojson = {
      type: 'FeatureCollection',
      features: visibleFeatures
    };

    try {
      const features = new GeoJSON().readFeatures(filteredGeojson, {
        featureProjection: 'EPSG:3857'
      });

      const stakingLayer = new VectorLayer({
        source: new VectorSource({ features }),
        style: (feature) => {
          const props = feature.getProperties();
          const color = props.color || '#00D2FF';
          const priority = props.priority || '';
          const isComparison = props.comparison === true;
          const isDashed = isComparison || priority === 'MEDIUM' || priority === 'OPTIONAL' || priority === 'POTENTIAL';

          const labelText = props.name || props.id || '';

          // Compute top-center of polygon extent for label placement
          const geom = feature.getGeometry();
          const extent = geom?.getExtent();
          const topCenter = extent
            ? [(extent[0] + extent[2]) / 2, extent[3]] // top-center of bbox
            : undefined;

          const polyStyle = new Style({
            fill: new Fill({ color: `${color}25` }),
            stroke: new Stroke({
              color: color,
              width: 2,
              lineDash: isDashed ? [8, 4] : undefined
            }),
          });

          // Label at top of polygon, not center — less blocking
          const labelStyle = topCenter ? new Style({
            geometry: new Point(topCenter),
            text: new Text({
              text: labelText,
              font: '10px monospace',
              fill: new Fill({ color }),
              stroke: new Stroke({ color: '#000', width: 2.5 }),
              overflow: true,
              textAlign: 'center',
              textBaseline: 'bottom',
              offsetY: -4,
            })
          }) : undefined;

          return labelStyle ? [polyStyle, labelStyle] : polyStyle;
        },
        zIndex: 160,
        properties: { name: 'staking-boundaries' }
      });

      mapInstance.addLayer(stakingLayer);
      stakingBoundariesLayerRef.current = stakingLayer;
    } catch (err) {
      console.error('Failed to render staking boundaries:', err);
    }
  }, [selectedProject?.id, selectedProjectStakingBoundaryIdSet, stakingTierVisibility, STAKING_BOUNDARIES_DATA]);

  // Toggle staking tier visibility
  const toggleStakingTier = useCallback((tierId: string, visible: boolean) => {
    setStakingTierVisibility((prev) => ({ ...prev, [tierId]: visible }));
  }, []);

  // Ropen VMS occurrence points data
  const ROPEN_VMS_POINTS = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: [
      { type: 'Feature' as const, properties: { id: 'ropen_pt_ropen', name: 'Ropen', grade: '11.38% Zn, 253 g/t Ag, 1.2 g/t Au', color: '#ff5252', zone: 'Zn-Pb-Ag-Au' }, geometry: { type: 'Point' as const, coordinates: [14.789, 65.587] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_rikker', name: 'Rikker', grade: '3.13% Zn, 0.66% Pb, 70 g/t Ag', color: '#ff9800', zone: 'Zn-Pb-Ag' }, geometry: { type: 'Point' as const, coordinates: [14.724, 65.579] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_rikkervardo', name: 'Rikkervardo', grade: 'Zn-Pb (no assay)', color: '#ffd740', zone: 'Zn-Pb' }, geometry: { type: 'Point' as const, coordinates: [14.794, 65.560] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_jemesjauretje', name: 'Jemesjauretje', grade: 'Zn-Pb ("Ropen S.")', color: '#ffd740', zone: 'Zn-Pb' }, geometry: { type: 'Point' as const, coordinates: [14.807, 65.544] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_bijetjatje', name: 'Bijetjatje', grade: 'Cu (Ropen unit, massive sulphide)', color: '#40c4ff', zone: 'Cu-proximal' }, geometry: { type: 'Point' as const, coordinates: [14.918, 65.593] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_kebnevardo', name: 'Kebnevardo', grade: 'Cu-Zn-Pb-Ag (polymetallic)', color: '#40c4ff', zone: 'Cu-proximal' }, geometry: { type: 'Point' as const, coordinates: [14.938, 65.574] } },
      { type: 'Feature' as const, properties: { id: 'ropen_pt_usmeten', name: 'Usmeten', grade: '153 kt @ 1.27% Cu (70 holes, open at depth)', color: '#76ff03', zone: 'drilled' }, geometry: { type: 'Point' as const, coordinates: [14.960, 65.554] } },
    ]
  }), []);

  // Render Ropen VMS occurrence points
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (ropenVmsPointsLayerRef.current) {
        mapInstance?.removeLayer(ropenVmsPointsLayerRef.current);
        ropenVmsPointsLayerRef.current = null;
      }
      return;
    }

    if (ropenVmsPointsLayerRef.current) {
      mapInstance.removeLayer(ropenVmsPointsLayerRef.current);
      ropenVmsPointsLayerRef.current = null;
    }

    const visiblePoints = ROPEN_VMS_POINTS.features.filter(
      (f) => stakingTierVisibility[f.properties.id] === true
    );

    if (visiblePoints.length === 0) return;

    try {
      const features = new GeoJSON().readFeatures(
        { type: 'FeatureCollection', features: visiblePoints },
        { featureProjection: 'EPSG:3857' }
      );

      const layer = new VectorLayer({
        source: new VectorSource({ features }),
        style: (feature) => {
          const props = feature.getProperties();
          const color = props.color || '#ff5252';
          const name = props.name || '';
          const grade = props.grade || '';
          return [
            new Style({
              image: new CircleStyle({
                radius: 7,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#fff', width: 2 }),
              }),
            }),
            new Style({
              text: new Text({
                text: `${name}\n${grade}`,
                font: '10px monospace',
                fill: new Fill({ color: '#fff' }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                offsetY: -18,
                textAlign: 'center',
                overflow: true,
              }),
            }),
          ];
        },
        zIndex: 170,
        properties: { name: 'ropen-vms-points' },
      });

      mapInstance.addLayer(layer);
      ropenVmsPointsLayerRef.current = layer;
    } catch (err) {
      console.error('Failed to render Ropen VMS points:', err);
    }
  }, [selectedProject?.id, stakingTierVisibility, ROPEN_VMS_POINTS]);

  // --- Saxe — Deep Dive (2026-05) vector layers ---
  // Lazy-load each /data/sweden/saxe_*.geojson into its own VectorLayer; per-layer toggle controls visibility.
  // Feature clicks are handled by the generic inspection panel (buildInspectionRecord), which lists all properties.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;
    const layerMap = saxeVectorLayersRef.current;

    const removeAll = () => {
      layerMap.forEach((layer) => mapInstance.removeLayer(layer));
      layerMap.clear();
    };

    if (selectedProject?.id !== 'project-sgu-sweden') {
      removeAll();
      return;
    }

    const hexToRgba = (hex: string, alpha: number) => {
      const h = (hex || '#888888').replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const makeStyle = (id: string) => (feature: any) => {
      const props = feature.getProperties();
      const geomType = feature.getGeometry()?.getType?.();
      const color = props.color || '#9ca3af';
      if (id === 'saxe_filed') {
        return new Style({
          stroke: new Stroke({ color: '#22c55e', width: 3 }),
          fill: new Fill({ color: hexToRgba('#22c55e', 0.15) }),
        });
      }
      if (id === 'saxe_v6_design') {
        return new Style({
          stroke: new Stroke({ color: '#9ca3af', width: 2, lineDash: [10, 6] }),
        });
      }
      if (id === 'saxe_unfiled_blocks') {
        return new Style({
          stroke: new Stroke({ color: '#0ea5e9', width: 2 }),
          fill: new Fill({ color: hexToRgba('#0ea5e9', 0.2) }),
        });
      }
      if (id === 'saxe_till_markers') {
        return new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#fff', width: 1 }),
          }),
        });
      }
      if (id === 'saxe_upice_corridors') {
        if (geomType === 'Point') {
          return new Style({
            image: new CircleStyle({
              radius: 5,
              fill: new Fill({ color: color || '#7c3aed' }),
              stroke: new Stroke({ color: '#fff', width: 1 }),
            }),
          });
        }
        return new Style({
          stroke: new Stroke({ color: color || '#a855f7', width: 3 }),
        });
      }
      if (id === 'saxe_geophys_targets') {
        return new Style({
          image: new RegularShape({
            points: 3,
            radius: 8,
            angle: 0,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#fff', width: 1.5 }),
          }),
        });
      }
      return new Style({ stroke: new Stroke({ color, width: 2 }) });
    };

    SAXE_VECTOR_LAYERS.forEach(({ id, file, label }) => {
      const visible = saxeLayerVisibility[id] === true;
      const existing = layerMap.get(id);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }
      // zIndex 165: above rasters (~85) and other staking fills, in line with Ropen points (170)
      const layer = new VectorLayer({
        source: new VectorSource(),
        style: makeStyle(id),
        zIndex: id.endsWith('markers') || id.endsWith('targets') || id.endsWith('corridors') ? 166 : 162,
        properties: { name: `saxe-${id}`, geomorphicLayerLabel: label, geomorphicLayerSource: `/data/sweden/${file}` },
      });
      mapInstance.addLayer(layer);
      layerMap.set(id, layer);
      fetch(`/data/sweden/${file}`)
        .then((res) => res.json())
        .then((data) => {
          const features = new GeoJSON().readFeatures(data, { featureProjection: 'EPSG:3857' });
          features.forEach((f) => f.set('__geomorphicLayerLabel', label));
          layer.getSource()?.addFeatures(features);
        })
        .catch((err) => console.error(`Failed to load Saxe layer ${id}:`, err));
    });
  }, [selectedProject?.id, mapReadyVersion, saxeLayerVisibility, SAXE_VECTOR_LAYERS]);

  // --- Saxe geophysics raster overlays (FECA 2016, ImageStatic, EPSG:3857) ---
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;
    const layerMap = saxeRasterLayersRef.current;

    const removeAll = () => {
      layerMap.forEach((layer) => mapInstance.removeLayer(layer));
      layerMap.clear();
    };

    if (selectedProject?.id !== 'project-sgu-sweden') {
      removeAll();
      return;
    }

    SAXE_RASTER_LAYERS.forEach(({ id, file, label }) => {
      const visible = saxeRasterVisibility[id] === true;
      const existing = layerMap.get(id);
      if (!visible) {
        if (existing) existing.setVisible(false);
        return;
      }
      if (existing) {
        existing.setVisible(true);
        return;
      }
      const layer = new ImageLayer({
        source: new Static({
          url: `/data/sweden/saxe_rasters/${file}`,
          imageExtent: SAXE_RASTER_EXTENT_3857,
          projection: 'EPSG:3857',
        }),
        opacity: 0.7,
        zIndex: 85, // below Saxe vectors (162+)
        properties: { name: `saxe-raster-${id}`, geomorphicLayerLabel: label },
      });
      mapInstance.addLayer(layer);
      layerMap.set(id, layer);
    });
  }, [selectedProject?.id, mapReadyVersion, saxeRasterVisibility, SAXE_RASTER_LAYERS, SAXE_RASTER_EXTENT_3857]);

  // Render KIURI drill holes layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (kiuriDrillholesLayerRef.current) {
        mapInstance?.removeLayer(kiuriDrillholesLayerRef.current);
        kiuriDrillholesLayerRef.current = null;
      }
      return;
    }

    // Remove existing layer
    if (kiuriDrillholesLayerRef.current) {
      mapInstance.removeLayer(kiuriDrillholesLayerRef.current);
      kiuriDrillholesLayerRef.current = null;
    }

    if (!kiuriDrillholesVisible) return;

    // Fetch and render drill holes
    fetch('/data/kiuri_drillholes.geojson')
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`KIURI drillholes fetch failed (${res.status})`);
        }
        const text = await res.text();
        return JSON.parse(text.replace(/-?Infinity|\bNaN\b/g, 'null'));
      })
      .then((geojson) => {
        const features = new GeoJSON().readFeatures(geojson, {
          featureProjection: 'EPSG:3857'
        });

        const drillholesLayer = new VectorLayer({
          source: new VectorSource({ features }),
          style: (feature) => {
            const props = feature.getProperties();
            const prospect = props.prospect || props.name || '';
            // Color by prospect
            const prospectColors: Record<string, string> = {
              'Kiuri': '#FF9800',
              'Rassåive West': '#F44336',
              'Rassåive East': '#F44336',
              'Pallem': '#E040FB',
              'Latanjarka': '#26A69A',
              'Akatjvare': '#26A69A',
              'Askeluokta': '#42A5F5',
              'Kårnanjunnje': '#78909C',
              'Sågudden': '#78909C',
            };
            const color = prospectColors[prospect] || '#9C27B0';
            const label = props.hole_id || props.name?.replace('KIU-', '') || '';

            return new Style({
              image: new CircleStyle({
                radius: 4,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#fff', width: 1 })
              }),
              text: new Text({
                text: label,
                offsetY: -10,
                font: '8px sans-serif',
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#000', width: 2 })
              })
            });
          },
          zIndex: 200,
          properties: { name: 'kiuri-drillholes' }
        });

        mapInstance.addLayer(drillholesLayer);
        kiuriDrillholesLayerRef.current = drillholesLayer;
      })
      .catch((err) => {
        console.error('Failed to load KIURI drill holes:', err);
      });
  }, [selectedProject?.id, kiuriDrillholesVisible]);

  // Render KIURI district context holes (outside project boundary — Palkasbacken/Iekelvare/etc.)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (kiuriDistrictHolesLayerRef.current) {
        mapInstance?.removeLayer(kiuriDistrictHolesLayerRef.current);
        kiuriDistrictHolesLayerRef.current = null;
      }
      return;
    }

    if (kiuriDistrictHolesLayerRef.current) {
      mapInstance.removeLayer(kiuriDistrictHolesLayerRef.current);
      kiuriDistrictHolesLayerRef.current = null;
    }

    if (!kiuriDistrictHolesVisible) return;

    fetch('/data/kiuri_district_holes.geojson')
      .then((res) => res.json())
      .then((geojson) => {
        const features = new GeoJSON().readFeatures(geojson, {
          featureProjection: 'EPSG:3857'
        });

        const districtLayer = new VectorLayer({
          source: new VectorSource({ features }),
          style: (feature) => {
            const name = (feature as any).getProperties().name || '';
            const holeId = (feature as any).getProperties().idcode || '';
            const nameColors: Record<string, string> = {
              'Iekelvare': '#E53935',
              'Majves': '#E53935',
              'Tallberget': '#7B1FA2',
              'Tjäula': '#00897B',
              'Kallak': '#F57C00',
            };
            const color = nameColors[name] || '#78909C';
            const isDDH04001 = holeId === 'D1MAJ';

            return new Style({
              image: new CircleStyle({
                radius: isDDH04001 ? 7 : 4,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: isDDH04001 ? '#FFD600' : '#fff', width: isDDH04001 ? 2.5 : 1 })
              }),
              text: new Text({
                text: isDDH04001 ? 'DDH 04001' : holeId,
                offsetY: -12,
                font: isDDH04001 ? 'bold 9px sans-serif' : '7px sans-serif',
                fill: new Fill({ color: isDDH04001 ? '#FFD600' : color }),
                stroke: new Stroke({ color: '#000', width: 2 })
              })
            });
          },
          zIndex: 195,
          properties: { name: 'kiuri-district-holes' }
        });

        mapInstance.addLayer(districtLayer);
        kiuriDistrictHolesLayerRef.current = districtLayer;
      })
      .catch((err) => {
        console.error('Failed to load Kiuri district holes:', err);
      });
  }, [selectedProject?.id, kiuriDistrictHolesVisible]);

  // Render Kiuri application polygons (SEITEVARE + KOBERGET + NJUORRAM)
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (kiuriApplicationPolygonsLayerRef.current) {
        mapInstance?.removeLayer(kiuriApplicationPolygonsLayerRef.current);
        kiuriApplicationPolygonsLayerRef.current = null;
      }
      return;
    }

    if (kiuriApplicationPolygonsLayerRef.current) {
      mapInstance.removeLayer(kiuriApplicationPolygonsLayerRef.current);
      kiuriApplicationPolygonsLayerRef.current = null;
    }

    if (!kiuriApplicationPolygonsVisible) return;

    fetch('/data/kiuri_application_polygons.geojson')
      .then((res) => res.json())
      .then((geojson) => {
        const features = new GeoJSON().readFeatures(geojson, {
          featureProjection: 'EPSG:3857'
        });

        const appLayer = new VectorLayer({
          source: new VectorSource({ features }),
          style: (feature) => {
            const props = (feature as any).getProperties();
            const blockId = String(props.id || '').toLowerCase();
            if (!isKiuriGrantBlockId(blockId) || !kiuriApplicationPolygonVisibility[blockId]) {
              return [];
            }
            const block = KIURI_GRANT_BLOCK_BY_ID[blockId];
            const label = `${block.name} ${block.permitId}`;

            return new Style({
              stroke: new Stroke({ color: block.color, width: 3, lineDash: [8, 4] }),
              fill: new Fill({ color: `${block.color}15` }),
              text: new Text({
                text: label,
                font: 'bold 11px sans-serif',
                fill: new Fill({ color: block.color }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                overflow: true
              })
            });
          },
          zIndex: 180,
          properties: { name: 'kiuri-application-polygons' }
        });

        mapInstance.addLayer(appLayer);
        kiuriApplicationPolygonsLayerRef.current = appLayer;
      })
      .catch((err) => {
        console.error('Failed to load Kiuri application polygons:', err);
      });
  }, [selectedProject?.id, kiuriApplicationPolygonsVisible, kiuriApplicationPolygonVisibility]);

  // Render the separate Granudden bolt-on design. It is screening work only,
  // deliberately kept out of the granted Kiuri block collection.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (granuddenDraftLayerRef.current) {
        mapInstance?.removeLayer(granuddenDraftLayerRef.current);
        granuddenDraftLayerRef.current = null;
      }
      if (granuddenEvidenceLayerRef.current) {
        mapInstance?.removeLayer(granuddenEvidenceLayerRef.current);
        granuddenEvidenceLayerRef.current = null;
      }
      return;
    }

    if (granuddenDraftLayerRef.current) {
      mapInstance.removeLayer(granuddenDraftLayerRef.current);
      granuddenDraftLayerRef.current = null;
    }
    if (granuddenEvidenceLayerRef.current) {
      mapInstance.removeLayer(granuddenEvidenceLayerRef.current);
      granuddenEvidenceLayerRef.current = null;
    }

    if (!granuddenDraftVisible) return;

    Promise.all([
      fetch('/data/granudden_draft_application_polygon.geojson'),
      fetch('/data/granudden_map_evidence.geojson')
    ])
      .then(async ([polygonResponse, evidenceResponse]) => {
        if (!polygonResponse.ok) {
          throw new Error(`Granudden draft polygon request failed: ${polygonResponse.status}`);
        }
        if (!evidenceResponse.ok) {
          throw new Error(`Granudden evidence request failed: ${evidenceResponse.status}`);
        }
        return Promise.all([polygonResponse.json(), evidenceResponse.json()]);
      })
      .then(([polygonGeojson, evidenceGeojson]) => {
        const polygonFeatures = new GeoJSON().readFeatures(polygonGeojson, {
          featureProjection: 'EPSG:3857'
        });
        const draftLayer = new VectorLayer({
          source: new VectorSource({ features: polygonFeatures }),
          style: (feature) => {
            const name = String(feature.get('name') || 'GRANUDDEN ONSHORE DRAFT');
            const blockArea = Number(feature.get('block_area_ha') || 0);
            const shortName = name.includes('RIO') ? 'RIO BOULDER BLOCK' : 'HISTORIC TARGET BLOCK';
            return new Style({
              stroke: new Stroke({
                color: GRANUDDEN_DRAFT_LAYER.color,
                width: 3,
                lineDash: [12, 6, 2, 6]
              }),
              fill: new Fill({ color: `${GRANUDDEN_DRAFT_LAYER.color}18` }),
              text: new Text({
                text: `${shortName} — ${blockArea.toFixed(0)} ha\nDRAFT / NOT FILED`,
                font: `700 10px ${MAP_UI_FONT}`,
                fill: new Fill({ color: GRANUDDEN_DRAFT_LAYER.color }),
                stroke: new Stroke({ color: '#020617', width: 3.4 }),
                overflow: true
              })
            });
          },
          zIndex: 181,
          properties: { name: 'granudden-draft-application-polygon' }
        });

        const evidenceFeatures = new GeoJSON().readFeatures(evidenceGeojson, {
          featureProjection: 'EPSG:3857'
        });
        const evidenceLayer = new VectorLayer({
          source: new VectorSource({ features: evidenceFeatures }),
          style: (feature) => {
            const kind = String(feature.get('feature_kind') || '');
            const evidenceId = String(feature.get('id') || '');
            const name = String(feature.get('name') || '');
            if (kind === 'historic_target_footprint') {
              return new Style({
                stroke: new Stroke({ color: '#F59E0B', width: 2, lineDash: [6, 4] }),
                fill: new Fill({ color: '#F59E0B12' }),
                text: new Text({
                  text: '1983 DRILL TARGET\nGEOREF ±100 m',
                  font: `700 9px ${MAP_UI_FONT}`,
                  fill: new Fill({ color: '#FBBF24' }),
                  stroke: new Stroke({ color: '#020617', width: 3 }),
                  overflow: true
                })
              });
            }
            if (kind === 'historic_block_train') {
              return new Style({
                image: new RegularShape({
                  points: 3,
                  radius: 7,
                  rotation: 0,
                  fill: new Fill({ color: '#F97316' }),
                  stroke: new Stroke({ color: '#FFF7ED', width: 1.5 })
                }),
                text: new Text({
                  text: evidenceId.endsWith('_1') ? '1972 BLOCK TRAIN (6)\nGEOREF ±100 m' : '',
                  offsetY: -13,
                  font: `700 8px ${MAP_UI_FONT}`,
                  fill: new Fill({ color: '#FDBA74' }),
                  stroke: new Stroke({ color: '#020617', width: 2.5 })
                })
              });
            }
            if (kind === 'planned_collar_interpreted') {
              return new Style({
                image: new CircleStyle({
                  radius: 5,
                  fill: new Fill({ color: '#020617' }),
                  stroke: new Stroke({ color: '#FBBF24', width: 2 })
                }),
                text: new Text({
                  text: evidenceId.endsWith('_1') ? '3 DISTINCT 1983 COLLAR SYMBOLS' : '',
                  offsetX: -18,
                  offsetY: 15,
                  font: `700 8px ${MAP_UI_FONT}`,
                  fill: new Fill({ color: '#FDE68A' }),
                  stroke: new Stroke({ color: '#020617', width: 2.5 })
                })
              });
            }
            if (kind === 'rio_1999_sample') {
              const captured = Boolean(feature.get('captured_by_draft'));
              const cuPct = Number(feature.get('cu_pct'));
              const moPpm = Number(feature.get('mo_ppm'));
              const gradeText = [
                Number.isFinite(cuPct) ? `${cuPct.toFixed(cuPct >= 1 ? 2 : 3)}% Cu` : null,
                Number.isFinite(moPpm) ? `${moPpm.toFixed(0)} Mo` : null
              ].filter(Boolean).join(' · ');
              const sampleLabelOffsets: Record<string, { x: number; y: number }> = {
                S17587: { x: 24, y: -16 },
                S17588: { x: 26, y: 17 },
                S17589: { x: -24, y: -3 }
              };
              const labelOffset = sampleLabelOffsets[evidenceId] || { x: 10, y: -16 };
              return new Style({
                image: new RegularShape({
                  points: 4,
                  radius: captured ? 8 : 6,
                  angle: Math.PI / 4,
                  fill: new Fill({ color: captured ? '#FACC15' : '#64748B' }),
                  stroke: new Stroke({
                    color: captured ? '#FEF9C3' : '#CBD5E1',
                    width: captured ? 2 : 1.5
                  })
                }),
                text: new Text({
                  text: `${name}${gradeText ? `\n${gradeText}` : ''}`,
                  offsetX: labelOffset.x,
                  offsetY: labelOffset.y,
                  font: `${captured ? 700 : 600} 9px ${MAP_UI_FONT}`,
                  fill: new Fill({ color: captured ? '#FDE047' : '#CBD5E1' }),
                  stroke: new Stroke({ color: '#020617', width: 3 }),
                  textAlign: labelOffset.x < 0 ? 'right' : 'left'
                })
              });
            }
            return [];
          },
          zIndex: 211,
          properties: { name: 'granudden-map-evidence' }
        });

        mapInstance.addLayer(draftLayer);
        mapInstance.addLayer(evidenceLayer);
        granuddenDraftLayerRef.current = draftLayer;
        granuddenEvidenceLayerRef.current = evidenceLayer;
      })
      .catch((err) => {
        console.error('Failed to load Granudden draft polygon/evidence:', err);
      });
  }, [selectedProject?.id, granuddenDraftVisible]);

  // Render KIURI grab samples layer
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance || selectedProject?.id !== 'project-sgu-sweden') {
      if (kiuriGrabSamplesLayerRef.current) {
        mapInstance?.removeLayer(kiuriGrabSamplesLayerRef.current);
        kiuriGrabSamplesLayerRef.current = null;
      }
      return;
    }

    // Remove existing layer
    if (kiuriGrabSamplesLayerRef.current) {
      mapInstance.removeLayer(kiuriGrabSamplesLayerRef.current);
      kiuriGrabSamplesLayerRef.current = null;
    }

    if (!kiuriGrabSamplesVisible) return;

    // Fetch and render grab samples
    fetch('/data/kiuri_grab_samples.geojson')
      .then((res) => res.json())
      .then((geojson) => {
        const features = new GeoJSON().readFeatures(geojson, {
          featureProjection: 'EPSG:3857'
        });

        const grabSamplesLayer = new VectorLayer({
          source: new VectorSource({ features }),
          style: (feature) => {
            const cu = feature.get('cu_pct');
            const zn = feature.get('zn_pct');
            const ag = feature.get('ag_ppm');
            const name = feature.get('name') || '';
            // Color by highest grade: Cu=red, Zn=blue, Ag=silver
            let color = '#9c27b0'; // purple default (no grades)
            if (cu && parseFloat(cu) > 1) color = '#f44336'; // red for high Cu
            else if (zn && parseFloat(zn) > 5) color = '#2196f3'; // blue for high Zn
            else if (ag && parseFloat(ag) > 100) color = '#9e9e9e'; // silver for high Ag

            const label = [
              cu ? `${cu}% Cu` : null,
              zn ? `${zn}% Zn` : null,
              ag ? `${ag} Ag` : null,
            ].filter(Boolean).join(', ') || 'No grades';

            return new Style({
              image: new RegularShape({
                points: 4, // diamond shape
                radius: 8,
                angle: Math.PI / 4,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#fff', width: 2 })
              }),
              text: new Text({
                text: `${name}\n${label}`,
                font: '9px sans-serif',
                fill: new Fill({ color: '#fff' }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                offsetY: -18,
                textAlign: 'center'
              })
            });
          },
          zIndex: 210,
          properties: { name: 'kiuri-grab-samples' }
        });

        mapInstance.addLayer(grabSamplesLayer);
        kiuriGrabSamplesLayerRef.current = grabSamplesLayer;
      })
      .catch((err) => {
        console.error('Failed to load KIURI grab samples:', err);
      });
  }, [selectedProject?.id, kiuriGrabSamplesVisible]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setShowAnalysisResults(false);
      return;
    }
    const stored = localStorage.getItem(`${HEATMAP_PREF_PREFIX}${selectedProject.id}`) === 'true';
    setShowAnalysisResults(stored);
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (stored) {
        next.add('results');
      } else {
        next.delete('results');
      }
	      const nextArr = Array.from(next);
      if (nextArr.length === prev.length && nextArr.every((value, index) => value === prev[index])) {
        return prev;
      }
      return nextArr;
    });
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    localStorage.setItem(`${HEATMAP_PREF_PREFIX}${selectedProject.id}`, showAnalysisResults ? 'true' : 'false');
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (showAnalysisResults) {
        next.add('results');
      } else {
        next.delete('results');
      }
	      const nextArr = Array.from(next);
      if (nextArr.length === prev.length && nextArr.every((value, index) => value === prev[index])) {
        return prev;
      }
      return nextArr;
    });

    if (showAnalysisResults) {
      const analysisIds = projectAnalysisResults
        .map((analysis) => getAnalysisId(analysis))
        .filter((id): id is string => Boolean(id));
      window.dispatchEvent(
        new CustomEvent('heatmap:request', {
          detail: {
            projectId: selectedProject.id,
            analysisIds
          }
        })
      );
    }
  }, [showAnalysisResults, selectedProject?.id, projectAnalysisResults]);

  useEffect(() => {
    if (!selectedProject?.id) {
      prefetchedProjectRef.current = null;
      return;
    }

    if (!prefetchedProjectRef.current?.startsWith(`${selectedProject.id}:`)) {
      prefetchedProjectRef.current = null;
    }

    const analysisIds = projectAnalysisResults
      .map((analysis) => getAnalysisId(analysis))
      .filter((id): id is string => Boolean(id));

    if (analysisIds.length === 0) {
      prefetchedProjectRef.current = null;
      return;
    }

    const cacheKey = `${selectedProject.id}:${analysisIds.length}`;

    if (prefetchedProjectRef.current === cacheKey) {
      return;
    }

    prefetchedProjectRef.current = cacheKey;

    window.dispatchEvent(
      new CustomEvent('heatmap:prefetch', {
        detail: {
          projectId: selectedProject.id,
          analysisIds
        }
      })
    );
  }, [selectedProject?.id, projectAnalysisResults]);

  // Mineral Discovery state
  const [discoveryPanelOpen, setDiscoveryPanelOpen] = useState(false);
  const [discoveryPanelWidth, setDiscoveryPanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const previousPolygonCountRef = useRef<number>(0);
  const previousPolygonBoundsRef = useRef<string | null>(null);
  
  // Visualization mode state
  const [visualizationMode, setVisualizationMode] = useState<'grid' | 'heatmap'>(() => {
    if (typeof window === 'undefined') {
      return 'heatmap';
    }
    const saved = localStorage.getItem('mineral-ai-visualization-mode');
    return saved === 'grid' ? 'grid' : 'heatmap';
  });
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      devInfo('[visualization-mode]', visualizationMode);
    }
  }, [visualizationMode]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem('mineral-ai-visualization-mode', visualizationMode);
  }, [visualizationMode]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<'grid' | 'heatmap'>).detail;
      if (!detail) {
        return;
      }
      setVisualizationMode(detail);
    };
    window.addEventListener('analysis:set-visualization-mode', handler as EventListener);
    return () => window.removeEventListener('analysis:set-visualization-mode', handler as EventListener);
  }, []);

  
  // Dual Analysis state
  const [showDualAnalysis, setShowDualAnalysis] = useState(false);
  const [dualAnalysisResults, setDualAnalysisResults] = useState<any>(null);
  const [isDualAnalysisRunning, setIsDualAnalysisRunning] = useState(false);
  const [dualAnalysisProgress, setDualAnalysisProgress] = useState(0);
  const [dualAnalysisMessage, setDualAnalysisMessage] = useState('Initializing analysis...');
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null);
  const [resultsViewerOpen, setResultsViewerOpen] = useState(false);
  const [ruleContributionOpen, setRuleContributionOpen] = useState(false);
  const [statisticsOpen, setStatisticsOpen] = useState(false);
  const [ruleTuningOpen, setRuleTuningOpen] = useState(false);
  const [showEnhancedAnalysisLog, setShowEnhancedAnalysisLog] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState<any[]>([]);
  const [currentRuleContributions, setCurrentRuleContributions] = useState<any[]>([]);

  useEffect(() => {
    const handler = () => setStatisticsOpen(true);
    window.addEventListener('analysis:open-statistics', handler);
    return () => window.removeEventListener('analysis:open-statistics', handler);
  }, []);

  useEffect(() => {
    const handler = () => setRuleTuningOpen(true);
    window.addEventListener('analysis:open-tuning', handler);
    return () => window.removeEventListener('analysis:open-tuning', handler);
  }, []);

  // Listen for staking polygons generated from sidebar
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ geojson: any; analysisId?: string }>;
      if (customEvent.detail?.geojson) {
        setStakingPolygonsGeojson(customEvent.detail.geojson);
        setStakingPolygonsVisible(true);
      }
    };
    window.addEventListener('staking:polygons-generated', handler as EventListener);
    return () => window.removeEventListener('staking:polygons-generated', handler as EventListener);
  }, []);

  // Render staking polygons layer when geojson is set
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing staking layer
    if (stakingPolygonLayerRef.current) {
      map.removeLayer(stakingPolygonLayerRef.current);
      stakingPolygonLayerRef.current = null;
    }

    if (!stakingPolygonsGeojson || !stakingPolygonsVisible) return;

    try {
      const features = new GeoJSON().readFeatures(stakingPolygonsGeojson, {
        featureProjection: 'EPSG:3857'
      });

      const stakingLayer = new VectorLayer({
        source: new VectorSource({ features }),
        style: (feature) => {
          const props = feature.getProperties();
          const maxProb = props.max_prob ?? props.max_probability ?? 0.8;
          const clusterRank = props.cluster_id ?? props.rank ?? 1;

          // Color based on probability within the project-boundary family:
          // green (high), cyan (medium), blue (lower).
          const hue = maxProb > 0.9 ? 142 : maxProb > 0.8 ? 188 : 213;
          const fillColor = `hsla(${hue}, 70%, 50%, 0.25)`;
          const strokeColor = `hsla(${hue}, 80%, 40%, 0.9)`;

          return new Style({
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({ color: strokeColor, width: 2.5, lineDash: [6, 4] }),
            text: new Text({
              text: `#${clusterRank}`,
              font: 'bold 12px monospace',
              fill: new Fill({ color: '#fff' }),
              stroke: new Stroke({ color: '#000', width: 3 }),
              overflow: true
            })
          });
        },
        zIndex: 150 // Above heatmaps but below popups
      });

      map.addLayer(stakingLayer);
      stakingPolygonLayerRef.current = stakingLayer;

      // Fit view to staking polygons
      const extent = stakingLayer.getSource()?.getExtent();
      if (extent && extent.every(Number.isFinite)) {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 12, duration: 500 });
      }
    } catch (err) {
      console.error('Failed to render staking polygons:', err);
    }
  }, [stakingPolygonsGeojson, stakingPolygonsVisible]);

  // Listen for drill-cluster staking polygon toggle events from DrillClusterReviewPanel
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setDcStakingVisible(!!customEvent.detail.visible);
        setDcStakingGeojson(customEvent.detail.geojson || null);
        setDcShowStaking(customEvent.detail.showStaking !== false);
        setDcShowCompetitor(!!customEvent.detail.showCompetitor);
      }
    };
    window.addEventListener('drill-cluster:staking-polygon-toggle', handler as EventListener);
    return () => window.removeEventListener('drill-cluster:staking-polygon-toggle', handler as EventListener);
  }, []);

  // Render drill-cluster staking polygon layer + competitor boundary layer
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing layers
    if (dcStakingLayerRef.current) {
      map.removeLayer(dcStakingLayerRef.current);
      dcStakingLayerRef.current = null;
    }
    if (dcCompetitorLayerRef.current) {
      map.removeLayer(dcCompetitorLayerRef.current);
      dcCompetitorLayerRef.current = null;
    }

    if (!dcStakingGeojson || !dcStakingVisible) return;

    try {
      const allFeatures = new GeoJSON().readFeatures(dcStakingGeojson, {
        featureProjection: 'EPSG:3857',
      });

      // Split features by toggleGroup
      const stakingFeatures = allFeatures.filter((f: any) => {
        const tg = f.get('toggleGroup') || f.get('layer') || '';
        return tg !== 'competitor' && tg !== 'bluelake_boundary';
      });
      const competitorFeatures = allFeatures.filter((f: any) => {
        const tg = f.get('toggleGroup') || f.get('layer') || '';
        return tg === 'competitor' || tg === 'bluelake_boundary';
      });

      // Render staking polygon layer
      if (dcShowStaking && stakingFeatures.length > 0) {
        const stakingLayer = new VectorLayer({
          source: new VectorSource({ features: stakingFeatures }),
          style: (feature) => {
            const props = feature.getProperties();
            const label = props.cluster_name || props.name || 'Staking Polygon';
            return new Style({
              fill: new Fill({ color: 'rgba(6, 182, 212, 0.15)' }),
              stroke: new Stroke({ color: '#06b6d4', width: 2.5, lineDash: [8, 5] }),
              text: new Text({
                text: label,
                font: 'bold 11px monospace',
                fill: new Fill({ color: '#06b6d4' }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                overflow: true,
              }),
            });
          },
          zIndex: 155,
        });
        map.addLayer(stakingLayer);
        dcStakingLayerRef.current = stakingLayer;

        // Fit view to staking polygon
        const extent = stakingLayer.getSource()?.getExtent();
        if (extent && extent.every(Number.isFinite)) {
          map.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 13, duration: 500 });
        }
      }

      // Render competitor boundary layer (red, solid)
      if (dcShowCompetitor && competitorFeatures.length > 0) {
        const competitorLayer = new VectorLayer({
          source: new VectorSource({ features: competitorFeatures }),
          style: (feature) => {
            const props = feature.getProperties();
            const permitType = props.permit_type || '';
            const label = props.name || '';
            const isConc = permitType.includes('concession');
            return new Style({
              fill: new Fill({ color: isConc ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)' }),
              stroke: new Stroke({
                color: '#ef4444',
                width: isConc ? 2.5 : 1.8,
                lineDash: isConc ? undefined : [6, 4],
              }),
              text: new Text({
                text: label,
                font: 'bold 10px monospace',
                fill: new Fill({ color: '#ef4444' }),
                stroke: new Stroke({ color: '#000', width: 3 }),
                overflow: true,
              }),
            });
          },
          zIndex: 154,
        });
        map.addLayer(competitorLayer);
        dcCompetitorLayerRef.current = competitorLayer;
      }
    } catch (err) {
      console.error('Failed to render drill-cluster staking polygon:', err);
      setLayerLoadError('Staking polygon failed to render');
    }
  }, [dcStakingGeojson, dcStakingVisible, dcShowStaking, dcShowCompetitor]);

  const [analysisResultsLayer, setAnalysisResultsLayer] = useState<VectorLayer<VectorSource> | null>(null);
  const currentAnalysisLayerIdRef = useRef<string | null>(null); // Track which analysis the current layer belongs to

  // Hide analysis results layer when not on copper project (e.g., switching to uranium project)
  useEffect(() => {
    if (selectedProject?.id !== 'project-sgu-sweden' && analysisResultsLayer) {
      analysisResultsLayer.setVisible(false);
    }
  }, [selectedProject?.id, analysisResultsLayer]);
  const analysisAreaCacheRef = useRef<globalThis.Map<string, { cacheKey: string; areas: Array<{ longitude: number; latitude: number; probability: number; score: number; confidence: number; priorityRank: number; areaId: string }> }>>(new globalThis.Map());
  const [loadedAnalysisData, setLoadedAnalysisData] = useState<any[]>([]); // Store analysis data for popup
  const loadedAnalysisRef = useRef<string | null>(null); // Track loaded analysis to prevent duplicate loads
  const previousProjectRef = useRef<string | null>(null); // Track previous project to prevent unnecessary layer clearing

  // Polygon Drawing state
  const [polygonDrawingEnabled, setPolygonDrawingEnabled] = useState(false);
  const [polygonManagerOpen, setPolygonManagerOpen] = useState(false);
  const [visiblePolygons, setVisiblePolygons] = useState<Set<string>>(new Set());

  // Sync visible polygons with polygonRegions data
  useEffect(() => {
    const visiblePolygonIds = polygonRegions
      .filter(polygon => polygon.visible)
      .map(polygon => polygon.id);
    
    setVisiblePolygons(new Set(visiblePolygonIds));
  }, [polygonRegions]);

  // (mapLegendCollapsed localStorage removed — legend replaced by isLayersCollapsed widget)

  // Add ESC key listener to close popups
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (popupInfo || popupType === 'polygon' || inspectionRecord)) {
        closePopup();
        closeInspectionDrawer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [popupInfo, popupType, inspectionRecord, closePopup, closeInspectionDrawer]);

  // Demo tour — digit keys jump to pre-configured stops (viewport + layers).
  // Applies through the QA bridge so it reuses the same tested toggle paths.
  useEffect(() => {
    const handleTourKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const stop = DEMO_TOUR_STOPS.find((s) => s.key === event.key);
      if (!stop) return;
      if (!(window as any).__geomorphicMapQa) return;
      (async () => {
        // Re-read the bridges on every use — they are recreated when the
        // project (and therefore MapView's layer registry) changes.
        const mapQa = () => (window as any).__geomorphicMapQa;
        const appQa = () => (window as any).__geomorphicAppQa;

        // Switch to the stop's project first: layer-control ids only exist
        // within their project, so toggles on the wrong project no-op.
        if (stop.projectId && appQa()?.setProjectById) {
          try {
            const currentProject = appQa().getState?.()?.currentProject?.id;
            if (currentProject !== stop.projectId) {
              await appQa().setProjectById(stop.projectId);
              // Wait for the new project's layer registry to register the
              // stop's layers (project data loads async).
              for (let attempt = 0; attempt < 20; attempt++) {
                await new Promise((resolve) => setTimeout(resolve, 400));
                const layers = mapQa()?.getState?.()?.layers || [];
                if (stop.layerIds.every((id) => layers.some((layer: any) => layer?.id === id))) break;
              }
            }
          } catch {}
        }

        for (const layerId of stop.layerIds) {
          try { await mapQa()?.toggleLayer(layerId, true); } catch {}
        }
        const viewport = { lon: stop.lon, lat: stop.lat, zoom: stop.zoom };
        try { await mapQa()?.setViewport(viewport); } catch {}
        // Re-assert after a beat — a late fit-to-extent from project/layer
        // loading can land after the first set and steal the viewport.
        await new Promise((resolve) => setTimeout(resolve, 1200));
        try { await mapQa()?.setViewport(viewport); } catch {}
      })();
      setTourToast(`${stop.key} · ${stop.title}`);
      if (tourToastTimerRef.current) clearTimeout(tourToastTimerRef.current);
      tourToastTimerRef.current = setTimeout(() => setTourToast(null), 2800);
    };
    document.addEventListener('keydown', handleTourKey);
    return () => {
      document.removeEventListener('keydown', handleTourKey);
      if (tourToastTimerRef.current) clearTimeout(tourToastTimerRef.current);
    };
  }, []);

  const [pendingPolygonData, setPendingPolygonData] = useState<{
    coordinates: number[][];
    metadata: PolygonMetadata;
    isValidationFallback?: boolean;
    color?: string;
    transparency?: number;
  } | null>(null);

  // Polygon handlers
  const handlePolygonComplete = (
    coordinates: number[][],
    metadata: PolygonMetadata,
    isValidationFallback?: boolean,
    color?: string,
    transparency?: number
  ) => {
                // DON'T stop drawing mode yet - keep polygon visible while naming
    // setPolygonDrawingEnabled(false); // Removed to keep polygon visible
    
    // Check if coordinates are in South America (rough bounds check)
    const inSouthAmerica = coordinates.some(([lon, lat]) => 
      lat >= -60 && lat <= 15 && lon >= -85 && lon <= -30
    );
    
    if (!inSouthAmerica) {
          }
    
    if (isValidationFallback) {
      devInfo('📊 Using fallback area calculations - validation service was unavailable');
    }
    
    // Open polygon manager to name and save the polygon
    setPolygonManagerOpen(true);
        // Store the pending polygon data for the manager
    setPendingPolygonData({ coordinates, metadata, isValidationFallback, color, transparency });
  };

  const handlePolygonModifyComplete = useCallback(
    async ({
      polygonId,
      coordinates
    }: {
      polygonId: string;
      coordinates: number[][];
      metadata: PolygonMetadata;
      isValidationFallback?: boolean;
    }) => {
      if (!onPolygonUpdate) {
        console.warn('Polygon update handler not provided; unable to commit polygon edits.');
        return;
      }

      try {
        await onPolygonUpdate(polygonId, { coordinates });
      } catch (error) {
        console.error('Failed to update polygon geometry:', error);
      } finally {
        setPolygonDrawingEnabled(false);
        setPendingPolygonData(null);
      }
    },
    [onPolygonUpdate]
  );

  const handlePolygonValidation = (coordinates: number[][], isValid: boolean, errors: string[]) => {
        if (!isValid) {
            // Check if this is a validation service failure (vs. actual invalid polygon)
      const isServiceFailure = errors.some(error => 
        error.includes('Failed to validate polygon with server') || 
        error.includes('approximate calculations')
      );
      if (isServiceFailure) {
        devInfo('📊 Validation service unavailable - using fallback calculations');
      }
    }
  };

  const handlePolygonVisibilityToggle = useCallback((polygonId: string, visible: boolean) => {
    setVisiblePolygons((prev) => {
      const next = new Set(prev);
      if (visible) {
        next.add(polygonId);
      } else {
        next.delete(polygonId);
      }
      return next;
    });
    onPolygonUpdate?.(polygonId, { visible });
  }, [onPolygonUpdate]);

  const handlePolygonManagerAnalysis = (polygonId: string, config: AnalysisConfig) => {
    const polygon = polygonRegions.find(p => p.id === polygonId);
    if (polygon && onPolygonAnalysis) {
      onPolygonAnalysis(polygonId, config);
    }
  };

  const handlePendingPolygonCleared = () => {
    setPendingPolygonData(null);
  };

  // 🚨 CRITICAL: Listen for forced map refresh events to clear persisted layers
  useEffect(() => {
    const handleForceMapRefresh = (event: CustomEvent) => {
            // Clear analysis results layer immediately
      if (analysisResultsLayer) {
                mapInstanceRef.current?.removeLayer(analysisResultsLayer);
        setAnalysisResultsLayer(null);
      }
      
      // Clear any other analysis-related layers
      if (mapInstanceRef.current) {
        const layers = mapInstanceRef.current.getLayers().getArray();
        layers.forEach((layer: any, index: number) => {
          const layerName = layer.get('name') || `layer_${index}`;
          if (layerName === 'analysis-results' || layerName?.includes('analysis')) {
                        mapInstanceRef.current?.removeLayer(layer);
          }
        });
      }
      
          };

    window.addEventListener('forceMapRefresh', handleForceMapRefresh as EventListener);
    return () => {
      window.removeEventListener('forceMapRefresh', handleForceMapRefresh as EventListener);
    };
  }, [analysisResultsLayer]);
  
  // Get the latest analysis for the current project  
  const getLatestProjectAnalysis = useCallback(async (projectId: string) => {
        try {
      const response = await apiFetch(`/api/mineral-discovery/analyses/${projectId}/latest`);
      if (!response.ok) {
        if (response.status === 404) {
                    return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
            return data.analysis;
    } catch (error) {
      console.error('❌ Failed to get latest analysis:', error);
      return null;
    }
  }, []);

  // Get all analyses for the current project
  const getAllProjectAnalyses = useCallback(async (projectId: string) => {
        try {
      const response = await apiFetch(`/api/mineral-discovery/analyses/${projectId}`);
      if (!response.ok) {
        if (response.status === 404) {
                    return [];
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
            return data.analyses || [];
    } catch (error) {
      console.error('❌ Failed to get all analyses:', error);
      return [];
    }
  }, []);

  // Load and display analysis results from backend
  const loadAnalysisResults = useCallback(async (analysisId: string, retryCount = 0) => {
    // Check if map is ready
    if (!mapInstanceRef.current) {
      if (retryCount < 5) { // Max 5 retries
                setTimeout(() => loadAnalysisResults(analysisId, retryCount + 1), 500);
        return null;
      } else {
                return null;
      }
    }
    
    try {
      const response = await apiFetch(`/api/mineral-discovery/results/${analysisId}?view=map`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const result = await response.json();
            // Store loaded analysis data for polygon popup
      setLoadedAnalysisData(prev => {
        const exists = prev.find(r => r.id === result.id);
        if (!exists) {
                    return [...prev, result];
        }
        return prev;
      });
      
      // Transform backend data to frontend format before creating layer
      const transformedResult = {
        ...result,
        project_id: result.project_id || result.id?.split('_')?.[0] || 'unknown',
        highPotentialAreas: []
      };

      // Use the actual high potential areas from the backend (already filtered)
      if (result.results?.ml_results?.high_potential_areas) {
        transformedResult.highPotentialAreas = result.results.ml_results.high_potential_areas;
              } else if (result.highPotentialAreas) {
        // Fallback to top-level highPotentialAreas if available
        transformedResult.highPotentialAreas = result.highPotentialAreas;
              }

      // Create visualization layer with transformed data
      setTimeout(() => createAnalysisResultsLayer(transformedResult, result.id || result.analysis_id), 0);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to load analysis results:', error);
      return null;
    }
  }, []);

  // Dynamic load latest analysis for current project
  const loadLatestProjectAnalysis = useCallback(async (projectId: string) => {
        const latestAnalysis = await getLatestProjectAnalysis(projectId);
    if (!latestAnalysis) {
            return null;
    }
    
            return await loadAnalysisResults(latestAnalysis.id);
  }, [getLatestProjectAnalysis, loadAnalysisResults]);

  // Load ALL analysis results for current project (for polygon-specific visualization)
  // Handle analysis completion
  const handleAnalysisComplete = useCallback((result: any) => {
                setDiscoveryResults(prev => [...prev, result]);
    setSelectedResultId(result.id);
    // Auto-open results viewer when analysis completes
    setResultsViewerOpen(true);
    
    // Create visualization layer for analysis results — the user just ran this
    // analysis, so show it (autoShow keeps legend + layer state in sync).
            createAnalysisResultsLayer(result, result.id || result.analysis_id, { autoShow: true });
    
    // Call the parent callback to populate Reports tab (this is the critical path)
    if (onAnalysisComplete) {
            try {
        onAnalysisComplete(result);
              } catch (error) {
        console.error('❌ MapView: Error calling parent onAnalysisComplete:', error);
      }
    } else {
      console.error('❌ No onAnalysisComplete callback provided to MapView!');
    }
    
    // Mock rule contributions for demonstration
    const mockRuleContributions = [
      {
        ruleId: 'sericite_alt',
        ruleName: 'Sericite Alteration',
        category: 'spectral',
        weight: 25,
        contribution: 35.2,
        confidence: 0.82,
        triggeredLocations: 47,
        description: 'SWIR1/SWIR2 ratio indicates phyllic alteration',
        threshold: { value: 1.15, operator: '>' as const }
      },
      {
        ruleId: 'fault_prox',
        ruleName: 'Fault Proximity',
        category: 'structural',
        weight: 20,
        contribution: 28.5,
        confidence: 0.91,
        triggeredLocations: 52,
        description: 'Within 800m of major fault structures',
        threshold: { value: 800, operator: '<' as const, unit: 'm' }
      },
      {
        ruleId: 'au_anomaly',
        ruleName: 'Gold Geochemical Anomaly',
        category: 'geochemical',
        weight: 30,
        contribution: 22.8,
        confidence: 0.76,
        triggeredLocations: 31,
        description: 'Elevated Au concentrations in stream sediments',
        threshold: { value: 15, operator: '>' as const, unit: 'ppb' }
      },
      {
        ruleId: 'host_rock',
        ruleName: 'Favorable Host Rock',
        category: 'geological',
        weight: 15,
        contribution: 8.5,
        confidence: 0.88,
        triggeredLocations: 28,
        description: 'Volcanic and metamorphic host rocks',
        statistics: { mean: 0.72, std: 0.15, min: 0.45, max: 0.95 }
      },
      {
        ruleId: 'ndvi_change',
        ruleName: 'Vegetation Change',
        category: 'temporal',
        weight: 10,
        contribution: 5.0,
        confidence: 0.65,
        triggeredLocations: 12,
        description: 'NDVI decrease over 90-day period',
        threshold: { value: -0.2, operator: '<' as const }
      }
    ];
    setCurrentRuleContributions(mockRuleContributions);
  }, []);

  // Create analysis results visualization layer
  const createAnalysisResultsLayer = useCallback((results: any, analysisId?: string, options?: { autoShow?: boolean }) => {
        // Show where it's called from
    devLog('🎯 MARKER DATA SOURCE DIAGNOSTIC:', {
      caller: 'createAnalysisResultsLayer',
      analysisId,
      hasEnhancedResults: !!results?.enhanced_results,
      hasGridPoints: !!results?.enhanced_results?.grid_points,
      gridPointsCount: results?.enhanced_results?.grid_points?.length,
      hasTopTargets: !!results?.results?.top_targets,
      topTargetsCount: results?.results?.top_targets?.length,
      hasMLGridCoords: !!results?.results?.ml_results?.grid_coordinates,
      mlGridCoordsCount: results?.results?.ml_results?.grid_coordinates?.length,
      hasRuleGridCoords: !!results?.results?.rule_based_results?.grid_coordinates,
      ruleGridCoordsCount: results?.results?.rule_based_results?.grid_coordinates?.length,
    });
    
    // Sample first 3 coordinates from each source for comparison
    if (results?.enhanced_results?.grid_points?.length > 0) {
      devLog('📍 Enhanced grid_points (first 3):', 
        results.enhanced_results.grid_points.slice(0, 3).map((p: any) => ({
          lat: p.latitude,
          lon: p.longitude,
          prob: p.probability
        }))
      );
    }
    if (results?.results?.top_targets?.length > 0) {
      devLog('📍 Top targets (first 3):', 
        results.results.top_targets.slice(0, 3).map((p: any) => ({
          lat: p.latitude,
          lon: p.longitude,
          prob: p.probability
        }))
      );
    }
    if (results?.results?.ml_results?.grid_coordinates?.length > 0) {
      devLog('📍 ML grid_coordinates (first 3):', 
        results.results.ml_results.grid_coordinates.slice(0, 3).map((c: any, i: number) => ({
          coord: c,
          prob: results.results.ml_results.grid_probabilities?.[i]
        }))
      );
    }
    
    // 🚨 PROJECT ISOLATION DEBUGGING - CHECK WHAT PROJECT THIS ANALYSIS BELONGS TO
                // CRITICAL: Don't display analysis results if they don't belong to current project
    if (results?.project_id && selectedProject?.id && results.project_id !== selectedProject.id) {
            return; // Don't create layer for wrong project
    }
    
    if (!mapInstanceRef.current) {
            return;
    }
    
    // CRITICAL FIX: Use the SAME data source as heat map (grid_points or top_targets)
    // This matches the extractGridData function logic
    // Prefer enhanced grid points, otherwise fall back to other shapes
    const gridPoints =
      results?.enhanced_results?.grid_points ||
      results?.results?.grid_points ||
      results?.results?.ml_results?.grid_points ||
      results?.grid_points;

    const mlCoords =
      results?.results?.ml_results?.grid_coordinates ||
      results?.results?.grid_coordinates ||
      results?.grid_coordinates;
    const mlProbs =
      results?.results?.ml_results?.grid_probabilities ||
      results?.results?.grid_probabilities ||
      results?.grid_probabilities;

    const topTargets = results?.results?.top_targets;
    
    // Normalize grid coordinate + probability pairs when provided separately
    let normalizedPairs: any[] = [];
    if (Array.isArray(mlCoords) && Array.isArray(mlProbs) && mlCoords.length === mlProbs.length) {
      normalizedPairs = mlCoords.map((coord: any, idx: number) => {
        const rawLat = Array.isArray(coord) ? coord[1] ?? coord[0] : coord?.latitude ?? coord?.lat;
        const rawLon = Array.isArray(coord) ? coord[0] ?? coord[1] : coord?.longitude ?? coord?.lon;
        const prob = mlProbs[idx] ?? coord?.probability ?? 0;
        const lat = Math.abs(rawLat) > 90 && Math.abs(rawLon) <= 90 ? rawLon : rawLat;
        const lon = Math.abs(rawLat) > 90 && Math.abs(rawLon) <= 90 ? rawLat : rawLon;
        return {
          latitude: Number(lat) || 0,
          longitude: Number(lon) || 0,
          probability: Number(prob) || 0,
          score: Number(prob) || 0,
          confidence: coord?.confidence ?? 0,
          areaId: `pair_${idx}`
        };
      });
    }

    // Use grid_points if available (all points), otherwise use normalized pairs, otherwise top_targets
    const dataPoints =
      (Array.isArray(gridPoints) && gridPoints.length > 0 && gridPoints) ||
      (normalizedPairs.length > 0 && normalizedPairs) ||
      topTargets;
    
    devLog('🎯 MARKER: Using data source:', gridPoints?.length > 0 ? 'grid_points' : 'top_targets');
    
    // Prepare marker data from the same source as heat map
    let markerData: any[] = [];
    
    if (dataPoints && Array.isArray(dataPoints) && dataPoints.length > 0) {
      // Use the same data transformation as heat map
      markerData = dataPoints.map((target: any, index: number) => ({
        longitude: target.longitude,
        latitude: target.latitude,
        probability: normalizeProbabilityValue(target.probability ?? target.score ?? 0),
        score: normalizeProbabilityValue(target.probability ?? target.score ?? 0),
        confidence: target.confidence_interval 
          ? 1 - (target.confidence_interval[1] - target.confidence_interval[0])
          : 1 - (target.total_uncertainty || 0.5),
        commodity: results.commodity || 'copper',
        id: `grid_${index}`
      }));
      
      devLog('🎯 Marker coordinates (first 3):', 
        markerData.slice(0, 3).map(m => ({
          lat: m.latitude,
          lon: m.longitude,
          prob: m.probability
        }))
      );
    } else {
      // Fallback to highPotentialAreas if no grid/target data
      const highPotentialAreas = results?.highPotentialAreas;
      if (!highPotentialAreas || highPotentialAreas.length === 0) {
        devLog('⚠️ No marker data available');
        return; // Exit early if no data
      }
      markerData = highPotentialAreas.map((area: any, idx: number) => ({
        ...area,
        probability: normalizeProbabilityValue(area?.probability ?? area?.score ?? 0),
        score: normalizeProbabilityValue(area?.probability ?? area?.score ?? 0),
        id: area.id || `fallback_${idx}`
      }));
    }
    
    if (!legacyAnalysisMarkersEnabled) {
      if (analysisResultsLayer && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(analysisResultsLayer);
        setAnalysisResultsLayer(null);
      }
      return;
    }

    // Remove existing analysis results layer
    if (analysisResultsLayer) {
      mapInstanceRef.current.removeLayer(analysisResultsLayer);
    }

    // Create vector source for analysis results
    const vectorSource = new VectorSource();

    // Check analysis-level visibility first
    const effectiveAnalysisId = analysisId || results.analysis_id || results.id || 'default';
    const analysisVisibilityKey = `heatmap_${effectiveAnalysisId}`;
    const analysisVisible = markerVisibility?.get(analysisVisibilityKey);
    // If explicitly set to false, don't render any markers for this analysis
    if (analysisVisible === false) {
      // Clear existing layer if analysis is hidden
      if (analysisResultsLayer && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(analysisResultsLayer);
        setAnalysisResultsLayer(null);
      }
      return;
    }

    // Sort by probability and create features
    markerData
      .sort((a: any, b: any) => (b.probability || 0) - (a.probability || 0))
      .slice(0, MAX_MARKERS_PER_ANALYSIS)
      .forEach((area: any, index: number) => {
        const rank = index + 1;
        // Use analysis-specific marker ID if available
        const markerId = `${effectiveAnalysisId}_rank_${rank}`;

        // Show the trimmed set by default for better granularity
        const isVisible = markerVisibility?.get(markerId) ?? (rank <= DEFAULT_VISIBLE_MARKER_COUNT);

        if (!isVisible) {
          return;
        }
        
        const { latitude, longitude, score, confidence, probability, commodity } = area;
        
        // Create point feature - backend returns latitude, longitude directly
        const transformedCoords = fromLonLat([longitude, latitude]);
        
        const feature = new Feature({
          geometry: new Point(transformedCoords),
        });
        
        // Set properties for styling and interaction
        feature.setProperties({
          analysisId: area.id,
          sourceAnalysisId: effectiveAnalysisId, // The actual analysis ID for visibility control
          score: score !== undefined ? score : (probability * 100),
          confidence: confidence,
          probability: probability,
          priorityRank: rank,
          analysisType: 'dual_analysis',
          commodity: commodity || area.commodity,
          type: 'analysis-result'
        });

        vectorSource.addFeature(feature);
      });

    // Probability-based color ramp (heatmap-style)
    const getMarkerColor = (probability: number): string => getProbabilityColor(probability);

    const probabilityStats = computeProbabilityStats(markerData.map((m) => m.probability ?? 0));

    // Create vector layer with high-contrast styling
    const newResultsLayer = new VectorLayer({
      source: vectorSource,
      declutter: true,
      style: (feature) => {
        const score = feature.get('score') || 0;
        const priorityRank = feature.get('priorityRank') || 999;

        const probabilityRaw = feature.get('probability') || 0;
        const probability = normalizeProbabilityValue(probabilityRaw);
        const stretchedProb = stretchProbability(probability, probabilityStats);
        const color = getMarkerColor(stretchedProb);
        const fillColor = applyAlphaToColor(color, 0.55 + stretchedProb * 0.35);
        
        const radius = MIN_MARKER_RADIUS_PX + (MAX_MARKER_RADIUS_PX - MIN_MARKER_RADIUS_PX) * stretchedProb;
        // Show labels for top ranked cells OR any cell with probability >= 70%
        const showLabel = priorityRank <= DEFAULT_LABELED_MARKER_COUNT || probability >= 0.70;

        return new Style({
          image: new CircleStyle({
            radius,
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({
              color: applyAlphaToColor(color, 0.4),
              width: 0.7
            }),
          }),
          text: showLabel
            ? new Text({
                text: `${(probability * 100).toFixed(0)}%`,
                fill: new Fill({ color: '#ffffff' }),
                stroke: new Stroke({ color: '#000000', width: 2 }),
                font: 'bold 10px monospace',
                textAlign: 'center',
                textBaseline: 'middle'
              })
            : undefined
        });
      },
    });

    // Set layer properties
    newResultsLayer.set('name', 'analysis-results');
    newResultsLayer.set('analysisId', effectiveAnalysisId); // Store for visibility control
    // Respect the user toggle unless the caller explicitly wants the fresh
    // result shown (e.g. an analysis the user just ran).
    const shouldShowLayer = options?.autoShow ? true : showAnalysisResults;
    newResultsLayer.setVisible(shouldShowLayer);
        // Add to map
            mapInstanceRef.current.addLayer(newResultsLayer);
        // 🧪 TESTING: Update global reference to ensure it matches current
    if (typeof window !== 'undefined') {
      (window as any).mapInstance = mapInstanceRef.current;
          }
    
    // Set high z-index to ensure layer is on top
    newResultsLayer.setZIndex(1000);
        // Check all layers on map
    const allMapLayers = mapInstanceRef.current.getLayers().getArray();
        allMapLayers.forEach((layer, index) => {
          });
    
    setAnalysisResultsLayer(newResultsLayer);
    // Track which analysis this layer belongs to (for visibility control)
    currentAnalysisLayerIdRef.current = effectiveAnalysisId;
    devLog('[createAnalysisResultsLayer] Set currentAnalysisLayerIdRef:', effectiveAnalysisId);
    // Auto-show keeps ALL results state in sync (legend checkbox reads these):
    // never enable one of activeLayers/showAnalysisResults without the other —
    // that desync is what made the legend show ON for an invisible layer.
    if (options?.autoShow) {
      setActiveLayers(prev => (prev.includes('results') ? prev : [...prev, 'results']));
      setShowAnalysisResults(true);
    }


    // Zoom to show all features (only if haven't zoomed already)
    if (!hasZoomedToMarkersRef.current && vectorSource.getFeatures().length > 0) {
      const extent = vectorSource.getExtent();
            // Validate extent before fitting
      if (extent && extent.length === 4 && 
          isFinite(extent[0]) && isFinite(extent[1]) && 
          isFinite(extent[2]) && isFinite(extent[3]) &&
          extent[0] < extent[2] && extent[1] < extent[3]) {
        mapInstanceRef.current.getView().fit(extent, {
          padding: [100, 100, 100, 100],
          duration: 1000
        });
        hasZoomedToMarkersRef.current = true; // Mark that we've zoomed
        } else {
              }
    } else if (hasZoomedToMarkersRef.current) {
          }
  }, [analysisResultsLayer, showAnalysisResults]); // Removed activeLayers to prevent recreation on toggle

  // Create combined analysis results layer for multiple analyses (polygon-specific)
  const createCombinedAnalysisLayer = useCallback((allResults: any[], skipZoom: boolean = false) => {
    if (!mapInstanceRef.current) {
      return;
    }

    if (!legacyAnalysisMarkersEnabled) {
      if (analysisResultsLayer && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(analysisResultsLayer);
        setAnalysisResultsLayer(null);
      }
      return;
    }

    const getMarkerColor = (probability: number): string => getProbabilityColor(probability);

    let layer = analysisResultsLayer;
    let vectorSource = (layer?.getSource?.() as VectorSource) || null;

    if (!layer || !vectorSource) {
      vectorSource = new VectorSource();
      layer = new VectorLayer({
        source: vectorSource,
        declutter: true
      });
      layer.set('name', 'analysis-results');
      layer.setZIndex(1000);
      layer.setVisible(showAnalysisResults);
      mapInstanceRef.current.addLayer(layer);
      setAnalysisResultsLayer(layer);
    } else {
      vectorSource.clear();
      layer.setVisible(showAnalysisResults);
    }

    if (!vectorSource) {
      return;
    }

    let totalFeatures = 0;
    const probabilityAccumulator: number[] = [];

    allResults.forEach((result) => {
      const gridPoints =
        result?.enhanced_results?.grid_points ||
        result?.results?.ml_results?.grid_points ||
        result?.results?.grid_points ||
        result?.grid_points;
      const topTargets = result?.results?.top_targets;
      const highPotentialAreas = result?.highPotentialAreas;
      const mlCoords =
        result?.results?.ml_results?.grid_coordinates ||
        result?.results?.grid_coordinates ||
        result?.grid_coordinates;
      const mlProbs =
        result?.results?.ml_results?.grid_probabilities ||
        result?.results?.grid_probabilities ||
        result?.grid_probabilities;

      let normalizedPairs: any[] = [];
      if (Array.isArray(mlCoords) && Array.isArray(mlProbs) && mlCoords.length === mlProbs.length) {
        normalizedPairs = mlCoords.map((coord: any, idx: number) => {
          const rawLat = Array.isArray(coord) ? coord[1] ?? coord[0] : coord?.latitude ?? coord?.lat;
          const rawLon = Array.isArray(coord) ? coord[0] ?? coord[1] : coord?.longitude ?? coord?.lon;
          const prob = mlProbs[idx] ?? coord?.probability ?? 0;
          const lat = Math.abs(rawLat) > 90 && Math.abs(rawLon) <= 90 ? rawLon : rawLat;
          const lon = Math.abs(rawLat) > 90 && Math.abs(rawLon) <= 90 ? rawLat : rawLon;
          return {
            latitude: Number(lat) || 0,
            longitude: Number(lon) || 0,
            probability: Number(prob) || 0,
            score: Number(prob) || 0,
            confidence: coord?.confidence ?? 0,
            areaId: `pair_${idx}`
          };
        });
      }

      const dataPoints =
        (Array.isArray(gridPoints) && gridPoints.length > 0 && gridPoints) ||
        (normalizedPairs.length > 0 && normalizedPairs) ||
        topTargets;
      const cacheKey = [
        result?.id || 'unknown',
        result?.updated_at || result?.date || '',
        dataPoints?.length || 0,
        highPotentialAreas?.length || 0
      ].join(':');

      let cacheEntry = analysisAreaCacheRef.current.get(result.id);
      if (!cacheEntry || cacheEntry.cacheKey !== cacheKey) {
        const areas: Array<{ longitude: number; latitude: number; probability: number; score: number; confidence: number; priorityRank: number; areaId: string }> = [];

        if (dataPoints && Array.isArray(dataPoints) && dataPoints.length > 0) {
          const transformed = dataPoints
            .map((target: any, idx: number) => {
              const probability = normalizeProbabilityValue(target.probability ?? target.score ?? 0);
              const confidence = target.confidence_interval
                ? 1 - (target.confidence_interval[1] - target.confidence_interval[0])
                : 1 - (target.total_uncertainty || 0.5);
              return {
                longitude: target.longitude,
                latitude: target.latitude,
                probability,
                score: probability,
                confidence,
                areaId: target.id || `grid_${idx}`
              };
            })
            .sort((a, b) => b.probability - a.probability)
            .slice(0, MAX_MARKERS_PER_ANALYSIS)
            .map((area, idx) => ({
              ...area,
              priorityRank: idx + 1
            }));
          areas.push(...transformed);
        } else if (highPotentialAreas && Array.isArray(highPotentialAreas) && highPotentialAreas.length > 0) {
          const sorted = [...highPotentialAreas]
            .sort((a: any, b: any) => (b.score || 0) - (a.score || 0))
            .slice(0, MAX_MARKERS_PER_ANALYSIS)
            .map((area: any, index: number) => {
              const score = area?.score ?? 0;
              const probability = normalizeProbabilityValue(area?.probability ?? score);
              return {
                longitude: area.longitude,
                latitude: area.latitude,
                probability,
                score,
                confidence: area?.confidence ?? 0,
                priorityRank: index + 1,
                areaId: area.id || `fallback_${index}`
              };
            });
          areas.push(...sorted);
        } else {
          analysisAreaCacheRef.current.set(result.id, { cacheKey, areas: [] });
          return;
        }

        analysisAreaCacheRef.current.set(result.id, { cacheKey, areas });
        cacheEntry = analysisAreaCacheRef.current.get(result.id);
      }

      const areasToRender = cacheEntry?.areas ?? [];

      areasToRender.forEach((area) => {
        const transformedCoords = fromLonLat([area.longitude, area.latitude]);
        const feature = new Feature({
          geometry: new Point(transformedCoords),
          score: area.score || 0,
          confidence: area.confidence || 0,
          probability: area.probability || 0,
          priorityRank: area.priorityRank,
          analysis_id: result.id,
          polygon_id: result.polygon_id,
          polygon_name: result.polygon_name,
          commodity: result.commodity,
          markerId: `${result.id}_rank_${area.priorityRank}`,
          mineralType: result.commodity || 'unknown',
          area_id: area.areaId
        });

        vectorSource.addFeature(feature);
        totalFeatures += 1;
        probabilityAccumulator.push(normalizeProbabilityValue(area.probability));
      });
    });

    if (totalFeatures === 0) {
      return;
    }

    const probabilityStats = computeProbabilityStats(probabilityAccumulator);

    const markerStyle = (feature: FeatureLike): Style | Style[] => {
      const markerId = feature.get('markerId');
      const mineralType = feature.get('mineralType') || 'unknown';
      const priorityRank = feature.get('priorityRank') || 999;
      const mineralMarkerKey = `markers_${mineralType}`;
      const mineralMarkersVisible = markerVisibility?.get(mineralMarkerKey) ?? true;
      const individualVisible = markerVisibility?.get(markerId) ?? (priorityRank <= DEFAULT_VISIBLE_MARKER_COUNT);

      if (!(mineralMarkersVisible && individualVisible)) {
        return [];
      }

      const rawScore = feature.get('score') || 0;
      const probabilityRaw = feature.get('probability') ?? (rawScore < 1 ? rawScore : rawScore / 100);
      const probability = normalizeProbabilityValue(probabilityRaw);
      const stretchedProb = stretchProbability(probability, probabilityStats);
      const color = getMarkerColor(stretchedProb);
      const fillColor = applyAlphaToColor(color, 0.55 + stretchedProb * 0.35);
      const radius = MIN_MARKER_RADIUS_PX + (MAX_MARKER_RADIUS_PX - MIN_MARKER_RADIUS_PX) * stretchedProb;
      // Show labels for top ranked cells OR any cell with probability >= 70%
      const showLabel = priorityRank <= DEFAULT_LABELED_MARKER_COUNT || probability >= 0.70;

      return new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: applyAlphaToColor(color, 0.4), width: 0.7 })
        }),
        text: showLabel
          ? new Text({
              text: `${(probability * 100).toFixed(0)}%`,
              font: 'bold 10px monospace',
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#000000', width: 2 }),
              textAlign: 'center',
              textBaseline: 'middle'
            })
          : undefined
      });
    };

    if (layer) {
      layer.setStyle(markerStyle);
    }

    setActiveLayers((prev) => {
      if (!prev.includes('results')) {
        return [...prev, 'results'];
      }
      return prev;
    });

    if (!skipZoom && !hasZoomedToMarkersRef.current && vectorSource.getFeatures().length > 0) {
      const extent = vectorSource.getExtent();
      if (
        extent &&
        extent.length === 4 &&
        isFinite(extent[0]) &&
        isFinite(extent[1]) &&
        isFinite(extent[2]) &&
        isFinite(extent[3]) &&
        extent[0] < extent[2] &&
        extent[1] < extent[3]
      ) {
        mapInstanceRef.current.getView().fit(extent, {
          padding: [100, 100, 100, 100],
          duration: 1000
        });
        hasZoomedToMarkersRef.current = true;
      }
    }
  }, [analysisResultsLayer, markerVisibility, showAnalysisResults]);

  const [selectedResultId, setSelectedResultId] = useState<string>('');
  const [useGridVisualization, setUseGridVisualization] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('mineral-ai-use-grid-visualization') === 'true';
  });
  const [initialHeatmapShown, setInitialHeatmapShown] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem('mineral-ai-use-grid-visualization', useGridVisualization ? 'true' : 'false');
  }, [useGridVisualization]);
  
  // Grid visualization settings - use props if provided, otherwise local state
  // Adjusted defaults for better separation - keep very low minimum threshold by default
  const [localGridSettings, setLocalGridSettings] = useState<globalThis.Map<string, GridSettings>>(() => {
    try {
      const saved = localStorage.getItem('mineral-ai-grid-settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        const restored = deserializeGridSettings(parsed);
        if (restored.size > 0) {
          return restored;
        }
      }
    } catch (error) {
      console.warn('MapView: failed to load local grid settings, using defaults', error);
    }

    const defaults = new globalThis.Map<string, GridSettings>();
    ['gold', 'silver', 'copper'].forEach((mineral) => {
      defaults.set(mineral, sanitizeGridSettings(DEFAULT_ANALYSIS_GRID_SETTINGS));
    });
    return defaults;
  });
  
  // Use props if provided, otherwise use local state
  const gridSettingsToUse = gridSettings || localGridSettings;
  const gridSettingsWithDefaults = gridSettingsToUse;

  const updateGridSetting = useCallback(
    (analysisId: string, mineralKey: string, updates: Partial<GridSettings>) => {
      if (onGridSettingsChange) {
        onGridSettingsChange(analysisId, mineralKey, updates);
      }

      setLocalGridSettings((prev) => {
        const next = new globalThis.Map(prev);
        const existing =
          next.get(analysisId) ||
          next.get(mineralKey) ||
          gridSettingsToUse.get(analysisId) ||
          gridSettingsToUse.get(mineralKey) ||
          DEFAULT_ANALYSIS_GRID_SETTINGS;

        const merged = sanitizeGridSettings({ ...existing, ...updates });

        next.set(analysisId, merged);
        next.set(mineralKey, { ...merged });

        try {
          const serializable = serializeGridSettings(next);
          localStorage.setItem('mineral-ai-grid-settings', JSON.stringify(serializable));
        } catch (error) {
          console.warn('MapView: failed to persist local grid settings', error);
        }

        return next;
      });
    },
    [onGridSettingsChange, gridSettingsToUse]
  );
  
  // Keep heatmap mode as default - don't auto-switch
  // User can manually switch between modes if desired
  useEffect(() => {
        // Commented out to preserve smooth heatmap visualization
        // if (gridDataByMineral.size > 0 && visualizationMode === 'heatmap') {
        //     setVisualizationMode('grid');
        // }
  }, [gridDataByMineral.size, visualizationMode]);
  
  const hasZoomedToMarkersRef = useRef<boolean>(false);
  const isTogglingMarkersRef = useRef<boolean>(false);
  const hasInitiallyCenteredRef = useRef<string | null>(null); // Track which project was initially centered
  const [currentZoomLevel, setCurrentZoomLevel] = useState<number>(10);
  const currentZoomLevelRef = useRef<number>(10);

  // Mineral Discovery Handlers
  const handleRunMineralDiscovery = useCallback(async (config: any) => {
    if (onRunMineralDiscovery) {
      try {
        onRunMineralDiscovery(config);
        // Mock result handling - in real implementation this would come from API
        const mockResult = {
          id: `analysis_${Date.now()}`,
          analysisDate: new Date().toISOString(),
          commodity: config.commodity,
          region: config.region,
          configuration: config,
          results: {
            totalLocations: 1250,
            highPotentialLocations: 47,
            averageScore: 32.8,
            maxScore: 87.3,
            averageConfidence: 0.74
          },
          locations: [],
          highPotentialAreas: [],
          ruleContributions: [],
          performanceMetrics: {
            precision: 0.68,
            recall: 0.72,
            f1Score: 0.70,
            accuracy: 0.69,
            validationDate: new Date().toISOString()
          }
        };
        setDiscoveryResults(prev => [...prev, mockResult]);
        setSelectedResultId(mockResult.id);
        setResultsViewerOpen(true);
        
        // Call the parent callback to populate Reports tab
        if (onAnalysisComplete) {
          onAnalysisComplete(mockResult);
        }
      } catch (error) {
        console.error('Failed to run mineral discovery:', error);
      }
    }
  }, [onRunMineralDiscovery]);

  // Dual Analysis Handler
  const handleRunDualAnalysis = useCallback(async (config: any) => {
                    if (!selectedProject || !config.polygon) {
      console.error('❌ Cannot run dual analysis: missing project or polygon');
      console.error('❌ selectedProject:', selectedProject);
      console.error('❌ config.polygon:', config.polygon);
      return;
    }
    
        setIsDualAnalysisRunning(true);
    setShowDualAnalysis(true);
    setDualAnalysisResults(null);
    setDualAnalysisProgress(0);
    setDualAnalysisMessage('Initializing enhanced analysis...');
    setCurrentAnalysisId(null);
    setShowEnhancedAnalysisLog(true);  // Show the log panel

    try {
                  // Use quick test endpoint if test mode is enabled
      const gridResolution = resolveGridResolutionFor(selectedProject?.id, config.polygonId);

                  // Call the enhanced analysis endpoint
            const response = await apiFetch('/api/enhanced-analysis/analyze', {
        method: 'POST',
        body: JSON.stringify({
          polygon_coordinates: config.polygon,
          commodity: config.commodity || 'gold',
          project_id: selectedProject?.id,
          exploration_strategy: 'balanced',
          budget_constraint: 1000000,
          polygon_id: config.polygonId,
          polygon_name: config.polygonName,
          test_mode: config.testMode || false,
          grid_resolution: gridResolution,
          manual_trigger: true,
          analysis_config: {
            grid: {
              default_grid_resolution: gridResolution
            }
          }
        }),
      });

                              if (!response.ok) {
        console.error('❌ Response not ok:', response.status, response.statusText);
        throw new Error(`Enhanced analysis request failed: ${response.status}`);
      }

            const result = await response.json();
            // Enhanced analysis completes immediately
      const analysisId = result.analysis_id || `enhanced_${Date.now()}`;
      setCurrentAnalysisId(analysisId);
      
      // Check if enhanced analysis returns immediate results or needs polling
      if (result.results) {
        // Keep the full results payload (including ml_results grid data) for rendering
        setDualAnalysisResults(result);
        setIsDualAnalysisRunning(false);
        
        // Process results directly without polling
        if (onAnalysisComplete) {
          const completionData = {
            analysis_id: analysisId,
            project_id: selectedProject?.id,
            type: 'dual_analysis',
            results: result.results, // includes ml_results grid_coordinates/grid_probabilities
            highPotentialAreas: result.results?.top_targets || [],
            drillingProgram: result.results?.drilling_program || null,
            portfolioMetrics: result.results?.portfolio_metrics || null
          };
          if (process.env.NODE_ENV === 'development') {
            devInfo('[handleRunDualAnalysis] calling onAnalysisComplete with:', {
              analysis_id: completionData.analysis_id,
              project_id: completionData.project_id,
              has_ml_results: !!completionData.results?.ml_results,
              grid_coordinates_count: completionData.results?.ml_results?.grid_coordinates?.length,
              grid_points_count: completionData.results?.ml_results?.grid_points?.length,
              grid_probabilities_count: completionData.results?.ml_results?.grid_probabilities?.length,
            });
          }
          onAnalysisComplete(completionData);
        }
        
                return;
      }

      // Enhanced analysis now runs in background - enable polling
      if (result.status === 'started' && result.analysis_id) {
                setCurrentAnalysisId(result.analysis_id);
        
        // Start polling for status and results
        // Poll for results with enhanced error handling (for longer analyses)
        let attempts = 0;
        let consecutiveErrors = 0;
        const maxAttempts = 1800; // 30 minutes with 1-second intervals (increased for realistic probability calculations)
        const maxConsecutiveErrors = 5; // Stop after 5 consecutive 404s

        const pollResults = async (): Promise<void> => {
          try {
            const statusResponse = await apiFetch(`/api/enhanced-analysis/analysis/${result.analysis_id}/status`);
          
          if (!statusResponse.ok) {
            if (statusResponse.status === 404) {
              consecutiveErrors++;
              if (consecutiveErrors >= maxConsecutiveErrors) {
                throw new Error(`Analysis ${result.analysis_id} not found after ${maxConsecutiveErrors} attempts. Backend may have restarted.`);
              }
              
              // Use exponential backoff for 404 errors
              const delay = Math.min(1000 * Math.pow(2, consecutiveErrors - 1), 10000); // Cap at 10s
              if (attempts < maxAttempts) {
                attempts++;
                const timeElapsed = Math.floor(attempts / 60);
                setDualAnalysisMessage(`Waiting for analysis to start... (${timeElapsed}m elapsed)`);
                setDualAnalysisProgress(Math.min((attempts / maxAttempts) * 20, 20)); // Up to 20% while waiting
                setTimeout(pollResults, delay);
                return;
              } else {
                throw new Error('Analysis timeout: Backend may have restarted or analysis failed to start');
              }
            } else if (statusResponse.status === 422) {
              // Treat validation errors as a terminal condition for this analysis ID
              console.warn(`Status polling received 422 for ${result.analysis_id}. Stopping poll.`);
              setDualAnalysisMessage('Analysis status unavailable (validation error).');
              return;
            } else {
              throw new Error(`Status request failed: ${statusResponse.status}`);
            }
          }
          
          // Reset error counter on successful response
          consecutiveErrors = 0;
          const statusData = await statusResponse.json();

                    // Update progress based on status
          if (statusData.progress !== undefined) {
            setDualAnalysisProgress(statusData.progress);
            
            // Enhanced progress messages based on progress ranges
            let progressMessage = statusData.message;
            if (!progressMessage) {
              if (statusData.progress < 10) {
                progressMessage = `Initializing analysis... (${statusData.progress}%)`;
              } else if (statusData.progress < 50) {
                progressMessage = `Extracting training data from deposits... (${statusData.progress}%)`;
              } else if (statusData.progress < 80) {
                progressMessage = `Training ML model... (${statusData.progress}%)`;
              } else if (statusData.progress < 95) {
                progressMessage = `Analyzing 625 grid points... (${statusData.progress}%)`;
              } else if (statusData.progress < 98) {
                progressMessage = `Processing grid analysis... (${statusData.progress}%)`;
              } else {
                progressMessage = `Finalizing results... (${statusData.progress}%)`;
              }
            }
            
            setDualAnalysisMessage(progressMessage);
          } else {
            // Fallback progress calculation
            const estimatedProgress = Math.min(20 + (attempts / maxAttempts) * 70, 90);
            setDualAnalysisProgress(estimatedProgress);
            setDualAnalysisMessage(statusData.status === 'running' ? 'Processing analysis (625 grid points)...' : 'Finalizing results...');
          }

          if (statusData.current_status === 'completed' || statusData.status === 'completed') {
            // Handle success when analysis completes
            setShowEnhancedAnalysisLog(false);
            
            // Fetch final results if not included in status response
            const finalResults = statusData.results || await (async () => {
              const resultsResponse = await apiFetch(`/api/enhanced-analysis/analysis/${result.analysis_id}/results`);
              return await resultsResponse.json();
            })();
            
                        setDualAnalysisProgress(100);
            setDualAnalysisMessage('Analysis completed successfully!');
            setDualAnalysisResults(finalResults); // Pass full result object with analysis_id and commodity
            setIsDualAnalysisRunning(false);
                
            // Call analysis complete callback
            if (onAnalysisComplete) {
              onAnalysisComplete({
                analysis_id: result.analysis_id,
                project_id: selectedProject?.id,
                type: 'dual_analysis',
                ...finalResults.results,
                // Add compatible fields for Reports tab
                results: {
                  highPotentialLocations: finalResults.results?.ml_results?.highPotentialLocations || 0,
                  totalLocations: finalResults.results?.ml_results?.totalLocations || 0,
                  averageConfidence: finalResults.results?.ml_results?.averageConfidence || 0
                },
                highPotentialAreas: finalResults.results?.highPotentialAreas || [] // Use actual coordinate data
              });
            }
          } else if (statusData.status === 'failed') {
            throw new Error(statusData.error || 'Dual analysis failed');
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollResults, 1000);
          } else {
            throw new Error('Analysis timeout after 30 minutes - may be processing large area or encountering issues');
          }
        } catch (error) {
          console.error('❌ Error polling dual analysis results:', error);
          setIsDualAnalysisRunning(false);
          setDualAnalysisProgress(0);
          setDualAnalysisMessage('Analysis failed');
          throw error;
        }
      };

        // Start polling
        setDualAnalysisMessage('Starting analysis...');
        setDualAnalysisProgress(5);
        setTimeout(pollResults, 1000);
      } // End of polling if statement

    } catch (error) {
      console.error('❌ Enhanced analysis failed:', error);
      console.error('❌ Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('❌ Error message:', error instanceof Error ? error.message : String(error));
      console.error('❌ Full error object:', error);
      setIsDualAnalysisRunning(false);
      setDualAnalysisProgress(0);
      setDualAnalysisMessage('Analysis failed');
    }
  }, [selectedProject, onAnalysisComplete]);

  const handleLocationClick = useCallback((location: any) => {
    if (mapInstanceRef.current) {
      const view = mapInstanceRef.current.getView();
      view.animate({
        center: [location.longitude, location.latitude],
        zoom: 15,
        duration: 1000
      });
    }
  }, []);

  const handleAreaClick = useCallback((area: any) => {
    if (mapInstanceRef.current) {
      const view = mapInstanceRef.current.getView();
      view.animate({
        center: area.centroid,
        zoom: 12,
        duration: 1000
      });
    }
  }, []);

  const handleExportResults = useCallback((resultId: string, format: string) => {
    if (onExportResults) {
      onExportResults(resultId, format);
    }
  }, [onExportResults]);

  // Function to center map on project coordinates
  // Handle resizing
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.min(800, Math.max(300, startWidth.current + (e.clientX - startX.current)));
      setDiscoveryPanelWidth(newWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Utility function to calculate combined bounds of all visible polygons
  const calculatePolygonsBounds = useCallback((polygons: PolygonRegion[]) => {
    // Filter for visible and valid polygons
    const visiblePolygons = polygons.filter(polygon => polygon.visible && polygon.is_valid);
    
    if (visiblePolygons.length === 0) {
            return null;
    }

    // Calculate combined bounds from all visible polygons
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;

    visiblePolygons.forEach(polygon => {
      const bounds = polygon.metadata.bounds;
      minLat = Math.min(minLat, bounds.min_lat);
      maxLat = Math.max(maxLat, bounds.max_lat);
      minLon = Math.min(minLon, bounds.min_lon);
      maxLon = Math.max(maxLon, bounds.max_lon);
    });

    const combinedBounds = { minLat, maxLat, minLon, maxLon };
    
    // Calculate center point
    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;
    
    // Calculate area span to determine appropriate zoom level
    const latSpan = maxLat - minLat;
    const lonSpan = maxLon - minLon;
    const maxSpan = Math.max(latSpan, lonSpan);
    
    // Smart zoom level calculation based on area span
    // Increased zoom levels by 1-2 for better default view
    let zoomLevel;
    if (maxSpan < 0.01) {        // Very small area (< 1km)
      zoomLevel = 17;            // Was 16
    } else if (maxSpan < 0.05) { // Small area (< 5km)
      zoomLevel = 15;            // Was 14
    } else if (maxSpan < 0.1) {  // Medium area (< 10km)
      zoomLevel = 14;            // Was 12
    } else if (maxSpan < 0.5) {  // Large area (< 50km)
      zoomLevel = 12;            // Was 10
    } else if (maxSpan < 1.0) {  // Very large area (< 100km)
      zoomLevel = 10;            // Was 8
    } else {                     // Huge area (> 100km)
      zoomLevel = 8;             // Was 6
    }

        return {
      bounds: combinedBounds,
      center: { lat: centerLat, lng: centerLon },
      zoomLevel,
      polygonCount: visiblePolygons.length
    };
  }, []);

  // Timers spawned by centerMapOnProject (viewport-verify timeouts + the
  // short-lived project viewport monitor). Cleared whenever a new centering starts or the project
  // switches, so a previous project's monitor can't yank the view back to the
  // old project's coordinates.
  const centerTimersRef = useRef<{ timeouts: number[]; intervals: number[] }>({ timeouts: [], intervals: [] });
  const clearCenterTimers = useCallback(() => {
    centerTimersRef.current.timeouts.forEach((id) => window.clearTimeout(id));
    centerTimersRef.current.intervals.forEach((id) => window.clearInterval(id));
    centerTimersRef.current = { timeouts: [], intervals: [] };
  }, []);
  const markProjectViewportUserInteraction = useCallback(() => {
    const projectId = selectedProjectRef.current?.id;
    if (!projectId || projectViewportUserInteractedRef.current === projectId) {
      return;
    }
    projectViewportUserInteractedRef.current = projectId;
    clearCenterTimers();
  }, [clearCenterTimers]);
  // Set when an actual project switch happens; consumed by the delayed
  // centering call so the recenter stays forced even if the effect re-runs
  // (dep churn during a switch) before the 50ms timer fires.
  const pendingForceCenterRef = useRef(false);

	  // Function to center map on project coordinates
	  const centerMapOnProject = useCallback((project: any, forceCenter: boolean = false) => {
	    if (projectViewportUserInteractedRef.current === project?.id) {
	      hasInitiallyCenteredRef.current = project?.id;
	      return;
	    }
	    // A global-search selection owns the viewport for a short window; project
	    // auto-centering (including the forced polygon-load recenter) must not
	    // stomp the fit-to-result zoom.
	    if (actionSearchFocusUntilRef.current > Date.now()) {
	      hasInitiallyCenteredRef.current = project?.id;
	      return;
	    }
	    if (hasSharedMapViewportForProject(project?.id)) {
	      hasInitiallyCenteredRef.current = project?.id;
	      return;
	    }
	    // Skip centering if we're just toggling markers, user has already zoomed, or we've already centered on this project
	    if (isTogglingMarkersRef.current ||
        (hasZoomedToMarkersRef.current && !forceCenter) ||
        (hasInitiallyCenteredRef.current === project?.id && !forceCenter)) {
            return;
    }
    
    if (!mapInstanceRef.current) {
            return;
    }

    const view = mapInstanceRef.current.getView();
    if (!view) {
            return;
    }

    // A new centering supersedes any verify/monitor timers from the previous one.
    clearCenterTimers();
    const trackTimeout = (id: number) => { centerTimersRef.current.timeouts.push(id); return id; };
    const trackInterval = (id: number) => { centerTimersRef.current.intervals.push(id); return id; };

    // Debug view constraints and properties
        // Debug map container
    const mapSize = mapInstanceRef.current.getSize();
        let centerCoords = null;
    // Use project-specific zoom if defined, otherwise default
    let zoomLevel = PROJECT_DEFAULT_ZOOMS[project?.id] ?? DEFAULT_PROJECT_ZOOM;

    // PRIORITY 1: Check for visible polygon regions first
    const polygonBounds = calculatePolygonsBounds(polygonRegions);
    if (process.env.NODE_ENV === 'development') {
      devInfo('[centerMapOnProject]', {
        projectId: project?.id,
        polygonCount: polygonRegions.length,
        polygonBounds,
        coords: project?.coordinates
      });
    }
    // For country-level projects, use project center and zoom, not polygon bounds
    const isCountryProject = PROJECT_DEFAULT_ZOOMS[project?.id] !== undefined && PROJECT_DEFAULT_ZOOMS[project?.id] <= 6;
    if (polygonBounds && !isCountryProject) {
      centerCoords = polygonBounds.center;
      zoomLevel = polygonBounds.zoomLevel;
    }
    
        // PRIORITY 2: Try to use project boundary center if no polygons available
    if (!centerCoords && project.project_boundary) {
      try {
        const boundaryData = typeof project.project_boundary === 'string' 
          ? JSON.parse(project.project_boundary) 
          : project.project_boundary;

        if (boundaryData?.features?.[0]?.geometry?.coordinates) {
          const coords = boundaryData.features[0].geometry.coordinates[0];
          let sumLat = 0, sumLng = 0, count = 0;
          
          // Calculate boundary center
          coords.forEach((coord: number[]) => {
            if (coord.length >= 2) {
              sumLng += coord[0];
              sumLat += coord[1];
              count++;
            }
          });
          
          if (count > 0) {
            centerCoords = {
              lng: sumLng / count,
              lat: sumLat / count
            };
            // Only override zoom for non-country projects
            if (!isCountryProject) {
              zoomLevel = Math.max(DEFAULT_PROJECT_ZOOM, 12); // Zoom in more for boundary areas
            }
          }
        }
      } catch (error) {
        console.error('Failed to calculate boundary center:', error);
      }
    }
    
    // PRIORITY 3: Fallback to project coordinates if no polygons or boundary
    if (!centerCoords && project.coordinates) {
      centerCoords = {
        lng: project.coordinates.lng || project.coordinates.lon || -112.074,
        lat: project.coordinates.lat || project.coordinates.latitude || 37.628
      };
          }
    
    // PRIORITY 4: Final fallback
    if (!centerCoords) {
      centerCoords = { lng: -112.074, lat: 37.628 };
          }
    
        // Debug current view state before animation
    const currentCenter = view.getCenter();
    const currentZoom = view.getZoom();
            // Convert current center back to lat/lng to see where we actually are
    if (currentCenter) {
      const currentLonLat = transform(currentCenter, 'EPSG:3857', 'EPSG:4326');
      }
    
    const targetCenter = fromLonLat([centerCoords.lng, centerCoords.lat]);
    const targetZoom = Math.min(zoomLevel, MAX_AUTO_ZOOM);
    const viewportNeedsProjectReset = (candidateView: any) => {
      if (projectViewportUserInteractedRef.current === project?.id) {
        return false;
      }
      const candidateCenter = candidateView?.getCenter?.();
      const candidateZoom = candidateView?.getZoom?.();
      if (!candidateCenter) {
        return true;
      }
      const centerDistance = Math.sqrt(
        Math.pow(candidateCenter[0] - targetCenter[0], 2) +
        Math.pow(candidateCenter[1] - targetCenter[1], 2)
      );
      // A late vector-layer fit can retain roughly the same country center while
      // zooming all the way out. Treat a materially wider view as a reset too,
      // while preserving any deliberate or data-driven zoom that is closer in.
      const zoomIsTooWide = !Number.isFinite(candidateZoom) || candidateZoom < targetZoom - 0.5;
      return centerDistance > 10000 || zoomIsTooWide;
    };
    // DISABLED Nuclear option to preserve mineral layers
            try {
      // Instead of recreating view, just update center and zoom
      const currentView = mapInstanceRef.current.getView();
      currentView.setCenter(targetCenter);
      currentView.setZoom(targetZoom);
      
      // Mark that we've centered on this project
      hasInitiallyCenteredRef.current = project?.id;
            // Force immediate render and position verification
      trackTimeout(window.setTimeout(() => {
        mapInstanceRef.current?.updateSize();
        mapInstanceRef.current?.renderSync();
        
        // Verify the position stuck
        const verifyView = mapInstanceRef.current?.getView();
        if (viewportNeedsProjectReset(verifyView)) {
          verifyView?.setCenter(targetCenter);
          verifyView?.setZoom(targetZoom);
          mapInstanceRef.current?.renderSync();
        }
              }, 10));

    } catch (error) {
      console.error('❌ Failed to recreate view:', error);
      
      // Fallback to original approach
      view.cancelAnimations();
      view.setCenter(targetCenter);
      view.setZoom(targetZoom);
      mapInstanceRef.current?.render();
    }
    
    // Verify the change took effect
    trackTimeout(window.setTimeout(() => {
      const currentView = mapInstanceRef.current?.getView();
      const finalCenter = currentView?.getCenter();
      const finalZoom = currentView?.getZoom();
                  if (finalCenter) {
        const distance = Math.sqrt(
          Math.pow(finalCenter[0] - targetCenter[0], 2) + 
          Math.pow(finalCenter[1] - targetCenter[1], 2)
        );
                        // Force map to refresh and load tiles
        mapInstanceRef.current?.updateSize();
        mapInstanceRef.current?.renderSync();
                // Check all layers
        const layers = mapInstanceRef.current?.getLayers();
        layers?.forEach((layer: any, index: number) => {
          });
        
        // Monitor if position gets overridden by other effects
        const monitorInterval = trackInterval(window.setInterval(() => {
          // Search selections intentionally own the viewport.
          if (actionSearchFocusUntilRef.current > Date.now()) {
            clearInterval(monitorInterval);
            return;
          }
          const monitorView = mapInstanceRef.current?.getView();
          if (viewportNeedsProjectReset(monitorView)) {
            // Reassert both parts of the project viewport. Keep monitoring:
            // multiple async layers can finish and fit in separate waves.
            monitorView?.setCenter(targetCenter);
            monitorView?.setZoom(targetZoom);
            mapInstanceRef.current?.renderSync();
          }
        }, 100));

        // Cover the normal async layer-hydration window, then release the view
        // completely to the operator.
        trackTimeout(window.setTimeout(() => {
          clearInterval(monitorInterval);
                  }, 12000));
      }
    }, 100));

    // Check if animation actually started
    trackTimeout(window.setTimeout(() => {
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();
                  if (newCenter && currentCenter) {
        const moved = Math.abs(newCenter[0] - currentCenter[0]) > 1000 || Math.abs(newCenter[1] - currentCenter[1]) > 1000;
              }
    }, 100));

      }, [polygonRegions, calculatePolygonsBounds, clearCenterTimers]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;

    // Create popup overlay
    const popupOverlay = new Overlay({
      element: popupRef.current!,
      positioning: 'bottom-center',
      stopEvent: true, // Prevent clicks on popup from reaching the map
      offset: [0, -10],
      // Keep the popup within view by panning when needed
      // We will handle keeping it in view by adjusting positioning/offset,
      // not by panning the map.
    });
    overlayRef.current = popupOverlay;

    // Helper: compute and apply a positioning/offset that keeps popup in view
    const adjustOverlayPlacement = () => {
      const el = popupRef.current;
      const position = popupOverlay.getPosition();
      if (!el || !position) return;
      const mapSize = map.getSize();
      if (!mapSize) return;

      const [mapW, mapH] = mapSize;
      const pixel = map.getPixelFromCoordinate(position);
      if (!pixel) return;
      const [x, y] = pixel;

      const margin = 16;
      const w = el.offsetWidth || 320;
      const h = el.offsetHeight || 240;

      const computeRect = (pos: any, ox: number, oy: number) => {
        let left = 0, top = 0, right = 0, bottom = 0;
        switch (pos) {
          case 'bottom-center':
            left = x + ox - w / 2; right = left + w; top = y + oy - h; bottom = y + oy; break;
          case 'top-center':
            left = x + ox - w / 2; right = left + w; top = y + oy; bottom = top + h; break;
          case 'bottom-left':
            left = x + ox; right = left + w; top = y + oy - h; bottom = y + oy; break;
          case 'bottom-right':
            left = x + ox - w; right = x + ox; top = y + oy - h; bottom = y + oy; break;
          case 'top-left':
            left = x + ox; right = left + w; top = y + oy; bottom = top + h; break;
          case 'top-right':
            left = x + ox - w; right = x + ox; top = y + oy; bottom = top + h; break;
          default:
            left = x + ox - w / 2; right = left + w; top = y + oy - h; bottom = y + oy; break;
        }
        return { left, right, top, bottom };
      };

      const tryApply = (pos: any, ox: number, oy: number) => {
        const rect = computeRect(pos, ox, oy);
        const fits = rect.left >= margin && rect.right <= (mapW - margin) && rect.top >= margin && rect.bottom <= (mapH - margin);
        if (fits) {
          popupOverlay.setPositioning(pos);
          popupOverlay.setOffset([ox, oy]);
          return true;
        }
        return false;
      };

      // Preferred order: bottom-center -> top-center -> bottom-left/right -> top-left/right
      const candidates: Array<{ pos: any; ox: number; oy: number }> = [
        { pos: 'bottom-center', ox: 0, oy: -10 },
        { pos: 'top-center', ox: 0, oy: 10 },
        { pos: 'bottom-left', ox: 8, oy: -10 },
        { pos: 'bottom-right', ox: -8, oy: -10 },
        { pos: 'top-left', ox: 8, oy: 10 },
        { pos: 'top-right', ox: -8, oy: 10 }
      ];

      for (const c of candidates) {
        if (tryApply(c.pos, c.ox, c.oy)) return;
      }

      // As a last resort, clamp horizontally using offset
      // Keep vertical as top-center or bottom-center depending on space
      const spaceBelow = mapH - y; const spaceAbove = y;
      const verticalPos = spaceBelow >= h + margin ? 'bottom-center' : 'top-center';
      let ox = 0; let oy = verticalPos === 'bottom-center' ? -10 : 10;
      // Clamp X so the rect stays within margins
      const half = w / 2; let left = x - half; let right = x + half;
      if (left < margin) ox += margin - left; else if (right > mapW - margin) ox -= (right - (mapW - margin));
      popupOverlay.setPositioning(verticalPos as any);
      popupOverlay.setOffset([ox, oy]);
    };

    // Expose via ref for handlers/timers
    adjustOverlayPlacementRef.current = adjustOverlayPlacement;

    // Note: we'll attach listeners after map is created

    // Base layers
    const osmLayer = new TileLayer({
      source: new OSM(),
      visible: false,
      properties: { name: 'osm' }
    });

    // Satellite layer - using ESRI World Imagery (free, no API key needed)
    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions: 'Tiles © Esri'
      }),
      visible: true,
      properties: { name: 'satellite' }
    });

    // Terrain/Topographic layer
    const terrainLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
        attributions: 'Tiles © Esri'
      }),
      visible: false,
      properties: { name: 'terrain' }
    });

    // USGS Geological layer (when available)
    const geologicalLayer = new TileLayer({
      source: new XYZ({
        url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
        attributions: 'USGS'
      }),
      visible: false,
      properties: { name: 'geological' }
    });

    // Vector layer for analysis points
    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      properties: { name: 'analysis' },
      style: (feature) => {
        const probability = feature.get('probability') || 0.5;
        const mineralType = feature.get('mineral_type') || 'copper';
        
        // Color based on probability
        const color = probability > 0.7 ? 'var(--palantir-status-success)' :
                     probability > 0.5 ? 'var(--palantir-status-warning)' :
                     probability > 0.3 ? 'var(--palantir-accent-amber)' :
                     'var(--palantir-accent-cyan)';
        
        return new Style({
          image: new CircleStyle({
            radius: 5 + (probability * 15),
            fill: new Fill({ color: color + '80' }),
            stroke: new Stroke({ color: '#FFFFFF', width: 2 })
          }),
          text: new Text({
            text: `${(probability * 100).toFixed(0)}%`,
            fill: new Fill({ color: '#FFFFFF' }),
            stroke: new Stroke({ color: '#000000', width: 3 }),
            offsetY: -20,
            font: 'bold 10px monospace'
          })
        });
      }
    });

    // Create map
    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, satelliteLayer, terrainLayer, geologicalLayer, vectorLayer],
      overlays: [popupOverlay],
      controls: defaultControls({ zoom: false, attribution: false }),
      interactions: defaultInteractions({ doubleClickZoom: true }),
      view: new View({
        center: selectedProject
          ? fromLonLat([selectedProject.coordinates?.lng || 0, selectedProject.coordinates?.lat || 0])
          : fromLonLat([0, 0]), // World center if no project
        zoom: selectedProject ? (PROJECT_DEFAULT_ZOOMS[selectedProject.id] ?? DEFAULT_PROJECT_ZOOM) : 2
      })
    });
    const mapViewport = map.getViewport();
    const handleProjectViewportUserInteraction = (event: any) => {
      const target = event?.originalEvent?.target || event?.target;
      if (
        target instanceof Element &&
        target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"]')
      ) {
        return;
      }
      markProjectViewportUserInteraction();
    };
    map.on('pointerdrag', handleProjectViewportUserInteraction);
    mapViewport.addEventListener('wheel', handleProjectViewportUserInteraction, { passive: true });

    // Load Natura 2000 clip (local) if requested
    const loadNatura = async () => {
      try {
        const resp = await fetch(NATURA_LOCAL_URL);
        devLog('Natura fetch status', resp.status, 'url', NATURA_LOCAL_URL);
        if (!resp.ok) {
          console.warn('Natura 2000 fetch failed', resp.status);
          return;
        }
        const data = await resp.json();
        const format = new GeoJSON();
        const features = format.readFeatures(data, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:4326',
        });
        const layer = new VectorLayer({
          source: new VectorSource({ features }),
          visible: false,
          zIndex: 1100,
          className: 'natura2000-layer',
          style: new Style({
            stroke: new Stroke({ color: '#8e44ad', width: 1.5 }),
            fill: new Fill({ color: 'rgba(142,68,173,0.15)' }),
          }),
        });
        map.addLayer(layer);
        setNaturaLayer(layer);
        // Apply initial visibility from state
        layer.setVisible(naturaVisible);
        devLog('Natura 2000 features loaded:', features.length);
      } catch (err) {
        console.warn('Failed to load Natura 2000 GeoJSON', err);
      }
    };
    loadNatura();

    let overlayFrame: number | null = null;
    const scheduleOverlayAdjustment = () => {
      if (!adjustOverlayPlacementRef.current) {
        return;
      }
      if (typeof window === 'undefined') {
        adjustOverlayPlacementRef.current();
        return;
      }
      if (overlayFrame !== null) {
        return;
      }
      overlayFrame = window.requestAnimationFrame(() => {
        overlayFrame = null;
        adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
      });
    };

    const onMoveEnd = () => scheduleOverlayAdjustment();
    const onZoom = () => scheduleOverlayAdjustment();
    const onResize = () => scheduleOverlayAdjustment();

    map.on('moveend', onMoveEnd);
    map.getView().on('change:resolution', onZoom);
    map.on('change:size', onResize);

    const handleScoreClick = (evt: any) => {
      const map = mapInstanceRef.current;
      const scoreLayer = scoreLayerRef.current;
      if (!map || !scoreLayer) {
        setActiveScoreCell(null);
        return;
      }
      const source = scoreLayer.getSource();
      if (!source) {
        setActiveScoreCell(null);
        return;
      }
      const feature = source.getClosestFeatureToCoordinate(evt.coordinate);
      const geom = feature && feature.getGeometry && feature.getGeometry();
      if (!feature || !geom || !(geom instanceof Point)) {
        setActiveScoreCell(null);
        return;
      }
      const coords = (geom as Point).getCoordinates();
      const featurePixel = map.getPixelFromCoordinate(coords as any);
      const dx = featurePixel[0] - evt.pixel[0];
      const dy = featurePixel[1] - evt.pixel[1];
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 20) {
        setActiveScoreCell(null);
        return;
      }
      const [lon, lat] = toLonLat(coords);
      setActiveScoreCell({
        lon,
        lat,
        score: feature.get('score'),
        occ: feature.get('occ'),
        bh: feature.get('bh'),
        interval_bonus: feature.get('interval_bonus'),
        intervals: feature.get('sgu_intervals') || []
      });
    };

    map.on('singleclick', handleScoreClick);

    // 🧪 TESTING: Expose map instance globally for Playwright tests and debugging
    if (typeof window !== 'undefined') {
      (window as any).mapInstance = map;
            // Add debugging helper function
      (window as any).debugMapLayers = () => {
        // Always use the current map instance reference
        const currentMap = (window as any).mapInstance;
        if (!currentMap) {
          console.error('❌ DEBUG: No map instance available');
          return { error: 'No map instance' };
        }
        
        const layers = currentMap.getLayers().getArray();
                layers.forEach((layer: any, index: number) => {
          const name = layer.get('name') || 'unnamed';
          const visible = layer.getVisible();
          const zIndex = layer.getZIndex();
          const source = layer.getSource();
          const featureCount = source && source.getFeatures ? source.getFeatures().length : 'N/A';
          
                                                  if (name === 'analysis-results' && source && source.getFeatures) {
            const features = source.getFeatures();
                        features.forEach((feature: any, fIndex: number) => {
              const geometry = feature.getGeometry();
              const coords = geometry ? geometry.getCoordinates() : 'No coords';
                          });
          }
        });
        return { totalLayers: layers.length, analysisLayer: layers.find((l: any) => l.get('name') === 'analysis-results') };
      };
      
      // Debug function to force marker creation using real data only
      (window as any).forceCreateMarkers = () => {
                if (analysisResults && analysisResults.length > 0) {
                              // Skip zoom if we already have user-set zoom level
          const shouldSkipZoom = hasZoomedToMarkersRef.current || isTogglingMarkersRef.current;
          createCombinedAnalysisLayer(analysisResults, shouldSkipZoom);
        } else {
                  }
      };
      
      // Debug function to inspect real analysis data
      (window as any).debugAnalysisData = () => {
                                if (analysisResults && analysisResults.length > 0) {
          analysisResults.forEach((result, index) => {
                      });
        }
      };
      
      }

    // Store map instance for use in separate click handler useEffect
    mapInstanceRef.current = map;
    setMapReadyVersion((version) => version + 1);

    const highlightSource = new VectorSource();
    const highlightLayer = new VectorLayer({
      source: highlightSource,
      style: (feature: FeatureLike) => {
        const highlightType = feature.get('highlightType') as InsightTabKey | undefined;
        const styleConfig = (highlightType && HIGHLIGHT_STYLE_MAP[highlightType]) || HIGHLIGHT_STYLE_MAP.heatmap;
        return new Style({
          image: new CircleStyle({
            radius: styleConfig.radius,
            fill: new Fill({ color: styleConfig.fill }),
            stroke: new Stroke({ color: styleConfig.stroke, width: 3 }),
          }),
        });
      },
      properties: { name: 'analysis-highlight' }
    });
    highlightLayer.setZIndex(1500);
    map.addLayer(highlightLayer);
    highlightLayerRef.current = highlightLayer;
    highlightSourceRef.current = highlightSource;

    // Track zoom level changes for grid visualization
    const view = map.getView();
    currentZoomLevelRef.current = view.getZoom() || 10;
    setCurrentZoomLevel(currentZoomLevelRef.current);
    
    view.on('change:resolution', () => {
      const newZoom = view.getZoom();
      if (newZoom !== undefined) {
        currentZoomLevelRef.current = newZoom;
        setCurrentZoomLevel(newZoom);
        projectGeoJsonLayerRefs.current.forEach((layer) => {
          updateClusterDistanceForZoom(layer, newZoom);
          layer.changed();
        });
        sguVectorLayersRef.current.forEach((layer) => {
          updateClusterDistanceForZoom(layer, newZoom);
          layer.changed();
        });
        [
          finlandDrillClustersLayerRef.current,
          swedenDrillClustersLayerRef.current,
          norwayDrillClustersLayerRef.current,
          irelandDrillClustersLayerRef.current,
          peruDrillClustersLayerRef.current,
          ontarioDrillClustersLayerRef.current,
          australiaDrillClustersLayerRef.current,
          quebecDrillClustersLayerRef.current,
          franceDrillClustersLayerRef.current,
          colombiaDrillClustersLayerRef.current,
        ].forEach((layer) => {
          if (layer) {
            updateClusterDistanceForZoom(layer, newZoom);
            layer.changed();
          }
        });
        finlandTillGoldLayerRef.current?.changed();
        finlandTillBasemetalsLayerRef.current?.changed();
        geochemLayerRef.current?.changed();
        // Mark that user has manually set zoom
        if (!isTogglingMarkersRef.current) {
          hasZoomedToMarkersRef.current = true;
                  }
      }
    });

    // Handle map load
    map.once('rendercomplete', () => {
      setIsLoading(false);
    });
    
    // 🧪 TESTING: Keep global reference in sync with current map instance
    if (typeof window !== 'undefined') {
      (window as any).mapInstance = map;
          }

    // Comprehensive cleanup
    return () => {
      try {
        map.un('moveend', onMoveEnd);
        map.un('pointerdrag', handleProjectViewportUserInteraction);
        map.getView().un('change:resolution', onZoom);
        map.un('change:size', onResize);
        map.un('singleclick', handleScoreClick);
        mapViewport.removeEventListener('wheel', handleProjectViewportUserInteraction);
      } catch {}
      if (typeof window !== 'undefined' && overlayFrame !== null) {
        window.cancelAnimationFrame(overlayFrame);
      }
            try {
        // Stop any active rendering
        if (map && typeof map.renderSync === 'function') {
          map.renderSync();
        }
        
        // Remove all layers to prevent memory leaks
        if (map && typeof map.getLayers === 'function') {
          const layers = map.getLayers().getArray();
          layers.forEach((layer: any) => {
            if (layer && typeof layer.getSource === 'function') {
              const source = layer.getSource();
              if (source && typeof source.clear === 'function') {
                source.clear();
              }
            }
          });
          map.getLayers().clear();
        }
        
        // Clear all interactions
        if (map && typeof map.getInteractions === 'function') {
          map.getInteractions().clear();
        }
        
        // Clear all overlays
        if (map && typeof map.getOverlays === 'function') {
          map.getOverlays().clear();
        }
        
        // Unset target to stop rendering
        if (map && typeof map.setTarget === 'function') {
          map.setTarget(undefined);
        }
        
        // Dispose of the map completely
        if (map && typeof map.dispose === 'function') {
          map.dispose();
        }

        highlightSourceRef.current = null;
        highlightLayerRef.current = null;

        // Clear global references
        if (typeof window !== 'undefined') {
          (window as any).mapInstance = null;
                  }
        
        // Clear local ref
        if (mapInstanceRef.current) {
          mapInstanceRef.current = null;
        }
        
              } catch (error) {
        console.error('❌ MapView cleanup error:', error);
        
        // Emergency cleanup - force clear references even if other cleanup fails
        if (mapInstanceRef.current) {
          mapInstanceRef.current = null;
        }
        if (typeof window !== 'undefined') {
          (window as any).mapInstance = null;
        }
      }
    };
  }, [markProjectViewportUserInteraction]);

  // Setup click handler with current polygonRegions
  useEffect(() => {
    const map = mapInstanceRef.current;
    const popupOverlay = overlayRef.current;
    
    if (!map || !popupOverlay) {
            return;
    }

        // Remove any existing click handlers first
    const existingListeners = map.getListeners('singleclick');
    if (existingListeners) {
      existingListeners.forEach((listener: any) => {
        map.un('singleclick', listener);
      });
    }

    // Define the click handler
    const handleMapClick = (evt: any) => {
      const coordinate = evt.coordinate;
      const lonLat = transform(coordinate, 'EPSG:3857', 'EPSG:4326');
      const inspectionHit = map.forEachFeatureAtPixel(
        evt.pixel,
        (feature: any, layer: any) => {
          if (
            feature?.get?.('__geomorphicSpiderLine') ||
            layer?.get?.('name') === 'selected-country-outline'
          ) {
            return undefined;
          }
          return { feature, layer };
        },
        { hitTolerance: 6 }
      );
      const spiderHitFeature = inspectionHit?.feature;
      const spiderMember = spiderHitFeature?.get?.('__geomorphicSpiderMember');
      if (spiderMember && spiderHitFeature) {
        const originalCoordinate = spiderHitFeature.get('__geomorphicSpiderOriginalCoordinate');
        const memberLonLat = Array.isArray(originalCoordinate)
          ? toLonLat(originalCoordinate)
          : lonLat;
        const memberRecord = buildInspectionRecord(
          spiderMember,
          inspectionHit.layer,
          memberLonLat as [number, number]
        );
        const memberTitle =
          formatInspectionValue(spiderMember.get?.('location_name')) ||
          formatInspectionValue(spiderMember.get?.('name')) ||
          formatInspectionValue(spiderMember.get?.('target_id')) ||
          memberRecord.title;
        setInspectionRecord({ ...memberRecord, title: memberTitle });
        return;
      }
      if (clusterSpiderLayerRef.current) {
        clearClusterSpiderLayer();
      }
      if (inspectionHit?.feature) {
        const clusterMembers = getClusterMemberFeatures(inspectionHit.feature);
        if (clusterMembers.length > 1) {
          const memberCoordinates = clusterMembers
            .map((member) => {
              const geometry = member.getGeometry?.();
              if (geometry instanceof Point) {
                return geometry.getCoordinates();
              }
              if (geometry?.getType?.() === 'MultiPoint') {
                const coordinates = geometry.getCoordinates?.();
                return Array.isArray(coordinates) && coordinates[0] ? coordinates[0] : null;
              }
              return null;
            })
            .filter((candidate): candidate is number[] =>
              Array.isArray(candidate) &&
              candidate.length >= 2 &&
              candidate.every((value) => Number.isFinite(value))
            );
          const extent = clusterMembers.reduce<[number, number, number, number] | null>((current, member) => {
            return mergeExtents(current, member.getGeometry?.()?.getExtent?.() as [number, number, number, number] | undefined);
          }, null);
          const distinctCoordinates = new Set(
            memberCoordinates.map((candidate) => `${candidate[0].toFixed(3)}:${candidate[1].toFixed(3)}`)
          );
          const coLocated = memberCoordinates.length > 1 && distinctCoordinates.size === 1;
          const layerName = formatInspectionValue(inspectionHit.layer?.get?.('geomorphicLayerLabel')) ||
            formatInspectionValue(inspectionHit.layer?.get?.('name')) ||
            'Clustered layer';

          if (coLocated) {
            const center = inspectionHit.feature.getGeometry?.()?.getCoordinates?.() || memberCoordinates[0];
            const resolution = map.getView().getResolution() || 1;
            const visibleMembers = clusterMembers.slice(0, 24);
            const spiderSource = new VectorSource();
            const layerColorValue = inspectionHit.layer?.get?.('geomorphicLayerColor');
            const layerColor = typeof layerColorValue === 'string'
              ? layerColorValue
              : getDominantClusterColor(clusterMembers, '#c084fc');
            const sourceLabel =
              formatInspectionValue(inspectionHit.layer?.get?.('geomorphicLayerSource')) ||
              formatInspectionValue(clusterMembers[0]?.get?.('__geomorphicLayerSource')) ||
              'Co-located source records';

            visibleMembers.forEach((member, index) => {
              const ring = Math.floor(index / 8);
              const ringStart = ring * 8;
              const ringCount = Math.min(8, visibleMembers.length - ringStart);
              const ringIndex = index - ringStart;
              const angle = -Math.PI / 2 + (Math.PI * 2 * ringIndex) / ringCount;
              const radiusPixels = 38 + ring * 28;
              const displayCoordinate = [
                center[0] + Math.cos(angle) * radiusPixels * resolution,
                center[1] + Math.sin(angle) * radiusPixels * resolution,
              ];
              const connector = new Feature(new LineString([center, displayCoordinate]));
              connector.set('__geomorphicSpiderLine', true, true);
              spiderSource.addFeature(connector);

              const displayFeature = member.clone();
              displayFeature.setGeometry(new Point(displayCoordinate));
              displayFeature.set('__geomorphicSpiderMember', member, true);
              displayFeature.set('__geomorphicSpiderOriginalCoordinate', memberCoordinates[index], true);
              displayFeature.set('__geomorphicSpiderIndex', index + 1, true);
              spiderSource.addFeature(displayFeature);
            });

            const spiderLayer = new VectorLayer({
              source: spiderSource,
              properties: {
                name: 'cluster-spider',
                geomorphicLayerLabel: layerName,
                geomorphicLayerSource: sourceLabel,
                geomorphicLayerColor: layerColor,
              },
              style: (feature: any) => {
                if (feature.get('__geomorphicSpiderLine')) {
                  return [
                    new Style({
                      stroke: new Stroke({ color: 'rgba(2, 6, 23, 0.88)', width: 3.2 }),
                      zIndex: 1398,
                    }),
                    new Style({
                      stroke: new Stroke({ color: layerColor, width: 1.4, lineDash: [4, 3] }),
                      zIndex: 1399,
                    }),
                  ];
                }
                const markerIndex = String(feature.get('__geomorphicSpiderIndex') || '');
                return [
                  new Style({
                    image: new CircleStyle({
                      radius: 11,
                      fill: new Fill({ color: 'rgba(2, 6, 23, 0.90)' }),
                      stroke: new Stroke({ color: '#f8fafc', width: 1.2 }),
                    }),
                    zIndex: 1400,
                  }),
                  new Style({
                    image: new CircleStyle({
                      radius: 8,
                      fill: new Fill({
                        color: layerColor.startsWith('#') ? hexToRgba(layerColor, 0.92) : layerColor,
                      }),
                    }),
                    text: new Text({
                      text: markerIndex,
                      font: `800 9px ${MAP_UI_MONO_FONT}`,
                      fill: new Fill({ color: '#f8fafc' }),
                      stroke: new Stroke({ color: '#020617', width: 2.4 }),
                    }),
                    zIndex: 1401,
                  }),
                ];
              },
              zIndex: 1400,
            });
            clusterSpiderLayerRef.current = spiderLayer;
            map.addLayer(spiderLayer);
            map.renderSync();

            const memberRows = visibleMembers.slice(0, 12).map((member, index) => ({
              label: `Feature ${index + 1}`,
              value:
                formatInspectionValue(member.get?.('location_name')) ||
                formatInspectionValue(member.get?.('name')) ||
                formatInspectionValue(member.get?.('target_id')) ||
                `Record ${index + 1}`,
            }));
            const sharedLonLat = toLonLat(center);
            setInspectionRecord({
              id: `${layerName}:spider:${clusterMembers.length}:${sharedLonLat[0].toFixed(5)}:${sharedLonLat[1].toFixed(5)}`,
              title: `${clusterMembers.length.toLocaleString()} co-located features`,
              subtitle: layerName,
              coordinates: `${sharedLonLat[1].toFixed(5)}, ${sharedLonLat[0].toFixed(5)}`,
              layerLabel: layerName,
              source: sourceLabel,
              rows: [
                { label: 'Feature count', value: clusterMembers.length.toLocaleString() },
                { label: 'Display', value: `Expanded as ${visibleMembers.length} numbered markers` },
                { label: 'Geometry', value: 'Radial offsets are display-only; source coordinates are unchanged' },
                { label: 'Action', value: 'Click a numbered marker for the underlying record' },
                ...memberRows,
              ],
            });

            const activeSpiderLayer = spiderLayer;
            map.getView().once('change:resolution', () => {
              if (clusterSpiderLayerRef.current === activeSpiderLayer) {
                clearClusterSpiderLayer();
              }
            });
          } else if (extent) {
            map.getView().fit(extent, {
              padding: [110, 420, 110, 110],
              maxZoom: Math.max(9, Math.min(12.5, (map.getView().getZoom() || 8) + 2.5)),
              duration: 260,
            });
            setInspectionRecord({
              id: `${layerName}:cluster:${clusterMembers.length}:${lonLat[0].toFixed(5)}:${lonLat[1].toFixed(5)}`,
              title: `${clusterMembers.length.toLocaleString()} grouped features`,
              subtitle: layerName,
              coordinates: `${lonLat[1].toFixed(5)}, ${lonLat[0].toFixed(5)}`,
              layerLabel: layerName,
              source: 'Cluster summary',
              rows: [
                { label: 'Feature count', value: clusterMembers.length.toLocaleString() },
                { label: 'Action', value: 'Zooming in separates the features' },
              ],
            });
          }
        } else {
          setInspectionRecord(buildInspectionRecord(getRenderableMapFeature(inspectionHit.feature), inspectionHit.layer, lonLat as [number, number]));
        }
      } else {
        setInspectionRecord(null);
      }
      
      // Priority -1: Check if clicked on a Peru Playbook analysis target
      const playbookFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const targetId = feature.get('target_id');
        const classification = feature.get('classification');
        if (targetId && classification) {
          return feature;
        }
        return null;
      });

      if (playbookFeature) {
        const props = playbookFeature.getProperties();
        const classColors: Record<string, string> = {
          HIGH_PRIORITY: '#E63946', MEDIUM: '#FFB703', LOW: '#ADB5BD', CONCEDED: '#555555'
        };
        setPopupType('exploration');
        setSelectedPolygon(null);
        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}S, ${Math.abs(lonLat[0]).toFixed(4)}W`,
          target_id: props.target_id,
          priority_tier: props.classification,
          commodity: props.deposit_type,
          deposit_model: props.deposit_type,
          anomaly_strength: props.priority_score ? `Score: ${props.priority_score}` : undefined,
          best_grade_value: undefined,
          open_ground_pct: props.open_ground ? '100' : '0',
          staking_action: props.open_ground ? 'Stakeable' : 'Conceded',
          notes: [
            props.anomalous_elements ? `Elements: ${props.anomalous_elements}` : null,
            props.belt ? `Belt: ${props.belt}` : null,
            props.nearest_deposit ? `Nearest deposit: ${props.nearest_deposit} (${props.nearest_deposit_km} km)` : null,
            props.has_spectral ? 'Within spectral anomaly' : null,
            props.drill_holes_5km > 0 ? `${props.drill_holes_5km} drill holes within 5km${props.best_drill_au ? ` (best Au: ${props.best_drill_au} ppm)` : ''}` : null,
          ].filter(Boolean).join('\n'),
        });
        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try {
            adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
          } catch {}
        }, 0);
        return;
      }

      // Priority -0.7: Check if clicked on Independent Systems feature
      const systemFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const featureType = feature.get('type');
        if (featureType === 'system_centroid' || featureType === 'member_target') {
          return feature;
        }
        return null;
      });

      if (systemFeature) {
        const props = systemFeature.getProperties();
        const featureType = props.type;
        setPopupType('exploration');
        setSelectedPolygon(null);

        if (featureType === 'system_centroid') {
          // System centroid clicked
          const notes: string[] = [];
          if (props.region) notes.push(`Region: ${props.region}`);
          if (props.deposit_type) notes.push(`Deposit type: ${props.deposit_type}`);
          if (props.au_ppb_max !== undefined) notes.push(`Max Au: ${props.au_ppb_max} ppb`);
          if (props.au_anomalies !== undefined) notes.push(`Au anomalies: ${props.au_anomalies}`);
          if (props.cu_ppm_max !== undefined) notes.push(`Max Cu: ${props.cu_ppm_max} ppm`);
          if (props.zn_ppm_max !== undefined) notes.push(`Max Zn: ${props.zn_ppm_max} ppm`);
          if (props.cadastre_status) notes.push(`Cadastre: ${props.cadastre_status}`);
          if (props.member_ranks) notes.push(`Member ranks: ${props.member_ranks}`);

          setPopupInfo({
            coordinates: `${lonLat[1].toFixed(4)}S, ${Math.abs(lonLat[0]).toFixed(4)}W`,
            target_id: props.system_id || 'Unknown System',
            priority_tier: props.staking_priority || 'NOT_RECOMMENDED',
            commodity: props.deposit_type || 'Unknown',
            deposit_model: props.deposit_type || 'Unknown',
            anomaly_strength: props.priority_rank ? `Priority Rank: ${props.priority_rank}` : undefined,
            best_grade_value: props.au_ppb_max ? `${props.au_ppb_max} ppb Au` : undefined,
            open_ground_pct: undefined,
            staking_action: props.staking_priority === 'IMMEDIATE' ? 'Immediate' : props.staking_priority === 'AFTER_CC' ? 'After CC' : 'Not Recommended',
            notes: notes.join('\n'),
          });
        } else if (featureType === 'member_target') {
          // Member target clicked
          const notes: string[] = [];
          if (props.system_id) notes.push(`System: ${props.system_id}`);
          if (props.rank !== undefined) notes.push(`Target rank: ${props.rank}`);
          if (props.score !== undefined) notes.push(`Score: ${props.score}`);
          if (props.cadastre_status) notes.push(`Cadastre: ${props.cadastre_status}`);
          if (props.cc_overlap !== undefined) notes.push(`CC overlap: ${props.cc_overlap ? 'Yes' : 'No'}`);
          if (props.cc_names) notes.push(`CC names: ${props.cc_names}`);

          setPopupInfo({
            coordinates: `${lonLat[1].toFixed(4)}S, ${Math.abs(lonLat[0]).toFixed(4)}W`,
            target_id: `Member #${props.rank || '?'}`,
            priority_tier: props.cadastre_status || 'Unknown',
            commodity: 'Member Target',
            deposit_model: props.system_id || 'Unknown System',
            anomaly_strength: props.score ? `Score: ${props.score}` : undefined,
            best_grade_value: undefined,
            open_ground_pct: props.cadastre_status === 'OPEN' ? '100' : '0',
            staking_action: props.cadastre_status === 'OPEN' ? 'Stakeable' : 'Conceded',
            notes: notes.join('\n'),
          });
        }

        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try { adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current(); } catch {}
        }, 0);
        return;
      }

      // Priority -0.5: Check if clicked on ANAP, LITOTECA, or Mining Projects feature
      const subsurfaceFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const codigo = feature.get('CODIGO');
        const cdCampo = feature.get('CD_CAMPO') || feature.get('CD_LITOTECA');
        const unidad = feature.get('UNIDAD');
        const proyecto = feature.get('PROYECTO');
        if (codigo || cdCampo || unidad || proyecto) return feature;
        return null;
      });

      if (subsurfaceFeature) {
        const props = subsurfaceFeature.getProperties();
        setPopupType('exploration');
        setSelectedPolygon(null);

        let notes: string[] = [];
        let title = '';
        let commodity = '';

        if (props.CODIGO) {
          // ANAP geochemistry sample
          title = `ANAP: ${props.CODIGO}`;
          commodity = props.dominant_element || 'Multi-element';
          const elements = ['AU_PPM', 'AG_PPM', 'CU_PPM', 'ZN_PPM', 'PB_PPM', 'AS_PPM', 'MO_PPM', 'W_PPM', 'SN_PPM', 'CO_PPM', 'NI_PPM', 'LI_PPM'];
          elements.forEach(elem => {
            const val = props[elem];
            if (val && val > 0) notes.push(`${elem.replace('_', ' ')}: ${val}`);
          });
          if (props.GEOLOGIA) notes.push(`Geology: ${props.GEOLOGIA}`);
          if (props.NOMBRE) notes.push(`Name: ${props.NOMBRE}`);
        } else if (props.PROYECTO && !props.UNIDAD && !props.CD_CAMPO) {
          // BDG rock geochemistry
          title = `BDG: ${props.CODIGO || props.PROYECTO}`;
          commodity = props.CLASIF_ROC || 'Rock Sample';
          const elements = ['AU_PPM', 'AG_PPM', 'CU_PPM', 'ZN_PPM', 'PB_PPM', 'AS_PPM', 'MO_PPM', 'W_PPM', 'SN_PPM', 'CO_PPM', 'NI_PPM', 'LI_PPM'];
          elements.forEach(elem => {
            const val = props[elem];
            if (val && val > 0) notes.push(`${elem.replace('_', ' ')}: ${val}`);
          });
          if (props.CLASIF_ROC) notes.push(`Rock class: ${props.CLASIF_ROC}`);
          if (props.GEOLOGIA) notes.push(`Geology: ${props.GEOLOGIA}`);
          if (props.TIPO_DE_MU) notes.push(`Sample type: ${props.TIPO_DE_MU}`);
        } else if (props.CD_CAMPO || props.CD_LITOTECA) {
          // LITOTECA rock sample
          title = `LITOTECA: ${props.CD_CAMPO || props.CD_LITOTECA}`;
          commodity = props.ELEM_PRINC || props.DEP_TIPO || 'Rock Sample';
          if (props.study_type) notes.push(`Study: ${props.study_type}`);
          if (props.TIPO_MTRA) notes.push(`Sample type: ${props.TIPO_MTRA}`);
          if (props.FORM_GEOL) notes.push(`Formation: ${props.FORM_GEOL}`);
          if (props.DEP_TIPO) notes.push(`Deposit type: ${props.DEP_TIPO}`);
          if (props.ELEM_PRINC) notes.push(`Primary elements: ${props.ELEM_PRINC}`);
          if (props.ELEM_ACOM) notes.push(`Secondary elements: ${props.ELEM_ACOM}`);
          if (props.UNID_MINERA) notes.push(`Mining unit: ${props.UNID_MINERA}`);
          if (props.LINK) notes.push(`PDF: ${props.LINK}`);
        } else if (props.UNIDAD) {
          // Mining project
          title = `Project: ${props.UNIDAD}`;
          commodity = props.ELE_PRINC || 'Unknown';
          if (props.ESTADO) notes.push(`Status: ${props.ESTADO}`);
          if (props.CATEGORIA) notes.push(`Category: ${props.CATEGORIA}`);
          if (props.T_YACIM) notes.push(`Deposit type: ${props.T_YACIM}`);
          if (props.RECURSO) notes.push(`Resource: ${props.RECURSO}`);
          if (props.RESERVA) notes.push(`Reserve: ${props.RESERVA}`);
          if (props.TITULAR) notes.push(`Owner: ${props.TITULAR}`);
          if (props.DEPARTAMENTO) notes.push(`Dept: ${props.DEPARTAMENTO}`);
        }

        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}S, ${Math.abs(lonLat[0]).toFixed(4)}W`,
          target_id: title,
          priority_tier: 'SURFACE_SAMPLE',
          commodity,
          deposit_model: commodity,
          anomaly_strength: undefined,
          best_grade_value: undefined,
          open_ground_pct: undefined,
          staking_action: undefined,
          notes: notes.join('\n'),
        });
        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try { adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current(); } catch {}
        }, 0);
        return;
      }

      // Priority 0: Check if clicked on an exploration target
      const explorationFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const targetId = feature.get('target_id');
        const priorityTier = feature.get('priority_tier');
        if (targetId && priorityTier) {
          return feature;
        }
        return null;
      });

      if (explorationFeature) {
        const props = explorationFeature.getProperties();
        setPopupType('exploration');
        setSelectedPolygon(null);
        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}N, ${lonLat[0].toFixed(4)}E`,
          target_id: props.target_id,
          priority_tier: props.priority_tier,
          commodity: props.commodity,
          deposit_model: props.deposit_model,
          anomaly_strength: props.anomaly_strength,
          best_grade_value: props.best_grade_value,
          best_grade_unit: props.best_grade_unit,
          best_grade_sample_type: props.best_grade_sample_type,
          open_ground_pct: props.open_ground_pct,
          staking_action: props.staking_action,
          claim_size_km2: props.claim_size_km2,
          budget_eur: props.budget_eur,
          host_rocks: props.host_rocks,
          environmental_risk: props.environmental_risk,
          geological_risk: props.geological_risk,
          permitting_risk: props.permitting_risk,
          exploration_maturity: props.exploration_maturity,
          notes: props.notes,
        });
        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try {
            adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
          } catch {}
        }, 0);
        return;
      }

      // Priority 1: Check if clicked on a polygon region
      let clickedPolygon: PolygonRegion | null = null;
      const polygonFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const polygonId = feature.get('polygonId');
                if (polygonId) {
          const foundPolygon = polygonRegions.find((p: PolygonRegion) => p.id === polygonId);
                    clickedPolygon = foundPolygon || null;
          return feature;
        }
        return null;
      });

      if (clickedPolygon && polygonFeature) {
        setSelectedPolygon(clickedPolygon as PolygonRegion);
        setPopupType('polygon');
        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}°N, ${Math.abs(lonLat[0]).toFixed(4)}°W`,
          clickedAt: coordinate
        });
        popupOverlay.setPosition(coordinate);
        // Recalculate placement so the popup stays visible without panning map
        setTimeout(() => {
          try {
            adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
          } catch {}
        }, 0);
        return; // Exit early for polygon clicks
      }

      // Priority 2: Geochemistry sample points
      const geochemFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const overlayId = feature.get('overlayId');
        if (overlayId) {
          return feature;
        }
        return null;
      });

      if (geochemFeature) {
        const overlayId = geochemFeature.get('overlayId');
        const overlayDescriptor = geochemOverlays.find((overlay) => overlay.id === overlayId);
        const values = geochemFeature.get('values') || {};
        const metadata = geochemFeature.get('metadata') || {};
        setPopupType('geochem');
        setSelectedPolygon(null);
        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}°N, ${Math.abs(lonLat[0]).toFixed(4)}°W`,
          overlayName: overlayDescriptor?.name || 'Geochemistry overlay',
          values,
          metadata
        });
        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try {
            adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
          } catch {}
        }, 0);

        if (overlayDescriptor) {
          const descriptorId = overlayDescriptor.id || '';
          if (drillOverlayEntry && descriptorId === drillOverlayEntry.id) {
            const holeIdRaw =
              metadata?.hole_id ||
              metadata?.holeId ||
              geochemFeature.get('hole_id') ||
              geochemFeature.get('HoleID');
            if (typeof holeIdRaw === 'string' && holeIdRaw.trim().length > 0) {
              const normalizedHoleId = holeIdRaw.trim();
              setInsightsTab('drill');
              setExpandedDrillHoleId(normalizedHoleId);
              setActiveDrillHoleId(normalizedHoleId);
              loadDrillIntervals(normalizedHoleId);
              setHighlightPoint({
                lat: lonLat[1],
                lon: lonLat[0],
                type: 'drill',
                payload: { holeId: normalizedHoleId },
              });
            }
          } else if (surfaceOverlayEntry && descriptorId === surfaceOverlayEntry.id) {
            const sampleCodeRaw =
              metadata?.sample_code ||
              metadata?.sampleCode ||
              geochemFeature.get('sample_code') ||
              geochemFeature.get('SampleCode');
            const resolvedSampleId =
              typeof sampleCodeRaw === 'string' && sampleCodeRaw.trim().length > 0
                ? sampleCodeRaw.trim()
                : `${lonLat[1].toFixed(5)}_${lonLat[0].toFixed(5)}`;
            setInsightsTab('surface');
            setExpandedSurfaceSampleId(resolvedSampleId);
            setHighlightPoint({
              lat: lonLat[1],
              lon: lonLat[0],
              type: 'surface',
              payload: { sampleCode: resolvedSampleId },
            });
          }
        }

        return;
      }

      // Priority 2: Check for analysis result features (heatmap points, etc.)
      const analysisFeature = map.forEachFeatureAtPixel(evt.pixel, (feature: any) => {
        const probability = feature.get('probability');
        const mineralType = feature.get('mineral_type');
        if (probability !== undefined || mineralType) {
          return feature;
        }
        return null;
      });

      if (analysisFeature) {
                const probability = analysisFeature.get('probability');
        const mineralType = analysisFeature.get('mineral_type');
        setPopupType('analysis');
        setSelectedPolygon(null);
        setPopupInfo({
          coordinates: `${lonLat[1].toFixed(4)}°N, ${Math.abs(lonLat[0]).toFixed(4)}°W`,
          mineralType: mineralType,
          probability: probability ? `${(probability * 100).toFixed(1)}%` : undefined,
          analysis: 'Analysis result'
        });
        popupOverlay.setPosition(coordinate);
        setTimeout(() => {
          try {
            adjustOverlayPlacementRef.current && adjustOverlayPlacementRef.current();
          } catch {}
        }, 0);
        return; // Exit early for analysis clicks
      }

      // No coordinate popup - only polygon and analysis popups supported
            popupOverlay.setPosition(undefined);
      setPopupInfo(null);
      setPopupType(null);
      setSelectedPolygon(null);
    };

    // singleclick only: binding both `click` and `singleclick` ran the handler
    // twice per click (and `click` also fires on each tap of a double-click
    // zoom), making popups open/revert unpredictably.
    map.on('singleclick', handleMapClick);
        // Cleanup function
    return () => {
      if (map) {
        map.un('singleclick', handleMapClick);
              }
    };
  }, [polygonRegions, geochemOverlays, drillOverlayEntry, surfaceOverlayEntry, loadDrillIntervals, buildInspectionRecord, clearClusterSpiderLayer]); // Dependencies: re-setup when polygonRegions or overlays change

  useEffect(() => {
    if (!popupInfo) {
      return;
    }
    const adjustPlacement = adjustOverlayPlacementRef.current;
    if (typeof adjustPlacement === 'function') {
      setTimeout(() => {
        try {
          adjustPlacement();
        } catch {
          /* noop */
        }
      }, 0);
    }
  }, [popupInfo, popupType]);

  // AUTO-TEST: Disabled to prevent interference with real analysis results
  // The auto-testing was creating fake high potential areas at wrong coordinates
  // and overriding the real analysis results from the backend
  // useEffect(() => {
  //   if (!mapInstanceRef.current) return;
  //   
  //   const timer = setTimeout(() => {
  //       //       //     const testResults = {
  //       id: 'auto_test_analysis',
  //       highPotentialAreas: [
  //         {
  //           id: 'test_area_1',
  //           coordinates: { lat: -23.75, lon: -68.25 },
  //           score: 35.5,
  //           confidence: 0.85,
  //           priorityRank: 1,
  //           area_km2: 5.2,
  //           recommendedAction: 'High priority drilling target'
  //         },
  //         {
  //           id: 'test_area_2', 
  //           coordinates: { lat: -23.76, lon: -68.26 },
  //           score: 28.3,
  //           confidence: 0.72,
  //           priorityRank: 2,
  //           area_km2: 3.8,
  //           recommendedAction: 'Secondary drilling target'
  //         }
  //       ]
  //     };
  //     createAnalysisResultsLayer(testResults);
  //   }, 3000); // Wait 3 seconds for map to fully initialize
  //   
  //   return () => clearTimeout(timer);
  // }, []); // Empty dependency array - runs once

  // Update analysis results
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    const vectorLayer = mapInstanceRef.current.getLayers().getArray()
      .find(layer => layer.get('name') === 'analysis') as VectorLayer<VectorSource>;
    
    if (!vectorLayer) return;
    
    const source = vectorLayer.getSource();
    source?.clear();
    
    if (analysisResults && analysisResults.length > 0) {
      // 🚨 PROJECT ISOLATION DEBUGGING - CHECK ANALYSIS RESULTS IN USEEFFECT
            // CRITICAL: Filter analysisResults by current project before processing
      const projectFilteredResults = selectedProject?.id 
        ? analysisResults.filter(result => result.project_id === selectedProject.id)
        : analysisResults;
      
            const features = projectFilteredResults
        .filter(result => result.coordinates)
        .map(result => {
          const coords = [
            result.coordinates.lng || result.coordinates.longitude,
            result.coordinates.lat || result.coordinates.latitude
          ];
          
          const feature = new Feature({
            geometry: new Point(fromLonLat(coords)),
            probability: result.probability || 0.5,
            mineral_type: result.mineral_type || 'copper',
            analysis_id: result.id
          });
          
          return feature;
        });
      
      source?.addFeatures(features);
      
      // Update heatmap data
      const heatmapPoints: HeatmapDataPoint[] = projectFilteredResults
        .filter(result => result.coordinates)
        .map(result => ({
          lat: result.coordinates.lat || result.coordinates.latitude,
          lng: result.coordinates.lng || result.coordinates.longitude,
          probability: result.probability || 0.5,
          mineral_type: result.mineral_type || 'copper'
        }));
      
      setHeatmapData(heatmapPoints);
    }
  }, [analysisResults, selectedProject?.id]); // Restored analysisResults dependency - setTimeout removed from App.tsx

  // Sync loaded analysis data with cached analysis results from parent to avoid redundant fetches
  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }

    if (!analysisResults || analysisResults.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        devInfo('[loadAnalysisData] No analysisResults, returning early');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      devInfo('[loadAnalysisData] START', {
        analysisResultsCount: analysisResults?.length,
        selectedProjectId: selectedProject?.id,
        firstResult_project_id: analysisResults?.[0]?.project_id,
        firstResult_analysis_id: analysisResults?.[0]?.analysis_id || analysisResults?.[0]?.id,
      });
    }

    const projectResults = analysisResults.filter(
      (result) => result?.project_id === selectedProject.id
    );

    if (projectResults.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        devInfo('[loadAnalysisData] No projectResults after filter', {
          selectedProjectId: selectedProject?.id,
          allProjectIds: analysisResults?.map((r: any) => r?.project_id),
        });
      }
      return;
    }

    const hasGridData = projectResults.some(
      (result) =>
        result?.results?.ml_results?.grid_coordinates?.length ||
        result?.results?.ml_results?.grid_points?.length ||
        result?.results?.rule_based_results?.grid_coordinates?.length ||
        result?.results?.rule_based_results?.grid_points?.length
    );

    if (process.env.NODE_ENV === 'development') {
      devInfo('[loadAnalysisData] hasGridData check', {
        hasGridData,
        projectResultsCount: projectResults.length,
        firstResult_grid_coordinates: projectResults?.[0]?.results?.ml_results?.grid_coordinates?.length,
        firstResult_grid_points: projectResults?.[0]?.results?.ml_results?.grid_points?.length,
      });
    }

    if (!hasGridData) {
      return;
    }

    const existingIds = new Set(loadedAnalysisData.map((item: any) => item?.id));
    const isSameSet =
      projectResults.length === loadedAnalysisData.length &&
      projectResults.every((item: any) => existingIds.has(item?.id));

    if (isSameSet) {
      loadedAnalysisRef.current = `${selectedProject.id}:cached`;
      return;
    }

    setLoadedAnalysisData(projectResults);
    loadedAnalysisRef.current = `${selectedProject.id}:cached`;
  }, [analysisResults, selectedProject?.id, loadedAnalysisData]);

  // Re-create analysis layer when marker visibility changes
  useEffect(() => {
                        // CRITICAL FIX v2: Always use analysisResults when available
    // This bypasses the complex loadedAnalysisData logic that's failing
    let dataToUse = [];
    
    if (analysisResults && analysisResults.length > 0) {
      // Use analysisResults directly - it already has the data we need
      dataToUse = analysisResults;
    } else if (loadedAnalysisData && loadedAnalysisData.length > 0) {
            dataToUse = loadedAnalysisData;
    } else {
            return;
    }
    
    // Set default visibility for top 8 if no markerVisibility state exists
    const hasVisibilityState = markerVisibility && markerVisibility.size > 0;
    if (!hasVisibilityState) {
          }
    
    // FORCE TEST: Always try to create markers regardless of visibility state
                // Use the combined analysis layer system (the one that's actually used for rendering)
                if (dataToUse.length > 0) {
                        // Debug the actual data structure
      const firstAnalysis = dataToUse[0];
                                          // Check if we need to fetch the full results
      if (!firstAnalysis.results?.ml_results?.grid_coordinates) {
                              }
      
      // Reset zoom flag when new analysis data is loaded
      if (dataToUse.some(d => d.id !== analysisResults?.[0]?.id)) {
        hasZoomedToMarkersRef.current = false;
              }
      
            // Always skip zoom when recreating layer due to visibility changes
      createCombinedAnalysisLayer(dataToUse, true);
    } else {
          }
  }, [analysisResults, loadedAnalysisData]); // Removed markerVisibility to prevent infinite loops

  // Handle show/hide analysis results toggle
  useEffect(() => {
    if (mapInstanceRef.current) {
            // Set flag to prevent recentering
      isTogglingMarkersRef.current = true;
      
      // Save current view state before making any changes
      const view = mapInstanceRef.current.getView();
      const savedZoom = view.getZoom();
      const savedCenter = view.getCenter();
      const savedViewportProjectId = hasInitiallyCenteredRef.current;
            // Find all analysis-results layers and update their visibility
      const allLayers = mapInstanceRef.current.getLayers().getArray();
      const analysisLayers = allLayers.filter(layer => layer.get('name') === 'analysis-results');
      
      analysisLayers.forEach((layer, index) => {
        layer.setVisible(showAnalysisResults);
              });
      
      if (analysisLayers.length === 0) {
              }
      
      // Restore the view state after a brief delay to override any zoom changes
      setTimeout(() => {
        if (
          savedViewportProjectId === selectedProjectRef.current?.id &&
          savedZoom &&
          savedCenter
        ) {
          view.setZoom(savedZoom);
          view.setCenter(savedCenter);
                  }
        // Clear the flag after a delay
        setTimeout(() => {
          isTogglingMarkersRef.current = false;
        }, 500);
      }, 50);
    }
  }, [showAnalysisResults]);

  // Populate currentAnalysisLayerIdRef from loaded data if not set
  useEffect(() => {
    if (!currentAnalysisLayerIdRef.current && loadedAnalysisData.length > 0) {
      // Use the last loaded analysis as the current layer's analysis
      const lastAnalysis = loadedAnalysisData[loadedAnalysisData.length - 1];
      const analysisId = lastAnalysis?.analysis_id || lastAnalysis?.id;
      if (analysisId) {
        currentAnalysisLayerIdRef.current = analysisId;
        devLog('[Init] Populated currentAnalysisLayerIdRef from loadedAnalysisData:', analysisId);
      }
    }
  }, [loadedAnalysisData]);

  // Handle marker visibility changes with layer-level visibility control
  useEffect(() => {
    if (mapInstanceRef.current && markerVisibility) {
      // Debug: Log all heatmap visibility keys
      const heatmapKeys = Array.from(markerVisibility.entries()).filter(([k]) => k.startsWith('heatmap_'));
      devLog('[MarkerVisibility useEffect] Triggered', { heatmapKeys, markerVisibilitySize: markerVisibility.size, currentLayerId: currentAnalysisLayerIdRef.current });

      // Set flag to indicate we're toggling markers (not user zoom)
      isTogglingMarkersRef.current = true;

      // Save current view state before any changes
      const view = mapInstanceRef.current.getView();
      const savedZoom = view.getZoom();
      const savedCenter = view.getCenter();
      const savedViewportProjectId = hasInitiallyCenteredRef.current;

      // Get visibility states for each mineral type
      const silverVisible = markerVisibility.get('markers_silver') !== false;
      const goldVisible = markerVisibility.get('markers_gold') !== false;
      const copperVisible = markerVisibility.get('markers_copper') !== false;

      // Get all layers on the map
      const allLayers = mapInstanceRef.current.getLayers().getArray();

      // Find all analysis result layers and control feature visibility
      const analysisLayers = allLayers.filter(layer => layer.get('name') === 'analysis-results');
      devLog('[MarkerVisibility useEffect] Found analysis layers:', analysisLayers.length);

      analysisLayers.forEach((layer) => {
        const vectorLayer = layer as VectorLayer<any>;
        const vectorSource = vectorLayer.getSource();
        // Get analysis ID from layer property, or fallback to ref
        const layerAnalysisId = layer.get('analysisId') || currentAnalysisLayerIdRef.current;
        devLog('[VisibilityCheck] Layer analysisId:', layerAnalysisId, '(from ref:', currentAnalysisLayerIdRef.current, ')');

        if (vectorSource) {
          const features = vectorSource.getFeatures();
          let anyVisible = false;

          // Check layer-level visibility first (covers all features in this layer)
          let layerAnalysisVisible = true;
          if (layerAnalysisId) {
            const layerVisibilityKey = `heatmap_${layerAnalysisId}`;
            const layerVisibilityValue = markerVisibility.get(layerVisibilityKey);
            devLog('[VisibilityCheck] Layer-level check:', { layerAnalysisId, layerVisibilityKey, layerVisibilityValue, featureCount: features.length });
            if (layerVisibilityValue === false) {
              layerAnalysisVisible = false;
              // Hide the entire layer immediately when analysis is hidden
              layer.setVisible(false);
              devLog('[VisibilityCheck] Layer hidden for analysis:', layerAnalysisId);
              return; // Skip feature iteration
            } else {
              // Ensure layer is visible when analysis is shown
              layer.setVisible(true);
              devLog('[VisibilityCheck] Layer shown for analysis:', layerAnalysisId);
            }
          }

          features.forEach((feature: Feature) => {
            const sourceAnalysisId = feature.get('sourceAnalysisId'); // Use the actual analysis ID
            const mineralType = feature.get('mineralType') || feature.get('commodity') || 'unknown';

            // Check per-analysis visibility using heatmap_<analysisId> key
            // First check feature-level ID, then fall back to layer-level ID
            let analysisVisible = layerAnalysisVisible; // Start with layer-level visibility
            if (sourceAnalysisId) {
              const visibilityKey = `heatmap_${sourceAnalysisId}`;
              const visibilityValue = markerVisibility.get(visibilityKey);
              if (visibilityValue === false) {
                analysisVisible = false;
              }
            }

            // Check mineral type visibility
            const mineralVisible =
              (mineralType === 'silver' && silverVisible) ||
              (mineralType === 'gold' && goldVisible) ||
              (mineralType === 'copper' && copperVisible) ||
              (mineralType !== 'silver' && mineralType !== 'gold' && mineralType !== 'copper');

            // Feature is visible if both analysis and mineral are visible
            const featureVisible = analysisVisible && mineralVisible;

            // Set feature style to transparent if hidden
            if (!featureVisible) {
              feature.setStyle(new Style({})); // Empty style = invisible
            } else {
              feature.setStyle(undefined); // Use layer's default style
              anyVisible = true;
            }
          });

          // Only show layer if at least one feature is visible
          layer.setVisible(anyVisible);
        }
      });

      // Restore the saved view state to prevent zoom reset
      setTimeout(() => {
        if (
          savedViewportProjectId === selectedProjectRef.current?.id &&
          savedZoom &&
          savedCenter
        ) {
          view.setZoom(savedZoom);
          view.setCenter(savedCenter);
        }
        // Clear the toggling flag after restoration
        isTogglingMarkersRef.current = false;
      }, 100);
    }
  }, [markerVisibility]);

  // Extract grid data for each mineral type
  const extractGridData = useCallback((allResults: any[]) => {
    const gridDataMap = new globalThis.Map<string, AnalysisGridEntry>();

    const ensureUnit = (value: any): number | null => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
      }
      return value > 1 ? value / 100 : value;
    };

    const normalizeBounds = (bounds: any): BoundsObject | null => {
      if (!bounds) {
        return null;
      }
      if (
        typeof bounds?.min_lat === 'number' &&
        typeof bounds?.max_lat === 'number' &&
        typeof bounds?.min_lon === 'number' &&
        typeof bounds?.max_lon === 'number'
      ) {
        return {
          min_lat: Number(bounds.min_lat),
          max_lat: Number(bounds.max_lat),
          min_lon: Number(bounds.min_lon),
          max_lon: Number(bounds.max_lon)
        };
      }
      if (Array.isArray(bounds) && bounds.length >= 4) {
        return {
          min_lat: Number(bounds[0]),
          min_lon: Number(bounds[1]),
          max_lat: Number(bounds[2]),
          max_lon: Number(bounds[3])
        };
      }
      return null;
    };

    allResults.forEach((result, index) => {
      if (!result) {
        return;
      }

      let gridPoints =
        result?.enhanced_results?.grid_points ??
        result?.results?.ml_results?.grid_points ??
        result?.results?.grid_points ??
        result?.results?.top_targets ??
        result?.top_targets;

      // If only coordinate/probability arrays are provided, synthesize point objects
      if ((!Array.isArray(gridPoints) || gridPoints.length === 0) &&
          Array.isArray(result?.results?.ml_results?.grid_coordinates) &&
          Array.isArray(result?.results?.ml_results?.grid_probabilities) &&
          result.results.ml_results.grid_coordinates.length === result.results.ml_results.grid_probabilities.length) {
        gridPoints = result.results.ml_results.grid_coordinates.map((coord: any, idx: number) => {
          const lat = Array.isArray(coord) ? coord[0] ?? coord[1] : coord?.latitude ?? coord?.lat;
          const lon = Array.isArray(coord) ? coord[1] ?? coord[0] : coord?.longitude ?? coord?.lon;
          const probability = result.results.ml_results.grid_probabilities[idx] ?? 0;
          return {
            latitude: lat,
            longitude: lon,
            probability,
            score: probability
          };
        });
      }

      // Debug logging for SGU heatmap ingestion
      if (process.env.NODE_ENV === 'development') {
        devInfo('[extractGridData] analysisId', result?.analysis_id || result?.id || `analysis_${index}`, {
          project_id: result?.project_id,
          coords_count: result?.results?.ml_results?.grid_coordinates?.length,
          probs_count: result?.results?.ml_results?.grid_probabilities?.length,
          gridPoints_count: Array.isArray(gridPoints) ? gridPoints.length : 0,
          hasTopTargets: Array.isArray(result?.results?.top_targets),
        });
      }

      if (!Array.isArray(gridPoints) || gridPoints.length === 0) {
        return;
      }

      const polygonInfo = result?.polygon_info || result?.results?.polygon_info || {};
      const projectId =
        result?.project_id ||
        result?.projectId ||
        result?.project?.id ||
        result?.results?.project_id ||
        null;
      const projectName =
        result?.project_name ||
        result?.projectName ||
        result?.project?.name ||
        result?.results?.project_name ||
        null;
      const rawPolygonId = (result?.polygon_id || polygonInfo?.polygon_id)?.toString()?.trim();
      const rawPolygonName = (result?.polygon_name || polygonInfo?.polygon_name)?.toString()?.trim();

      const resolvedPolygon = resolvePolygonForResult(result);

      let polygonId = resolvedPolygon.id ?? (rawPolygonId && rawPolygonId.length > 0 ? rawPolygonId : undefined);
      let polygonName = resolvedPolygon.name ?? rawPolygonName;

      const commodity = (result?.commodity || result?.results?.commodity || 'unknown')
        .toString()
        .toLowerCase();

      const rawAnalysisId =
        result?.analysis_id ||
        result?.id ||
        result?.results?.analysis_id ||
        result?.results?.id ||
        `analysis_${index}`;
      const analysisId = String(rawAnalysisId);

      const probabilities: number[] = [];
      const coordinates: [number, number][] = [];
      const confidences: Array<number | null> = [];
      const uncertainties: Array<number | null> = [];
      const pointDetails: AnalysisPointDetails[] = [];

      gridPoints.forEach((rawPoint: any) => {
        if (!rawPoint) {
          return;
        }

        const lat = Number(
          rawPoint.latitude ??
            rawPoint.lat ??
            rawPoint.y ??
            (Array.isArray(rawPoint) ? rawPoint[1] : NaN)
        );
        const lon = Number(
          rawPoint.longitude ??
            rawPoint.lon ??
            rawPoint.x ??
            (Array.isArray(rawPoint) ? rawPoint[0] : NaN)
        );

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          return;
        }

        const probability =
          ensureUnit(
            Number(
              rawPoint.probability ??
                rawPoint.score ??
                rawPoint.value ??
                rawPoint.prediction ??
                rawPoint.weight ??
                0
            )
          ) ?? 0;

        const confidence = ensureUnit(
          getDetailNumber(
            rawPoint,
            'confidence',
            'confidence_score',
            'confidenceScore',
            'confidence_metric'
          )
        );

        const uncertainty = ensureUnit(
          getDetailNumber(rawPoint, 'total_uncertainty', 'uncertainty', 'uncertainty_score')
        );

        coordinates.push([lat, lon]);
        probabilities.push(probability);
        confidences.push(confidence);
        uncertainties.push(uncertainty);

        const detail: AnalysisPointDetails = {
          probability,
          ...rawPoint
        };

        if (typeof confidence === 'number') {
          (detail as any).confidence = confidence;
          (detail as any).confidence_score = confidence;
        }
        if (typeof uncertainty === 'number') {
          (detail as any).total_uncertainty = uncertainty;
        }

        pointDetails.push(detail);
      });

      if (probabilities.length === 0) {
        return;
      }

      const averageProbability =
        probabilities.reduce((sum, value) => sum + value, 0) / probabilities.length;
      const maxProbability = Math.max(...probabilities);
      const highPotentialCount = probabilities.filter((value) => value >= 0.7).length;

      const polygonBoundsRaw =
        result?.results?.polygon_bounds ??
        result?.polygon_bounds ??
        result?.results?.ml_results?.polygon_bounds ??
        null;

      const normalizedBounds = normalizeBounds(polygonBoundsRaw);

      let boundsArray: [number, number, number, number] | null = null;
      if (normalizedBounds) {
        boundsArray = [
          normalizedBounds.min_lat,
          normalizedBounds.min_lon,
          normalizedBounds.max_lat,
          normalizedBounds.max_lon
        ];
      } else if (coordinates.length) {
        const lats = coordinates.map(([latitude]) => latitude);
        const lons = coordinates.map(([, longitude]) => longitude);
        boundsArray = [
          Math.min(...lats),
          Math.min(...lons),
          Math.max(...lats),
          Math.max(...lons)
        ];
      }

      const discoveryProbability =
        ensureUnit(
          getDetailNumber(
            result?.enhanced_results,
            'discovery_probability',
            'discoveryProbability'
          )
        ) ??
        ensureUnit(
          getDetailNumber(
            result?.results?.summary ?? result?.results?.ml_results,
            'discovery_probability',
            'discoveryProbability'
          )
        ) ??
        averageProbability;

      const modelVersion =
        getDetailString(result?.results, 'model_version', 'modelVersion') ||
        getDetailString(result?.metadata, 'model_version', 'modelVersion') ||
        getDetailString(result, 'model_version', 'modelVersion') ||
        undefined;

      const completedAt =
        result?.completed_at ||
        result?.results?.completed_at ||
        result?.updated_at ||
        result?.created_at ||
        null;

      const analysisNameCandidate =
        getDetailString(result, 'analysis_name', 'analysisName') ||
        getDetailString(result?.results, 'analysis_name', 'analysisName') ||
        getDetailString(result?.metadata, 'analysis_name', 'analysisName') ||
        getDetailString(result?.results?.report_summary, 'analysis_name', 'analysisName') ||
        null;

      const metadata: AnalysisGridMetadata = {
        analysisName: analysisNameCandidate || undefined,
        analysisLabel: analysisNameCandidate || undefined,
        discoveryProbability,
        averageProbability,
        maxProbability,
        totalPoints: probabilities.length,
        highPotentialCount,
        modelVersion,
        completedAt,
        bounds: boundsArray,
        polygonBounds: normalizedBounds,
        projectId: projectId ? String(projectId) : null,
        projectName: projectName ? String(projectName) : null,
        uncertainties,
        commodity,
        discovery_probability: discoveryProbability,
        average_probability: averageProbability,
        max_probability: maxProbability,
        total_points: probabilities.length,
        high_potential_count: highPotentialCount
      };

      const entry: AnalysisGridEntry = {
        analysisId,
        polygonId,
        polygonName,
        projectId: projectId ? String(projectId) : null,
        commodity,
        probabilities,
        coordinates,
        confidences,
        pointDetails,
        metadata,
        completedAt
      };

      const rawRasterAssets =
        result?.results?.ml_results?.raster_assets ??
        result?.results?.raster_assets ??
        result?.ml_results?.raster_assets ??
        result?.raster_assets ??
        null;

      if (rawRasterAssets && typeof rawRasterAssets === 'object') {
        const normalizedRasterAssets: Record<string, RasterAssetDescriptor> = {};
        Object.entries(rawRasterAssets).forEach(([rawKey, rawValue]) => {
          if (!rawValue || typeof rawValue !== 'object') {
            return;
          }

          const value = rawValue as Record<string, any>;

          const boundsArray =
            Array.isArray(value.bounds) && value.bounds.length >= 4
              ? ([
                  Number(value.bounds[0]),
                  Number(value.bounds[1]),
                  Number(value.bounds[2]),
                  Number(value.bounds[3])
                ] as [number, number, number, number])
              : ([0, 0, 0, 0] as [number, number, number, number]);

          const valueRange =
            Array.isArray(value.value_range) && value.value_range.length >= 2
              ? ([Number(value.value_range[0]), Number(value.value_range[1])] as [number, number])
              : undefined;

          const pixelSize =
            Array.isArray(value.pixel_size) && value.pixel_size.length >= 2
              ? ([Number(value.pixel_size[0]), Number(value.pixel_size[1])] as [number, number])
              : undefined;

          const statistics =
            value.statistics && typeof value.statistics === 'object'
              ? {
                  min: Number(value.statistics.min ?? value.statistics.minimum ?? 0),
                  max: Number(value.statistics.max ?? value.statistics.maximum ?? 1),
                  mean: Number(value.statistics.mean ?? 0),
                  stdev: Number(value.statistics.stdev ?? value.statistics.stddev ?? 0)
                }
              : undefined;

          const descriptor: RasterAssetDescriptor = {
            layer:
              typeof value.layer === 'string' && value.layer.length > 0 ? value.layer : rawKey,
            format: typeof value.format === 'string' ? value.format : 'GTiff',
            url: typeof value.url === 'string' ? value.url : String(value.url ?? ''),
            width: Number(value.width ?? 0),
            height: Number(value.height ?? 0),
            bounds: boundsArray,
            value_range: valueRange,
            statistics,
            nodata: value.nodata !== undefined && value.nodata !== null ? Number(value.nodata) : undefined,
            pixel_size: pixelSize,
            content_type: typeof value.content_type === 'string' ? value.content_type : undefined,
            filename: typeof value.filename === 'string' ? value.filename : undefined,
            relative_path: typeof value.relative_path === 'string' ? value.relative_path : undefined
          };

          if (descriptor.url) {
            normalizedRasterAssets[descriptor.layer] = descriptor;
          }
        });

        if (Object.keys(normalizedRasterAssets).length > 0) {
          entry.rasterAssets = normalizedRasterAssets;
        }
      }

      gridDataMap.set(analysisId, entry);
    });

    // Debug: Log gridDataMap contents to diagnose missing sidebar entries
    if (process.env.NODE_ENV === 'development') {
      devInfo('[extractGridData] Final gridDataMap entries:', {
        size: gridDataMap.size,
        entries: Array.from(gridDataMap.entries()).map(([id, e]) => ({
          analysisId: id,
          polygonName: e.polygonName,
          polygonId: e.polygonId,
          pointCount: e.probabilities?.length || 0,
        })),
      });
    }

    setGridDataByMineral(gridDataMap);
  }, []);

  useEffect(() => {
    const projectFilteredResults = selectedProject?.id
      ? (analysisResults || []).filter((result) => result.project_id === selectedProject.id)
      : analysisResults || [];

    // Debug: Log analysis count to diagnose missing sidebar entries
    if (process.env.NODE_ENV === 'development') {
      devInfo('[extractGridData useEffect] Analysis counts:', {
        analysisResults_total: analysisResults?.length || 0,
        projectFilteredResults_count: projectFilteredResults.length,
        loadedAnalysisData_count: loadedAnalysisData?.length || 0,
        selectedProject_id: selectedProject?.id,
        analysisIds: analysisResults?.map((r: any) => r?.analysis_id || r?.id) || [],
        projectIds: analysisResults?.map((r: any) => r?.project_id) || [],
      });
    }

    if (projectFilteredResults.length > 0) {
      extractGridData(projectFilteredResults);
      return;
    }

    if (loadedAnalysisData && loadedAnalysisData.length > 0) {
      extractGridData(loadedAnalysisData);
      return;
    }

    extractGridData([]);
  }, [analysisResults, loadedAnalysisData, extractGridData, selectedProject?.id]);

  useEffect(() => {
    // No auto-switch — respect current visualization mode
    if (gridDataByMineral.size > 0 && !useGridVisualization) {
      // intentionally left blank
    }
  }, [gridDataByMineral.size, useGridVisualization]);

  useEffect(() => {
    if (!initialHeatmapShown && gridDataByMineral.size > 0) {
      setVisualizationMode('heatmap');
      setInitialHeatmapShown(true);
    }
  }, [gridDataByMineral.size, initialHeatmapShown]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }

    const cache = rasterLayerCacheRef.current;
    const activeKeys = new Set<string>();

    gridDataByMineral.forEach((entry) => {
      if (!entry || !entry.analysisId || !entry.rasterAssets) {
        return;
      }
      const assetKeys = Object.keys(entry.rasterAssets);
      if (assetKeys.length === 0) {
        return;
      }
      const primaryKey = assetKeys.includes('probability') ? 'probability' : assetKeys[0];
      const asset = entry.rasterAssets[primaryKey];
      if (!asset || !asset.url) {
        return;
      }

      const visibilityKey = `heatmap_${entry.analysisId}`;
      const baseVisibility = markerVisibility?.get(visibilityKey);
      const isVisible = visualizationMode === 'heatmap' && (typeof baseVisibility === 'boolean' ? baseVisibility : true);
      const cacheKey = `${entry.analysisId}:${asset.layer || primaryKey}`;
      activeKeys.add(cacheKey);

      const baseMineralType = (entry.commodity || entry.metadata?.commodity || 'unknown').toLowerCase();
      const settings =
        gridSettingsWithDefaults?.get(entry.analysisId) ||
        (baseMineralType ? gridSettingsWithDefaults?.get(baseMineralType) : undefined) ||
        DEFAULT_ANALYSIS_GRID_SETTINGS;
      const desiredOpacity = settings?.opacity ?? DEFAULT_ANALYSIS_GRID_SETTINGS.opacity;

      const styleExpression = {
        color: [
          'interpolate',
          ['linear'],
          ['band', 1],
          0, 'rgba(0, 0, 0, 0)',
          0.3, 'rgba(0, 98, 255, 0.55)',
          0.5, 'rgba(0, 207, 255, 0.65)',
          0.7, 'rgba(0, 255, 0, 0.75)',
          0.85, 'rgba(255, 204, 0, 0.85)',
          1.0, 'rgba(255, 0, 0, 0.95)'
        ]
      } as any;

      let layer = cache.get(cacheKey);
      const existingUrl = layer?.get('assetUrl');

      if (!layer || existingUrl !== asset.url) {
        if (layer) {
          map.removeLayer(layer);
          cache.delete(cacheKey);
        }

        const source = new GeoTIFF({
          sources: [
            {
              url: asset.url,
              nodata: asset.nodata ?? undefined,
              min: asset.value_range?.[0],
              max: asset.value_range?.[1],
              overviews: []
            }
          ],
          convertToRGB: false,
          normalize: true,
          transition: 0,
          wrapX: false,
          crossOrigin: 'anonymous'
        } as any);

        layer = new WebGLTile({
          source,
          opacity: desiredOpacity,
          visible: isVisible,
          style: styleExpression,
        } as any);
        layer.set('name', `raster_${entry.analysisId}_${asset.layer || 'primary'}`);
        layer.set('assetUrl', asset.url);
        layer.setZIndex(140);
        map.addLayer(layer);
        cache.set(cacheKey, layer);
      } else {
        layer.setOpacity(desiredOpacity);
        layer.setVisible(isVisible);
        const existingLayerAny = layer as any;
        if (existingLayerAny && typeof existingLayerAny.setStyle === 'function') {
          existingLayerAny.setStyle(styleExpression);
        }
      }
    });

    cache.forEach((layer, key) => {
      if (!activeKeys.has(key)) {
        map.removeLayer(layer);
        cache.delete(key);
      }
    });
  }, [
    gridDataByMineral,
    gridSettingsWithDefaults,
    markerVisibility,
    visualizationMode
  ]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }

    const cache = staticRasterLayerCacheRef.current;
    const activeKeys = new Set<string>();

    const isFiniteNumber = (value: unknown): value is number =>
      typeof value === 'number' && Number.isFinite(value);

    projectStaticRasters.forEach((raster) => {
      const layerKey = raster.id;
      const url = raster.url;
      if (!url) {
        return;
      }

      const isVisible = staticRasterVisibility[raster.id] ?? false;
      const desiredOpacity = raster.defaultOpacity ?? 0.85;

      const range = raster.valueRange;
      let minValue = range && isFiniteNumber(range[0]) ? range[0] : undefined;
      let maxValue = range && isFiniteNumber(range[1]) ? range[1] : undefined;

      if (!isFiniteNumber(minValue) && isFiniteNumber(raster.statistics?.min)) {
        minValue = raster.statistics?.min;
      }
      if (!isFiniteNumber(maxValue) && isFiniteNumber(raster.statistics?.max)) {
        maxValue = raster.statistics?.max;
      }

      if (!isFiniteNumber(minValue)) {
        minValue = 0;
      }
      if (!isFiniteNumber(maxValue)) {
        maxValue = minValue + 1;
      }
      if (maxValue <= minValue) {
        maxValue = minValue + 1;
      }

      const span = maxValue - minValue;
      const q1 = minValue + span * 0.25;
      const q2 = minValue + span * 0.5;
      const q3 = minValue + span * 0.75;

      const styleExpression = {
        color: [
          'interpolate',
          ['linear'],
          ['band', 1],
          minValue,
          'rgba(40, 54, 99, 0.35)',
          q1,
          'rgba(72, 123, 182, 0.55)',
          q2,
          'rgba(108, 169, 109, 0.7)',
          q3,
          'rgba(210, 198, 128, 0.85)',
          maxValue,
          'rgba(255, 255, 255, 0.95)'
        ]
      } as any;

      const styleKey = `${minValue.toFixed(6)}:${maxValue.toFixed(6)}:${desiredOpacity.toFixed(3)}`;
      let layer = cache.get(layerKey);
      const needsRebuild =
        !layer ||
        layer.get('assetUrl') !== url ||
        layer.get('styleKey') !== styleKey;

      if (needsRebuild) {
        if (layer) {
          map.removeLayer(layer);
          cache.delete(layerKey);
        }
        const source = new GeoTIFF({
          sources: [
            {
              url,
              nodata: raster.nodata ?? undefined,
              min: minValue,
              max: maxValue,
              overviews: []
            }
          ],
          convertToRGB: false,
          normalize: false,
          wrapX: false,
          transition: 0,
          crossOrigin: 'anonymous'
        } as any);

        layer = new WebGLTile({
          source,
          opacity: desiredOpacity,
          visible: isVisible,
          style: styleExpression
        } as any);
        layer.set('name', `static_raster_${raster.id}`);
        layer.set('assetUrl', url);
        layer.set('styleKey', styleKey);
        layer.setZIndex(90);
        map.addLayer(layer);
        cache.set(layerKey, layer);
      }

      if (!layer) {
        return;
      }

      layer.setVisible(isVisible);
      layer.setOpacity(desiredOpacity);
      layer.changed();
      activeKeys.add(layerKey);
    });

    cache.forEach((layer, key) => {
      if (!activeKeys.has(key)) {
        map.removeLayer(layer);
        cache.delete(key);
      }
    });
  }, [projectStaticRasters, staticRasterVisibility]);

  useEffect(() => {
    return () => {
      const map = mapInstanceRef.current;
      if (map) {
        rasterLayerCacheRef.current.forEach((layer) => {
          map.removeLayer(layer);
        });
        staticRasterLayerCacheRef.current.forEach((layer) => {
          map.removeLayer(layer);
        });
      }
      rasterLayerCacheRef.current.clear();
      staticRasterLayerCacheRef.current.clear();
    };
  }, []);

  const sortedGridEntries = useMemo(() => {
    if (gridDataByMineral.size === 0) {
      return [] as AnalysisGridEntry[];
    }
    return Array.from(gridDataByMineral.values()).sort((a, b) => {
      const timeA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
      const timeB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      if (timeB !== timeA) {
        return timeB - timeA;
      }
      return a.analysisId.localeCompare(b.analysisId);
    });
  }, [gridDataByMineral]);

  useEffect(() => {
    if (onLegendEntriesChange) {
      onLegendEntriesChange(sortedGridEntries);
    }
  }, [sortedGridEntries, onLegendEntriesChange]);

  const polygonOptions = useMemo(() => {
    const lookup = new globalThis.Map<string, string>();
    analysisPolygons.forEach((polygon) => lookup.set(polygon.id, polygon.name));

    const polygonsWithData = new Set<string>();

    sortedGridEntries.forEach((entry) => {
      if (entry.polygonId) {
        polygonsWithData.add(entry.polygonId);
        const label = entry.polygonName || lookup.get(entry.polygonId) || entry.polygonId;
        lookup.set(entry.polygonId, label);
      }
    });

    const options = Array.from(lookup.entries())
      .filter(([id]) => polygonsWithData.has(id))
      .map(([id, name]) => ({ id, name }));

    if (sortedGridEntries.some((entry) => !entry.polygonId)) {
      options.push({ id: '__unassigned__', name: 'Unassigned results' });
    }

    return options;
  }, [analysisPolygons, sortedGridEntries]);

  const entriesByPolygon = useMemo(() => {
    const map = new globalThis.Map<string, AnalysisGridEntry[]>();
    sortedGridEntries.forEach((entry) => {
      const key = entry.polygonId ?? '__unassigned__';
      const list = map.get(key) ?? [];
      list.push(entry);
      map.set(key, list);
    });
    return map;
  }, [sortedGridEntries]);

  useEffect(() => {
    if (sortedGridEntries.length === 0) {
      if (insightsPolygonId || insightsAnalysisId || insightsCommodity || expandedCellKey) {
        setInsightsPolygonId('');
        setInsightsAnalysisId('');
        setInsightsCommodity('');
        setExpandedCellKey(null);
      }
      return;
    }

    let nextPolygonId = insightsPolygonId;
    if (nextPolygonId && !entriesByPolygon.has(nextPolygonId)) {
      nextPolygonId = '';
    }

    if (!nextPolygonId) {
      const firstOption = polygonOptions.find((option) => entriesByPolygon.has(option.id));
      nextPolygonId =
        firstOption?.id ??
        (sortedGridEntries[0]?.polygonId ?? (entriesByPolygon.has('__unassigned__') ? '__unassigned__' : ''));
    }

    if (nextPolygonId && nextPolygonId !== insightsPolygonId) {
      setInsightsPolygonId(nextPolygonId);
      return;
    }

    const candidateEntries =
      (nextPolygonId && entriesByPolygon.get(nextPolygonId)) || sortedGridEntries;

    let nextAnalysisId = insightsAnalysisId;
    if (nextAnalysisId && !candidateEntries.some((entry) => entry.analysisId === nextAnalysisId)) {
      nextAnalysisId = '';
    }

    if (!nextAnalysisId) {
      nextAnalysisId = candidateEntries[0]?.analysisId ?? sortedGridEntries[0]?.analysisId ?? '';
    }

    if (nextAnalysisId && nextAnalysisId !== insightsAnalysisId) {
      setInsightsAnalysisId(nextAnalysisId);
      return;
    }

    const activeEntry =
      candidateEntries.find((entry) => entry.analysisId === nextAnalysisId) ??
      candidateEntries[0] ??
      sortedGridEntries[0];

    if (activeEntry && activeEntry.commodity !== insightsCommodity) {
      setInsightsCommodity(activeEntry.commodity);
    }
  }, [
    sortedGridEntries,
    entriesByPolygon,
    polygonOptions,
    insightsPolygonId,
    insightsAnalysisId,
    insightsCommodity,
    expandedCellKey
  ]);

  const selectedPolygonEntries = useMemo(() => {
    if (sortedGridEntries.length === 0) {
      return [] as AnalysisGridEntry[];
    }
    if (!insightsPolygonId) {
      return sortedGridEntries;
    }
    if (entriesByPolygon.has(insightsPolygonId)) {
      return entriesByPolygon.get(insightsPolygonId) ?? [];
    }
    if (insightsPolygonId === '__unassigned__') {
      return entriesByPolygon.get('__unassigned__') ?? [];
    }
    return sortedGridEntries;
  }, [sortedGridEntries, entriesByPolygon, insightsPolygonId]);

  const selectedAnalysisEntry = useMemo(() => {
    if (selectedPolygonEntries.length === 0) {
      return null;
    }
    if (insightsAnalysisId) {
      const match = selectedPolygonEntries.find((entry) => entry.analysisId === insightsAnalysisId);
      if (match) {
        return match;
      }
    }
    return selectedPolygonEntries[0] ?? null;
  }, [selectedPolygonEntries, insightsAnalysisId]);

  const currentHeatmapLegend = useMemo(() => {
    if (gridDataByMineral.size === 0) {
      return null;
    }
    const entry = selectedAnalysisEntry || sortedGridEntries[0];
    if (!entry) {
      return null;
    }

    const mineralType = entry.commodity || 'unknown';
    const mineralKey = mineralType.toLowerCase();
    const metadataCommodity = entry.metadata?.commodity;
    const metadataMineralKey =
      typeof metadataCommodity === 'string' && metadataCommodity.trim().length > 0
        ? metadataCommodity.toLowerCase()
        : undefined;

    const resolvedSettings =
      gridSettingsWithDefaults.get(entry.analysisId) ||
      (metadataMineralKey ? gridSettingsWithDefaults.get(metadataMineralKey) : undefined) ||
      gridSettingsWithDefaults.get(mineralType) ||
      gridSettingsWithDefaults.get(mineralKey);

    const effectiveSettings = resolvedSettings ?? DEFAULT_ANALYSIS_GRID_SETTINGS;

    const toNumber = (value: any): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? value : null;

    const summary = {
      discoveryProbability: toNumber(
        entry.metadata?.discoveryProbability ?? entry.metadata?.discovery_probability
      ),
      avgProbability: toNumber(
        entry.metadata?.averageProbability ??
          entry.metadata?.average_probability ??
          entry.metadata?.avg_probability
      ),
      maxProbability: toNumber(
        entry.metadata?.maxProbability ??
          entry.metadata?.max_probability
      ),
      highPotential: (() => {
        const value =
          entry.metadata?.highPotentialCount ??
          entry.metadata?.high_potential_count;
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
      })(),
      gridPoints: entry.probabilities?.length ?? 0
    };

    const colorScheme =
      effectiveSettings.colorScheme ?? DEFAULT_ANALYSIS_GRID_SETTINGS.colorScheme;

    const markers = [
      { label: 'Threshold', value: effectiveSettings.threshold, color: '#FFB300' },
      { label: 'Average', value: summary.avgProbability, color: '#58A6FF' },
      { label: 'Discovery', value: summary.discoveryProbability, color: '#4CAF50' },
      { label: 'Max', value: summary.maxProbability, color: '#E91E63' }
    ];

    return {
      entry,
      mineralType,
      summary,
      effectiveSettings,
      colorScheme,
      markers
    };
  }, [
    gridDataByMineral,
    selectedAnalysisEntry,
    sortedGridEntries,
    gridSettingsWithDefaults
  ]);

  const controlFlyoutEntry = useMemo(() => {
    if (!controlFlyoutId) {
      return null;
    }
    return sortedGridEntries.find((entry) => entry.analysisId === controlFlyoutId) || null;
  }, [sortedGridEntries, controlFlyoutId]);

  const openHeatmapControls = useCallback(
    (entry: AnalysisGridEntry) => {
      if (!entry) {
        return;
      }
      setControlFlyoutId(entry.analysisId);
      window.dispatchEvent(
        new CustomEvent('analysis:controls-open', {
          detail: { analysisId: entry.analysisId, polygonId: entry.polygonId }
        })
      );
    },
    [setControlFlyoutId]
  );

  const geochemOverlaysForAnalysis = useMemo(() => {
    if (!controlFlyoutEntry) {
      return [] as GeochemOverlayDescriptor[];
    }
    const polygonName = controlFlyoutEntry.polygonName?.toLowerCase() || '';
    const polygonBounds = normalizeBoundsObject(
      controlFlyoutEntry.metadata?.polygon_bounds || controlFlyoutEntry.metadata?.bounds || null
    );

    return geochemOverlays.filter((overlay) => {
      if (polygonName && overlay.polygonHint && overlay.polygonHint.toLowerCase().includes(polygonName)) {
        return true;
      }
      if (polygonBounds && overlay.bounds) {
        return boundsIntersect(overlay.bounds, polygonBounds);
      }
      return !polygonName && !polygonBounds;
    });
  }, [controlFlyoutEntry, geochemOverlays]);

  const activeGeochemOverlay = useMemo(() => {
    if (!geochemOverlayId) {
      return null;
    }
    return geochemOverlays.find((overlay) => overlay.id === geochemOverlayId) || null;
  }, [geochemOverlayId, geochemOverlays]);

  const activeGeochemOverlaySummary = useMemo(() => {
    if (!activeGeochemOverlay) {
      return [] as Array<{ element: string; min: number; max: number; avg: number; count: number }>;
    }
    const aggregates = new globalThis.Map<string, { min: number; max: number; sum: number; count: number }>();
    activeGeochemOverlay.data.forEach((point) => {
      Object.entries(point.values || {}).forEach(([element, value]) => {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
          return;
        }
        const current = aggregates.get(element) || { min: value, max: value, sum: 0, count: 0 };
        current.min = Math.min(current.min, value);
        current.max = Math.max(current.max, value);
        current.sum += value;
        current.count += 1;
        aggregates.set(element, current);
      });
    });
    return Array.from(aggregates.entries()).map(([element, stats]) => ({
      element,
      min: stats.min,
      max: stats.max,
      avg: stats.count ? stats.sum / stats.count : 0,
      count: stats.count
    }));
  }, [activeGeochemOverlay]);

  useEffect(() => {
    if (!activeGeochemOverlay) {
      if (geochemColorField) {
        setGeochemColorField('');
      }
      return;
    }

    const available = (activeGeochemOverlay.elements || []).filter((element) => typeof element === 'string' && element.trim().length > 0);

    if (!available.length) {
      if (geochemColorField) {
        setGeochemColorField('');
      }
      return;
    }

    if (geochemColorField && available.includes(geochemColorField)) {
      return;
    }

    const lowerMap = new globalThis.Map<string, string>();
    available.forEach((element) => {
      lowerMap.set(element.toLowerCase(), element);
    });

    const preferenceOrder = [
      'gold_ppm',
      'gold_ppb',
      'gold_gpt',
      'silver_ppm',
      'silver_ppb',
      'copper_ppm',
      'lead_ppm',
      'zinc_ppm'
    ];

    const preferred = preferenceOrder.find((candidate) => lowerMap.has(candidate));
    const nextField = preferred ? lowerMap.get(preferred)! : available[0];

    if (nextField && nextField !== geochemColorField) {
      setGeochemColorField(nextField);
    }
  }, [activeGeochemOverlay, geochemColorField]);

  useEffect(() => {
    if (!controlFlyoutEntry) {
      return;
    }
    if (geochemOverlayId) {
      return;
    }
    if (geochemOverlaysForAnalysis.length > 0) {
      setGeochemOverlayId(geochemOverlaysForAnalysis[0].id);
    }
  }, [controlFlyoutEntry, geochemOverlayId, geochemOverlaysForAnalysis]);

  const activeGeochemColorStats = useMemo(() => {
    if (!activeGeochemOverlay || !geochemColorField) {
      return null;
    }
    const summaryEntry = activeGeochemOverlaySummary.find((item) => item.element === geochemColorField);
    if (summaryEntry && summaryEntry.count > 0) {
      return summaryEntry;
    }
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let sum = 0;
    let count = 0;
    activeGeochemOverlay.data.forEach((point) => {
      const rawValue = point.values?.[geochemColorField];
      const numeric = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      if (Number.isFinite(numeric)) {
        min = Math.min(min, numeric);
        max = Math.max(max, numeric);
        sum += numeric;
        count += 1;
      }
    });
    if (!count || !Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    return {
      element: geochemColorField,
      min,
      max,
      avg: sum / count,
      count
    };
  }, [activeGeochemOverlay, geochemColorField, activeGeochemOverlaySummary]);

  useEffect(() => {
    if (controlFlyoutId && !controlFlyoutEntry) {
      setControlFlyoutId('');
    }
  }, [controlFlyoutId, controlFlyoutEntry]);

  useEffect(() => {
    if (insightsTab === 'drill' && activeDrillHoleId) {
      loadDrillIntervals(activeDrillHoleId);
    }
  }, [insightsTab, activeDrillHoleId, loadDrillIntervals]);

  useEffect(() => {
    if (insightsTab === 'heatmap') {
      setHighlightPoint((prev) => (prev && prev.type !== 'heatmap' ? null : prev));
    }
  }, [insightsTab]);

  const flyoutMineralType = controlFlyoutEntry?.commodity || 'unknown';
  const flyoutMineralKey = flyoutMineralType.toLowerCase();
  const flyoutMetadataKey = (controlFlyoutEntry?.metadata?.commodity || '').toLowerCase();
  const flyoutUpdateKey = flyoutMetadataKey || flyoutMineralKey;
  const flyoutSettings = useMemo(() => {
    if (!controlFlyoutEntry) {
      return null as { threshold: number; opacity: number; modulated: boolean } | null;
    }
    return (
      gridSettingsWithDefaults.get(controlFlyoutEntry.analysisId) ||
      gridSettingsWithDefaults.get(flyoutMineralKey) ||
      { threshold: 0.05, opacity: 0.85, modulated: false }
    );
  }, [controlFlyoutEntry, gridSettingsWithDefaults, flyoutMineralKey]);

  const flyoutControlsContainerId = controlFlyoutEntry
    ? `${HEATMAP_CONTROLS_ID}-${controlFlyoutEntry.analysisId}`
    : undefined;

  const handleRasterVisibilityToggle = useCallback(
    (analysisId: string, nextVisible: boolean) => {
      if (!onMarkerVisibilityChange) {
        return;
      }
      onMarkerVisibilityChange(`raster_${analysisId}`, nextVisible);
    },
    [onMarkerVisibilityChange]
  );

  const controlFlyoutSummary = useMemo<ControlFlyoutSummary | null>(() => {
    if (!controlFlyoutEntry) {
      return null;
    }

    const toNumber = (value: any): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? value : null;

    return {
      discoveryProbability: toNumber(
        controlFlyoutEntry.metadata?.discoveryProbability ??
          controlFlyoutEntry.metadata?.discovery_probability
      ),
      avgProbability: toNumber(
        controlFlyoutEntry.metadata?.averageProbability ??
          controlFlyoutEntry.metadata?.average_probability ??
          controlFlyoutEntry.metadata?.avg_probability
      ),
      highPotential: (() => {
        const value =
          controlFlyoutEntry.metadata?.highPotentialCount ??
          controlFlyoutEntry.metadata?.high_potential_count;
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
      })(),
      gridPoints: controlFlyoutEntry.probabilities?.length ?? 0,
      completedAt: controlFlyoutEntry.completedAt ? formatTimestamp(controlFlyoutEntry.completedAt) : '—',
      polygonName: controlFlyoutEntry.polygonName || 'Unassigned'
    };
  }, [controlFlyoutEntry]);

  const controlFlyoutVisibilityKey = controlFlyoutEntry ? `heatmap_${controlFlyoutEntry.analysisId}` : null;
  const controlFlyoutVisible = controlFlyoutVisibilityKey
    ? markerVisibility?.get(controlFlyoutVisibilityKey) !== false
    : true;

  const handleFlyoutVisibilityToggle = useCallback(() => {
    if (!controlFlyoutVisibilityKey || !onMarkerVisibilityChange) {
      return;
    }
    onMarkerVisibilityChange(controlFlyoutVisibilityKey, !controlFlyoutVisible);
  }, [controlFlyoutVisibilityKey, controlFlyoutVisible, onMarkerVisibilityChange]);

  const analysisOptions = useMemo(() => {
    return selectedPolygonEntries.map((entry) => ({
      id: entry.analysisId,
      label: buildAnalysisRunLabel(entry),
      timestamp: entry.completedAt ? formatTimestamp(entry.completedAt) : undefined
    }));
  }, [selectedPolygonEntries]);

  useEffect(() => {
    if (expandedCellKey) {
      setExpandedCellKey(null);
    }
    if (hoveredGridPoint) {
      setHoveredGridPoint(null);
    }
  }, [insightsAnalysisId]);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ analysisId?: string; polygonId?: string }>;
      const detail = customEvent.detail;
      if (!detail) {
        return;
      }
      if (detail.analysisId) {
        setInsightsAnalysisId(detail.analysisId);
      }
      if (detail.polygonId) {
        setInsightsPolygonId(detail.polygonId);
      }
    };
    window.addEventListener('analysis:select', handler as EventListener);
    return () => window.removeEventListener('analysis:select', handler as EventListener);
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }

    if (geochemLayerRef.current) {
      map.removeLayer(geochemLayerRef.current);
      geochemLayerRef.current = null;
    }

    if (!geochemOverlayVisible || !activeGeochemOverlay) {
      return;
    }

    const features: Feature<Point>[] = [];
    let maxIntensity = 0;
    let colorMin = Number.POSITIVE_INFINITY;
    let colorMax = Number.NEGATIVE_INFINITY;

    activeGeochemOverlay.data.forEach((point, index) => {
      const { lat, lon, values, metadata } = point;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }

      const numericValues = Object.values(values || {})
        .map((value) => (typeof value === 'number' ? value : Number(value)))
        .filter((value) => Number.isFinite(value)) as number[];

      const intensityRaw = numericValues.length ? Math.max(...numericValues) : 0;
      if (Number.isFinite(intensityRaw) && intensityRaw > maxIntensity) {
        maxIntensity = intensityRaw;
      }

      let colorValue: number | null = null;
      if (geochemColorField) {
        const rawColorValue = values?.[geochemColorField];
        const numericColor = typeof rawColorValue === 'number' ? rawColorValue : Number(rawColorValue);
        if (Number.isFinite(numericColor)) {
          colorValue = numericColor;
          colorMin = Math.min(colorMin, numericColor);
          colorMax = Math.max(colorMax, numericColor);
        }
      }

      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        overlayId: activeGeochemOverlay.id,
        values,
        metadata: metadata || {},
        index
      });
      feature.set('intensityRaw', intensityRaw);
      feature.set('colorValue', colorValue);
      features.push(feature);
    });

    if (!features.length) {
      return;
    }

    const safeMax = maxIntensity > 0 ? maxIntensity : 1;
    features.forEach((feature) => {
      const raw = feature.get('intensityRaw') ?? 0;
      feature.set('intensity', clamp01(Number.isFinite(raw) ? raw / safeMax : 0));
    });

    const hasColorValues = Number.isFinite(colorMin) && Number.isFinite(colorMax);
    const colorRange = hasColorValues ? colorMax - colorMin : 0;

    features.forEach((feature) => {
      const value = feature.get('colorValue');
      let normalized: number | null = null;
      if (typeof value === 'number' && Number.isFinite(value) && hasColorValues) {
        normalized = colorRange > 0 ? clamp01((value - colorMin) / colorRange) : 1;
      }
      feature.set('colorNormalized', normalized);
    });

    const styleFunction = (feature: FeatureLike) => {
      const zoom = currentZoomLevelRef.current;
      const colorNormalizedValue = feature.get('colorNormalized');
      if (typeof colorNormalizedValue === 'number' && Number.isFinite(colorNormalizedValue)) {
        const normalized = clamp01(colorNormalizedValue);
        if (zoom < 6.5 && normalized < 0.78) return EMPTY_VECTOR_STYLE;
        if (zoom < 8.5 && normalized < 0.38) return EMPTY_VECTOR_STYLE;
        const rgb = getGeochemColor(normalized);
        const radius = 3.5 + normalized * 7.5;
        const fillAlpha = 0.2 + normalized * 0.55;
        const strokeAlpha = 0.5 + normalized * 0.3;
        return new Style({
          image: new CircleStyle({
            radius,
            fill: new Fill({ color: rgbaFromTuple(rgb, fillAlpha) }),
            stroke: new Stroke({
              color: rgbaFromTuple(rgb, strokeAlpha),
              width: 1.1 + normalized * 0.6
            })
          })
        });
      }

      const intensityValue = feature.get('intensity');
      const fallbackNormalized =
        typeof intensityValue === 'number' && Number.isFinite(intensityValue)
          ? clamp01(intensityValue)
          : 0;
      if (zoom < 6.5 && fallbackNormalized < 0.78) return EMPTY_VECTOR_STYLE;
      if (zoom < 8.5 && fallbackNormalized < 0.38) return EMPTY_VECTOR_STYLE;
      const neutralFill: RgbTuple = [150, 150, 150];
      const neutralStroke: RgbTuple = [180, 180, 180];
      const radius = 3 + fallbackNormalized * 6;
      const fillAlpha = 0.15 + fallbackNormalized * 0.35;
      const strokeAlpha = 0.45 + fallbackNormalized * 0.3;
      return new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color: rgbaFromTuple(neutralFill, fillAlpha) }),
          stroke: new Stroke({
            color: rgbaFromTuple(neutralStroke, strokeAlpha),
            width: 1
          })
        })
      });
    };

    const source = new VectorSource({ features });
    const layer = new VectorLayer({
      source,
      style: styleFunction,
      declutter: false
    });
    layer.set('name', `geochem_overlay_${activeGeochemOverlay.id}`);
    layer.setZIndex(135);

    map.addLayer(layer);
    geochemLayerRef.current = layer;

    return () => {
      if (mapInstanceRef.current && geochemLayerRef.current === layer) {
        mapInstanceRef.current.removeLayer(layer);
        geochemLayerRef.current = null;
      } else {
        layer.getSource()?.clear();
      }
    };
  }, [activeGeochemOverlay, geochemOverlayVisible, geochemColorField]);

  useEffect(() => {
    const openHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ analysisId?: string }>).detail;
      if (detail?.analysisId) {
        setControlFlyoutId(detail.analysisId);
      }
    };

    const closeHandler = () => {
      setControlFlyoutId('');
    };

    window.addEventListener('analysis:controls-open', openHandler as EventListener);
    window.addEventListener('analysis:controls-close', closeHandler);

    return () => {
      window.removeEventListener('analysis:controls-open', openHandler as EventListener);
      window.removeEventListener('analysis:controls-close', closeHandler);
    };
  }, []);

  const hoveredCellKey = useMemo(() => {
    if (!hoveredGridPoint || !selectedAnalysisEntry) {
      return null;
    }
    if (hoveredGridPoint.analysisId !== selectedAnalysisEntry.analysisId) {
      return null;
    }
    if (typeof hoveredGridPoint.index === 'number' && hoveredGridPoint.index >= 0) {
      return `${hoveredGridPoint.analysisId}_${hoveredGridPoint.index}`;
    }
    return null;
  }, [hoveredGridPoint, selectedAnalysisEntry]);

  const topGridCells = useMemo(() => {
    if (!selectedAnalysisEntry) {
      return [] as Array<{
        key: string;
        index: number;
        lat: number;
        lon: number;
        probability: number;
        confidence: number | null;
        uncertainty: number | null;
        detail?: AnalysisPointDetails | null;
      }>;
    }

    return selectedAnalysisEntry.coordinates
      .map(([lat, lon], index) => {
        const probability = toUnitInterval(selectedAnalysisEntry.probabilities[index] ?? 0);
        const detail = selectedAnalysisEntry.pointDetails?.[index] ?? null;
        const confidenceCandidate = selectedAnalysisEntry.confidences?.[index];
        const detailConfidence = getDetailNumber(
          detail,
          'confidence',
          'confidence_score',
          'confidenceScore',
          'confidence_metric'
        );
        const confidence =
          typeof confidenceCandidate === 'number' && Number.isFinite(confidenceCandidate)
            ? Math.max(0, Math.min(1, confidenceCandidate))
            : detailConfidence != null
              ? Math.max(0, Math.min(1, toUnitInterval(detailConfidence)))
              : null;

        const metadataUncertainty =
          selectedAnalysisEntry.metadata?.uncertainties &&
          selectedAnalysisEntry.metadata.uncertainties[index] != null
            ? selectedAnalysisEntry.metadata.uncertainties[index]
            : null;
        const detailUncertainty = getDetailNumber(
          detail,
          'total_uncertainty',
          'uncertainty',
          'uncertainty_score'
        );
        const uncertainty =
          typeof metadataUncertainty === 'number' && Number.isFinite(metadataUncertainty)
            ? Math.max(0, Math.min(1, metadataUncertainty))
            : detailUncertainty != null
              ? Math.max(0, Math.min(1, toUnitInterval(detailUncertainty)))
              : null;

        return {
          key: `${selectedAnalysisEntry.analysisId}_${index}`,
          index,
          lat,
          lon,
          probability,
          confidence,
          uncertainty,
          detail
        };
      })
      .filter((cell) => Number.isFinite(cell.lat) && Number.isFinite(cell.lon))
      .sort((a, b) => b.probability - a.probability)
      .slice(0, 20);
  }, [selectedAnalysisEntry]);

  const insightsSummary = useMemo(() => {
    if (!selectedAnalysisEntry) {
      return null;
    }

    const metadata = selectedAnalysisEntry.metadata || {};

    const averageProbability =
      typeof metadata.averageProbability === 'number'
        ? metadata.averageProbability
        : typeof metadata.average_probability === 'number'
          ? toUnitInterval(metadata.average_probability as number)
          : selectedAnalysisEntry.probabilities.length
            ? selectedAnalysisEntry.probabilities.reduce(
                (sum, value) => sum + toUnitInterval(value),
                0
              ) / selectedAnalysisEntry.probabilities.length
            : 0;

    const maxProbability =
      typeof metadata.maxProbability === 'number'
        ? metadata.maxProbability
        : typeof metadata.max_probability === 'number'
          ? toUnitInterval(metadata.max_probability as number)
          : selectedAnalysisEntry.probabilities.reduce(
              (max, value) => Math.max(max, toUnitInterval(value)),
              0
            );

    const totalPoints =
      metadata.totalPoints ??
      metadata.total_points ??
      selectedAnalysisEntry.probabilities.length;

    const highPotentialCount =
      metadata.highPotentialCount ??
      metadata.high_potential_count ??
      selectedAnalysisEntry.probabilities.filter((value) => toUnitInterval(value) >= 0.7).length;

    const discoveryProbability =
      typeof metadata.discoveryProbability === 'number'
        ? metadata.discoveryProbability
        : typeof metadata.discovery_probability === 'number'
          ? toUnitInterval(metadata.discovery_probability as number)
          : averageProbability;

    const completedAt =
      selectedAnalysisEntry.completedAt ?? metadata.completedAt ?? null;

    return {
      averageProbability,
      maxProbability,
      totalPoints,
      highPotentialCount,
      discoveryProbability,
      completedAt,
      modelVersion: metadata.modelVersion as string | undefined
    };
  }, [selectedAnalysisEntry]);

  const handleCellHover = useCallback(
    (cell: {
      index: number;
      lat: number;
      lon: number;
      probability: number;
      detail?: AnalysisPointDetails | null;
    } | null) => {
      if (!cell || !selectedAnalysisEntry) {
        setHoveredGridPoint(null);
        return;
      }

      setHoveredGridPoint({
        analysisId: selectedAnalysisEntry.analysisId,
        commodity: selectedAnalysisEntry.commodity,
        lat: cell.lat,
        lon: cell.lon,
        probability: cell.probability,
        detail: cell.detail ?? null,
        index: cell.index
      });
    },
    [selectedAnalysisEntry]
  );

  const handleHeatMapPointClick = useCallback(
    (point: { lat: number; lon: number; probability: number; details?: AnalysisPointDetails | null }) => {
      if (!selectedAnalysisEntry) {
        return;
      }

      const normalizedProbability = toUnitInterval(point.probability);
      let matchedIndex: number | null = null;

      selectedAnalysisEntry.coordinates.forEach(([lat, lon], idx) => {
        if (matchedIndex !== null) {
          return;
        }
        if (Math.abs(lat - point.lat) < 1e-4 && Math.abs(lon - point.lon) < 1e-4) {
          matchedIndex = idx;
        }
      });

      const detail =
        point.details ??
        (matchedIndex !== null ? selectedAnalysisEntry.pointDetails?.[matchedIndex] ?? null : null);

      const cell = {
        index: matchedIndex ?? -1,
        lat: point.lat,
        lon: point.lon,
        probability: normalizedProbability,
        detail
      };

      handleCellHover(cell);

      if (matchedIndex !== null && matchedIndex >= 0) {
        setExpandedCellKey(`${selectedAnalysisEntry.analysisId}_${matchedIndex}`);
      }
    },
    [handleCellHover, selectedAnalysisEntry]
  );

  useEffect(() => {
    if (!selectedAnalysisEntry) {
      setHoveredGridPoint(null);
    }
  }, [selectedAnalysisEntry]);

  useEffect(() => {
    if (hoveredGridPoint) {
      setHighlightPoint({
        lat: hoveredGridPoint.lat,
        lon: hoveredGridPoint.lon,
        type: 'heatmap',
        payload: hoveredGridPoint,
      });
    } else {
      setHighlightPoint((prev) => (prev && prev.type === 'heatmap' ? null : prev));
    }
  }, [hoveredGridPoint]);

  useEffect(() => {
    const source = highlightSourceRef.current;
    if (!source) {
      return;
    }

    source.clear();

    if (!highlightPoint) {
      return;
    }

    const { lat, lon, type, payload } = highlightPoint;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      return;
    }

    const feature = new Feature({
      geometry: new Point(fromLonLat([lon, lat])),
    });
    feature.set('highlightType', type);
    if (payload && typeof payload === 'object' && payload.probability !== undefined) {
      feature.set('probability', payload.probability);
    }
    source.addFeature(feature);
  }, [highlightPoint]);

  // Update visible layers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    mapInstanceRef.current.getLayers().forEach((layer: any) => {
      const layerName = layer.get('name');
      if (layerName === 'satellite') {
        layer.setVisible(activeLayers.includes('satellite'));
      } else if (layerName === 'terrain') {
        layer.setVisible(activeLayers.includes('terrain'));
      } else if (layerName === 'geological') {
        layer.setVisible(activeLayers.includes('geological'));
      } else if (layerName === 'osm') {
        layer.setVisible(activeLayers.includes('street'));
      } else if (layerName === 'analysis-results') {
        // Hide circular markers when grid view is active
        const shouldShowMarkers = activeLayers.includes('results') && !useGridVisualization;
        layer.setVisible(shouldShowMarkers);
        }
    });
  }, [activeLayers, useGridVisualization]);

  // Center on project - prioritize polygon regions, then boundary center, then project coordinates
  useEffect(() => {
            // 🚨 CRITICAL FIX: Only clear analysis results layer when ACTUALLY switching projects
    // Don't clear if it's the same project (initial load, re-renders, etc.)
    const isActualProjectSwitch = previousProjectRef.current !== null && previousProjectRef.current !== selectedProject?.id;
    
    if (isActualProjectSwitch) {
      if (analysisResultsLayer) {
        mapInstanceRef.current?.removeLayer(analysisResultsLayer);
        setAnalysisResultsLayer(null);
      }
      analysisAreaCacheRef.current.clear();
      // Clear loaded analysis data for new project
      setLoadedAnalysisData([]);
      loadedAnalysisRef.current = null;
      // Reset centering flag for new project to allow initial centering
      hasInitiallyCenteredRef.current = null;
      hasZoomedToMarkersRef.current = false;
      projectViewportUserInteractedRef.current = null;
      // Kill the previous project's position-verify/monitor timers immediately so
      // they can't yank the view back to the old project, and make the upcoming
      // delayed recenter forced (survives marker auto-zoom + effect re-runs).
      clearCenterTimers();
      pendingForceCenterRef.current = true;
          } else if (analysisResultsLayer && !isActualProjectSwitch) {
          }
    
    // Update the previous project reference
    previousProjectRef.current = selectedProject?.id || null;
    
    // 🚨 DYNAMIC AUTO-LOAD: Load latest analysis for any project with completed analyses
    // Check if we need to reload to get grid coordinates for toggleable markers
    const hasGridCoordinates =
      loadedAnalysisData.some(
        (analysis) =>
          analysis.results?.ml_results?.grid_coordinates ||
          analysis.results?.rule_based_results?.grid_coordinates
      ) ||
      projectAnalysisResults.some(
        (analysis) =>
          analysis.results?.ml_results?.grid_coordinates ||
          analysis.results?.rule_based_results?.grid_coordinates
      );

    if (selectedProject?.id && hasDetailedAnalyses) {
      loadedAnalysisRef.current = `${selectedProject.id}:props`;
    }

    if (!selectedProject?.id) {
      // Reset when no project selected
            loadedAnalysisRef.current = null;
    } else if (hasDetailedAnalyses) {
      loadedAnalysisRef.current = `${selectedProject.id}:cached`;
    }

    const polygonBoundsSnapshot = (() => {
      const bounds = calculatePolygonsBounds(polygonRegions);
      return bounds ? JSON.stringify(bounds.bounds) : null;
    })();
    const polygonsChanged =
      polygonRegions.length !== previousPolygonCountRef.current ||
      polygonBoundsSnapshot !== previousPolygonBoundsRef.current;

    if (polygonsChanged) {
      previousPolygonCountRef.current = polygonRegions.length;
      previousPolygonBoundsRef.current = polygonBoundsSnapshot;
      if (polygonRegions.length > 0 && selectedProject) {
        centerMapOnProject(selectedProject, true);
      }
    }

    if (!selectedProject) {
            return;
    }

    // Project selection should move immediately, without waiting behind the
    // large default GeoJSON requests or the asynchronous outline fetch. The
    // outline effect below refines this to the authoritative boundary extent.
    if (
      hasInitiallyCenteredRef.current !== selectedProject.id &&
      !hasSharedMapViewportForProject(selectedProject.id) &&
      actionSearchFocusUntilRef.current <= Date.now() &&
      mapInstanceRef.current
    ) {
      const lng = selectedProject.coordinates?.lng ?? selectedProject.coordinates?.lon;
      const lat = selectedProject.coordinates?.lat ?? selectedProject.coordinates?.latitude;
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        const view = mapInstanceRef.current.getView();
        clearCenterTimers();
        view.cancelAnimations();
        view.setCenter(fromLonLat([lng, lat]));
        view.setZoom(PROJECT_DEFAULT_ZOOMS[selectedProject.id] ?? DEFAULT_PROJECT_ZOOM);
        mapInstanceRef.current.renderSync();
        hasInitiallyCenteredRef.current = selectedProject.id;
      }
    }

    // Add a small delay to ensure map is fully initialized
    const timer = setTimeout(() => {
      const force = pendingForceCenterRef.current;
      pendingForceCenterRef.current = false;
      centerMapOnProject(selectedProject, force);
    }, 50);

    return () => clearTimeout(timer);
  }, [
    selectedProject,
    centerMapOnProject,
    polygonRegions,
    visiblePolygons,
    projectAnalysisResults,
    loadedAnalysisData,
    hasDetailedAnalyses
  ]); // Removed analysisResultsLayer to prevent recentering on toggle

  // Display selected country/province outline automatically.
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) return;

    if (countryOutlineLayerRef.current) {
      mapInstance.removeLayer(countryOutlineLayerRef.current);
      countryOutlineLayerRef.current = null;
    }

    const outlineConfig = selectedProject?.id ? PROJECT_COUNTRY_OUTLINE[selectedProject.id] : null;
    if (!outlineConfig) return;

    let cancelled = false;
    const outlineUrl = outlineConfig.url || COUNTRY_OUTLINE_URL;

    fetch(outlineUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled || !mapInstanceRef.current) return;

        const selectedFeatures = outlineConfig.iso
          ? {
              type: 'FeatureCollection',
              features: (data.features || []).filter((feature: any) => {
                const props = feature?.properties || {};
                return feature?.id === outlineConfig.iso || props.iso_a3 === outlineConfig.iso || props.ISO_A3 === outlineConfig.iso;
              }),
            }
          : data;

        if (!selectedFeatures.features || selectedFeatures.features.length === 0) return;

        const features = new GeoJSON().readFeatures(selectedFeatures, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:4326',
        });

        const outlineSource = new VectorSource({ features });
        const outlineLayer = new VectorLayer({
          source: outlineSource,
          style: [
            new Style({
              stroke: new Stroke({ color: 'rgba(0,0,0,0.84)', width: 4.2 }),
              fill: new Fill({ color: 'rgba(255,255,255,0.00)' }),
            }),
            new Style({
              stroke: new Stroke({ color: 'rgba(255,255,255,0.92)', width: 2.1 }),
            }),
          ],
          zIndex: 1200,
          properties: { name: 'selected-country-outline' },
        });

        countryOutlineLayerRef.current = outlineLayer;
        mapInstanceRef.current.addLayer(outlineLayer);

        // Project selection must visibly move the operator to the selected
        // jurisdiction. Fit the authoritative outline after it loads so async
        // project hydration cannot leave the map at the previous/world extent.
        // Preserve explicit shared-URL viewports and any genuine map movement.
        if (
          selectedProject?.id &&
          !hasSharedMapViewportForProject(selectedProject.id) &&
          actionSearchFocusUntilRef.current <= Date.now()
        ) {
          const activeMap = mapInstanceRef.current;
          const extent = outlineSource.getExtent();
          if (
            activeMap &&
            extent.length === 4 &&
            extent.every((value) => Number.isFinite(value))
          ) {
            // Cancel any verifier/monitor left by the prior project's
            // asynchronous centering before fitting the new jurisdiction.
            clearCenterTimers();
            const mapWidth = activeMap.getSize()?.[0] || 0;
            const rightPadding = mapWidth >= 1100 ? 420 : 48;
            activeMap.updateSize();
            activeMap.getView().fit(extent, {
              padding: [48, rightPadding, 56, 64],
              maxZoom: PROJECT_DEFAULT_ZOOMS[selectedProject.id] ?? DEFAULT_PROJECT_ZOOM,
              duration: 450,
            });
            hasInitiallyCenteredRef.current = selectedProject.id;
          }
        }
      })
      .catch((error) => {
        console.warn('Failed to load selected country outline:', error);
      });

    return () => {
      cancelled = true;
      if (countryOutlineLayerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(countryOutlineLayerRef.current);
        countryOutlineLayerRef.current = null;
      }
    };
  }, [selectedProject?.id]);

  // Display project boundary
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remove existing boundary layer
    if (boundaryLayerRef.current) {
      mapInstanceRef.current.removeLayer(boundaryLayerRef.current);
      boundaryLayerRef.current = null;
    }

    // Add boundary if project has one and visibility is enabled
    if (selectedProject?.project_boundary && showProjectBoundary) {
      try {
        const boundaryData = typeof selectedProject.project_boundary === 'string' 
          ? JSON.parse(selectedProject.project_boundary) 
          : selectedProject.project_boundary;

        if (boundaryData && boundaryData.features) {
          const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(boundaryData, {
              featureProjection: 'EPSG:3857'
            })
          });

          const boundaryLayer = new VectorLayer({
            source: vectorSource,
            style: new Style({
              stroke: new Stroke({
                color: '#58a6ff',
                width: 4
              }),
              fill: new Fill({
                color: 'rgba(88, 166, 255, 0.1)'
              })
            }),
            zIndex: 100
          });

          boundaryLayerRef.current = boundaryLayer;
          mapInstanceRef.current.addLayer(boundaryLayer);

                    // Don't force individual renders - let master render handle it
        }
      } catch (error) {
        console.error('Failed to display project boundary:', error);
      }
    }
  }, [selectedProject?.project_boundary, showProjectBoundary]);

  // Initialize visible polygons from polygon regions
  useEffect(() => {
    if (polygonRegions.length > 0) {
      const initialVisiblePolygons = new Set(
        polygonRegions.filter(p => p.visible).map(p => p.id)
      );
      setVisiblePolygons(initialVisiblePolygons);
    }
  }, [polygonRegions]);

  // Center map on mineral analysis bounds ONLY when new mineral data is generated
  useEffect(() => {
    // Skip centering on mineral bounds - keep map centered on project location instead
    if (mineralMapData?.bounds && isNewMineralData) {
                  // Reset the new mineral data flag without centering
      if (onMineralDataCentered) {
        setTimeout(() => onMineralDataCentered?.(), 100);
      }
    } else if (mineralMapData?.bounds && !isNewMineralData) {
          }
  }, [mineralMapData?.bounds, isNewMineralData]);
  
  // Master render effect - DISABLED to prevent interference with nuclear refresh
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    return; // EXIT EARLY - let nuclear refresh handle everything
    
    // Debounced render to ensure all layers (boundary + minerals) are visible
    const renderTimer = setTimeout(() => {
      if (mapInstanceRef.current) {
                // Nuclear approach: Force complete map re-render
        const view = mapInstanceRef.current.getView();
        const currentCenter = view.getCenter();
        const currentZoom = view.getZoom();
        
        // Force map size update
        mapInstanceRef.current.updateSize();
        
        // Count visible layers before
        let visibleLayers = 0;
        mapInstanceRef.current.getLayers().forEach((layer: any, index: number) => {
          const layerName = layer.get('name');
          const isVisible = layer.getVisible();
          if (isVisible) visibleLayers++;
                  });
        
                // Force all layers to refresh and ensure they're visible
        mapInstanceRef.current.getLayers().forEach((layer: any) => {
          const layerName = layer.get('name');
          if (layerName?.startsWith('mineral_') || layerName === undefined) {
            layer.setVisible(true); // Force visibility
            layer.changed(); // Force refresh
          }
        });
        
        // NUCLEAR RENDERING APPROACH - simulate view switch behavior
                // 1. Force map to think it's been resized (what happens during view switch)
        const mapElement = mapInstanceRef.current.getTargetElement();
        if (mapElement) {
                    // Temporarily hide and show to force browser reflow
          mapElement.style.display = 'none';
          // Force reflow
          void mapElement.offsetHeight;
          mapElement.style.display = 'block';
        }
        
        // 2. Force size update (critical for layer rendering)
        mapInstanceRef.current?.updateSize();
        
        // 3. Force all layers to redraw
        mapInstanceRef.current?.getLayers()?.forEach((layer: any) => {
          layer.changed();
        });
        
        // 4. Multiple render calls with increasing delays
        mapInstanceRef.current?.renderSync();
        mapInstanceRef.current?.render();
        
        setTimeout(() => {
          mapInstanceRef.current?.updateSize();
          mapInstanceRef.current?.render();
                  }, 50);
        
        setTimeout(() => {
          mapInstanceRef.current?.updateSize();
          mapInstanceRef.current?.renderSync();
                  }, 100);
      }
    }, 300); // Longer wait to ensure all other effects complete
    
    return () => clearTimeout(renderTimer);
  }, [selectedProject, mineralMapData, showProjectBoundary]);

  const handleLayerToggle = (
    event: React.MouseEvent<HTMLElement>,
    newLayers: string[]
  ) => {
    const clickedValue = (event.currentTarget as HTMLButtonElement).value;
    let adjustedLayers = [...newLayers];

    if (BASE_LAYER_KEYS.includes(clickedValue)) {
      if (adjustedLayers.includes(clickedValue)) {
        adjustedLayers = adjustedLayers.filter((layer) => !BASE_LAYER_KEYS.includes(layer) || layer === clickedValue);
      }
    }

    // Save current view state before any changes
    const view = mapInstanceRef.current?.getView();
    const savedZoom = view?.getZoom();
    const savedCenter = view?.getCenter();
    const savedViewportProjectId = hasInitiallyCenteredRef.current;
    setActiveLayers(adjustedLayers);

    // Special handling for Analysis Results toggle
    const analysisResultsToggled = adjustedLayers.includes('results') !== activeLayers.includes('results');
    if (analysisResultsToggled) {
      // Set flag to prevent recentering
      isTogglingMarkersRef.current = true;

      const showResults = adjustedLayers.includes('results');
      setShowAnalysisResults(showResults);
      if (analysisResultsLayer) {
        analysisResultsLayer.setVisible(showResults);
              }

      // Force restore view state after toggle to prevent any zoom changes
      setTimeout(() => {
        if (
          savedViewportProjectId === selectedProjectRef.current?.id &&
          view &&
          savedZoom &&
          savedCenter
        ) {
          view.setZoom(savedZoom);
          view.setCenter(savedCenter);
                  }
        // Clear the flag after a delay
        setTimeout(() => {
          isTogglingMarkersRef.current = false;
        }, 500);
      }, 100);
    }
  };

  const setBaseMapLayer = useCallback((layerId: string, visible: boolean) => {
    if (!BASE_LAYER_KEYS.includes(layerId)) {
      return;
    }
    setActiveLayers((prev) => {
      if (!visible) {
        return prev.filter((layer) => layer !== layerId);
      }
      const withoutBase = prev.filter((layer) => !BASE_LAYER_KEYS.includes(layer));
      return [...withoutBase, layerId];
    });
  }, [BASE_LAYER_KEYS]);

  const setResultsLayerVisible = useCallback((visible: boolean) => {
    const view = mapInstanceRef.current?.getView();
    const savedZoom = view?.getZoom();
    const savedCenter = view?.getCenter();
    const savedViewportProjectId = hasInitiallyCenteredRef.current;

    isTogglingMarkersRef.current = true;
    setActiveLayers((prev) => {
      if (visible) {
        return prev.includes('results') ? prev : [...prev, 'results'];
      }
      return prev.filter((layer) => layer !== 'results');
    });
    setShowAnalysisResults(visible);
    if (analysisResultsLayer) {
      analysisResultsLayer.setVisible(visible);
    }

    window.setTimeout(() => {
      if (
        savedViewportProjectId === selectedProjectRef.current?.id &&
        view &&
        Number.isFinite(savedZoom) &&
        savedCenter
      ) {
        view.setZoom(savedZoom as number);
        view.setCenter(savedCenter);
      }
      window.setTimeout(() => {
        isTogglingMarkersRef.current = false;
      }, 500);
    }, 100);
  }, [analysisResultsLayer]);

  const handleStaticRasterToggle = useCallback(
    (rasterId: string, visible: boolean) => {
      setStaticRasterVisibility((prev) => {
        if ((prev[rasterId] ?? false) === visible) {
          return prev;
        }
        return { ...prev, [rasterId]: visible };
      });
      if (onMarkerVisibilityChange) {
        onMarkerVisibilityChange(`static_raster_${rasterId}`, visible);
      }
    },
    [onMarkerVisibilityChange]
  );

  const zoomIn = () => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      markProjectViewportUserInteraction();
      view.animate({ zoom: view.getZoom()! + 1, duration: 250 });
    }
  };

  const zoomOut = () => {
    const view = mapInstanceRef.current?.getView();
    if (view) {
      markProjectViewportUserInteraction();
      view.animate({ zoom: view.getZoom()! - 1, duration: 250 });
    }
  };

  const centerOnProject = () => {
    if (selectedProject?.coordinates && mapInstanceRef.current) {
      markProjectViewportUserInteraction();
      const view = mapInstanceRef.current.getView();
      const lng = selectedProject.coordinates.lng || selectedProject.coordinates.lon || -112.074;
      const lat = selectedProject.coordinates.lat || selectedProject.coordinates.latitude || 37.628;
      
            view.animate({
        center: fromLonLat([lng, lat]),
        zoom: 10,
        duration: 1000
      });
    } else {
          }
  };

  // Mineral mapping functions
  // Commented out per strategic pivot to focus on document management
  /*
  const handleGenerateMineralMap = () => {
    if (selectedProject && onGenerateMineralMap) {
      let coordinates = selectedProject.coordinates;
      
      // If project has a boundary, use the boundary center instead of project coordinates
      if (selectedProject.project_boundary) {
        try {
          const boundaryData = typeof selectedProject.project_boundary === 'string' 
            ? JSON.parse(selectedProject.project_boundary) 
            : selectedProject.project_boundary;
          
          if (boundaryData?.features?.[0]?.geometry?.coordinates?.[0]) {
            const coords = boundaryData.features[0].geometry.coordinates[0];
            
            // Calculate center of boundary polygon
            let sumLat = 0, sumLng = 0, count = 0;
            coords.forEach((coord: number[]) => {
              if (coord.length >= 2) {
                sumLng += coord[0]; // longitude
                sumLat += coord[1]; // latitude
                count++;
              }
            });
            
            if (count > 0) {
              coordinates = {
                lat: sumLat / count,
                lng: sumLng / count
              };
                          }
          }
        } catch (error) {
                  }
      }
      
      if (coordinates) {
        onGenerateMineralMap(coordinates);
        setShowMineralLegend(true);
      }
    }
  };
  */

  // NEW: Test Geological Data Hub
  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGlobalGeology = () => {
    if (selectedProject && onTestGlobalGeology) {
      let coordinates = selectedProject.coordinates;
      
      // If project has a boundary, use the boundary center instead of project coordinates
      if (selectedProject.project_boundary) {
        try {
          const boundaryData = typeof selectedProject.project_boundary === 'string' 
            ? JSON.parse(selectedProject.project_boundary) 
            : selectedProject.project_boundary;
          
          // Calculate center of boundary for global geology test
          if (boundaryData.features && boundaryData.features[0]) {
            const coords = boundaryData.features[0].geometry.coordinates[0];
            if (coords && coords.length > 0) {
              const latSum = coords.reduce((sum: number, coord: number[]) => sum + coord[1], 0);
              const lngSum = coords.reduce((sum: number, coord: number[]) => sum + coord[0], 0);
              coordinates = {
                lat: latSum / coords.length,
                lng: lngSum / coords.length
              };
            }
          }
        } catch (error) {
          console.error('Error parsing boundary for global geology test:', error);
        }
      }
      
      onTestGlobalGeology(coordinates);
    }
  };
  */

  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestDrillingDatabase = () => {
    if (selectedProject && onTestDrillingDatabase) {
      let coordinates = selectedProject.coordinates;
      
      // If project has a boundary, use the boundary center instead of project coordinates
      if (selectedProject.project_boundary) {
        try {
          const boundary = JSON.parse(selectedProject.project_boundary);
          if (boundary && boundary.coordinates && boundary.coordinates[0]) {
            const coords = boundary.coordinates[0];
            if (coords.length > 0) {
              // Calculate center of boundary
              let latSum = 0, lngSum = 0;
              coords.forEach((coord: number[]) => {
                lngSum += coord[0];
                latSum += coord[1];
              });
              coordinates = {
                lat: latSum / coords.length,
                lng: lngSum / coords.length
              };
            }
          }
        } catch (error) {
          console.error('Error parsing boundary for drilling database test:', error);
        }
      }
      
      onTestDrillingDatabase(coordinates);
    }
  };
  */

  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGeophysicalData = () => {
    if (selectedProject && onTestGeophysicalData) {
      let coordinates = selectedProject.coordinates;
      
      // If project has a boundary, use the boundary center instead of project coordinates
      if (selectedProject.project_boundary) {
        try {
          const boundaryGeoJSON = JSON.parse(selectedProject.project_boundary);
          if (boundaryGeoJSON.type === 'Polygon' && boundaryGeoJSON.coordinates && boundaryGeoJSON.coordinates[0]) {
            const coords = boundaryGeoJSON.coordinates[0];
            let sumLat = 0, sumLng = 0;
            coords.forEach((coord: [number, number]) => {
              sumLng += coord[0];
              sumLat += coord[1];
            });
            coordinates = {
              lat: sumLat / coords.length,
              lng: sumLng / coords.length
            };
          }
        } catch (error) {
          console.error('Error parsing boundary for geophysical test:', error);
        }
      }
      
      onTestGeophysicalData(coordinates);
    }
  };
  */

  // Commented out per strategic pivot to focus on document management
  /*
  const handleTestGeologicalHub = () => {
    if (selectedProject && onTestGeologicalHub) {
      let coordinates = selectedProject.coordinates;
      
      // If project has a boundary, use the boundary center instead of project coordinates
      if (selectedProject.project_boundary) {
        try {
          const boundaryData = typeof selectedProject.project_boundary === 'string' 
            ? JSON.parse(selectedProject.project_boundary) 
            : selectedProject.project_boundary;
          
          if (boundaryData?.features?.[0]?.geometry?.coordinates?.[0]) {
            const coords = boundaryData.features[0].geometry.coordinates[0];
            
            // Calculate center of boundary polygon
            let sumLat = 0, sumLng = 0, count = 0;
            coords.forEach((coord: number[]) => {
              if (coord.length >= 2) {
                sumLng += coord[0]; // longitude
                sumLat += coord[1]; // latitude
                count++;
              }
            });
            
            if (count > 0) {
              coordinates = {
                lat: sumLat / count,
                lng: sumLng / count
              };
                          }
          }
        } catch (error) {
                  }
      }
      
      if (coordinates) {
        onTestGeologicalHub(coordinates);
      }
    }
  };
  */

  const toggleMineralLayer = (mineralType: string) => {
    const newActiveLayers = activeMineralLayers.includes(mineralType)
      ? activeMineralLayers.filter(layer => layer !== mineralType)
      : [...activeMineralLayers, mineralType];
    
    setActiveMineralLayers(newActiveLayers);
    
    // Update layer visibility
    const layer = mineralLayers.get(mineralType);
    if (layer) {
      layer.setVisible(newActiveLayers.includes(mineralType));
    }
  };

  // Debug mineralMapData changes to understand why effect doesn't trigger
  useEffect(() => {
    // If mineral data disappeared, log it prominently
    if (!mineralMapData && mineralLayers.size > 0) {
            }
  }, [mineralMapData, selectedProject?.id]);

  // Debug project changes separately
  useEffect(() => {
    }, [selectedProject]);

  // Effect to update mineral layers when data changes
  useEffect(() => {
    // Prevent multiple concurrent executions
    if (isProcessingMineralLayers) {
            return;
    }
    
    // Generate unique ID for this useEffect execution
    const effectId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    
    const debugInfo = {
      hasMap: !!mapInstanceRef.current,
      hasMineralData: !!mineralMapData,
      hasMineralLayers: !!mineralMapData?.mineral_layers,
      mineralDataKeys: mineralMapData ? Object.keys(mineralMapData) : null,
      selectedProjectId: selectedProject?.id,
      timestamp: new Date().toISOString(),
      mineralMapDataRef: mineralMapData,
      stackTrace: new Error().stack?.split('\n').slice(0, 5),
      currentLayerCount: mapInstanceRef.current?.getLayers().getLength() || 0,
      effectId: effectId,
      isProcessingMineralLayers: isProcessingMineralLayers
    };
    
        // Set processing flag
    setIsProcessingMineralLayers(true);
    
    // Log the full mineralMapData structure for debugging
    if (mineralMapData) {
      }
    
    if (!mapInstanceRef.current || !mineralMapData?.mineral_layers) {
            // Clear processing flag on early return
      setIsProcessingMineralLayers(false);
      return;
    }

    const map = mapInstanceRef.current;
    const newMineralLayers = new globalThis.Map<string, VectorLayer<VectorSource>>();

    // Clear existing mineral layers
        mineralLayers.forEach((layer: VectorLayer<VectorSource>) => {
            map.removeLayer(layer);
    });

    // Auto-enable all mineral layers when new data arrives
    const mineralTypes = Object.keys(mineralMapData.mineral_layers);
        if (activeMineralLayers.length === 0 && mineralTypes.length > 0) {
      setActiveMineralLayers(mineralTypes);
      setShowMineralLegend(true);
          }

    // Add new mineral layers with amorphous coverage styling
        Object.entries(mineralMapData.mineral_layers).forEach(([mineralType, geoJsonData]: [string, any]) => {
      try {
              // Debug: log the structure of the GeoJSON data
            const vectorSource = new VectorSource({
        features: new GeoJSON().readFeatures(geoJsonData, {
          featureProjection: 'EPSG:3857'
        })
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        properties: { name: `mineral_${mineralType}` },
        zIndex: 50, // Place mineral layers above base layers but below boundaries
        style: (feature) => {
          // Debug: log feature properties to see what we're actually getting (reduced logging)
          const featureProps = feature.getProperties();
          if (Object.keys(featureProps).length > 1 && Math.random() < 0.05) { // Only log 5% of features
                      }
          
          // Get properties from the new amorphous coverage data
          const probabilityLevel = feature.get('probability_level') || 'medium';
          const opacity = feature.get('opacity') || 0.4;
          const areaHectares = feature.get('area_hectares') || 0;
          const color = geoJsonData.properties.color;
          
          // Check if this is old dot-based data vs new amorphous coverage
          const hasNewProperties = feature.get('probability_level') !== undefined;
          if (!hasNewProperties) {
            // Upgrade legacy data on-the-fly - cast to Feature for proper typing
            const legacyValue = feature.get('value') || 1.0;
            const upgradedLevel = legacyValue > 2.0 ? 'high' : legacyValue > 1.2 ? 'medium' : 'low';
            const upgradedOpacity = legacyValue > 2.0 ? 0.7 : legacyValue > 1.2 ? 0.5 : 0.3;
            
            (feature as any).set('probability_level', upgradedLevel);
            (feature as any).set('opacity', upgradedOpacity);
            (feature as any).set('confidence', upgradedLevel);
            
            if (Math.random() < 0.01) { // Only log 1% of upgrades
                          }
          }
          
          // Convert hex color to rgba with opacity
          const hexToRgba = (hex: string, alpha: number) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          };

          // Handle both new amorphous coverage and legacy data
          if (hasNewProperties) {
            // New amorphous coverage styling
            const strokeWidth = probabilityLevel === 'high' ? 2 : probabilityLevel === 'medium' ? 1.5 : 1;
            const fillOpacity = opacity;
            const strokeOpacity = Math.min(opacity + 0.3, 1.0);
            
            return new Style({
              fill: new Fill({
                color: hexToRgba(color, fillOpacity)
              }),
              stroke: new Stroke({
                color: hexToRgba(color, strokeOpacity),
                width: strokeWidth + 1
              }),
              text: areaHectares > 50 ? new Text({
                text: `${mineralType.replace('_', ' ').toUpperCase()}\n${probabilityLevel} confidence\n${areaHectares.toFixed(0)} ha`,
                fill: new Fill({ color: '#FFFFFF' }),
                stroke: new Stroke({ color: '#000000', width: 2 }),
                font: 'bold 11px monospace',
                textAlign: 'center',
                textBaseline: 'middle',
                backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
                padding: [2, 4, 2, 4]
              }) : undefined
            });
          } else {
            // Legacy fallback styling (for old cached data)
            const probability = feature.get('probability') || feature.get('value') || 0.5;
            if (Math.random() < 0.01) { // Only log 1% of legacy styling calls
                          }
            
            return new Style({
              fill: new Fill({
                color: hexToRgba(color, 0.4)
              }),
              stroke: new Stroke({
                color: color,
                width: 2
              }),
              text: new Text({
                text: `${mineralType.replace('_', ' ').toUpperCase()}\n${(probability * 100).toFixed(0)}%`,
                fill: new Fill({ color: '#FFFFFF' }),
                stroke: new Stroke({ color: '#000000', width: 2 }),
                font: 'bold 10px monospace',
                textAlign: 'center',
                textBaseline: 'middle'
              })
            });
          }
        }
      });

      // Show layer if it's in active layers OR if we just auto-enabled all layers OR if it's restored data
      const shouldShow = activeMineralLayers.includes(mineralType) || 
                        (activeMineralLayers.length === 0 && mineralTypes.includes(mineralType)) ||
                        (!isNewMineralData); // Always show restored mineral data
      
      vectorLayer.setVisible(shouldShow);
      
            map.addLayer(vectorLayer);
            newMineralLayers.set(mineralType, vectorLayer);
            } catch (error) {
        console.error(`❌ Error creating layer for ${mineralType}:`, error);
      }
    });
    
        setMineralLayers(newMineralLayers);
    
    // NUCLEAR APPROACH: Force complete layer refresh by re-adding them
    setTimeout(() => {
      if (mapInstanceRef.current && newMineralLayers.size > 0) {
                // Step 1: Remove all mineral layers
        const allLayers = mapInstanceRef.current.getLayers().getArray().slice();
        const mineralLayersToReAdd: any[] = [];
        
        allLayers.forEach((layer: any) => {
          const layerName = layer.get('name');
          if (layerName?.startsWith('mineral_')) {
                        mapInstanceRef.current!.removeLayer(layer);
            mineralLayersToReAdd.push(layer);
          }
        });
        
        // Step 2: Force a render without mineral layers
        if (mapInstanceRef.current) {
          mapInstanceRef.current.updateSize();
          mapInstanceRef.current.renderSync();
        }
        
        // Step 3: Re-add mineral layers with fresh visibility
        mineralLayersToReAdd.forEach((layer: any, index: number) => {
          const layerName = layer.get('name');
                    layer.setVisible(true);
          layer.setZIndex(50 + index); // Ensure proper z-index
          if (mapInstanceRef.current) {
            mapInstanceRef.current.addLayer(layer);
          }
        });
        
        // Step 4: Final render with all layers
        if (mapInstanceRef.current) {
          mapInstanceRef.current.updateSize();
          mapInstanceRef.current.renderSync();
          mapInstanceRef.current.render();
        }
        
                // Verify layers are visible and properly counted
        const finalLayers = mapInstanceRef.current!.getLayers().getArray();
        const visibleLayers = finalLayers.filter(layer => layer.getVisible());
        
                finalLayers.forEach((layer: any, index: number) => {
          const layerName = layer.get('name') || 'unnamed';
          const visible = layer.getVisible();
                  });
        
                // Additional render to ensure visibility
        setTimeout(() => {
          mapInstanceRef.current?.render();
          mapInstanceRef.current?.renderSync();
                    // Monitor layers for the next few seconds to see when they disappear
          const monitorInterval = setInterval(() => {
            if (mapInstanceRef.current) {
              const currentLayers = mapInstanceRef.current.getLayers().getArray();
              const currentVisible = currentLayers.filter(layer => layer.getVisible());
              const mineralCount = currentLayers.filter(layer => layer.get('name')?.startsWith('mineral_')).length;
              
                            if (mineralCount === 0) {
                                clearInterval(monitorInterval);
              }
            }
          }, 500);
          
          // Stop monitoring after 10 seconds
          setTimeout(() => {
            clearInterval(monitorInterval);
                        // Clear processing flag after monitoring is done
            setIsProcessingMineralLayers(false);
                      }, 10000);
        }, 200);
      } else {
        // Clear processing flag if no layers to process
        setIsProcessingMineralLayers(false);
              }
    }, 100); // Longer delay for complete layer processing
    
  }, [mineralMapData, isProcessingMineralLayers]);

  useEffect(() => {
    const target = mapRef.current;
    if (!target || typeof ResizeObserver === 'undefined') {
      return;
    }

    // Only call updateSize when the rendered size actually diverges from the
    // container. WKWebView (the map embedded in Morph's scaled iframe) coalesces
    // the post-load layout grow and can drop the ResizeObserver callback for it,
    // leaving the canvas at a stale smaller size — so a timed burst of convergence
    // checks self-heals what the observer misses.
    const syncSize = () => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const size = map.getSize();
      if (!size || size[0] !== target.clientWidth || size[1] !== target.clientHeight) {
        map.updateSize();
        map.render();
      }
    };
    let frame = 0;
    const scheduleSync = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(syncSize);
    };
    const observer = new ResizeObserver(scheduleSync);
    observer.observe(target);
    const timers = [0, 250, 700, 1500].map((delay) => window.setTimeout(syncSize, delay));

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

	  const layerControlItems: LayerControlItem[] = [];
	  const layerVisibilityStorageKey = `${MAP_LAYER_VISIBILITY_PREFIX}${storageProjectKey(selectedProject?.id)}`;
	  const rememberLayerVisibility = useCallback((layerId: string, visible: boolean) => {
	    if (BASE_MAP_CONTROL_IDS.has(layerId)) {
	      try {
	        const previous = readBooleanRecord(layerVisibilityStorageKey);
	        BASE_MAP_CONTROL_IDS.forEach((baseLayerId) => {
	          delete previous[baseLayerId];
	        });
	        writeJsonStorage(layerVisibilityStorageKey, previous);
	      } catch {
	        // Best-effort cleanup only; the base-map toggle still needs to run.
	      }
	      return;
	    }
	    try {
	      const previous = readBooleanRecord(layerVisibilityStorageKey);
	      writeJsonStorage(layerVisibilityStorageKey, { ...previous, [layerId]: visible });
	    } catch {
	      // Best-effort persistence only; the layer toggle still needs to run.
	    }
	  }, [layerVisibilityStorageKey]);
	  const addLayerControlItem = (item: LayerControlItem) => {
	    layerControlItems.push({
	      ...item,
	      onToggle: (visible: boolean) => {
	        rememberLayerVisibility(item.id, visible);
	        return item.onToggle(visible);
	      }
	    });
	  };
  const addSimpleLayerControl = (
    id: string,
    label: string,
    group: LayerControlGroup,
    color: string,
    checked: boolean,
    onToggle: (visible: boolean) => void | Promise<void>,
    options: Partial<Pick<LayerControlItem, 'targetArea' | 'detail' | 'count' | 'disabled' | 'disabledReason' | 'presetControlled' | 'swatch' | 'portfolioStage' | 'legend' | 'legendGroup'>> = {}
  ) => addLayerControlItem({ id, label, group, color, checked, onToggle, ...options });

  addSimpleLayerControl('base:satellite', 'Satellite', 'Base Map', '#58a6ff', activeLayers.includes('satellite'), (visible) => setBaseMapLayer('satellite', visible), { presetControlled: false, swatch: 'square' });
  addSimpleLayerControl('base:terrain', 'Topographic', 'Base Map', '#22c55e', activeLayers.includes('terrain'), (visible) => setBaseMapLayer('terrain', visible), { presetControlled: false, swatch: 'square' });
  addSimpleLayerControl('base:geological', 'USGS geological', 'Base Map', '#f97316', activeLayers.includes('geological'), (visible) => setBaseMapLayer('geological', visible), { presetControlled: false, swatch: 'square' });
  addSimpleLayerControl('base:street', 'Street map', 'Base Map', '#94a3b8', activeLayers.includes('street'), (visible) => setBaseMapLayer('street', visible), { presetControlled: false, swatch: 'square' });
  addSimpleLayerControl('base:results', 'Analysis results', 'Base Map', '#facc15', showAnalysisResults, setResultsLayerVisible, { presetControlled: false });

  projectStaticRasters.forEach((raster) => {
    addSimpleLayerControl(
      `static:${raster.id}`,
      raster.displayName,
      raster.category === 'geophysics' ? 'Geophysics' : 'Reference',
      '#38bdf8',
      Boolean(staticRasterVisibility[raster.id]),
      (visible) => handleStaticRasterToggle(raster.id, visible),
      { detail: raster.description || raster.source, swatch: 'gradient' }
    );
  });

  if (selectedProject?.project_boundary) {
    addSimpleLayerControl(
      'project:boundary',
      'Project boundary',
      'Filing',
      '#58a6ff',
      showProjectBoundary,
      setShowProjectBoundary,
      { swatch: 'dash' }
    );
  }

  projectGeoJsonLayersForProject.forEach((layerDef) => {
    const displayColor = projectGeoJsonDisplayColorById[layerDef.id] || layerDef.color;
    addSimpleLayerControl(
      `geojson:${layerDef.id}`,
      layerDef.label,
      layerDef.group || 'Targets',
      displayColor,
      Boolean(projectGeoJsonVisibility[layerDef.id]),
      (visible) => setProjectGeoJsonVisibility((prev) => ({ ...prev, [layerDef.id]: visible })),
      { targetArea: layerDef.targetArea, detail: layerDef.detail, swatch: layerDef.dashed ? 'dash' : 'square', portfolioStage: layerDef.portfolioStage }
    );
  });

  if (mineralMapData?.mineral_layers) {
    Object.keys(mineralMapData.mineral_layers).forEach((mineralType) => {
      addSimpleLayerControl(
        `mineral:${mineralType}`,
        `${mineralType} model`,
        'Model Output',
        '#8b5cf6',
        activeMineralLayers.includes(mineralType),
        () => toggleMineralLayer(mineralType),
        { detail: 'Generated mineral map' }
      );
    });
  }

  if (selectedProject?.id === 'project-canada-nl') {
    // Polygon / footprint layers (Active Claims, Coming Open, Exclusions, Open Drilled
    // Districts, Airborne Survey Index) are registered in mapLayerRegistry and surface
    // via the projectGeoJsonLayersForProject loop above. These are the hand-coded
    // point / line / raster layers.
    addSimpleLayerControl('nl:mods-occurrences', 'Mineral Occurrences (MODS)', 'Evidence', '#9ca3af', nlModsOccurrencesVisible, setNlModsOccurrencesVisible);
    addSimpleLayerControl('nl:drill-collars', 'Drill Collars (all)', 'Drilling', '#1e3a8a', nlDrillCollarsVisible, setNlDrillCollarsVisible);
    addSimpleLayerControl('nl:open-drill-collars', 'Open Drill Collars', 'Targets', '#22c55e', nlOpenDrillCollarsVisible, setNlOpenDrillCollarsVisible);
    addSimpleLayerControl('nl:open-occurrences', 'Open Occurrences', 'Targets', '#16a34a', nlOpenOccurrencesVisible, setNlOpenOccurrencesVisible);
    addSimpleLayerControl('nl:open-geochem-anomalies', 'Open Geochem Anomalies', 'Geochemistry', '#d946ef', nlOpenGeochemAnomaliesVisible, setNlOpenGeochemAnomaliesVisible);
    addSimpleLayerControl('nl:faults', 'Faults', 'Geology', '#334155', nlFaultsVisible, setNlFaultsVisible);
    addSimpleLayerControl('nl:magnetics-nfl', 'Magnetics — Newfoundland (Residual)', 'Geophysics', '#14b8a6', nlMagneticsNflVisible, setNlMagneticsNflVisible, { swatch: 'gradient' });
    addSimpleLayerControl('nl:magnetics-labrador', 'Magnetics — Labrador (Residual)', 'Geophysics', '#0ea5a4', nlMagneticsLabradorVisible, setNlMagneticsLabradorVisible, { swatch: 'gradient' });
  }

  if (selectedProject?.id === 'project-gtk-finland') {
    FINLAND_EXPLORATION_PERMIT_STATUS_ORDER.forEach((statusKey) => {
      const visual = FINLAND_EXPLORATION_PERMIT_VISUALS[statusKey];
      addSimpleLayerControl(
        `finland:exploration-permits-${statusKey}`,
        visual.label,
        'Tenure',
        visual.color,
        finlandExplorationPermitStatusVisible[statusKey],
        (visible) => setFinlandExplorationPermitStatusKeyVisible(statusKey, visible),
        {
          count: visual.count,
          detail: visual.detail,
          swatch: visual.swatch,
          legend: FINLAND_EXPLORATION_PERMIT_LEGEND,
          legendGroup: 'finland-exploration-permits',
        }
      );
    });
    addSimpleLayerControl('finland:reservations', 'Reservations (TUKES 2026-07-21)', 'Tenure', '#f59e0b', finlandReservationsVisible, setFinlandReservationsVisible, { count: '107', swatch: 'dash' });
    addSimpleLayerControl('finland:mining-areas', 'Mining areas', 'Tenure', '#22c55e', finlandMiningAreasVisible, setFinlandMiningAreasVisible, { count: '41', swatch: 'square' });
    addSimpleLayerControl('finland:expired-claims', 'Expired claims', 'Tenure', '#64748b', finlandExpiredClaimsVisible, setFinlandExpiredClaimsVisible, { count: '12k' });
    addSimpleLayerControl('finland:natura', 'Natura 2000', 'Constraints', '#a855f7', finlandNaturaVisible, setFinlandNaturaVisible, { count: '1,642', swatch: 'square' });
    addSimpleLayerControl('finland:mineral-deposits', 'Mineral deposits', 'Evidence', '#facc15', finlandMineralDepositsVisible, setFinlandMineralDepositsVisible, { count: '1,090' });
    addSimpleLayerControl('finland:drillholes', 'Drillholes', 'Drilling', '#38bdf8', finlandDrillholesVisible, setFinlandDrillholesVisible, { count: '34k' });
    addSimpleLayerControl('finland:drill-clusters', 'Drill clusters', 'Drilling', '#a855f7', finlandDrillClustersVisible, setFinlandDrillClustersVisible, { detail: 'Review status colors', legend: DRILL_CLUSTER_REVIEW_LEGEND });
    addSimpleLayerControl('finland:till-samples', 'Till samples', 'Geochemistry', '#fb923c', finlandTillSamplesVisible, setFinlandTillSamplesVisible, { count: '753k' });
    addSimpleLayerControl('finland:till-gold', 'Au assays', 'Geochemistry', '#ef4444', finlandTillGoldVisible, setFinlandTillGoldVisible, { count: '47k' });
    addSimpleLayerControl('finland:till-basemetals', 'Cu-Ni-Co-Zn', 'Geochemistry', '#92400e', finlandTillBasemetalsVisible, setFinlandTillBasemetalsVisible, { count: '82k' });
    addSimpleLayerControl('finland:targeting-till', 'Targeting till', 'Geochemistry', '#4169e1', finlandTargetingTillVisible, setFinlandTargetingTillVisible, { count: '385k' });
    addSimpleLayerControl('finland:stream-sediments', 'Stream sediments', 'Geochemistry', '#22c55e', finlandStreamSedimentsVisible, setFinlandStreamSedimentsVisible, { count: '70k' });
    addSimpleLayerControl('finland:rock-samples', 'Rock samples', 'Geochemistry', '#f97316', finlandRockSamplesVisible, setFinlandRockSamplesVisible);
    addSimpleLayerControl('finland:ore-boulders', 'Ore boulders', 'Evidence', '#ff5722', finlandOreBoulderVisible, setFinlandOreBoulderVisible, { count: '31k', swatch: 'square' });
    addSimpleLayerControl('finland:aster-alteration', 'ASTER alteration', 'Evidence', '#ff5722', finlandAsterAlterationVisible, setFinlandAsterAlterationVisible, { count: '5.7k' });
    addSimpleLayerControl('finland:fodd', 'FODD Finland', 'Reference', '#e91e63', finlandFoddDepositsVisible, setFinlandFoddDepositsVisible, { count: '470' });
    addSimpleLayerControl('finland:fodd-sweden', 'FODD Sweden', 'Reference', '#2196f3', finlandFoddSwedenVisible, setFinlandFoddSwedenVisible, { count: '1.1k' });
    addSimpleLayerControl('finland:fodd-metallogenic', 'FODD metallogenic', 'Reference', '#e91e63', finlandFoddMetallogenicVisible, setFinlandFoddMetallogenicVisible, { count: '200', swatch: 'dash' });
    addSimpleLayerControl('finland:egdi-crm', 'EGDI CRM', 'Reference', '#00e676', finlandEgdiCrmVisible, setFinlandEgdiCrmVisible, { count: '1.5k' });
    addSimpleLayerControl('finland:magnetic', 'GTK magnetic', 'Geophysics', '#ef4444', finlandMagneticVisible, setFinlandMagneticVisible, { swatch: 'gradient' });
    addSimpleLayerControl('finland:em-resistivity', 'EM resistivity', 'Geophysics', '#14b8a6', finlandEMResistivityVisible, setFinlandEMResistivityVisible, { swatch: 'gradient' });
    addSimpleLayerControl('finland:radiometric', 'Radiometric ternary', 'Geophysics', '#facc15', finlandRadiometricTernaryVisible, setFinlandRadiometricTernaryVisible, { swatch: 'gradient' });
    addSimpleLayerControl('finland:gold-targets', 'Gold targets', 'Targets', '#facc15', finlandGoldTargetsVisible, setFinlandGoldTargetsVisible);
    addSimpleLayerControl('finland:copper-targets', 'Copper targets', 'Targets', '#f97316', finlandCopperTargetsVisible, setFinlandCopperTargetsVisible);
    addSimpleLayerControl('finland:staking-priorities', 'Staking priorities', 'Targets', '#22c55e', finlandStakingPrioritiesVisible, setFinlandStakingPrioritiesVisible);
	    addSimpleLayerControl('finland:pge-targets', 'PGE targets', 'Targets', '#22d3ee', finlandPGETargetsVisible, setFinlandPGETargetsVisible, { targetArea: 'PGE targets' });
    addSimpleLayerControl('finland:koitelainen', 'Koitelainen staking', 'Targets', '#2196f3', finlandKoitelainenStakingVisible, setFinlandKoitelainenStakingVisible);
    addSimpleLayerControl('finland:perakorpi-ti', 'Perakorpi Ti staking', 'Targets', '#14b8a6', finlandPerakorpiTiStakingVisible, setFinlandPerakorpiTiStakingVisible);
    addSimpleLayerControl('finland:makara', 'Makara staking', 'Targets', '#22c55e', finlandMakaraStakingVisible, setFinlandMakaraStakingVisible);
    addSimpleLayerControl('finland:makara-till-au', 'Makara till Au', 'Geochemistry', '#facc15', finlandMakaraTillAuVisible, setFinlandMakaraTillAuVisible);
    addSimpleLayerControl('finland:koivuniemi-graphite', 'Koivuniemi — Graphite target', 'Targets', '#64748b', finlandKoivuniemiGraphiteVisible, setFinlandKoivuniemiGraphiteVisible, { targetArea: 'Koivuniemi' });
	    addSimpleLayerControl('finland:koivuniemi-consultant-original-2026-05-20', 'Koivuniemi — Consultant design (original)', 'Targets', '#94a3b8', finlandKoivuniemiConsultantOriginalVisible, setFinlandKoivuniemiConsultantOriginalVisible, { targetArea: 'Koivuniemi', detail: '5/20/26 consultant cut', swatch: 'dash' });
    addSimpleLayerControl('finland:koivuniemi-consultant-mod-2026-05-22', 'Koivuniemi — Submitted filing polygon', 'Targets', '#38bdf8', finlandKoivuniemiConsultantModVisible, setFinlandKoivuniemiConsultantModVisible, { targetArea: 'Koivuniemi', detail: '5/22/26 filing, PH-7/PH-17 offset (not the registered outline)', swatch: 'dash' });
    addSimpleLayerControl('finland:koivuniemi-va2026-0039-registered', 'Koivuniemi — VA2026:0039 granted', 'Targets', '#22c55e', finlandKoivuniemiRegisteredVisible, setFinlandKoivuniemiRegisteredVisible, { targetArea: 'Koivuniemi', detail: 'Registered 2,156.55 ha, granted 2026-06-17' });
    addSimpleLayerControl('finland:koivuniemi-drill-collars', 'Koivuniemi — Camp drill collars', 'Targets', '#22c55e', finlandKoivuniemiDrillCollarsVisible, setFinlandKoivuniemiDrillCollarsVisible, { targetArea: 'Koivuniemi', detail: '44-hole camp + district; red = outside granted geometry' });
	    addSimpleLayerControl('finland:kokkoharju-review', 'Kokkoharju review', 'Targets', '#0ea5e9', finlandKokkoharjuReviewVisible, setFinlandKokkoharjuReviewVisible);
    addSimpleLayerControl('finland:kultanummi-gold', 'Kultanummi gold', 'Targets', '#facc15', finlandKultanummiGoldVisible, setFinlandKultanummiGoldVisible);
  }

  if (selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-sweden-uranium') {
    const sguGroupForLayer = (layerId: string): LayerControlGroup => {
      if (layerId.includes('permit')) return ['active_permits', 'pending_permits'].includes(layerId) ? 'Tenure' : 'Reference';
      if (layerId.startsWith('heritage')) return 'Constraints';
      if (layerId === 'boreholes' || layerId === 'brap_cu_results' || layerId === 'njugg_trench_overlay') return 'Drilling';
      if (layerId === 'gravity_bouguer_contours') return 'Geophysics';
      if (layerId === 'mineral_occurrences') return 'Evidence';
      return 'Reference';
    };
    SGU_VECTOR_LAYERS.forEach((layer) => {
      const detail = layer.id === 'boreholes' ? 'Nearby review colors' : undefined;
      const legend = layer.id === 'boreholes' ? BOREHOLE_REVIEW_LEGEND : undefined;
      addSimpleLayerControl(
        `sgu:${layer.id}`,
        layer.label,
        sguGroupForLayer(layer.id),
        layer.color,
        Boolean(sguLayerVisibility[layer.id]),
        (visible) => toggleSguLayer(layer.id, visible),
        {
          detail,
          legend,
          swatch: layer.id.includes('permit') || layer.id.startsWith('heritage') ? 'square' : layer.id === 'gravity_bouguer_contours' ? 'line' : 'circle'
        }
      );
    });
    addSimpleLayerControl('sgu:natura', 'Natura 2000', 'Constraints', '#8e44ad', naturaVisible, toggleNaturaLayer, { swatch: 'square' });
    addSimpleLayerControl('sgu:deposit-labels', 'Deposit labels', 'Operations', '#facc15', clusterLabelsVisible, toggleClusterLabels, { presetControlled: false, swatch: 'square' });
    addSimpleLayerControl('sgu:borehole-id-labels', 'Borehole ID labels', 'Operations', '#38bdf8', boreholeIdLabelsVisible, toggleBoreholeIdLabels, {
      disabled: !sguLayerVisibility['boreholes'],
      disabledReason: !sguLayerVisibility['boreholes'] ? 'Enable SGU Boreholes first' : undefined,
      presetControlled: false
    });
  }

  if (selectedProject?.id === 'project-sweden-uranium') {
    addSimpleLayerControl('uranium:known-deposits', 'Known uranium deposits', 'Evidence', '#dc143c', uraniumKnownDepositsVisible, setUraniumKnownDepositsVisible, { count: '11' });
    addSimpleLayerControl('uranium:ranked-targets', 'Ranked targets', 'Targets', '#22c55e', uraniumRankedTargetsVisible, setUraniumRankedTargetsVisible, { count: '50' });
    addSimpleLayerControl('uranium:palange', 'Palange polygons + holes', 'Targets', '#00d2ff', palangeVerificationVisible, setPalangeVerificationVisible, { swatch: 'dash', portfolioStage: 'Filed' });
    addSimpleLayerControl('uranium:occurrences', 'SGU U occurrences', 'Evidence', '#facc15', uraniumOccurrencesVisible, setUraniumOccurrencesVisible, { count: '254' });
    addSimpleLayerControl('uranium:alum-shale', 'Alum shale extent', 'Evidence', '#8b4513', alumShaleVisible, setAlumShaleVisible, { swatch: 'square' });
    addSimpleLayerControl('uranium:gamma-u', 'Gamma uranium', 'Geophysics', '#7ee787', radiometricUraniumVisible, setRadiometricUraniumVisible, { swatch: 'gradient' });
    addSimpleLayerControl('uranium:gamma-th', 'Gamma thorium', 'Geophysics', '#14b8a6', radiometricThoriumVisible, setRadiometricThoriumVisible, { swatch: 'gradient' });
  }

  if (selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') {
    const stakingTierIds = selectedProjectStakingBoundaryIds;
    const visibleStakingTierCount = stakingTierIds.filter((tierId) => stakingTierVisibility[tierId]).length;
    const stakingBoundaryCountryLabel = selectedProject.id === 'project-gtk-finland' ? 'Finland' : 'Sweden';
    addSimpleLayerControl(
      `${selectedProject.id}:staking-target-boundaries`,
      `${stakingBoundaryCountryLabel} staking boundaries`,
      'Targets',
      '#22c55e',
      visibleStakingTierCount > 0,
      setSelectedProjectStakingBoundariesVisible,
      { count: `${visibleStakingTierCount}/${stakingTierIds.length}`, swatch: 'square' }
    );
  }

  if (selectedProject?.id === 'project-sgu-sweden') {
    addSimpleLayerControl(
      'sweden:ropen-filed-polygon',
      'Ropen claimed polygon',
      'Targets',
      '#cddc39',
      Boolean(stakingTierVisibility['ropen_vms_east_extension']),
      (visible) => toggleStakingTier('ropen_vms_east_extension', visible),
      {
        targetArea: 'Ropen',
        detail: '10,547 ha v4, 70/70 Abelvattnet collars',
        swatch: 'dash',
        portfolioStage: 'Claimed'
      }
    );
    addSimpleLayerControl('sweden:koli-vms', 'Koli VMS targets', 'Targets', '#ef4444', koliVmsTargetsVisible, setKoliVmsTargetsVisible, { count: '8' });
    addSimpleLayerControl('sweden:ropen-permits', 'Ropen nearby claims', 'Tenure', '#b91c1c', ropenNearbyPermitsVisible, setRopenNearbyPermitsVisible, { count: '8', swatch: 'square' });
    addSimpleLayerControl('sweden:bergslagen-au', 'Bergslagen Au till', 'Geochemistry', '#ef4444', bergslagenTillAuVisible, setBergslagenTillAuVisible, { count: '2,454' });
    addSimpleLayerControl('sweden:bergslagen-te', 'Bergslagen Te till', 'Geochemistry', '#9b59b6', bergslagenTillTeVisible, setBergslagenTillTeVisible, { count: '1,923' });
    addSimpleLayerControl('sweden:drill-clusters', 'Drill clusters', 'Drilling', '#a855f7', swedenDrillClustersVisible, setSwedenDrillClustersVisible, { detail: 'Review status colors', legend: DRILL_CLUSTER_REVIEW_LEGEND });
    addSimpleLayerControl('sweden:singleton-clusters', 'Singleton drill clusters', 'Drilling', '#38bdf8', swedenSingletonsVisible, setSwedenSingletonsVisible, { detail: 'Rescue candidates' });
    addSimpleLayerControl('sweden:njugg-aeromag', 'Njugg aeromagnetic', 'Geophysics', '#e879f9', njuggAeromagLayerVisible, setNjuggAeromagLayerVisible, { swatch: 'gradient' });
    addSimpleLayerControl('sweden:njugg-density', 'Njugg density model', 'Geophysics', '#00bcd4', njuggDensityLayerVisible, setNjuggDensityLayerVisible, { swatch: 'gradient' });
    addSimpleLayerControl('sweden:njugg-susceptibility', 'Njugg susceptibility', 'Geophysics', '#ff5722', njuggMagneticLayerVisible, setNjuggMagneticLayerVisible, { swatch: 'gradient' });
    SWEDEN_NJUGG_STATIC_TARGET_LAYERS.forEach((layer) => {
      addSimpleLayerControl(
        layer.controlId,
        layer.label,
        'Targets',
        layer.color,
        Boolean(stakingTierVisibility[layer.id]),
        (visible) => toggleStakingTier(layer.id, visible),
        {
          targetArea: layer.targetArea,
          detail: layer.detail,
          swatch: 'square',
          portfolioStage: layer.portfolioStage,
        }
      );
    });
    addSimpleLayerControl('sweden:kiuri-holes', 'Kiuri drillholes', 'Drilling', '#00bcd4', kiuriDrillholesVisible, setKiuriDrillholesVisible);
    addSimpleLayerControl('sweden:kiuri-district-holes', 'Kiuri district holes', 'Drilling', '#ef4444', kiuriDistrictHolesVisible, setKiuriDistrictHolesVisible);
    addSimpleLayerControl('sweden:kiuri-samples', 'Kiuri grab samples', 'Evidence', '#00bcd4', kiuriGrabSamplesVisible, setKiuriGrabSamplesVisible);
	    addSimpleLayerControl('sweden:kiuri-applications', 'All Kiuri grant blocks', 'Targets', '#e2e8f0', kiuriApplicationPolygonsVisible, setKiuriApplicationPolygonsVisible, {
      count: `${KIURI_GRANT_BLOCKS.filter((block) => kiuriApplicationPolygonVisibility[block.id]).length}/3`,
      detail: 'Toggle all granted blocks',
      swatch: 'dash',
      portfolioStage: 'Claimed'
    });
    KIURI_GRANT_BLOCKS.forEach((block) => {
      addSimpleLayerControl(
        `sweden:kiuri-${block.id}`,
        `${block.name} ${block.permitId}`,
        'Targets',
        block.color,
        Boolean(kiuriApplicationPolygonVisibility[block.id]),
        (visible) => setKiuriApplicationPolygonBlockVisible(block.id, visible),
        {
          targetArea: 'Kiuri grant blocks',
          detail: `${block.dnr} / ${block.area} / former ${block.formerName}`,
          swatch: 'dash',
          portfolioStage: 'Claimed'
        }
      );
    });
    addSimpleLayerControl(
      'sweden:granudden-draft',
      `${GRANUDDEN_DRAFT_LAYER.name} — not filed`,
      'Targets',
      GRANUDDEN_DRAFT_LAYER.color,
      granuddenDraftVisible,
      setGranuddenDraftVisible,
      {
        targetArea: 'Kiuri District bolt-on',
        detail: `${GRANUDDEN_DRAFT_LAYER.area} / 0% lake / 10 Rio samples + historic target`,
        swatch: 'dash',
        portfolioStage: 'Open'
      }
    );
    const saxeControlMeta: Record<string, {
      group: LayerControlGroup;
      color: string;
      swatch?: LayerControlItem['swatch'];
      detail?: string;
      portfolioStage?: PortfolioStage;
      count?: string;
    }> = {
      saxe_filed: {
        group: 'Targets',
        color: '#22c55e',
        swatch: 'square',
        detail: 'Saxe nr 1 / BS 200-988-2026',
        portfolioStage: 'Filed',
      },
      saxe_v6_design: {
        group: 'Targets',
        color: '#9ca3af',
        swatch: 'dash',
        detail: 'Superseded 38,355 ha design',
        portfolioStage: 'Monitor',
      },
      saxe_unfiled_blocks: {
        group: 'Targets',
        color: '#0ea5e9',
        swatch: 'square',
        detail: '4 unfiled blocks / re-file candidates',
        portfolioStage: 'Open',
      },
      saxe_till_markers: {
        group: 'Geochemistry',
        color: '#facc15',
        detail: '43 of 62 samples inside filed polygon',
        count: '62',
      },
      saxe_upice_corridors: {
        group: 'Targets',
        color: '#a855f7',
        swatch: 'dash',
        detail: 'C1/C2/C3 source-tracing corridors',
        portfolioStage: 'Reviewed',
      },
      saxe_geophys_targets: {
        group: 'Targets',
        color: '#ef4444',
        detail: 'Ranked geophysics targets',
        portfolioStage: 'Reviewed',
      },
    };
    SAXE_VECTOR_LAYERS.forEach((layer) => {
      const meta = saxeControlMeta[layer.id] || { group: 'Targets', color: '#22c55e' };
      addSimpleLayerControl(
        `sweden:${layer.id}`,
        layer.label,
        meta.group,
        meta.color,
        Boolean(saxeLayerVisibility[layer.id]),
        (visible) => toggleSaxeLayer(layer.id, visible),
        {
          targetArea: meta.group === 'Targets' ? 'Saxe — Deep Dive (2026-05)' : undefined,
          detail: meta.detail,
          count: meta.count,
          swatch: meta.swatch,
          portfolioStage: meta.portfolioStage,
        }
      );
    });
    SAXE_RASTER_LAYERS.forEach((layer) => {
      addSimpleLayerControl(
        `sweden:${layer.id}`,
        layer.label,
        'Geophysics',
        '#9c27b0',
        Boolean(saxeRasterVisibility[layer.id]),
        (visible) => toggleSaxeRaster(layer.id, visible),
        {
          detail: 'FECA 2016 Saxe raster',
          swatch: 'gradient',
        }
      );
    });
  }

  if (selectedProject?.id === 'project-peru-wide') {
    PERU_VECTOR_LAYERS.forEach((layer) => {
      const staticCategories = ['analysis', 'geochemistry_analysis', 'surface_samples', 'reference_databases'];
      const isStatic = staticCategories.includes(layer.category);
      const group: LayerControlGroup =
        layer.category === 'permits' ? 'Tenure' :
        layer.category === 'analysis' ? 'Targets' :
        layer.category.includes('geochemistry') || layer.category === 'surface_samples' ? 'Geochemistry' :
        layer.category === 'geophysics' ? 'Geophysics' :
        layer.category === 'geology' || layer.category === 'reference_databases' ? 'Reference' :
        'Evidence';
      addSimpleLayerControl(
        `peru:${layer.id}`,
        layer.label,
        group,
        layer.color,
        Boolean(isStatic ? peruAnalysisVisibility[layer.id] : peruLayerVisibility[layer.id]),
        (visible) => isStatic ? togglePeruAnalysisLayer(layer.id, visible) : togglePeruLayer(layer.id, visible),
        { swatch: layer.category === 'permits' || layer.id.includes('cadastre') ? 'square' : 'circle' }
      );
    });
    addSimpleLayerControl('peru:drill-clusters', 'Peru drill clusters', 'Drilling', '#a855f7', peruDrillClustersVisible, setPeruDrillClustersVisible, { count: '13', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }

  if (selectedProject?.id === 'project-png-wide') {
    PNG_VECTOR_LAYERS.forEach((layer) => {
      const group: LayerControlGroup =
        layer.category === 'cadastre' ? 'Tenure' :
        layer.category === 'open_ground' ? 'Targets' :
        layer.category === 'deposits' ? 'Evidence' :
        layer.category === 'drilling' ? 'Drilling' :
        layer.category === 'constraints' ? 'Constraints' :
        'Reference';
      addSimpleLayerControl(
        `png:${layer.id}`,
        layer.label,
        group,
        layer.color,
        Boolean(pngLayerVisibility[layer.id]),
        (visible) => togglePngLayer(layer.id, visible),
        { swatch: layer.type === 'polygon' ? 'square' : 'circle' }
      );
    });
  }

  if (selectedProject?.id === 'project-yukon-wide') {
    YUKON_VECTOR_LAYERS.forEach((layer) => {
      const group: LayerControlGroup =
        layer.category === 'cadastre' ? 'Tenure' :
        layer.category === 'constraints' ? 'Constraints' :
        layer.category === 'open_ground' ? 'Targets' :
        layer.category === 'targets' ? 'Targets' :
        layer.category === 'deposits' ? 'Evidence' :
        layer.category === 'drilling' ? 'Drilling' :
        layer.category === 'geochemistry' ? 'Geochemistry' :
        layer.category === 'geophysics' ? 'Geophysics' :
        layer.category === 'reports' ? 'Reference' :
        'Reference';
      addSimpleLayerControl(
        `yukon:${layer.id}`,
        layer.label,
        group,
        layer.color,
        Boolean(yukonLayerVisibility[layer.id]),
        (visible) => toggleYukonLayer(layer.id, visible),
        { swatch: layer.type === 'line' ? 'line' : layer.type === 'point' ? 'circle' : 'square' }
      );
    });
  }

  if (selectedProject?.id === 'project-caujul') {
    CAUJUL_VECTOR_LAYERS.forEach((layer) => {
      const group: LayerControlGroup =
        layer.category === 'geology' || layer.category === 'alteration' ? 'Reference' :
        layer.category === 'targeting' ? 'Targets' :
        layer.category === 'geochemistry' ? 'Geochemistry' :
        'Evidence';
      addSimpleLayerControl(`caujul:${layer.id}`, layer.label, group, layer.color, Boolean(caujulLayerVisibility[layer.id]), (visible) => toggleCaujulLayer(layer.id, visible));
    });
  }

  if (selectedProject?.id === 'project-france-brgm') {
    FRANCE_VECTOR_LAYERS.forEach((layer) => {
      const group: LayerControlGroup = layer.category === 'permits' ? 'Tenure' : layer.category === 'geophysics' ? 'Geophysics' : 'Evidence';
      addSimpleLayerControl(`france:${layer.id}`, layer.label, group, layer.color, Boolean(franceLayerVisibility[layer.id]), (visible) => toggleFranceLayer(layer.id, visible), { swatch: layer.category === 'permits' ? 'square' : 'circle' });
    });
    addSimpleLayerControl('france:drill-clusters', 'France drill clusters', 'Drilling', '#a855f7', franceDrillClustersVisible, setFranceDrillClustersVisible, { count: '200', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }

  if (JURISDICTION_LAYER_CONFIG[selectedProject?.id || '']) {
    JURISDICTION_LAYER_CONFIG[selectedProject?.id || ''].layers.forEach((layer) => {
      const category = layer.category.toLowerCase();
      const group: LayerControlGroup =
        category.includes('tenure') ? 'Tenure' :
        category.includes('risk') || category.includes('constraint') ? 'Constraints' :
        category.includes('drill') ? 'Drilling' :
        category.includes('geochem') ? 'Geochemistry' :
        category.includes('geophys') ? 'Geophysics' :
        category.includes('curated') || category.includes('target') ? 'Targets' :
        category.includes('report') ? 'Evidence' :
        category.includes('raw') || category.includes('reference') || category.includes('geology') ? 'Reference' :
        'Evidence';
      addSimpleLayerControl(
        `jurisdiction:${layer.id}`,
        layer.label,
        group,
        layer.color,
        Boolean(jurisdictionLayerVisibility[layer.id]),
        (visible) => toggleJurisdictionLayer(layer, visible),
        { disabled: Boolean(layer.disabledReason), disabledReason: layer.disabledReason, swatch: category.includes('tenure') || category.includes('risk') || category.includes('constraint') || category.includes('geophys') || category.includes('report') ? 'square' : 'circle' }
      );
    });
  }

  if (selectedProject?.id === 'project-norway-ngu') {
    addSimpleLayerControl('norway:drill-clusters', 'Norway drill clusters', 'Drilling', '#a855f7', norwayDrillClustersVisible, setNorwayDrillClustersVisible, { count: '49', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }
  if (selectedProject?.id === 'project-ireland-gsi') {
    addSimpleLayerControl('ireland:drill-clusters', 'Ireland drill clusters', 'Drilling', '#a855f7', irelandDrillClustersVisible, setIrelandDrillClustersVisible, { count: '360', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }
  if (selectedProject?.id === 'project-canada-ontario') {
    addSimpleLayerControl('ontario:drill-clusters', 'Ontario drill clusters', 'Drilling', '#a855f7', ontarioDrillClustersVisible, setOntarioDrillClustersVisible, { count: '3', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }
  if (selectedProject?.id === 'project-australia-wa') {
    addSimpleLayerControl('wa:drill-clusters', 'Western Australia drill clusters', 'Drilling', '#a855f7', australiaDrillClustersVisible, setAustraliaDrillClustersVisible, { count: '4,281', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }
  if (selectedProject?.id === 'project-canada-quebec') {
    addSimpleLayerControl('quebec:drill-clusters', 'Quebec drill clusters', 'Drilling', '#a855f7', quebecDrillClustersVisible, setQuebecDrillClustersVisible, { count: '451', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }
  if (selectedProject?.id === 'project-colombia-sgc') {
    addSimpleLayerControl('colombia:drill-clusters', 'Colombia drill clusters', 'Drilling', '#a855f7', colombiaDrillClustersVisible, setColombiaDrillClustersVisible, { count: '71', legend: DRILL_CLUSTER_REVIEW_LEGEND });
  }

  const layerControlIdsByGroup = (groups: LayerControlGroup[]) =>
    layerControlItems
      .filter((item) => groups.includes(item.group) && item.presetControlled !== false && !item.disabled)
      .map((item) => item.id);
  const targetPresetGeoJsonIds = new Set(
    projectGeoJsonLayersForProject
      .filter((layerDef) => layerDef.targetPreset !== false)
      .map((layerDef) => `geojson:${layerDef.id}`)
  );
  const targetPolygonLayerIds = layerControlItems
    .filter((item) => targetPresetGeoJsonIds.has(item.id) && item.presetControlled !== false && !item.disabled)
    .map((item) => item.id);
	  const layerControlPresets: LayerPreset[] = [
	    { id: 'preset-filing', label: 'Filing', layerIds: layerControlIdsByGroup(['Filing', 'Tenure', 'Constraints']) },
	    { id: 'preset-evidence', label: 'Evidence', layerIds: layerControlIdsByGroup(['Evidence', 'Drilling', 'Geochemistry', 'Reference']) },
	    { id: 'preset-targets', label: 'Targets', layerIds: targetPolygonLayerIds },
	    { id: 'preset-geophysics', label: 'Geophysics', layerIds: layerControlIdsByGroup(['Geophysics']) }
	  ].filter((preset) => preset.layerIds.length > 0);
	  const restoredLayerIdsRef = useRef<globalThis.Map<string, globalThis.Set<string>>>(new globalThis.Map());
	  const lastRestoredProjectKeyRef = useRef<string | null>(null);
	  const sharedUrlRestoreRef = useRef<string | null>(null);
	  const layerControlItemsRef = useRef<LayerControlItem[]>([]);
	  const layerControlIdsSignature = layerControlItems.map((item) => item.id).join('|');
	  const layerControlStateSignature = layerControlItems
	    .map((item) => `${item.id}:${item.checked ? '1' : '0'}:${item.disabled ? '1' : '0'}`)
	    .join('|');
	  layerControlItemsRef.current = layerControlItems;

	  const getViewportSnapshot = useCallback(() => {
	    const view = mapInstanceRef.current?.getView();
	    const center = view?.getCenter();
	    const [lon, lat] = center ? toLonLat(center) : [null, null];
	    const zoom = view?.getZoom();
	    return {
	      lon: typeof lon === 'number' && Number.isFinite(lon) ? lon : null,
	      lat: typeof lat === 'number' && Number.isFinite(lat) ? lat : null,
	      zoom: typeof zoom === 'number' && Number.isFinite(zoom) ? zoom : null,
	    };
	  }, []);

	  const captureActionSearchRestoreState = useCallback((): ActionSearchRestoreState => ({
	    projectId: selectedProjectRef.current?.id || selectedProject?.id || null,
	    viewport: getViewportSnapshot(),
	    activeLayers: [...activeLayers],
	    projectGeoJsonVisibility: { ...projectGeoJsonVisibility },
	    projectGeoJsonExpanded,
	    projectGeoJsonPolygonOnly,
	    inspectionRecord,
	    actionSearchInput,
	  }), [
	    actionSearchInput,
	    activeLayers,
	    getViewportSnapshot,
	    inspectionRecord,
	    projectGeoJsonExpanded,
	    projectGeoJsonPolygonOnly,
	    projectGeoJsonVisibility,
	    selectedProject?.id,
	  ]);

	  const buildShareUrl = useCallback(() => {
	    const url = new URL(window.location.href);
	    const viewport = getViewportSnapshot();
	    if (selectedProject?.id) {
	      url.searchParams.set('project', selectedProject.id);
	    } else {
	      url.searchParams.delete('project');
	    }
	    if (viewport.lat !== null && viewport.lon !== null) {
	      url.searchParams.set('lat', viewport.lat.toFixed(5));
	      url.searchParams.set('lon', viewport.lon.toFixed(5));
	    }
	    if (viewport.zoom !== null) {
	      url.searchParams.set('zoom', viewport.zoom.toFixed(2));
	    }
	    const activeLayerIds = layerControlItemsRef.current
	      .filter((item) => item.checked)
	      .map((item) => item.id);
	    if (activeLayerIds.length > 0) {
	      url.searchParams.set('layers', activeLayerIds.join(','));
	    } else {
	      url.searchParams.delete('layers');
	    }
	    url.searchParams.delete('smoke');
	    return url.toString();
	  }, [getViewportSnapshot, selectedProject?.id]);

	  const fitActionSearchExtent = useCallback((extent?: [number, number, number, number] | null, maxZoom = 13.5) => {
	    const map = mapInstanceRef.current;
	    if (!map || !extent || extent.some((value) => !Number.isFinite(value))) {
	      return false;
	    }
	    map.getView().fit(extent, {
	      padding: [84, 420, 96, 84],
	      maxZoom,
	      duration: 420,
	    });
	    map.updateSize();
	    return true;
	  }, []);

	  const getProjectGeoJsonExtent = useCallback((layerIds: string[]) => {
	    let extent: [number, number, number, number] | null = null;
	    layerIds.forEach((layerId) => {
	      const layer = projectGeoJsonLayerRefs.current.get(layerId.replace(/^geojson:/, ''));
	      const source = getRawLayerSource(layer);
	      source?.getFeatures().forEach((feature) => {
	        const candidate = feature.getGeometry()?.getExtent() as [number, number, number, number] | undefined;
	        extent = mergeExtents(extent, candidate || null);
	      });
	    });
	    return extent;
	  }, []);

	  const getLayerSourceExtent = useCallback((controlId: string) => {
	    if (controlId.startsWith('geojson:')) {
	      return getProjectGeoJsonExtent([controlId]);
	    }
	    if (controlId.startsWith('sgu:')) {
	      const layer = sguVectorLayersRef.current.get(controlId.replace(/^sgu:/, ''));
	      const source = getRawLayerSource(layer);
	      return source ? mergeExtents(null, source.getExtent() as [number, number, number, number]) : null;
	    }
	    return null;
	  }, [getProjectGeoJsonExtent]);

	  const getFirstProjectGeoJsonFeature = useCallback((layerIds: string[]) => {
	    for (const layerId of layerIds) {
	      const normalizedId = layerId.replace(/^geojson:/, '');
	      const layer = projectGeoJsonLayerRefs.current.get(normalizedId);
	      const feature = getRawLayerSource(layer)?.getFeatures()[0];
	      if (feature && layer) {
	        return { feature, layer };
	      }
	    }
	    return null;
	  }, []);

	  const openInspectionForFeature = useCallback((feature: any, layer: any) => {
	    const coordinate = getGeometryFocusCoordinate(feature?.getGeometry?.());
	    if (!coordinate) {
	      return false;
	    }
	    const lonLat = toLonLat(coordinate) as [number, number];
	    setInspectionRecord(buildInspectionRecord(feature, layer, lonLat));
	    return true;
	  }, [buildInspectionRecord]);

	  const openActionSearchSummaryDrawer = useCallback((result: ActionSearchResult) => {
	    const viewport = getViewportSnapshot();
	    const lon = result.lonLat?.[0] ?? viewport.lon ?? 0;
	    const lat = result.lonLat?.[1] ?? viewport.lat ?? 0;
	    const enabledLayerCount = result.layerIds.length;
	    setInspectionRecord({
	      id: result.id,
	      title: result.label,
	      subtitle: result.subtitle || result.kind,
	      coordinates: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
	      layerLabel: result.kind,
	      source: 'Global map search',
	      portfolioStage: result.portfolioStage,
	      portfolioStageDefinition: result.portfolioStage ? PORTFOLIO_STAGE_DEFINITIONS[result.portfolioStage] : undefined,
	      nextAction: result.portfolioStage ? PORTFOLIO_STAGE_DEFINITIONS[result.portfolioStage].description : undefined,
	      rows: result.rows || [
	        { label: 'Result type', value: result.kind },
	        { label: 'Enabled layers', value: enabledLayerCount.toLocaleString() },
	      ],
	    });
	  }, [getViewportSnapshot]);

	  const enableActionSearchLayers = useCallback(async (layerIds: string[]) => {
	    const uniqueLayerIds = Array.from(new Set(layerIds.filter(Boolean)));
	    for (const layerId of uniqueLayerIds) {
	      const item = layerControlItemsRef.current.find((candidate) => candidate.id === layerId);
	      if (!item || item.disabled || item.checked) {
	        continue;
	      }
	      await item.onToggle(true);
	    }
	    await waitForMapQaFrame(260);
	  }, []);

	    const waitForProjectGeoJsonLayers = useCallback(async (layerIds: string[], timeoutMs = 3600) => {
	    const normalizedIds = Array.from(new Set(layerIds.map((layerId) => layerId.replace(/^geojson:/, ''))));
	    if (normalizedIds.length === 0) {
	      return;
	    }
	    const started = performance.now();
	    while (performance.now() - started < timeoutMs) {
	      const ready = normalizedIds.every((layerId) => {
	        const source = getRawLayerSource(projectGeoJsonLayerRefs.current.get(layerId));
	        return Boolean(source && source.getFeatures().length > 0);
	      });
	      if (ready) {
	        return;
	      }
	      await waitForMapQaFrame(120);
	    }
	  }, []);

	  const waitForSelectedProject = useCallback(async (projectId: string, timeoutMs = 4200) => {
	    const started = performance.now();
	    while (performance.now() - started < timeoutMs) {
	      if (selectedProjectRef.current?.id === projectId) {
	        return true;
	      }
	      await waitForMapQaFrame(120);
	    }
	    return selectedProjectRef.current?.id === projectId;
	  }, []);

	  const waitForLayerControlItems = useCallback(async (layerIds: string[], timeoutMs = 4200) => {
	    const uniqueLayerIds = Array.from(new Set(layerIds.filter(Boolean)));
	    if (uniqueLayerIds.length === 0) {
	      return true;
	    }
	    const started = performance.now();
	    while (performance.now() - started < timeoutMs) {
	      const found = uniqueLayerIds.every((layerId) =>
	        layerControlItemsRef.current.some((item) => item.id === layerId)
	      );
	      if (found) {
	        return true;
	      }
	      await waitForMapQaFrame(120);
	    }
	    return uniqueLayerIds.every((layerId) =>
	      layerControlItemsRef.current.some((item) => item.id === layerId)
	    );
	  }, []);

	  const restoreActionSearchState = useCallback(async () => {
	    const restore = actionSearchRestoreRef.current;
	    if (!restore) {
	      return;
	    }
	    actionSearchFocusUntilRef.current = 0;

	    closePopup();
	    const restoreProjectId = restore.projectId || null;
	    if (restoreProjectId && selectedProjectRef.current?.id !== restoreProjectId && onProjectSelect) {
	      await onProjectSelect(restoreProjectId);
	      await waitForSelectedProject(restoreProjectId);
	      await waitForMapQaFrame(280);
	    }

	    setActionSearchInput(restore.actionSearchInput || '');
	    setActiveLayers(normalizeActiveLayers(restore.activeLayers));
	    setProjectGeoJsonExpanded(restore.projectGeoJsonExpanded);
	    setProjectGeoJsonPolygonOnly(restore.projectGeoJsonPolygonOnly);
	    setProjectGeoJsonVisibility({ ...restore.projectGeoJsonVisibility });
	    setInspectionRecord(restore.inspectionRecord);

	    const map = mapInstanceRef.current;
	    if (
	      map &&
	      restore.viewport.lon !== null &&
	      restore.viewport.lat !== null &&
	      Number.isFinite(restore.viewport.lon) &&
	      Number.isFinite(restore.viewport.lat)
	    ) {
	      const view = map.getView();
	      view.setCenter(fromLonLat([restore.viewport.lon, restore.viewport.lat]));
	      if (restore.viewport.zoom !== null && Number.isFinite(restore.viewport.zoom)) {
	        view.setZoom(restore.viewport.zoom);
	      }
	      map.updateSize();
	      map.renderSync();
	    }

	    actionSearchRestoreRef.current = null;
	    setActionSearchRestoreAvailable(false);
	  }, [closePopup, onProjectSelect, waitForSelectedProject]);

	  const actionSearchOptions = useMemo<ActionSearchResult[]>(() => {
	    const query = actionSearchInput.trim();
	    if (query.length < 2) {
	      return [];
	    }

	    const currentLayerControlItems = layerControlItemsRef.current;
	    const options: ActionSearchResult[] = [];
	    const seen = new Set<string>();
	    const addOption = (option: ActionSearchResult) => {
	      if (seen.has(option.id)) {
	        return;
	      }
	      seen.add(option.id);
	      options.push(option);
	    };
	    const getProjectName = (projectId?: string | null) =>
	      (projectId ? availableProjectById.get(projectId)?.name : null) || projectId || 'Unknown project';
	    const getProjectLocation = (projectId?: string | null) =>
	      (projectId ? availableProjectById.get(projectId)?.location : null) || '';
	    const resolveProjectIdForCountry = (country?: string | null) => {
	      const normalizedCountry = (country || '').trim().toLowerCase();
	      if (!normalizedCountry) {
	        return undefined;
	      }
	      if (RANKED_TARGET_COUNTRY_PROJECT_ID[normalizedCountry]) {
	        return RANKED_TARGET_COUNTRY_PROJECT_ID[normalizedCountry];
	      }
	      return Array.from(availableProjectById.values()).find((project) => {
	        const text = `${project.name || ''} ${project.location || ''}`.toLowerCase();
	        return text.includes(normalizedCountry);
	      })?.id;
	    };
	    const formatRankedCountry = (country?: string | null) => {
	      const normalizedCountry = (country || '').trim();
	      return normalizedCountry ? normalizedCountry.charAt(0).toUpperCase() + normalizedCountry.slice(1) : '';
	    };

	    availableProjectById.forEach((project) => {
	      const projectSearchText = buildActionSearchText([
	        project.id,
	        project.name,
	        project.location,
	      ]);
	      if (!matchesActionSearchQuery(projectSearchText, query)) {
	        return;
	      }
	      const lon = Number(project.coordinates?.lng);
	      const lat = Number(project.coordinates?.lat);
	      addOption({
	        id: `project:${project.id}`,
	        kind: 'Project',
	        label: project.name || project.id,
	        subtitle: [project.location, project.id].filter(Boolean).join(' · '),
	        color: '#7dd3fc',
	        layerIds: [],
	        projectId: project.id,
	        projectName: project.name || project.id,
	        lonLat: Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : undefined,
	        rows: [
	          { label: 'Project', value: project.name || project.id },
	          ...(project.location ? [{ label: 'Location', value: project.location }] : []),
	          { label: 'Project ID', value: project.id },
	        ],
	      });
	    });

	    actionSearchRankedTargets.forEach((target, index) => {
	      const label = formatInspectionValue(target.target_name) || formatInspectionValue(target.target_slug) || `Ranked target ${index + 1}`;
	      const country = formatRankedCountry(target.country);
	      const projectId = resolveProjectIdForCountry(target.country);
	      const projectName = projectId ? getProjectName(projectId) : country || 'Global database';
	      const lon = Number(target.longitude);
	      const lat = Number(target.latitude);
	      const tierLabel = formatInspectionValue(target.tier_label) || formatInspectionValue(target.tier);
	      const openGround = formatInspectionValue(target.open_ground);
	      addOption({
	        id: `ranked-target:${target.target_slug || target.id || `${label}:${index}`}`,
	        kind: 'Ranked Target',
	        label,
	        subtitle: [projectName, tierLabel, target.commodity, openGround].filter(Boolean).join(' · '),
	        color: RANKED_TARGET_TIER_COLOR[String(target.tier || '').toLowerCase()] || '#38bdf8',
	        layerIds: [],
	        projectId,
	        projectName,
	        lonLat: Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : undefined,
	        rows: [
	          ...(country ? [{ label: 'Country', value: country }] : []),
	          ...(tierLabel ? [{ label: 'Tier', value: tierLabel }] : []),
	          ...(target.rank_in_tier !== null && target.rank_in_tier !== undefined ? [{ label: 'Rank', value: `${target.rank_in_tier}` }] : []),
	          ...(target.commodity ? [{ label: 'Commodity', value: target.commodity }] : []),
	          ...(target.key_grade ? [{ label: 'Key grade', value: target.key_grade }] : []),
	          ...(target.conviction ? [{ label: 'Conviction', value: target.conviction }] : []),
	          ...(openGround ? [{ label: 'Open ground', value: openGround }] : []),
	          ...(target.claim_size_ha !== null && target.claim_size_ha !== undefined ? [{ label: 'Claim size', value: `${target.claim_size_ha} ha` }] : []),
	          ...(target.staking_action ? [{ label: 'Action', value: target.staking_action }] : []),
	          ...(target.blocker ? [{ label: 'Blocker', value: target.blocker }] : []),
	        ],
	        payload: target,
	      });
	    });

	    STATIC_GLOBAL_TARGET_LAYERS.forEach((layerDef) => {
	      const projectName = getProjectName(layerDef.projectId);
	      const projectLocation = getProjectLocation(layerDef.projectId);
	      const searchText = buildActionSearchText([
	        layerDef.id,
	        layerDef.controlId,
	        layerDef.label,
	        layerDef.targetArea,
	        layerDef.detail,
	        layerDef.portfolioStage,
	        projectName,
	        projectLocation,
	        ...(layerDef.aliases || []),
	      ]);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      addOption({
	        id: `layer:${layerDef.controlId}`,
	        kind: 'Layer',
	        label: layerDef.label,
	        subtitle: [projectName, layerDef.targetArea, layerDef.detail, layerDef.portfolioStage].filter(Boolean).join(' · '),
	        color: layerDef.color,
	        layerIds: [layerDef.controlId],
	        layerControlId: layerDef.controlId,
	        projectId: layerDef.projectId,
	        projectName,
	        portfolioStage: layerDef.portfolioStage,
	        lonLat: layerDef.lonLat,
	        rows: [
	          { label: 'Project', value: projectName },
	          { label: 'Layer ID', value: layerDef.controlId },
	          ...(layerDef.targetArea ? [{ label: 'Target area', value: layerDef.targetArea }] : []),
	          ...(layerDef.detail ? [{ label: 'Detail', value: layerDef.detail }] : []),
	          ...(layerDef.portfolioStage ? [{ label: 'Status', value: layerDef.portfolioStage }] : []),
	        ],
	      });
	    });

	    const globalTargetAreaLayers = new globalThis.Map<string, ProjectGeoJsonPolygonLayer[]>();
	    PROJECT_GEOJSON_POLYGON_LAYERS.forEach((layerDef) => {
	      const projectName = getProjectName(layerDef.projectId);
	      const projectLocation = getProjectLocation(layerDef.projectId);
	      const controlId = `geojson:${layerDef.id}`;
	      const searchText = buildActionSearchText([
	        layerDef.label,
	        layerDef.id,
	        layerDef.targetArea,
	        layerDef.detail,
	        layerDef.portfolioStage,
	        layerDef.confidence,
	        layerDef.nextAction,
	        layerDef.sourceLabel,
	        layerDef.projectId,
	        projectName,
	        projectLocation,
	      ]);
	      if (layerDef.targetArea) {
	        const areaKey = `${layerDef.projectId}:${layerDef.targetArea}`;
	        globalTargetAreaLayers.set(areaKey, [...(globalTargetAreaLayers.get(areaKey) || []), layerDef]);
	      }
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      addOption({
	        id: `global-layer:${layerDef.projectId}:${layerDef.id}`,
	        kind: 'Layer',
	        label: layerDef.label,
	        subtitle: [projectName, layerDef.targetArea, layerDef.detail, layerDef.portfolioStage].filter(Boolean).join(' · '),
	        color: layerDef.color,
	        layerIds: [controlId],
	        layerControlId: controlId,
	        projectId: layerDef.projectId,
	        projectName,
	        portfolioStage: layerDef.portfolioStage,
	        rows: [
	          { label: 'Project', value: projectName },
	          { label: 'Layer ID', value: layerDef.id },
	          ...(layerDef.targetArea ? [{ label: 'Target area', value: layerDef.targetArea }] : []),
	          ...(layerDef.detail ? [{ label: 'Detail', value: layerDef.detail }] : []),
	          ...(layerDef.portfolioStage ? [{ label: 'Status', value: layerDef.portfolioStage }] : []),
	          ...(layerDef.confidence ? [{ label: 'Confidence', value: layerDef.confidence }] : []),
	        ],
	      });
	    });

	    globalTargetAreaLayers.forEach((areaLayers, areaKey) => {
	      const [projectId, ...areaParts] = areaKey.split(':');
	      const areaName = areaParts.join(':');
	      const projectName = getProjectName(projectId);
	      const searchText = buildActionSearchText([
	        areaName,
	        projectId,
	        projectName,
	        getProjectLocation(projectId),
	        ...areaLayers.flatMap((layerDef) => [
	          layerDef.label,
	          layerDef.id,
	          layerDef.detail,
	          layerDef.portfolioStage,
	          layerDef.confidence,
	          layerDef.nextAction,
	        ]),
	      ]);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      const firstLayer = areaLayers[0];
	      const stage = areaLayers.find((layerDef) => layerDef.portfolioStage)?.portfolioStage;
	      addOption({
	        id: `global-target-area:${projectId}:${areaName}`,
	        kind: 'Target Area',
	        label: areaName,
	        subtitle: `${projectName} · ${areaLayers.length} target layer${areaLayers.length === 1 ? '' : 's'}${stage ? ` · ${stage}` : ''}`,
	        color: firstLayer?.color || '#facc15',
	        layerIds: areaLayers.map((layerDef) => `geojson:${layerDef.id}`),
	        projectId,
	        projectName,
	        portfolioStage: stage,
	        rows: [
	          { label: 'Project', value: projectName },
	          { label: 'Target area', value: areaName },
	          { label: 'Target layers', value: areaLayers.length.toLocaleString() },
	        ],
	      });
	    });

	    PORTFOLIO_STAGE_ORDER.forEach((stage) => {
	      const stageDefinition = PORTFOLIO_STAGE_DEFINITIONS[stage];
	      const stageItems = currentLayerControlItems.filter((item) => item.portfolioStage === stage && item.group !== 'Base Map' && !item.disabled);
	      if (stageItems.length === 0) {
	        return;
	      }
	      const searchText = buildActionSearchText([
	        stage,
	        stageDefinition.label,
	        stageDefinition.shortLabel,
	        stageDefinition.description,
	      ]);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      addOption({
	        id: `status:${stage}`,
	        kind: 'Status',
	        label: `${stageDefinition.label} portfolio`,
	        subtitle: `${stageItems.length} visible layer${stageItems.length === 1 ? '' : 's'} in ${selectedProject?.name || 'current project'}`,
	        color: stageDefinition.color,
	        layerIds: stageItems.map((item) => item.id),
	        projectId: selectedProject?.id,
	        projectName: selectedProject?.name,
	        portfolioStage: stage,
	        extent: getProjectGeoJsonExtent(stageItems.map((item) => item.id)) || undefined,
	        rows: [
	          { label: 'Portfolio status', value: stageDefinition.label },
	          { label: 'Layer count', value: stageItems.length.toLocaleString() },
	          { label: 'Meaning', value: stageDefinition.description },
	        ],
	      });
	    });

	    const targetAreaItems = new globalThis.Map<string, LayerControlItem[]>();
	    currentLayerControlItems.forEach((item) => {
	      if (item.group !== 'Targets' || !item.targetArea || item.disabled) {
	        return;
	      }
	      const current = targetAreaItems.get(item.targetArea) || [];
	      current.push(item);
	      targetAreaItems.set(item.targetArea, current);
	    });
	    targetAreaItems.forEach((areaItems, areaName) => {
	      const searchText = buildActionSearchText([
	        areaName,
	        ...areaItems.flatMap((item) => [item.label, item.detail, item.portfolioStage]),
	      ]);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      const firstItem = areaItems[0];
	      const stage = areaItems.find((item) => item.portfolioStage)?.portfolioStage;
	      addOption({
	        id: `target-area:${areaName}`,
	        kind: 'Target Area',
	        label: areaName,
	        subtitle: `${areaItems.length} target layer${areaItems.length === 1 ? '' : 's'}${stage ? ` · ${stage}` : ''}`,
	        color: firstItem?.color || '#facc15',
	        layerIds: areaItems.map((item) => item.id),
	        projectId: selectedProject?.id,
	        projectName: selectedProject?.name,
	        portfolioStage: stage,
	        extent: getProjectGeoJsonExtent(areaItems.map((item) => item.id)) || undefined,
	        rows: [
	          { label: 'Target area', value: areaName },
	          { label: 'Target layers', value: areaItems.length.toLocaleString() },
	        ],
	      });
	    });

	    currentLayerControlItems.forEach((item) => {
	      if (item.disabled) {
	        return;
	      }
	      const searchText = buildActionSearchText([
	        item.label,
	        item.id,
	        item.group,
	        item.detail,
	        item.count,
	        item.targetArea,
	        item.portfolioStage,
	      ]);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      addOption({
	        id: `layer:${item.id}`,
	        kind: 'Layer',
	        label: item.label,
	        subtitle: [item.group, item.detail || item.count, item.checked ? 'on' : 'off'].filter(Boolean).join(' · '),
	        color: item.color,
	        layerIds: [item.id],
	        layerControlId: item.id,
	        projectId: selectedProject?.id,
	        projectName: selectedProject?.name,
	        portfolioStage: item.portfolioStage,
	        extent: getLayerSourceExtent(item.id) || undefined,
	        rows: [
	          { label: 'Layer ID', value: item.id },
	          { label: 'Group', value: item.group },
	          ...(item.detail ? [{ label: 'Detail', value: item.detail }] : []),
	          ...(item.count !== undefined ? [{ label: 'Feature count', value: `${item.count}` }] : []),
	        ],
	      });
	    });

	    let loadedFeatureCount = 0;
	    const addLoadedFeature = (
	      layerId: string,
	      layerLabel: string,
	      color: string,
	      layer: any,
	      feature: any,
	      resultLayerIds: string[]
	    ) => {
	      if (loadedFeatureCount >= 14) {
	        return;
	      }
	      const searchText = getFeatureSearchText(feature, layerLabel);
	      if (!matchesActionSearchQuery(searchText, query)) {
	        return;
	      }
	      const geometry = feature.getGeometry?.();
	      const extent = geometry?.getExtent?.() as [number, number, number, number] | undefined;
	      const title = getFeatureActionTitle(feature, layerLabel);
	      addOption({
	        id: `feature:${layerId}:${feature.getId?.() || loadedFeatureCount}:${title}`,
	        kind: 'Feature',
	        label: title,
	        subtitle: layerLabel,
	        color,
	        layerIds: resultLayerIds,
	        projectId: selectedProject?.id,
	        projectName: selectedProject?.name,
	        feature,
	        layer,
	        extent,
	        rows: buildInspectionRows(feature.getProperties?.() || {}),
	      });
	      loadedFeatureCount += 1;
	    };

	    projectGeoJsonLayersForProject.forEach((layerDef) => {
	      const layer = projectGeoJsonLayerRefs.current.get(layerDef.id);
	      const features = getRawLayerSource(layer)?.getFeatures() || [];
	      const color = projectGeoJsonDisplayColorById[layerDef.id] || layerDef.color;
	      features.slice(0, 80).forEach((feature) => {
	        addLoadedFeature(
	          `geojson:${layerDef.id}`,
	          layerDef.label,
	          color,
	          layer,
	          feature,
	          [`geojson:${layerDef.id}`]
	        );
	      });
	    });

	    sguVectorLayersRef.current.forEach((layer, layerId) => {
	      const definition = SGU_VECTOR_LAYERS.find((candidate) => candidate.id === layerId);
	      const layerLabel = definition?.label || layerId;
	      const color = definition?.color || '#38bdf8';
	      const features = getRawLayerSource(layer)?.getFeatures() || [];
	      features.slice(0, 120).forEach((feature) => {
	        addLoadedFeature(`sgu:${layerId}`, layerLabel, color, layer, feature, [`sgu:${layerId}`]);
	      });
	    });

	    actionSearchRemoteBoreholes.forEach((result, index) => {
	      const lon = Number(result.lon ?? result.longitude);
	      const lat = Number(result.lat ?? result.latitude);
	      if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
	        return;
	      }
	      const label =
	        formatInspectionValue(result.drillhole) ||
	        formatInspectionValue(result.hole_id) ||
	        formatInspectionValue(result.idcode) ||
	        formatInspectionValue(result.name) ||
	        `Borehole ${index + 1}`;
	      const subtitle = [
	        formatInspectionValue(result.municipality),
	        formatInspectionValue(result.depth_m) ? `${formatInspectionValue(result.depth_m)} m` : null,
	        formatInspectionValue(result.year),
	      ].filter(Boolean).join(' · ');
	      addOption({
	        id: `borehole:${label}:${lon.toFixed(5)}:${lat.toFixed(5)}`,
	        kind: 'Borehole',
	        label,
	        subtitle: subtitle || 'SGU borehole',
	        color: '#38bdf8',
	        layerIds: ['sgu:boreholes'],
	        projectId: selectedProject?.id,
	        projectName: selectedProject?.name,
	        lonLat: [lon, lat],
	        rows: buildInspectionRows(result),
	        payload: result,
	      });
	    });

	    return options.slice(0, 42);
	  }, [
	    actionSearchInput,
	    actionSearchRankedTargets,
	    actionSearchRemoteBoreholes,
	    availableProjectById,
	    getLayerSourceExtent,
	    getProjectGeoJsonExtent,
	    projectGeoJsonDisplayColorById,
	    projectGeoJsonLayersForProject,
	    selectedProject?.id,
	    selectedProject?.name,
	  ]);

	  const handleActionSearchSelect = useCallback(async (result: ActionSearchResult) => {
	    if (!actionSearchRestoreRef.current) {
	      actionSearchRestoreRef.current = captureActionSearchRestoreState();
	      setActionSearchRestoreAvailable(true);
	    }
	    actionSearchFocusUntilRef.current = Date.now() + 30000;

	    setActionSearchInput(result.label);
	    closePopup();
	    setIsLayersCollapsed(false);

	    const targetProjectId = result.projectId || selectedProjectRef.current?.id || null;
	    if (targetProjectId && selectedProjectRef.current?.id !== targetProjectId) {
	      if (!onProjectSelect) {
	        openActionSearchSummaryDrawer({
	          ...result,
	          rows: [
	            ...(result.rows || []),
	            { label: 'Project switch', value: 'Unavailable in this view' },
	          ],
	        });
	        return;
	      }
	      await onProjectSelect(targetProjectId);
	      await waitForSelectedProject(targetProjectId);
	      await waitForMapQaFrame(300);
	    }

	    const geoJsonLayerIds = result.layerIds
	      .filter((layerId) => layerId.startsWith('geojson:'))
	      .map((layerId) => layerId.replace(/^geojson:/, ''));
	    if (geoJsonLayerIds.length > 0) {
	      setProjectGeoJsonExpanded(true);
	      setProjectGeoJsonPolygonOnly(false);
	    }

	    await waitForLayerControlItems(result.layerIds);
	    await enableActionSearchLayers(result.layerIds);

	    if (result.kind === 'Project') {
	      if (result.lonLat) {
	        const map = mapInstanceRef.current;
	        if (map) {
	          map.getView().animate({
	            center: fromLonLat(result.lonLat),
	            zoom: 6.3,
	            duration: 420,
	          });
	          map.updateSize();
	        }
	      }
	      await waitForMapQaFrame(260);
	      if (result.lonLat) {
	        const map = mapInstanceRef.current;
	        if (map) {
	          const view = map.getView();
	          view.setCenter(fromLonLat(result.lonLat));
	          view.setZoom(6.3);
	          map.updateSize();
	          map.renderSync();
	        }
	      }
	      openActionSearchSummaryDrawer(result);
	      return;
	    }

	    if (result.kind === 'Borehole' && result.lonLat) {
	      handleBoreholeSelect(result.payload || { lon: result.lonLat[0], lat: result.lonLat[1], drillhole: result.label });
	      setInspectionRecord({
	        id: result.id,
	        title: result.label,
	        subtitle: result.subtitle || 'SGU borehole',
	        coordinates: `${result.lonLat[1].toFixed(5)}, ${result.lonLat[0].toFixed(5)}`,
	        layerLabel: 'SGU Boreholes',
	        source: 'SGU borehole search',
	        rows: result.rows || [
	          { label: 'Longitude', value: result.lonLat[0].toFixed(5) },
	          { label: 'Latitude', value: result.lonLat[1].toFixed(5) },
	        ],
	      });
	      return;
	    }

	    if (result.lonLat) {
	      const map = mapInstanceRef.current;
	      const zoom = result.kind === 'Ranked Target' ? 11.2 : 10.2;
	      if (map) {
	        map.getView().animate({
	          center: fromLonLat(result.lonLat),
	          zoom,
	          duration: 500,
	        });
	        map.updateSize();
	      }
	      await waitForMapQaFrame(560);
	      if (mapInstanceRef.current) {
	        const map = mapInstanceRef.current;
	        const view = map.getView();
	        view.setCenter(fromLonLat(result.lonLat));
	        view.setZoom(zoom);
	        map.updateSize();
	        map.renderSync();
	      }
	      await waitForMapQaFrame(220);
	      openActionSearchSummaryDrawer(result);
	      return;
	    }

	    if (geoJsonLayerIds.length > 0) {
	      await waitForProjectGeoJsonLayers(geoJsonLayerIds, 9000);
	    }

	    const fitGeoJsonResult = () =>
	      fitActionSearchExtent(result.extent, result.kind === 'Feature' ? 14 : 12.8) ||
	      fitActionSearchExtent(getProjectGeoJsonExtent(geoJsonLayerIds), 12.8);
	    const fitted = fitGeoJsonResult();

	    await waitForMapQaFrame(fitted ? 620 : 160);
	    if (geoJsonLayerIds.length > 0) {
	      let refitCount = 0;
	      for (let attempt = 0; attempt < 8 && refitCount < 2; attempt += 1) {
	        const refitted = fitActionSearchExtent(getProjectGeoJsonExtent(geoJsonLayerIds), 12.8);
	        if (refitted) {
	          refitCount += 1;
	        }
	        await waitForMapQaFrame(refitted ? 520 : 260);
	      }
	    }

	    if (result.feature && openInspectionForFeature(result.feature, result.layer)) {
	      return;
	    }

	    const projectFeature = geoJsonLayerIds.length > 0 ? getFirstProjectGeoJsonFeature(geoJsonLayerIds) : null;
	    if (projectFeature && openInspectionForFeature(projectFeature.feature, projectFeature.layer)) {
	      return;
	    }

	    openActionSearchSummaryDrawer(result);
	  }, [
	    captureActionSearchRestoreState,
	    closePopup,
	    enableActionSearchLayers,
	    fitActionSearchExtent,
	    getFirstProjectGeoJsonFeature,
	    getProjectGeoJsonExtent,
	    handleBoreholeSelect,
	    onProjectSelect,
	    openActionSearchSummaryDrawer,
	    openInspectionForFeature,
	    waitForLayerControlItems,
	    waitForProjectGeoJsonLayers,
	    waitForSelectedProject,
	  ]);

	  const runLayerHealthCheck = useCallback(async (layerIds?: string[]): Promise<LayerQaReport> => {
	    const requestedIds = new globalThis.Set((layerIds || []).filter(Boolean));
	    const startedAt = new Date().toISOString();
	    const candidates = layerControlItemsRef.current.filter((item) => {
	      if (item.group === 'Base Map') {
	        return false;
	      }
	      if (requestedIds.size > 0 && !requestedIds.has(item.id)) {
	        return false;
	      }
	      return true;
	    });
	    const results: LayerQaResult[] = [];

	    for (const candidate of candidates) {
	      const current = layerControlItemsRef.current.find((item) => item.id === candidate.id) || candidate;
	      const started = performance.now();
	      if (current.disabled) {
	        results.push({
	          id: current.id,
	          label: current.label,
	          group: current.group,
	          status: 'skipped',
	          message: current.disabledReason || 'Layer is disabled',
	          elapsedMs: performance.now() - started,
	        });
	        continue;
	      }

	      const wasChecked = current.checked;
	      try {
	        await current.onToggle(true);
	        await waitForMapQaFrame(650);
	        const afterOn = layerControlItemsRef.current.find((item) => item.id === current.id);
	        const activeConfirmed = Boolean(afterOn?.checked);
	        results.push({
	          id: current.id,
	          label: current.label,
	          group: current.group,
	          status: activeConfirmed ? 'pass' : 'warn',
	          message: activeConfirmed ? 'Toggle reached active state' : 'Toggle completed but active state was not confirmed',
	          elapsedMs: performance.now() - started,
	        });
	      } catch (error) {
	        results.push({
	          id: current.id,
	          label: current.label,
	          group: current.group,
	          status: 'fail',
	          message: error instanceof Error ? error.message : 'Toggle handler failed',
	          elapsedMs: performance.now() - started,
	        });
	      } finally {
	        if (!wasChecked) {
	          try {
	            const restoreItem = layerControlItemsRef.current.find((item) => item.id === current.id) || current;
	            await restoreItem.onToggle(false);
	            await waitForMapQaFrame(160);
	          } catch {
	            // Report the primary toggle result; restoration is best effort.
	          }
	        }
	      }
	    }

	    const countStatus = (status: LayerQaResultStatus) =>
	      results.filter((result) => result.status === status).length;
	    return {
	      startedAt,
	      finishedAt: new Date().toISOString(),
	      total: results.length,
	      passed: countStatus('pass'),
	      warned: countStatus('warn'),
	      failed: countStatus('fail'),
	      skipped: countStatus('skipped'),
	      results,
	    };
	  }, []);

	  useEffect(() => {
	    const projectKey = storageProjectKey(selectedProject?.id);
	    // Re-derive stored prefs on every project ENTRY (not on every layer-signature
	    // change): a control shared across projects may have been toggled while
	    // another project was active, so a once-only restore leaves stale state.
	    if (lastRestoredProjectKeyRef.current !== projectKey) {
	      lastRestoredProjectKeyRef.current = projectKey;
	      restoredLayerIdsRef.current.delete(projectKey);
	    }
	    const preferences = readBooleanRecord(`${MAP_LAYER_VISIBILITY_PREFIX}${projectKey}`);
	    if (Object.keys(preferences).length === 0) {
	      return;
	    }
	    let restoredForProject = restoredLayerIdsRef.current.get(projectKey);
	    if (!restoredForProject) {
	      restoredForProject = new globalThis.Set<string>();
	      restoredLayerIdsRef.current.set(projectKey, restoredForProject);
	    }
	    layerControlItemsRef.current.forEach((item) => {
	      if (
	        restoredForProject?.has(item.id) ||
	        item.disabled ||
	        BASE_MAP_CONTROL_IDS.has(item.id) ||
	        preferences[item.id] === undefined
	      ) {
	        return;
	      }
	      restoredForProject?.add(item.id);
	      const visible = Boolean(preferences[item.id]);
	      if (item.checked !== visible) {
	        void item.onToggle(visible);
	      }
	    });
	  }, [layerControlIdsSignature, selectedProject?.id]);

	  useEffect(() => {
	    if (typeof window === 'undefined') {
	      return;
	    }
	    const params = new URLSearchParams(window.location.search);
	    const hasSharedState = params.has('project') || params.has('layers') || params.has('lat') || params.has('lon') || params.has('zoom');
	    if (!hasSharedState) {
	      return;
	    }
	    const projectParam = params.get('project');
	    if (projectParam && selectedProject?.id !== projectParam) {
	      return;
	    }
	    const restoreKey = `${selectedProject?.id || 'global'}:${window.location.search}:${layerControlIdsSignature}`;
	    if (sharedUrlRestoreRef.current === restoreKey) {
	      return;
	    }
	    sharedUrlRestoreRef.current = restoreKey;

	    const layerParam = params.get('layers');
	    if (layerParam !== null) {
	      const requestedLayerIds = new globalThis.Set(
	        layerParam
	          .split(',')
	          .map((layerId) => layerId.trim())
	          .filter(Boolean)
	      );
	      const hasRequestedBaseLayer = Array.from(requestedLayerIds).some((layerId) => layerId.startsWith('base:'));
	      layerControlItemsRef.current.forEach((item) => {
	        if (item.disabled) {
	          return;
	        }
	        const shouldBeVisible = requestedLayerIds.has(item.id);
	        if (item.group === 'Base Map') {
	          if (hasRequestedBaseLayer && shouldBeVisible && !item.checked) {
	            void item.onToggle(true);
	          }
	          return;
	        }
	        if (item.checked !== shouldBeVisible) {
	          void item.onToggle(shouldBeVisible);
	        }
	      });
	    }

	    const parseSharedNumber = (key: string) => {
	      const rawValue = params.get(key);
	      if (rawValue === null || rawValue.trim() === '') {
	        return null;
	      }
	      const parsedValue = Number(rawValue);
	      return Number.isFinite(parsedValue) ? parsedValue : null;
	    };
	    const lon = parseSharedNumber('lon');
	    const lat = parseSharedNumber('lat');
	    const zoom = parseSharedNumber('zoom');
	    if (lon !== null && lat !== null) {
	      window.setTimeout(() => {
	        const map = mapInstanceRef.current;
	        if (!map) {
	          return;
	        }
	        const view = map.getView();
	        view.setCenter(fromLonLat([lon, lat]));
	        if (zoom !== null) {
	          view.setZoom(zoom);
	        }
	        map.updateSize();
	        map.renderSync();
	      }, 250);
	    }
	  }, [layerControlIdsSignature, selectedProject?.id]);

	  useEffect(() => {
	    if (!isMapQaBridgeEnabled()) {
	      if ((window as any).__geomorphicMapQa?.version === QA_BRIDGE_VERSION) {
	        delete (window as any).__geomorphicMapQa;
	      }
	      return;
	    }

	    const getViewport = () => {
	      const view = mapInstanceRef.current?.getView();
	      const center = view?.getCenter();
	      const [lon, lat] = center ? toLonLat(center) : [null, null];
	      const zoom = view?.getZoom();
	      return {
	        lon: typeof lon === 'number' && Number.isFinite(lon) ? lon : null,
	        lat: typeof lat === 'number' && Number.isFinite(lat) ? lat : null,
	        zoom: typeof zoom === 'number' && Number.isFinite(zoom) ? zoom : null,
	      };
	    };

	    const getState = (): MapQaBridgeState => ({
	      version: QA_BRIDGE_VERSION,
	      selectedProject: selectedProject
	        ? {
	            id: selectedProject.id,
	            name: selectedProject.name,
	            location: selectedProject.location,
	          }
	        : null,
	      viewport: getViewport(),
	      layerPanelCollapsed: isLayersCollapsed,
	      activeLayers: [...activeLayers],
	      inspection: inspectionRecord,
	      projectGeoJsonExpanded,
	      projectGeoJsonPolygonOnly,
	      projectGeoJsonVisibility: { ...projectGeoJsonVisibility },
	      layers: layerControlItems.map(({ onToggle, ...item }) => item),
	      presets: layerControlPresets.map((preset) => ({ ...preset, layerIds: [...preset.layerIds] })),
	      projectGeoJsonLayers: projectGeoJsonLayersForProject.map((layerDef) => {
	        const layer = projectGeoJsonLayerRefs.current.get(layerDef.id);
	        const source = getRawLayerSource(layer);
	        return {
	          ...layerDef,
	          color: projectGeoJsonDisplayColorById[layerDef.id] || layerDef.color,
	          controlId: `geojson:${layerDef.id}`,
	          checked: Boolean(projectGeoJsonVisibility[layerDef.id]),
	          visible: layer?.getVisible() ?? Boolean(projectGeoJsonVisibility[layerDef.id]),
	          featureCount: source?.getFeatures().length ?? 0,
	        };
	      }),
	    });

	    const resolveLayerItem = (layerId: string) => {
	      const exactMatch = layerControlItems.find((item) => item.id === layerId);
	      if (exactMatch) {
	        return exactMatch;
	      }
	      const geoJsonMatch = projectGeoJsonLayersForProject.find((layerDef) => layerDef.id === layerId);
	      if (geoJsonMatch) {
	        return layerControlItems.find((item) => item.id === `geojson:${geoJsonMatch.id}`);
	      }
	      return undefined;
	    };

	    const respondError = (error: string): MapQaBridgeResponse => ({
	      ok: false,
	      error,
	      state: getState(),
	    });

	    const fitProjectGeoJsonLayerIds = (layerIds: string[]) => {
	      const map = mapInstanceRef.current;
	      if (!map) {
	        return false;
	      }
	      const normalizedIds = layerIds.map((layerId) => layerId.replace(/^geojson:/, ''));
	      let extent: [number, number, number, number] | null = null;
	      normalizedIds.forEach((layerId) => {
	        const source = getRawLayerSource(projectGeoJsonLayerRefs.current.get(layerId));
	        source?.getFeatures().forEach((feature) => {
	          const candidate = feature.getGeometry()?.getExtent();
	          if (!candidate || candidate.length !== 4 || candidate.some((value) => !Number.isFinite(value))) {
	            return;
	          }
	          if (!extent) {
	            extent = [candidate[0], candidate[1], candidate[2], candidate[3]];
	            return;
	          }
	          extent = [
	            Math.min(extent[0], candidate[0]),
	            Math.min(extent[1], candidate[1]),
	            Math.max(extent[2], candidate[2]),
	            Math.max(extent[3], candidate[3]),
	          ];
	        });
	      });
	      if (!extent) {
	        return false;
	      }
	      map.getView().fit(extent, {
	        padding: [80, 420, 80, 80],
	        maxZoom: 12.5,
	        duration: 0,
	      });
	      map.updateSize();
	      map.renderSync();
	      return true;
	    };

	    const bridge: MapQaBridge = {
	      version: QA_BRIDGE_VERSION,
	      getState,
	      setViewport: async ({ lon, lat, zoom }) => {
	        const map = mapInstanceRef.current;
	        if (!map) {
	          return respondError('Map instance is not ready');
	        }
	        if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
	          return respondError('Viewport requires finite lon and lat values');
	        }
	        const view = map.getView();
	        view.setCenter(fromLonLat([lon, lat]));
	        if (typeof zoom === 'number' && Number.isFinite(zoom)) {
	          view.setZoom(zoom);
	        }
	        map.updateSize();
	        map.renderSync();
	        await waitForMapQaFrame(100);
	        return { ok: true, state: getState() };
	      },
	      toggleLayer: async (layerId, visible) => {
	        const item = resolveLayerItem(layerId);
	        if (!item) {
	          return respondError(`Layer not found: ${layerId}`);
	        }
	        if (item.disabled) {
	          return respondError(item.disabledReason || `Layer is disabled: ${layerId}`);
	        }
	        await item.onToggle(typeof visible === 'boolean' ? visible : !item.checked);
	        await waitForMapQaFrame();
	        return { ok: true, state: getState() };
	      },
	      applyPreset: async (presetId) => {
	        if (presetId === 'clear') {
	          setProjectGeoJsonPolygonOnly(false);
	          if (selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') {
	            setSelectedProjectStakingBoundariesVisible(false);
	          }
	          layerControlItems.forEach((item) => {
	            if (item.disabled || item.presetControlled === false || item.group === 'Base Map') {
	              return;
	            }
	            void item.onToggle(false);
	          });
	          await waitForMapQaFrame();
	          return { ok: true, state: getState() };
	        }

	        const preset = layerControlPresets.find((candidate) => candidate.id === presetId);
	        if (!preset) {
	          return respondError(`Preset not found: ${presetId}`);
	        }
	        const enabled = new Set(preset.layerIds);
	        const polygonOnly = presetId === 'preset-targets';
	        setProjectGeoJsonPolygonOnly(polygonOnly);
	        if ((selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') && polygonOnly) {
	          setSelectedProjectStakingBoundariesVisible(false);
	        }
	        layerControlItems.forEach((item) => {
	          if (item.disabled || item.presetControlled === false) {
	            return;
	          }
	          void item.onToggle(enabled.has(item.id));
	        });
	        await waitForMapQaFrame();
	        return { ok: true, state: getState() };
	      },
	      setProjectGeoJsonLayers: async (layerIds, options = {}) => {
	        const normalizedIds = new Set(layerIds.map((layerId) => layerId.replace(/^geojson:/, '')));
	        const missingIds = Array.from(normalizedIds).filter(
	          (layerId) => !projectGeoJsonLayersForProject.some((layerDef) => layerDef.id === layerId)
	        );
	        if (missingIds.length > 0) {
	          return respondError(`Project GeoJSON layer(s) not found: ${missingIds.join(', ')}`);
	        }
	        const visible = options.visible ?? true;
	        const only = options.only ?? false;
	        if (typeof options.polygonOnly === 'boolean') {
	          setProjectGeoJsonPolygonOnly(options.polygonOnly);
	        }
	        setProjectGeoJsonVisibility((prev) => {
	          const next = { ...prev };
	          projectGeoJsonLayersForProject.forEach((layerDef) => {
	            if (only) {
	              next[layerDef.id] = normalizedIds.has(layerDef.id) ? visible : false;
	            } else if (normalizedIds.has(layerDef.id)) {
	              next[layerDef.id] = visible;
	            }
	          });
	          return next;
	        });
	        await waitForMapQaFrame();
	        return { ok: true, state: getState() };
	      },
	      runLayerHealthCheck,
	      getShareUrl: buildShareUrl,
	      runScenario: async (scenarioId) => {
	        if (scenarioId === 'finland-pahkosuo-labels') {
	          if (selectedProject?.id !== 'project-gtk-finland') {
	            return respondError('Switch to project-gtk-finland before running finland-pahkosuo-labels');
	          }
	          const pahkosuoLayerIds = projectGeoJsonLayersForProject
	            .filter((layerDef) => layerDef.targetArea === 'Pahkosuo')
	            .map((layerDef) => layerDef.id);
	          if (pahkosuoLayerIds.length === 0) {
	            return respondError('No Pahkosuo target layers are registered for this project');
	          }
	          setIsLayersCollapsed(false);
	          setProjectGeoJsonExpanded(true);
	          setProjectGeoJsonPolygonOnly(false);
	          setBaseMapLayer('satellite', true);
	          setProjectGeoJsonVisibility((prev) => {
	            const next = { ...prev };
	            projectGeoJsonLayersForProject.forEach((layerDef) => {
	              if (layerDef.targetArea) {
	                next[layerDef.id] = layerDef.targetArea === 'Pahkosuo';
	              }
	            });
	            pahkosuoLayerIds.forEach((layerId) => {
	              next[layerId] = true;
	            });
	            return next;
	          });
	          const focusPahkosuo = () => {
	            if (fitProjectGeoJsonLayerIds(pahkosuoLayerIds)) {
	              return;
	            }
	            const map = mapInstanceRef.current;
	            if (map) {
	              map.getView().setCenter(fromLonLat([29.35, 65.32]));
	              map.getView().setZoom(11.2);
	              map.updateSize();
	              map.renderSync();
	            }
	          };
	          await waitForMapQaFrame(900);
	          focusPahkosuo();
	          await waitForMapQaFrame(900);
	          focusPahkosuo();
	          await waitForMapQaFrame(250);
	          return { ok: true, state: getState() };
	        }

	        if (scenarioId === 'sweden-klippen-labels') {
	          if (selectedProject?.id !== 'project-sgu-sweden') {
	            return respondError('Switch to project-sgu-sweden before running sweden-klippen-labels');
	          }
	          const klippenLayerIds = projectGeoJsonLayersForProject
	            .filter((layerDef) => layerDef.targetArea === 'Klippen')
	            .map((layerDef) => layerDef.id);
	          if (klippenLayerIds.length === 0) {
	            return respondError('No Klippen target layers are registered for this project');
	          }
	          setIsLayersCollapsed(false);
	          setProjectGeoJsonExpanded(true);
	          setProjectGeoJsonPolygonOnly(false);
	          setBaseMapLayer('satellite', true);
	          setProjectGeoJsonVisibility((prev) => {
	            const next = { ...prev };
	            projectGeoJsonLayersForProject.forEach((layerDef) => {
	              if (layerDef.targetArea) {
	                next[layerDef.id] = layerDef.targetArea === 'Klippen';
	              }
	            });
	            klippenLayerIds.forEach((layerId) => {
	              next[layerId] = true;
	            });
	            return next;
	          });
	          const focusKlippen = () => {
	            if (fitProjectGeoJsonLayerIds(klippenLayerIds)) {
	              return;
	            }
	            const map = mapInstanceRef.current;
	            if (map) {
	              map.getView().setCenter(fromLonLat([17.9, 65.6]));
	              map.getView().setZoom(10.4);
	              map.updateSize();
	              map.renderSync();
	            }
	          };
	          await waitForMapQaFrame(900);
	          focusKlippen();
	          await waitForMapQaFrame(900);
	          focusKlippen();
	          await waitForMapQaFrame(250);
	          return { ok: true, state: getState() };
	        }

	        if (scenarioId === 'finland-targets-overview') {
	          if (selectedProject?.id !== 'project-gtk-finland') {
	            return respondError('Switch to project-gtk-finland before running finland-targets-overview');
	          }
	          const preset = layerControlPresets.find((candidate) => candidate.id === 'preset-targets');
	          if (!preset) {
	            return respondError('Targets preset is not available for this project');
	          }
	          setIsLayersCollapsed(false);
	          setProjectGeoJsonExpanded(true);
	          setProjectGeoJsonPolygonOnly(true);
	          const enabled = new Set(preset.layerIds);
	          layerControlItems.forEach((item) => {
	            if (item.disabled || item.presetControlled === false) {
	              return;
	            }
	            void item.onToggle(enabled.has(item.id));
	          });
	          await waitForMapQaFrame(900);
	          mapInstanceRef.current?.getView().setCenter(fromLonLat([26.0, 64.8]));
	          mapInstanceRef.current?.getView().setZoom(5.4);
	          mapInstanceRef.current?.renderSync();
	          await waitForMapQaFrame(250);
	          return { ok: true, state: getState() };
	        }

	        return respondError(`Unknown scenario: ${scenarioId}`);
	      },
	    };

	    (window as any).__geomorphicMapQa = bridge;
	    window.dispatchEvent(new CustomEvent('geomorphic:qa-ready', {
	      detail: { surface: 'map', version: QA_BRIDGE_VERSION },
	    }));

	    return () => {
	      if ((window as any).__geomorphicMapQa === bridge) {
	        delete (window as any).__geomorphicMapQa;
	      }
	    };
	  }, [
	    activeLayers,
	    buildShareUrl,
	    inspectionRecord,
	    isLayersCollapsed,
	    layerControlIdsSignature,
	    layerControlStateSignature,
	    layerControlPresets,
	    projectGeoJsonExpanded,
	          projectGeoJsonLayersForProject,
	          projectGeoJsonPolygonOnly,
	          projectGeoJsonVisibility,
	          projectGeoJsonDisplayColorById,
	          runLayerHealthCheck,
	          selectedProject?.id,
	    selectedProject?.location,
	    selectedProject?.name,
	    setBaseMapLayer,
	  ]);

	  return (
    <Box sx={{ position: 'absolute', inset: 0, width: '100%', backgroundColor: '#0D1117', overflow: 'hidden' }}>
      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000
        }}>
          <CircularProgress sx={{ color: 'var(--palantir-blue-600)' }} />
        </Box>
      )}

	      {/* Map container */}
	      <div ref={mapRef} data-testid="map-container" style={{ width: '100%', height: '100%' }} />

	      <MapActionSearch
	        inputValue={actionSearchInput}
	        options={actionSearchOptions}
	        loading={actionSearchLoading || actionSearchRankedTargetsLoading}
	        rightOffset={legendPortalElement ? 24 : (isLayersCollapsed ? 92 : 372)}
	        onInputChange={setActionSearchInput}
	        onSelect={handleActionSearchSelect}
	        restoreAvailable={actionSearchRestoreAvailable}
	        onRestore={restoreActionSearchState}
	      />

	      {(() => {
	        const agentActivity = agentEvalRequest ? (
	          <AgentEvaluationPanel
	            request={agentEvalRequest}
	            onClose={() => setAgentEvalRequest(null)}
	            embedded
	            onFlyTo={(lon, lat) => {
	              const view = mapInstanceRef.current?.getView();
	              if (!view) return;
	              view.animate({
	                center: transform([lon, lat], 'EPSG:4326', 'EPSG:3857'),
	                zoom: Math.max(view.getZoom() || 8, 10.5),
	                duration: 600,
	              });
	            }}
	          />
	        ) : null;
	        const inspectionDrawer = (
	          <InspectionDrawer
	            record={inspectionRecord}
	            onClose={closeInspectionDrawer}
	            rightOffset={legendPortalElement ? 24 : (isLayersCollapsed ? 92 : 356)}
	            onEvaluate={setAgentEvalRequest}
	            agentActivity={agentActivity}
	            docked={Boolean(inspectionPortalElement)}
	          />
	        );
	        return inspectionPortalElement
	          ? createPortal(inspectionDrawer, inspectionPortalElement)
	          : inspectionDrawer;
	      })()}

      {/* Popup - hidden during enhanced analysis log view */}
      <div
        ref={popupRef}
        style={{ display: !showEnhancedAnalysisLog && popupInfo ? 'block' : 'none' }}
      >
        {popupInfo && popupType === 'geochem' &&
          (() => {
            const metadataEntries = Object.entries(popupInfo.metadata || {})
              .filter(([, value]) => {
                if (value === null || value === undefined) {
                  return false;
                }
                const text = `${value}`.trim();
                if (!text) {
                  return false;
                }
                const lower = text.toLowerCase();
                if (lower === 'nan' || lower === 'none' || lower === 'null') {
                  return false;
                }
                return true;
              })
              .slice(0, 3);
            const valueEntries = Object.entries(popupInfo.values || {})
              .filter(([, value]) => typeof value === 'number' && Number.isFinite(value))
              .sort((a, b) => Math.abs((b[1] as number) || 0) - Math.abs((a[1] as number) || 0))
              .slice(0, 12);

            return (
              <Paper
                elevation={6}
                sx={{
                  minWidth: 260,
                  maxWidth: 320,
                  backgroundColor: 'rgba(13, 19, 27, 0.92)',
                  border: '1px solid rgba(88, 166, 255, 0.25)',
                  borderRadius: 2,
                  color: '#f5f7fb',
                  backdropFilter: 'blur(12px)',
                  pointerEvents: 'auto'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    p: 1.5,
                    pb: 1
                  }}
                >
                  <Box sx={{ pr: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#9fbefc',
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={popupInfo.overlayName || 'Geochemistry overlay'}
                    >
                      {popupInfo.overlayName || 'Geochemistry overlay'}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: 'var(--palantir-text-subtle)' }}>
                      {popupInfo.coordinates}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={closePopup}
                    sx={{ color: 'var(--palantir-text-subtle)' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {metadataEntries.length > 0 && (
                  <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {metadataEntries.map(([key, value]) => (
                      <Typography
                        key={key}
                        sx={{ fontSize: '10px', color: 'var(--palantir-text-muted)' }}
                      >
                        <strong>{prettifyFieldLabel(key)}:</strong> {`${value}`}
                      </Typography>
                    ))}
                  </Box>
                )}

                {valueEntries.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(88, 166, 255, 0.2)', mx: 1.5 }} />
                    <Box sx={{ px: 1.5, py: 1 }}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '10px', mb: 0.5, color: '#58a6ff' }}>
                        Assay values
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                        {valueEntries.map(([key, value]) => (
                          <Box
                            key={key}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              fontSize: '10px',
                              color: '#e6ecff'
                            }}
                          >
                            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#9fbefc' }}>
                              {prettifyFieldLabel(key)}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace' }}>
                              {formatGeochemValue(key, value as number)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Paper>
            );
          })()}

        {popupInfo && popupType === 'exploration' && (() => {
          const tierColors: Record<string, string> = {
            MUST_STAKE: '#E63946', HIGH: '#FF6B35', MEDIUM: '#FFB703', LOW: '#ADB5BD'
          };
          const tierColor = tierColors[popupInfo.priority_tier] || '#ADB5BD';
          const riskColor = (r: string) => r === 'LOW' ? '#00C853' : r === 'HIGH' ? '#ff5252' : r === 'MEDIUM' ? '#FFB703' : '#9fbefc';

          const gradeRange = popupInfo.grade_min != null && popupInfo.grade_max != null
            ? `${popupInfo.grade_min}–${popupInfo.grade_max} ${popupInfo.best_grade_unit || ''}${popupInfo.grade_median != null ? ` (med ${popupInfo.grade_median})` : ''}`
            : undefined;

          // Build evidence breakdown: "5 till, 3 rock, 2 drill holes"
          const evidenceParts: string[] = [];
          if (popupInfo.till_samples > 0) evidenceParts.push(`${popupInfo.till_samples} till`);
          if (popupInfo.rock_samples > 0) evidenceParts.push(`${popupInfo.rock_samples} rock`);
          if (popupInfo.soil_samples > 0) evidenceParts.push(`${popupInfo.soil_samples} soil`);
          if (popupInfo.stream_samples > 0) evidenceParts.push(`${popupInfo.stream_samples} stream`);
          if (popupInfo.drill_holes_in_area > 0) evidenceParts.push(`${popupInfo.drill_holes_in_area} drill hole${popupInfo.drill_holes_in_area !== 1 ? 's' : ''}`);
          const evidenceBreakdown = evidenceParts.length > 0
            ? evidenceParts.join(', ')
            : (popupInfo.sample_count != null ? `${popupInfo.sample_count} sample${popupInfo.sample_count !== 1 ? 's' : ''}` : undefined);

          const spreadInfo = popupInfo.cluster_radius_km != null && popupInfo.cluster_radius_km > 0
            ? `${popupInfo.cluster_radius_km} km`
            : (popupInfo.cluster_radius_km === 0 ? 'point anomaly' : undefined);

          const infoRows: [string, string | undefined][] = [
            ['Deposit Model', popupInfo.deposit_model !== 'unknown' ? popupInfo.deposit_model : undefined],
            ['Best Grade', popupInfo.best_grade_value ? `${popupInfo.best_grade_value} ${popupInfo.best_grade_unit || ''} (${popupInfo.best_grade_sample_type || '?'})` : undefined],
            ['Grade Range', gradeRange],
            ['Evidence', evidenceBreakdown],
            ['Spread', spreadInfo],
            ['Drill Result', popupInfo.drill_best_intersection],
            ['Geophysics', popupInfo.geophysics_types],
            ['Confirmation', popupInfo.confirmation_type],
            ['Anomaly', popupInfo.anomaly_strength ? `${popupInfo.anomaly_strength}` : undefined],
            ['Open Ground', popupInfo.open_ground_pct != null ? `${popupInfo.open_ground_pct}%` : undefined],
            ['Staking Action', popupInfo.staking_action],
            ['Claim Size', popupInfo.claim_size_km2 ? `${popupInfo.claim_size_km2} km2` : undefined],
            ['Budget', popupInfo.budget_eur ? `EUR ${Number(popupInfo.budget_eur).toLocaleString()}` : undefined],
            ['Host Rocks', popupInfo.host_rocks],
            ['Maturity', popupInfo.exploration_maturity],
          ];

          return (
            <Paper
              elevation={6}
              sx={{
                minWidth: 280,
                maxWidth: 360,
                backgroundColor: 'rgba(13, 19, 27, 0.94)',
                border: `1px solid ${tierColor}55`,
                borderRadius: 2,
                color: '#f5f7fb',
                backdropFilter: 'blur(12px)',
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 1.5, pb: 0.5 }}>
                <Box sx={{ pr: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: tierColor }}>
                      {popupInfo.target_id}
                    </Typography>
                    <Chip
                      label={popupInfo.priority_tier}
                      size="small"
                      sx={{
                        backgroundColor: tierColor + '22',
                        border: `1px solid ${tierColor}55`,
                        color: tierColor,
                        fontFamily: 'monospace',
                        fontSize: '9px',
                        fontWeight: 700,
                        height: 20,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '10px', color: 'var(--palantir-text-subtle)' }}>
                    {popupInfo.commodity} {popupInfo.coordinates}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={closePopup} sx={{ color: 'var(--palantir-text-subtle)' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Divider sx={{ borderColor: `${tierColor}33`, mx: 1.5, mt: 0.5 }} />

              <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {infoRows.filter(([, v]) => v).map(([label, value]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#9fbefc', whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6ecff', textAlign: 'right', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {(popupInfo.environmental_risk || popupInfo.geological_risk || popupInfo.permitting_risk) && (
                <>
                  <Divider sx={{ borderColor: `${tierColor}33`, mx: 1.5 }} />
                  <Box sx={{ px: 1.5, py: 0.8, display: 'flex', gap: 1.5 }}>
                    {popupInfo.environmental_risk && (
                      <Typography sx={{ fontSize: '9px', fontFamily: 'monospace' }}>
                        ENV: <span style={{ color: riskColor(popupInfo.environmental_risk) }}>{popupInfo.environmental_risk}</span>
                      </Typography>
                    )}
                    {popupInfo.geological_risk && (
                      <Typography sx={{ fontSize: '9px', fontFamily: 'monospace' }}>
                        GEO: <span style={{ color: riskColor(popupInfo.geological_risk) }}>{popupInfo.geological_risk}</span>
                      </Typography>
                    )}
                    {popupInfo.permitting_risk && (
                      <Typography sx={{ fontSize: '9px', fontFamily: 'monospace' }}>
                        PERM: <span style={{ color: riskColor(popupInfo.permitting_risk) }}>{popupInfo.permitting_risk}</span>
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {popupInfo.notes && (
                <>
                  <Divider sx={{ borderColor: `${tierColor}33`, mx: 1.5 }} />
                  <Box sx={{ px: 1.5, py: 0.8 }}>
                    <Typography sx={{ fontSize: '9px', fontFamily: 'monospace', color: 'var(--palantir-text-muted)', lineHeight: 1.4 }}>
                      {String(popupInfo.notes).slice(0, 200)}{String(popupInfo.notes).length > 200 ? '...' : ''}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          );
        })()}
      </div>

      {tourToast && (
        <Box sx={{ position: 'absolute', bottom: 26, left: '50%', transform: 'translateX(-50%)', px: 2, py: 0.8, borderRadius: 2, backgroundColor: 'rgba(5, 8, 13, 0.92)', border: '1px solid rgba(226, 232, 240, 0.30)', backdropFilter: 'blur(10px)', zIndex: 1500, pointerEvents: 'none' }}>
          <Typography sx={{ color: '#f1f5f9', fontFamily: MAP_UI_MONO_FONT, fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em' }}>
            {tourToast}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={Boolean(layerLoadError)}
        autoHideDuration={6000}
        onClose={() => setLayerLoadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 1500 }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setLayerLoadError(null)} sx={{ fontSize: '12px' }}>
          {layerLoadError}
        </Alert>
      </Snackbar>

      {controlFlyoutEntry && flyoutSettings && controlFlyoutSummary && (
        <Slide
          direction="right"
          in={Boolean(controlFlyoutEntry)}
          mountOnEnter
          unmountOnExit
        >
          <Paper
            elevation={12}
            sx={{
              position: 'absolute',
              top: 120,
              left: 24,
              width: 320,
              maxHeight: 'calc(100% - 140px)',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(13, 19, 27, 0.82)',
              color: '#f5f7fb',
	                border: '1px solid rgba(88, 166, 255, 0.42)',
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(14px)',
              pointerEvents: 'auto',
              zIndex: 1200,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(88, 166, 255, 0.15)'
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#58a6ff',
                    mb: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={controlFlyoutEntry.metadata?.analysisName || controlFlyoutEntry.polygonName || controlFlyoutEntry.analysisId}
                >
                  {controlFlyoutEntry.metadata?.analysisName || controlFlyoutEntry.polygonName || controlFlyoutEntry.analysisId}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'var(--palantir-text-subtle)' }}>
                  {controlFlyoutSummary.polygonName} • {controlFlyoutSummary.completedAt}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={controlFlyoutVisible ? 'Hide heatmap layer' : 'Show heatmap layer'}>
                  <IconButton
                    onClick={handleFlyoutVisibilityToggle}
                    size="small"
                    sx={{ color: controlFlyoutVisible ? '#58a6ff' : 'rgba(148,191,255,0.55)' }}
                  >
                    {controlFlyoutVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close controls">
                  <IconButton onClick={closeControlFlyout} size="small" sx={{ color: 'var(--palantir-text-subtle)' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.8
              }}
            >
              {flyoutControlsContainerId && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography sx={{ color: '#58a6ff', fontFamily: 'monospace', fontSize: '10px', fontWeight: 600 }}>
                    Heat map controls
                  </Typography>
                  <Box
                    id={flyoutControlsContainerId}
                    sx={{
                      '& .MuiPaper-root': {
                        backgroundColor: 'rgba(10, 14, 21, 0.75)',
                        border: '1px solid rgba(48, 54, 61, 0.45)',
                        borderRadius: 2,
                        boxShadow: 'none'
                      }
                    }}
                  />
                </Box>
              )}

              {flyoutSettings && (
                <Box>
                  <Typography sx={{ color: '#58a6ff', fontFamily: 'monospace', fontSize: '10px', mb: 0.5 }}>
                    Layer Opacity ({Math.round((flyoutSettings.opacity ?? 0.85) * 100)}%)
                  </Typography>
                  <Slider
                    value={flyoutSettings.opacity ?? 0.85}
                    onChange={(_, value) =>
                      updateGridSetting(controlFlyoutEntry.analysisId, flyoutUpdateKey, {
                        opacity: value as number,
                      })
                    }
                    min={0.1}
                    max={1.0}
                    step={0.05}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round((value as number) * 100)}%`}
                    sx={{
                      color: '#00D2FF',
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.3
                      }
                    }}
                  />
                </Box>
              )}

              {controlFlyoutEntry?.rasterAssets &&
                Object.keys(controlFlyoutEntry.rasterAssets).length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <Divider sx={{ borderColor: 'rgba(88, 166, 255, 0.14)' }} />
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '10px',
                        letterSpacing: 0.6,
                        color: '#58a6ff',
                      }}
                    >
                      RASTER OVERLAYS
                    </Typography>
                    {Object.entries(controlFlyoutEntry.rasterAssets).map(([layerKey, asset]) => {
                      const visible =
                        markerVisibility?.get(`raster_${controlFlyoutEntry.analysisId}`) === true;
                      return (
                        <FormControlLabel
                          key={layerKey}
                          control={
                            <Switch
                              size="small"
                              checked={visible}
                              onChange={(_, checked) =>
                                handleRasterVisibilityToggle(controlFlyoutEntry.analysisId, checked)
                              }
                              color="primary"
                            />
                          }
                          label={asset.layer || 'Model raster'}
                          sx={{
                            marginLeft: 0,
                            color: 'var(--palantir-text-subtle)',
                            '& .MuiTypography-root': { fontSize: '12px' },
                          }}
                        />
                      );
                    })}
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '10px', color: 'var(--palantir-text-subtle)', mt: 0.5 }}
                    >
                      Raster layers load on demand and follow the current opacity setting.
                    </Typography>
                  </Box>
                )}

              {geochemOverlaysForAnalysis.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  <Divider sx={{ borderColor: 'rgba(88, 166, 255, 0.14)' }} />
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '10px', letterSpacing: 0.6, color: '#58a6ff' }}>
                    GEOCHEM OVERLAY
                  </Typography>
                  {geochemOverlaysForAnalysis.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel id={`geochem-overlay-select-${controlFlyoutEntry.analysisId}`}>Overlay</InputLabel>
                      <Select
                        labelId={`geochem-overlay-select-${controlFlyoutEntry.analysisId}`}
                        label="Overlay"
                        value={
                          geochemOverlayId && geochemOverlaysForAnalysis.some((overlay) => overlay.id === geochemOverlayId)
                            ? geochemOverlayId
                            : ''
                        }
                        onChange={(event) => {
                          const newId = event.target.value as string;
                          if (newId) {
                            setGeochemOverlayId(newId);
                            setGeochemOverlayVisible(true);
                          } else {
                            setGeochemOverlayId('');
                            setGeochemOverlayVisible(false);
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="">Select overlay…</MenuItem>
                        {geochemOverlaysForAnalysis.map((overlay) => (
                          <MenuItem key={overlay.id} value={overlay.id}>
                            {overlay.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={geochemOverlayVisible && Boolean(activeGeochemOverlay)}
                        onChange={(event) => {
                          const nextVisible = event.target.checked;
                          if (nextVisible) {
                            if (!geochemOverlayId) {
                              const fallback = geochemOverlaysForAnalysis[0];
                              if (fallback) {
                                setGeochemOverlayId(fallback.id);
                              }
                            }
                            setGeochemOverlayVisible(true);
                          } else {
                            setGeochemOverlayVisible(false);
                          }
                        }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#58a6ff' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(88, 166, 255, 0.45)' }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '10px', color: geochemOverlayVisible ? '#58a6ff' : '#8b949e' }}>
                        Show geochem assays
                      </Typography>
                    }
                  />

                  {activeGeochemOverlay && geochemOverlayVisible && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                      {activeGeochemOverlay.elements.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id={`geochem-color-field-${controlFlyoutEntry.analysisId}`}>Color by</InputLabel>
                          <Select
                            labelId={`geochem-color-field-${controlFlyoutEntry.analysisId}`}
                            label="Color by"
                            value={
                              geochemColorField && activeGeochemOverlay.elements.includes(geochemColorField)
                                ? geochemColorField
                                : ''
                            }
                            displayEmpty
                            onChange={(event) => setGeochemColorField(event.target.value as string)}
                          >
                            <MenuItem value="">
                              <em>Select metric…</em>
                            </MenuItem>
                            {activeGeochemOverlay.elements.map((element) => (
                              <MenuItem key={element} value={element}>
                                {prettifyFieldLabel(element)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {geochemColorField && activeGeochemColorStats && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: '#9fbefc' }}>
                            {prettifyFieldLabel(geochemColorField)} scale
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: 'var(--palantir-text-subtle)' }}>
                              {formatGeochemValue(geochemColorField, activeGeochemColorStats.min)}
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 999,
                                backgroundImage: GEOCHEM_LEGEND_GRADIENT,
                                border: '1px solid rgba(88, 166, 255, 0.2)'
                              }}
                            />
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: 'var(--palantir-text-subtle)' }}>
                              {formatGeochemValue(geochemColorField, activeGeochemColorStats.max)}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', color: 'var(--palantir-text-subtle)' }}>
                        Points: {activeGeochemOverlay.pointCount.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {activeGeochemOverlaySummary.slice(0, 6).map((item) => (
                          <Chip
                            key={item.element}
                            label={`${item.element}: avg ${item.avg.toFixed(2)}`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(88, 166, 255, 0.12)',
                              border: '1px solid rgba(88, 166, 255, 0.18)',
                              color: '#9fbefc',
                              fontFamily: 'monospace',
                              height: 20
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Slide>
      )}

      {renderLegendAnalysis !== false && (() => {
        const isPortaled = !!legendPortalElement;
        const layersContent = (
          <Paper
            elevation={isPortaled ? 0 : undefined}
            sx={{
              ...(isPortaled ? {
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
              } : {
                position: 'absolute',
                top: 16,
                right: 24,
                width: isLayersCollapsed ? 56 : 360,
                minWidth: isLayersCollapsed ? 56 : 360,
                maxWidth: isLayersCollapsed ? 56 : 360,
                height: isLayersCollapsed ? 92 : 'auto',
                minHeight: isLayersCollapsed ? 92 : 'auto',
                maxHeight: 'calc(100% - 32px)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#0e141b',
                border: '1px solid rgba(88, 166, 255, 0.35)',
                borderRadius: 3,
                boxShadow: '0 12px 24px rgba(0,0,0,0.45)',
                overflow: 'hidden',
                transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
              })
            }}
          >
            {/* Widget header */}
            <Box
              sx={{
                px: isPortaled ? 0 : 2,
                py: isPortaled ? 0 : 1.8,
	                borderBottom: isPortaled ? 'none' : '1px solid rgba(88, 166, 255, 0.45)',
                background: isPortaled ? 'none' : 'linear-gradient(90deg, rgba(14,20,27,0.9) 0%, rgba(14,20,27,0.4) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: (isPortaled || !isLayersCollapsed) ? 'space-between' : 'center',
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              {!isPortaled && !isLayersCollapsed && (
	                <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.4, color: '#dbeafe', fontFamily: MAP_UI_FONT }}>
	                  MAP LAYERS
	                </Typography>
              )}
              {!isPortaled && isLayersCollapsed && (
                <Typography
	                  sx={{
	                    fontSize: 10,
	                    color: '#58a6ff',
	                    letterSpacing: 1.4,
	                    fontFamily: MAP_UI_FONT,
	                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)'
                  }}
                >
                  LAYERS
                </Typography>
              )}
              {!isPortaled && (
                <IconButton
                  size="small"
                  onClick={() => setIsLayersCollapsed((prev) => !prev)}
                  sx={{
                    color: '#58a6ff',
                    '&:hover': { color: '#7fb4ff' }
                  }}
                >
                  {isLayersCollapsed ? (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              )}
            </Box>

            {/* Layer content */}
            <Collapse
              in={isPortaled || !isLayersCollapsed}
              timeout="auto"
              unmountOnExit
              sx={{
                flex: 1,
                display: (isPortaled || !isLayersCollapsed) ? 'flex' : 'none',
                minHeight: 0,
                flexDirection: 'column',
                // Let the Collapse inner wrappers pass height through so the scroll
                // box below can flex-fill (zoom-safe; avoids vh cap under uiscale).
                '& .MuiCollapse-wrapper': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
                '& .MuiCollapse-wrapperInner': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
              }}
            >
              {/* pt must stay 0: any top padding on the scroll container leaves a
                  see-through band above stuck (position: sticky) group headers;
                  the panel's first child carries its own top margin instead. */}
              <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: isPortaled ? 0 : 1.5, pb: isPortaled ? 0 : 1.5, pt: 0, '& > *:first-of-type': { mt: isPortaled ? 0 : 1.5 } }}>
	                <LayerOperationsPanel
	                  items={layerControlItems}
	                  presets={layerControlPresets}
	                  storageKey={storageProjectKey(selectedProject?.id)}
	                  onRunLayerQa={runLayerHealthCheck}
	                  onCopyShareUrl={async () => buildShareUrl()}
	                  onPresetApply={(presetId) => {
                    const polygonOnly = presetId === 'preset-targets';
                    setProjectGeoJsonPolygonOnly(polygonOnly);
                    if (
                      (selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') &&
                      (polygonOnly || presetId === 'clear')
                    ) {
                      setSelectedProjectStakingBoundariesVisible(false);
                    }
                  }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowLegacyLayerList((prev) => !prev)}
                  sx={{
                    mt: 1.2,
                    height: 28,
                    borderRadius: 1,
	                    borderColor: 'rgba(148, 163, 184, 0.48)',
	                    color: '#94a3b8',
	                    fontFamily: MAP_UI_FONT,
	                    fontSize: '10px',
	                    textTransform: 'uppercase',
                    justifyContent: 'space-between',
                    '&:hover': { borderColor: '#58a6ff', backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                  }}
                >
                  Detailed layer list
                  {showLegacyLayerList ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                </Button>

                <Collapse
                  in={showLegacyLayerList}
                  timeout="auto"
                  unmountOnExit
	                  sx={{ mt: 1.2, pt: 1, borderTop: '1px solid rgba(148, 163, 184, 0.38)' }}
                >
            <ToggleButtonGroup
              value={activeLayers}
              onChange={handleLayerToggle}
              aria-label="map layers"
              orientation="vertical"
              sx={{ width: '100%' }}
            >
              <ToggleButton
                value="satellite"
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--palantir-blue-600)',
                    color: '#000'
                  }
                }}
              >
                <SatelliteIcon sx={{ fontSize: 16, mr: 1 }} />
                Satellite Imagery
              </ToggleButton>
              <ToggleButton
                value="terrain"
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--palantir-blue-600)',
                    color: '#000'
                  }
                }}
              >
                <TerrainIcon sx={{ fontSize: 16, mr: 1 }} />
                Topographic
              </ToggleButton>
              <ToggleButton
                value="geological"
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--palantir-blue-600)',
                    color: '#000'
                  }
                }}
              >
                <LayersIcon sx={{ fontSize: 16, mr: 1 }} />
                USGS Geological
              </ToggleButton>
              <ToggleButton
                value="street"
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--palantir-blue-600)',
                    color: '#000'
                  }
                }}
              >
                <GridIcon sx={{ fontSize: 16, mr: 1 }} />
                Street Map
              </ToggleButton>
              <ToggleButton
                value="results"
                sx={{
                  color: 'var(--palantir-text-primary)',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--palantir-blue-600)',
                    color: '#000'
                  }
                }}
              >
                <ResultsIcon sx={{ fontSize: 16, mr: 1 }} />
                🎯 Analysis Results
              </ToggleButton>
            </ToggleButtonGroup>
            {projectStaticRasters.length > 0 && (
              <Box sx={{ mt: 1.5, pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                <Typography
                  sx={{
                    color: '#58a6ff',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    fontWeight: 600
                  }}
                >
                  STATIC TERRAIN OVERLAYS
                </Typography>
                {projectStaticRasters.map((raster) => {
                  const isActive = staticRasterVisibility[raster.id] ?? false;
                  return (
                    <FormControlLabel
                      key={`static-raster-${raster.id}`}
                      sx={{
                        alignItems: 'flex-start',
                        m: 0,
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 2,
                        transition: 'background-color 120ms ease',
                        '&:hover': {
                          backgroundColor: 'rgba(88, 166, 255, 0.08)'
                        }
                      }}
                      control={
                        <Switch
                          size="small"
                          checked={isActive}
                          onChange={(_, checked) => handleStaticRasterToggle(raster.id, checked)}
                          sx={{
                            mt: 0.2,
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#58a6ff' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(88, 166, 255, 0.45)' }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <TerrainIcon sx={{ fontSize: 14, color: isActive ? '#58a6ff' : 'var(--palantir-text-muted)' }} />
                            <Typography
                              sx={{
                                color: isActive ? '#E6F1FF' : 'var(--palantir-text-primary)',
                                fontFamily: 'monospace',
                                fontSize: '10px'
                              }}
                            >
                              {raster.displayName}
                            </Typography>
                          </Box>
                          {(raster.description || raster.source) && (
                            <Typography
                              sx={{
                                color: 'var(--palantir-text-subtle)',
                                fontFamily: 'monospace',
                                fontSize: '9px'
                              }}
                            >
                              {raster.description || raster.source}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            )}
            <Box sx={{ mt: 1.5, pl: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              {selectedProject?.id !== 'project-caujul' && (
                <>
                  <Typography
                    sx={{
                      color: '#58a6ff',
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 600
                    }}
                  >
                    SGU OPEN DATA
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '9px',
                      lineHeight: 1.3
                    }}
                  >
                    Uses current map view (bbox) to fetch Sweden layers
                  </Typography>
                </>
              )}
              {/* URANIUM TARGETING LAYERS (for Sweden Uranium project) */}
              {selectedProject?.id === 'project-sweden-uranium' && (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#ffcc00',
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    URANIUM TARGETING
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={uraniumKnownDepositsVisible}
                        onChange={(e) => setUraniumKnownDepositsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#dc143c', border: '1px solid #0d1117' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Known Deposits (11)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 204, 0, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={uraniumRankedTargetsVisible}
                        onChange={(e) => setUraniumRankedTargetsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4caf50', border: '2px solid #000' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Ranked Targets (50)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 204, 0, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={palangeVerificationVisible}
                        onChange={(e) => setPalangeVerificationVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: 'rgba(0, 210, 255, 0.35)', border: '2px dashed #00d2ff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Palange Polygons + Holes
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 210, 255, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={uraniumOccurrencesVisible}
                        onChange={(e) => setUraniumOccurrencesVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffff00', border: '1px solid #ffa500' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          SGU Occurrences (254)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 204, 0, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={alumShaleVisible}
                        onChange={(e) => setAlumShaleVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '4px', backgroundColor: '#8b4513', border: '1px solid #5c3317' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Alum Shale Extent
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 204, 0, 0.08)' } }}
                  />
                  <Typography
                    sx={{
                      color: '#7ee787',
                      fontFamily: 'monospace',
                      fontSize: '9px',
                      fontWeight: 600,
                      mt: 1,
                      mb: 0.3
                    }}
                  >
                    SGU RADIOMETRICS (WMS)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={radiometricUraniumVisible}
                        onChange={(e) => setRadiometricUraniumVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                        Gamma Uranium (eU)
                      </Typography>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(126, 231, 135, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={radiometricThoriumVisible}
                        onChange={(e) => setRadiometricThoriumVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                        Gamma Thorium (eTh)
                      </Typography>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(126, 231, 135, 0.08)' } }}
                  />
                </Box>
              )}

              {/* KÖLI VMS TARGETS - NEW DISCOVERY */}
              {selectedProject?.id === 'project-sgu-sweden' && (
                <Box sx={{ mt: 1.5, mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#ff5252',
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    KÖLI VMS TARGETS
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={koliVmsTargetsVisible}
                        onChange={(e) => setKoliVmsTargetsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff0000', border: '2px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Köli VMS Targets (8)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 82, 82, 0.08)' } }}
                  />
                  <Typography
                    sx={{
                      color: '#888',
                      fontFamily: 'monospace',
                      fontSize: '8px',
                      mt: 0.5,
                      ml: 1
                    }}
                  >
                    🔴 Tier 1 (Stake Now) | 🟠 Tier 2 | 🔵 Reference (Mined)
                  </Typography>
                  <Typography
                    sx={{
                      color: '#666',
                      fontFamily: 'monospace',
                      fontSize: '8px',
                      mt: 0.3,
                      ml: 1
                    }}
                  >
                    ⬤ Gray = PERMITTED | ⬤ Green bg = OPEN GROUND
                  </Typography>

                  {/* Ropen nearby active mineral rights — sub-section */}
                  <Box sx={{ mt: 1, ml: 1, pl: 1, borderLeft: '2px solid rgba(239, 83, 80, 0.4)' }}>
                    <Typography
                      sx={{
                        color: '#ef5350',
                        fontFamily: 'monospace',
                        fontSize: '9px',
                        fontWeight: 600,
                        mb: 0.3,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      ↳ Ropen — Nearby Claims
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={ropenNearbyPermitsVisible}
                          onChange={(e) => setRopenNearbyPermitsVisible(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 10, height: 10, backgroundColor: 'rgba(198,40,40,0.35)', border: '1.5px solid #b71c1c' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Active Permits &amp; Concessions (8)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.2, px: 0.3, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(239, 83, 80, 0.08)' } }}
                    />
                    <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '8px', mt: 0.3, ml: 0.5 }}>
                      Dark = concession · Light = exploration · Dashed = pending
                    </Typography>
                    <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '8px', mt: 0.2, ml: 0.5 }}>
                      Closest: Njeretjakke nr 1001 (Greenhills AS) — 8.9 km
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* BERGSLAGEN TILL GEOCHEM (AR-ICPMS) */}
              {selectedProject?.id === 'project-sgu-sweden' && (
                <Box sx={{ mt: 1.5, mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#f1c40f',
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    BERGSLAGEN TILL (AR-ICPMS, 2022 SGU)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={bergslagenTillAuVisible}
                        onChange={(e) => setBergslagenTillAuVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#e74c3c', border: '2px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Au Till Samples (2,454)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(241, 196, 15, 0.08)' } }}
                  />
                  <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '8px', mt: 0.2, ml: 1 }}>
                    🔵 &lt;100 ppb · 🟦 100-500 · 🟡 500-1,000 · 🟧 1,000-3,000 · 🔴 &gt;3,000
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={bergslagenTillTeVisible}
                        onChange={(e) => setBergslagenTillTeVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#9b59b6', border: '2px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Te Till Samples (1,923)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(155, 89, 182, 0.08)' } }}
                  />
                  <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '8px', mt: 0.2, ml: 1 }}>
                    🔵 &lt;1k · 🟦 1k-5k · 🟡 5k-15k · 🟧 15k-30k · 🟣 &gt;30k ppb
                  </Typography>
                  <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '8px', mt: 0.3, ml: 1 }}>
                    2,482 samples across Bergslagen (14-16°E / 59-61°N). Raw data: moran_0063mm_ar_icpms.csv
                  </Typography>
                </Box>
              )}

              {/* SWEDEN DRILL CLUSTERS — review queue rendered from _status.json */}
              {selectedProject?.id === 'project-sgu-sweden' && (
                <Box sx={{ mt: 1.5, mb: 1, pl: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={swedenDrillClustersVisible}
                        onChange={(e) => setSwedenDrillClustersVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Drill Clusters (review)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.08)' } }}
                  />
                  {swedenDrillClustersVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(128,128,128,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>pending</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>defined</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.85)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>promoted</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.6)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>rejected</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(234,179,8,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>monitor</Typography>
                    </Box>
                  )}
                  <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '8px', mt: 0.3, ml: 1 }}>
                    654 reviewed clusters + 95 rescue candidates. Source: geomorphic-docs/sweden/drill-clusters/_status.json
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={swedenSingletonsVisible}
                        onChange={(e) => setSwedenSingletonsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, transform: 'rotate(45deg)', backgroundColor: 'rgba(255,152,0,0.8)', border: '1.5px solid #e65100' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Singletons (Tier 4)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.08)' } }}
                  />
                  {swedenSingletonsVisible && (
                    <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '8px', mt: 0.2, ml: 1 }}>
                      35 single-hole prospects: assays uploaded, mineral operator, &gt;8 km from any cluster, outside active-mine buffer. Brightness ∝ score. Click for hole metadata.
                    </Typography>
                  )}
                </Box>
              )}

              {/* DRILL CLUSTERS — 8 new countries seeded 2026-05-03 */}
              {['project-norway-ngu', 'project-ireland-gsi', 'project-peru-wide', 'project-canada-ontario', 'project-canada-quebec', 'project-australia-wa', 'project-france-brgm', 'project-colombia-sgc'].includes(selectedProject?.id || '') && (
                <Box sx={{ mt: 1.5, mb: 1, pl: 0.5 }}>
                  <Typography sx={{ color: '#a855f7', fontFamily: 'monospace', fontSize: '10px', fontWeight: 600, mb: 0.5 }}>
                    DRILL CLUSTERS (review)
                  </Typography>
                  {selectedProject?.id === 'project-norway-ngu' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={norwayDrillClustersVisible} onChange={(e) => setNorwayDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Norway (49)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-ireland-gsi' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={irelandDrillClustersVisible} onChange={(e) => setIrelandDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Ireland (360)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-peru-wide' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={peruDrillClustersVisible} onChange={(e) => setPeruDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Peru (13)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-canada-ontario' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={ontarioDrillClustersVisible} onChange={(e) => setOntarioDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Ontario (3)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-australia-wa' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={australiaDrillClustersVisible} onChange={(e) => setAustraliaDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — WA (4,281)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-canada-quebec' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={quebecDrillClustersVisible} onChange={(e) => setQuebecDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Québec (451)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-france-brgm' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={franceDrillClustersVisible} onChange={(e) => setFranceDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — France (200: 87 promote / 33 monitor / 80 pending)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  {selectedProject?.id === 'project-colombia-sgc' && (
                    <FormControlLabel
                      control={<Switch size="small" checked={colombiaDrillClustersVisible} onChange={(e) => setColombiaDrillClustersVisible(e.target.checked)} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Drill Clusters — Colombia (71: 2 tier_2 / 11 monitor / 3 killed / 55 pending)
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2 }}
                    />
                  )}
                  <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '8px', mt: 0.3, ml: 1 }}>
                    Seeded 2026-05-03 — pending review. Same legend as Sweden/Finland.
                  </Typography>
                </Box>
              )}

              {/* COMPETITOR CLAIMS SECTION */}
              {(selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') && (
                <Box sx={{ mt: 1.5, mb: 1, pl: 0.5 }}>
                  <Typography sx={{ color: '#ff6f00', fontFamily: 'monospace', fontSize: '10px', fontWeight: 600, mb: 0.5 }}>
                    COMPETITORS
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={koboldClaimsVisible}
                        onChange={(e) => setKoboldClaimsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: 'transparent', border: '2px solid #e53935' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: 'transparent', border: '2px solid #ff6f00' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          KoBold Claims (4 FI + 8 SE)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 111, 0, 0.08)' } }}
                  />
                  {koboldClaimsVisible && (
                    <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '8px', mt: 0.2, ml: 4 }}>
                      Red=FI · Orange=SE · solid=permit · dashed=reservation · dotted=application
                    </Typography>
                  )}
                </Box>
              )}

              {/* STAKING BOUNDARIES SECTION */}
              {(selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-gtk-finland') && (
                <Box sx={{ mt: 1.5, mb: 1, pl: 0.5 }}>
                  <Typography sx={{ color: '#00e5ff', fontFamily: 'monospace', fontSize: '10px', fontWeight: 600, mb: 0.5 }}>
                    STAKING CONFIRMED
                  </Typography>
                  <Typography sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'monospace', fontSize: '9px', mb: 1 }}>
                    Validated 2026-03-16 vs live permit data
                  </Typography>

                  {/* === SWEDEN CONFIRMED === */}
                  {selectedProject?.id === 'project-sgu-sweden' && (<>
                  {/* Northern Sweden */}
                  <Box sx={{ mb: 0.5 }}>
                    <Typography sx={{ color: '#00e5ff', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Northern Sweden
                    </Typography>
                    {[
                      { id: 'njuggtraskliden_expanded', label: 'Njuggtraskliden', color: '#38bdf8', area: '12.4k', status: 'OPEN' },
                      { id: 'seitevare', label: 'Seitevare 2026:80', color: '#00E5FF', area: '25.5k', status: 'GRANT' },
                      { id: 'koberget', label: 'Koberget 2026:82', color: '#22c55e', area: '1.6k', status: 'GRANT' },
                      { id: 'njuorram', label: 'Njuorram 2026:81', color: '#3b82f6', area: '2.0k', status: 'GRANT' },
                      { id: 'kiuri_district_core', label: 'Kiuri District Core', color: '#94a3b8', area: '7.8k', status: 'ALT' },
                      { id: 'adakfaltet', label: 'Adakfaltet', color: '#64748b', area: '6.3k', status: 'BLOCKED' },
                      { id: 'aijajarvi', label: 'Aijajarvi', color: '#14b8a6', area: '740', status: '24% OPEN' },
                      { id: 'klippen_au', label: 'Klippen Au', color: '#22d3ee', area: '954', status: 'UNRESOLVED' },
                    ].map((tier) => (
                      <Box
                        key={tier.id}
                        onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3,
                          cursor: 'pointer', borderRadius: 1,
                          opacity: stakingTierVisibility[tier.id] ? 1 : (tier.status === 'BLOCKED' ? 0.25 : 0.4),
                          '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: tier.status === 'BLOCKED' ? '#666' : '#e6edf3', textDecoration: tier.status === 'BLOCKED' ? 'line-through' : 'none' }}>
                          {tier.label}
                        </Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: tier.status === 'OPEN' ? '#4caf50' : tier.status === 'BLOCKED' ? '#ef5350' : '#ff9800', ml: 'auto' }}>
                          {tier.area}
                        </Typography>
                      </Box>
                    ))}
                    {/* Kiuri sub-layers */}
                    <Box
                      onClick={() => setKiuriDrillholesVisible(!kiuriDrillholesVisible)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: kiuriDrillholesVisible ? 1 : 0.4, ml: 1, borderLeft: '2px solid #00bcd4', '&:hover': { backgroundColor: 'rgba(0, 188, 212, 0.1)' } }}
                    >
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                      <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#aaa' }}>Project Drillholes (177)</Typography>
                    </Box>
                    <Box
                      onClick={() => setKiuriDistrictHolesVisible(!kiuriDistrictHolesVisible)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: kiuriDistrictHolesVisible ? 1 : 0.4, ml: 1, borderLeft: '2px solid #E53935', '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.1)' } }}
                    >
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#E53935' }} />
                      <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#aaa' }}>District Context Holes (156)</Typography>
                    </Box>
                    <Box
                      onClick={() => setKiuriGrabSamplesVisible(!kiuriGrabSamplesVisible)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: kiuriGrabSamplesVisible ? 1 : 0.4, ml: 1, borderLeft: '2px solid #00bcd4', '&:hover': { backgroundColor: 'rgba(0, 188, 212, 0.1)' } }}
                    >
                      <Box sx={{ width: 5, height: 5, transform: 'rotate(45deg)', backgroundColor: '#9c27b0' }} />
                      <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#aaa' }}>Kiuri Grab Samples (5)</Typography>
                    </Box>
                    {KIURI_GRANT_BLOCKS.map((block) => (
                      <Box
                        key={`kiuri-grant-${block.id}`}
                        onClick={() => setKiuriApplicationPolygonBlockVisible(block.id, !kiuriApplicationPolygonVisibility[block.id])}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: kiuriApplicationPolygonVisibility[block.id] ? 1 : 0.4, ml: 1, borderLeft: `2px solid ${block.color}`, '&:hover': { backgroundColor: 'rgba(56, 189, 248, 0.1)' } }}
                      >
                        <Box sx={{ width: 6, height: 6, border: `1.5px dashed ${block.color}` }} />
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#aaa' }}>
                          {block.name} {block.permitId} ({block.area})
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      onClick={() => setGranuddenDraftVisible(!granuddenDraftVisible)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        py: 0.15,
                        px: 0.3,
                        cursor: 'pointer',
                        borderRadius: 1,
                        opacity: granuddenDraftVisible ? 1 : 0.4,
                        ml: 1,
                        backgroundColor: granuddenDraftVisible ? 'rgba(20, 184, 166, 0.08)' : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(20, 184, 166, 0.12)' }
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, border: `1.5px dashed ${GRANUDDEN_DRAFT_LAYER.color}` }} />
                      <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#aaa' }}>
                        Granudden — ONSHORE DRAFT ({GRANUDDEN_DRAFT_LAYER.area}; evidence shown)
                      </Typography>
                    </Box>
                  </Box>

                  {/* Köli VMS Belt */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(0, 188, 212, 0.3)' }}>
                    <Typography sx={{ color: '#00bcd4', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Köli VMS Belt
                    </Typography>
                    {[
                    { id: 'ropen_vms_confirmed', label: 'Ropen Original District', color: '#00bcd4', area: '10.1k', status: 'ORIG' },
                    { id: 'ropen_vms_east_extension', label: 'Ropen Filed Polygon', color: '#cddc39', area: '10.5k', status: 'FILED' },
                    { id: 'ropen_vms_updated', label: 'Ropen Updated Core', color: '#4caf50', area: '3.5k', status: 'UPDATED' },
                    { id: 'ropen_vms_split', label: 'Ropen Heritage-Skirted Split', color: '#38bdf8', area: '1.5k', status: 'SPLIT' },
                    { id: 'ropen_vms_shoreline', label: 'Ropen Provisional Shoreline Split', color: '#2dd4bf', area: '1.16k', status: 'SHORE' },
                  ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: tier.color, ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                    <Typography sx={{ fontSize: '6.5px', fontFamily: 'monospace', color: '#666', mt: 0.25, ml: 0.3 }}>
                      Original = current 7-target district concept. Extension = original + full Abelvattnet collar coverage. Updated = western core only.
                    </Typography>
                    {/* Ropen VMS occurrence sub-toggles */}
                    <Box sx={{ pl: 1.5, mt: 0.2 }}>
                      <Typography sx={{ fontSize: '6.5px', fontFamily: 'monospace', color: '#666', mb: 0.2 }}>Zn-Pb-Ag-Au zone (west)</Typography>
                      {[
                        { id: 'ropen_pt_ropen', label: 'Ropen', detail: '11.38% Zn, 253 Ag, 1.2 Au', color: '#ff5252' },
                        { id: 'ropen_pt_rikker', label: 'Rikker', detail: '3.13% Zn, 70 Ag', color: '#ff9800' },
                        { id: 'ropen_pt_rikkervardo', label: 'Rikkervardo', detail: 'Zn-Pb', color: '#ffd740' },
                        { id: 'ropen_pt_jemesjauretje', label: 'Jemesjauretje', detail: 'Zn-Pb', color: '#ffd740' },
                      ].map((pt) => (
                        <Box key={pt.id} onClick={() => toggleStakingTier(pt.id, !stakingTierVisibility[pt.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, py: 0.1, px: 0.2, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[pt.id] ? 1 : 0.35, '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.08)' } }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: pt.color, border: stakingTierVisibility[pt.id] ? '1.5px solid #fff' : '1px solid #555', flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#ccc' }}>{pt.label}</Typography>
                          <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#888', ml: 'auto', whiteSpace: 'nowrap' }}>{pt.detail}</Typography>
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '6.5px', fontFamily: 'monospace', color: '#666', mt: 0.3, mb: 0.2 }}>Cu-proximal zone (east)</Typography>
                      {[
                        { id: 'ropen_pt_bijetjatje', label: 'Bijetjatje', detail: 'Cu massive sulph.', color: '#40c4ff' },
                        { id: 'ropen_pt_kebnevardo', label: 'Kebnevardo', detail: 'Cu-Zn-Pb-Ag', color: '#40c4ff' },
                      ].map((pt) => (
                        <Box key={pt.id} onClick={() => toggleStakingTier(pt.id, !stakingTierVisibility[pt.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, py: 0.1, px: 0.2, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[pt.id] ? 1 : 0.35, '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.08)' } }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: pt.color, border: stakingTierVisibility[pt.id] ? '1.5px solid #fff' : '1px solid #555', flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#ccc' }}>{pt.label}</Typography>
                          <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#888', ml: 'auto', whiteSpace: 'nowrap' }}>{pt.detail}</Typography>
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '6.5px', fontFamily: 'monospace', color: '#666', mt: 0.3, mb: 0.2 }}>Drilled prospects (SE)</Typography>
                      {[
                        { id: 'ropen_pt_usmeten', label: 'Usmeten', detail: '153kt 1.27% Cu, 70 holes', color: '#76ff03' },
                      ].map((pt) => (
                        <Box key={pt.id} onClick={() => toggleStakingTier(pt.id, !stakingTierVisibility[pt.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, py: 0.1, px: 0.2, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[pt.id] ? 1 : 0.35, '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.08)' } }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: pt.color, border: stakingTierVisibility[pt.id] ? '1.5px solid #fff' : '1px solid #555', flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#ccc' }}>{pt.label}</Typography>
                          <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#888', ml: 'auto', whiteSpace: 'nowrap' }}>{pt.detail}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Central Sweden */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(0, 188, 212, 0.3)' }}>
                    <Typography sx={{ color: '#26a69a', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Bergslagen W Corridor
                    </Typography>
                    {[
                      { id: 'w_belt', label: 'Dyranfältet (3 claims)', color: '#26a69a', area: '4.8k', status: 'OPEN' },
                      { id: 'svardsjo_norra_w', label: 'Svärdsjö Norra W', color: '#26a69a', area: '~1.2k', status: 'OPEN' },
                      { id: 'fajsmyran_w', label: 'Fajsmyran W-Skarn', color: '#26a69a', area: '~1.5k', status: 'OPEN' },
                      { id: 'lurgarden_w', label: 'Lurgården W-Skarn', color: '#26a69a', area: '~6.5k', status: 'OPEN' },
                      { id: 'hofors_torsaker_w', label: 'Hofors-Torsåker W-Mo', color: '#26a69a', area: '749', status: 'CONFIRMED' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#4caf50', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Bergslagen Au-Te */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(14, 165, 233, 0.3)' }}>
                    <Typography sx={{ color: '#38bdf8', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Bergslagen (Au-Te)
                    </Typography>
                    {[
                      { id: 'saxe_au_te', label: 'Saxe nr 1 (FILED)', color: '#22c55e', area: '26.6k', status: 'FILED' },
                      { id: 'pershytte_east', label: 'Pershytte East', color: '#3b82f6', area: '~22k', status: 'OPEN' },
                      { id: 'bottengruvan', label: 'Bottengruvan', color: '#3b82f6', area: '~5k', status: 'OPEN' },
                      { id: 'ingagruvan_south', label: 'Ingagruvan-South', color: '#3b82f6', area: '~10k', status: 'OPEN' },
                      { id: 'ostanfalla', label: 'Ostanfalla', color: '#3b82f6', area: '~1k', status: 'OPEN' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(14, 165, 233, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#4caf50', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Saxe — Deep Dive (2026-05) */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <Typography sx={{ color: '#22c55e', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Saxe — Deep Dive (2026-05)
                    </Typography>
                    {SAXE_VECTOR_LAYERS.map((lyr) => (
                      <Box key={lyr.id} onClick={() => toggleSaxeLayer(lyr.id, !saxeLayerVisibility[lyr.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: saxeLayerVisibility[lyr.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#22c55e', border: saxeLayerVisibility[lyr.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{lyr.label}</Typography>
                      </Box>
                    ))}
                    <Typography sx={{ color: '#9c27b0', fontSize: '7px', fontFamily: 'monospace', fontWeight: 600, mt: 0.4, mb: 0.2 }}>
                      Geophysics (FECA 2016)
                    </Typography>
                    {SAXE_RASTER_LAYERS.map((lyr) => (
                      <Box key={lyr.id} onClick={() => toggleSaxeRaster(lyr.id, !saxeRasterVisibility[lyr.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: saxeRasterVisibility[lyr.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#9c27b0', border: saxeRasterVisibility[lyr.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{lyr.label}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Sweden Exploratory */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed rgba(88, 166, 255, 0.2)' }}>
                    <Typography sx={{ color: '#58a6ff', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3, opacity: 0.6 }}>
                      Exploratory / Pipeline
                    </Typography>
                    {[
                      { id: 'laisvall', label: 'Laisvall 47%', color: '#3b82f6', area: '3.8k' },
                      { id: 'autjak', label: 'Autjak 79%', color: '#00bcd4', area: '2.9k' },
                      { id: 'bjorntjarn', label: 'Bjorntjarn 98%', color: '#3b82f6', area: '2.9k' },
                      { id: 'pleutajokk', label: 'Pleutajokk 79%', color: '#26a69a', area: '1.1k' },
                      { id: 'njuggtraskliden', label: 'Njugg (original)', color: '#78909c', area: '14.4k' },
                      { id: 'mjovattnet_watch', label: 'Mjovattnet (CONTESTED)', color: '#78909c', area: '1.9k' },
                      { id: 'mjovattnet_nr101_block', label: 'Mjovattnet regional block (Boliden nr 1001)', color: '#ef4444', area: '5.8k' },
                      { id: 'njugg_boliden_district', label: 'Boliden Skellefte district (48 permits)', color: '#f43f5e', area: '100k' },
                      { id: 'sodra_svalvatjakko', label: 'S. Svalvatjakko', color: '#5c6bc0', area: '3.6k' },
                      { id: 'dalovardo', label: 'Dalovardo', color: '#5c6bc0', area: '15k' },
                      { id: 'norra_svalvatjakko', label: 'N. Svalvatjakko', color: '#3b82f6', area: '3.8k' },
                      { id: 'birdotjarre', label: 'Birdotjarre', color: '#3b82f6', area: '4.5k' },
                      { id: 'birdejaure', label: 'Birdejaure', color: '#3b82f6', area: '5.6k' },
                      { id: 'bergslagen_south_au', label: 'Bergslagen South Au', color: '#22c55e', area: '10.8k' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.15, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.3, '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' } }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #444' }} />
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#8b949e' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#484f58', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>
                  </>)}

                  {/* === FINLAND CONFIRMED === */}
                  {selectedProject?.id === 'project-gtk-finland' && (<>
                  {/* Lapland */}
                  <Box sx={{ mb: 0.5 }}>
                    <Typography sx={{ color: '#2196f3', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Lapland
                    </Typography>
                    {[
                      { id: 'koitelainen', label: 'Koitelainen Cr-V-PGE', color: '#2196f3', area: '9.3k', status: 'OPEN' },
                      { id: 'kaitaselka', label: 'Kaitaselka Corridor Cu-Au', color: '#4caf50', area: '276', status: 'STAKE NOW' },
                      { id: 'kuusivaara', label: 'Kuusivaara Au-Co', color: '#14b8a6', area: '~3k', status: 'COMPETITIVE' },
                      { id: 'salla_gold', label: 'Salla Corridor Au', color: '#2196f3', area: '5.9k', status: 'OPEN' },
                      { id: 'tongosoivanaapa', label: 'Tongosoivanaapa Ni-Cu', color: '#2196f3', area: '~15k', status: 'OPEN' },
                      { id: 'tervola_cu', label: 'Tervola Cu', color: '#2196f3', area: '3.5k', status: '96% OPEN' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: tier.status === 'OPEN' || tier.status === 'STAKE NOW' ? '#4caf50' : tier.status === 'COMPETITIVE' ? '#ff9800' : '#4caf50', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Central/Eastern Finland */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(33, 150, 243, 0.3)' }}>
                    <Typography sx={{ color: '#2196f3', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Central & Eastern Finland
                    </Typography>
                    {[
                      { id: 'raahe_ladoga', label: 'Raahe-Ladoga Au (v3.1.5)', color: '#22d3ee', area: '3.8k', status: 'STAKE NOW' },
                      { id: 'paljakka', label: 'Paljakka Cu-Au (v3)', color: '#38bdf8', area: '5.7k', status: 'STAKE NOW' },
                      { id: 'paljakka_karenssi', label: 'Paljakka +karenssi wedge', color: '#2dd4bf', area: '6.0k', status: 'UPSIDE' },
                      { id: 'paljakka_v4', label: 'Paljakka v4 (R314 capture)', color: '#22c55e', area: '6.2k', status: 'PREFERRED' },
                      { id: 'hammaslahti', label: 'Hammaslahti Cu-Zn', color: '#2196f3', area: '~3k', status: 'OPEN' },
                      { id: 'jarvi_pohjanmaa', label: 'Jarvi-Pohjanmaa Li', color: '#22c55e', area: '~9k', status: 'OPEN' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: tier.status.includes('OPEN') || tier.status === 'STAKE NOW' ? '#4caf50' : '#ff9800', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Western Finland */}
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(0, 191, 165, 0.3)' }}>
                    <Typography sx={{ color: '#00bfa5', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, mb: 0.3 }}>
                      Western Finland
                    </Typography>
                    {[
                      { id: 'ritakallionmaa', label: 'Ritakallionmaa Au (v3)', color: '#2196f3', area: '3,243 ha', status: 'STAKE NOW' },
                      { id: 'ritakallionmaa_option_f', label: 'Rita Option F district umbrella (FILING-READY)', color: '#0ea5e9', area: '14,548 ha', status: 'FILE NOW' },
                      { id: 'dragon_jokisivu', label: 'Dragon Jokisivu (8 tenures)', color: '#ff3333', area: '600', status: 'COMPETITOR' },
                      { id: 'dragon_uunimaki', label: 'Dragon Uunimäki 1 + 2', color: '#ff3333', area: '1.4k', status: 'COMPETITOR' },
                      { id: 'vammala_inferred_corridor', label: 'Vammala inferred corridor (dashed)', color: '#00bcd4', area: '10.9k', status: 'SPECULATIVE' },
                      { id: 'cluster_317_ritakallionmaa', label: 'Cluster 317 — Ritakallionmaa (our drill set)', color: '#22c55e', area: '30 holes', status: 'PROMOTE' },
                      { id: 'cluster_196_palokallio', label: 'Cluster 196 — Palokallio (13/17 holes ≥0.5 g/t Au, R329 4.75 m @ 9.10 g/t, full pathfinder Bi ρ=0.86)', color: '#facc15', area: '23 holes', status: 'STAKE-NOW SATELLITE' },
                      { id: 'cluster_422_punovuori', label: 'Cluster 422 — Punovuori (Ni-Cu-PGE peridotite, no report)', color: '#ef4444', area: '3 holes', status: 'KILLED' },
                      { id: 'cluster_753_vanttila', label: 'Cluster 753 — Vanttila (Ni-Cu, rejected, wrong commodity)', color: '#ef4444', area: '11 holes', status: 'KILLED' },
                      { id: 'perakorpi_lumikangas', label: 'Lumikangas Ti-P', color: '#00bfa5', area: '2.5k', status: 'OPEN' },
                      { id: 'perakorpi_kauhajarvi', label: 'Kauhajärvi Ti-P', color: '#00bfa5', area: '3.6k', status: 'OPEN' },
                      { id: 'perakorpi_peramaa', label: 'Perämaa Ti-P', color: '#00897b', area: '3.5k', status: 'OPEN' },
                      { id: 'perakorpi_hyyppa', label: 'Hyyppä Ti (early)', color: '#80cbc4', area: '1.2k', status: 'OPEN' },
                    ].map((tier) => (
                      <Box key={tier.id} onClick={() => toggleStakingTier(tier.id, !stakingTierVisibility[tier.id])} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, py: 0.2, px: 0.3, cursor: 'pointer', borderRadius: 1, opacity: stakingTierVisibility[tier.id] ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(0, 191, 165, 0.1)' } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: tier.color, border: stakingTierVisibility[tier.id] ? '2px solid #fff' : '1px solid #555' }} />
                        <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>{tier.label}</Typography>
                        <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#4caf50', ml: 'auto' }}>{tier.area}</Typography>
                      </Box>
                    ))}
                  </Box>
                  </>)}

                  {projectGeoJsonLayersForProject.length > 0 && (
                    <Box sx={{ mt: 0.7, pt: 0.6, borderTop: '1px dashed rgba(148, 163, 184, 0.24)' }}>
                      <Box
                        onClick={() => setProjectGeoJsonExpanded(!projectGeoJsonExpanded)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          borderRadius: 1,
                          py: 0.2,
                          px: 0.3,
                          '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' },
                        }}
                      >
                        <Typography sx={{ color: '#cbd5e1', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, flex: 1 }}>
                          SOURCE GEOJSON POLYGONS ({projectGeoJsonLayersForProject.length})
                        </Typography>
                        {projectGeoJsonExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />
                        )}
                      </Box>
                      <Collapse in={projectGeoJsonExpanded}>
                        <Box sx={{ mt: 0.3 }}>
                          {projectGeoJsonLayersForProject.map((layerDef) => {
                            const visible = Boolean(projectGeoJsonVisibility[layerDef.id]);
                            const displayColor = projectGeoJsonDisplayColorById[layerDef.id] || layerDef.color;
                            return (
                              <Box
                                key={layerDef.id}
                                onClick={() => {
                                  setProjectGeoJsonVisibility(prev => ({
                                    ...prev,
                                    [layerDef.id]: !prev[layerDef.id]
                                  }));
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.45,
                                  py: 0.18,
                                  px: 0.3,
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  opacity: visible ? 1 : 0.42,
                                  '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: layerDef.dashed ? '50%' : '2px',
                                    backgroundColor: visible ? displayColor : 'transparent',
                                    border: `${visible ? 2 : 1}px ${layerDef.dashed ? 'dashed' : 'solid'} ${displayColor}`,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#cbd5e1', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                  {layerDef.label}
                                </Typography>
                                {layerDef.detail && (
                                  <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: displayColor, flexShrink: 0 }}>
                                    {layerDef.detail}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Zoom buttons */}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    {selectedProject?.id === 'project-sgu-sweden' && (<>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([18.51, 66.91]), zoom: 11, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#00e5ff', borderColor: '#00e5ff', py: 0.2, minWidth: '55px' }}>Jokkmokk</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([19.29, 64.82]), zoom: 12, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#00e5ff', borderColor: '#00e5ff', py: 0.2, minWidth: '55px' }}>Njugg</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([15.2, 65.8]), zoom: 8, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#00bcd4', borderColor: '#00bcd4', py: 0.2, minWidth: '55px' }}>Koli</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([15.1, 59.5]), zoom: 9, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#ffd700', borderColor: '#ffd700', py: 0.2, minWidth: '55px' }}>Bergslagen</Button>
                    </>)}
                    {selectedProject?.id === 'project-gtk-finland' && (<>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([27.3, 67.85]), zoom: 11, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#2196f3', borderColor: '#2196f3', py: 0.2, minWidth: '55px' }}>Koitelainen</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([26.8, 67.35]), zoom: 10, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#2196f3', borderColor: '#2196f3', py: 0.2, minWidth: '55px' }}>Salla</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([28.3, 63.44]), zoom: 11, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#2196f3', borderColor: '#2196f3', py: 0.2, minWidth: '55px' }}>Paljakka</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([24.195404, 63.125135]), zoom: 11, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#ffd700', borderColor: '#ffd700', py: 0.2, minWidth: '55px' }}>Raahe-Ladoga</Button>
                    <Button size="small" variant="outlined" onClick={() => { const view = mapInstanceRef.current?.getView(); if (view) view.animate({ center: fromLonLat([22.25, 62.25]), zoom: 10, duration: 500 }); }} sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#00bfa5', borderColor: '#00bfa5', py: 0.2, minWidth: '55px' }}>Peräkorpi</Button>
                    </>)}
                  </Box>

                  {/* Njuggtraskliden Geophysics Analysis */}
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0, 229, 255, 0.3)' }}>
                    <Box
                      onClick={() => setNjuggAnalysisExpanded(!njuggAnalysisExpanded)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' },
                        borderRadius: 1,
                        py: 0.2,
                        px: 0.3,
                      }}
                    >
                      <Typography sx={{ color: '#00e5ff', fontSize: '8px', fontFamily: 'monospace', fontWeight: 600, flex: 1 }}>
                        📊 NJUGGTRÄSKLIDEN ANALYSIS
                      </Typography>
                      {njuggAnalysisExpanded ? (
                        <ExpandLessIcon sx={{ fontSize: 14, color: '#00e5ff' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 14, color: '#00e5ff' }} />
                      )}
                    </Box>
                    <Collapse in={njuggAnalysisExpanded}>
                      <Box sx={{ mt: 0.5 }}>
                        {/* Aeromagnetic Gradient Layer Toggle (SGU 1973/1977) */}
                        <Box
                          onClick={() => setNjuggAeromagLayerVisible(!njuggAeromagLayerVisible)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.2,
                            px: 0.3,
                            cursor: 'pointer',
                            borderRadius: 1,
                            opacity: njuggAeromagLayerVisible ? 1 : 0.5,
                            '&:hover': { backgroundColor: 'rgba(255, 0, 255, 0.1)' }
                          }}
                        >
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            background: 'linear-gradient(90deg, #0000FF, #00FFFF, #00FF00, #FFFF00, #FF00FF)',
                            border: njuggAeromagLayerVisible ? '2px solid #fff' : '1px solid #555'
                          }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Aeromagnetic ΔT (SGU 1973)
                          </Typography>
                        </Box>
                        <Collapse in={njuggAeromagLayerVisible}>
                          <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#666', ml: 2, mt: 0.3 }}>
                            Pink/Magenta = high mag (ultramafic) | Blue = low
                          </Typography>
                        </Collapse>

                        {/* Density Layer Map Toggle */}
                        <Box
                          onClick={() => setNjuggDensityLayerVisible(!njuggDensityLayerVisible)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.2,
                            px: 0.3,
                            cursor: 'pointer',
                            borderRadius: 1,
                            opacity: njuggDensityLayerVisible ? 1 : 0.5,
                            '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' }
                          }}
                        >
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            backgroundColor: '#00bcd4',
                            border: njuggDensityLayerVisible ? '2px solid #fff' : '1px solid #555'
                          }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Density Model (Map)
                          </Typography>
                        </Box>

                        {/* Depth Selector - shown when layer is visible */}
                        <Collapse in={njuggDensityLayerVisible}>
                          <Box sx={{ ml: 2, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#8b949e', mb: 0.3 }}>
                              Depth slice:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                              {[50, 150, 300, 500].map((depth) => (
                                <Chip
                                  key={depth}
                                  label={`${depth}m`}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNjuggDensityDepth(depth);
                                  }}
                                  sx={{
                                    height: 16,
                                    fontSize: '7px',
                                    fontFamily: 'monospace',
                                    backgroundColor: njuggDensityDepth === depth ? '#00bcd4' : 'rgba(255,255,255,0.1)',
                                    color: njuggDensityDepth === depth ? '#000' : '#8b949e',
                                    '&:hover': { backgroundColor: njuggDensityDepth === depth ? '#00bcd4' : 'rgba(0, 188, 212, 0.3)' }
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#666', mt: 0.3 }}>
                              Red = ultramafic (+ρ) | Blue = graphite (-ρ)
                            </Typography>
                          </Box>
                        </Collapse>

                        {/* Cross-sections Button */}
                        <Box
                          onClick={() => setNjuggCrossSectionOpen(true)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.2,
                            px: 0.3,
                            mt: 0.3,
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.1)' }
                          }}
                        >
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            backgroundColor: '#ff9800',
                            border: '1px solid #555'
                          }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Density Cross-Sections ▸
                          </Typography>
                        </Box>

                        {/* Magnetic Susceptibility Layer Toggle */}
                        <Box
                          onClick={() => setNjuggMagneticLayerVisible(!njuggMagneticLayerVisible)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.2,
                            px: 0.3,
                            mt: 0.5,
                            cursor: 'pointer',
                            borderRadius: 1,
                            opacity: njuggMagneticLayerVisible ? 1 : 0.5,
                            '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                          }}
                        >
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            backgroundColor: '#ff5722',
                            border: njuggMagneticLayerVisible ? '2px solid #fff' : '1px solid #555'
                          }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Susceptibility Model (Map)
                          </Typography>
                        </Box>

                        {/* Magnetic Depth Selector - shown when layer is visible */}
                        <Collapse in={njuggMagneticLayerVisible}>
                          <Box sx={{ ml: 2, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#8b949e', mb: 0.3 }}>
                              Depth slice:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                              {[50, 150, 300, 500].map((depth) => (
                                <Chip
                                  key={depth}
                                  label={`${depth}m`}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNjuggMagneticDepth(depth);
                                  }}
                                  sx={{
                                    height: 16,
                                    fontSize: '7px',
                                    fontFamily: 'monospace',
                                    backgroundColor: njuggMagneticDepth === depth ? '#ff5722' : 'rgba(255,255,255,0.1)',
                                    color: njuggMagneticDepth === depth ? '#fff' : '#8b949e',
                                    '&:hover': { backgroundColor: njuggMagneticDepth === depth ? '#ff5722' : 'rgba(255, 87, 34, 0.3)' }
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography sx={{ fontSize: '6px', fontFamily: 'monospace', color: '#666', mt: 0.3 }}>
                              Yellow/Red = high susceptibility (magnetite)
                            </Typography>
                          </Box>
                        </Collapse>

                        {/* Magnetic Cross-sections Button */}
                        <Box
                          onClick={() => setNjuggMagneticCrossSectionOpen(true)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.2,
                            px: 0.3,
                            mt: 0.3,
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                          }}
                        >
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            backgroundColor: '#ff5722',
                            border: '1px solid #555'
                          }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            Magnetic Cross-Sections ▸
                          </Typography>
                        </Box>

                        {/* Zoom to Njuggtraskliden button */}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const view = mapInstanceRef.current?.getView();
                            if (view) {
                              view.animate({ center: fromLonLat([19.278, 64.831]), zoom: 12, duration: 500 });
                            }
                          }}
                          sx={{
                            mt: 0.5,
                            fontSize: '7px',
                            fontFamily: 'monospace',
                            color: '#00bcd4',
                            borderColor: '#00bcd4',
                            py: 0.2,
                            width: '100%'
                          }}
                        >
                          Zoom to Deposit
                        </Button>
                      </Box>
                    </Collapse>
                  </Box>

                </Box>
              )}

              {/* FINLAND (GTK) LAYERS */}
              {selectedProject?.id === 'project-gtk-finland' && (
                <Box sx={{ mt: 1.5, mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#2196f3',
                      fontFamily: 'monospace',
                      fontSize: '10px',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    FINLAND (GTK) DATA
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandMineralDepositsVisible}
                        onChange={(e) => setFinlandMineralDepositsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffd700' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Mineral Deposits (1,090)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' } }}
                  />
                  {finlandMineralDepositsVisible && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 4, mb: 0.5 }}>
                      {[
                        { color: '#ffd700', label: 'Au' },
                        { color: '#ff5722', label: 'Cu' },
                        { color: '#4caf50', label: 'Ni' },
                        { color: '#9c27b0', label: 'Zn' },
                        { color: '#795548', label: 'Fe' },
                        { color: '#2196f3', label: 'Co' },
                        { color: '#00bcd4', label: 'Li' },
                      ].map(({ color, label }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
                          <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#8b949e' }}>{label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {FINLAND_EXPLORATION_PERMIT_STATUS_ORDER.map((statusKey) => {
                    const visual = FINLAND_EXPLORATION_PERMIT_VISUALS[statusKey];
                    return (
                      <FormControlLabel
                        key={`legacy-finland-exploration-${statusKey}`}
                        control={
                          <Switch
                            size="small"
                            checked={finlandExplorationPermitStatusVisible[statusKey]}
                            onChange={(e) => setFinlandExplorationPermitStatusKeyVisible(statusKey, e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '2px',
                              backgroundColor: 'transparent',
                              border: `2px ${visual.lineDash ? 'dashed' : 'solid'} ${visual.color}`,
                            }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3', lineHeight: 1.1 }}>
                                {visual.label} ({visual.count})
                              </Typography>
                              <Typography sx={{ fontSize: '7px', fontFamily: 'monospace', color: '#8b949e', lineHeight: 1.1 }}>
                                {visual.detail}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: `${hexToRgba(visual.color, 0.08)}` } }}
                      />
                    );
                  })}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandMiningAreasVisible}
                        onChange={(e) => setFinlandMiningAreasVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: '#4caf50', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Mining Areas (41)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandReservationsVisible}
                        onChange={(e) => setFinlandReservationsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: 'transparent', border: '2px dashed #ff9800' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Reservations (107) (TUKES 2026-07-21)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandNaturaVisible}
                        onChange={(e) => setFinlandNaturaVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#8e44ad', border: '1px solid #0d1117' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Natura 2000 (1,642)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(142, 68, 173, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandTillSamplesVisible}
                        onChange={(e) => setFinlandTillSamplesVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff9900', border: '1px solid #ff6600' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Till Samples (753k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 153, 0, 0.08)' } }}
                  />
                  <Typography sx={{ color: '#ff6b6b', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    GEOCHEMISTRY (Regional Till)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandTillGoldVisible}
                        onChange={(e) => setFinlandTillGoldVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff0000' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Au Assays (47k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 0, 0, 0.08)' } }}
                  />
                  {finlandTillGoldVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box
                        onClick={() => setFinlandTillGoldLevels(prev => ({ ...prev, low: !prev.low }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandTillGoldLevels.low ? 'rgba(255,255,150,0.2)' : 'transparent',
                          border: finlandTillGoldLevels.low ? '1px solid rgba(255,255,150,0.5)' : '1px solid transparent',
                          opacity: finlandTillGoldLevels.low ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,255,150,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(255,255,150,0.8)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>low</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandTillGoldLevels(prev => ({ ...prev, p90: !prev.p90 }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandTillGoldLevels.p90 ? 'rgba(255,165,0,0.2)' : 'transparent',
                          border: finlandTillGoldLevels.p90 ? '1px solid rgba(255,165,0,0.5)' : '1px solid transparent',
                          opacity: finlandTillGoldLevels.p90 ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,165,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(255,165,0,0.9)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>p90</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandTillGoldLevels(prev => ({ ...prev, p99: !prev.p99 }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandTillGoldLevels.p99 ? 'rgba(255,0,0,0.2)' : 'transparent',
                          border: finlandTillGoldLevels.p99 ? '1px solid rgba(255,0,0,0.5)' : '1px solid transparent',
                          opacity: finlandTillGoldLevels.p99 ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,0,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(255,0,0,0.9)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>p99</Typography>
                      </Box>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandTillBasemetalsVisible}
                        onChange={(e) => setFinlandTillBasemetalsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#8b4513' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Cu-Ni-Co-Zn (82k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(139, 69, 19, 0.08)' } }}
                  />
                  {finlandTillBasemetalsVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(144,238,144,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>&gt;10</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(255,140,0,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>&gt;30</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(139,69,19,0.9)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>&gt;100 ppm Cu</Typography>
                    </Box>
                  )}
                  <Typography sx={{ color: '#00bcd4', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    GEOCHEMISTRY (Targeting/Stream)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandTargetingTillVisible}
                        onChange={(e) => setFinlandTargetingTillVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4169e1' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Targeting Till (385k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(65, 105, 225, 0.08)' } }}
                  />
                  {finlandTargetingTillVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(173,216,230,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>low</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(65,105,225,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>p90</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(0,0,139,0.9)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>p99 Cu</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandStreamSedimentsVisible}
                        onChange={(e) => setFinlandStreamSedimentsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#228b22' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Stream Sediments (70k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(34, 139, 34, 0.08)' } }}
                  />
                  {finlandStreamSedimentsVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(144,238,144,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>low</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(34,139,34,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>p90</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(0,100,0,0.9)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>p99 Cu</Typography>
                    </Box>
                  )}
                  <Typography sx={{ color: '#9c27b0', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    GEOPHYSICS (WMS)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandMagneticVisible}
                        onChange={(e) => setFinlandMagneticVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: 'linear-gradient(90deg, #000 0%, #888 50%, #fff 100%)' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Aeromagnetic
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandEMResistivityVisible}
                        onChange={(e) => setFinlandEMResistivityVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: 'linear-gradient(90deg, #00f 0%, #0ff 50%, #f00 100%)' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          EM Resistivity
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandRadiometricTernaryVisible}
                        onChange={(e) => setFinlandRadiometricTernaryVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: 'linear-gradient(135deg, #f00 0%, #0f0 50%, #00f 100%)' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Radiometric K-Th-U
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.08)' } }}
                  />
                  <Typography sx={{ color: '#ffd700', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    OPEN GROUND TARGETS
                  </Typography>
                  <Box sx={{
                    border: '1px solid rgba(255, 215, 0, 0.25)',
                    borderRadius: 1,
                    p: 0.8,
                    mb: 0.5,
                    backgroundColor: 'rgba(255, 215, 0, 0.04)',
                  }}>
                    <Typography sx={{ fontSize: '8px', color: '#c9d1d9', fontWeight: 600, mb: 0.5 }}>
                      Marker Key
                    </Typography>
                    {/* Shape legend row */}
                    <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box sx={{ width: 10, height: 10, backgroundColor: '#ff6600', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                        <Typography sx={{ fontSize: '7px', color: '#e6edf3' }}>Au</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box sx={{ width: 8, height: 8, backgroundColor: '#ff4500', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontSize: '7px', color: '#e6edf3' }}>Cu</Typography>
                      </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box sx={{ width: 10, height: 7, border: '1.5px solid #22d3ee', backgroundColor: 'rgba(34,211,238,0.15)' }} />
                        <Typography sx={{ fontSize: '7px', color: '#e6edf3' }}>PGE</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box sx={{ width: 10, height: 10, backgroundColor: '#00ff00', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', border: '1px solid #00ff00' }} />
                        <Typography sx={{ fontSize: '7px', color: '#00ff00' }}>Stake</Typography>
                      </Box>
                    </Box>
                    {/* Size legend row */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.4 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ff4500', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)', border: '1px solid #000' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Large = Top 20 ranked</Typography>
                      <Box sx={{ width: 6, height: 6, backgroundColor: '#ffd700', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Small = Others</Typography>
                    </Box>
                    {/* Grade color explanation */}
                    <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center', mb: 0.3 }}>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e', fontWeight: 600, mr: 0.3 }}>Au grade:</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,215,0,0.8)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>&lt;0.05</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,165,0,0.9)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>0.05–0.1</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,0,0,0.9)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>&gt;0.1 ppm</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e', fontWeight: 600, mr: 0.3 }}>Cu grade:</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,140,0,0.8)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>&lt;200</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,69,0,0.9)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>200–500</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(139,0,0,0.9)', borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '6.5px', color: '#8b949e' }}>&gt;500 ppm</Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandGoldTargetsVisible}
                        onChange={(e) => setFinlandGoldTargetsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: '#ff0000',
                          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Au Targets (1,474)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.08)' } }}
                  />
                  {finlandGoldTargetsVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box
                        onClick={() => setFinlandGoldTargetLevels(prev => ({ ...prev, low: !prev.low }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandGoldTargetLevels.low ? 'rgba(255,215,0,0.2)' : 'transparent',
                          border: finlandGoldTargetLevels.low ? '1px solid rgba(255,215,0,0.5)' : '1px solid transparent',
                          opacity: finlandGoldTargetLevels.low ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,215,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,215,0,0.8)', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&lt;0.05</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandGoldTargetLevels(prev => ({ ...prev, mid: !prev.mid }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandGoldTargetLevels.mid ? 'rgba(255,165,0,0.2)' : 'transparent',
                          border: finlandGoldTargetLevels.mid ? '1px solid rgba(255,165,0,0.5)' : '1px solid transparent',
                          opacity: finlandGoldTargetLevels.mid ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,165,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,165,0,0.9)', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&gt;0.05</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandGoldTargetLevels(prev => ({ ...prev, high: !prev.high }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandGoldTargetLevels.high ? 'rgba(255,0,0,0.2)' : 'transparent',
                          border: finlandGoldTargetLevels.high ? '1px solid rgba(255,0,0,0.5)' : '1px solid transparent',
                          opacity: finlandGoldTargetLevels.high ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,0,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,0,0,0.9)', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&gt;0.1</Typography>
                      </Box>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandCopperTargetsVisible}
                        onChange={(e) => setFinlandCopperTargetsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: '#ff4500',
                          transform: 'rotate(45deg)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Cu Targets (1,626)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 69, 0, 0.08)' } }}
                  />
                  {finlandCopperTargetsVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box
                        onClick={() => setFinlandCopperTargetLevels(prev => ({ ...prev, low: !prev.low }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandCopperTargetLevels.low ? 'rgba(255,140,0,0.2)' : 'transparent',
                          border: finlandCopperTargetLevels.low ? '1px solid rgba(255,140,0,0.5)' : '1px solid transparent',
                          opacity: finlandCopperTargetLevels.low ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,140,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,140,0,0.8)', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&lt;200</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandCopperTargetLevels(prev => ({ ...prev, mid: !prev.mid }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandCopperTargetLevels.mid ? 'rgba(255,69,0,0.2)' : 'transparent',
                          border: finlandCopperTargetLevels.mid ? '1px solid rgba(255,69,0,0.5)' : '1px solid transparent',
                          opacity: finlandCopperTargetLevels.mid ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(255,69,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(255,69,0,0.9)', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&gt;200</Typography>
                      </Box>
                      <Box
                        onClick={() => setFinlandCopperTargetLevels(prev => ({ ...prev, high: !prev.high }))}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer', px: 0.5, py: 0.2,
                          borderRadius: 1, backgroundColor: finlandCopperTargetLevels.high ? 'rgba(139,0,0,0.2)' : 'transparent',
                          border: finlandCopperTargetLevels.high ? '1px solid rgba(139,0,0,0.5)' : '1px solid transparent',
                          opacity: finlandCopperTargetLevels.high ? 1 : 0.4, '&:hover': { backgroundColor: 'rgba(139,0,0,0.3)' }
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, backgroundColor: 'rgba(139,0,0,0.9)', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontSize: '8px', color: '#e6edf3' }}>&gt;500</Typography>
                      </Box>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandStakingPrioritiesVisible}
                        onChange={(e) => setFinlandStakingPrioritiesVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          backgroundColor: '#00ff00',
                          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#00ff00', fontWeight: 600 }}>
                          Staking Priority (40)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 255, 0, 0.08)' } }}
                  />
                  {finlandStakingPrioritiesVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ffd700', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Au</Typography>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ff4500', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Cu</Typography>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ff00ff', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Cu-Au</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandPGETargetsVisible}
                        onChange={(e) => setFinlandPGETargetsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '2px solid #22d3ee',
                          backgroundColor: 'rgba(34, 211, 238, 0.15)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#67e8f9', fontWeight: 600 }}>
                          PGE Target Areas (8)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(34, 211, 238, 0.08)' } }}
                  />
                  {finlandPGETargetsVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 10, height: 8, border: '2px solid #22c55e', backgroundColor: 'rgba(34,197,94,0.18)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>OPEN</Typography>
                      <Box sx={{ width: 10, height: 8, border: '2px solid #22d3ee', backgroundColor: 'rgba(34,211,238,0.15)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>GAPS</Typography>
                      <Box sx={{ width: 10, height: 8, border: '2px solid #3b82f6', backgroundColor: 'rgba(59,130,246,0.12)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>RISK</Typography>
                      <Box sx={{ width: 10, height: 8, border: '2px dashed #94a3b8', backgroundColor: 'rgba(148,163,184,0.08)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>LOCKED</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandKoitelainenStakingVisible}
                        onChange={(e) => setFinlandKoitelainenStakingVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '2px solid #0ea5e9',
                          backgroundColor: 'rgba(14, 165, 233, 0.2)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#38bdf8', fontWeight: 600 }}>
                          Koitelainen Staking + Satellites
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(14, 165, 233, 0.08)' } }}
                  />
                  {finlandKoitelainenStakingVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '7px', color: '#38bdf8', fontWeight: 600 }}>Cr-V-PGE</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8e44ad' }}>Natura 2000</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#00ff00', fontWeight: 700 }}>100% OPEN</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#00e5ff', fontWeight: 600 }}>Kait corridor 276 ha</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#22c55e', fontWeight: 600 }}>East lobe 447 ha</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandPerakorpiTiStakingVisible}
                        onChange={(e) => setFinlandPerakorpiTiStakingVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '2px solid #00bfa5',
                          backgroundColor: 'rgba(0, 191, 165, 0.25)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#00bfa5', fontWeight: 600 }}>
                          Peräkorpi Ti Staking (10,768 ha)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 191, 165, 0.08)' } }}
                  />
                  {finlandPerakorpiTiStakingVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '7px', color: '#00bfa5', fontWeight: 600 }}>Ti-P-Fe</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#00ff00', fontWeight: 700 }}>100% OPEN</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#00bfa5', fontWeight: 600 }}>527 Mt | 4 zones</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandMakaraStakingVisible}
                        onChange={(e) => setFinlandMakaraStakingVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '2px solid #c49600',
                          backgroundColor: 'rgba(255, 243, 163, 0.55)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#c49600', fontWeight: 600 }}>
                          Mäkärä proposed polygon (4,022 ha)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(196, 150, 0, 0.08)' } }}
                  />
                  {finlandMakaraStakingVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '7px', color: '#c49600', fontWeight: 600 }}>Au (Lapland)</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#c49600', fontWeight: 600 }}>4,022 ha taper (v2)</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandKoivuniemiGraphiteVisible}
                        onChange={(e) => setFinlandKoivuniemiGraphiteVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '2px dashed #2dd4bf',
                          backgroundColor: 'rgba(45, 212, 191, 0.14)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#2dd4bf', fontWeight: 600 }}>
                          Koivuniemi expanded filing + intercepts
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(45, 212, 191, 0.08)' } }}
                  />
	                  {finlandKoivuniemiGraphiteVisible && (
	                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
	                      <Typography sx={{ fontSize: '7px', color: '#2dd4bf', fontWeight: 600 }}>Graphite</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#2dd4bf', fontWeight: 600 }}>44 graphite-camp collars</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#ffb703', fontWeight: 700 }}>high-grade intercepts</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#22c55e', fontWeight: 700 }}>42 ha fallback</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#86efac', fontWeight: 700 }}>3,509 ha send-ready filing</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#cbd5e1', fontWeight: 700 }}>1,601 ha conservative</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#a78bfa', fontWeight: 700 }}>2017 mag + MaxMin</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#facc15', fontWeight: 700 }}>TITLE-GATED</Typography>
	                    </Box>
	                  )}
	                  <FormControlLabel
	                    control={
	                      <Switch
	                        size="small"
	                        checked={finlandKoivuniemiConsultantOriginalVisible}
	                        onChange={(e) => setFinlandKoivuniemiConsultantOriginalVisible(e.target.checked)}
	                      />
	                    }
	                    label={
	                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
	                        <Box sx={{
	                          width: 14,
	                          height: 10,
	                          border: '2px dashed #94a3b8',
	                          backgroundColor: 'rgba(148, 163, 184, 0.08)'
	                        }} />
	                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#cbd5e1', fontWeight: 600 }}>
	                          5/20/26 Consultant Original
	                        </Typography>
	                      </Box>
	                    }
	                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' } }}
	                  />
	                  {finlandKoivuniemiConsultantOriginalVisible && (
	                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
	                      <Typography sx={{ fontSize: '7px', color: '#cbd5e1', fontWeight: 700 }}>2,156.5 ha</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#cbd5e1', fontWeight: 700 }}>consultant cut</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#ffb703', fontWeight: 700 }}>PH-7/PH-17 excluded</Typography>
	                    </Box>
	                  )}
	                  <FormControlLabel
	                    control={
	                      <Switch
	                        size="small"
	                        checked={finlandKoivuniemiConsultantModVisible}
	                        onChange={(e) => setFinlandKoivuniemiConsultantModVisible(e.target.checked)}
	                      />
	                    }
	                    label={
	                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
	                        <Box sx={{
	                          width: 14,
	                          height: 10,
	                          border: '2px dashed #38bdf8',
	                          backgroundColor: 'rgba(56, 189, 248, 0.11)'
	                        }} />
	                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#7dd3fc', fontWeight: 600 }}>
	                          5/22/26 Geomorphic Consultant Mod
	                        </Typography>
	                      </Box>
	                    }
	                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(56, 189, 248, 0.08)' } }}
	                  />
	                  {finlandKoivuniemiConsultantModVisible && (
	                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
	                      <Typography sx={{ fontSize: '7px', color: '#7dd3fc', fontWeight: 700 }}>2,156.9 ha</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#7dd3fc', fontWeight: 700 }}>submitted 5/29 filing, PH-7/PH-17 25 m offset</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#ffb703', fontWeight: 700 }}>TUKES registered original outline instead</Typography>
	                    </Box>
	                  )}
	                  <FormControlLabel
	                    control={
	                      <Switch
	                        size="small"
	                        checked={finlandKoivuniemiRegisteredVisible}
	                        onChange={(e) => setFinlandKoivuniemiRegisteredVisible(e.target.checked)}
	                      />
	                    }
	                    label={
	                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
	                        <Box sx={{
	                          width: 14,
	                          height: 10,
	                          border: '2px solid #22c55e',
	                          backgroundColor: 'rgba(34, 197, 94, 0.08)'
	                        }} />
	                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#86efac', fontWeight: 600 }}>
	                          VA2026:0039 Granted
	                        </Typography>
	                      </Box>
	                    }
	                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.08)' } }}
	                  />
	                  {finlandKoivuniemiRegisteredVisible && (
	                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
	                      <Typography sx={{ fontSize: '7px', color: '#86efac', fontWeight: 700 }}>2,156.55 ha</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#86efac', fontWeight: 700 }}>granted 2026-06-17, valid to 2027-05-31</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#f87171', fontWeight: 700 }}>PH-7/PH-17/PH-10/PH-16 outside</Typography>
	                    </Box>
	                  )}
	                  <FormControlLabel
	                    control={
	                      <Switch
	                        size="small"
	                        checked={finlandKoivuniemiDrillCollarsVisible}
	                        onChange={(e) => setFinlandKoivuniemiDrillCollarsVisible(e.target.checked)}
	                      />
	                    }
	                    label={
	                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
	                        <Box sx={{
	                          width: 10,
	                          height: 10,
	                          borderRadius: '50%',
	                          border: '1px solid #000',
	                          backgroundColor: '#22c55e'
	                        }} />
	                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#bbf7d0', fontWeight: 600 }}>
	                          Camp Drill Collars
	                        </Typography>
	                      </Box>
	                    }
	                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.08)' } }}
	                  />
	                  {finlandKoivuniemiDrillCollarsVisible && (
	                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
	                      <Typography sx={{ fontSize: '7px', color: '#86efac', fontWeight: 700 }}>44-hole camp + district (62 collars)</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#f87171', fontWeight: 700 }}>red = camp hole outside granted</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#94a3b8', fontWeight: 700 }}>grey = non-camp national collar</Typography>
	                    </Box>
	                  )}
	                  <FormControlLabel
	                    control={
	                      <Switch
                        size="small"
                        checked={finlandKokkoharjuReviewVisible}
                        onChange={(e) => setFinlandKokkoharjuReviewVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '3px solid #0ea5e9',
                          boxShadow: '0 0 0 2px #000',
                          backgroundColor: 'rgba(14, 165, 233, 0.08)'
                        }} />
	                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e5e7eb', fontWeight: 600 }}>
	                          Kokkoharju-Antinoja broad polygon
	                        </Typography>
	                      </Box>
	                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' } }}
                  />
                  {finlandKokkoharjuReviewVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '7px', color: '#e5e7eb', fontWeight: 600 }}>Au-Cu review</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#e5e7eb', fontWeight: 700 }}>1,413 ha title-watch</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#38bdf8', fontWeight: 700 }}>2,371 ha broad consultant-test</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#ec4899', fontWeight: 700 }}>4 south + 2 NE holes</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
	                      <Typography sx={{ fontSize: '7px', color: '#a855f7', fontWeight: 700 }}>IP + mag + slingram</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#facc15', fontWeight: 700 }}>TITLE-WATCH</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandKultanummiGoldVisible}
                        onChange={(e) => setFinlandKultanummiGoldVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 10,
                          border: '3px solid #38bdf8',
                          boxShadow: '0 0 0 2px #000',
                          backgroundColor: 'rgba(14, 165, 233, 0.08)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e0f2fe', fontWeight: 600 }}>
                          Kultanummi Au polygon + intercepts
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(14, 165, 233, 0.08)' } }}
                  />
                  {finlandKultanummiGoldVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '7px', color: '#e0f2fe', fontWeight: 600 }}>Au source polygon</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#38bdf8', fontWeight: 700 }}>2,340 ha screening-open</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#fef08a', fontWeight: 700 }}>31 collars | R400 7 m @ 2.42 g/t</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#a855f7', fontWeight: 700 }}>IP + mag + slingram</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>|</Typography>
                      <Typography sx={{ fontSize: '7px', color: '#22c55e', fontWeight: 700 }}>0 TUKES/Natura/heritage residual</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandMakaraTillAuVisible}
                        onChange={(e) => setFinlandMakaraTillAuVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(220, 30, 30, 0.80)',
                          border: '1px solid #7a0000'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#c0392b', fontWeight: 600 }}>
                          Mäkärä till Au samples (178, peak 10.9 g/t)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(220, 30, 30, 0.08)' } }}
                  />
                  {finlandMakaraTillAuVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(220,30,30,0.80)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>&gt;100 ppb</Typography>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'rgba(255,140,0,0.75)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>10–100</Typography>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'rgba(160,160,160,0.55)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>&lt;10</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandRockSamplesVisible}
                        onChange={(e) => setFinlandRockSamplesVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          backgroundColor: '#ff00ff',
                          transform: 'rotate(45deg)'
                        }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#ff00ff', fontWeight: 600 }}>
                          Rock Samples (4)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 0, 255, 0.08)' } }}
                  />
                  {finlandRockSamplesVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ff0000', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Cu</Typography>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ffd700', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Au</Typography>
                      <Box sx={{ width: 10, height: 10, backgroundColor: '#ff00ff', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Cu-Au</Typography>
                    </Box>
                  )}
                  <Typography sx={{ color: '#00bfff', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    HISTORIC DRILLING
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandDrillholesVisible}
                        onChange={(e) => setFinlandDrillholesVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#00bfff', border: '1px solid #000' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Drillholes (34k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 191, 255, 0.08)' } }}
                  />
                  {finlandDrillholesVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(128,128,128,0.6)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>no assays</Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(0,200,255,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>has assays</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandDrillClustersVisible}
                        onChange={(e) => setFinlandDrillClustersVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)', border: '1px solid #6b21a8' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Drill Clusters (review)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.08)' } }}
                  />
                  {finlandDrillClustersVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(128,128,128,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>pending</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.7)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>defined</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.85)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>promoted</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.6)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>rejected</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(234,179,8,0.8)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>monitor</Typography>
                    </Box>
                  )}
                  {/* Zoom to key areas */}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const view = mapInstanceRef.current?.getView();
                        if (view) {
                          view.animate({ center: fromLonLat([27.5, 67.5]), zoom: 8, duration: 500 });
                        }
                      }}
                      sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#ffd700', borderColor: '#ffd700', py: 0.2, minWidth: '60px' }}
                    >
                      C. Lapland Gold
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const view = mapInstanceRef.current?.getView();
                        if (view) {
                          view.animate({ center: fromLonLat([29.0, 62.5]), zoom: 9, duration: 500 });
                        }
                      }}
                      sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#ff5722', borderColor: '#ff5722', py: 0.2, minWidth: '60px' }}
                    >
                      Outokumpu
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        const view = mapInstanceRef.current?.getView();
                        if (view) {
                          // Target #4 area - 11.94% Cu sample
                          view.animate({ center: fromLonLat([28.29, 63.35]), zoom: 11, duration: 500 });
                        }
                      }}
                      sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', backgroundColor: '#ff00ff', color: '#fff', py: 0.3, minWidth: '80px', '&:hover': { backgroundColor: '#cc00cc' } }}
                    >
                      11.94% Cu Sample
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const view = mapInstanceRef.current?.getView();
                        if (view) {
                          // Target #4 till anomaly
                          view.animate({ center: fromLonLat([28.25, 63.24]), zoom: 12, duration: 500 });
                        }
                      }}
                      sx={{ flex: 1, fontSize: '7px', fontFamily: 'monospace', color: '#00ff00', borderColor: '#00ff00', py: 0.3, minWidth: '60px' }}
                    >
                      Target #4
                    </Button>
                  </Box>
                  <Typography sx={{ color: '#e91e63', fontSize: '9px', fontWeight: 600, mt: 1, mb: 0.3 }}>
                    FENNOSCANDIAN DATABASES
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandFoddDepositsVisible}
                        onChange={(e) => setFinlandFoddDepositsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#e91e63', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          FODD Finland (470)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(233, 30, 99, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandFoddSwedenVisible}
                        onChange={(e) => setFinlandFoddSwedenVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2196f3', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          FODD Sweden (1.1k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandFoddMetallogenicVisible}
                        onChange={(e) => setFinlandFoddMetallogenicVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 8, border: '2px dashed #e91e63', backgroundColor: 'rgba(233,30,99,0.1)' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          FODD Metallogenic (200)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(233, 30, 99, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandEgdiCrmVisible}
                        onChange={(e) => setFinlandEgdiCrmVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#00e676', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          EGDI CRM (1.5k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 230, 118, 0.08)' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandAsterAlterationVisible}
                        onChange={(e) => setFinlandAsterAlterationVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff5722', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          ASTER Alteration (5.7k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 87, 34, 0.08)' } }}
                  />
                  {finlandAsterAlterationVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ff5722' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>iron oxide</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#e0e0e0' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>silica</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff176' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>illite/muscovite</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#66bb6a' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>propylitic</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ab47bc' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>alunite/kaolinite</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>ferric iron</Typography>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#90caf9' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>carbonate</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandOreBoulderVisible}
                        onChange={(e) => setFinlandOreBoulderVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 10, height: 10, backgroundColor: '#ff5722', transform: 'rotate(45deg)' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Ore Boulders (31k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 87, 34, 0.08)' } }}
                  />
                  {finlandOreBoulderVisible && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 4, mb: 0.3, alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#ff5722', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Cu</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#ffd700', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Au</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#4caf50', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Ni</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#795548', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>Fe</Typography>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#9e9e9e', transform: 'rotate(45deg)' }} />
                      <Typography sx={{ fontSize: '7px', color: '#8b949e' }}>other</Typography>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={finlandExpiredClaimsVisible}
                        onChange={(e) => setFinlandExpiredClaimsVisible(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(96,125,139,0.4)', border: '1px solid #fff' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Expired Claims (12k)
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(96, 125, 139, 0.08)' } }}
                  />
                </Box>
              )}

              {/* SGU Layers - Sweden only */}
              {(selectedProject?.id === 'project-sgu-sweden' || selectedProject?.id === 'project-sweden-uranium') && (
                <>
                  {SGU_VECTOR_LAYERS.filter((l) => l.id !== 'boreholes').map((layer) => (
                    <FormControlLabel
                      key={`sgu-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(sguLayerVisibility[layer.id])}
                          onChange={(e) => toggleSguLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          {layer.id === 'gravity_bouguer_contours' ? (
                            // Gradient bar for gravity contours (relative scale)
                            <Box
                              sx={{
                                width: 40,
                                height: 8,
                                borderRadius: '2px',
                                background: 'linear-gradient(to right, rgb(30, 80, 220), rgb(255, 255, 0), rgb(255, 80, 30))',
                                border: '1px solid #0d1117',
                              }}
                              title="Relative scale: blue = local low, yellow = mid, red = local high"
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: layer.color,
                                border: '1px solid #0d1117',
                              }}
                            />
                          )}
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.id === 'gravity_bouguer_contours' ? 'Gravity (relative)' : layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                  {/* SGU Boreholes — dots toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(sguLayerVisibility['boreholes'])}
                        onChange={(e) => toggleSguLayer('boreholes', e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#00d2ff', border: '1px solid #0d1117' }} />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>SGU Boreholes</Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' } }}
                  />
                  {/* Borehole ID labels — sub-toggle (indented) */}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={boreholeIdLabelsVisible}
                        onChange={(e) => toggleBoreholeIdLabels(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#00d2ff', letterSpacing: '0.5px' }}>ABC</Typography>
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>Borehole ID Labels</Typography>
                      </Box>
                    }
                    sx={{ m: 0, py: 0.3, px: 0.5, pl: 3, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0, 210, 255, 0.06)' } }}
                  />
                  {/* Borehole ID search */}
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={boreholeSearchResults}
                    loading={boreholeSearchLoading}
                    getOptionLabel={(option: any) =>
                      typeof option === 'string' ? option : option.drillhole || ''
                    }
                    filterOptions={(x) => x}
                    inputValue={boreholeSearchInput}
                    onInputChange={(_e, value) => {
                      setBoreholeSearchInput(value);
                      searchBoreholes(value);
                    }}
                    onChange={(_e, value) => {
                      if (value && typeof value !== 'string') {
                        handleBoreholeSelect(value);
                      }
                    }}
                    renderOption={(props, option: any) => (
                      <li {...props} key={option.drillhole}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 'bold', color: '#00d2ff' }}>
                            {option.drillhole}
                          </Typography>
                          <Typography sx={{ fontSize: '9px', fontFamily: 'monospace', color: '#8b949e' }}>
                            {option.name}{option.drillyear ? ` (${option.drillyear})` : ''}{option.tot_depth ? ` ${option.tot_depth}m` : ''}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search borehole ID..."
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 14, color: '#8b949e' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            color: '#e6edf3',
                            backgroundColor: 'rgba(13, 17, 23, 0.6)',
                            '& fieldset': { borderColor: 'rgba(48, 54, 61, 0.8)' },
                            '&:hover fieldset': { borderColor: '#00d2ff' },
                            '&.Mui-focused fieldset': { borderColor: '#00d2ff' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#8b949e',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                    componentsProps={{
                      paper: {
                        sx: {
                          backgroundColor: '#161b22',
                          border: '1px solid #30363d',
                          '& .MuiAutocomplete-option': {
                            py: 0.5,
                            '&:hover': { backgroundColor: 'rgba(0, 210, 255, 0.1)' },
                          },
                        },
                      },
                    }}
                    sx={{ mx: 0.5, my: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={naturaVisible}
                        onChange={(e) => toggleNaturaLayer(e.target.checked)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: '#8e44ad',
                            border: '1px solid #0d1117',
                          }}
                        />
                        <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                          Natura 2000 (Sweden)
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      py: 0.3,
                      px: 0.5,
                      borderRadius: 2,
                      '&:hover': { backgroundColor: 'rgba(142, 68, 173, 0.08)' }
                    }}
                  />
                </>
              )}

              {/* Peru/INGEMMET Layers - Peru wide screen project */}
              {selectedProject?.id === 'project-peru-wide' && (
                <>
                  {/* PLAYBOOK ANALYSIS section */}
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#E63946', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    PLAYBOOK ANALYSIS
                  </Typography>
                  {PERU_VECTOR_LAYERS.filter((l) => l.category === 'analysis').map((layer) => (
                    <FormControlLabel
                      key={`peru-analysis-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(peruAnalysisVisibility[layer.id])}
                          onChange={(e) => togglePeruAnalysisLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(230, 57, 70, 0.08)' }
                      }}
                    />
                  ))}

                  {/* GEOCHEMISTRY section */}
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#00bcd4', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    GEOCHEMISTRY
                  </Typography>
                  {PERU_VECTOR_LAYERS.filter((l) => l.category === 'geochemistry_analysis').map((layer) => (
                    <FormControlLabel
                      key={`peru-geochem-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(peruAnalysisVisibility[layer.id])}
                          onChange={(e) => togglePeruAnalysisLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(0, 188, 212, 0.08)' }
                      }}
                    />
                  ))}

                  {/* SURFACE SAMPLES section */}
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#ff6b35', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    SURFACE SAMPLES
                  </Typography>
                  {PERU_VECTOR_LAYERS.filter((l) => l.category === 'surface_samples').map((layer) => (
                    <FormControlLabel
                      key={`peru-surface-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(peruAnalysisVisibility[layer.id])}
                          onChange={(e) => togglePeruAnalysisLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: layer.id === 'litoteca_samples' ? '2px' : layer.id === 'mining_projects' ? '0%' : '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                              transform: layer.id === 'litoteca_samples' ? 'rotate(45deg)' : 'none',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(255, 107, 53, 0.08)' }
                      }}
                    />
                  ))}

                  {/* REFERENCE DATABASES section (USGS, SAmMD) */}
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6366f1', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    REFERENCE DATABASES
                  </Typography>
                  {PERU_VECTOR_LAYERS.filter((l) => l.category === 'reference_databases').map((layer) => (
                    <FormControlLabel
                      key={`peru-ref-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(peruAnalysisVisibility[layer.id])}
                          onChange={(e) => togglePeruAnalysisLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: layer.id.includes('porphyry') || layer.id.includes('mrds') ? '50%' : layer.id === 'sammd_deposits' ? '2px' : '2px',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.08)' }
                      }}
                    />
                  ))}

                  {/* INGEMMET API Layers section */}
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    INGEMMET Layers
                  </Typography>
                  {PERU_VECTOR_LAYERS.filter((l) => !['analysis', 'geochemistry_analysis', 'surface_samples', 'reference_databases'].includes(l.category)).map((layer) => (
                    <FormControlLabel
                      key={`peru-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(peruLayerVisibility[layer.id])}
                          onChange={(e) => togglePeruLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}

              {/* Papua New Guinea (MRA) Layers - PNG wide screen project */}
              {selectedProject?.id === 'project-png-wide' && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    MRA Layers
                  </Typography>
                  {PNG_VECTOR_LAYERS.map((layer) => (
                    <FormControlLabel
                      key={`png-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(pngLayerVisibility[layer.id])}
                          onChange={(e) => togglePngLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: layer.type === 'polygon' ? '2px' : '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}

              {/* Yukon (GeoYukon) Layers - Yukon wide screen project */}
              {selectedProject?.id === 'project-yukon-wide' && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    GeoYukon Layers
                  </Typography>
                  {YUKON_VECTOR_LAYERS.map((layer) => (
                    <FormControlLabel
                      key={`yukon-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(yukonLayerVisibility[layer.id])}
                          onChange={(e) => toggleYukonLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: layer.type === 'point' ? '50%' : '2px',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}

              {/* Caujul Layers - Caujul project */}
              {selectedProject?.id === 'project-caujul' && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    Caujul Layers
                  </Typography>
                  {CAUJUL_VECTOR_LAYERS.map((layer) => (
                    <FormControlLabel
                      key={`caujul-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(caujulLayerVisibility[layer.id])}
                          onChange={(e) => toggleCaujulLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}
              {/* France Layers - BRGM project */}
              {selectedProject?.id === 'project-france-brgm' && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    France / BRGM Layers
                  </Typography>
                  {FRANCE_VECTOR_LAYERS.map((layer) => (
                    <FormControlLabel
                      key={`france-${layer.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(franceLayerVisibility[layer.id])}
                          onChange={(e) => toggleFranceLayer(layer.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: layer.color,
                              border: '1px solid #0d1117',
                            }}
                          />
                          <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                            {layer.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}

              {JURISDICTION_LAYER_CONFIG[selectedProject?.id || ''] && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    {JURISDICTION_LAYER_CONFIG[selectedProject?.id || ''].title}
                  </Typography>
                  {Object.entries(
                    JURISDICTION_LAYER_CONFIG[selectedProject?.id || ''].layers.reduce<Record<string, JurisdictionLayerDefinition[]>>((groups, layer) => {
                      groups[layer.category] = groups[layer.category] || [];
                      groups[layer.category].push(layer);
                      return groups;
                    }, {})
                  ).map(([category, layers]) => (
                    <Box key={`jurisdiction-category-${category}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#8b949e', mt: 0.7, mb: 0.2, fontFamily: 'monospace', textTransform: 'uppercase' }}>
                        {category}
                      </Typography>
                      {layers.map((layer) => {
                        const disabled = Boolean(layer.disabledReason);
                        const layerCategory = layer.category.toLowerCase();
                        return (
                          <FormControlLabel
                            key={`jurisdiction-${layer.id}`}
                            disabled={disabled}
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(jurisdictionLayerVisibility[layer.id])}
                                onChange={(e) => toggleJurisdictionLayer(layer, e.target.checked)}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: layerCategory.includes('tenure') || layerCategory.includes('risk') || layerCategory.includes('constraint') || layerCategory.includes('geophys') || layerCategory.includes('report') ? '2px' : '50%',
                                    backgroundColor: layer.color,
                                    border: '1px solid #0d1117',
                                    opacity: disabled ? 0.45 : 1,
                                  }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: disabled ? '#6e7681' : '#e6edf3', lineHeight: 1.15 }}>
                                    {layer.label}
                                  </Typography>
                                  {disabled && (
                                    <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#8b949e', lineHeight: 1.15 }}>
                                      {layer.disabledReason}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                            sx={{
                              m: 0,
                              py: 0.3,
                              px: 0.5,
                              borderRadius: 2,
                              '&:hover': { backgroundColor: disabled ? 'transparent' : 'rgba(88, 166, 255, 0.08)' }
                            }}
                          />
                        );
                      })}
                    </Box>
                  ))}
                </>
              )}

              {selectedProject?.id === 'project-norway-ngu' && norwayTargetPolygons.length > 0 && (
                <>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#58a6ff', mt: 1, mb: 0.5, fontFamily: 'monospace' }}>
                    Norway Targets
                  </Typography>
                  {norwayTargetPolygons.map((target) => (
                    <FormControlLabel
                      key={`norway-target-${target.polygon.id}`}
                      control={
                        <Switch
                          size="small"
                          checked={target.polygon.visible !== false}
                          onChange={(e) => handlePolygonVisibilityToggle(target.polygon.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '2px',
                              backgroundColor: target.color,
                              border: '1px solid #0d1117',
                              mt: '2px',
                              flexShrink: 0
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3', lineHeight: 1.2 }}>
                              {target.label}
                            </Typography>
                            <Typography sx={{ fontSize: '8px', fontFamily: 'monospace', color: '#8b949e', lineHeight: 1.2 }}>
                              {target.note}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        py: 0.3,
                        px: 0.5,
                        alignItems: 'flex-start',
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(88, 166, 255, 0.08)' }
                      }}
                    />
                  ))}
                </>
              )}

              {selectedProject?.id !== 'project-caujul' && (
                <>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={clusterLabelsVisible}
                    onChange={(e) => toggleClusterLabels(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        backgroundColor: '#ffeb3b',
                        border: '1px solid #0d1117',
                      }}
                    />
                    <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                      Deposit Labels
                    </Typography>
                  </Box>
                }
                sx={{
                  m: 0,
                  py: 0.3,
                  px: 0.5,
                  borderRadius: 2,
                  '&:hover': { backgroundColor: 'rgba(255, 235, 59, 0.08)' }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fetchGeochemSummary()}
                disabled={geochemLoading}
                sx={{
                  mt: 0.5,
                  color: '#58a6ff',
                  borderColor: '#58a6ff',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#58a6ff' }
                }}
              >
                {geochemLoading ? 'Loading geochem…' : 'Geochem Stats (view)'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => fetchOpenGroundScores()}
                disabled={scoring}
                sx={{
                  mt: 0.5,
                  color: '#7ee787',
                  borderColor: '#7ee787',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#7ee787' }
                }}
              >
                {scoring ? 'Scoring…' : 'Score open ground'}
              </Button>
              {geochemTopSamples.length > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showTopAnomalies}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setShowTopAnomalies(next);
                        if (next) {
                          drawGeochemMarkers(geochemTopSamples, geochemMarkerLayerRef, { zIndex: 92, radius: 5 });
                        } else if (geochemMarkerLayerRef.current && mapInstanceRef.current) {
                          mapInstanceRef.current.removeLayer(geochemMarkerLayerRef.current);
                          geochemMarkerLayerRef.current = null;
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                      Show top anomalies (by element)
                    </Typography>
                  }
                  sx={{ m: 0, py: 0.3, px: 0.5 }}
                />
              )}
              {geochemNearSamples.length > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showNearAnomalies}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setShowNearAnomalies(next);
                        if (next) {
                          drawGeochemMarkers(geochemNearSamples, geochemNearLayerRef, { zIndex: 88, radius: 4, opacity: 0.85 });
                        } else if (geochemNearLayerRef.current && mapInstanceRef.current) {
                          mapInstanceRef.current.removeLayer(geochemNearLayerRef.current);
                          geochemNearLayerRef.current = null;
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                      Show near-threshold (p90–thr)
                    </Typography>
                  }
                  sx={{ m: 0, py: 0.3, px: 0.5 }}
                />
              )}
              {Object.keys(geochemSummaries || {}).length > 0 && (
                <Box sx={{ mt: 0.5, p: 0.5, border: '1px solid #30363D', borderRadius: 2 }}>
                  <Typography sx={{ color: '#9fb3c8', fontSize: '10px', fontFamily: 'monospace' }}>
                    Per-element (bbox)
                  </Typography>
                  {Object.entries(geochemSummaries).slice(0, 6).map(([elem, stats]: any) => {
                    if (!stats || !stats.count) return null;
                    const bg = geochemBackground?.[elem];
                    return (
                      <Box key={elem} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                        <span>{elem}</span>
                        <span>
                          {stats.count} | min {Number(stats.min).toPrecision(3)} | max {Number(stats.max).toPrecision(3)} | p95 {Number(stats.p95).toPrecision(3)}
                          {bg && bg.p95 ? ` | nat p95 ${Number(bg.p95).toPrecision(3)}` : ''}
                          {stats?.anomaly_threshold ? ` | thr ${Number(stats.anomaly_threshold).toPrecision(3)}` : ''}
                        </span>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {scores.length > 0 && (
                <Box sx={{ mt: 0.5, p: 0.5, border: '1px solid #30363D', borderRadius: 2 }}>
                  <Typography sx={{ color: '#7ee787', fontSize: '10px', fontFamily: 'monospace' }}>
                    Top scored cells
                  </Typography>
                  {scores.slice(0, 5).map((cell, idx) => (
                    <Box key={`cell-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontFamily: 'monospace', color: '#e6edf3' }}>
                      <span>
                        {cell.score.toFixed(1)}
                        {typeof cell.interval_bonus === 'number' ? ` (+int ${cell.interval_bonus.toFixed(1)})` : ''}
                      </span>
                      <span>
                        occ {cell.occ_count} | bh {cell.borehole_count}
                        {Array.isArray(cell.sgu_intervals) ? ` | ints ${cell.sgu_intervals.length}` : ''}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
              {activeScoreCell && (
                <Box sx={{ mt: 0.5, p: 0.5, border: '1px solid #30363D', borderRadius: 2 }}>
                  <Typography sx={{ color: '#58a6ff', fontSize: '10px', fontFamily: 'monospace' }}>
                    Selected cell
                  </Typography>
                  <Typography sx={{ color: '#e6edf3', fontSize: '10px', fontFamily: 'monospace' }}>
                    {activeScoreCell.score?.toFixed(1)}{typeof activeScoreCell.interval_bonus === 'number' ? ` (int +${activeScoreCell.interval_bonus.toFixed(1)})` : ''}
                    {' '}occ {activeScoreCell.occ} | bh {activeScoreCell.bh}
                  </Typography>
                  <Typography sx={{ color: '#9fb3c8', fontSize: '9px', fontFamily: 'monospace' }}>
                    {activeScoreCell.lat?.toFixed(3)}, {activeScoreCell.lon?.toFixed(3)}
                  </Typography>
                  {Array.isArray(activeScoreCell.intervals) && activeScoreCell.intervals.length > 0 && (
                    <Box sx={{ mt: 0.4, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                      {activeScoreCell.intervals.slice(0, 5).map((iv: any, idx: number) => {
                        const pdfs = typeof iv.pdfs === 'string' ? iv.pdfs.split(',') : [];
                        return (
                          <Typography key={idx} sx={{ color: '#e6edf3', fontSize: '9px', fontFamily: 'monospace' }}>
                            {iv.element}: max {iv.max_grade} ({iv.count} rows){iv.in_open_ground_any ? ' | open' : ''}
                            {pdfs.length > 0 && (
                              <span>
                                {' | '}
                                {pdfs.map((p: string, i: number) => (
                                  <Link
                                    key={p}
                                    href={`${API_BASE}/analysis_outputs/sgu_reports_dl/${p}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ color: '#58a6ff', fontSize: '9px', fontFamily: 'monospace', textDecoration: 'underline', mr: 0.5 }}
                                  >
                                    {p}{i < pdfs.length - 1 ? ',' : ''}
                                  </Link>
                                ))}
                              </span>
                            )}
                          </Typography>
                        );
                      })}
                      {activeScoreCell.intervals.length > 5 && (
                        <Typography sx={{ color: '#9fb3c8', fontSize: '9px', fontFamily: 'monospace' }}>
                          +{activeScoreCell.intervals.length - 5} more…
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
                </>
              )}
            </Box>
                </Collapse>

	        <Divider sx={{ my: 2, borderColor: '#30363D' }} />

        {discoveryResults.length > 0 && (
          <>
            <Button
              onClick={() => setResultsViewerOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                mb: 1,
                borderColor: '#00D2FF',
                color: '#00D2FF',
                fontSize: '10px',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#00B8E6',
                  backgroundColor: 'rgba(0, 210, 255, 0.1)'
                }
              }}
            >
              <ResultsIcon sx={{ fontSize: 14, mr: 0.5 }} />
              View Results ({discoveryResults.length})
            </Button>
            
            <Button
              onClick={() => setRuleContributionOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                mb: 1,
                borderColor: 'var(--palantir-status-success)',
                color: 'var(--palantir-status-success)',
                fontSize: '10px',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#00CC6A',
                  backgroundColor: 'rgba(0, 255, 136, 0.1)'
                }
              }}
            >
              <ResultsIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Rule Contributions
            </Button>
            
            <Button
              onClick={() => setStatisticsOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                mb: 1,
                borderColor: '#B388FF',
                color: '#B388FF',
                fontSize: '10px',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#9C64FF',
                  backgroundColor: 'rgba(179, 136, 255, 0.1)'
                }
              }}
            >
              <ScienceIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Statistical Validation
            </Button>
            
            <Button
              onClick={() => setRuleTuningOpen(true)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                mb: 1,
                borderColor: '#FFD93D',
                color: '#FFD93D',
                fontSize: '10px',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#CCAD00',
                  backgroundColor: 'rgba(255, 217, 61, 0.1)'
                }
              }}
            >
              <TuneIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Rule Tuning
            </Button>
          </>
        )}

        <Divider sx={{ my: 2, borderColor: '#30363D' }} />

        {/* Polygon Drawing Controls */}
        <Typography sx={{
          color: 'var(--palantir-blue-600)',
          fontFamily: 'monospace',
          fontSize: '12px',
          mb: 1
        }}>
          POLYGON REGIONS
        </Typography>

        <Button
          onClick={() => setPolygonDrawingEnabled(!polygonDrawingEnabled)}
          variant={polygonDrawingEnabled ? "contained" : "outlined"}
          size="small"
          fullWidth
          sx={{
            mb: 1,
            backgroundColor: polygonDrawingEnabled ? 'var(--palantir-status-success)' : 'transparent',
            borderColor: 'var(--palantir-status-success)',
            color: polygonDrawingEnabled ? '#000' : 'var(--palantir-status-success)',
            fontSize: '10px',
            fontFamily: 'monospace',
            '&:hover': {
              borderColor: '#00CC6A',
              backgroundColor: polygonDrawingEnabled ? '#00CC6A' : 'rgba(0, 255, 136, 0.1)'
            }
          }}
        >
          <EditIcon sx={{ fontSize: 14, mr: 0.5 }} />
          {polygonDrawingEnabled ? 'Finish & Save Polygon' : 'Draw Polygon Region'}
        </Button>

        <Button
          onClick={() => setPolygonManagerOpen(true)}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            mb: 1,
            borderColor: '#00D2FF',
            color: '#00D2FF',
            fontSize: '10px',
            fontFamily: 'monospace',
            '&:hover': {
              borderColor: '#00B8E6',
              backgroundColor: 'rgba(0, 210, 255, 0.1)'
            }
          }}
        >
          <LayersIcon sx={{ fontSize: 14, mr: 0.5 }} />
          Manage Regions ({polygonRegions.length})
        </Button>
        
        {/* Mineral Analysis Controls */}
        {mineralMapData?.mineral_layers && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              onClick={() => onSaveMineralAnalysis?.(mineralMapData)}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                borderColor: '#00D2FF',
                color: '#00D2FF',
                fontFamily: 'monospace',
                fontSize: '9px',
                '&:hover': {
                  borderColor: '#00B8E6',
                  backgroundColor: 'rgba(0, 210, 255, 0.1)'
                }
              }}
            >
              <SaveIcon sx={{ fontSize: 12, mr: 0.5 }} />
              Save
            </Button>
            <Button
              onClick={() => onClearMineralAnalysis?.()}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                borderColor: '#F85149',
                color: '#F85149',
                fontFamily: 'monospace',
                fontSize: '9px',
                '&:hover': {
                  borderColor: '#DA3633',
                  backgroundColor: 'rgba(248, 81, 73, 0.1)'
                }
              }}
            >
              <ClearIcon sx={{ fontSize: 12, mr: 0.5 }} />
              Clear
            </Button>
          </Box>
        )}
        {/* Project Boundary Toggle */}
        {selectedProject?.project_boundary && (
          <Button
            onClick={() => setShowProjectBoundary(!showProjectBoundary)}
            variant={showProjectBoundary ? "contained" : "outlined"}
            size="small"
            fullWidth
            sx={{
              backgroundColor: showProjectBoundary ? 'var(--palantir-blue-600)' : 'transparent',
              color: showProjectBoundary ? '#000' : 'var(--palantir-blue-600)',
              borderColor: 'var(--palantir-blue-600)',
              fontFamily: 'monospace',
              fontSize: '10px',
              '&:hover': {
                backgroundColor: showProjectBoundary ? 'var(--palantir-blue-700)' : 'rgba(88, 166, 255, 0.1)'
              }
            }}
          >
            🔲 {showProjectBoundary ? 'Hide' : 'Show'} Project Boundary
          </Button>
        )}
              </Box>
            </Collapse>
          </Paper>
        );
        return isPortaled ? createPortal(layersContent, legendPortalElement) : layersContent;
      })()}


      {false && (
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: isInsightsCollapsed ? 56 : 400,
          minWidth: isInsightsCollapsed ? 56 : 400,
          maxWidth: isInsightsCollapsed ? 56 : 400,
          height: isInsightsCollapsed ? 92 : 'auto',
          minHeight: isInsightsCollapsed ? 92 : 'auto',
          maxHeight: 'calc(100% - 32px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#0e141b',
          border: '1px solid rgba(88, 166, 255, 0.35)',
          borderRadius: 3,
          boxShadow: '0 12px 24px rgba(0,0,0,0.45)',
          overflow: 'hidden',
          transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease'
        }}
      >
        <Box
          sx={{
            px: 2.4,
            py: 1.8,
            borderBottom: '1px solid rgba(88, 166, 255, 0.25)',
            background: 'linear-gradient(90deg, rgba(14,20,27,0.9) 0%, rgba(14,20,27,0.4) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isInsightsCollapsed ? 'center' : 'space-between',
            gap: 1.5
          }}
        >
          {!isInsightsCollapsed && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.4, color: '#58a6ff' }}>
                ANALYSIS INSIGHTS
              </Typography>
              {selectedProject?.name && (
                <Typography sx={{ fontSize: 11, color: '#8b949e', mt: 0.5 }}>
                  {selectedProject.name}
                </Typography>
              )}
            </Box>
          )}
          {isInsightsCollapsed && (
            <Typography
              sx={{
                fontSize: 10,
                color: '#58a6ff',
                letterSpacing: 1.4,
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)'
              }}
            >
              INSIGHTS
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setIsInsightsCollapsed((prev) => !prev)}
            sx={{
              color: '#58a6ff',
              '&:hover': {
                color: '#7fb4ff'
              }
            }}
          >
            {isInsightsCollapsed ? (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>

        <Collapse
          in={!isInsightsCollapsed}
          timeout="auto"
          unmountOnExit
          sx={{ flex: 1, display: isInsightsCollapsed ? 'none' : 'flex', minHeight: 0 }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              p: 2.4
            }}
          >
          {analysisPolygons.length === 0 ? (
            <Box sx={{ color: '#8b949e', fontSize: 12, lineHeight: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: '#8b949e', mb: 1 }}>
                Draw and save an analysis polygon to unlock heat-map insights for this project.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPolygonDrawingEnabled(true)}
                sx={{
                  borderColor: '#58a6ff',
                  color: '#58a6ff',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  alignSelf: 'flex-start'
                }}
              >
                Start Drawing
              </Button>
            </Box>
          ) : sortedGridEntries.length === 0 ? (
            <Box sx={{ color: '#8b949e', fontSize: 12, lineHeight: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: '#8b949e' }}>
                Run an enhanced analysis for the selected polygon to populate live insights here.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.6, flex: 1, minHeight: 0 }}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputLabel-root': { color: '#8b949e' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#58a6ff' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(88, 166, 255, 0.25)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(88, 166, 255, 0.45)' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(13, 19, 27, 0.9)',
                    borderRadius: 2,
                    color: '#f5f7fb'
                  }
                }}
              >
                <InputLabel id="insights-polygon-label">Polygon</InputLabel>
                <Select
                  labelId="insights-polygon-label"
                  label="Polygon"
                  value={insightsPolygonId}
                  onChange={(event) => {
                    setInsightsPolygonId(event.target.value);
                    setInsightsAnalysisId('');
                  }}
                  sx={{
                    backgroundColor: 'rgba(10, 14, 21, 0.85)',
                    color: '#f5f7fb',
                    borderRadius: 1.5,
                    fontSize: 12,
                    '& .MuiSelect-icon': { color: '#58a6ff' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#0b1118',
                        color: '#f5f7fb'
                      }
                    }
                  }}
                >
                  {polygonOptions.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                      sx={{ fontSize: 12, '&.Mui-selected': { backgroundColor: 'rgba(88,166,255,0.16)' } }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {analysisOptions.length > 0 && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': { color: '#8b949e' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#58a6ff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(88, 166, 255, 0.25)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(88, 166, 255, 0.45)' },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(13, 19, 27, 0.9)',
                      borderRadius: 2,
                      color: '#f5f7fb'
                    }
                  }}
                >
                  <InputLabel id="insights-analysis-label">Analysis Run</InputLabel>
                  <Select
                    labelId="insights-analysis-label"
                    label="Analysis Run"
                    value={insightsAnalysisId}
                    onChange={(event) => setInsightsAnalysisId(event.target.value)}
                    sx={{
                      backgroundColor: 'rgba(10, 14, 21, 0.85)',
                      color: '#f5f7fb',
                      borderRadius: 1.5,
                      fontSize: 12,
                      '& .MuiSelect-icon': { color: '#58a6ff' }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#0b1118',
                          color: '#f5f7fb'
                        }
                      }
                    }}
                  >
                    {analysisOptions.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{ fontSize: 12, '&.Mui-selected': { backgroundColor: 'rgba(88,166,255,0.16)' } }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontSize: 12 }}>{option.label}</Typography>
                          {option.timestamp && (
                            <Typography sx={{ fontSize: 10, color: '#8b949e' }}>{option.timestamp}</Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Tabs
                value={insightsTab}
                onChange={(_, value) => setInsightsTab(value as InsightTabKey)}
                variant="fullWidth"
                textColor="secondary"
                indicatorColor="secondary"
                sx={{
                  mt: 2,
                  minHeight: 36,
                  '& .MuiTabs-indicator': { backgroundColor: '#58a6ff' },
                  '& .MuiTab-root': {
                    minHeight: 36,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    color: '#8b949e',
                    textTransform: 'uppercase'
                  },
                  '& .Mui-selected': {
                    color: '#58a6ff !important'
                  }
                }}
              >
                <Tab value="heatmap" label={`Heat Grid (${topGridCells.length})`} />
                <Tab value="drill" label={`Drill Collars (${drillCollars.length})`} />
                <Tab value="surface" label={`Surface Assays (${surfaceAssays.length})`} />
              </Tabs>
              {insightsTab === 'heatmap' && (
                <>
                  {selectedAnalysisEntry && insightsSummary ? (
                    <>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                        <Chip
                          size="small"
                          label={(selectedAnalysisEntry?.commodity || 'analysis').toUpperCase()}
                          sx={{
                            backgroundColor: 'rgba(88, 166, 255, 0.15)',
                            color: '#58a6ff',
                            fontSize: 10,
                            fontFamily: 'monospace'
                          }}
                        />
                        {insightsSummary?.modelVersion && (
                          <Chip
                            size="small"
                            label={insightsSummary?.modelVersion}
                            sx={{
                              backgroundColor: 'rgba(110, 231, 183, 0.15)',
                              color: '#3fb37f',
                              fontSize: 10,
                              fontFamily: 'monospace'
                            }}
                          />
                        )}
                        {insightsSummary?.completedAt && (
                          <Chip
                            size="small"
                            label={formatTimestamp(insightsSummary?.completedAt)}
                            sx={{
                              backgroundColor: 'rgba(139, 148, 158, 0.15)',
                              color: '#8b949e',
                              fontSize: 10,
                              fontFamily: 'monospace'
                            }}
                          />
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: 1.2,
                          mt: 1.5
                        }}
                      >
                        {[
                          { label: 'Avg Probability', value: formatPercentText(insightsSummary?.averageProbability) },
                          { label: 'Peak Probability', value: formatPercentText(insightsSummary?.maxProbability) },
                          { label: 'Discovery Probability', value: formatPercentText(insightsSummary?.discoveryProbability) },
                          { label: 'High Potential Cells', value: insightsSummary?.highPotentialCount?.toString() ?? '0' },
                          { label: 'Grid Cells', value: insightsSummary?.totalPoints?.toString() ?? '0' }
                        ].map((metric) => (
                          <Box
                            key={metric.label}
                            sx={{
                              backgroundColor: 'rgba(88, 166, 255, 0.08)',
                              border: '1px solid rgba(88, 166, 255, 0.15)',
                              borderRadius: 2,
                              p: 1.2
                            }}
                          >
                            <Typography sx={{ fontSize: 10, color: '#8b949e' }}>{metric.label}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{metric.value}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: '#8b949e', mt: 2 }}>
                      Select an analysis run to view grid metrics.
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.6, borderColor: 'rgba(88, 166, 255, 0.12)' }} />

                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#58a6ff' }}>
                    Top Grid Cells
                  </Typography>

                  {topGridCells.length === 0 ? (
                    <Typography sx={{ fontSize: 11, color: '#8b949e' }}>
                      No grid cell data available for this analysis.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                      {topGridCells.map((cell) => {
                        const isExpanded = expandedCellKey === cell.key;
                        const isHovered = hoveredCellKey === cell.key;
                        return (
                          <Box
                            key={cell.key}
                            onMouseEnter={() => handleCellHover(cell)}
                            onMouseLeave={() => {
                              if (!isExpanded) {
                                handleCellHover(null);
                              }
                            }}
                            onClick={() => {
                              setExpandedCellKey((prev) => {
                                const next = prev === cell.key ? null : cell.key;
                                if (next) {
                                  handleCellHover(cell);
                                } else {
                                  handleCellHover(null);
                                }
                                return next;
                              });
                            }}
                            sx={{
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: isExpanded || isHovered ? 'rgba(88, 166, 255, 0.65)' : 'rgba(88, 166, 255, 0.25)',
                              backgroundColor: isExpanded || isHovered ? 'rgba(88, 166, 255, 0.12)' : 'rgba(14, 20, 27, 0.6)',
                              transition: 'all 0.2s ease',
                              p: 1.2,
                              cursor: 'pointer'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  {formatLatitude(cell.lat)} · {formatLongitude(cell.lon)}
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                  {formatPercentText(cell.probability)}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={isExpanded ? 'Hide' : 'Details'}
                                sx={{
                                  fontSize: 9,
                                  fontFamily: 'monospace',
                                  backgroundColor: 'rgba(88, 166, 255, 0.15)',
                                  color: '#58a6ff'
                                }}
                              />
                            </Box>
                            <Collapse in={isExpanded}>
                              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  Probability: {formatPercentText(cell.probability, 2)}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  Confidence: {formatPercentText(cell.confidence)}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  Uncertainty: {cell.uncertainty != null ? formatPercentText(cell.uncertainty) : '—'}
                                </Typography>
                                {cell.detail?.risk_level && (
                                  <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                    Risk Level: {String(cell.detail.risk_level)}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}

              {insightsTab === 'drill' && (
                <Box sx={{ mt: 2 }}>
                  {drillCollarsLoading ? (
                    <LinearProgress sx={{ height: 3, borderRadius: 999 }} />
                  ) : drillCollarError ? (
                    <Typography sx={{ fontSize: 11, color: '#ff9b9b' }}>{drillCollarError}</Typography>
                  ) : drillCollarsForDisplay.length === 0 ? (
                    <Typography sx={{ fontSize: 11, color: '#8b949e' }}>
                      No drill collars registered for this project yet.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                      {drillCollarsForDisplay.map((collar) => {
                        const isExpanded = expandedDrillHoleId === collar.holeId;
                        const cacheKey = collar.holeId.toLowerCase();
                        const cachedIntervals = drillIntervalCacheRef.current.get(cacheKey);
                        const isActiveHole = activeDrillHoleId === collar.holeId;
                        const intervalsForHole = isActiveHole
                          ? activeDrillIntervalsTop
                          : cachedIntervals
                            ? getTopIntervals(cachedIntervals.intervals)
                            : [];
                        const statsForHole = isActiveHole
                          ? activeDrillIntervalStats
                          : cachedIntervals?.stats ?? null;

                        return (
                          <Box
                            key={collar.holeId}
                            onMouseEnter={() => handleDrillHover(collar)}
                            onMouseLeave={() => {
                              if (!isExpanded) {
                                handleDrillHover(null);
                              }
                            }}
                            onClick={() => {
                              const nextExpanded = isExpanded ? null : collar.holeId;
                              setExpandedDrillHoleId(nextExpanded);
                              setActiveDrillHoleId(collar.holeId);
                              if (nextExpanded) {
                                loadDrillIntervals(collar.holeId);
                                handleDrillHover(collar);
                              }
                            }}
                            sx={{
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: isExpanded ? 'rgba(249, 115, 22, 0.65)' : 'rgba(249, 115, 22, 0.25)',
                              backgroundColor: isExpanded ? 'rgba(249, 115, 22, 0.12)' : 'rgba(14, 20, 27, 0.6)',
                              transition: 'all 0.2s ease',
                              p: 1.2,
                              cursor: 'pointer'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  {collar.region || 'Unknown region'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fef9f3' }}>
                                  {collar.holeId}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.6} alignItems="center">
                                <Chip
                                  size="small"
                                  label={`Au max ${formatPpm(collar.maxGoldPpm)}`}
                                  sx={{
                                    backgroundColor: 'rgba(249, 115, 22, 0.18)',
                                    color: '#f97316',
                                    fontSize: 10,
                                    fontFamily: 'monospace'
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`g·m ${formatGradeThickness(collar.gradeThicknessAuGm ?? null)}`}
                                  sx={{
                                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                                    color: '#fb923c',
                                    fontSize: 10,
                                    fontFamily: 'monospace'
                                  }}
                                />
                              </Stack>
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: 0.8,
                                mt: 1,
                                fontSize: 10,
                                color: '#8b949e'
                              }}
                            >
                              <Typography>Total Depth: {formatLengthMeters(collar.totalDepthM ?? null)}</Typography>
                              <Typography>Sampled: {formatLengthMeters(collar.sampledLengthM ?? null)}</Typography>
                              <Typography>Intervals: {collar.intervalCount ?? 0}</Typography>
                              <Typography>Avg Au: {formatPpm(collar.avgGoldPpm)}</Typography>
                            </Box>
                            <Collapse in={isExpanded}>
                              <Box sx={{ mt: 1.2, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                {isActiveHole && drillIntervalsLoading ? (
                                  <LinearProgress sx={{ height: 3, borderRadius: 999 }} />
                                ) : isActiveHole && drillIntervalsError ? (
                                  <Typography sx={{ fontSize: 10, color: '#ff9b9b' }}>
                                    {drillIntervalsError}
                                  </Typography>
                                ) : intervalsForHole.length === 0 ? (
                                  <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                    No assay intervals logged for this collar.
                                  </Typography>
                                ) : (
                                  <>
                                    {statsForHole && (
                                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                        <Chip
                                          size="small"
                                          label={`Intervals ${statsForHole.intervalCount}`}
                                          sx={{ backgroundColor: 'rgba(249, 115, 22, 0.12)', color: '#f97316', fontSize: 9 }}
                                        />
                                        <Chip
                                          size="small"
                                          label={`Sampled ${formatLengthMeters(statsForHole.totalSampledLengthM)}`}
                                          sx={{ backgroundColor: 'rgba(249, 115, 22, 0.12)', color: '#f97316', fontSize: 9 }}
                                        />
                                        <Chip
                                          size="small"
                                          label={`Au g·m ${formatGradeThickness(statsForHole.gradeThicknessAuGm)}`}
                                          sx={{ backgroundColor: 'rgba(249, 115, 22, 0.12)', color: '#f97316', fontSize: 9 }}
                                        />
                                      </Stack>
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                      {intervalsForHole.map((interval, intervalIndex) => (
                                        <Box
                                          key={`${collar.holeId}-interval-${interval.sampleCode || intervalIndex}`}
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                            gap: 0.6,
                                            fontSize: 10,
                                            fontFamily: 'monospace',
                                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                            borderRadius: 1,
                                            p: 0.6
                                          }}
                                        >
                                          <Typography sx={{ color: '#8b949e' }}>
                                            {interval.fromM != null && interval.toM != null
                                              ? `${interval.fromM.toFixed(1)}–${interval.toM.toFixed(1)} m`
                                              : '—'}
                                          </Typography>
                                          <Typography sx={{ color: '#f97316' }}>
                                            Au {formatPpm(interval.goldPpm)}
                                          </Typography>
                                          <Typography sx={{ color: '#9fbefc' }}>
                                            Ag {formatPpm(interval.silverPpm)}
                                          </Typography>
                                          <Typography sx={{ color: '#8b949e' }}>
                                            g·m {formatGradeThickness(interval.gradeThicknessAuGm ?? null)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {insightsTab === 'surface' && (
                <Box sx={{ mt: 2 }}>
                  {surfaceAssaysLoading ? (
                    <LinearProgress sx={{ height: 3, borderRadius: 999 }} />
                  ) : surfaceAssaysError ? (
                    <Typography sx={{ fontSize: 11, color: '#ff9b9b' }}>{surfaceAssaysError}</Typography>
                  ) : surfaceAssaysForDisplay.length === 0 ? (
                    <Typography sx={{ fontSize: 11, color: '#8b949e' }}>
                      No surface assays available for this project.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                      {surfaceAssaysForDisplay.map((sample, index) => {
                        const sampleId =
                          sample.sampleCode && sample.sampleCode.trim().length > 0
                            ? sample.sampleCode.trim()
                            : `sample-${index}`;
                        const isExpanded = expandedSurfaceSampleId === sampleId;

                        return (
                          <Box
                            key={sampleId}
                            onMouseEnter={() => handleSurfaceHover(sample)}
                            onMouseLeave={() => {
                              if (!isExpanded) {
                                handleSurfaceHover(null);
                              }
                            }}
                            onClick={() => {
                              const nextExpanded = isExpanded ? null : sampleId;
                              setExpandedSurfaceSampleId(nextExpanded);
                              if (nextExpanded) {
                                handleSurfaceHover(sample);
                              }
                            }}
                            sx={{
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: isExpanded ? 'rgba(168, 85, 247, 0.55)' : 'rgba(168, 85, 247, 0.25)',
                              backgroundColor: isExpanded ? 'rgba(168, 85, 247, 0.12)' : 'rgba(14, 20, 27, 0.6)',
                              transition: 'all 0.2s ease',
                              p: 1.2,
                              cursor: 'pointer'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  {sample.region || 'Unassigned region'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#ede9fe' }}>
                                  {sample.sampleCode || `Sample ${index + 1}`}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.6}>
                                <Chip
                                  size="small"
                                  label={`Au ${formatPpm(sample.goldPpm)}`}
                                  sx={{
                                    backgroundColor: 'rgba(168, 85, 247, 0.15)',
                                    color: '#c084fc',
                                    fontSize: 10,
                                    fontFamily: 'monospace'
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`Ag ${formatPpm(sample.silverPpm)}`}
                                  sx={{
                                    backgroundColor: 'rgba(168, 85, 247, 0.08)',
                                    color: '#a78bfa',
                                    fontSize: 10,
                                    fontFamily: 'monospace'
                                  }}
                                />
                              </Stack>
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 0.6,
                                mt: 1,
                                fontSize: 10,
                                color: '#8b949e'
                              }}
                            >
                              <Typography>Cu {formatPpm(sample.copperPpm)}</Typography>
                              <Typography>Pb {formatPpm(sample.leadPpm)}</Typography>
                              <Typography>Zn {formatPpm(sample.zincPpm)}</Typography>
                            </Box>
                            <Collapse in={isExpanded}>
                              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                  Coordinates: {sample.latitude.toFixed(5)}°N · {Math.abs(sample.longitude).toFixed(5)}°W
                                </Typography>
                                {sample.metadata?.source_file && (
                                  <Typography sx={{ fontSize: 10, color: '#8b949e' }}>
                                    Source: {sample.metadata.source_file}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                  {Object.entries(sample.values || {}).map(([key, value]) => (
                                    <Chip
                                      key={`${sampleId}-${key}`}
                                      size="small"
                                      label={`${prettifyFieldLabel(key)} ${formatPpm(
                                        value as number | null | undefined
                                      )}`}
                                      sx={{
                                        backgroundColor: 'rgba(168, 85, 247, 0.12)',
                                        color: '#c4b5fd',
                                        fontSize: 9,
                                        fontFamily: 'monospace'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
        </Collapse>
      </Paper>
      )}

      {/* Mineral Legend */}
      {showMineralLegend && mineralMapData?.mineral_layers && (
        <Paper sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          p: 2,
          backgroundColor: '#161B22',
          border: '1px solid #30363D',
          borderRadius: '4px',
          minWidth: '220px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{
              color: 'var(--palantir-blue-600)',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}>
              MINERAL MAP LEGEND
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowMineralLegend(false)}
              sx={{ color: 'var(--palantir-text-muted)' }}
            >
              ×
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            
            {Object.entries(mineralMapData.mineral_layers).map(([mineralType, data]: [string, any]) => (
              <Box
                key={mineralType}
                onClick={() => toggleMineralLayer(mineralType)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  cursor: 'pointer',
                  borderRadius: '4px',
                  backgroundColor: activeMineralLayers.includes(mineralType) ? '#30363D' : 'transparent',
                  '&:hover': {
                    backgroundColor: '#21262D'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: data.properties.color,
                    borderRadius: '2px',
                    mr: 1,
                    opacity: activeMineralLayers.includes(mineralType) ? 1 : 0.5
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{
                    color: activeMineralLayers.includes(mineralType) ? 'var(--palantir-text-primary)' : 'var(--palantir-text-muted)',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    textTransform: 'capitalize'
                  }}>
                    {mineralType.replace('_', ' ')}
                  </Typography>
                  <Typography sx={{
                    color: 'var(--palantir-text-muted)',
                    fontFamily: 'monospace',
                    fontSize: '8px'
                  }}>
                    {data.properties.coverage_type === 'amorphous_zones' 
                      ? `${data.properties.feature_count || 0} coverage areas`
                      : `Max: ${(data.properties.max_probability * 100).toFixed(0)}%`
                    }
                  </Typography>
                  {data.properties.coverage_type === 'amorphous_zones' && (
                    <Typography sx={{
                      color: 'var(--palantir-text-muted)',
                      fontFamily: 'monospace',
                      fontSize: '7px'
                    }}>
                      Smoothed probability zones
                    </Typography>
                  )}
                </Box>
                <Typography sx={{
                  color: activeMineralLayers.includes(mineralType) ? '#00D2FF' : 'var(--palantir-text-muted)',
                  fontFamily: 'monospace',
                  fontSize: '9px'
                }}>
                  {activeMineralLayers.includes(mineralType) ? '●' : '○'}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Typography sx={{
            color: 'var(--palantir-text-muted)',
            fontFamily: 'monospace',
            fontSize: '8px',
            mt: 2,
            textAlign: 'center'
          }}>
            Click to toggle layers • Amorphous coverage areas
          </Typography>
          <Typography sx={{
            color: 'var(--palantir-text-muted)',
            fontFamily: 'monospace',
            fontSize: '7px',
            textAlign: 'center'
          }}>
            Dashed lines: Low confidence • Solid: Med/High
          </Typography>
        </Paper>
      )}

      {/* Zoom Controls */}
      {/* Bottom-left; raised above the ~85px-tall Analysis Results Summary (bottom:16,left:16) when it is shown so they never overlap. */}
      <Box sx={{
        position: 'absolute',
        bottom: heatmapData.length > 0 ? 120 : 24,
        left: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        <Tooltip title="Zoom In">
          <IconButton
            onClick={zoomIn}
            sx={{
              backgroundColor: '#161B22',
              border: '1px solid #30363D',
              color: 'var(--palantir-text-primary)',
              '&:hover': { backgroundColor: '#30363D' }
            }}
          >
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Zoom Out">
          <IconButton
            onClick={zoomOut}
            sx={{
              backgroundColor: '#161B22',
              border: '1px solid #30363D',
              color: 'var(--palantir-text-primary)',
              '&:hover': { backgroundColor: '#30363D' }
            }}
          >
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>

        {selectedProject && (
          <Tooltip title="Center on Project">
            <IconButton
              onClick={centerOnProject}
              sx={{
                backgroundColor: '#161B22',
                border: '1px solid #30363D',
                color: 'var(--palantir-blue-600)',
                '&:hover': { backgroundColor: '#30363D' }
              }}
            >
              <LocationIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Analysis Results Summary */}
      {heatmapData.length > 0 && (
        <Paper sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          p: 2,
          backgroundColor: '#161B22',
          border: '1px solid #30363D',
          borderRadius: '4px',
          maxWidth: '300px'
        }}>
          <Typography sx={{
            color: 'var(--palantir-blue-600)',
            fontFamily: 'monospace',
            fontSize: '12px',
            mb: 1
          }}>
            ANALYSIS COVERAGE
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${heatmapData.length} sites analyzed`}
              size="small"
              sx={{
                backgroundColor: 'var(--palantir-status-success)',
                color: '#000',
                fontFamily: 'monospace',
                fontSize: '9px',
                height: '18px'
              }}
            />
            {heatmapData.length > 0 && (
              <Chip
                label={`Avg: ${(heatmapData.reduce((sum, p) => sum + p.probability, 0) / heatmapData.length * 100).toFixed(1)}%`}
                size="small"
                sx={{
                  backgroundColor: 'var(--palantir-blue-600)',
                  color: '#000',
                  fontFamily: 'monospace',
                  fontSize: '9px',
                  height: '18px'
                }}
              />
            )}
          </Box>

          <Typography sx={{
            color: 'var(--palantir-text-muted)',
            fontFamily: 'monospace',
            fontSize: '9px',
            mt: 1
          }}>
            Use Mineral Discovery to analyze regions
          </Typography>
        </Paper>
      )}

      {/* Mineral Discovery Panel Drawer */}
      <Drawer
        anchor="left"
        open={discoveryPanelOpen}
        onClose={() => setDiscoveryPanelOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: discoveryPanelWidth,
            backgroundColor: '#161B22',
            borderRight: '1px solid #30363D',
            position: 'relative'
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -4,
            top: 0,
            width: 8,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1000,
            '&:hover': {
              backgroundColor: 'rgba(147, 51, 234, 0.3)'
            }
          }}
          onMouseDown={(e) => {
            setIsResizing(true);
            startX.current = e.clientX;
            startWidth.current = discoveryPanelWidth;
          }}
        />
        <MineralDiscoveryPanel 
          onAnalysisComplete={handleAnalysisComplete} 
          selectedProject={selectedProject}
          polygonRegions={polygonRegions}
          gridResolutionResolver={gridResolutionResolver}
        />
      </Drawer>

      {/* Dual Analysis Results Drawer */}
      <Drawer
        anchor="right"
        open={showDualAnalysis}
        onClose={() => setShowDualAnalysis(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 800, // Wider for dual comparison
            backgroundColor: '#161B22',
            borderLeft: '1px solid #30363D'
          }
        }}
      >
        {isDualAnalysisRunning ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, textAlign: 'center' }}>
              Running Dual Analysis...
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
              Comparing rule-based and machine learning approaches
            </Typography>
            
            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.primary', mb: 1, fontSize: '0.875rem' }}>
                {dualAnalysisMessage}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={dualAnalysisProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196f3',
                    borderRadius: 4
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block', textAlign: 'center' }}>
                Progress: {Math.round(dualAnalysisProgress)}%
              </Typography>
            </Box>
            
          </Box>
        ) : dualAnalysisResults ? (
          <DualAnalysisResultsPanel
            results={dualAnalysisResults}
            onRunNewAnalysis={() => {
                            setShowDualAnalysis(false);
              setDualAnalysisResults(null);
              setIsDualAnalysisRunning(false);
              
              // Clear any existing analysis result layers from the map
              if (analysisResultsLayer) {
                mapInstanceRef.current?.removeLayer(analysisResultsLayer);
                setAnalysisResultsLayer(null);
                              }
              
                          }}
            onToggleMapOverlay={(type) => {
                            if (type === 'ml' && dualAnalysisResults) {
                                // Use the dual analysis results to create map markers
                createAnalysisResultsLayer({
                  highPotentialAreas: dualAnalysisResults.highPotentialAreas || [],
                  project_id: selectedProject?.id,
                  type: 'dual_analysis'
                }, undefined, { autoShow: true });
              }
            }}
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              Dual Analysis
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No dual analysis results available. Run a dual analysis to see comparisons.
            </Typography>
          </Box>
        )}
      </Drawer>

      {/* Results Viewer Drawer */}
      <Drawer
        anchor="right"
        open={resultsViewerOpen}
        onClose={() => setResultsViewerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 500,
            backgroundColor: '#161B22',
            borderLeft: '1px solid #30363D'
          }
        }}
      >
        <AlgorithmResultsViewer
          results={discoveryResults}
          selectedResultId={selectedResultId}
          onResultSelect={setSelectedResultId}
          onLocationClick={handleLocationClick}
          onAreaClick={handleAreaClick}
          onExportResults={handleExportResults}
        />
      </Drawer>

      {/* Grid Potential Layers (New) - only render when Analysis Results toggle is on */}
      {showAnalysisResults && gridDataByMineral.size > 0 && (
        <>
          {Array.from(gridDataByMineral.entries()).map(([analysisKey, gridData]) => {
            const analysisId = gridData.analysisId || analysisKey;
            const baseMineralType = gridData.commodity && gridData.commodity !== 'unknown'
              ? gridData.commodity
              : gridData.metadata?.commodity || 'unknown';
            const rawMineralType = baseMineralType;
            const mineralKey = rawMineralType.toLowerCase();
            const metadataMineralKey = gridData.metadata?.commodity
              ? gridData.metadata.commodity.toLowerCase()
              : '';
            const visibilityKey = `heatmap_${analysisId}`;
            const baseVisibility = markerVisibility?.get(visibilityKey);
            const isSelectedEntry = selectedAnalysisEntry?.analysisId === analysisId;
            const heatmapVisible = typeof baseVisibility === 'boolean' ? baseVisibility : true;
            const shouldRenderGrid =
              showAnalysisResults && visualizationMode === 'grid' && isSelectedEntry && useGridVisualization;
            const shouldRenderHeatmap = showAnalysisResults && visualizationMode === 'heatmap' && heatmapVisible;

            const settings =
              gridSettingsWithDefaults?.get(analysisId) ||
              gridSettingsWithDefaults?.get(rawMineralType) ||
              (metadataMineralKey ? gridSettingsWithDefaults?.get(metadataMineralKey) : undefined) ||
              gridSettingsWithDefaults?.get(mineralKey);
            const threshold = settings?.threshold ?? 0.05;
            const opacity = settings?.opacity ?? 0.85;
            const modulated = settings?.modulated ?? false;
            const blurSetting = settings?.blur ?? DEFAULT_ANALYSIS_GRID_SETTINGS.blur;
            const radiusSetting = settings?.radius ?? DEFAULT_ANALYSIS_GRID_SETTINGS.radius;
            const colorSchemeModeSetting = settings?.colorScheme ?? DEFAULT_ANALYSIS_GRID_SETTINGS.colorScheme;
            const scalingModeSetting = settings?.scalingMode ?? DEFAULT_ANALYSIS_GRID_SETTINGS.scalingMode;
            const focusRangeOverride = settings?.focusRange;
            const customFocusRange = settings?.customFocusRange ?? DEFAULT_ANALYSIS_GRID_SETTINGS.customFocusRange;
            const intensityGammaSetting =
              settings?.intensityGamma ?? DEFAULT_ANALYSIS_GRID_SETTINGS.intensityGamma;
            const customGammaSetting = settings?.customGamma ?? DEFAULT_ANALYSIS_GRID_SETTINGS.customGamma;

            const isControlFlyoutTarget = controlFlyoutEntry?.analysisId === analysisId;
            const controlsContainerId = isControlFlyoutTarget ? flyoutControlsContainerId : undefined;

            const updateMineralKey = metadataMineralKey || mineralKey;

            const handleHeatmapSettingsChange = isControlFlyoutTarget
              ? (changes: Partial<GridSettings>) => {
                  if (!changes) {
                    return;
                  }
                  updateGridSetting(analysisId, updateMineralKey, changes);
                }
              : undefined;

            const handleVisibilityChange = (visible: boolean) => {
              onMarkerVisibilityChange?.(visibilityKey, visible);
            };

            const uniqueLatitudes = Array.from(new Set(gridData.coordinates.map(([lat]) => lat))).sort((a, b) => a - b);
            const uniqueLongitudes = Array.from(new Set(gridData.coordinates.map(([, lon]) => lon))).sort((a, b) => a - b);
            const estimatedRows = Math.max(1, uniqueLatitudes.length);
            const estimatedCols = Math.max(1, uniqueLongitudes.length);
            const latSpacing = estimatedRows > 1 ? Math.abs(uniqueLatitudes[1] - uniqueLatitudes[0]) : 0.01;
            const lonSpacing = estimatedCols > 1 ? Math.abs(uniqueLongitudes[1] - uniqueLongitudes[0]) : 0.01;

            const boundsFromMetadata = gridData.metadata?.bounds
              ? {
                  min_lat: gridData.metadata.bounds[0],
                  min_lon: gridData.metadata.bounds[1],
                  max_lat: gridData.metadata.bounds[2],
                  max_lon: gridData.metadata.bounds[3]
                }
              : null;

            const computedBoundsObject =
              gridData.metadata?.polygonBounds ??
              boundsFromMetadata ??
              computeBoundsFromLatLon(gridData.coordinates);

            const fallbackBoundsArray: [number, number, number, number] = computedBoundsObject
              ? [
                  computedBoundsObject.min_lat,
                  computedBoundsObject.min_lon,
                  computedBoundsObject.max_lat,
                  computedBoundsObject.max_lon
                ]
              : [
                  gridData.coordinates[0]?.[0] ?? 0,
                  gridData.coordinates[0]?.[1] ?? 0,
                  gridData.coordinates[0]?.[0] ?? 0,
                  gridData.coordinates[0]?.[1] ?? 0
                ];

            const polygonBoundsMetadata = {
              min_lat: fallbackBoundsArray[0],
              max_lat: fallbackBoundsArray[2],
              min_lon: fallbackBoundsArray[1],
              max_lon: fallbackBoundsArray[3],
              center_lat: (fallbackBoundsArray[0] + fallbackBoundsArray[2]) / 2,
              center_lon: (fallbackBoundsArray[1] + fallbackBoundsArray[3]) / 2
            };

            const sanitizedConfidences = gridData.confidences
              ? gridData.confidences.map((value) =>
                  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.5
                )
              : undefined;

            const sanitizedGridData: GridLayerData = {
              probabilities: gridData.probabilities.map((value) => Math.max(0, Math.min(1, toUnitInterval(value)))),
              coordinates: gridData.coordinates,
              confidences: sanitizedConfidences,
              metadata: {
                bounds: fallbackBoundsArray,
                grid_size: [
                  gridData.metadata?.grid_size?.[0] ?? estimatedRows,
                  gridData.metadata?.grid_size?.[1] ?? estimatedCols
                ] as [number, number],
                cell_dimensions: gridData.metadata?.cell_dimensions ?? {
                  width_deg: lonSpacing || 0.01,
                  height_deg: latSpacing || 0.01,
                  width_km: (lonSpacing || 0.01) * 111,
                  height_km: (latSpacing || 0.01) * 111
                },
                polygon_bounds: gridData.metadata?.polygon_bounds ?? polygonBoundsMetadata
              }
            };

            const computedUncertainties = sanitizedConfidences
              ? sanitizedConfidences.map((value) => Math.max(0, Math.min(1, 1 - value)))
              : undefined;

            const heatMapData =
              visualizationMode === 'heatmap'
                ? {
                    probabilities: sanitizedGridData.probabilities,
                    coordinates: sanitizedGridData.coordinates,
                    uncertainties: computedUncertainties,
                    pointDetails: gridData.pointDetails,
                    metadata: {
                      bounds: fallbackBoundsArray,
                      high_potential_count:
                        gridData.metadata?.highPotentialCount ??
                        gridData.metadata?.high_potential_count ??
                        sanitizedGridData.probabilities.filter((p: number) => p >= 0.7).length,
                      discovery_probability:
                        gridData.metadata?.discoveryProbability ??
                        gridData.metadata?.discovery_probability ??
                        (sanitizedGridData.probabilities.length
                          ? sanitizedGridData.probabilities.reduce((sum: number, value: number) => sum + value, 0) /
                            sanitizedGridData.probabilities.length
                          : 0),
                      exploration_value: (gridData.metadata as any)?.exploration_value ?? null
                    }
                  }
                : null;

            if (visualizationMode === 'grid') {
              if (!shouldRenderGrid) {
                return null;
              }
              return (
                <GridPotentialLayer
                  key={`grid_${analysisId}`}
                  map={mapInstanceRef.current}
                  gridData={sanitizedGridData}
                  mineralType={rawMineralType}
                  visible={true}
                  onVisibilityChange={handleVisibilityChange}
                  zoomLevel={currentZoomLevel}
                  polygonBounds={sanitizedGridData.metadata?.bounds}
                  showControlPanel={false}
                  threshold={threshold}
                  opacity={opacity}
                  modulated={modulated}
                />
              );
            }

            // Always render heatmap component so it can properly manage layer visibility/cleanup
            if (visualizationMode !== 'heatmap') {
              return null;
            }

            return (
              <ContinuousHeatMapLayer
                key={`heatmap_${analysisId}`}
                map={mapInstanceRef.current}
                heatMapData={heatMapData}
                mineralType={rawMineralType}
                visible={heatmapVisible && showAnalysisResults}
                onVisibilityChange={handleVisibilityChange}
                                onPointClick={isSelectedEntry ? handleHeatMapPointClick : undefined}
                showUncertainty={modulated}
                threshold={threshold}
                blur={blurSetting}
                radius={radiusSetting}
                colorSchemeMode={colorSchemeModeSetting}
                scalingMode={scalingModeSetting}
                focusRangeOverride={focusRangeOverride}
                customFocusRange={customFocusRange}
                intensityGamma={intensityGammaSetting}
                customGamma={customGammaSetting}
                controlsContainerId={controlsContainerId}
                onSettingsChange={handleHeatmapSettingsChange}
                analysisId={analysisId}
              />
            );
          })}
        </>
      )}

      {/* Rule Contribution Panel Drawer */}
      <Drawer
        anchor="bottom"
        open={ruleContributionOpen}
        onClose={() => setRuleContributionOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            height: '60%',
            backgroundColor: '#161B22',
            borderTop: '1px solid #30363D'
          }
        }}
      >
        <RuleContributionPanel
          analysisId={selectedResultId}
          ruleContributions={currentRuleContributions}
          onRuleSelect={(ruleId) => {
                        // Could highlight areas on map where this rule triggered
          }}
        />
      </Drawer>

      {/* Statistical Validation Dashboard Drawer */}
      <Drawer
        anchor="right"
        open={statisticsOpen}
        onClose={() => setStatisticsOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 800,
            backgroundColor: '#161B22',
            borderLeft: '1px solid #30363D'
          }
        }}
      >
        <StatisticalValidationDashboard
          commodity="gold"
          onRuleSelect={(ruleId) => {
                        // Could show detailed rule analysis
          }}
        />
      </Drawer>

      {/* Interactive Rule Tuning Panel Drawer */}
      <Drawer
        anchor="left"
        open={ruleTuningOpen}
        onClose={() => setRuleTuningOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            backgroundColor: '#161B22',
            borderRight: '1px solid #30363D'
          }
        }}
      >
        <InteractiveRuleTuningPanel
          onRuleChange={(ruleId, threshold, weight, enabled) => {
                        // Could update analysis in real-time
          }}
          onRunAnalysis={(rules) => {
                        // Could trigger new analysis with updated rules
          }}
          onSavePreset={() => {
                      }}
          onLoadPreset={(presetId) => {
                      }}
        />
      </Drawer>

      {/* Polygon Drawing Tool - Always rendered to show existing polygons */}
      {mapInstanceRef.current && (
        <PolygonDrawingTool
          map={mapInstanceRef.current}
          onPolygonComplete={handlePolygonComplete}
          onPolygonModifyComplete={handlePolygonModifyComplete}
          onPolygonValidation={handlePolygonValidation}
          disabled={isLoading || !polygonDrawingEnabled}
          existingPolygons={memoizedVisiblePolygons}
          onPolygonDelete={(polygonId) => onPolygonDelete?.(polygonId)}
        />
      )}

      {/* Polygon Manager Drawer */}
      <Drawer
        anchor="left"
        open={polygonManagerOpen}
        onClose={() => {
          setPolygonManagerOpen(false);
          // Clear pending polygon data on cancel
          setPendingPolygonData(null);
          // Disable drawing when dialog is closed (if user cancels)
          setPolygonDrawingEnabled(false);
                  }}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            width: 450,
            backgroundColor: '#161B22',
            border: '1px solid #30363D',
            color: 'var(--palantir-text-primary)'
          },
        }}
      >
          <PolygonManager
            projectId={selectedProject?.id || 'default'}
            polygons={polygonRegions}
            pendingPolygonData={pendingPolygonData}
            onPolygonCreate={(polygon) => {
              onPolygonCreate?.(polygon);
              // Clear pending polygon data after successful save
              setPendingPolygonData(null);
              // Disable drawing after polygon is successfully created
              setPolygonDrawingEnabled(false);
                      }}
            onPolygonUpdate={(polygonId, updates) => onPolygonUpdate?.(polygonId, updates)}
          onPolygonDelete={(polygonId) => onPolygonDelete?.(polygonId)}
          onDeleteAllPolygons={onPolygonDeleteAll}
          onPolygonVisibilityToggle={handlePolygonVisibilityToggle}
          onRunAnalysis={handlePolygonManagerAnalysis}
          onExportPolygon={(polygonId, format) => {
                        // TODO: Implement polygon export
          }}
          onImportPolygons={(file, options) => {
            if (onPolygonImport) {
              onPolygonImport(file, options);
            } else {
              console.warn('Polygon import handler not configured.');
            }
          }}
          onPendingPolygonCleared={handlePendingPolygonCleared}
        />
      </Drawer>

      {/* Enhanced Analysis Log Panel */}
      <EnhancedAnalysisLogPanel
        analysisId={currentAnalysisId || ''}
        isVisible={showEnhancedAnalysisLog && currentAnalysisId !== null}
        onClose={() => setShowEnhancedAnalysisLog(false)}
      />

      {/* Njuggtraskliden Cross-Sections Dialog */}
      <Dialog
        open={njuggCrossSectionOpen}
        onClose={() => setNjuggCrossSectionOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#161b22',
            backgroundImage: 'none',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#e6edf3',
          borderBottom: '1px solid #30363d',
          fontFamily: 'monospace'
        }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            Njuggträskliden Gravity Inversion - Cross-Sections
          </Typography>
          <IconButton onClick={() => setNjuggCrossSectionOpen(false)} sx={{ color: '#8b949e' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#8b949e', mb: 2 }}>
            SimPEG 3D density inversion from 1,065 SGU gravity stations. Positive density (red) indicates ultramafic intrusions; negative density (blue) indicates graphite-rich host rock.
          </Typography>

          {/* E-W Cross-Section */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontFamily: 'monospace', color: '#00e5ff', fontWeight: 600, mb: 1 }}>
              E-W Cross-Section through Zone D
            </Typography>
            <Box
              component="img"
              src="/analysis/njuggtraskliden/cross_section_ew.png"
              alt="E-W Cross-Section"
              sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
              onClick={() => window.open('/analysis/njuggtraskliden/cross_section_ew.png', '_blank')}
            />
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#666', mt: 0.5 }}>
              Dashed line = 200m max historic drill depth. Positive density anomaly extends to 400-500m.
            </Typography>
          </Box>

          {/* N-S Cross-Section */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontFamily: 'monospace', color: '#00e5ff', fontWeight: 600, mb: 1 }}>
              N-S Cross-Section
            </Typography>
            <Box
              component="img"
              src="/analysis/njuggtraskliden/cross_section_ns.png"
              alt="N-S Cross-Section"
              sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
              onClick={() => window.open('/analysis/njuggtraskliden/cross_section_ns.png', '_blank')}
            />
          </Box>

          {/* Density Histogram */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontFamily: 'monospace', color: '#00e5ff', fontWeight: 600, mb: 1 }}>
              Density Distribution
            </Typography>
            <Box
              component="img"
              src="/analysis/njuggtraskliden/density_histogram.png"
              alt="Density Histogram"
              sx={{ width: '100%', maxWidth: 600, borderRadius: 1, cursor: 'pointer' }}
              onClick={() => window.open('/analysis/njuggtraskliden/density_histogram.png', '_blank')}
            />
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#666', mt: 0.5 }}>
              Min: -0.500 g/cc | Max: +0.129 g/cc | Green dashed = ultramafic threshold (+0.1 g/cc)
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Njuggtraskliden Magnetic Cross-Sections Dialog */}
      <Dialog
        open={njuggMagneticCrossSectionOpen}
        onClose={() => setNjuggMagneticCrossSectionOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#161b22',
            backgroundImage: 'none',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#e6edf3',
          borderBottom: '1px solid #30363d',
          fontFamily: 'monospace'
        }}>
          <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            Njuggträskliden Magnetic Inversion - Cross-Sections
          </Typography>
          <IconButton onClick={() => setNjuggMagneticCrossSectionOpen(false)} sx={{ color: '#8b949e' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#8b949e', mb: 2 }}>
            SimPEG 3D magnetic inversion from SGU aeromagnetic data. High susceptibility (yellow/red) indicates magnetite-bearing ultramafic rocks; low susceptibility (dark) indicates felsic rocks or altered zones.
          </Typography>

          {/* E-W Cross-Section */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '14px', fontFamily: 'monospace', color: '#ff5722', fontWeight: 600, mb: 1 }}>
              E-W Cross-Section
            </Typography>
            <Box
              component="img"
              src="/analysis/njuggtraskliden/magnetic_cross_section_ew.png"
              alt="Magnetic E-W Cross-Section"
              sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
              onClick={() => window.open('/analysis/njuggtraskliden/magnetic_cross_section_ew.png', '_blank')}
            />
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#666', mt: 0.5 }}>
              Log-scale susceptibility (SI units). High susceptibility zones indicate magnetite-bearing ultramafic intrusions.
            </Typography>
          </Box>

          {/* N-S Cross-Section */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontFamily: 'monospace', color: '#ff5722', fontWeight: 600, mb: 1 }}>
              N-S Cross-Section
            </Typography>
            <Box
              component="img"
              src="/analysis/njuggtraskliden/magnetic_cross_section_ns.png"
              alt="Magnetic N-S Cross-Section"
              sx={{ width: '100%', borderRadius: 1, cursor: 'pointer' }}
              onClick={() => window.open('/analysis/njuggtraskliden/magnetic_cross_section_ns.png', '_blank')}
            />
            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#666', mt: 0.5 }}>
              Susceptibility range: 2.6e-06 to 0.51 SI. High values (&gt;0.01 SI) indicate strongly magnetic ultramafic bodies.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default MapView;
